(() => {
  const root = document.querySelector('[data-pet]');
  if (!root) return;

  const find = selector => root.querySelector(selector);
  const els = {
    canvas: find('[data-pet-canvas]'),
    who: find('[data-pet-who]'),
    crest: find('[data-pet-crest]'),
    name: find('[data-pet-name]'),
    hqLevel: find('[data-pet-hq-level]'),
    life: find('[data-pet-life]'),
    lifeFill: find('[data-pet-life-fill]'),
    cash: find('[data-pet-cash]'),
    cashCount: find('[data-pet-cash-count]'),
    cashRate: find('[data-pet-cash-rate]'),
    fans: find('[data-pet-fans]'),
    fansCount: find('[data-pet-fans-count]'),
    fansRate: find('[data-pet-fans-rate]'),
    move: find('[data-pet-move]'),
    raid: find('[data-pet-raid]'),
    heist: find('[data-pet-heist]'),
    pay: find('[data-pet-pay]'),
    magnet: find('[data-pet-magnet]'),
    daily: find('[data-pet-daily]'),
    rescue: find('[data-pet-rescue]'),
    chief: find('[data-pet-chief]'),
    feed: find('[data-pet-feed]'),
    tools: find('[data-pet-tools]'),
    war: find('[data-pet-war]'),
    card: find('[data-pet-card]'),
    buildCount: find('[data-pet-build-count]'),
    intro: find('[data-pet-intro]'),
    over: find('[data-pet-over]'),
    modal: find('[data-pet-modal]'),
    panel: find('[data-pet-panel]'),
    panelTitle: find('[data-pet-panel-title]'),
    wallet: find('[data-pet-wallet]'),
    panelBody: find('[data-pet-panel-body]'),
    close: find('[data-pet-close]')
  };
  const canvas = els.canvas;
  const view = canvas.getContext('2d');
  let ctx = view;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const STORAGE_KEY = 'mixedown-kerlchen';
  const SAVE_VERSION = 15;
  const SECOND = 1000;
  const MINUTE = 60 * SECOND;
  const HOUR = 60 * MINUTE;
  const DAY = 24 * HOUR;
  const TAU = Math.PI * 2;
  const FONT = '"MIXEDOWN Sans", "Helvetica Neue", Helvetica, Arial, sans-serif';
  const INK = '#141415';
  const PAPER = '#f4f3ef';
  const MINT = '#baecb7';
  const MINT_D = '#7cc878';
  const SIGNAL = '#ff4b1f';
  const YELLOW = '#ffd23c';
  const GOLD = '#e2b33c';
  // The Moshpit's stage-light violet.
  const MOSH = '#b026ff';
  const DRONE_RED = '#ff2a3d';
  const BOSS_RED = '#b3122e';

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const lerp = (a, b, t) => a + (b - a) * t;
  const ease = (value, target, rate, dt) => value + (target - value) * (1 - Math.exp(-rate * dt));
  const dist = (ax, ay, bx, by) => Math.hypot(ax - bx, ay - by);
  const escapeHtml = text => String(text).replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

  const mulberry32 = seed => () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const hashText = text => {
    let hash = 2166136261;
    for (let i = 0; i < text.length; i += 1) hash = Math.imul(hash ^ text.charCodeAt(i), 16777619);
    return hash >>> 0;
  };
  const newSeed = () => {
    try {
      return crypto.getRandomValues(new Uint32Array(1))[0];
    } catch {
      return Math.floor(Math.random() * 4294967296);
    }
  };
  const oneOf = (rand, list) => list[Math.floor(rand() * list.length)];

  // Big numbers: 1.234, 12,3 K, 4,5 Mio, then letter pairs, without end.
  const SUFFIX = ['', 'K', 'Mio', 'Mrd', 'Bio'];
  const fmt = value => {
    const n = Math.floor(Math.max(0, value));
    if (n < 10000) return n.toLocaleString('de-DE');
    let tier = Math.floor(Math.log10(n) / 3);
    let scaled = n / 10 ** (tier * 3);
    if (scaled >= 999.95) {
      tier += 1;
      scaled /= 1000;
    }
    let suffix = SUFFIX[tier];
    if (!suffix) {
      const k = tier - SUFFIX.length;
      suffix = String.fromCharCode(97 + Math.floor(k / 26) % 26) + String.fromCharCode(97 + (k % 26));
    }
    const digits = scaled < 100 ? 1 : 0;
    return `${scaled.toFixed(digits).replace('.', ',').replace(/,0$/, '')} ${suffix}`;
  };
  const formatClock = ms => {
    const seconds = Math.max(0, Math.ceil(ms / 1000));
    if (seconds >= 3600) return `${Math.floor(seconds / 3600)}:${String(Math.floor((seconds % 3600) / 60)).padStart(2, '0')} Std`;
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  };
  const formatSpan = ms => {
    if (ms < HOUR) return `${Math.max(1, Math.round(ms / MINUTE))} Min`;
    if (ms < DAY) return `${Math.floor(ms / HOUR)} Std`;
    const days = Math.floor(ms / DAY);
    return days === 1 ? '1 Tag' : `${days} Tage`;
  };

  // ---------------------------------------------------------------------------------------------
  // The hustle works like MIXEDOWN On-Demand: promo brings fans, sales turn fans into orders,
  // MIXEDOWN produces every order only after it is sold, and every sale pays out cash. No stock, no risk.
  // Every level costs more than the last and pays a little more; every tenth level doubles output.

  const CHUNK = 6;
  const PAYOUT = 18.4;
  const TYPES = {
    hq: { kind: 'home', name: 'Base', w: 3, d: 3, hp: 420, unlock: 1, cost: 0, per: 0, text: 'Von der Garage zum Tower. Ihre Stufe bestimmt, wie weit alles andere wachsen darf.' },
    plakat: { kind: 'promo', name: 'Wildplakate', w: 2, d: 1, hp: 110, unlock: 1, cost: 40, per: 0.2, rate: 15, text: 'Plakate an jeder Ecke. Wer vorbeiläuft, kennt danach deinen Namen.' },
    sticker: { kind: 'promo', name: 'Sticker-Spot', w: 2, d: 1, hp: 130, unlock: 2, cost: 320, per: 0.2, rate: 38, text: 'Stromkasten und Laterne, vollgeklebt mit deinen Stickern. Klein, billig, überall.' },
    wall: { kind: 'promo', name: 'Graffiti-Wall', w: 2, d: 1, hp: 220, unlock: 3, cost: 1500, per: 0.2, rate: 90, text: 'Ein Piece in XXL. Die halbe Stadt macht Fotos davor.' },
    studio: { kind: 'promo', name: 'Content-Studio', w: 2, d: 2, hp: 240, unlock: 4, cost: 8000, per: 0.2, rate: 255, text: 'Ringlicht, Kamera, live. Jeder Clip mit deiner Merch holt neue Follower.' },
    party: { kind: 'promo', name: 'Block Party', w: 2, d: 2, hp: 260, unlock: 5, cost: 40000, per: 0.2, rate: 600, text: 'Truck, Boxen, DJ. Die Straße feiert mit und folgt dir danach.' },
    billboard: { kind: 'promo', name: 'LED-Billboard', w: 2, d: 1, hp: 300, unlock: 6, cost: 500000, per: 0.16, rate: 1500, text: 'Riesig an der Hauptstraße. Erst Papier, dann ein Screen, der deine Drops rund um die Uhr zeigt.' },
    buehne: { kind: 'promo', name: 'Festival', w: 3, d: 2, hp: 380, unlock: 7, cost: 3000000, per: 0.16, rate: 3750, text: 'Live-Shows. Starte ein Konzert, und jede Show bringt eine Welle neuer Fans.' },
    court: { kind: 'promo', name: 'Streetball-Court', w: 2, d: 2, hp: 250, unlock: 4, cost: 20000, per: 0.2, rate: 400, text: 'Ein Korb, ein Käfig, dein Logo auf dem Boden. Jedes Spiel hier wird gefilmt und geteilt.' },
    skate: { kind: 'promo', name: 'Skate-Spot', w: 2, d: 2, hp: 280, unlock: 5, cost: 120000, per: 0.2, rate: 1000, text: 'Rampe, Rail und Boards mit deinem Logo. Die Clips von hier laufen überall.' },
    radio: { kind: 'promo', name: 'Piratensender', w: 2, d: 2, hp: 400, unlock: 8, cost: 25000000, per: 0.16, rate: 9000, text: 'Ein Sender auf dem Dach, auf keiner Liste. Die ganze Stadt hört deine Drops.' },
    stand: { kind: 'sale', name: 'Corner-Stand', w: 2, d: 1, hp: 140, unlock: 1, cost: 50, per: 0.2, walk: 1, conv: 0.005, text: 'Tisch, Schirm, Kleiderstange. Verkauft an alle, die vorbeikommen.' },
    shop: { kind: 'sale', name: 'On-Demand Shop', w: 2, d: 2, hp: 240, unlock: 2, cost: 450, per: 0.14, walk: 0, conv: 0.015, online: true, text: 'Dein Shop bei MIXEDOWN. Verkauft rund um die Uhr an alle deine Fans, das Cash kommt von selbst.' },
    truck: { kind: 'sale', name: 'Merch-Truck', w: 2, d: 1, hp: 220, unlock: 3, cost: 1600, per: 0.2, walk: 3.3, conv: 0.007, text: 'Klappe hoch, Musik an. Der Truck verkauft da, wo gerade was los ist.' },
    popup: { kind: 'sale', name: 'Pop-up Store', w: 2, d: 2, hp: 300, unlock: 4, cost: 8000, per: 0.2, walk: 7, conv: 0.011, text: 'Ein Container mitten im Kiez. Limitierte Teile, lange Schlangen.' },
    zelt: { kind: 'sale', name: 'Merch-Zelt', w: 2, d: 2, hp: 330, unlock: 6, cost: 400000, per: 0.16, walk: 11, conv: 0.028, text: 'Das Zelt vom Festival. Tische voller Shirts, und deine Fans stehen Schlange.' },
    flagship: { kind: 'sale', name: 'Flagship Store', w: 3, d: 2, hp: 520, unlock: 8, cost: 20000000, per: 0.14, walk: 36, conv: 0.05, text: 'Dein eigener Laden auf der Shoppingmeile. Schaufenster, Puppen, Schlange vor der Tür.' },
    automat: { kind: 'sale', name: 'Merch-Automat', w: 1, d: 1, hp: 160, unlock: 2, cost: 900, per: 0.2, walk: 1.6, conv: 0.004, text: 'Caps, Socken und Sticker hinter Glas, ganz ohne Personal. Wer vorbeiläuft, zieht sich was.' },
    concept: { kind: 'sale', name: 'Concept Store', w: 2, d: 2, hp: 360, unlock: 5, cost: 90000, per: 0.2, walk: 9, conv: 0.018, text: 'Ein Laden im Altbau, nur ausgewählte Teile. Die Szene kauft hier zuerst.' },
    halle: { kind: 'sale', name: 'Markthalle', w: 3, d: 2, hp: 480, unlock: 7, cost: 3000000, per: 0.16, walk: 20, conv: 0.04, text: 'Eine alte Halle voller Stände, alle mit deiner Merch. Am Wochenende ist die halbe Stadt da.' },
    // Heroes: the MIXEDOWN artists, one of each, and the only ones who fight for the block. Open from the start,
    // kept back by their price alone; all cost and level the same, what sets them apart is how they look, fight and
    // their special.
    caruso: { kind: 'crew', hero: true, name: 'Caruso', w: 1, d: 1, hp: 1140, unlock: 1, cost: 300, range: 3.4, roam: 9, rate: 1.5, dmg: 21.6, perk: ['speed', 'Gummiwirbel', 'Jeder 3. rundum'], text: 'Blond, ovale Sonnenbrille, rote Lederjacke und dicke Gummiarme. Seine Fäuste schnellen quer über den Block, jeder dritte Hieb ist eine Drehung durch alles um ihn, und im Gummi-Tornado wirbelt er die ganze Gang platt.' },
    retro: { kind: 'crew', hero: true, name: 'Retro', w: 1, d: 1, hp: 2520, unlock: 1, cost: 300, range: 3.2, roam: 6, rate: 0.5, dmg: 38.4, splash: 1.8, knock: 1.6, stun: 0.8, taunt: 4.5, perk: ['shield', 'Zieht Feinde', 'Alle an'], text: 'Dreads, dicke Sonnenbrille, Kippe und ein Schultergurt voller Granaten. Zieht alle Bootlegs auf sich und wirft Clustergranaten in die Meute, die in Bomblets und dann noch einmal in kleine Knaller zerplatzen. Sein Bodyslam lässt den ganzen Block beben.' },
    zola: { kind: 'crew', hero: true, name: 'Zola', w: 1, d: 1, hp: 1860, unlock: 1, cost: 300, range: 1.5, roam: 7, rate: 0.42, dmg: 116, knock: 3.2, perk: ['fist', 'Rückstoß', '3 Felder'], text: 'Über zwei Meter, Brille, Band-Tee, weite Hose. Jeder Tritt schickt einen Bootleg über die Straße, sein Sparta-Kick tritt eine ganze Schneise frei.' },
    kuza: { kind: 'crew', hero: true, name: 'Kuza & Kamua', w: 1, d: 1, hp: 1080, unlock: 1, cost: 300, range: 5.5, roam: 5, rate: 0.9, dmg: 8, hits: 6, perk: ['range', 'Sternensalve', 'Je drei Sterne'], text: 'Zwei Brüder, ein Team. Jeder wirft Salven aus drei Bumerang-Sternen, die durch die Meute schneiden und zurückkommen, und im Sternenhagel fliegen sie in drei Ringen überall hin.' },
    blade: { kind: 'crew', hero: true, name: '2lade', w: 1, d: 1, hp: 1440, unlock: 1, cost: 300, range: 1.6, roam: 9, rate: 0.8, dmg: 24, hits: 3, leech: 0.3, perk: ['heart', 'Lebensraub', '30 %'], text: 'Alien-Brille mit roten Gläsern, Silber-Grill, Senfjacke und ein langes Silberschwert. Hieb, Rückhand, Stich: der Stich geht durch alle in der Reihe, jeder Treffer heilt ihn, und unterm Blutmond schneidet er durch alle.' },
    capi: { kind: 'crew', hero: true, name: 'Prettyfacecapi', w: 1, d: 1, hp: 1200, unlock: 1, cost: 300, range: 2.2, roam: 9, rate: 0.8, dmg: 42, splash: 2.2, perk: ['target', 'Sensenwirbel', 'Alles um ihn'], text: 'Bucket Hat, Puffer, Goldkette und das schönste Gesicht der Stadt, dazu eine lila glühende Sense. Jeder Hieb ist eine ganze Drehung durch alles um ihn herum, und im Sensensturm wirbelt er quer durch die Meute.' },
    johno: { kind: 'crew', hero: true, name: 'Johno', w: 1, d: 1, hp: 1560, unlock: 1, cost: 300, range: 1.5, roam: 7, rate: 0.75, dmg: 48, stun: 1.4, perk: ['up', 'Uppercut & Flykick', 'Ab in die Luft'], text: 'Schiebermütze, weißes Tanktop, Kippe im Mundwinkel. Uppercut, Flykick, Uppercut: Bootlegs fliegen hoch und weit, bei der Himmelfahrt gleich eine ganze Reihe.' },
    polarise: { kind: 'crew', hero: true, name: 'Polarise', w: 1, d: 1, hp: 2040, unlock: 1, cost: 300, range: 1.7, roam: 6, rate: 0.5, dmg: 68, splash: 1.7, knock: 2.8, stun: 0.6, perk: ['target', 'Homerun', 'Ab vom Block'], text: 'Monogramm-Cap, grauer Tracksuit, der perfekt sitzt, dicke Uhr und immer die Ruhe selbst. Sein riesiger Schläger mit Stacheldraht und langen Nägeln schickt Bootlegs vom Block, und beim Homerun fliegt die ganze Meute.' },
    bigtoe: { kind: 'crew', hero: true, name: 'Bigtoe', w: 1, d: 1, hp: 1680, unlock: 1, cost: 300, range: 1.4, roam: 8, rate: 1, dmg: 18.4, knock: 1.8, stun: 0.5, perk: ['fist', 'Schlagkombo', '3 Treffer'], text: 'Bordeaux-Samtanzug, dicke Goldkette und Arme wie Baumstämme, an jeder Faust ein goldener Schlagring voller Diamanten. Er verteilt Kombos, und beim Diamanthagel prasseln die Schläge auf alle.' },
    maliq: { kind: 'crew', hero: true, name: 'Maliq', w: 1, d: 1, hp: 960, unlock: 1, cost: 300, range: 6.8, roam: 4, rate: 0.85, dmg: 20, hits: 3, perk: ['range', 'Fernkampf', 'Ganz weit'], text: 'Dreads unterm Fitted, Shutter Shades, Iced-out Chain, Stil von 2008. Wirft magische Karten aus der Ferne, und bei Royal Flush regnet es Asse.' }
  };
  const BUILD_TABS = [
    ['promo', 'Promo', ['plakat', 'sticker', 'wall', 'studio', 'court', 'party', 'skate', 'billboard', 'buehne', 'radio']],
    ['sale', 'Verkauf', ['stand', 'shop', 'automat', 'truck', 'popup', 'concept', 'zelt', 'halle', 'flagship']],
    ['aces', 'Attacken', []],
    ['hero', 'Helden', ['caruso', 'retro', 'zola', 'kuza', 'blade', 'capi', 'johno', 'maliq', 'polarise', 'bigtoe']]
  ];
  // Aces: cards bought once in the shop, each at full strength from then on. Three of them go into a fight, picked
  // outside of one, and each goes off for free ACE_USES times a round. The Goldfaust is the player's own fist, always
  // at hand. `price` is what a card costs, in ACE_BUY minutes of what the block sells; `cool` the seconds it needs
  // between two goes.
  const TOOLS = {
    faust: { name: 'Goldfaust', unlock: 1, tap: true, text: 'Tipp im Kampf auf einen Bootleg und deine Faust schlägt zu, jeder Krit mit einer Schockwelle für die Nachbarn. Bei jeder 15er Combo kracht die große Goldfaust auf die Menge.' },
    shirt: { name: 'Mörser', unlock: 2, price: 0.8, cool: 11, text: 'Vom Dach der Base gehen zwei Salven schwerer Mörsergranaten hoch und schlagen mitten in der Menge ein.' },
    bombe: { name: 'Airdrop', unlock: 3, price: 1, cool: 15, text: 'Drei Stahlcontainer voller Ware stürzen dorthin, wo die meisten Bootlegs stehen, und lassen Krater zurück, die bremsen.' },
    drohne: { name: 'Blackout', unlock: 4, price: 1.2, cool: 21, text: 'Ein großer schwarzer Drohnenschwarm jagt Bootlegs mit Kettenblitzen und stürzt sich zum Schluss Drohne für Drohne in die Menge.' },
    fans: { name: 'Moshpit', unlock: 5, price: 1.4, cool: 24, text: 'Deine Fans reißen zwei Moshpits quer durch die Schlacht, und wen die Wall of Death erwischt, der bleibt liegen.' },
    rakete: { name: 'Raketenhagel', unlock: 6, price: 1.5, cool: 17, text: 'Vom Dach der Base steigen zwei Salven Lenkraketen auf, jede sucht sich ihren Bootleg und platzt in drei Bomben.' },
    birne: { name: 'Abrissbirne', unlock: 7, price: 1.6, cool: 18, text: 'Eine riesige Stahlkugel am Kran schwingt quer durch die Menge und wieder zurück und fegt jeden Bootleg davon.' },
    tornado: { name: 'Tornado', unlock: 8, price: 1.8, cool: 22, text: 'Zwei Superzellen ziehen durch die Schlacht, saugen die Bootlegs ein, wirbeln sie herum und schleudern sie davon.' },
    laser: { name: 'Orbitallaser', unlock: 9, price: 2, cool: 21, text: 'Zwei breite Strahlen aus dem All heften sich an die dichtesten Mengen und folgen ihnen, und was sie streifen, verglüht.' },
    beben: { name: 'Erdbeben', unlock: 10, price: 2.2, cool: 24, text: 'Der Boden reißt auf: Vier Schockwellen rollen über den Kampf, schleudern alle hoch und lassen sie lange benommen liegen.' }
  };
  const ACE_SLOTS = 3;
  const ACE_BUY = 20;
  const ACE_USES = 3;

  // Looks change at these levels; past the last one every doubling adds a star.
  const TIER_AT = [1, 5, 10, 20, 40, 80];
  const TIER_NAMES = ['Rookie', 'Kiez', 'Block', 'City', 'Gold', 'Legende'];
  const tierOf = level => {
    if (level >= 80) return 5 + Math.floor(Math.log2(level / 80));
    let tier = 0;
    TIER_AT.forEach((at, i) => {
      if (level >= at) tier = i;
    });
    return tier;
  };
  const tierName = level => {
    const tier = tierOf(level);
    return tier <= 5 ? TIER_NAMES[tier] : `Legende ${'★'.repeat(Math.min(5, tier - 5))}`;
  };

  // The Bootlegs: a crew selling fakes. They want the turf, the cash and, above all, the hustler.
  const GANG = {
    kid: { name: 'Läufer', hp: 20, speed: 1.55, dps: 9, size: 0.3, reward: 3, from: 1 },
    brute: { name: 'Schläger', hp: 75, speed: 0.82, dps: 25, size: 0.4, reward: 9, from: 3 },
    spray: { name: 'Sprayer', hp: 27, speed: 1.12, dps: 13, reach: 1.7, size: 0.32, reward: 6, from: 5, back: true },
    copy: { name: 'Kopierer', hp: 34, speed: 1.3, dps: 10, size: 0.33, reward: 5, from: 6, split: 2, text: 'Fällt er, stehen zwei Kopien von ihm da.' },
    shield: { name: 'Schildträger', hp: 52, speed: 0.95, dps: 16, size: 0.36, reward: 7, from: 8, armor: 0.5 },
    shouter: { name: 'Einpeitscher', hp: 44, speed: 1, dps: 8, size: 0.35, reward: 8, from: 9, back: true, aura: { r: 2.4, boost: 0.3 }, text: 'Brüllt durchs Megafon: Bootlegs um ihn herum rennen schneller und hauen härter zu.' },
    roller: { name: 'Rollerfahrer', hp: 22, speed: 2.5, dps: 11, size: 0.32, reward: 5, from: 11 },
    patch: { name: 'Flicker', hp: 38, speed: 1, dps: 6, size: 0.34, reward: 9, from: 13, back: true, heal: { r: 2.2, rate: 0.06 }, text: 'Spritzt verletzte Bootlegs um sich herum wieder fit. Erst ihn ausschalten!' },
    bomber: { name: 'Knallkopf', hp: 30, speed: 1, dps: 15, reach: 3, size: 0.33, reward: 8, from: 15, back: true, text: 'Wirft Kracher von weit hinten, auf alles, was ihm vor die Augen kommt.' },
    boss: { name: 'Pate', hp: 390, speed: 0.68, dps: 47, size: 0.56, reward: 60, boss: true, from: 5 }
  };
  // Each raid unlocks a little more: more Bootlegs, more kinds, more sides, more waves.
  const GANG_WEIGHT = { kid: 1, brute: 0.6, spray: 0.45, copy: 0.35, shield: 0.4, shouter: 0.2, roller: 0.4, patch: 0.2, bomber: 0.3 };
  // From Problem 8 on, every tenth Bootleg is an elite: bigger, three times as tough, harder hitting, four times the pay.
  const ELITE = { from: 8, share: 0.1, hp: 3, dps: 1.5, size: 1.2, pay: 4, name: 'Elite-Bootleg', text: 'Rot umrandet, dreimal so zäh und schlägt härter zu. Bringt vierfache Beute.' };
  // Problems come every twenty to thirty minutes of play.
  const RAID_GAP = { min: 20 * MINUTE, max: 30 * MINUTE };
  const raidGap = () => lerp(RAID_GAP.min, RAID_GAP.max, Math.random());
  // The last minute before a Problem shows as a countdown, and no Raid starts in it.
  const PROBLEM_NOTICE = MINUTE;
  // In that minute the gang can be paid off with half the cash on hand; then this Problem stays away until the next.
  const PROTECTION = 0.5;
  // Tips on other gangs' stashes: five at a time, and a used tip comes back after an hour.
  const HEIST_MAX = 5;
  const HEIST_GAP = HOUR;
  const HEIST_GANGS = ['Fake Kings', 'Copy Crew', 'Ramschbande', 'Die Nachmacher', 'Plagiat Posse', 'Kopierwerk', 'Billig-Brüder', 'Replika Gang', 'Fälscher-Front', 'Schwarzmarkt Süd'];
  // Back after this long away, the magnet collects everything that piled up, once.
  const MAGNET_AWAY = 15 * MINUTE;
  // Raids learn from how you do: a win makes the next gang tougher, a loss softer, within limits, so a Raid
  // stays a fight you can win.
  const HEIST_EDGE = { min: 0.5, max: 2.5, win: 1.1, loss: 1.25 };
  const FIRST_RAID = 6 * MINUTE;
  // Shows: started by hand, short and loud, then a short break.
  const SHOW_LEN = 30 * SECOND;
  const SHOW_GAP = 3 * MINUTE;

  // Hustler looks: everything comes from the seed, so no two hustlers look alike.
  const SKINS = ['#f2d0b4', '#e6b796', '#c99572', '#a87553', '#7c5238', '#5e3c28'];
  const HAIRS = ['#1a1a1c', '#2b2420', '#5a3b26', '#8a5a35', '#b9772f', '#e9e6df'];
  const CUTS = ['fade', 'locken', 'zoepfe', 'dutt', 'lang', 'buzz'];
  const HATS = ['cap', 'cap', 'kappe', 'muetze', 'bucket', 'keiner', 'keiner'];
  const BEARDS = ['voll', 'drei', 'kinn', 'keiner', 'keiner', 'keiner'];
  const TOPS = ['hoodie', 'hoodie', 'bomber', 'track', 'puffer'];
  const TOP_COLORS = ['#141415', '#2b2b2e', '#f4f3ef', '#c2330f', '#1f3a8a', '#2e6b4a', '#6a2c3a', '#d9b26a', '#baecb7'];
  const PANTS = ['#141415', '#2b3a55', '#55575c', '#6b5a43', '#d9d4c7'];
  const BRANDS = [SIGNAL, MINT_D, YELLOW, '#2f6fff', '#b44cff', '#ff4fa3'];
  const makeHustler = seed => {
    const rand = mulberry32(seed);
    return {
      skin: oneOf(rand, SKINS),
      hair: oneOf(rand, HAIRS),
      cut: oneOf(rand, CUTS),
      hat: oneOf(rand, HATS),
      beard: oneOf(rand, BEARDS),
      top: oneOf(rand, TOPS),
      color: oneOf(rand, TOP_COLORS),
      pants: oneOf(rand, PANTS),
      brand: oneOf(rand, BRANDS),
      shades: rand() < 0.35,
      chain: rand() < 0.65,
      hold: rand() < 0.5 ? 'phone' : 'cash',
      brows: rand() < 0.5,
      belly: 0.9 + rand() * 0.2
    };
  };

  // Every three hours three tasks out of these, each counted from where the round started.
  const TASK_COUNT = 3;
  const TASK_ROUND = 3 * HOUR;
  const TASKS = {
    heists: { text: n => (n > 1 ? `Gewinn ${n} Raids` : 'Gewinn einen Raid'), need: [1, 2], stat: () => S.stats.heists, open: () => heistOpen() },
    problems: { text: n => (n > 1 ? `Wehr ${n} Probleme ab` : 'Wehr ein Problem ab'), need: [1, 2], stat: () => S.stats.raids },
    kills: { text: n => `Schalte ${n} Bootlegs aus`, need: [10, 15, 25], stat: () => S.stats.kills },
    punches: { text: n => `Hau ${n}-mal mit der Goldfaust zu`, need: [20, 30, 50], stat: () => S.stats.punches },
    combo: { text: n => `Schaff eine ${n}er Combo`, need: [10, 15, 20] },
    aces: { text: n => `Setz ${n} Attacken ein`, need: [2, 3], stat: () => S.stats.aces, open: () => S.deck.length > 0 },
    talks: { text: n => `Quatsch ${n} Leute an`, need: [3, 5, 8], stat: () => S.stats.talks },
    upgrades: { text: n => `Verbessere ${n}-mal etwas`, need: [2, 3, 5], stat: () => S.stats.upgrades }
  };
  // What a task pays, as a share of what the block's shops hold when full: the more the Base has grown, the more.
  const TASK_PAY = 0.2;
  // The chest of the day, in full shops as well: a streak of days in a row opens bigger ones, the seventh is the big
  // one, then it starts over.
  const CHEST = [0.5, 0.6, 0.75, 0.9, 1.1, 1.3, 3];
  // Weekends bring an event, a different one each week.
  const EVENTS = [
    { id: 'beute', name: 'Doppel-Beute', mark: '2×', text: 'Raids und Probleme zahlen doppelt' },
    { id: 'gold', name: 'Goldene Bootlegs', mark: '5×', text: 'Jeder fünfte Bootleg ist aus Gold und zahlt das Fünffache' }
  ];

  // ---------------------------------------------------------------------------------------------
  // State lives in localStorage only, on the player's device. Promo and sales keep running while the page is closed;
  // a raid that falls due while nobody watches waits at the edge of the block.

  const freshSave = now => {
    const seed = newSeed();
    return {
      v: SAVE_VERSION,
      seed,
      name: '',
      founded: 0,
      cash: 120,
      fans: 0,
      earned: 0,
      land: ['-1,-1', '0,-1', '-1,0', '0,0'],
      items: [{ id: 1, type: 'hq', x: -2, y: -2, level: 1 }],
      nextId: 2,
      tools: Object.fromEntries(Object.keys(TOOLS).map(id => [id, TOOLS[id].tap ? 1 : 0])),
      deck: [],
      raidN: 1,
      raidAt: 0,
      buyerAt: 0,
      heists: [],
      heistBack: [],
      heistEdge: 1,
      move: null,
      moveN: 0,
      stats: { raids: 0, kills: 0, lost: 0, fallen: 0, stolen: 0, talks: 0, shows: 0, sold: 0, heists: 0, loot: 0, punches: 0, aces: 0, upgrades: 0 },
      over: null,
      magnet: false,
      revenge: null,
      daily: null,
      chest: { day: -1, streak: 0 },
      best: { streak: 0, fastest: 0, combo: 0, loot: 0 },
      streak: 0,
      met: [],
      last: now
    };
  };
  // A save is checked field by field before it is trusted: anything cut off, edited by hand or out of range
  // is mended from a fresh save, and a save without a single Base cannot be mended at all.
  const finite = (value, fallback) => (Number.isFinite(value) ? value : fallback);
  const isRecord = value => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
  const numbers = (value, base) => {
    const out = { ...base };
    if (isRecord(value)) Object.keys(base).forEach(key => {
      out[key] = Math.max(0, finite(value[key], base[key]));
    });
    return out;
  };
  // What building and levelling a thing cost: the nth of its kind costs four times the one before.
  const spentOn = (cost, nth, level) => cost * 4 ** nth + cost * 0.6 * Array.from({ length: level - 1 }, (_, i) => 1.34 ** (i + 1)).reduce((sum, step) => sum + step, 0);
  // Pays back things that are gone, the nth of each kind at what the nth cost.
  const refunds = () => {
    const built = {};
    let sum = 0;
    return {
      back: (type, cost, level) => {
        built[type] = (built[type] ?? -1) + 1;
        sum += spentOn(cost, built[type], level);
        return false;
      },
      total: () => sum
    };
  };
  // An older save is carried forward one version at a time instead of being thrown away. Each step reshapes what
  // its version changed and says what it pays back; cleanSave mends whatever is still missing after that.
  // Saves from version 9 and older belong to the village game that came before and start fresh.
  const HERO_SUCCESSORS = { titan: 'retro', ghost: 'capi', dj: 'maliq', queen: 'caruso' };
  const RETIRED = { druck: 60, stick: 500, digital: 6500, dtg: 350000, dtf: 15000000 };
  // The aces of version 13 with the Base level each opened at, and the price of each one that is gone since.
  const ACES_13 = { faust: 1, shirt: 2, blitz: 2, bombe: 3, bass: 3, drohne: 4, energy: 4, fans: 5, kette: 6, oel: 7, guard: 8, sani: 9 };
  const RETIRED_ACES = { blitz: 0.8, bass: 1, energy: 1.2, kette: 1.5, oel: 1.6, guard: 1.8, sani: 2 };
  // Up to version 14 aces went up to level 10, the first level cost ACE_BUY_14 shop minutes times the price and every
  // one after it 1.4 times the last; the Goldfaust came free at level 1.
  const ACE_TOP_14 = 10;
  const ACE_BUY_14 = 12;
  const ACE_STEP_14 = 1.4;
  const ACE_PRICES_14 = { faust: 0.6, shirt: 0.8, bombe: 1, drohne: 1.2, fans: 1.4, rakete: 1.5, birne: 1.6, tornado: 1.8, laser: 2, beben: 2.2 };
  // The team of version 14, gone since, with what each member cost.
  const TEAM_14 = { ball: 70, tuer: 240, doc: 380, farbe: 600, collector: 1000, sprinkler: 1500, hype: 2600, laser: 7000, hacker: 50000, boeller: 400000 };
  // What `level` levels of a version 14 ace cost in shop minutes, past the `free` ones it came with.
  const aceMinutes14 = (price, level, free) => (price * ACE_BUY_14 * (ACE_STEP_14 ** level - ACE_STEP_14 ** free)) / (ACE_STEP_14 - 1);
  const MIGRATIONS = {
    // Version 11 brought boosters and a drop multiplier, both gone again: nothing to carry over.
    10: saved => ({ saved, refund: 0 }),
    // The first heroes made way for the MIXEDOWN artists: whoever had one gets the artist who took his place.
    // MIXEDOWN produces everything now, so the Werke are gone and paid back. Boosters and drops are gone too.
    11: saved => {
      const paid = refunds();
      const items = saved.items
        .map(item => (isRecord(item) && HERO_SUCCESSORS[item.type] ? { ...item, type: HERO_SUCCESSORS[item.type] } : item))
        .filter(item => !isRecord(item) || !RETIRED[item.type] || paid.back(item.type, RETIRED[item.type], Math.max(1, Math.floor(finite(item.level, 1)))));
      const next = { ...saved, items };
      delete next.boosts;
      delete next.dropUntil;
      return { saved: next, refund: paid.total() };
    },
    // Aces used to come with the Base and level by being used; now they are cards bought in a shop. Every ace the
    // Base had opened is kept at the level it had, up to the new top, and the first three go into the deck.
    12: saved => {
      const hq = saved.items.find(item => isRecord(item) && item.type === 'hq');
      const base = hq ? Math.max(1, Math.floor(finite(hq.level, 1))) : 1;
      const old = isRecord(saved.tools) ? saved.tools : {};
      const tools = Object.fromEntries(Object.keys(ACES_13).map(id => [id, base >= ACES_13[id] ? clamp(Math.floor(finite(old[id], 0)), 1, ACE_TOP_14) : 0]));
      const deck = Object.keys(ACES_13).filter(id => id !== 'faust' && tools[id] > 0).slice(0, ACE_SLOTS);
      const next = { ...saved, tools, deck };
      delete next.toolXp;
      return { saved: next, refund: 0 };
    },
    // Only aces that strike are left. Every level of one that is gone comes back at what it costs in the shop; that
    // depends on what the block sells, so it is kept in shop minutes and paid out once the game has loaded.
    13: saved => {
      const old = isRecord(saved.tools) ? saved.tools : {};
      const minutes = Object.keys(RETIRED_ACES).reduce((sum, id) => {
        const level = clamp(Math.floor(finite(old[id], 0)), 0, ACE_TOP_14);
        return sum + aceMinutes14(RETIRED_ACES[id], level, 0);
      }, 0);
      const tools = Object.fromEntries(Object.entries(old).filter(([id]) => !RETIRED_ACES[id]));
      const deck = Array.isArray(saved.deck) ? saved.deck.filter(id => !RETIRED_ACES[id]) : [];
      return { saved: { ...saved, tools, deck, aceBack: minutes }, refund: 0 };
    },
    // The team is gone, only the heroes fight now, and every member comes back at what it cost. Aces have no levels
    // any more: one bought is kept for good, and whatever its levels cost beyond today's price comes back in shop minutes.
    14: saved => {
      const paid = refunds();
      const items = saved.items.filter(item => !isRecord(item) || !TEAM_14[item.type] || paid.back(item.type, TEAM_14[item.type], Math.max(1, Math.floor(finite(item.level, 1)))));
      const old = isRecord(saved.tools) ? saved.tools : {};
      let minutes = Math.max(0, finite(saved.aceBack, 0));
      const tools = Object.fromEntries(Object.keys(ACE_PRICES_14).map(id => {
        const price = ACE_PRICES_14[id];
        if (id === 'faust') {
          minutes += aceMinutes14(price, clamp(Math.floor(finite(old[id], 1)), 1, ACE_TOP_14), 1);
          return [id, 1];
        }
        const level = clamp(Math.floor(finite(old[id], 0)), 0, ACE_TOP_14);
        if (level) minutes += Math.max(0, aceMinutes14(price, level, 0) - price * ACE_BUY);
        return [id, level ? 1 : 0];
      }));
      return { saved: { ...saved, items, tools, aceBack: minutes }, refund: paid.total() };
    }
  };
  const migrate = saved => {
    let current = saved;
    let refund = 0;
    while (isRecord(current) && Array.isArray(current.items) && MIGRATIONS[current.v]) {
      const step = MIGRATIONS[current.v](current);
      refund += step.refund;
      current = { ...step.saved, v: current.v + 1 };
    }
    return { saved: current, refund };
  };
  // What a save just loaded paid back, for the banner on the way in.
  let refunded = 0;
  // A name the player typed: single spaces, trimmed, short enough for the card.
  const NAME_MAX = 18;
  const cleanName = value => (typeof value === 'string' ? value.replace(/\s+/g, ' ').trim().slice(0, NAME_MAX) : '');
  // The round's tasks as they were dealt; a task of a kind the game no longer knows makes the round deal again.
  const cleanDaily = daily => {
    if (!isRecord(daily) || !Number.isFinite(daily.round) || !Array.isArray(daily.tasks)) return null;
    const tasks = daily.tasks.slice(0, TASK_COUNT);
    if (!tasks.every(task => isRecord(task) && TASKS[task.kind] && Number.isFinite(task.need) && task.need > 0 && Number.isFinite(task.from))) return null;
    return {
      round: daily.round,
      tasks: tasks.map(({ kind, need, from, done }) => ({ kind, need, from, done: done === true })),
      combo: Math.max(0, finite(daily.combo, 0))
    };
  };
  // The heroes are one of each: a second of a kind goes, and what it cost comes back on top of what a migration paid.
  const cleanSave = (saved, carried = 0) => {
    if (!isRecord(saved) || saved.v !== SAVE_VERSION || !Array.isArray(saved.items)) return null;
    const fresh = freshSave(Date.now());
    const ids = new Set();
    const paid = refunds();
    const valid = saved.items.filter(item => {
      if (!isRecord(item) || !TYPES[item.type] || !Number.isSafeInteger(item.id) || ids.has(item.id) || !Number.isFinite(item.x) || !Number.isFinite(item.y)) return false;
      ids.add(item.id);
      item.level = Math.max(1, Math.floor(finite(item.level, 1)));
      if (item.stock !== undefined) item.stock = Math.max(0, finite(item.stock, 0));
      if (item.paid !== undefined) item.paid = Math.max(0, finite(item.paid, 0));
      return true;
    });
    const best = {};
    valid.forEach(item => {
      if (TYPES[item.type].kind === 'crew' && (!best[item.type] || item.level > best[item.type].level)) best[item.type] = item;
    });
    const items = valid.filter(item => TYPES[item.type].kind !== 'crew' || best[item.type] === item || paid.back(item.type, TYPES[item.type].cost, item.level));
    if (items.filter(item => item.type === 'hq').length !== 1) return null;
    const clean = { ...fresh, ...saved, items };
    delete clean.heistAt;
    delete clean.pass;
    delete clean.looks;
    ['seed', 'founded', 'raidAt', 'buyerAt', 'last'].forEach(key => {
      clean[key] = finite(saved[key], fresh[key]);
    });
    ['cash', 'fans', 'earned'].forEach(key => {
      clean[key] = Math.max(0, finite(saved[key], fresh[key]));
    });
    clean.heistEdge = clamp(finite(saved.heistEdge, 1), HEIST_EDGE.min, HEIST_EDGE.max);
    refunded = Math.round(carried + paid.total());
    clean.cash += refunded;
    clean.name = cleanName(saved.name);
    clean.magnet = saved.magnet === true;
    clean.revenge = isRecord(saved.revenge) && HEIST_GANGS.includes(saved.revenge.gang) && Number.isFinite(saved.revenge.at) ? { gang: saved.revenge.gang, at: saved.revenge.at } : null;
    clean.raidN = Math.max(1, Math.floor(finite(saved.raidN, 1)));
    clean.moveN = Math.max(0, Math.floor(finite(saved.moveN, 0)));
    clean.nextId = Math.max(Math.floor(finite(saved.nextId, 1)), ...[...ids].map(id => id + 1));
    clean.land = Array.isArray(saved.land) ? [...new Set(saved.land.filter(key => typeof key === 'string' && /^-?\d{1,4},-?\d{1,4}$/.test(key)))] : fresh.land;
    const tools = isRecord(saved.tools) ? saved.tools : {};
    clean.tools = Object.fromEntries(Object.keys(TOOLS).map(id => [id, clamp(Math.floor(finite(tools[id], 0)), TOOLS[id].tap ? 1 : 0, 1)]));
    clean.deck = [...new Set(Array.isArray(saved.deck) ? saved.deck : [])].filter(id => TOOLS[id] && !TOOLS[id].tap && clean.tools[id] > 0).slice(0, ACE_SLOTS);
    const aceBack = Math.max(0, finite(saved.aceBack, 0));
    if (aceBack) clean.aceBack = aceBack;
    else delete clean.aceBack;
    const tips = new Set();
    clean.heists = (Array.isArray(saved.heists) ? saved.heists : []).filter(h => {
      if (!isRecord(h) || !Number.isSafeInteger(h.id) || tips.has(h.id) || ![0, 1, 2, 3].includes(h.size) || !Number.isFinite(h.seed) || !Number.isSafeInteger(h.n) || h.n < 1) return false;
      tips.add(h.id);
      return true;
    }).slice(0, HEIST_MAX).map(({ id, size, seed, n }) => ({ id, size, seed, n }));
    clean.heistBack = (Array.isArray(saved.heistBack) ? saved.heistBack : []).filter(Number.isFinite).slice(0, HEIST_MAX - clean.heists.length);
    clean.stats = numbers(saved.stats, fresh.stats);
    clean.move = isRecord(saved.move) && ['n', 'reward', 'target'].every(key => Number.isFinite(saved.move[key])) ? saved.move : null;
    clean.daily = cleanDaily(saved.daily);
    clean.chest = isRecord(saved.chest) ? { day: Math.floor(finite(saved.chest.day, -1)), streak: Math.max(0, Math.floor(finite(saved.chest.streak, 0))) } : fresh.chest;
    clean.best = numbers(saved.best, fresh.best);
    clean.streak = Math.max(0, Math.floor(finite(saved.streak, 0)));
    clean.met = [...new Set(Array.isArray(saved.met) ? saved.met.filter(key => key === 'elite' || (GANG[key] && GANG[key].text)) : [])];
    clean.over = isRecord(saved.over) ? numbers(saved.over, { reign: 0, raids: 0, kills: 0, earned: 0, hq: 1, at: 0 }) : null;
    return clean;
  };
  const stored = key => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  };
  const store = (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Private mode or a full disk: the game keeps running for this visit.
    }
  };
  const parseSave = raw => {
    try {
      const { saved, refund } = migrate(JSON.parse(raw));
      return cleanSave(saved, refund);
    } catch {
      return null;
    }
  };
  // A save is never thrown away. The one that loaded last stays behind as a backup; one that does not load is put
  // aside before anything could write over it, and the backup takes its place.
  const BACKUP_KEY = `${STORAGE_KEY}:backup`;
  const BROKEN_KEY = `${STORAGE_KEY}:broken`;
  // A save from a newer version of the game, left by a newer page: this one cannot read it and must not touch it.
  let newerSave = false;
  const readSave = () => {
    const raw = stored(STORAGE_KEY);
    if (raw === null) return null;
    const loaded = parseSave(raw);
    if (loaded) {
      store(BACKUP_KEY, raw);
      return loaded;
    }
    try {
      newerSave = JSON.parse(raw).v > SAVE_VERSION;
    } catch {
      newerSave = false;
    }
    if (newerSave) return null;
    store(BROKEN_KEY, raw);
    const backup = stored(BACKUP_KEY);
    return backup === null ? null : parseSave(backup);
  };
  let S = readSave() || freshSave(Date.now());
  // Asks the browser to keep the save even when the disk runs full, once a visit and only once there is a block.
  let keepAsked = false;
  const askToKeep = () => {
    const disk = window.navigator.storage;
    if (keepAsked || !S.founded || !disk || !disk.persist) return;
    keepAsked = true;
    disk.persisted().then(kept => kept || disk.persist()).catch(() => {});
  };
  // Damage belongs to a raid in progress; a reload starts that raid over. Ruins stay until they are repaired.
  const RUNTIME = new Set(['crew', 'hp', 'shake', 'pop', 'rebuild', 'fired']);
  // Two tabs on one save would overwrite each other, so the tab in use owns the save. One left in the
  // background stops quietly and picks up the latest save the moment it is used again.
  const OWNER_KEY = `${STORAGE_KEY}:tab`;
  const TAB = `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
  const claimSave = () => {
    try {
      localStorage.setItem(OWNER_KEY, TAB);
    } catch {
      // Without storage there is nothing to share between tabs.
    }
  };
  const ownsSave = () => {
    try {
      const owner = localStorage.getItem(OWNER_KEY);
      return !owner || owner === TAB;
    } catch {
      return true;
    }
  };
  let elsewhere = false;
  // A Raid in progress: the crew is on another gang's ground. It is never saved; a reload brings everyone home.
  let heist = null;
  // The trip to a Raid and back behind a wall of clouds, while it lasts (see travelTo).
  let travel = null;
  const persist = () => {
    if (elsewhere || newerSave) return;
    if (!ownsSave()) {
      elsewhere = true;
      return;
    }
    S.last = Date.now();
    store(STORAGE_KEY, JSON.stringify(S, (key, value) => (RUNTIME.has(key) ? undefined : value)));
  };
  let hustlerLook = makeHustler(S.seed);
  const founded = () => S.founded > 0 && !S.over;

  // Economy lookups
  const itemsOf = type => S.items.filter(item => item.type === type);
  const hq = () => S.items.find(item => item.type === 'hq');
  const hqLevel = () => (hq() ? hq().level : 1);
  // Buildings stay a few levels within reach of the Base, so growing it is what opens the next ones.
  const levelCap = baseLevel => 2 + baseLevel * 3;
  const baseFor = level => Math.max(hqLevel() + 1, Math.ceil((level - 2) / 3));
  // Heroes level as far as the cash goes; everything else up to what the Base allows.
  const maxLevel = type => (type === 'hq' || TYPES[type].hero ? Infinity : levelCap(hqLevel()));
  const unlocked = type => hqLevel() >= TYPES[type].unlock;
  // Buildings come in more copies as the Base grows; the heroes are one of each.
  const limitOf = type => {
    const info = TYPES[type];
    if (type === 'hq') return 1;
    if (!unlocked(type)) return 0;
    if (info.kind === 'crew') return 1;
    return 1 + Math.floor((hqLevel() - info.unlock) * info.per + 1e-9);
  };
  // Every level costs a third more while it adds less and less, so growth stays quick early and slows down later.
  // The Base sets the pace: cheap at first, steeper through the mid game, then twelve times per level for good.
  const HQ_COST = [200, 600, 2400, 11000, 70000, 500000, 4500000, 50000000, 700000000];
  // Heroes all cost the same, and each one six times the last: the first is within reach before the first Problem,
  // the last one waits for a big Base.
  const HERO_PRICE = 150;
  const heroCount = () => S.items.filter(item => TYPES[item.type].hero).length;
  const buildCost = type => Math.round(TYPES[type].hero ? HERO_PRICE * 6 ** heroCount() : TYPES[type].cost * 4 ** itemsOf(type).length);
  const upgradeCost = item => {
    if (item.type === 'hq') return Math.round(HQ_COST[Math.min(item.level, HQ_COST.length) - 1] * 12 ** Math.max(0, item.level - HQ_COST.length));
    return Math.round(TYPES[item.type].cost * 0.6 * 1.34 ** item.level);
  };
  // A ruin stands still until it is paid for: a quarter of its next level, but never more than
  // two minutes of what the block sells (four for the base). Then it rebuilds.
  const repairCost = item => Math.round(Math.min(upgradeCost(item) * 0.25, (30 + totalCashRate()) * (item.type === 'hq' ? 4 : 2)));
  const REBUILD = 6;
  // Every tenth level doubles sales and promo.
  const scaleOf = level => level * 2 ** Math.floor(level / 10);
  const working = item => !item.ruin && !item.rebuild;
  // The bigger the Base, the bigger the name: every Base level makes each piece of merch worth more.
  const BRAND = [1, 1.4, 2, 3, 4.8, 8, 15, 30, 70, 150, 230, 310, 390, 490, 600];
  const brandOf = level => (level <= BRAND.length ? BRAND[level - 1] : BRAND[BRAND.length - 1] * 1.2 ** (level - BRAND.length));
  const payoutAt = level => PAYOUT * brandOf(level);
  const payout = () => payoutAt(hqLevel());
  const showLive = (item, now = Date.now()) => (item.showUntil || 0) > now;
  // Promo: fans per minute; a show multiplies the stage for as long as it plays.
  const fansRateOf = (type, level) => (TYPES[type].rate || 0) * scaleOf(level);
  const fansNow = item => fansRateOf(item.type, item.level) * (showLive(item) ? 8 : 1);
  // Sales: orders per minute from people walking by and from your fans.
  // Fans count with their square root: a big following still sells more, but no longer runs away.
  const ordersOf = (type, level) => (TYPES[type].walk + Math.sqrt(S.fans) * TYPES[type].conv) * scaleOf(level);
  const cashRateOf = (type, level) => ordersOf(type, level) * payout();
  const capMinutes = () => 50 + hqLevel() * 10;
  const capOf = item => (TYPES[item.type].kind === 'promo' ? fansRateOf(item.type, item.level) : cashRateOf(item.type, item.level)) * capMinutes();
  // The heroes are one of each, so each grows with the Base as well: every Base level past its unlock adds half again
  // to its life and damage.
  const rankOf = type => (TYPES[type].kind === 'crew' ? 1 + Math.max(0, hqLevel() - TYPES[type].unlock) * 0.5 : 1);
  const hpOf = (type, level) => (TYPES[type].hp || 0) * 1.13 ** (level - 1) * (type === 'hq' ? 1 + (level - 1) * 0.1 : 1) * rankOf(type);
  const dmgOf = (type, level) => (TYPES[type].dmg || TYPES[type].dps || 0) * 1.12 ** (level - 1) * (1 + 0.25 * Math.floor(level / 10)) * rankOf(type);
  const rangeOf = (type, level) => (TYPES[type].range || 0) + Math.min(1, (level - 1) * 0.03);
  const hustlerMaxHp = () => 100 * 1.12 ** (hqLevel() - 1);
  const totalCashRate = () => S.items.reduce((sum, item) => sum + (TYPES[item.type].kind === 'sale' && working(item) ? cashRateOf(item.type, item.level) : 0), 0);
  const nice = value => {
    const step = 10 ** Math.max(0, Math.floor(Math.log10(value)) - 1);
    return Math.round(value / step) * step;
  };
  const totalFansRate = () => S.items.reduce((sum, item) => sum + (TYPES[item.type].kind === 'promo' && working(item) ? fansNow(item) : 0), 0);
  // A tap on a stand sells one piece by hand; a tap on promo wins a handful of fans.
  const hustleCash = item => payout() * (1 + (item.level - 1) * 0.05);
  // Someone on the street who wants merch is rare, and pays big when you talk to them yourself: about two
  // minutes of what your block makes, never less than twenty pieces sold by hand.
  const streetPrice = () => nice(Math.max(payout() * 20, totalCashRate() * 2));
  // Aces are bought by the minute of what the block sells, at least three pieces a minute while it is small:
  // a card costs its price in ACE_BUY minutes of that, once.
  const salesPace = () => Math.max(payout() * 3, totalCashRate());
  const aceCost = id => (hasAce(id) ? 0 : nice(salesPace() * ACE_BUY * TOOLS[id].price));
  // The Goldfaust is the player's own fist and free: a hit takes a thirteenth off a plain Läufer, a long combo twice
  // that, and the big one at its end knocks the crowd around it back. It backs the heroes up, it does not do their job.
  const punchDamage = () => 1.5 * hpScale(raid ? raid.n : S.raidN) * ((raid && raid.tough) || 1);
  const hustleFans = item => Math.max(1, Math.round(fansRateOf(item.type, item.level) * 0.08));
  // An ace counts once the Base has opened it and it is bought; the Goldfaust is the player's own from the start.
  const aceOpen = id => hqLevel() >= TOOLS[id].unlock;
  const hasAce = id => Boolean(TOOLS[id].tap || (aceOpen(id) && S.tools[id]));
  // Aces keep pace with the Bootlegs they face, toughness and all, so they hit just as hard at every stage of the
  // game: light Bootlegs go down in droves, the heavy ones and the bosses are left battered for the heroes.
  // On a Raid the gang stands packed round its stash, where every ace hits many at once, so there they hit softer.
  const ACE_POWER = 0.4;
  const ACE_RAID = 0.3;
  const aceScale = () => ACE_POWER * (heist ? ACE_RAID : 1) * hpScale(raid ? raid.n : S.raidN) * ((raid && raid.tough) || 1);
  const shirtDamage = () => 12 * aceScale();
  const SHIRT_COUNT = 12;
  const bombeDamage = () => 150 * aceScale();
  const BOMBE_RADIUS = 2.6;
  const droneDamage = () => 18 * aceScale();
  const DRONE_TIME = 11;
  const fansDamage = () => 48 * aceScale();
  // Raketenhagel: one rocket for every Bootleg it can find.
  const rocketDamage = () => 14 * aceScale();
  const ROCKET_COUNT = 14;
  // Abrissbirne: one heavy blow for everyone on the ball's path.
  const wreckDamage = () => 75 * aceScale();
  const WRECK_RADIUS = 1.6;
  // Tornado: it burns everyone it holds a little every second and throws them out at the end.
  const twisterDamage = () => 9 * aceScale();
  const TWISTER_RADIUS = 2.8;
  const TWISTER_TIME = 7;
  // Orbitallaser: what stands in the beam burns, every second it stays in.
  const laserDamage = () => 44 * aceScale();
  const LASER_RADIUS = 1.05;
  // Erdbeben: every wave hits everyone it reaches.
  const quakeDamage = () => 32 * aceScale();
  const QUAKE_STUN = 2.4;

  // Promo fills up with fans and stands with cash until someone collects; the On-Demand Shop pays straight in.
  // Long absences count at the plain rate: no show runs while nobody watches.
  const produce = seconds => {
    const away = seconds > 5;
    S.items.forEach(item => {
      const t = TYPES[item.type];
      if (!working(item)) return;
      if (t.kind === 'promo') {
        const rate = away ? fansRateOf(item.type, item.level) : fansNow(item);
        item.stock = Math.min(capOf(item), (item.stock || 0) + (rate / 60) * seconds);
      } else if (t.kind === 'sale') {
        const made = (cashRateOf(item.type, item.level) / 60) * seconds;
        S.stats.sold += (ordersOf(item.type, item.level) / 60) * seconds;
        if (t.online) {
          S.cash += made;
          S.earned += made;
          item.paid = (item.paid || 0) + made;
        } else item.stock = Math.min(capOf(item), (item.stock || 0) + made);
      }
    });
  };

  // Moves: there is always a next goal with a reward. The first ones teach the game, then they keep coming.
  const countOf = type => itemsOf(type).length;
  const STARTER_MOVES = [
    { text: 'Bau einen Corner-Stand', test: () => countOf('stand') >= 1 },
    { text: 'Kleb Wildplakate', test: () => countOf('plakat') >= 1 },
    { text: 'Quatsch 3 Leute auf der Straße an', goal: () => 3, value: () => S.stats.talks },
    { text: 'Hol dir 40 Fans', goal: () => 40, value: () => S.fans },
    { text: 'Bring die Wildplakate auf Stufe 2', goal: () => 2, value: () => Math.max(0, ...itemsOf('plakat').map(item => item.level)) },
    { text: 'Bring den Corner-Stand auf Stufe 3', goal: () => 3, value: () => Math.max(0, ...itemsOf('stand').map(item => item.level)) },
    { text: 'Bring die Base auf Stufe 2', goal: () => 2, value: () => hqLevel() },
    { text: 'Eröffne den On-Demand Shop', test: () => countOf('shop') >= 1 },
    { text: 'Hol dir deinen ersten Helden', test: () => heroCount() >= 1 },
    { text: 'Lös dein erstes Problem', goal: () => 1, value: () => S.stats.raids }
  ];
  const makeMove = () => {
    const n = S.moveN;
    const reward = Math.round(Math.max(60 + n * 25, totalCashRate() * 3 + payout() * 8));
    if (n < STARTER_MOVES.length) return { n, reward };
    // Past the starters the goals rotate and grow from where you stand.
    const pick = (n - STARTER_MOVES.length) % 4;
    const own = S.items.filter(item => item.type !== 'hq');
    if (pick === 0) return { n, reward, kind: 'fans', from: S.fans, target: Math.ceil((S.fans * 1.6 + 80) / 10) * 10 };
    if (pick === 1) return { n, reward, kind: 'earn', from: S.earned, target: Math.ceil((totalCashRate() * 6 + payout() * 30) / 10) * 10 };
    if (pick === 2 && own.length) {
      const low = own.reduce((a, b) => (b.level < a.level ? b : a));
      if (low.level < maxLevel(low.type)) return { n, reward, kind: 'level', id: low.id, target: Math.min(maxLevel(low.type), low.level + 2) };
    }
    if (pick === 3) return { n, reward, kind: 'talks', from: S.stats.talks, target: 5 };
    return { n, reward, kind: 'base', target: hqLevel() + 1 };
  };
  const moveInfo = move => {
    if (move.n < STARTER_MOVES.length) {
      const m = STARTER_MOVES[move.n];
      if (m.test) return { text: m.text, have: m.test() ? 1 : 0, need: 1 };
      return { text: m.text, have: Math.min(m.goal(), m.value()), need: m.goal() };
    }
    if (move.kind === 'fans') return { text: `Komm auf ${fmt(move.target)} Fans`, have: Math.min(move.target, S.fans), need: move.target };
    if (move.kind === 'earn') return { text: `Mach ${fmt(move.target)} Cash`, have: Math.min(move.target, S.earned - move.from), need: move.target };
    if (move.kind === 'level') {
      const item = S.items.find(it => it.id === move.id);
      if (!item) return { text: 'Bau etwas Neues', have: 1, need: 1 };
      return { text: `${TYPES[item.type].name} auf Stufe ${move.target}`, have: Math.min(move.target, item.level), need: move.target };
    }
    if (move.kind === 'talks') return { text: 'Quatsch 5 Leute an', have: Math.min(5, S.stats.talks - move.from), need: 5 };
    return { text: `Bring die Base auf Stufe ${move.target}`, have: Math.min(move.target, hqLevel()), need: move.target };
  };
  const currentMove = () => {
    if (!S.move || S.move.n !== S.moveN) S.move = makeMove();
    return S.move;
  };

  // ---------------------------------------------------------------------------------------------
  // Sound: soft, warm one-shots, only when the section enables them and after the visitor touched something.
  // Every voice is rendered once into a buffer: wooden mallets, bubbles, felt thumps and airy noise, no
  // raw beeps. Pitched sounds share one pentatonic scale, money and knockouts climb it note by note while
  // they keep coming, and busy fight noise stays far below what the player does.

  let audio = null;
  let bus = null;
  let echo = null;
  const soundOn = () => root.dataset.sound === 'on';
  // Browsers only let audio start after a gesture, so the context is created and woken in one and nowhere else.
  const unlockAudio = () => {
    if (!soundOn()) return;
    if (audio) {
      if (audio.state === 'suspended') audio.resume();
      return;
    }
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    audio = new AudioContext();
    const squeeze = audio.createDynamicsCompressor();
    squeeze.threshold.value = -16;
    squeeze.knee.value = 12;
    squeeze.ratio.value = 4;
    squeeze.attack.value = 0.004;
    squeeze.release.value = 0.2;
    const soften = audio.createBiquadFilter();
    soften.type = 'lowpass';
    soften.frequency.value = 7500;
    bus = audio.createGain();
    bus.gain.value = 0.7;
    bus.connect(soften).connect(squeeze).connect(audio.destination);
    echo = audio.createGain();
    const delay = audio.createDelay(1);
    delay.delayTime.value = 0.16;
    const damp = audio.createBiquadFilter();
    damp.type = 'lowpass';
    damp.frequency.value = 2200;
    const feedback = audio.createGain();
    feedback.gain.value = 0.28;
    echo.connect(delay).connect(damp).connect(feedback).connect(delay);
    damp.connect(bus);
  };
  document.addEventListener('pointerup', unlockAudio, true);
  document.addEventListener('click', unlockAudio, true);
  // Escape is no gesture to the browser.
  document.addEventListener('keydown', event => event.key !== 'Escape' && unlockAudio(), true);
  // C major pentatonic, step 0 is C5; negative steps go down.
  const pitch = step => 523.25 * 2 ** ((Math.floor(step / 5) * 12 + [0, 2, 4, 7, 9][((step % 5) + 5) % 5]) / 12);
  const vary = (amount = 0.04) => 1 + (Math.random() - 0.5) * amount;
  const decay = (t, k) => Math.exp(-t * k);
  const voices = {};
  const voice = (key, seconds, fill) => {
    if (!voices[key]) {
      const rate = audio.sampleRate;
      const buf = audio.createBuffer(1, Math.ceil(seconds * rate), rate);
      fill(buf.getChannelData(0), rate);
      voices[key] = buf;
    }
    return voices[key];
  };
  // Noise shaped by two one-pole filters: `lo` keeps what is below, `hi` takes away what is below that.
  const shaped = (d, rate, { lo = 0.5, hi = 0, amp }) => {
    let a = 0;
    let b = 0;
    for (let i = 0; i < d.length; i += 1) {
      const t = i / rate;
      const k = typeof lo === 'function' ? lo(t) : lo;
      a += k * (Math.random() * 2 - 1 - a);
      b += hi * (a - b);
      d[i] += (a - b) * amp(t);
    }
  };
  const glide = (d, rate, freq, amp, wave = Math.sin) => {
    let phase = 0;
    for (let i = 0; i < d.length; i += 1) {
      const t = i / rate;
      phase += (TAU * freq(t)) / rate;
      d[i] += wave(phase) * amp(t);
    }
  };
  const VOICES = {
    // A soft mallet on a wooden bar, rendered at C5 and played at any note.
    mallet: () => voice('mallet', 0.9, (d, rate) => {
      for (let i = 0; i < d.length; i += 1) {
        const t = i / rate;
        const f = 523.25;
        d[i] = (Math.sin(TAU * f * t) * decay(t, 5) + 0.3 * Math.sin(TAU * f * 3.98 * t) * decay(t, 28) + 0.07 * Math.sin(TAU * f * 9.1 * t) * decay(t, 80)) * Math.min(1, t * 700) * 0.8;
      }
    }),
    // A small bell for sparkle on top, also at C5.
    bell: () => voice('bell', 1.4, (d, rate) => {
      for (let i = 0; i < d.length; i += 1) {
        const t = i / rate;
        const f = 523.25;
        d[i] = (Math.sin(TAU * f * t) * decay(t, 3) + 0.4 * Math.sin(TAU * f * 2.76 * t) * decay(t, 8) + 0.18 * Math.sin(TAU * f * 5.4 * t) * decay(t, 16)) * Math.min(1, t * 1500) * 0.6;
      }
    }),
    // A bubble popping: a quick upward chirp.
    pop: () => voice('pop', 0.12, (d, rate) => glide(d, rate, t => 240 * (1 + 3.2 * Math.min(1, t / 0.05)), t => decay(t, 42) * Math.min(1, t * 1500))),
    // A wood block, for the interface.
    tock: () => voice('tock', 0.08, (d, rate) => {
      for (let i = 0; i < d.length; i += 1) {
        const t = i / rate;
        d[i] = (Math.sin(TAU * 880 * t) * decay(t, 70) + 0.45 * Math.sin(TAU * 1390 * t) * decay(t, 110)) * Math.min(1, t * 3000) * 0.8;
      }
    }),
    // A felt thump, low and round.
    thump: () => voice('thump', 0.4, (d, rate) => {
      glide(d, rate, t => 48 + 110 * decay(t, 22), t => decay(t, 11));
      shaped(d, rate, { lo: 0.12, amp: t => decay(t, 110) * 0.6 });
      for (let i = 0; i < d.length; i += 1) d[i] = Math.tanh(d[i] * 1.4) * 0.85;
    }),
    // A punch: a short low knock with a smack on top.
    punch: () => voice('punch', 0.22, (d, rate) => {
      glide(d, rate, t => 62 + 170 * decay(t, 38), t => decay(t, 20));
      shaped(d, rate, { lo: 0.35, hi: 0.04, amp: t => decay(t, 65) * 0.9 });
      for (let i = 0; i < d.length; i += 1) d[i] = Math.tanh(d[i] * 1.6) * 0.8;
    }),
    // A slap: a short crack of bright noise with a tiny click, nothing low underneath.
    slap: () => voice('slap', 0.07, (d, rate) => {
      shaped(d, rate, { lo: 0.7, hi: 0.3, amp: t => decay(t, 90) * Math.min(1, t * 4000) * 0.9 });
      glide(d, rate, t => 1400 - 500 * Math.min(1, t / 0.04), t => decay(t, 70) * 0.25);
    }),
    whoosh: () => voice('whoosh', 0.3, (d, rate) => shaped(d, rate, { lo: t => 0.03 + t * 0.6, hi: 0.02, amp: t => Math.sin(Math.PI * Math.min(1, t / 0.3)) ** 2 * 0.9 })),
    psst: () => voice('psst', 0.14, (d, rate) => shaped(d, rate, { lo: 0.9, hi: 0.35, amp: t => decay(t, 24) * Math.min(1, t * 400) * 0.7 })),
    boom: () => voice('boom', 1, (d, rate) => {
      shaped(d, rate, { lo: 0.035, amp: t => decay(t, 4.5) * Math.min(1, t * 600) * 2.2 });
      glide(d, rate, t => 38 + 40 * decay(t, 9), t => decay(t, 5) * 0.8);
      for (let i = 0; i < d.length; i += 1) d[i] = Math.tanh(d[i] * 1.3) * 0.85;
    }),
    riser: () => voice('riser', 0.5, (d, rate) => {
      shaped(d, rate, { lo: t => 0.01 + (t / 0.5) ** 2 * 0.5, hi: 0.01, amp: t => (t / 0.5) ** 2 * 0.8 });
      glide(d, rate, t => 160 * 2 ** ((t / 0.5) * 2), t => (t / 0.5) ** 2 * 0.15);
    }),
    // A crowd going up: soft band of noise that swells and breathes.
    cheer: () => voice('cheer', 1.6, (d, rate) => {
      const wobble = [3.1, 4.7, 6.3].map(f => [f, Math.random() * TAU]);
      shaped(d, rate, { lo: 0.18, hi: 0.03, amp: t => Math.min(1, t / 0.3) * Math.min(1, (1.6 - t) / 0.7) * (0.75 + wobble.reduce((m, [f, ph]) => m + Math.sin(TAU * f * t + ph) * 0.08, 0)) * 0.7 });
    }),
    whistle: () => voice('whistle', 0.6, (d, rate) => glide(d, rate, t => 1700 - 1800 * t + Math.sin(TAU * 9 * t) * 25, t => Math.sin(Math.PI * Math.min(1, t / 0.6)) * 0.25)),
    // A like: two soft sine notes a fifth apart with a slow attack, so it never clicks.
    like: () => voice('like', 0.35, (d, rate) => {
      for (let i = 0; i < d.length; i += 1) {
        const t = i / rate;
        d[i] = (Math.sin(TAU * 1318.5 * t) + 0.45 * Math.sin(TAU * 1977.8 * t) * decay(t, 10)) * decay(t, 16) * Math.min(1, t * 250) * 0.6;
      }
    }),
    // A LEGO stud: a near pure tone high up at 4.25 kHz with a slow shimmer from a twin a little above,
    // a tick an octave over it as it lands, and a short ring.
    stud: () => voice('stud', 0.4, (d, rate) => {
      for (let i = 0; i < d.length; i += 1) {
        const t = i / rate;
        const ring = (Math.sin(TAU * 4250 * t) + 0.14 * Math.sin(TAU * 4280 * t)) * decay(t, 11) * Math.min(1, t * 330);
        d[i] = (ring + 0.25 * Math.sin(TAU * 9150 * t) * decay(t, 140)) * 0.7;
      }
    }),
    // A hammer on a nail: a short bright ring of metal with a tick in front, nothing low.
    tink: () => voice('tink', 0.25, (d, rate) => {
      [[2350, 30, 0.5], [5640, 48, 0.22], [9630, 80, 0.1]].forEach(([f, fade, amp]) => glide(d, rate, () => f, t => decay(t, fade) * Math.min(1, t * 6000) * amp));
      shaped(d, rate, { lo: 0.9, hi: 0.45, amp: t => decay(t, 400) * 0.6 });
    }),
    // An electric zap: bright crackle with a falling whine.
    zap: () => voice('zap', 0.12, (d, rate) => {
      shaped(d, rate, { lo: 0.95, hi: 0.6, amp: t => decay(t, 40) * Math.min(1, t * 3000) * (0.6 + 0.4 * Math.sign(Math.sin(TAU * 90 * t))) * 0.7 });
      glide(d, rate, t => 3200 - 1800 * Math.min(1, t / 0.1), t => decay(t, 30) * 0.18, phase => Math.sign(Math.sin(phase)) * 0.6);
    }),
    // Rotors: a soft high hum that wobbles, no low end.
    buzz: () => voice('buzz', 0.7, (d, rate) => {
      glide(d, rate, t => 520 + Math.sin(TAU * 7 * t) * 30, t => Math.sin(Math.PI * Math.min(1, t / 0.7)) * 0.14, phase => Math.sin(phase) + 0.3 * Math.sin(phase * 2));
      shaped(d, rate, { lo: 0.6, hi: 0.3, amp: t => Math.sin(Math.PI * Math.min(1, t / 0.7)) * (0.5 + 0.5 * Math.sin(TAU * 38 * t)) * 0.12 });
    }),
    // A ratchet wrench: a quick row of small dry clicks.
    ratchet: () => voice('ratchet', 0.2, (d, rate) => {
      [0, 0.028, 0.056, 0.084, 0.112, 0.14].forEach(at => shaped(d, rate, { lo: 0.85, hi: 0.5, amp: t => (t >= at ? decay(t - at, 500) * 0.8 : 0) }));
    }),
    // A shirt hitting someone: cloth flapping open, a quick flutter of filtered noise.
    flap: () => voice('flap', 0.14, (d, rate) => shaped(d, rate, { lo: 0.5, hi: 0.15, amp: t => decay(t, 22) * Math.min(1, t * 900) * (0.55 + 0.45 * Math.sin(TAU * 42 * t)) * 0.9 })),
    // Wood splintering: a knock and a short run of dry cracks.
    crack: () => voice('crack', 0.3, (d, rate) => {
      [0, 0.018, 0.04, 0.07, 0.11].forEach((at, k) => shaped(d, rate, { lo: 0.6, hi: 0.2, amp: t => (t >= at ? decay(t - at, 180) * (0.9 - k * 0.12) : 0) }));
      glide(d, rate, () => 180, t => decay(t, 30) * Math.min(1, t * 2000) * 0.4);
    }),
    // A small motor diving: a thin whine that drops as it comes down.
    whine: () => voice('whine', 0.55, (d, rate) => glide(d, rate, t => 2200 - 1500 * (t / 0.55), t => Math.min(1, t * 20) * (0.3 + 0.7 * (t / 0.55)) * 0.16, phase => Math.sin(phase) + 0.35 * Math.sin(phase * 2) + 0.2 * Math.sin(phase * 3))),
    // Rubber snapping back: a tone that springs up and wobbles down again.
    boing: () => voice('boing', 0.35, (d, rate) => glide(d, rate, t => 150 + 420 * Math.min(1, t / 0.05) * decay(t, 7) + Math.sin(TAU * 26 * t) * 60 * decay(t, 9), t => decay(t, 9) * Math.min(1, t * 900) * 0.45)),
    // A blade spinning through the air: a hiss that flutters as it turns.
    whirr: () => voice('whirr', 0.4, (d, rate) => shaped(d, rate, { lo: 0.55, hi: 0.2, amp: t => Math.sin(Math.PI * Math.min(1, t / 0.4)) * (0.45 + 0.55 * Math.abs(Math.sin(TAU * 17 * t))) * 0.7 })),
    // Steel drawn and cutting: a bright ring with a scrape in front.
    shing: () => voice('shing', 0.5, (d, rate) => {
      [[3150, 9, 0.22], [4730, 13, 0.14], [6890, 20, 0.08]].forEach(([f, fade, amp]) => glide(d, rate, t => f * (1 + 0.02 * Math.min(1, t / 0.08)), t => decay(t, fade) * Math.min(1, t * 300) * amp));
      shaped(d, rate, { lo: 0.9, hi: 0.5, amp: t => decay(t, 35) * 0.5 });
    }),
    clang: () => voice('clang', 0.7, (d, rate) => {
      [[1, 6], [1.47, 9], [2.09, 12], [2.56, 15]].forEach(([k, fade]) => {
        for (let i = 0; i < d.length; i += 1) {
          const t = i / rate;
          d[i] += Math.sin(TAU * 330 * k * t) * decay(t, fade) * Math.min(1, t * 2000) * 0.2;
        }
      });
    })
  };
  const fire = (name, vol, { delay = 0, rate = 1, wet = 0 } = {}) => {
    const src = audio.createBufferSource();
    src.buffer = VOICES[name]();
    src.playbackRate.value = rate;
    const gain = audio.createGain();
    gain.gain.value = vol;
    src.connect(gain).connect(bus);
    if (wet) {
      const send = audio.createGain();
      send.gain.value = wet;
      gain.connect(send).connect(echo);
    }
    src.start(audio.currentTime + delay);
  };
  // Mallet and bell notes on the scale; the bell sits an octave above.
  const mallet = (step, vol, delay = 0, wet = 0.12) => fire('mallet', vol * 0.55, { delay, rate: pitch(step) / 523.25, wet });
  const ding = (step, vol, delay = 0, wet = 0.25) => fire('bell', vol * 0.55, { delay, rate: pitch(step) / 523.25, wet });
  // Things that keep coming climb the scale and start low again after a pause.
  const runs = {};
  const climb = (key, window, top) => {
    const now = audio.currentTime;
    const run = runs[key] || { n: 0, at: -9 };
    run.n = now - run.at < window ? Math.min(top, run.n + 1) : 0;
    run.at = now;
    runs[key] = run;
    return run.n;
  };
  // The sparkle after a stud: whole steps up from the stud's own note, as in the games.
  const STUD_RUN = [1.15, 1.29, 1.45, 1.63, 1.72, 1.94, 2.17, 2.3];
  const SOUNDS = {
    tap: () => fire('tock', 0.16, { rate: vary(0.08) }),
    tick: () => fire('tock', 0.07, { rate: 1.35 }),
    nope: () => {
      mallet(-3, 0.13);
      mallet(-5, 0.13, 0.11);
    },
    swish: () => fire('whoosh', 0.1, { rate: 1.2 }),
    // Clouds rolling in or parting: a long, soft rush of air.
    clouds: () => {
      fire('whoosh', 0.16, { rate: 0.32 });
      fire('whoosh', 0.08, { rate: 0.45, delay: 0.12 });
    },
    lift: () => fire('pop', 0.22, { rate: 0.75 }),
    // Cash sounds like LEGO studs: one stud, then a quick sparkle running up the scale. Every bill that
    // lands is another stud, a step higher while they keep coming.
    coin: () => {
      fire('stud', 0.07, { wet: 0.1 });
      STUD_RUN.forEach((step, i) => fire('stud', 0.035 - i * 0.003, { delay: 0.12 + i * 0.04, rate: step, wet: 0.15 }));
    },
    // Fans: a soft two-note like, a third apart, under the studs so both can play at once.
    fans: () => {
      fire('like', 0.07, { wet: 0.15 });
      fire('like', 0.045, { delay: 0.09, rate: 1.26, wet: 0.15 });
    },
    clink: fans => {
      const step = climb('clink', 0.5, 4);
      if (fans) fire('like', 0.035, { rate: 2 ** ([0, 2, 4, 7, 9][step] / 12), wet: 0.1 });
      else fire('stud', 0.04, { rate: STUD_RUN[step] / STUD_RUN[0], wet: 0.08 });
    },
    build: () => {
      fire('thump', 0.3);
      mallet(-5, 0.24, 0.05);
      mallet(-2, 0.24, 0.13);
    },
    // A repair: the wrench turns, then the hammer taps the scaffold up. While it stands the crew keeps
    // tapping, and when it comes down a bright ring says it is good as new.
    repair: () => {
      fire('ratchet', 0.1);
      [0, 0.18, 0.31].forEach((at, i) => fire('tink', 0.1 - i * 0.015, { delay: 0.22 + at, rate: vary(0.05) * (1 + i * 0.04), wet: 0.08 }));
    },
    hammer: () => fire('tink', 0.035, { rate: vary(0.12), wet: 0.05 }),
    fixed: () => {
      fire('tink', 0.08, { rate: 1.12 });
      ding(5, 0.09, 0.06);
      ding(9, 0.07, 0.14, 0.3);
    },
    // A level up: a bright pop and a quick three-note lift with a bell on top, no low end.
    // Upgrades in a row climb a step each, so tapping through levels sounds like going up.
    level: () => {
      const step = climb('level', 1.2, 5);
      fire('pop', 0.12, { rate: 1.3 * 2 ** (step / 12) });
      [0, 2, 4].forEach((note, i) => mallet(note + step, 0.15, 0.03 + i * 0.05, 0.15));
      ding(7 + step, 0.07, 0.16, 0.3);
    },
    quest: () => {
      [0, 2, 3, 5, 7].forEach((step, i) => mallet(step, 0.24, i * 0.07, 0.2));
      [10, 12].forEach((step, i) => ding(step, 0.12, 0.38 + i * 0.1, 0.35));
    },
    fanfare: () => {
      [0, 2, 3].forEach(step => mallet(step, 0.18));
      [5, 7, 8].forEach(step => mallet(step, 0.18, 0.16, 0.25));
      ding(10, 0.12, 0.3, 0.35);
    },
    drop: () => {
      fire('riser', 0.14);
      fire('thump', 0.25, { delay: 0.45 });
      [0, 3, 5, 8].forEach((step, i) => mallet(step, 0.22, 0.45 + i * 0.06, 0.25));
    },
    crowd: () => {
      fire('cheer', 0.3);
      [-5, -3, -2].forEach(step => mallet(step, 0.16, 0.15, 0.25));
    },
    // The warning before a raid: a doorbell ding-dong, twice.
    alarm: () => [0, 0.55].forEach(delay => {
      mallet(2, 0.22, delay, 0.2);
      mallet(0, 0.22, delay + 0.2, 0.2);
      fire('thump', 0.12, { delay });
    }),
    spray: () => fire('psst', 0.06, { rate: vary(0.1) }),
    // Bootlegs hitting things: a faint slap every now and then, so a long fight never drones.
    thud: () => {
      if (Math.random() < 0.5) fire('slap', 0.035, { rate: vary(0.3) });
    },
    boom: () => fire('boom', 0.22, { rate: 1.15 * vary(0.1) }),
    // A knockout: a pop and a note that climbs with the streak.
    ko: n => {
      fire('pop', 0.22, { rate: 0.9 * vary(0.05) });
      mallet(Math.min(12, n || 0) - 3, 0.32, 0.02);
    },
    combo: () => [5, 7, 8, 10].forEach((step, i) => ding(step, 0.22, i * 0.06, 0.3)),
    crash: () => {
      fire('boom', 0.45, { rate: 0.75 });
      fire('punch', 0.3, { rate: 0.6, delay: 0.03 });
    },
    down: () => {
      mallet(-3, 0.2);
      mallet(-6, 0.2, 0.2);
    },
    whistle: () => fire('whistle', 0.12),
    // Aces, each with its own call, its own hits and its own finish.
    // Goldfaust: every hit of the player's own fist, climbing while they keep landing, and the big one crunching down.
    jab: () => {
      const step = climb('jab', 0.3, 12);
      fire('punch', 0.15, { rate: 1.5 * vary(0.1) * (1 + step * 0.04) });
      fire('slap', 0.09, { rate: 1.3 * vary(0.2) });
    },
    megafaust: () => {
      fire('punch', 0.45, { rate: 0.5 });
      fire('boom', 0.4, { rate: 0.8 * vary(0.06) });
      fire('crack', 0.25, { rate: 0.7, delay: 0.01 });
      fire('clang', 0.1, { rate: 0.6, delay: 0.02 });
      [0, -2, -5].forEach((step, i) => mallet(step, 0.16, 0.06 + i * 0.07, 0.3));
    },
    // Mörser: the mortar going off, then every shell bursting where it lands.
    cannon: () => {
      fire('thump', 0.22, { rate: 1.4 });
      fire('pop', 0.2, { rate: 0.55 });
      fire('psst', 0.1, { rate: 0.7, delay: 0.02 });
      fire('whoosh', 0.08, { rate: 1.6 });
    },
    shellhit: () => {
      fire('boom', 0.22, { rate: 1.1 * vary(0.1) });
      fire('crack', 0.12, { rate: 0.9 * vary(0.1), delay: 0.01 });
      fire('thump', 0.12, { rate: 0.8 * vary(0.1) });
    },
    // Blackout: rotors spinning up, a zap for each hit, and a whine as it dives in for its last blast.
    drone: () => {
      fire('buzz', 0.5);
      ding(9, 0.05, 0.1);
    },
    dive: () => fire('whine', 0.5, { rate: vary(0.06) }),
    dronekill: () => {
      fire('boom', 0.35, { rate: 1.3 * vary(0.08) });
      fire('zap', 0.22, { rate: 0.6 });
      fire('clang', 0.08, { rate: 1.8, delay: 0.02 });
      [0, 0.05, 0.12].forEach((delay, i) => fire('tink', 0.05, { delay: 0.06 + delay, rate: vary(0.2) * (1.3 - i * 0.15), wet: 0.1 }));
    },
    zap: () => fire('zap', 0.1, { rate: vary(0.2) }),
    // Moshpit: the crowd roars and runs, and every Bootleg it flattens is another like going up the scale.
    stampede: () => {
      fire('cheer', 0.3, { rate: 1.1 });
      [0, 2, 4].forEach((step, i) => mallet(step + 5, 0.12, i * 0.09, 0.25));
      for (let i = 0; i < 10; i += 1) fire('tock', 0.025, { delay: i * 0.12, rate: 0.7 + (i % 3) * 0.12 });
    },
    trample: () => {
      const step = climb('trample', 0.6, 9);
      fire('thump', 0.12, { rate: 1.6 * vary(0.1) });
      fire('like', 0.05, { rate: 2 ** ([0, 2, 4, 7, 9, 12, 14, 16, 19, 21][step] / 12), wet: 0.15 });
    },
    // Airdrop: the whistle on the way down, then the flightcase slamming in, wood splitting and metal corners ringing.
    slam: () => {
      fire('boom', 0.5, { rate: 0.9 });
      fire('crack', 0.3, { rate: vary(0.1) });
      fire('clang', 0.2, { rate: vary(0.1) });
    },
    // Raketenhagel: the salvo hissing off the roof, then the missiles bursting one after another.
    missile: () => {
      fire('thump', 0.18, { rate: 1.6 });
      for (let i = 0; i < 5; i += 1) fire('psst', 0.08, { delay: i * 0.07, rate: 0.8 + i * 0.08 });
      fire('whoosh', 0.14, { rate: 1.3, delay: 0.05 });
    },
    missilehit: () => {
      fire('boom', 0.2, { rate: 1.3 * vary(0.12) });
      fire('crack', 0.1, { rate: 1.1 * vary(0.1), delay: 0.01 });
    },
    // Abrissbirne: the chain creaking into its swing, then steel crashing into the crowd.
    wreck: () => {
      fire('whoosh', 0.28, { rate: 0.45 });
      fire('clang', 0.06, { rate: 0.45, wet: 0.2 });
    },
    wreckhit: () => {
      fire('punch', 0.35, { rate: 0.5 * vary(0.08) });
      fire('clang', 0.14, { rate: 0.7 * vary(0.1) });
      fire('thump', 0.2, { rate: 0.6 });
    },
    // Tornado: wind rising and howling round and round.
    twister: () => {
      [0, 0.25, 0.5, 0.75, 1, 1.25].forEach((delay, i) => fire('whoosh', 0.16, { delay, rate: 0.35 + (i % 2) * 0.1 }));
      fire('whirr', 0.3, { rate: 0.4 });
      fire('whine', 0.12, { rate: 0.35, delay: 0.2, wet: 0.3 });
    },
    // Orbitallaser: the beam charging up in orbit, then the burn.
    laser: () => {
      fire('riser', 0.18, { rate: 1.8 });
      ding(19, 0.05, 0.05, 0.4);
    },
    laserfire: () => {
      fire('zap', 0.3, { rate: 0.45 });
      fire('buzz', 0.35, { rate: 0.6 });
      fire('boom', 0.25, { rate: 1.5 });
    },
    // Erdbeben: deep rumbling and rock splitting.
    quake: () => {
      fire('boom', 0.45, { rate: 0.5 * vary(0.06) });
      fire('thump', 0.4, { rate: 0.4 });
      fire('crack', 0.25, { rate: 0.5 * vary(0.1), delay: 0.03 });
    },
    // The MIXEDOWN heroes, each with his own sound for every move.
    // Caruso: rubber fists that spring out and snap back.
    stretch: () => {
      fire('boing', 0.22, { rate: vary(0.12) });
      fire('punch', 0.12, { rate: 1.3 * vary(0.1), delay: 0.06 });
    },
    gatling: () => {
      fire('boing', 0.2, { rate: 1.4 });
      [0, 2, 4, 7].forEach((step, i) => mallet(step + 5, 0.1, i * 0.05, 0.2));
    },
    bazooka: () => {
      fire('boing', 0.3, { rate: 0.6 });
      fire('punch', 0.4, { rate: 0.55, delay: 0.04 });
      fire('boom', 0.25, { rate: 1.2, delay: 0.05 });
    },
    // Retro: the pin pulled and the grenades lobbed, bomblets rattling after the blast, and a body slam that shakes the block.
    grenade: () => {
      fire('tink', 0.12, { rate: 0.8 * vary(0.08) });
      fire('whoosh', 0.07, { rate: 0.8, delay: 0.06 });
    },
    cluster: () => fire('boom', 0.09, { rate: 1.9 * vary(0.15) }),
    bodyslam: () => {
      fire('boom', 0.5, { rate: 0.7 });
      fire('thump', 0.45, { rate: 0.5 });
      fire('crack', 0.3, { rate: 0.8, delay: 0.02 });
      [0, -3, -7].forEach((step, i) => mallet(step, 0.18, 0.05 + i * 0.07, 0.3));
    },
    // Zola: every kick lands like a hammer.
    heavy: () => {
      fire('punch', 0.4, { rate: 0.6 * vary(0.08) });
      fire('crack', 0.16, { rate: 1.2 * vary(0.1) });
      fire('slap', 0.12, { rate: 0.5 });
    },
    spartakick: () => {
      fire('whoosh', 0.15, { rate: 0.5 });
      fire('punch', 0.55, { rate: 0.45, delay: 0.35 });
      fire('boom', 0.4, { rate: 0.9, delay: 0.36 });
      fire('clang', 0.12, { rate: 0.5, delay: 0.37 });
    },
    // Kuza & Kamua: throwing stars whirring out and back.
    star: () => {
      fire('whirr', 0.14, { rate: 1.3 * vary(0.12) });
      ding(16, 0.03, 0.02, 0.2);
    },
    starhit: () => {
      fire('tink', 0.08, { rate: vary(0.2) });
      fire('slap', 0.07, { rate: 1.5 * vary(0.2) });
    },
    starstorm: () => {
      [0, 0.07, 0.14, 0.21].forEach((delay, i) => fire('whirr', 0.12, { delay, rate: 1 + i * 0.15 }));
      [7, 11, 14, 19].forEach((step, i) => ding(step, 0.06, i * 0.06, 0.35));
    },
    // 2lade: silver cutting, and the blood moon rising before he goes through them all.
    blade: () => {
      fire('shing', 0.12, { rate: vary(0.1) });
      fire('psst', 0.08, { rate: 1.3 * vary(0.1) });
    },
    bloodmoon: () => {
      fire('riser', 0.14, { rate: 0.6 });
      [0, 0.1, 0.2, 0.3, 0.4, 0.5].forEach((delay, i) => fire('shing', 0.1, { delay: 0.35 + delay, rate: 0.9 + i * 0.08 }));
      [-2, 1, 5].forEach((step, i) => mallet(step, 0.14, 0.35 + i * 0.12, 0.35));
    },
    // Prettyfacecapi: the scythe turning through, and the storm starting up.
    sickles: () => {
      fire('whoosh', 0.16, { rate: 0.8 * vary(0.1) });
      fire('shing', 0.14, { rate: 0.75 * vary(0.08), delay: 0.04 });
    },
    moonslash: () => {
      fire('shing', 0.22, { rate: 0.6 });
      fire('whoosh', 0.2, { rate: 0.45 });
      [12, 9, 7, 4].forEach((step, i) => ding(step, 0.07, 0.05 + i * 0.07, 0.4));
    },
    // Johno: an uppercut, and the Bootleg whistling up into the sky.
    uppercut: () => {
      fire('punch', 0.35, { rate: 0.8 * vary(0.1) });
      fire('whoosh', 0.1, { rate: 1.8, delay: 0.03 });
      fire('whistle', 0.05, { rate: 1.6, delay: 0.05 });
    },
    ascend: () => {
      fire('riser', 0.12, { rate: 1.3 });
      [0, 4, 7, 12].forEach((step, i) => mallet(step + 2, 0.12, i * 0.08, 0.3));
    },
    // Polarise: the bat through the air and into a Bootleg, and the homerun that clears the block.
    bat: () => {
      fire('whoosh', 0.12, { rate: 0.7 * vary(0.08) });
      fire('punch', 0.35, { rate: 0.7 * vary(0.08), delay: 0.04 });
      fire('crack', 0.18, { rate: 1.4 * vary(0.1), delay: 0.04 });
    },
    homerun: () => {
      fire('crack', 0.4, { rate: 0.9 });
      fire('punch', 0.5, { rate: 0.5 });
      fire('whistle', 0.08, { rate: 1.2, delay: 0.1 });
      [0, 4, 7, 12].forEach((step, i) => mallet(step + 5, 0.14, 0.12 + i * 0.07, 0.3));
    },
    // Bigtoe: gold on the knuckles, the hook that ends the combination, and the hail of blows.
    dusters: () => {
      fire('punch', 0.24, { rate: 1.1 * vary(0.12) });
      fire('tink', 0.05, { rate: 1.7 * vary(0.1) });
    },
    hook: () => {
      fire('punch', 0.4, { rate: 0.75 * vary(0.08) });
      fire('slap', 0.12, { rate: 0.6 });
      fire('tink', 0.08, { rate: 1.3, delay: 0.02 });
    },
    hail: () => {
      fire('riser', 0.1, { rate: 1.5 });
      [12, 16, 19, 24].forEach((step, i) => ding(step, 0.05, i * 0.05, 0.25));
    },
    // Maliq: cards flicked off the deck, and a royal flush going off.
    card: () => {
      fire('slap', 0.06, { rate: 2.2 * vary(0.15) });
      fire('whoosh', 0.04, { rate: 2.4 });
      ding(14 + Math.floor(Math.random() * 3) * 2, 0.03, 0.02, 0.3);
    },
    royalflush: () => {
      [0, 4, 7, 11, 14].forEach((step, i) => ding(step, 0.08, i * 0.07, 0.45));
      fire('riser', 0.1, { rate: 1.1 });
      fire('boom', 0.28, { rate: 1.3, delay: 0.5 });
    },
    win: () => {
      fire('cheer', 0.3, { delay: 0.2 });
      [0, 2, 3, 5, 7].forEach((step, i) => mallet(step, 0.22, i * 0.08, 0.2));
      [5, 7, 10].forEach(step => mallet(step, 0.2, 0.5, 0.3));
      ding(12, 0.12, 0.55, 0.4);
    },
    calm: () => [-5, -3, -2].forEach((step, i) => mallet(step, 0.2, i * 0.1, 0.2)),
    lose: () => [-2, -3, -5, -7].forEach((step, i) => mallet(step, 0.22, i * 0.22, 0.2))
  };
  // Busy sounds keep a little distance from themselves so a crowded fight stays a texture, not a wall.
  const GAPS = { spray: 0.12, thud: 0.35, boom: 0.12, ko: 0.05, clink: 0.07, level: 0.06, tick: 0.04, tap: 0.03, hammer: 0.25, jab: 0.04, shellhit: 0.06, missilehit: 0.05, wreckhit: 0.06, trample: 0.05, megafaust: 0.1, dive: 0.1, dronekill: 0.12, zap: 0.12, stretch: 0.06, grenade: 0.15, cluster: 0.05, heavy: 0.1, star: 0.08, starhit: 0.04, blade: 0.06, sickles: 0.1, uppercut: 0.08, card: 0.05, bat: 0.1, dusters: 0.04, hook: 0.08 };
  const lastPlayed = {};
  const play = (sound, n) => {
    if (!SOUNDS[sound] || !soundOn()) return;
    if (!audio) return;
    const now = audio.currentTime;
    if (now - (lastPlayed[sound] || -9) < (GAPS[sound] || 0)) return;
    lastPlayed[sound] = now;
    SOUNDS[sound](n);
  };

  // ---------------------------------------------------------------------------------------------
  // Every three hours three tasks; every day a chest that grows with a streak of days in a row, records, and an event on
  // weekends. A day is a calendar day on the player's own clock.

  const dayOf = (t = Date.now()) => Math.floor((t - new Date(t).getTimezoneOffset() * MINUTE) / DAY);
  // Midnight at the start of a day, on the player's clock.
  const dayStart = day => {
    const d = new Date(day * DAY);
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()).getTime();
  };
  const SEASON_START = dayOf(new Date(2026, 0, 5).getTime());
  const mod = (a, n) => ((a % n) + n) % n;
  // Sunday is 0, Saturday 6.
  const weekday = day => mod(day + 4, 7);
  const eventOn = (day = dayOf()) => {
    const wd = weekday(day);
    return wd === 6 || wd === 0 ? EVENTS[mod(Math.floor((day - SEASON_START) / 7), EVENTS.length)] : null;
  };
  const eventIs = id => eventOn()?.id === id;
  const eventLeft = () => {
    const day = dayOf();
    return dayStart(day + (weekday(day) === 6 ? 2 : 1)) - Date.now();
  };

  // Rounds of tasks run from midnight on the player's clock: the round now, and how long until the next one.
  const roundOf = (t = Date.now()) => Math.floor((t - new Date(t).getTimezoneOffset() * MINUTE) / TASK_ROUND);
  const roundLeft = () => TASK_ROUND - mod(Date.now() - new Date().getTimezoneOffset() * MINUTE, TASK_ROUND);
  // The round's tasks, dealt from the player's seed when the round begins and counted from there.
  const dealTasks = round => {
    const rand = mulberry32((S.seed ^ Math.imul(round, 2654435761)) >>> 0);
    const kinds = Object.keys(TASKS).filter(kind => !TASKS[kind].open || TASKS[kind].open());
    const tasks = [];
    while (tasks.length < TASK_COUNT && kinds.length) {
      const kind = kinds.splice(Math.floor(rand() * kinds.length), 1)[0];
      const task = TASKS[kind];
      tasks.push({ kind, need: oneOf(rand, task.need), from: task.stat ? task.stat() : 0, done: false });
    }
    return { round, tasks, combo: 0 };
  };
  const taskRound = () => {
    const round = roundOf();
    if (!S.daily || S.daily.round !== round) S.daily = dealTasks(round);
    return S.daily;
  };
  const taskHave = task => Math.min(task.need, Math.max(0, task.kind === 'combo' ? taskRound().combo : TASKS[task.kind].stat() - task.from));
  const taskText = task => TASKS[task.kind].text(task.need);
  const taskReady = task => !task.done && taskHave(task) >= task.need;
  const taskPay = () => nice(salesPace() * capMinutes() * TASK_PAY);
  const noteCombo = n => {
    const daily = taskRound();
    daily.combo = Math.max(daily.combo, n);
  };

  // Where today's chest sits in the run of seven: the streak goes on from yesterday, or starts over.
  const chestReady = () => S.chest.day !== dayOf();
  const chestIndex = () => {
    const day = dayOf();
    if (S.chest.day === day) return mod(S.chest.streak - 1, CHEST.length);
    return S.chest.day === day - 1 ? mod(S.chest.streak, CHEST.length) : 0;
  };
  const chestPay = index => nice(salesPace() * capMinutes() * CHEST[index]);

  // Records: a new value counts when it beats the best so far; a time beats it by being shorter.
  const RECORD_LOW = { fastest: true };
  const beats = (key, value) => {
    const was = S.best[key];
    const better = RECORD_LOW[key] ? !was || value < was : value > was;
    if (better) S.best[key] = value;
    return better && was > 0;
  };

  // ---------------------------------------------------------------------------------------------
  // The map: tiles seen from above at a slant. Land is bought in chunks next to what you own,
  // so the block can grow in every direction without end.

  const TILT = 0.62;
  const cam = { x: 0, y: 0, z: 1, ax: 0, ay: 0 };
  let cell = 56;
  let clock = 0;
  const cw = () => cell * cam.z;
  const proj = (x, y, h = 0) => ({ x: cam.ax + (x - cam.x) * cw(), y: cam.ay + (y - cam.y) * cw() * TILT - h * cw() });
  const unproj = (sx, sy) => ({ x: cam.x + (sx - cam.ax) / cw(), y: cam.y + (sy - cam.ay) / (cw() * TILT) });

  const chunkKey = (cx, cy) => `${cx},${cy}`;
  const chunkOf = (x, y) => chunkKey(Math.floor(x / CHUNK), Math.floor(y / CHUNK));
  let landKeys = new Set();
  const bounds = { x0: 0, y0: 0, x1: 0, y1: 0 };
  // What stands on the map right now: your block, or during a Raid only your crew on the other gang's ground.
  const world = () => (heist ? heist.items : S.items);
  const refreshLand = () => {
    const land = heist ? heist.land : S.land;
    landKeys = new Set(land);
    const chunks = land.map(key => key.split(',').map(Number));
    bounds.x0 = Math.min(...chunks.map(c => c[0])) * CHUNK;
    bounds.y0 = Math.min(...chunks.map(c => c[1])) * CHUNK;
    bounds.x1 = (Math.max(...chunks.map(c => c[0])) + 1) * CHUNK;
    bounds.y1 = (Math.max(...chunks.map(c => c[1])) + 1) * CHUNK;
  };
  const owned = (x, y) => landKeys.has(chunkOf(Math.floor(x), Math.floor(y)));
  const forSale = () => {
    const out = new Set();
    S.land.forEach(key => {
      const [cx, cy] = key.split(',').map(Number);
      [[1, 0], [-1, 0], [0, 1], [0, -1]].forEach(([dx, dy]) => {
        const next = chunkKey(cx + dx, cy + dy);
        if (!landKeys.has(next)) out.add(next);
      });
    });
    return [...out].map(key => {
      const [cx, cy] = key.split(',').map(Number);
      return { key, cx, cy };
    });
  };
  const landPrice = () => Math.round(350 * 2.15 ** (S.land.length - 4));

  const info = item => TYPES[item.type];
  const kindOf = item => TYPES[item.type].kind;
  const itemById = id => world().find(item => item.id === id) || null;
  const footprint = item => ({ x: item.x, y: item.y, w: info(item).w, d: info(item).d });
  // The crew leaves its post during a fight, so it is hit and drawn where it stands.
  const center = item => (kindOf(item) === 'crew' ? crewAt(item) : { x: item.x + info(item).w / 2, y: item.y + info(item).d / 2 });
  const hitBox = item => {
    if (kindOf(item) !== 'crew') return footprint(item);
    const at = crewAt(item);
    return { x: at.x - 0.3, y: at.y - 0.3, w: 0.6, d: 0.6 };
  };
  const door = item => ({ x: item.x + info(item).w / 2, y: item.y + info(item).d + 0.18 });
  const maxHp = item => hpOf(item.type, item.level);
  const standing = item => !item.ruin && !item.rebuild;
  // Anything with walls and a door hides the hustler; open stands, vending machines, walls and stages do not.
  const isShelter = item => (kindOf(item) === 'home' || (kindOf(item) === 'sale' && item.type !== 'stand' && item.type !== 'automat')) && standing(item);
  const isSolid = item => !item.ruin;
  // A hero who is down lies there until he is back up; nobody bothers with him meanwhile.
  const isHero = item => Boolean(TYPES[item.type].hero);
  const isTarget = item => isSolid(item);
  const rectDist = (item, x, y) => {
    const f = hitBox(item);
    return dist(x, y, clamp(x, f.x, f.x + f.w), clamp(y, f.y, f.y + f.d));
  };

  // A spot just behind a building disappears under its walls on screen. Nobody stops, shops or fights there.
  const hiddenAt = (x, y) => world().some(item => {
    if (kindOf(item) === 'crew' || item.ruin) return false;
    const f = footprint(item);
    return x > f.x - 0.15 && x < f.x + f.w + 0.15 && y < f.y + 0.25 && y > f.y - heightOf(item.type, item.level) / TILT - 0.3;
  });
  // Where a Bootleg hits: the front or the front half of a side, never the back, and never a spot
  // another building hides. The closest such spot wins; if all are hidden, the closest of them.
  const attackPoint = (item, x, y) => {
    const f = hitBox(item);
    const top = kindOf(item) === 'crew' ? f.y : f.y + f.d * 0.5;
    const spots = [{ x: clamp(x, f.x, f.x + f.w), y: f.y + f.d }, { x: f.x, y: clamp(y, top, f.y + f.d) }, { x: f.x + f.w, y: clamp(y, top, f.y + f.d) }];
    for (let k = 0; k <= 4; k += 1) spots.push({ x: f.x + (f.w * k) / 4, y: f.y + f.d });
    const nearest = list => list.reduce((a, b) => (dist(x, y, b.x, b.y) < dist(x, y, a.x, a.y) ? b : a));
    const open = spots.filter(spot => !hiddenAt(spot.x + Math.sign(spot.x - (f.x + f.w / 2)) * 0.3, spot.y + 0.3));
    return nearest(open.length ? open : spots);
  };

  let occ = new Map();
  const refreshOcc = () => {
    occ = new Map();
    S.items.forEach(item => {
      const f = footprint(item);
      for (let x = f.x; x < f.x + f.w; x += 1) for (let y = f.y; y < f.y + f.d; y += 1) occ.set(`${x},${y}`, item);
    });
  };
  const canPlace = (type, x, y, skip = null) => {
    const t = TYPES[type];
    for (let tx = x; tx < x + t.w; tx += 1) {
      for (let ty = y; ty < y + t.d; ty += 1) {
        if (!owned(tx, ty)) return false;
        const there = occ.get(`${tx},${ty}`);
        if (there && there !== skip) return false;
      }
    }
    return true;
  };
  // The closest free spot to a point, searched in rings.
  const freeSpot = (type, near) => {
    const t = TYPES[type];
    const ox = Math.round(near.x - t.w / 2);
    const oy = Math.round(near.y - t.d / 2);
    for (let r = 0; r < 40; r += 1) {
      for (let dx = -r; dx <= r; dx += 1) {
        for (let dy = -r; dy <= r; dy += 1) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
          if (canPlace(type, ox + dx, oy + dy)) return { x: ox + dx, y: oy + dy };
        }
      }
    }
    return null;
  };

  // ---------------------------------------------------------------------------------------------
  // Bodies walk straight when the way is clear and find a way round buildings when it is not; a body
  // still stuck in a crowd steps aside.

  // This runs for every body in every frame, so it builds no lists: the buildings' boxes are kept for the frame,
  // the crew's are taken where they stand, and everything is walked in the same order as the world.
  const footprints = new Map();
  let footprintsAt = -1;
  const solidBox = item => {
    if (kindOf(item) === 'crew') return hitBox(item);
    if (footprintsAt !== clock) {
      footprints.clear();
      footprintsAt = clock;
    }
    let box = footprints.get(item);
    if (!box) {
      box = footprint(item);
      footprints.set(item, box);
    }
    return box;
  };
  const pushOutOf = (body, f) => {
    const r = body.r;
    const x0 = f.x - r;
    const x1 = f.x + f.w + r;
    const y0 = f.y - r;
    const y1 = f.y + f.d + r;
    if (body.x <= x0 || body.x >= x1 || body.y <= y0 || body.y >= y1) return;
    const left = body.x - x0;
    const right = x1 - body.x;
    const top = body.y - y0;
    const bottom = y1 - body.y;
    const least = Math.min(left, right, top, bottom);
    if (least === left) body.x = x0;
    else if (least === right) body.x = x1;
    else if (least === top) body.y = y0;
    else body.y = y1;
  };
  const pushOut = (body, skip = null) => {
    for (const item of world()) if (item !== skip && isSolid(item)) pushOutOf(body, solidBox(item));
    if (!heist) return;
    for (const f of heist.props) pushOutOf(body, f);
    for (const f of heist.cover) pushOutOf(body, f);
  };
  // The ground as tiles: which ones a building or a stash stands on, built once per frame.
  let grid = null;
  let gridAt = -1;
  const blockedTiles = () => {
    if (gridAt === clock && grid) return grid;
    grid = new Set();
    gridAt = clock;
    const boxes = world().filter(item => kindOf(item) !== 'crew' && isSolid(item)).map(hitBox);
    if (heist) boxes.push(...heist.props, ...heist.cover);
    boxes.forEach(f => {
      for (let x = Math.floor(f.x - 0.3); x < f.x + f.w + 0.3; x += 1) {
        for (let y = Math.floor(f.y - 0.3); y < f.y + f.d + 0.3; y += 1) {
          if (x + 0.5 > f.x - 0.3 && x + 0.5 < f.x + f.w + 0.3 && y + 0.5 > f.y - 0.3 && y + 0.5 < f.y + f.d + 0.3) grid.add(`${x},${y}`);
        }
      }
    });
    return grid;
  };
  const tileFree = (x, y) => !blockedTiles().has(`${Math.floor(x)},${Math.floor(y)}`);
  const clearLine = (ax, ay, bx, by) => {
    const n = Math.ceil(dist(ax, ay, bx, by) / 0.25);
    for (let i = 1; i < n; i += 1) if (!tileFree(lerp(ax, bx, i / n), lerp(ay, by, i / n))) return false;
    return true;
  };
  // A small binary heap for the open list, cheapest first.
  const heapPush = (heap, node) => {
    heap.push(node);
    for (let i = heap.length - 1; i > 0;) {
      const up = (i - 1) >> 1;
      if (heap[up].f <= heap[i].f) break;
      [heap[up], heap[i]] = [heap[i], heap[up]];
      i = up;
    }
  };
  const heapPop = heap => {
    const top = heap[0];
    const last = heap.pop();
    if (heap.length) {
      heap[0] = last;
      for (let i = 0; ;) {
        const l = i * 2 + 1;
        const r = l + 1;
        let m = i;
        if (l < heap.length && heap[l].f < heap[m].f) m = l;
        if (r < heap.length && heap[r].f < heap[m].f) m = r;
        if (m === i) break;
        [heap[m], heap[i]] = [heap[i], heap[m]];
        i = m;
      }
    }
    return top;
  };
  // A* over the tiles around start and goal, no cutting corners; the path is then pulled tight.
  const findPath = (ax, ay, bx, by) => {
    const sx = Math.floor(ax);
    const sy = Math.floor(ay);
    const ex = Math.floor(bx);
    const ey = Math.floor(by);
    const pad = 8;
    const x0 = Math.min(sx, ex) - pad;
    const x1 = Math.max(sx, ex) + pad;
    const y0 = Math.min(sy, ey) - pad;
    const y1 = Math.max(sy, ey) + pad;
    const blocked = blockedTiles();
    const free = (x, y) => (x === ex && y === ey) || (x === sx && y === sy) || (x >= x0 && x <= x1 && y >= y0 && y <= y1 && !blocked.has(`${x},${y}`));
    const key = (x, y) => `${x},${y}`;
    const guess = (x, y) => Math.hypot(x - ex, y - ey);
    const open = [{ x: sx, y: sy, g: 0, f: guess(sx, sy) }];
    const from = new Map([[key(sx, sy), null]]);
    const cost = new Map([[key(sx, sy), 0]]);
    let found = false;
    for (let n = 0; open.length && n < 2500; n += 1) {
      const cur = heapPop(open);
      if (cur.g > cost.get(key(cur.x, cur.y))) continue;
      if (cur.x === ex && cur.y === ey) {
        found = true;
        break;
      }
      for (let dx = -1; dx <= 1; dx += 1) {
        for (let dy = -1; dy <= 1; dy += 1) {
          if (!dx && !dy) continue;
          const nx = cur.x + dx;
          const ny = cur.y + dy;
          if (!free(nx, ny) || (dx && dy && (!free(cur.x + dx, cur.y) || !free(cur.x, cur.y + dy)))) continue;
          const g = cur.g + (dx && dy ? Math.SQRT2 : 1);
          const k = key(nx, ny);
          if (cost.has(k) && cost.get(k) <= g) continue;
          cost.set(k, g);
          from.set(k, key(cur.x, cur.y));
          heapPush(open, { x: nx, y: ny, g, f: g + guess(nx, ny) });
        }
      }
    }
    if (!found) return null;
    const tiles = [];
    for (let k = key(ex, ey); k; k = from.get(k)) {
      const [x, y] = k.split(',').map(Number);
      tiles.unshift({ x: x + 0.5, y: y + 0.5 });
    }
    tiles[tiles.length - 1] = { x: bx, y: by };
    const pts = [];
    let at = { x: ax, y: ay };
    for (let i = 1; i < tiles.length; i += 1) {
      if (i === tiles.length - 1 || !clearLine(at.x, at.y, tiles[i + 1].x, tiles[i + 1].y)) {
        pts.push(tiles[i]);
        at = tiles[i];
      }
    }
    return pts.length ? pts : null;
  };
  // Where to head next on the way to a goal: the goal itself, or the next corner round the buildings.
  const waypoint = (body, gx, gy, dt) => {
    const plan = body.plan || (body.plan = { t: 0, gx, gy, pts: null });
    plan.t -= dt;
    if (plan.t <= 0 || dist(plan.gx, plan.gy, gx, gy) > 0.8) {
      plan.t = 0.5 + Math.random() * 0.25;
      plan.gx = gx;
      plan.gy = gy;
      plan.pts = clearLine(body.x, body.y, gx, gy) ? null : findPath(body.x, body.y, gx, gy);
    }
    if (!plan.pts) return { x: gx, y: gy };
    while (plan.pts.length > 1 && dist(body.x, body.y, plan.pts[0].x, plan.pts[0].y) < 0.35) plan.pts.shift();
    const next = plan.pts[0];
    return plan.pts.length === 1 ? { x: gx, y: gy } : next;
  };
  const moveBody = (body, goalX, goalY, speed, dt, skip = null) => {
    if (Math.hypot(goalX - body.x, goalY - body.y) < 0.001) return 0;
    const { x: gx, y: gy } = waypoint(body, goalX, goalY, dt);
    let dx = gx - body.x;
    let dy = gy - body.y;
    const d = Math.hypot(dx, dy);
    if (d < 0.001) return 0;
    dx /= d;
    dy /= d;
    if (body.detour > 0) {
      body.detour -= dt;
      const ox = -dy * body.side;
      const oy = dx * body.side;
      dx = dx * 0.25 + ox;
      dy = dy * 0.25 + oy;
      const n = Math.hypot(dx, dy);
      dx /= n;
      dy /= n;
    }
    // Nobody snaps to full speed or turns on the spot: a body speeds up out of a stop, swings round
    // corners and eases into where it is going.
    const left = Math.hypot(goalX - body.x, goalY - body.y);
    const fresh = clock - (body.movedAt === undefined ? -9 : body.movedAt) > 0.25;
    body.v = fresh ? speed * 0.35 : ease(body.v || 0, speed * clamp(left * 2.5, 0.35, 1), 7, dt);
    if (fresh || body.hx === undefined || left < 0.3) {
      body.hx = dx;
      body.hy = dy;
    } else {
      const hx = ease(body.hx, dx, 12, dt);
      const hy = ease(body.hy, dy, 12, dt);
      const n = Math.hypot(hx, hy);
      body.hx = n < 0.2 ? dx : hx / n;
      body.hy = n < 0.2 ? dy : hy / n;
    }
    const stepLen = Math.min(d, body.v * dt);
    const px = body.x;
    const py = body.y;
    body.x += body.hx * stepLen;
    body.y += body.hy * stepLen;
    pushOut(body, skip);
    const moved = Math.hypot(body.x - px, body.y - py);
    if (moved < stepLen * 0.35) {
      body.stuck = (body.stuck || 0) + dt;
      if (body.stuck > 0.35) {
        body.detour = 0.8 + Math.random() * 0.5;
        body.side = Math.random() < 0.5 ? -1 : 1;
        body.stuck = 0;
      }
    } else body.stuck = 0;
    // Walking almost straight up or down the screen keeps the facing it had, so nobody flickers left and right.
    if (Math.abs(body.x - px) > Math.max(0.002, stepLen * 0.3)) body.face = body.x > px ? 1 : -1;
    body.walk = (body.walk || 0) + moved;
    if (moved > 0.001) body.movedAt = clock;
    return moved;
  };

  // ---------------------------------------------------------------------------------------------
  // Effects: particles, paint on the ground, little numbers and cash flying to the counter.

  const parts = [];
  const decals = [];
  const floats = [];
  const flyers = [];
  // Effects stay within a budget, so a crowded fight on a small phone keeps its frame rate: the oldest go first.
  const PARTS_MAX = 600;
  const FLOATS_MAX = 40;
  const burst = (x, y, h, n, colors, opts = {}) => {
    if (reducedMotion.matches) n = Math.ceil(n / 3);
    if (parts.length + n > PARTS_MAX) parts.splice(0, parts.length + n - PARTS_MAX);
    for (let i = 0; i < n; i += 1) {
      const a = Math.random() * TAU;
      const s = (opts.speed || 2) * (0.4 + Math.random() * 0.8);
      parts.push({
        x, y, h,
        vx: Math.cos(a) * s,
        vy: Math.sin(a) * s,
        vh: (opts.up || 2.5) * (0.5 + Math.random()),
        g: opts.g === undefined ? 9 : opts.g,
        life: (opts.life || 0.7) * (0.6 + Math.random() * 0.6),
        max: 1,
        size: (opts.size || 0.06) * (0.6 + Math.random() * 0.8),
        color: colors[i % colors.length],
        kind: opts.kind || 'dot',
        spin: Math.random() * TAU
      });
      parts[parts.length - 1].max = parts[parts.length - 1].life;
    }
  };
  const splat = (x, y, r, color, life = 6, crack = false) => {
    decals.push({ x, y, r, color, life, max: life, seed: Math.random() * 1000, crack });
    if (decals.length > 60) decals.shift();
  };
  const floatText = (x, y, h, text, color = INK, big = 1, mark = '') => {
    if (floats.length >= FLOATS_MAX) floats.shift();
    floats.push({ x, y, h, text, color, big, mark, life: 1.1, max: 1.1 });
  };
  // Moments on top of the scene: slaps, shock rings, a fallen hero. Each runs for `dur` seconds.
  const moments = [];
  const updateEffects = dt => {
    for (let i = parts.length - 1; i >= 0; i -= 1) {
      const p = parts[i];
      p.life -= dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vh -= p.g * dt;
      p.h = Math.max(0, p.h + p.vh * dt);
      if (p.h === 0) {
        p.vx *= 0.8;
        p.vy *= 0.8;
        p.vh = Math.abs(p.vh) * 0.3;
      }
      p.spin += dt * 6;
      if (p.life <= 0) parts.splice(i, 1);
    }
    for (let i = decals.length - 1; i >= 0; i -= 1) {
      decals[i].life -= dt;
      if (decals[i].life <= 0) decals.splice(i, 1);
    }
    for (let i = moments.length - 1; i >= 0; i -= 1) {
      const f = moments[i];
      f.t += dt;
      if (f.apply && f.t >= 0) {
        f.apply();
        f.apply = null;
      }
      if (f.t >= f.dur) moments.splice(i, 1);
    }
    for (let i = floats.length - 1; i >= 0; i -= 1) {
      floats[i].life -= dt;
      floats[i].h += dt * 0.8;
      if (floats[i].life <= 0) floats.splice(i, 1);
    }
  };

  // ---------------------------------------------------------------------------------------------
  // The hustler. Needs no looking after; looks after the business. In peace: rounds and checks;
  // in a raid: the nearest roof, and once the roofs are gone, a run for dear life.

  const K = { x: 0, y: 0, r: 0.2, face: 1, hp: 100, goal: null, wait: 1, act: 'look', emote: null, inside: null, hurt: 0, dead: 0, walk: 0, panic: 0, moving: false };
  const placeHustler = () => {
    const home = hq();
    const at = home ? door(home) : { x: 0, y: 0 };
    K.x = at.x;
    K.y = at.y + 0.3;
    K.goal = null;
    K.inside = null;
    K.dead = 0;
    K.hp = hustlerMaxHp();
    K.wait = 1.5;
    K.act = 'look';
    K.emote = null;
  };
  const hustlerExposed = () => !heist && !K.inside && !K.dead;
  // A fight on, at home or away: no building, moving or repairing until it is over.
  const busy = () => Boolean(heist || (raid && raid.phase !== 'end'));
  const shelters = () => S.items.filter(isShelter);
  const nearestShelter = () => {
    let best = null;
    let bestD = Infinity;
    shelters().forEach(item => {
      const at = door(item);
      const d = dist(K.x, K.y, at.x, at.y) + (item.type === 'hq' ? -1 : 0);
      if (d < bestD) {
        best = item;
        bestD = d;
      }
    });
    return best;
  };
  const randomFreePoint = () => {
    for (let i = 0; i < 20; i += 1) {
      const x = bounds.x0 + 0.5 + Math.random() * (bounds.x1 - bounds.x0 - 1);
      const y = bounds.y0 + 0.5 + Math.random() * (bounds.y1 - bounds.y0 - 1);
      if (!owned(x, y)) continue;
      const there = occ.get(`${Math.floor(x)},${Math.floor(y)}`);
      if (there && isSolid(there)) continue;
      if (dist(x, y, K.x, K.y) > 7 || hiddenAt(x, y)) continue;
      return { x, y };
    }
    return null;
  };
  const HUSTLER_ACTS = [
    { id: 'stroll', weight: 4 },
    { id: 'inspect', weight: 4 },
    { id: 'look', weight: 2 },
    { id: 'wave', weight: 1.5 },
    { id: 'phone', weight: 1.5 },
    { id: 'coffee', weight: 1 },
    { id: 'count', weight: 1 }
  ];
  const chooseAct = () => {
    let roll = Math.random() * HUSTLER_ACTS.reduce((sum, a) => sum + a.weight, 0);
    let act = HUSTLER_ACTS[0].id;
    for (const a of HUSTLER_ACTS) {
      roll -= a.weight;
      if (roll < 0) {
        act = a.id;
        break;
      }
    }
    K.emote = null;
    K.visit = null;
    if (act === 'stroll') {
      K.goal = randomFreePoint();
      K.act = 'stroll';
      K.wait = 1 + Math.random() * 2;
    } else if (act === 'inspect') {
      const options = S.items.filter(item => {
        if (!standing(item)) return false;
        const at = kindOf(item) === 'crew' ? { x: item.x + 0.5, y: item.y + 1.35 } : door(item);
        return !hiddenAt(at.x, at.y + 0.25);
      });
      const item = options[Math.floor(Math.random() * options.length)];
      if (!item) return chooseAct();
      const at = kindOf(item) === 'crew' ? { x: item.x + 0.5, y: item.y + 1.35 } : door(item);
      K.goal = { x: at.x + (Math.random() - 0.5) * 0.4, y: at.y + 0.25 };
      K.visit = item.id;
      K.act = 'inspect';
      K.wait = 2.6 + Math.random() * 1.5;
    } else {
      K.goal = null;
      K.act = act;
      K.wait = 2.2 + Math.random() * 2;
      K.emote = act === 'look' ? null : act;
    }
  };
  const actLine = () => {
    if (K.dead) return 'ist gefallen';
    if (raid && raid.phase !== 'end') {
      if (K.inside) return `versteckt sich: ${info(itemById(K.inside) || { type: 'hq' }).name}`;
      return shelters().length ? 'rennt in Deckung' : 'hat keine Deckung mehr';
    }
    const visit = K.visit && itemById(K.visit);
    if (K.act === 'inspect' && visit) return `checkt: ${info(visit).name}`;
    return { stroll: 'dreht eine Runde durchs Viertel', look: 'checkt die Lage', wave: 'grüßt seine Helden', phone: 'macht Deals am Handy', coffee: 'holt Kaffee vom Späti', count: 'zählt Cash' }[K.act] || 'checkt die Lage';
  };
  const updateHustler = dt => {
    K.hurt = Math.max(0, K.hurt - dt);
    K.moving = false;
    if (K.dead) {
      K.dead += dt;
      return;
    }
    const fighting = raid && raid.phase !== 'end';
    if (fighting) {
      K.emote = null;
      if (K.inside) {
        const home = itemById(K.inside);
        if (home && standing(home)) return;
        K.inside = null;
        K.panic = 1.2;
      }
      const goal = nearestShelter();
      if (goal) {
        const at = door(goal);
        K.moving = moveBody(K, at.x, at.y, 2.4, dt, goal) > 0;
        if (rectDist(goal, K.x, K.y) < K.r + 0.12) {
          K.inside = goal.id;
          burst(K.x, K.y, 0.4, 6, [PAPER, MINT], { speed: 1, up: 1.5, size: 0.04 });
        }
        return;
      }
      // Nowhere left to hide: run away from the closest Bootleg, inside the block.
      let threat = null;
      let best = Infinity;
      enemies.forEach(enemy => {
        const d = dist(enemy.x, enemy.y, K.x, K.y);
        if (d < best) {
          best = d;
          threat = enemy;
        }
      });
      if (threat && best < 4) {
        const ax = K.x - threat.x;
        const ay = K.y - threat.y;
        const n = Math.hypot(ax, ay) || 1;
        let gx = K.x + (ax / n) * 2;
        let gy = K.y + (ay / n) * 2;
        gx = clamp(gx, bounds.x0 + 0.4, bounds.x1 - 0.4);
        gy = clamp(gy, bounds.y0 + 0.4, bounds.y1 - 0.4);
        K.moving = moveBody(K, gx, gy, 2.1, dt) > 0;
      }
      return;
    }
    if (K.inside) {
      const home = itemById(K.inside);
      const at = home ? door(home) : { x: K.x, y: K.y };
      K.x = at.x;
      K.y = at.y + 0.2;
      K.inside = null;
      K.wait = 1.2;
      K.act = 'look';
      K.goal = null;
    }
    if (K.goal) {
      const moved = moveBody(K, K.goal.x, K.goal.y, 0.9, dt);
      K.moving = moved > 0;
      if (dist(K.x, K.y, K.goal.x, K.goal.y) < 0.08 || (!K.moving && K.stuck > 0.3)) {
        K.goal = null;
        if (K.act === 'inspect') {
          K.emote = 'inspect';
          const visit = itemById(K.visit);
          if (visit) K.face = center(visit).x >= K.x ? 1 : -1;
        }
      }
      return;
    }
    K.wait -= dt;
    if (K.wait <= 0) chooseAct();
  };
  // The first blow that would fell the hustler in a fight does not, yet: time slows to a crawl and the player
  // has a moment to tap him free. Miss it, or take a second such blow in the same fight, and he falls for good.
  const RESCUE_TIME = 2.4;
  let rescue = null;
  const fallHustler = () => {
    rescue = null;
    K.hp = 0;
    K.dead = 0.001;
    burst(K.x, K.y, 0.6, 18, [YELLOW, GOLD, hustlerLook.brand, PAPER], { speed: 2.5, up: 3.5, size: 0.07 });
    play('lose');
    onHustlerDown();
  };
  // The hustler is no fighter, and he stays as hard to bring down whatever the gang: a blow hurts him by how hard
  // it is for the Bootlegs of this Problem, so a plain Läufer alone needs about ten seconds, a Schläger under four.
  // On a young block he holds out longer still, while the crew is small.
  const HUSTLER_GRIT = 10;
  const hurtHustler = amount => {
    if (K.dead || rescue) return;
    const punch = raid ? GANG.kid.dps * dpsScale(raid.n) * (raid.punch || 1) : 0;
    K.hp -= punch ? (amount / punch) * (hustlerMaxHp() / HUSTLER_GRIT) * rookieEase() ** 2 : amount;
    K.hurt = 0.25;
    if (K.hp > 0) return;
    if (raid && !raid.rescued) {
      raid.rescued = true;
      K.hp = 1;
      rescue = { left: RESCUE_TIME };
      play('alarm');
      return;
    }
    fallHustler();
  };
  const updateRescue = real => {
    if (!rescue) return;
    // The fight was won in the slow beat: he gets up on his own.
    if (!raid || raid.phase === 'end') {
      rescue = null;
      return;
    }
    rescue.left -= real;
    if (rescue.left <= 0) fallHustler();
  };

  // ---------------------------------------------------------------------------------------------
  // Raids: the Bootlegs come from outside the block, go for buildings, and hunt the hustler.

  const enemies = [];
  const shots = [];
  // Bootlegs on the run: at home they are let go, on a battlefield they can still be cut down until they reach their stash.
  const fled = enemy => enemy.fleeing && !(heist && raid && raid.phase === 'fight' && enemy.y > heist.lines.stop - 0.6);
  let raid = null;
  let enemyId = 0;
  // Bootlegs grow with every raid and with your Base: a bigger block draws a tougher gang, and pays more for it.
  // They come as a crowd: many of them, each a little weaker than one alone would be.
  const raidPower = () => 1.5 ** Math.max(0, hqLevel() - 4);
  const HORDE = 0.8;
  const hpScale = n => HORDE * 1.17 ** (n - 1) * (n > 25 ? 1.04 ** (n - 25) : 1) * raidPower();
  const dpsScale = n => HORDE * 1.07 ** (n - 1) * Math.sqrt(raidPower());
  const rewardScale = n => 1.12 ** (n - 1) * raidPower();
  const raidSize = n => Math.min(64, 4 + Math.floor(n * 1.25));
  const eliteShare = n => (n >= ELITE.from ? ELITE.share : 0);
  // What the elites add to the gang's average health or punch.
  const eliteBoost = (n, key) => 1 + eliteShare(n) * (ELITE[key] - 1);
  const raidWaves = n => Math.min(6, 1 + Math.floor((n - 1) / 4));
  const raidSideCount = n => (n < 4 ? 1 : n < 10 ? 2 : 3);
  // Every fifth Problem is a boss Problem: the Pate comes along himself, behind the last wave.
  const BOSS_EVERY = 5;
  const bossProblem = count => count % BOSS_EVERY === 0;
  const raidPlanSize = (n, boss) => raidSize(n) + (boss ? 1 : 0);
  // Never from the top: Bootlegs coming down from there would walk out of sight behind the buildings.
  const RAID_SIDES = ['e', 's', 'w'];
  // A Bootleg of any kind the gang has by raid n, the common ones more often.
  const rollGang = n => {
    const kinds = Object.keys(GANG_WEIGHT).filter(type => n >= GANG[type].from);
    let roll = Math.random() * kinds.reduce((sum, type) => sum + GANG_WEIGHT[type], 0);
    for (const kind of kinds) {
      roll -= GANG_WEIGHT[kind];
      if (roll < 0) return kind;
    }
    return kinds[0];
  };
  const waveSize = (count, waves, w) => Math.round(count / waves) + (w === waves - 1 ? count - Math.round(count / waves) * waves : 0);
  const raidPlan = (n, boss) => {
    const count = raidSize(n);
    const waves = raidWaves(n);
    const sides = [...RAID_SIDES];
    for (let i = sides.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [sides[i], sides[j]] = [sides[j], sides[i]];
    }
    sides.length = raidSideCount(n);
    const plan = [];
    let at = 0.4;
    for (let w = 0; w < waves; w += 1) {
      // Every wave picks its own side, so the defence has to cover more than one approach.
      const lane = { side: sides[w % sides.length], at: 0.15 + Math.random() * 0.7 };
      const size = waveSize(count, waves, w);
      for (let i = 0; i < size; i += 1) {
        // Now and then one breaks away and comes round from somewhere else entirely.
        const flank = Math.random() < 0.25 ? { side: RAID_SIDES[Math.floor(Math.random() * RAID_SIDES.length)], at: 0.1 + Math.random() * 0.8 } : null;
        plan.push({ type: rollGang(n), lane: flank || lane, at, wave: w });
        at += 0.4 + Math.random() * 0.9;
      }
      at += 9;
    }
    if (boss) plan.push({ type: 'boss', chief: true, lane: { side: sides[0], at: 0.5 }, at: at - 6, wave: waves - 1 });
    return plan.sort((a, b) => a.at - b.at);
  };
  // A good walk out from your land, so there is time to see them coming; each one at its own distance
  // and spot along the stretch its wave comes from.
  const lanePoint = (lane, near = false) => {
    const { x0, y0, x1, y1 } = bounds;
    const j = (Math.random() - 0.5) * (near ? 1.6 : 5);
    const far = near ? 1.6 : 4.5 + Math.random() * 3.5;
    if (lane.side === 'n') return { x: lerp(x0, x1, lane.at) + j, y: y0 - far };
    if (lane.side === 's') return { x: lerp(x0, x1, lane.at) + j, y: y1 + far };
    if (lane.side === 'w') return { x: x0 - far, y: lerp(y0, y1, lane.at) + j };
    return { x: x1 + far, y: lerp(y0, y1, lane.at) + j };
  };
  // On a Goldene-Bootlegs weekend every fifth Bootleg is gold and pays five times as much.
  const GOLD_SHARE = 0.2;
  const GOLD_PAY = 5;
  // The copies a Kopierer leaves behind: weaker, smaller, and worth little.
  const COPY = { hp: 0.4, pay: 0.3, size: 0.85 };
  // The first time a new kind of Bootleg turns up, the player is told what it does.
  const meetFoe = key => {
    if (S.met.includes(key)) return;
    S.met.push(key);
    onFoeMet(key);
  };
  const spawnEnemy = (type, lane, squad = null, copy = false) => {
    const g = GANG[type];
    const n = raid.n;
    // On a Raid they stand in their line or come out of their stash; at home from a good walk out.
    const at = lane.slot || (lane.door ? { x: lane.door.x + (Math.random() - 0.5) * 0.8, y: lane.door.y + Math.random() * 0.4 } : lanePoint(lane));
    const elite = !g.boss && !copy && Math.random() < eliteShare(n);
    const hp = g.hp * hpScale(n) * (raid.tough || 1) * (elite ? ELITE.hp : 1) * (copy ? COPY.hp : 1);
    enemyId += 1;
    enemies.push({
      id: enemyId, type, x: at.x, y: at.y, sx: at.x, sy: at.y, r: g.size * 0.45 * (elite ? ELITE.size : 1) * (copy ? COPY.size : 1),
      hp, max: hp, dps: g.dps * dpsScale(n) * (raid.punch || 1) * (elite ? ELITE.dps : 1), speed: g.speed * (0.92 + Math.random() * 0.16),
      t: Math.random() * 10, face: 1, target: null, retarget: 0, stun: 0, slow: 0, slowAmt: 0,
      hit: 0, swing: 0, fly: null, fleeing: false, walk: 0, look: Math.floor(Math.random() * 4),
      squad, slot: lane.slot || null, gold: eventIs('gold') && !g.boss && Math.random() < GOLD_SHARE,
      elite, copy, hyped: false, shout: 2 + Math.random() * 3
    });
    if (g.text) meetFoe(type);
    if (elite) meetFoe('elite');
    return enemies[enemies.length - 1];
  };
  // A Kopierer that goes down leaves copies of himself standing where he fell.
  const splitCopies = enemy => {
    const split = GANG[enemy.type].split;
    for (let i = 0; i < split; i += 1) {
      const copy = spawnEnemy('kid', { door: { x: enemy.x + (i - (split - 1) / 2) * 0.5, y: enemy.y - 0.2 } }, null, true);
      copy.stun = 0.4;
    }
    raid.total += split;
    floatText(enemy.x, enemy.y, 1.7, 'KOPIEN!', '#4fd1ff', 0.8);
    burst(enemy.x, enemy.y, 0.5, 12, [PAPER, '#4fd1ff', '#c9c8c3'], { speed: 2, up: 2.4, size: 0.07, life: 0.8, kind: 'shirt' });
  };
  // The Pate of a boss Problem: much tougher than any other, his bar across the screen. Every few seconds he
  // stamps the ground and hurts everything round him, and once he is down to half he calls in his men.
  const CHIEF = { hp: 2.5, stomp: 5.5, reach: 2.4, punch: 1.6, men: 4 };
  const makeChief = enemy => {
    enemy.hp *= CHIEF.hp;
    enemy.max = enemy.hp;
    enemy.chief = true;
    enemy.stomp = CHIEF.stomp * 0.6;
  };
  const updateChief = (enemy, dt) => {
    enemy.stomp -= dt;
    if (!enemy.called && enemy.hp < enemy.max / 2) {
      enemy.called = true;
      for (let i = 0; i < CHIEF.men; i += 1) spawnEnemy(rollGang(raid.n), { door: { x: enemy.x + (Math.random() - 0.5) * 2, y: enemy.y + (Math.random() - 0.5) * 1.4 } });
      raid.total += CHIEF.men;
      moments.push({ kind: 'callout', x: enemy.x, y: enemy.y, h: 1.9, text: 'VERSTÄRKUNG!', color: SIGNAL, t: 0, dur: 1.6 });
      play('whistle');
    }
    if (enemy.stomp > 0 || enemy.stun > 0 || enemy.fly) return;
    const near = world().filter(item => isTarget(item) && dist(center(item).x, center(item).y, enemy.x, enemy.y) <= CHIEF.reach + 0.5);
    const hustler = hustlerExposed() && dist(K.x, K.y, enemy.x, enemy.y) <= CHIEF.reach;
    if (!near.length && !hustler) return;
    enemy.stomp = CHIEF.stomp;
    near.forEach(item => {
      item.hp = (item.hp === undefined ? maxHp(item) : item.hp) - blowOn(item, enemy.dps * CHIEF.punch);
      item.shake = 0.3;
      if (item.hp <= 0) destroyItem(item);
    });
    if (hustler) hurtHustler(GANG.brute.dps * dpsScale(raid.n) * raid.punch * CHIEF.punch * 0.5);
    moments.push({ kind: 'ring', x: enemy.x, y: enemy.y, r: CHIEF.reach, t: 0, dur: 0.45, color: '#c9b48a' });
    moments.push({ kind: 'pow', x: enemy.x, y: enemy.y, t: 0, dur: 0.6, text: 'STAMPF!', boss: true, tilt: -0.1 });
    burst(enemy.x, enemy.y, 0.1, 16, ['#c9b48a', '#8a7a5c', '#5a4a3c'], { speed: 2.4, up: 1.6, size: 0.07, life: 0.7, kind: 'debris' });
    shake = Math.max(shake, 0.4);
    hitstop = Math.max(hitstop, 0.06);
    play('heavy');
  };
  // Buildings hold out as long at any level: a plain Läufer of this Problem needs WALL_GRIT seconds for each point
  // of a building's base health, so a stronger gang wears the block down no faster. The crew takes blows as they come.
  const WALL_GRIT = 0.08;
  const blowOn = (item, amount) => {
    if (kindOf(item) === 'crew' || !raid) return amount;
    const punch = GANG.kid.dps * dpsScale(raid.n) * (raid.punch || 1);
    return (amount / punch) * (maxHp(item) / (TYPES[item.type].hp * WALL_GRIT));
  };
  const WAVE_HOLD = 15;
  const WAVE_BREATH = 2.5;
  // `revenge` names the gang when this is its counterattack.
  const startRaid = (revenge = null) => {
    const boss = !revenge && bossProblem(S.raidN);
    const { n, tough, punch } = problemLevel(boss);
    raid = { n, tough: tough * (revenge ? REVENGE.tough : 1), punch: punch * (revenge ? REVENGE.tough : 1), count: S.raidN, boss, revenge, phase: 'warn', t: 5, plan: [], clock: 0, held: 0, total: raidPlanSize(n, boss), waves: raidWaves(n), killed: 0, earned: 0, stolen: 0, lost: [], result: null, streak: 0, streakAt: -9 };
    play('alarm');
    onRaidWarn();
  };
  const beginFight = () => {
    raid.phase = 'fight';
    // A battle's army stands on the field already; only its reinforcements are still to come.
    if (!raid.heist) {
      raid.plan = raidPlan(raid.n, raid.boss);
      raid.total = raid.plan.length;
      raid.waves = raidWaves(raid.n);
    }
    raid.clock = 0;
  };
  const targetFor = enemy => {
    const g = GANG[enemy.type];
    if (hustlerExposed() && (dist(enemy.x, enemy.y, K.x, K.y) < 3.2 || g.boss || !world().some(isTarget))) return 'hustler';
    const pool = world().filter(isTarget);
    if (g.boss && K.inside) {
      const home = itemById(K.inside);
      if (home && standing(home)) return home.id;
    }
    // Retro close by draws everyone but the boss onto himself.
    const bouncer = !g.boss && pool.find(item => TYPES[item.type].taunt && dist(enemy.x, enemy.y, center(item).x, center(item).y) <= TYPES[item.type].taunt);
    if (bouncer) return bouncer.id;
    let candidates = pool;
    if (enemy.type === 'spray') candidates = pool.filter(item => kindOf(item) === 'crew');
    if (enemy.type === 'brute') candidates = pool.filter(item => kindOf(item) !== 'crew');
    if (!candidates.length) candidates = pool;
    if (!candidates.length) return hustlerExposed() ? 'hustler' : null;
    let best = null;
    let bestD = Infinity;
    candidates.forEach(item => {
      const d = rectDist(item, enemy.x, enemy.y);
      if (d < bestD) {
        bestD = d;
        best = item;
      }
    });
    return best.id;
  };
  // Heroes who fall are gone for good, at home and on a Raid; buildings turn to rubble and come
  // back after the raid.
  const crewDown = item => {
    const c = center(item);
    const index = world().indexOf(item);
    if (index < 0) return;
    world().splice(index, 1);
    if (heist) {
      S.items.splice(S.items.indexOf(item), 1);
      raid.fallen = (raid.fallen || 0) + 1;
    }
    refreshOcc();
    raid.lost.push(item.type);
    S.stats.fallen += 1;
    moments.push({ kind: 'fallen', type: item.type, level: item.level, id: item.id, face: crewState(item).face, x: c.x, y: c.y, t: 0, dur: 2.6 });
    burst(c.x, c.y, 0.6, 14, ['#8a8a8e', PAPER, SIGNAL], { speed: 1.8, up: 2.5, size: 0.05 });
    play('down');
    shake = Math.max(shake, 0.3);
    enemies.forEach(enemy => {
      if (enemy.target === item.id) enemy.retarget = 0;
    });
    onCrewDown(item);
  };
  const destroyItem = item => {
    if (kindOf(item) === 'crew') {
      crewDown(item);
      return;
    }
    item.ruin = true;
    item.hp = 0;
    // The Bootlegs take the cash from a stand; posters and walls just lose the fans they gathered.
    const stock = Math.floor(item.stock || 0);
    if (stock > 0 && kindOf(item) === 'sale') {
      raid.stolen += stock;
      S.stats.stolen += stock;
    }
    item.stock = 0;
    raid.lost.push(item.type);
    S.stats.lost += 1;
    const c = center(item);
    burst(c.x, c.y, 0.6, 22, ['#8a8a8e', '#5b5b61', PAPER, '#c9c8c3'], { speed: 2.4, up: 3, size: 0.09, kind: 'debris' });
    burst(c.x, c.y, 0.8, 10, ['rgba(80,80,84,0.5)'], { speed: 0.8, up: 1.2, g: -0.5, life: 1.6, size: 0.25, kind: 'smoke' });
    play('crash');
    shake = Math.max(shake, 0.35);
    if (K.inside === item.id) {
      K.inside = null;
      const at = door(item);
      K.x = at.x;
      K.y = at.y + 0.1;
      K.panic = 1.2;
      onHustlerExposed(item);
    }
    enemies.forEach(enemy => {
      if (enemy.target === item.id) enemy.retarget = 0;
    });
  };
  let shake = 0;
  // A heavy hit or the last KO freezes the world for a beat.
  let hitstop = 0;
  const killEnemy = enemy => {
    const g = GANG[enemy.type];
    const index = enemies.indexOf(enemy);
    if (index >= 0) enemies.splice(index, 1);
    raid.killed += 1;
    S.stats.kills += 1;
    if (enemy.squad && enemy.squad.boss) bossDown(enemy);
    if (g.split && !enemy.copy) splitCopies(enemy);
    // KOs in quick succession build a streak, and every link pays a tenth more, up to double.
    raid.streak = clock - raid.streakAt < 1.6 ? raid.streak + 1 : 1;
    raid.streakAt = clock;
    const streak = 1 + Math.min(10, raid.streak - 1) * 0.1;
    const reward = Math.ceil(g.reward * rewardScale(raid.n) * streak * (enemy.gold ? GOLD_PAY : 1) * (enemy.elite ? ELITE.pay : 1) * (enemy.copy ? COPY.pay : 1));
    raid.earned += reward;
    S.cash += reward;
    S.earned += reward;
    // The Bootleg goes flying away from the base, the fakes fly out of the hoodie.
    const home = baseCenter();
    const away = dist(enemy.x, enemy.y, home.x, home.y) || 1;
    const push = g.boss ? 1.2 : 2.6;
    moments.push({ kind: 'ko', enemy: { ...enemy, hit: 0, stun: 0, slow: 0, fly: null, hp: enemy.max }, x: enemy.x, y: enemy.y, vx: ((enemy.x - home.x) / away) * push, vy: ((enemy.y - home.y) / away) * push, t: 0, dur: 0.75 });
    burst(enemy.x, enemy.y, 0.5, g.boss ? 8 : 3, [HOODIES[enemy.id % HOODIES.length], '#3a3a3d', PAPER], { speed: 2.2, up: 3.2, size: 0.13, life: 1.1, kind: 'shirt' });
    burst(enemy.x, enemy.y, 0.5, g.boss ? 22 : 10, [YELLOW, PAPER, MINT], { speed: 2.6, up: 2.4, size: 0.045, kind: 'spark' });
    const big = g.boss || enemy.elite || enemy.type === 'brute' || enemy.type === 'shield' || raid.streak >= 3;
    if (big) moments.push({ kind: 'pow', x: enemy.x, y: enemy.y, t: 0, dur: 0.55, text: raid.streak >= 3 ? `${raid.streak}× KO` : ['K.O.', 'DOWN!', 'RAUS!', 'ERLEDIGT!'][enemy.id % 4], boss: g.boss, tilt: enemy.id % 2 ? -0.12 : 0.12 });
    if (g.boss) {
      hitstop = 0.25;
      shake = Math.max(shake, 0.45);
    } else if (big) {
      hitstop = Math.max(hitstop, 0.06);
      shake = Math.max(shake, 0.12);
    }
    fly(proj(enemy.x, enemy.y, 0.5), reward);
    if (enemy.gold) {
      floatText(enemy.x, enemy.y, 1.7, `GOLD ×${GOLD_PAY}`, GOLD, 1);
      burst(enemy.x, enemy.y, 0.6, 18, [GOLD, YELLOW, PAPER], { speed: 2.6, up: 3.4, size: 0.06, life: 1.1, kind: 'spark' });
    }
    play('ko', raid.streak);
    if (raid.streak >= 5 && raid.streak % 5 === 0) play('combo');
  };
  // Single hits (cards, zaps, taps) bounce off a shield; splash gets around it.
  const hurtEnemy = (enemy, amount, single = false) => {
    if (!enemies.includes(enemy)) return;
    const armor = GANG[enemy.type].armor;
    const charging = heist && heist.charge > 0 ? CHARGE_HIT : 1;
    const dealt = (single && armor ? amount * (1 - armor) : amount) * charging;
    if (enemy.squad && enemy.squad.state === 'hold') advance(enemy.squad);
    enemy.hp -= dealt;
    enemy.hit = 0.12;
    if (dealt >= enemy.max * 0.08) floatText(enemy.x, enemy.y, 1.1, fmt(Math.max(1, Math.round(dealt))), dealt >= enemy.max * 0.3 ? SIGNAL : INK, dealt >= enemy.max * 0.3 ? 0.9 : 0.65);
    if (enemy.hp <= 0) killEnemy(enemy);
  };
  const splashAt = (x, y, radius, amount) => {
    [...enemies].forEach(enemy => {
      if (enemy.fly) return;
      const d = dist(enemy.x, enemy.y, x, y);
      if (d <= radius) hurtEnemy(enemy, amount * (1 - (d / radius) * 0.4));
    });
  };
  // The gang's helpers: an Einpeitscher drives on whoever is near him, a Flicker patches them up.
  const updateHelpers = dt => {
    const able = enemy => !enemy.fly && !enemy.fleeing && enemy.stun <= 0;
    const shouters = enemies.filter(enemy => GANG[enemy.type].aura && able(enemy));
    const medics = enemies.filter(enemy => GANG[enemy.type].heal && able(enemy));
    enemies.forEach(enemy => {
      enemy.hyped = shouters.some(one => one !== enemy && dist(one.x, one.y, enemy.x, enemy.y) <= GANG[one.type].aura.r);
    });
    shouters.forEach(one => {
      one.shout -= dt;
      if (one.shout > 0 || !enemies.some(enemy => enemy.hyped && dist(one.x, one.y, enemy.x, enemy.y) <= GANG[one.type].aura.r)) return;
      one.shout = 4 + Math.random() * 3;
      one.swing = 0.45;
      floatText(one.x, one.y, 1.6, ['LOS!', 'DRAUF!', 'WEITER!', 'HOLT SIE!'][Math.floor(Math.random() * 4)], SIGNAL, 0.75);
    });
    medics.forEach(one => {
      const { r, rate } = GANG[one.type].heal;
      enemies.forEach(enemy => {
        if (enemy === one || enemy.hp >= enemy.max || enemy.fly || dist(one.x, one.y, enemy.x, enemy.y) > r) return;
        enemy.hp = Math.min(enemy.max, enemy.hp + enemy.max * rate * dt);
        if (Math.random() < dt * 2.5) burst(enemy.x, enemy.y, 0.7, 2, [MINT, PAPER], { speed: 0.5, up: 1.4, g: -0.6, size: 0.05, life: 0.7, kind: 'spark' });
      });
    });
  };
  const updateEnemies = dt => {
    updateHelpers(dt);
    [...enemies].forEach(enemy => {
      const g = GANG[enemy.type];
      enemy.t += dt;
      enemy.hit = Math.max(0, enemy.hit - dt);
      enemy.swing = Math.max(0, enemy.swing - dt);
      if (enemy.fly) {
        enemy.fly.t += dt;
        const p = Math.min(1, enemy.fly.t / enemy.fly.dur);
        enemy.x = lerp(enemy.fly.fx, enemy.fly.tx, p);
        enemy.y = lerp(enemy.fly.fy, enemy.fly.ty, p);
        enemy.h = Math.sin(p * Math.PI) * (enemy.fly.arc || 2.2);
        if (p >= 1) {
          const { slam } = enemy.fly;
          enemy.stun = enemy.fly.stun === undefined ? 1 : enemy.fly.stun;
          enemy.fly = null;
          enemy.h = 0;
          burst(enemy.x, enemy.y, 0, 6, ['#c9c8c3'], { speed: 1, up: 1, size: 0.05 });
          // Launched sky-high, the landing hurts too.
          if (slam) {
            moments.push({ kind: 'ring', x: enemy.x, y: enemy.y, r: 0.8, t: 0, dur: 0.3, color: '#c9b48a' });
            burst(enemy.x, enemy.y, 0.1, 8, ['#c9b48a', '#8a7a5c'], { speed: 1.6, up: 1, size: 0.06, life: 0.5, kind: 'debris' });
            hurtEnemy(enemy, slam);
          }
        }
        return;
      }
      if (enemy.chief && !enemy.fleeing) updateChief(enemy, dt);
      if (enemy.stun > 0) {
        enemy.stun -= dt;
        return;
      }
      const boost = enemy.hyped ? 1 + GANG.shouter.aura.boost : 1;
      let speed = enemy.speed * boost;
      const dps = enemy.dps * boost;
      if (zones.some(z => dist(enemy.x, enemy.y, z.x, z.y) < z.r)) {
        enemy.slow = Math.max(enemy.slow, 0.3);
        enemy.slowAmt = Math.max(enemy.slowAmt, 0.5);
      }
      if (enemy.slow > 0) {
        enemy.slow -= dt;
        speed *= 1 - enemy.slowAmt;
      }
      if (enemy.fleeing) {
        moveBody(enemy, enemy.sx, enemy.sy, speed * 1.2, dt);
        if (dist(enemy.x, enemy.y, enemy.sx, enemy.sy) < 0.3) enemies.splice(enemies.indexOf(enemy), 1);
        return;
      }
      // In the line: a squad keeps its place in the formation until it charges.
      if (enemy.squad && enemy.squad.state === 'hold') {
        moveBody(enemy, enemy.slot.x, enemy.slot.y, speed * 0.6, dt);
        return;
      }
      enemy.retarget -= dt;
      const current = enemy.target === 'hustler' ? (hustlerExposed() ? 'hustler' : null) : itemById(enemy.target);
      if (!current || (current !== 'hustler' && !isTarget(current)) || enemy.retarget <= 0) {
        enemy.target = targetFor(enemy);
        enemy.retarget = 1.2;
      }
      if (enemy.target === 'hustler') {
        const reach = (g.reach || 0) + K.r + enemy.r + 0.1;
        if (dist(enemy.x, enemy.y, K.x, K.y) > reach) moveBody(enemy, K.x, K.y, speed, dt);
        else {
          enemy.face = K.x >= enemy.x ? 1 : -1;
          if (enemy.swing <= 0) enemy.swing = 0.45;
          hurtHustler(Math.min(dps, g.boss ? GANG.brute.dps * dpsScale(raid.n) * raid.punch : dps) * dt);
          if (g.reach) shootAt(enemy, K.x, K.y, dt);
        }
        return;
      }
      const item = itemById(enemy.target);
      if (!item) return;
      const reach = (g.reach || 0) + enemy.r + 0.08;
      const at = attackPoint(item, enemy.x, enemy.y);
      if (dist(enemy.x, enemy.y, at.x, at.y) > reach) {
        moveBody(enemy, at.x, at.y, speed, dt, kindOf(item) === 'crew' ? item : null);
      } else {
        const c = center(item);
        enemy.face = c.x >= enemy.x ? 1 : -1;
        if (enemy.swing <= 0) {
          enemy.swing = 0.45;
          if (!g.reach) play('thud');
        }
        item.hp = (item.hp === undefined ? maxHp(item) : item.hp) - blowOn(item, dps * dt * (heist && heist.order === 'halten' ? HOLD_GUARD : 1));
        item.shake = 0.15;
        if (g.reach) shootAt(enemy, c.x, c.y, dt);
        if (item.hp <= 0) destroyItem(item);
      }
    });
  };
  // What a Bootleg at range sends over while it hurts its target: puffs of paint, or a firecracker now and then.
  const shootAt = (enemy, tx, ty, dt) => {
    if (enemy.type === 'bomber') {
      if (Math.random() < dt * 1.2) {
        enemy.swing = 0.45;
        shots.push({ kind: 'cracker', x: enemy.x, y: enemy.y, h: 0.6, fx: enemy.x, fy: enemy.y, tx: tx + (Math.random() - 0.5) * 0.6, ty: ty + (Math.random() - 0.5) * 0.4, t: 0, dur: 0.7, arc: 1.6 });
      }
      return;
    }
    if (Math.random() >= dt * 4) return;
    const colors = ['#ff4fa3', '#4fd1ff', YELLOW, MINT_D];
    shots.push({ kind: 'spray', x: enemy.x, y: enemy.y, h: 0.35, fx: enemy.x, fy: enemy.y, tx, ty, t: 0, dur: 0.35, color: colors[enemy.id % colors.length] });
  };

  // The crew guards its post: it steps out toward the closest Bootleg within its beat, then walks back.
  const crewState = item => {
    if (!item.crew) item.crew = { cd: Math.random() * 0.5, aim: 0.35, face: item.id % 2 ? 1 : -1, recoil: 0, x: item.x + 0.5, y: item.y + 0.5, r: 0.22, walk: 0, moving: false };
    return item.crew;
  };
  // Where a hero stands guard: the spot on your block, or its place in the line on a Raid.
  const postOf = item => {
    if (!heist || !heist.posts.has(item.id)) return { x: item.x + 0.5, y: item.y + 0.5 };
    const post = heist.posts.get(item.id);
    return { x: post.x, y: post.y - heist.front };
  };
  const crewAt = item => {
    const state = crewState(item);
    return { x: state.x, y: state.y };
  };
  // Where a fight is fought: your block with a step round it, or the Raid's ground. Nobody is flung, dashes or walks
  // out of it, so the whole fight stays where the camera can keep it in view. One already outside, a Bootleg still on
  // its way in, is not pulled in but goes no further out.
  const ARENA_EDGE = 1;
  const inArena = (x, y, from = { x, y }) => {
    const a = heist ? { x0: heist.x0 + 0.3, x1: heist.x1 - 0.3, y0: 0.3, y1: heist.y1 + 1.5 } : { x0: bounds.x0 - ARENA_EDGE, x1: bounds.x1 + ARENA_EDGE, y0: bounds.y0 - ARENA_EDGE, y1: bounds.y1 + ARENA_EDGE };
    return { x: clamp(x, Math.min(a.x0, from.x), Math.max(a.x1, from.x)), y: clamp(y, Math.min(a.y0, from.y), Math.max(a.y1, from.y)) };
  };
  const crewWalk = (item, state, goalX, goalY, speed, dt) => {
    const { x: gx, y: gy } = inArena(goalX, goalY, state);
    const moved = dist(state.x, state.y, gx, gy) > 0.05 ? moveBody(state, gx, gy, speed, dt, item) : 0;
    state.moving = moved > 0.001;
  };
  // How far a fighter strays from its post. Once a Problem has sent its last Bootleg, the crew goes after whoever is
  // left wherever they are, so nobody can hold out at the edge of the block out of everyone's reach.
  const mopUp = () => Boolean(raid && !heist && raid.phase === 'fight' && !raid.plan.length);
  const roamOf = item => (mopUp() && info(item).roam ? Infinity : info(item).roam * chargeRoam());
  const crewStep = (item, state, target, range, dt) => {
    const { x: px, y: py } = postOf(item);
    const hero = HERO_MOVES[item.type];
    const speed = hero.speed * (heist && heist.charge > 0 ? 1.3 : 1);
    const roam = roamOf(item);
    if (!target) {
      crewWalk(item, state, px, py, speed, dt);
      return;
    }
    const d = dist(state.x, state.y, target.x, target.y) || 1;
    const melee = Boolean(hero.melee);
    // A shooter with a brawler in its face steps back to keep its distance, as far as the post allows.
    if (!melee) {
      const pest = enemies.find(enemy => !enemy.fly && !GANG[enemy.type].reach && dist(enemy.x, enemy.y, state.x, state.y) < 0.9);
      if (pest) {
        const e = dist(state.x, state.y, pest.x, pest.y) || 1;
        let bx = state.x + ((state.x - pest.x) / e) * 1.4;
        let by = state.y + ((state.y - pest.y) / e) * 1.4;
        const off = dist(bx, by, px, py);
        if (off > roam) {
          bx = px + ((bx - px) / off) * roam;
          by = py + ((by - py) / off) * roam;
        }
        if (!hiddenAt(bx, by) && tileFree(bx, by)) {
          crewWalk(item, state, bx, by, speed * 1.15, dt);
          return;
        }
      }
    }
    // Close enough: hold the spot.
    const want = melee ? hero.hold || 0.9 : range * 0.75;
    if (d <= want) {
      state.moving = false;
      return;
    }
    let gx = target.x - ((target.x - state.x) / d) * want;
    let gy = target.y - ((target.y - state.y) / d) * want;
    const off = dist(gx, gy, px, py);
    if (off > roam) {
      gx = px + ((gx - px) / off) * roam;
      gy = py + ((gy - py) / off) * roam;
    }
    // Never step out to a spot behind a building, where nobody could see the fight.
    if (hiddenAt(gx, gy)) {
      state.moving = false;
      return;
    }
    crewWalk(item, state, gx, gy, speed, dt);
  };
  // Who to go for: whoever is closest, but a Bootleg on the hustler, a sprayer or one nearly down comes first.
  const onHustler = enemy => !heist && !K.dead && !K.inside && dist(enemy.x, enemy.y, K.x, K.y) < 2.5;
  const threatNear = (x, y, range) => {
    let best = null;
    let bestScore = Infinity;
    enemies.forEach(enemy => {
      if (enemy.fly || fled(enemy)) return;
      const d = dist(x, y, enemy.x, enemy.y);
      if (d > range) return;
      const score = d + (enemy.hp / enemy.max) * 1.2 - (onHustler(enemy) ? 2 : 0) - (GANG[enemy.type].back ? 0.8 : 0);
      if (score < bestScore) {
        best = enemy;
        bestScore = score;
      }
    });
    return best;
  };
  // Who a fighter goes for: the Bootleg it is already on until that one is done, also while a blow sends it flying,
  // when it runs to where it lands. Only a Bootleg on the hustler draws it off sooner.
  const stayOn = (state, post, reach) => {
    const foe = state.foe;
    const kept = foe && enemies.includes(foe) && !fled(foe) && (foe.fly ? { x: foe.fly.tx, y: foe.fly.ty } : foe);
    const best = threatNear(post.x, post.y, reach);
    if (kept && dist(post.x, post.y, kept.x, kept.y) <= reach + 1 && !(best && best !== foe && onHustler(best) && !onHustler(foe))) return kept;
    state.foe = best;
    return best;
  };
  // A shooter's pick in range: finish off the wounded first, then the closest.
  const pickTarget = (x, y, range) => {
    let best = null;
    let bestScore = Infinity;
    enemies.forEach(enemy => {
      if (enemy.fly || fled(enemy)) return;
      const d = dist(x, y, enemy.x, enemy.y);
      if (d > range) return;
      const score = d / range + (enemy.hp / enemy.max) * 0.8 - (GANG[enemy.type].back ? 0.25 : 0);
      if (score < bestScore) {
        best = enemy;
        bestScore = score;
      }
    });
    return best;
  };
  // The MIXEDOWN heroes. How each moves, his special with its name in big letters, and his own way to hit.
  // A brawler with `hold` stays that far from whoever he fights instead of walking right up.
  const HERO_MOVES = {
    caruso: { speed: 3.8 }, retro: { speed: 2, melee: true, hold: 2.2 }, zola: { speed: 2.5, melee: true }, kuza: { speed: 2.6 },
    blade: { speed: 3.4, melee: true }, capi: { speed: 3.3, melee: true }, johno: { speed: 2.6, melee: true }, maliq: { speed: 2.1 },
    polarise: { speed: 2.2, melee: true }, bigtoe: { speed: 2.9, melee: true }
  };
  const HERO_ULTS = {
    caruso: { name: 'GUMMI-TORNADO', cool: 10, reach: 4.5, color: '#d6283a' },
    retro: { name: 'BODYSLAM', cool: 12, reach: 6, color: '#ff4fa3' },
    zola: { name: 'THIS IS SPARTA!', cool: 12, reach: 5, color: '#ff8a1f' },
    kuza: { name: 'STERNENHAGEL', cool: 11, reach: 6, color: '#ff2d55' },
    blade: { name: 'BLUTMOND', cool: 12, reach: 5, color: '#b0102a' },
    capi: { name: 'SENSENSTURM', cool: 11, reach: 6, color: '#a64dff' },
    johno: { name: 'HIMMELFAHRT', cool: 12, reach: 5, color: '#1d1d20' },
    maliq: { name: 'ROYAL FLUSH', cool: 12, reach: 8, color: '#7b3fe4' },
    polarise: { name: 'HOMERUN', cool: 12, reach: 5, color: '#4fb6e8' },
    bigtoe: { name: 'DIAMANTHAGEL', cool: 11, reach: 5, color: '#d4a017' }
  };
  const THRUST = 2.6;
  const CAPI_GLOW = '#b45cff';
  const CAPI_FILL = 'rgba(225,200,255,0.5)';
  const CARD_COLORS = ['#ff2d55', '#3ee8ff', YELLOW, '#b49cff'];
  const foes = (x, y, reach) => enemies.filter(enemy => !enemy.fly && !fled(enemy) && dist(enemy.x, enemy.y, x, y) <= reach);
  const byDistance = (list, x, y) => list.sort((a, b) => dist(a.x, a.y, x, y) - dist(b.x, b.y, x, y));
  const healItem = (item, amount) => {
    item.hp = Math.min(maxHp(item), (item.hp === undefined ? maxHp(item) : item.hp) + amount);
  };
  // Straight up into the sky and back down: the landing hurts once more.
  const launch = (enemy, arc, dur, stun, slam) => {
    if (!enemies.includes(enemy) || enemy.fly) return;
    if (GANG[enemy.type].boss) {
      enemy.stun = Math.max(enemy.stun || 0, 0.5);
      return;
    }
    enemy.fly = { t: 0, dur, fx: enemy.x, fy: enemy.y, tx: enemy.x, ty: enemy.y, arc, stun, slam };
  };
  // A quick step to just beside a Bootleg, unless that spot is behind a building.
  const blinkTo = (state, enemy) => {
    const bx = enemy.x - Math.sign(enemy.x - state.x || 1) * 0.5;
    if (hiddenAt(bx, enemy.y)) return;
    burst(state.x, state.y, 0.7, 6, ['rgba(20,20,21,0.5)', PAPER], { speed: 0.8, up: 1, size: 0.08, life: 0.4, kind: 'smoke' });
    state.x = bx;
    state.y = enemy.y;
    state.face = enemy.x >= bx ? 1 : -1;
  };
  // Caruso's fist flies out on a rubber arm, lands, and snaps back.
  const rubberPunch = (item, enemy, dmg, dur, push = 0.5) => {
    const state = crewState(item);
    state.arm = Math.max(state.arm || 0, dur);
    state.face = enemy.x >= state.x ? 1 : -1;
    moments.push({ kind: 'stretch', id: item.id, enemy, x: enemy.x, y: enemy.y, t: 0, dur });
    play('stretch');
    later(dur * 0.45, () => {
      if (!enemies.includes(enemy)) return;
      hurtEnemy(enemy, dmg);
      burst(enemy.x, enemy.y, 0.6, 6, [YELLOW, PAPER, '#d6283a'], { speed: 1.8, up: 2, size: 0.05, life: 0.4, kind: 'spark' });
      knock(enemy, state.x, state.y, push, 0.25);
    });
  };
  // Caruso spinning with his arms stretched out: everyone within `r` gets hit and thrown off outward.
  const rubberSpin = (state, r, dmg, dur) => {
    state.spin = dur;
    state.spinFor = dur;
    whirl(state.x, state.y, r, dmg, '#d6283a', 'rgba(255,214,186,0.5)');
    foes(state.x, state.y, r).forEach(enemy => knock(enemy, state.x, state.y, 1, 0.25));
    play('stretch');
  };
  // Retro throws three cluster grenades at a time, in three stages: each grenade bursts and scatters four bomblets
  // around where it lands, and each bomblet bursts once more into three little ones that pop all round it.
  const GRENADES = 3;
  const BOMBLETS = 4;
  const SPLINTERS = 3;
  const landGrenade = shot => {
    const { tx: x, ty: y } = shot;
    const t = info(shot.owner);
    splashAt(x, y, 1, shot.dmg * 0.5);
    foes(x, y, 1).forEach(enemy => knock(enemy, x, y, t.knock * 0.6, t.stun));
    moments.push({ kind: 'ring', x, y, r: 1, t: 0, dur: 0.35, color: '#ff4fa3' });
    burst(x, y, 0.3, 16, ['#ff8a1f', YELLOW, PAPER], { speed: 3, up: 2, g: 3, size: 0.05, life: 0.5, kind: 'spark' });
    burst(x, y, 0.2, 5, ['rgba(120,120,125,0.5)'], { speed: 0.6, up: 0.8, g: -0.4, size: 0.14, life: 0.8, kind: 'smoke' });
    splat(x, y, 0.45, 'rgba(40,40,44,0.5)', 4);
    shake = Math.max(shake, 0.15);
    play('boom');
    for (let k = 0; k < BOMBLETS; k += 1) {
      const a = (k / BOMBLETS) * TAU + Math.random() * 0.8;
      const r = 0.7 + Math.random() * 0.5;
      shots.push({ kind: 'bomblet', fx: x, fy: y, tx: x + Math.cos(a) * r, ty: y + Math.sin(a) * r, x, y, h: 0.3, stage: 2, t: -k * 0.04, dur: 0.28 + Math.random() * 0.1, dmg: shot.dmg * 0.2 });
    }
  };
  const landBomblet = shot => {
    const { tx: x, ty: y } = shot;
    const last = shot.stage === 3;
    const r = last ? 0.4 : 0.6;
    splashAt(x, y, r, shot.dmg);
    moments.push({ kind: 'ring', x, y, r, t: 0, dur: last ? 0.2 : 0.25, color: last ? '#ff4fa3' : YELLOW });
    burst(x, y, 0.2, last ? 4 : 7, last ? ['#ff4fa3', YELLOW, PAPER] : [YELLOW, '#ff8a1f', PAPER], { speed: last ? 1.5 : 2, up: 1.5, g: 3, size: last ? 0.035 : 0.04, life: 0.35, kind: 'spark' });
    play('cluster');
    if (last) return;
    for (let k = 0; k < SPLINTERS; k += 1) {
      const a = (k / SPLINTERS) * TAU + Math.random() * 1.2;
      const d = 0.45 + Math.random() * 0.3;
      shots.push({ kind: 'bomblet', stage: 3, fx: x, fy: y, tx: x + Math.cos(a) * d, ty: y + Math.sin(a) * d, x, y, h: 0.2, t: -0.04 - k * 0.05, dur: 0.2 + Math.random() * 0.08, dmg: shot.dmg * 0.3 });
    }
  };
  // A throwing star from Kuza or Kamua: out through the crowd and back to the hand, cutting both ways.
  const throwStar = (item, from, tx, ty, dmg, delay, side) => {
    const d = dist(from.x, from.y, tx, ty) || 1;
    const far = d + 0.9;
    const fx = from.x + (side ? 0.25 : -0.25);
    shots.push({ kind: 'star', owner: item, fx, fy: from.y, tx: fx + ((tx - fx) / d) * far, ty: from.y + ((ty - from.y) / d) * far, x: fx, y: from.y, h: 0.8, t: -delay, dur: 0.22 + far * 0.06, back: false, hit: new Set(), dmg, color: side ? YELLOW : '#ff2d55' });
  };
  // A blade swung all the way round: everyone within `r` of (x, y) gets `dmg`, and a ring of light follows the edge.
  const whirl = (x, y, r, dmg, color, fill) => {
    foes(x, y, r).forEach(enemy => {
      hurtEnemy(enemy, dmg);
      burst(enemy.x, enemy.y, 0.6, 5, [color, PAPER], { speed: 1.8, up: 1.5, size: 0.05, life: 0.4, kind: 'spark' });
    });
    moments.push({ kind: 'arc', x, y, r, a: Math.random() * TAU, span: Math.PI * 1.9, t: 0, dur: 0.35, color, fill });
  };
  const heroHit = (item, state, c, target, dmg) => {
    const t = info(item);
    const { x: tx, y: ty } = target;
    if (item.type === 'caruso') {
      // Two punches, then every third blow he spins round with both rubber arms out, through everyone around him.
      state.blows = (state.blows || 0) + 1;
      if (state.blows % 3) rubberPunch(item, target, dmg, 0.22);
      else rubberSpin(state, 2.2, dmg * 1.6, 0.4);
    } else if (item.type === 'retro') {
      // Cluster grenades off his belt, lobbed into the crowd one after the other: the target first, then whoever
      // stands closest to it; with nobody else there they land around it.
      const aims = [target, ...byDistance(foes(tx, ty, t.splash).filter(enemy => enemy !== target), tx, ty)];
      for (let k = 0; k < GRENADES; k += 1) {
        const a = Math.random() * TAU;
        const aim = aims[k] || { x: tx + Math.cos(a) * 0.9, y: ty + Math.sin(a) * 0.9 };
        const d = dist(c.x, c.y, aim.x, aim.y);
        shots.push({ kind: 'grenade', owner: item, fx: c.x, fy: c.y, tx: aim.x, ty: aim.y, x: c.x, y: c.y, h: 1.1, t: -k * 0.14, dur: 0.5 + d * 0.08, arc: 1.2 + d * 0.15, dmg, spin: (Math.random() < 0.5 ? -1 : 1) * (9 + Math.random() * 4) });
      }
      play('grenade');
    } else if (item.type === 'zola') {
      // One straight kick, sole first, and the Bootleg flies across the street; whoever stands next to it gets some.
      hurtEnemy(target, dmg);
      knock(target, c.x, c.y, t.knock, 1);
      foes(tx, ty, 0.9).forEach(other => {
        if (other === target) return;
        hurtEnemy(other, dmg * 0.3);
        knock(other, c.x, c.y, 1.2, 0.5);
      });
      moments.push({ kind: 'ring', x: tx, y: ty, r: 1, t: 0, dur: 0.3, color: '#ff8a1f' });
      burst(tx, ty, 0.7, 16, ['#ff8a1f', YELLOW, PAPER], { speed: 3.4, up: 1.6, size: 0.06, life: 0.5, kind: 'spark' });
      burst(c.x, c.y, 0.05, 6, ['rgba(205,200,190,0.6)'], { speed: 1.2, up: 0.3, g: -0.2, size: 0.1, life: 0.6, kind: 'smoke' });
      shake = Math.max(shake, 0.35);
      hitstop = Math.max(hitstop, 0.07);
      play('heavy');
    } else if (item.type === 'kuza') {
      // A volley from each: three stars fanned out, one after the other, Kuza's at the closest, Kamua's at the next.
      const next = byDistance(foes(c.x, c.y, rangeOf(item.type, item.level)).filter(enemy => enemy !== target), c.x, c.y)[0] || target;
      [target, next].forEach((enemy, side) => {
        const a = Math.atan2(enemy.y - c.y, enemy.x - c.x);
        const d = dist(enemy.x, enemy.y, c.x, c.y);
        [0, -1, 1].forEach((fan, n) => throwStar(item, c, c.x + Math.cos(a + fan * 0.2) * d, c.y + Math.sin(a + fan * 0.2) * d, dmg, n * 0.07 + side * 0.035, side));
      });
      [0, 0.07, 0.14].forEach(delay => later(delay, () => play('star')));
    } else if (item.type === 'blade') {
      // Three cuts in one flow, every one healing him: down across, back up the other way, and a thrust that goes
      // through the Bootleg and everyone standing behind it.
      const cut = (enemy, amount) => {
        hurtEnemy(enemy, amount);
        healItem(item, amount * t.leech);
        burst(enemy.x, enemy.y, 0.6, 7, ['#b0102a', '#ff2d55', PAPER], { speed: 2, up: 2, size: 0.05, life: 0.45, kind: 'spark' });
      };
      [[0, 0.9, -0.7], [0.15, 0.8, 0.7]].forEach(([delay, share, a]) => later(delay, () => {
        if (!enemies.includes(target)) return;
        cut(target, dmg * share);
        moments.push({ kind: 'slash', x: target.x, y: target.y, t: 0, dur: 0.25, a });
        play('blade');
      }));
      const a = Math.atan2(ty - c.y, tx - c.x);
      const dx = Math.cos(a);
      const dy = Math.sin(a);
      later(0.32, () => {
        foes(state.x, state.y, THRUST + 0.5).forEach(enemy => {
          const along = (enemy.x - state.x) * dx + (enemy.y - state.y) * dy;
          const side = (enemy.x - state.x) * dy - (enemy.y - state.y) * dx;
          if (along > -0.2 && along < THRUST && Math.abs(side) < 0.5) cut(enemy, dmg * 1.3);
        });
        moments.push({ kind: 'thrust', fx: state.x + dx * 0.3, fy: state.y + dy * 0.3, x: state.x + dx * THRUST, y: state.y + dy * THRUST, t: 0, dur: 0.3 });
        play('blade');
      });
    } else if (item.type === 'capi') {
      // The scythe goes a whole turn round him, through everyone in reach.
      whirl(c.x, c.y, t.splash, dmg, CAPI_GLOW, CAPI_FILL);
      play('sickles');
    } else if (item.type === 'johno') {
      // Uppercut and flying kick, turn about. The uppercut sends the Bootleg straight up and it lands hard;
      // the kick catches it at head height and throws it back across the street.
      state.kick = Boolean(state.kickNext);
      state.kickNext = !state.kick;
      hurtEnemy(target, dmg);
      if (state.kick) {
        knock(target, c.x, c.y, 2.2, t.stun * 0.6);
        burst(tx, ty, 0.9, 12, [YELLOW, PAPER, '#d6283a'], { speed: 2.6, up: 1.5, size: 0.05, life: 0.45, kind: 'spark' });
        moments.push({ kind: 'ring', x: tx, y: ty, r: 0.9, t: 0, dur: 0.3, color: PAPER });
        shake = Math.max(shake, 0.2);
        play('heavy');
      } else {
        launch(target, 3.2, 1, t.stun, dmg * 0.5);
        burst(tx, ty, 0.8, 10, [YELLOW, PAPER, '#d6283a'], { speed: 1.6, up: 4, size: 0.05, life: 0.5, kind: 'spark' });
        moments.push({ kind: 'ring', x: tx, y: ty, r: 0.8, t: 0, dur: 0.3, color: YELLOW });
        play('uppercut');
      }
    } else if (item.type === 'polarise') {
      // One flat swing of the nail bat: the Bootleg it catches flies off down the street, the ones beside it get the nails.
      hurtEnemy(target, dmg);
      knock(target, c.x, c.y, t.knock, t.stun);
      foes(tx, ty, t.splash).forEach(other => {
        if (other === target) return;
        hurtEnemy(other, dmg * 0.5);
        knock(other, c.x, c.y, 1.4, 0.4);
      });
      moments.push({ kind: 'arc', x: c.x, y: c.y, r: 2, a: Math.atan2(ty - c.y, tx - c.x), t: 0, dur: 0.25, color: '#9fe4ff' });
      burst(tx, ty, 0.8, 12, ['#c3c8ce', YELLOW, PAPER], { speed: 2.8, up: 1.6, size: 0.05, life: 0.45, kind: 'spark' });
      shake = Math.max(shake, 0.25);
      hitstop = Math.max(hitstop, 0.06);
      play('bat');
    } else if (item.type === 'bigtoe') {
      // Jab, cross, hook: two quick ones, then the hook that throws the Bootleg back. The diamonds flash on every hit.
      [[0, 0.5], [0.13, 0.5], [0.27, 1]].forEach(([delay, share], k) => later(delay, () => {
        const victim = enemies.includes(target) && !target.fly ? target : byDistance(foes(state.x, state.y, rangeOf(item.type, item.level)), state.x, state.y)[0];
        if (!victim) return;
        hurtEnemy(victim, dmg * share);
        burst(victim.x, victim.y, 0.8, k === 2 ? 12 : 6, [GOLD, '#eef8ff', '#9fd8ff'], { speed: 2, up: 1.6, size: 0.05, life: 0.4, kind: 'spark' });
        if (k < 2) {
          play('dusters');
          return;
        }
        knock(victim, state.x, state.y, t.knock, t.stun);
        moments.push({ kind: 'ring', x: victim.x, y: victim.y, r: 0.8, t: 0, dur: 0.3, color: GOLD });
        shake = Math.max(shake, 0.2);
        play('hook');
      }));
    } else if (item.type === 'maliq') {
      // A fan of magic cards, each one homing in on its own Bootleg.
      const list = [target, ...byDistance(foes(c.x, c.y, rangeOf(item.type, item.level)).filter(enemy => enemy !== target), c.x, c.y)].slice(0, t.hits);
      list.forEach((enemy, k) => shots.push({ kind: 'card', x: c.x, y: c.y, h: 1.4, target: enemy, speed: 10, dmg, t: -k * 0.08, spin: (k - 1) * 0.5, color: CARD_COLORS[(k + item.id) % CARD_COLORS.length] }));
      play('card');
    }
  };
  const heroUlt = (item, state, c, dmg) => {
    const ult = HERO_ULTS[item.type];
    moments.push({ kind: 'callout', x: c.x, y: c.y, h: heightOf(item.type, item.level) + 0.4, text: ult.name, color: ult.color, t: 0, dur: 1.7 });
    const near = foes(state.x, state.y, ult.reach);
    if (item.type === 'caruso') {
      // Gummi-Tornado: round and round with both arms out through everyone near him, then the big double punch.
      play('gatling');
      for (let k = 0; k < 6; k += 1) later(k * 0.18, () => rubberSpin(state, 3.2, dmg * 1.1, 1.1 - k * 0.18));
      later(1.2, () => {
        const spot = hotSpot(1.8);
        if (!spot || dist(spot.x, spot.y, state.x, state.y) > ult.reach + 1) return;
        state.arm = 0.4;
        moments.push({ kind: 'stretch', id: item.id, x: spot.x, y: spot.y, t: 0, dur: 0.4, big: true });
        later(0.15, () => {
          foes(spot.x, spot.y, 2.2).forEach(enemy => {
            hurtEnemy(enemy, dmg * 5);
            knock(enemy, state.x, state.y, 2.8, 1.2);
          });
          moments.push({ kind: 'pow', x: spot.x, y: spot.y, t: 0, dur: 0.7, text: 'BAZOOKA!', boss: true, tilt: -0.1 });
          burst(spot.x, spot.y, 0.6, 24, ['#d6283a', YELLOW, PAPER], { speed: 3.5, up: 2.5, size: 0.06, life: 0.7, kind: 'spark' });
          shake = Math.max(shake, 0.45);
          hitstop = Math.max(hitstop, 0.08);
          play('bazooka');
        });
      });
    } else if (item.type === 'retro') {
      // Bodyslam: up into the air, onto the thickest crowd, and the whole block shakes.
      const spot = hotSpot(1.8);
      const goal = spot && dist(spot.x, spot.y, state.x, state.y) <= ult.reach && !hiddenAt(spot.x, spot.y) ? spot : { x: state.x, y: state.y };
      state.dash = { fx: state.x, fy: state.y, tx: goal.x, ty: goal.y, t: 0, dur: 0.7 };
      state.hop = { t: 0, dur: 0.7, h: 1.6 };
      play('swish');
      later(0.7, () => {
        const { x, y } = goal;
        foes(x, y, ult.reach).forEach(enemy => {
          const d = dist(enemy.x, enemy.y, x, y);
          if (d <= 2.4) {
            hurtEnemy(enemy, dmg * 7 * (1 - (d / 2.4) * 0.4));
            knock(enemy, x, y, 2.6 - d * 0.4, 1.6);
          }
          if (enemies.includes(enemy) && !enemy.fly) enemy.stun = Math.max(enemy.stun || 0, GANG[enemy.type].boss ? 1 : 1.4);
        });
        [0, 0.08, 0.16].forEach((lag, i) => moments.push({ kind: 'ring', x, y, r: ult.reach * (1 - i * 0.25), t: -lag, dur: 0.6, color: i ? YELLOW : '#ff4fa3', wide: !i, fill: i ? '' : 'rgba(138,92,255,0.12)' }));
        for (let k = 0; k < 7; k += 1) {
          const a = (k / 7) * TAU + Math.random() * 0.4;
          splat(x + Math.cos(a) * 1.3, y + Math.sin(a) * 1.3, 0.5, 'rgba(60,50,40,0.35)', 8);
        }
        splat(x, y, 1.3, 'rgba(60,50,40,0.4)', 8);
        burst(x, y, 0.1, 40, ['#c9b48a', '#8a7a5c', PAPER, '#ff4fa3'], { speed: 4.2, up: 3, g: 6, size: 0.08, life: 1, kind: 'debris' });
        burst(x, y, 0.1, 14, ['rgba(205,200,190,0.6)'], { speed: 2.2, up: 0.5, g: -0.2, size: 0.16, life: 1.1, kind: 'smoke' });
        shake = Math.max(shake, 0.7);
        hitstop = Math.max(hitstop, 0.12);
        flare = 0.3;
        flareColor = '#ff4fa3';
        play('bodyslam');
      });
    } else if (item.type === 'zola') {
      // This is Sparta: he steps up to the toughest Bootleg and kicks it straight through the gang behind it.
      const victim = [...near].sort((a, b) => (GANG[b.type].boss ? 1e9 : b.hp) - (GANG[a.type].boss ? 1e9 : a.hp))[0];
      const from = { x: state.x, y: state.y };
      const d0 = dist(victim.x, victim.y, from.x, from.y) || 1;
      const stand = { x: victim.x - ((victim.x - from.x) / d0) * 0.7, y: victim.y - ((victim.y - from.y) / d0) * 0.7 };
      if (!hiddenAt(stand.x, stand.y)) state.dash = { fx: from.x, fy: from.y, tx: stand.x, ty: stand.y, t: 0, dur: 0.25 };
      state.face = victim.x >= from.x ? 1 : -1;
      play('spartakick');
      later(0.4, () => {
        const ax = enemies.includes(victim) ? victim.x : stand.x + ((victim.x - from.x) / d0) * 0.7;
        const ay = enemies.includes(victim) ? victim.y : stand.y + ((victim.y - from.y) / d0) * 0.7;
        const dx = (ax - state.x) / (dist(ax, ay, state.x, state.y) || 1);
        const dy = (ay - state.y) / (dist(ax, ay, state.x, state.y) || 1);
        if (enemies.includes(victim)) hurtEnemy(victim, dmg * 10);
        knock(victim, state.x, state.y, 4, 1.6);
        foes(state.x, state.y, ult.reach + 1).forEach(enemy => {
          const along = (enemy.x - state.x) * dx + (enemy.y - state.y) * dy;
          const side = Math.abs((enemy.x - state.x) * dy - (enemy.y - state.y) * dx);
          if (along < 0 || side > 1) return;
          hurtEnemy(enemy, dmg * 3);
          knock(enemy, enemy.x - dx, enemy.y - dy, 3, 1.4);
        });
        for (let k = 1; k <= 5; k += 1) moments.push({ kind: 'ring', x: state.x + dx * k, y: state.y + dy * k, r: 0.9, t: -k * 0.04, dur: 0.4, color: k % 2 ? '#ff8a1f' : YELLOW });
        moments.push({ kind: 'pow', x: ax, y: ay, t: 0, dur: 0.8, text: 'SPARTA!', boss: true, tilt: 0.12 });
        burst(ax, ay, 0.6, 30, ['#ff8a1f', YELLOW, PAPER, '#c9b48a'], { speed: 5, up: 2, g: 4, size: 0.07, life: 0.8, kind: 'debris' });
        shake = Math.max(shake, 0.6);
        hitstop = Math.max(hitstop, 0.14);
        flare = 0.25;
        flareColor = '#ff8a1f';
      });
    } else if (item.type === 'kuza') {
      // Sternenhagel: three rings of twelve stars in every direction, each ring turned a little, all coming back.
      for (let wave = 0; wave < 3; wave += 1) {
        for (let k = 0; k < 12; k += 1) {
          const a = ((k + wave / 3) / 12) * TAU + state.x;
          throwStar(item, c, c.x + Math.cos(a) * (ult.reach - 0.9), c.y + Math.sin(a) * (ult.reach - 0.9), dmg * 2.2, wave * 0.2 + k * 0.012, k % 2);
        }
        later(wave * 0.2, () => play('starstorm'));
      }
    } else if (item.type === 'blade') {
      // Blutmond: the moon turns red, and he cuts his way round everyone in reach, healing with every cut.
      moments.push({ kind: 'moon', x: state.x, y: state.y, t: 0, dur: 2.2 });
      play('bloodmoon');
      const home = { x: state.x, y: state.y };
      const list = near.sort((a, b) => Math.atan2(a.y - home.y, a.x - home.x) - Math.atan2(b.y - home.y, b.x - home.x)).slice(0, 10);
      list.forEach((enemy, i) => later(0.4 + i * 0.09, () => {
        if (!enemies.includes(enemy)) return;
        blinkTo(state, enemy);
        state.recoil = 1;
        moments.push({ kind: 'slash', x: enemy.x, y: enemy.y, t: 0, dur: 0.3, a: Math.random() * Math.PI - Math.PI / 2 });
        burst(enemy.x, enemy.y, 0.6, 10, ['#b0102a', '#ff2d55', PAPER], { speed: 2.2, up: 2, size: 0.05, life: 0.5, kind: 'spark' });
        hurtEnemy(enemy, dmg * 4);
        healItem(item, maxHp(item) * 0.06);
      }));
      later(0.5 + list.length * 0.09, () => {
        if (hiddenAt(home.x, home.y)) return;
        burst(state.x, state.y, 0.7, 8, ['rgba(176,16,42,0.5)', PAPER], { speed: 0.8, up: 1, size: 0.08, life: 0.5, kind: 'smoke' });
        state.x = home.x;
        state.y = home.y;
      });
    } else if (item.type === 'capi') {
      // Sensensturm: spinning, he cuts a way right through the thickest crowd and through everyone along it.
      const spot = hotSpot(1.8) || near[0];
      const goal = dist(spot.x, spot.y, state.x, state.y) <= ult.reach && !hiddenAt(spot.x, spot.y) ? spot : { x: state.x, y: state.y };
      state.dash = { fx: state.x, fy: state.y, tx: goal.x, ty: goal.y, t: 0, dur: 0.9 };
      state.face = goal.x >= state.x ? 1 : -1;
      play('moonslash');
      for (let k = 0; k < 6; k += 1) later(0.05 + k * 0.15, () => {
        whirl(state.x, state.y, 2.6, dmg * 1.1, CAPI_GLOW, CAPI_FILL);
        play('sickles');
      });
      later(0.95, () => {
        moments.push({ kind: 'pow', x: state.x, y: state.y, t: 0, dur: 0.8, text: 'STURM!', boss: true, tilt: 0.1 });
        flare = 0.2;
        flareColor = CAPI_GLOW;
        shake = Math.max(shake, 0.3);
      });
    } else if (item.type === 'johno') {
      // Himmelfahrt: uppercut after uppercut down the line, and the whole lot goes up into the sky.
      play('ascend');
      byDistance(near, state.x, state.y).slice(0, 7).forEach((enemy, i) => later(0.15 + i * 0.13, () => {
        if (!enemies.includes(enemy)) return;
        blinkTo(state, enemy);
        state.recoil = 1;
        hurtEnemy(enemy, dmg * 2.5);
        launch(enemy, 5.5, 1.5, 2, dmg * 2);
        burst(enemy.x, enemy.y, 0.8, 12, [YELLOW, PAPER, '#d6283a'], { speed: 1.8, up: 5, size: 0.05, life: 0.6, kind: 'spark' });
        play('uppercut');
      }));
      later(1.2, () => moments.push({ kind: 'smokering', x: state.x, y: state.y, h: heightOf(item.type, item.level) * 0.85, t: 0, dur: 1.4 }));
    } else if (item.type === 'polarise') {
      // Homerun: a step up to the thickest crowd, the bat right back, and one swing clears everything round him.
      const spot = hotSpot(1.8);
      const goal = spot && dist(spot.x, spot.y, state.x, state.y) <= ult.reach ? spot : near[0];
      const d0 = dist(goal.x, goal.y, state.x, state.y) || 1;
      const stand = { x: goal.x - ((goal.x - state.x) / d0) * 0.9, y: goal.y - ((goal.y - state.y) / d0) * 0.9 };
      if (d0 > 1 && !hiddenAt(stand.x, stand.y)) state.dash = { fx: state.x, fy: state.y, tx: stand.x, ty: stand.y, t: 0, dur: 0.35 };
      state.face = goal.x >= state.x ? 1 : -1;
      play('swish');
      later(0.65, () => {
        foes(state.x, state.y, 2.8).forEach(enemy => {
          hurtEnemy(enemy, dmg * 5);
          knock(enemy, state.x, state.y, 4.5, 1.5);
        });
        moments.push({ kind: 'arc', x: state.x, y: state.y, r: 2.8, a: state.face > 0 ? 0 : Math.PI, t: 0, dur: 0.45, color: '#9fe4ff' });
        [0, 0.08].forEach((lag, i) => moments.push({ kind: 'ring', x: state.x, y: state.y, r: 2.8 - i * 0.8, t: -lag, dur: 0.5, color: i ? PAPER : '#4fb6e8' }));
        moments.push({ kind: 'pow', x: goal.x, y: goal.y, t: 0, dur: 0.8, text: 'HOMERUN!', boss: true, tilt: -0.12 });
        burst(state.x + state.face * 0.8, state.y, 0.8, 26, ['#c3c8ce', '#9fe4ff', PAPER, YELLOW], { speed: 4.5, up: 2.2, g: 4, size: 0.06, life: 0.7, kind: 'spark' });
        shake = Math.max(shake, 0.55);
        hitstop = Math.max(hitstop, 0.12);
        flare = 0.25;
        flareColor = '#9fe4ff';
        play('homerun');
      });
    } else if (item.type === 'bigtoe') {
      // Diamanthagel: twelve blows in a second on whoever stands closest, then a punch into the ground for the rest.
      play('hail');
      const list = byDistance(near, state.x, state.y).slice(0, 6);
      for (let k = 0; k < 12; k += 1) later(0.1 + k * 0.07, () => {
        const pool = list.filter(enemy => enemies.includes(enemy) && !enemy.fly);
        const enemy = pool[k % Math.max(1, pool.length)];
        if (!enemy) return;
        if (k % 4 === 0) blinkTo(state, enemy);
        state.recoil = 1;
        hurtEnemy(enemy, dmg * 1.2);
        burst(enemy.x, enemy.y, 0.8, 6, [GOLD, '#eef8ff', '#9fd8ff'], { speed: 2.2, up: 1.8, size: 0.05, life: 0.4, kind: 'spark' });
        play('dusters');
      });
      later(1.05, () => {
        foes(state.x, state.y, 2.2).forEach(enemy => {
          hurtEnemy(enemy, dmg * 3);
          knock(enemy, state.x, state.y, 2.4, 1);
        });
        [0, 0.07].forEach((lag, i) => moments.push({ kind: 'ring', x: state.x, y: state.y, r: 2.2 - i * 0.6, t: -lag, dur: 0.45, color: i ? PAPER : GOLD }));
        moments.push({ kind: 'pow', x: state.x, y: state.y, t: 0, dur: 0.8, text: 'BLING!', boss: true, tilt: 0.1 });
        burst(state.x, state.y, 0.2, 24, [GOLD, '#ffe28a', '#eef8ff', '#9fd8ff'], { speed: 3.8, up: 2.6, g: 4, size: 0.06, life: 0.8, kind: 'spark' });
        shake = Math.max(shake, 0.45);
        hitstop = Math.max(hitstop, 0.1);
        flare = 0.22;
        flareColor = '#ffd23c';
        play('heavy');
      });
    } else if (item.type === 'maliq') {
      // Royal Flush: five giant cards come down on the five thickest crowds and go off in his colours.
      const spots = [];
      byDistance([...near], state.x, state.y).forEach(enemy => {
        if (spots.length < 5 && spots.every(spot => dist(spot.x, spot.y, enemy.x, enemy.y) > 1.5)) spots.push({ x: enemy.x, y: enemy.y });
      });
      spots.forEach((spot, i) => shots.push({ kind: 'bigcard', fx: spot.x, fy: spot.y, tx: spot.x, ty: spot.y, x: spot.x, y: spot.y, h: 7, t: -i * 0.12, dur: 0.55, dmg: dmg * 5, splash: 1.6, rank: ['10', 'J', 'Q', 'K', 'A'][i], color: CARD_COLORS[i % CARD_COLORS.length] }));
      flare = 0.2;
      flareColor = '#7b3fe4';
      play('royalflush');
    }
  };
  const updateCrew = dt => {
    const fight = raid && raid.phase === 'fight';
    world().forEach(item => {
      if (kindOf(item) !== 'crew') return;
      const state = crewState(item);
      state.recoil = Math.max(0, state.recoil - dt * 4);
      // For the animation: time since the last blow, and whether somebody is there to hit.
      state.since = (state.since === undefined ? 9 : state.since) + dt;
      state.engaged = false;
      state.arm = Math.max(0, (state.arm || 0) - dt);
      if (state.hop) {
        state.hop.t += dt;
        if (state.hop.t >= state.hop.dur) state.hop = null;
      }
      state.special = Math.max(0, (state.special || 0) - dt);
      state.spin = Math.max(0, (state.spin || 0) - dt);
      const t = info(item);
      const range = rangeOf(item.type, item.level);
      // A hero in the middle of a move, a leap or a dash, goes where it takes him and does nothing else meanwhile.
      if (state.dash) {
        const dash = state.dash;
        dash.t += dt;
        const p = Math.min(1, dash.t / dash.dur);
        const at = inArena(lerp(dash.fx, dash.tx, 1 - (1 - p) ** 2), lerp(dash.fy, dash.ty, 1 - (1 - p) ** 2), { x: dash.fx, y: dash.fy });
        state.x = at.x;
        state.y = at.y;
        state.moving = false;
        if (p >= 1) state.dash = null;
        return;
      }
      if (!fight) {
        state.aim = ease(state.aim, 0.35, 4, dt);
        state.ult = 3 + Math.random() * 3;
        crewStep(item, state, null, range, dt);
        return;
      }
      const post = postOf(item);
      const goal = stayOn(state, post, range + roamOf(item));
      // Between two Bootlegs a fighter waits where it stands for the next one, instead of walking back to its post.
      if (!goal && enemies.some(enemy => !fled(enemy))) state.moving = false;
      else crewStep(item, state, goal || null, range, dt);
      const c = center(item);
      const dmg = dmgOf(item.type, item.level);
      state.cd -= dt;
      // A hero's special comes round every few seconds, as soon as there is somebody to use it on.
      state.ult = (state.ult === undefined ? 4 : state.ult) - dt;
      if (state.ult <= 0 && foes(state.x, state.y, HERO_ULTS[item.type].reach).length) {
        state.ult = HERO_ULTS[item.type].cool;
        state.special = 1.3;
        heroUlt(item, state, c, dmg);
        return;
      }
      const foe = state.foe;
      const onFoe = foe && enemies.includes(foe) && !foe.fly && !fled(foe) && dist(c.x, c.y, foe.x, foe.y) <= range;
      const target = onFoe ? foe : pickTarget(c.x, c.y, range);
      if (!target) return;
      state.engaged = true;
      const want = Math.atan2((target.y - c.y) * TILT, target.x - c.x);
      let delta = want - state.aim;
      while (delta > Math.PI) delta -= TAU;
      while (delta < -Math.PI) delta += TAU;
      state.aim += delta * Math.min(1, dt * 12);
      state.face = Math.cos(state.aim) >= 0 ? 1 : -1;
      if (state.cd > 0) return;
      state.cd = 1 / t.rate;
      state.recoil = 1;
      state.since = 0;
      heroHit(item, state, c, target, dmg);
    });
  };
  const updateShots = dt => {
    for (let i = shots.length - 1; i >= 0; i -= 1) {
      const shot = shots[i];
      shot.t += dt;
      if (shot.kind === 'shell') {
        // A shell follows its Bootleg in a steep arc and drops onto it.
        if (shot.t < 0) continue;
        if (enemies.includes(shot.target)) {
          shot.tx = shot.target.x;
          shot.ty = shot.target.y;
        }
        const p = Math.min(1, shot.t / shot.dur);
        shot.x = lerp(shot.fx, shot.tx, p);
        shot.y = lerp(shot.fy, shot.ty, p);
        shot.h = lerp(2, 0.3, p) + Math.sin(p * Math.PI) * 4.2;
        if (p < 1) continue;
        shots.splice(i, 1);
        landShell(shot);
        continue;
      }
      if (shot.kind === 'missile') {
        // A missile climbs off the roof, curves out to one side and dives onto its Bootleg.
        if (shot.t < 0) continue;
        if (enemies.includes(shot.target)) {
          shot.tx = shot.target.x;
          shot.ty = shot.target.y;
        }
        const p = Math.min(1, shot.t / shot.dur);
        const len = dist(shot.fx, shot.fy, shot.tx, shot.ty) || 1;
        const bend = Math.sin(p * Math.PI) * shot.side;
        shot.px = shot.x;
        shot.py = shot.y;
        shot.ph = shot.h;
        shot.x = lerp(shot.fx, shot.tx, p * p) - ((shot.ty - shot.fy) / len) * bend;
        shot.y = lerp(shot.fy, shot.ty, p * p) + ((shot.tx - shot.fx) / len) * bend;
        shot.h = lerp(2.4, 0.2, p) + Math.sin(Math.min(1, p * 1.4) * Math.PI) * 3.6;
        if (p < 1) continue;
        shots.splice(i, 1);
        landMissile(shot);
        continue;
      }
      if (shot.kind === 'star') {
        // Out to the end of its flight, then back to whoever threw it, cutting every Bootleg once each way.
        if (shot.t < 0) continue;
        const home = shot.owner.crew;
        if (shot.back && home) {
          shot.tx = home.x;
          shot.ty = home.y;
        }
        const p = Math.min(1, shot.t / shot.dur);
        const e = shot.back ? p * p : 1 - (1 - p) ** 2;
        shot.x = lerp(shot.fx, shot.tx, e);
        shot.y = lerp(shot.fy, shot.ty, e);
        shot.h = 0.8 + Math.sin(p * Math.PI) * 0.35;
        enemies.forEach(enemy => {
          if (enemy.fly || shot.hit.has(enemy) || dist(enemy.x, enemy.y, shot.x, shot.y) > 0.5) return;
          shot.hit.add(enemy);
          burst(enemy.x, enemy.y, 0.6, 5, [shot.color, PAPER, '#c9ced3'], { speed: 1.8, up: 1.6, size: 0.04, life: 0.35, kind: 'spark' });
          play('starhit');
          later(0, () => hurtEnemy(enemy, shot.dmg));
        });
        if (p < 1) continue;
        if (shot.back || !home) {
          shots.splice(i, 1);
          continue;
        }
        shot.back = true;
        shot.t = 0;
        shot.fx = shot.x;
        shot.fy = shot.y;
        shot.hit = new Set();
        continue;
      }
      if (shot.kind === 'grenade' || shot.kind === 'bomblet') {
        // A grenade lobbed high and turning over; its bomblets hop out low.
        if (shot.t < 0) continue;
        const p = Math.min(1, shot.t / shot.dur);
        shot.x = lerp(shot.fx, shot.tx, p);
        shot.y = lerp(shot.fy, shot.ty, p);
        shot.h = shot.kind === 'grenade' ? lerp(1.1, 0.1, p) + Math.sin(p * Math.PI) * shot.arc : lerp(0.3, 0.05, p) + Math.sin(p * Math.PI) * (shot.stage === 3 ? 0.28 : 0.45);
        if (p < 1) continue;
        shots.splice(i, 1);
        if (shot.kind === 'grenade') landGrenade(shot);
        else landBomblet(shot);
        continue;
      }
      if (shot.kind === 'bigcard') {
        // A giant playing card dropping out of the sky, flat onto the crowd.
        if (shot.t < 0) continue;
        const p = Math.min(1, shot.t / shot.dur);
        shot.h = (1 - p * p) * 7;
        if (p < 1) continue;
        shots.splice(i, 1);
        splashAt(shot.tx, shot.ty, shot.splash, shot.dmg);
        foes(shot.tx, shot.ty, shot.splash).forEach(enemy => knock(enemy, shot.tx, shot.ty, 1.4, 1));
        [0, 0.07].forEach((lag, k) => moments.push({ kind: 'ring', x: shot.tx, y: shot.ty, r: shot.splash * (1 - k * 0.3), t: -lag, dur: 0.45, color: k ? PAPER : shot.color }));
        burst(shot.tx, shot.ty, 0.5, 22, [shot.color, PAPER, YELLOW, '#7b3fe4'], { speed: 3.2, up: 2.5, g: 3, size: 0.06, life: 0.8, kind: 'spark' });
        shake = Math.max(shake, 0.3);
        play('card');
        continue;
      }
      if (shot.kind === 'card') {
        if (shot.t < 0) continue;
        if (!enemies.includes(shot.target)) {
          shots.splice(i, 1);
          continue;
        }
        const dx = shot.target.x - shot.x;
        const dy = shot.target.y - shot.y;
        const d = Math.hypot(dx, dy);
        const stepLen = shot.speed * dt;
        if (d <= stepLen) {
          hurtEnemy(shot.target, shot.dmg, true);
          burst(shot.x, shot.y, 0.6, 8, [shot.color, PAPER, '#7b3fe4'], { speed: 1.8, up: 2, size: 0.05, life: 0.5, kind: 'spark' });
          shots.splice(i, 1);
          continue;
        }
        shot.x += (dx / d) * stepLen;
        shot.y += (dy / d) * stepLen;
        shot.h = lerp(shot.h, 0.5, dt * 6);
        continue;
      }
      if (shot.kind === 'spray' || shot.kind === 'cracker' || shot.kind === 'case') {
        const p = Math.min(1, shot.t / shot.dur);
        shot.x = lerp(shot.fx, shot.tx, p);
        shot.y = lerp(shot.fy, shot.ty, p);
        if (shot.kind === 'case') shot.h = (1 - p * p) * 10;
        else shot.h = lerp(shot.kind === 'spray' ? 0.35 : 0.6, 0.1, p) + Math.sin(p * Math.PI) * (shot.arc || 0.3);
        if (p < 1) continue;
        shots.splice(i, 1);
        if (shot.kind === 'spray') continue;
        if (shot.kind === 'cracker') {
          burst(shot.tx, shot.ty, 0.2, 9, [YELLOW, SIGNAL, PAPER], { speed: 2.2, up: 2, size: 0.04, kind: 'spark', life: 0.45 });
          burst(shot.tx, shot.ty, 0.3, 3, ['rgba(80,80,84,0.45)'], { speed: 0.5, up: 0.8, g: -0.5, life: 0.9, size: 0.14, kind: 'smoke' });
          play('pop');
          continue;
        }
        landCase(shot);
      }
    }
  };
  // Heroes who have lost a third of their number and are clearly outmatched pull out before anyone else falls.
  const shouldRetreat = () => {
    const crew = world().filter(item => kindOf(item) === 'crew');
    if (!enemies.length || crew.length > heist.items0 * (2 / 3)) return false;
    const ours = crew.reduce((sum, item) => sum + crewDps(item), 0) * crew.reduce((sum, item) => sum + (item.hp === undefined ? maxHp(item) : item.hp), 0);
    const left = 1 + raid.plan.length / Math.max(1, enemies.length);
    const theirs = enemies.reduce((sum, enemy) => sum + enemy.dps, 0) * enemies.reduce((sum, enemy) => sum + Math.max(0, enemy.hp), 0) * left * left;
    return ours < theirs * 0.35;
  };
  const updateRaid = dt => {
    if (!raid) return;
    raid.t -= dt;
    if (raid.phase === 'warn') {
      if (raid.t <= 0) beginFight();
      return;
    }
    if (raid.phase === 'end') {
      if (raid.heist && raid.t <= 0 && !travel) travelTo(comeHome);
      return;
    }
    // The next wave waits for the last one to be beaten, a while at most; once it is, it comes soon.
    const coming = raid.plan.length && raid.plan[0].wave + 1 > (raid.wave || 1) ? raid.plan[0] : null;
    if (coming && enemies.length && raid.held < WAVE_HOLD && coming.at <= raid.clock + dt) raid.held += dt;
    else {
      if (coming && !enemies.length) raid.clock = Math.max(raid.clock, coming.at - WAVE_BREATH);
      raid.clock += dt;
    }
    while (raid.plan.length && raid.plan[0].at <= raid.clock) {
      const next = raid.plan.shift();
      if (next.wave + 1 > (raid.wave || 1)) {
        raid.held = 0;
        if (raid.heist) onReinforce(raid.plan.filter(entry => entry.wave === next.wave).length + 1);
      }
      raid.wave = Math.max(raid.wave || 1, next.wave + 1);
      const foe = spawnEnemy(next.type, next.lane, next.squad);
      if (next.chief) makeChief(foe);
    }
    if (raid.heist) {
      updateBattle(dt);
      if (!world().length) endHeist('lost');
      else if (shouldRetreat()) {
        raid.fled = true;
        endHeist('lost');
      }
      else if (!raid.plan.length && !enemies.length) endHeist('won');
      return;
    }
    // A Problem is fought out to the last Bootleg; nobody leaves early.
    if (K.dead) return;
    if (!raid.plan.length && !enemies.length) endRaid();
  };
  const endRaid = () => {
    raid.phase = 'end';
    raid.result = 'won';
    const bonus = Math.round(Math.max(30 * rewardScale(raid.n), totalCashRate() * 6) * (raid.boss ? 3 : raid.revenge ? 2 : 1) * (eventIs('beute') ? 2 : 1));
    raid.bonus = bonus;
    S.cash += bonus;
    S.earned += bonus;
    S.stats.raids += 1;
    const home = baseCenter();
    burst(home.x, home.y, 2.2, 60, PAINTS, { speed: 3.5, up: 4, g: 3, size: 0.06, life: 2.2, kind: 'debris' });
    hitstop = 0.35;
    S.raidN += 1;
    S.raidAt = Date.now() + raidGap();
    S.items.forEach(item => {
      if (!item.ruin) item.hp = maxHp(item);
    });
    shots.length = 0;
    zones.length = 0;
    drones.length = 0;
    flare = 0;
    calmAnime();
    K.hp = hustlerMaxHp();
    play('win');
    persist();
    onRaidEnd(raid);
  };
  // ---------------------------------------------------------------------------------------------
  // Raids: tips on other gangs' stashes come in over time. The whole crew goes over, takes on
  // their gang on their ground and clears out the stash. Whoever goes down there is carried home,
  // and your own block waits untouched until everyone is back.

  const HEIST_SIZES = [
    { name: 'Leicht', hard: 1.4, odds: 0.6, waves: 1, guards: n => Math.min(16, 4 + Math.floor(n * 0.5)), bosses: () => 0, cash: 5, fans: 2, wide: [3, 3], stash: [['van']], pallets: [1, 3] },
    { name: 'Mittel', hard: 1.7, odds: 0.25, waves: 2, guards: n => Math.min(32, 6 + Math.floor(n * 0.75)), bosses: () => 0, cash: 12, fans: 4, wide: [3, 4], stash: [['shed', 'van']], pallets: [2, 4] },
    { name: 'Schwer', hard: 2, odds: 0.14, waves: 3, guards: n => Math.min(48, 10 + Math.floor(n * 1.05)), bosses: n => 1 + Math.floor(n / 20), cash: 25, fans: 10, wide: [4, 4], stash: [['hall'], ['van', 'shed']], pallets: [2, 4] },
    { name: 'Sehr riskant', hard: 2.3, odds: 0.1, waves: 3, guards: n => Math.min(60, 12 + Math.floor(n * 1.25)), bosses: n => 2 + Math.floor(n / 15), cash: 55, fans: 22, wide: [4, 5], stash: [['hall'], ['shed'], ['van']], pallets: [3, 4] }
  ];
  const HEIST_PLACES = [
    ['Kofferraum am Kanal', 'Parkdeck 3', 'Hinter dem Späti', 'Bahnhofsvorplatz'],
    ['Garage am Bahnbogen', 'Hinterhof-Lager', 'Kellerlager Ost', 'Schrottplatz Nord'],
    ['Fake-Fabrik', 'Lagerhalle am Hafen', 'Großmarkt-Halle', 'Druckerei im Gewerbegebiet'],
    ['Zentrallager der Fälscher', 'Bunker unterm Güterbahnhof', 'Containerterminal 7', 'Alte Brauerei']
  ];
  // Far off to the east, out of reach of any block.
  const HEIST_CX = 100;
  const heistGang = h => HEIST_GANGS[h.seed % HEIST_GANGS.length];
  const heistPlace = h => HEIST_PLACES[h.size][Math.floor(h.seed / HEIST_GANGS.length) % HEIST_PLACES[h.size].length];
  const heistGuards = h => HEIST_SIZES[h.size].guards(h.n) + HEIST_SIZES[h.size].bosses(h.n);
  // Raids open up with the first hero.
  const heistOpen = () => founded() && S.items.some(isHero);
  // A gang robbed on a Raid swears revenge half the time and comes for your block a while later, tougher than a
  // plain Problem; the other half it lets it go without a word. One at a time: while one is on its way, other gangs
  // keep their grudge to themselves.
  const REVENGE = { odds: 0.5, min: 8 * MINUTE, max: 12 * MINUTE, tough: 1.3 };
  const scheduleRevenge = h => {
    if (S.revenge || Math.random() >= REVENGE.odds) return;
    S.revenge = { gang: heistGang(h), at: Date.now() + lerp(REVENGE.min, REVENGE.max, Math.random()) };
    onRevengeSworn(S.revenge);
  };
  // Whatever comes to the block next, a Problem or a gang's revenge.
  const threatAt = () => Math.min(S.raidAt, S.revenge ? S.revenge.at : Infinity);
  const problemSoon = () => founded() && !raid && !heist && !travel && threatAt() - Date.now() <= PROBLEM_NOTICE;
  const protectionPrice = () => Math.ceil(S.cash * PROTECTION);
  // The stash is worth some minutes of what your block makes, and never less than the gang's own rewards.
  const heistLoot = h => {
    const s = HEIST_SIZES[h.size];
    const fansRate = S.items.reduce((sum, item) => sum + (kindOf(item) === 'promo' && working(item) ? fansRateOf(item.type, item.level) : 0), 0);
    const double = eventIs('beute') ? 2 : 1;
    return {
      cash: nice(Math.max(40 * rewardScale(h.n) * (1 + h.size * 1.5), totalCashRate() * s.cash)) * double,
      fans: nice(Math.max(5 * (1 + h.size * 2), fansRate * s.fans)) * double
    };
  };
  // Heroes against gang: damage per second and health on each side; their product gives a rough read of the odds. No
  // blow takes more off a Bootleg than it has, `bootleg` its health, so against small fry the big hitters count for
  // less.
  const crewDps = (item, bootleg = Infinity) => {
    const t = info(item);
    return Math.min(dmgOf(item.type, item.level), bootleg) * t.rate * (t.splash ? 2 : 1) * (t.hits || 1);
  };
  const crewSums = (bootleg = Infinity) => {
    const crew = S.items.filter(item => kindOf(item) === 'crew');
    return {
      dps: crew.reduce((sum, item) => sum + crewDps(item, bootleg), 0),
      hp: crew.reduce((sum, item) => sum + hpOf(item.type, item.level), 0)
    };
  };
  const crewPower = (bootleg = Infinity) => {
    const crew = crewSums(bootleg);
    return crew.dps * crew.hp;
  };
  // How much more health (tough) and punch than plain each Bootleg gets against `gang`, the plain gang's health,
  // damage and head count, whose Bootlegs have `bootleg` health when plain. Whatever the crew is made of, it would beat
  // the gang down in FIGHT_CLEAR seconds if every blow landed, and the hero in the middle stands MEMBER_HOLD seconds
  // against one Bootleg alone; a `hard` fight stretches the one and shortens the other. The tougher they are, the less
  // of each blow goes to waste.
  const FIGHT_CLEAR = 62;
  const MEMBER_HOLD = 40;
  const memberHp = () => {
    const hps = S.items.filter(item => kindOf(item) === 'crew').map(item => hpOf(item.type, item.level)).sort((a, b) => a - b);
    return hps.length ? hps[hps.length >> 1] : 1;
  };
  const gangFor = (bootleg, gang, low, hard = 1) => {
    let tough = 1;
    for (let i = 0; i < 4; i += 1) tough = clamp((crewSums(bootleg * tough).dps * FIGHT_CLEAR * hard) / gang.hp, low, 25);
    return { tough, punch: clamp((memberHp() * hard) / (MEMBER_HOLD * (gang.dps / gang.count)), low, 25) };
  };
  // A Bootleg of raid n on average, the elites among them included.
  const gangAvg = (n, key) => {
    const kinds = Object.keys(GANG_WEIGHT).filter(type => n >= GANG[type].from);
    const weight = kinds.reduce((sum, type) => sum + GANG_WEIGHT[type], 0);
    return (kinds.reduce((sum, type) => sum + GANG[type][key] * GANG_WEIGHT[type], 0) / weight) * eliteBoost(n, key);
  };
  // The plain gang of a Raid: health and damage of its guards and bosses together.
  const guardGang = (size, n) => {
    const count = HEIST_SIZES[size].guards(n);
    const bosses = HEIST_SIZES[size].bosses(n);
    return { dps: (count * gangAvg(n, 'dps') + bosses * GANG.boss.dps) * dpsScale(n), hp: (count * gangAvg(n, 'hp') + bosses * GANG.boss.hp) * hpScale(n), count: count + bosses };
  };
  const gangPower = gang => gang.dps * gang.hp;
  // A Problem grows with your heroes like a Raid does: the gang your heroes have just these odds against. The count of
  // Problems so far sets how many come and of which kinds, at least, and only so far ahead of it; whatever strength
  // is still missing or too much goes on each Bootleg.
  const PROBLEM_ODDS = 0.55;
  const PROBLEM_LEAD = 14;
  // The plain gang of a Problem; a boss Problem counts the Pate and the men he calls in on top.
  const problemGang = (n, boss) => {
    const count = raidSize(n) + (boss ? CHIEF.men : 0);
    return {
      dps: (count * gangAvg(n, 'dps') + (boss ? GANG.boss.dps : 0)) * dpsScale(n),
      hp: (count * gangAvg(n, 'hp') + (boss ? GANG.boss.hp * CHIEF.hp : 0)) * hpScale(n),
      count: count + (boss ? 1 : 0)
    };
  };
  const plainHp = n => gangAvg(n, 'hp') * hpScale(n);
  // A young block gets it easier: under a third of the gang's strength at Base level 1, the whole from level 6.
  const rookieEase = () => Math.sqrt(Math.min(1, (hqLevel() + 1) / 7));
  const problemLevel = boss => {
    let n = S.raidN;
    for (let k = S.raidN + 1; k <= S.raidN + PROBLEM_LEAD; k += 1) if (crewPower(plainHp(k)) / gangPower(problemGang(k, boss)) >= PROBLEM_ODDS) n = k;
    return { n, ...gangFor(plainHp(n), problemGang(n, boss), 0.1, rookieEase()) };
  };
  // How strong a tip's gang is: the toughest it can be while your heroes still have the odds its size calls for.
  // So every Raid is a fight, and it grows with each hero and level, and with your Base.
  const tuneHeist = h => {
    const want = HEIST_SIZES[h.size].odds / S.heistEdge;
    let n = 1;
    while (n < 400 && crewPower(plainHp(n + 1)) / Math.max(1, gangPower(guardGang(h.size, n + 1))) >= want) n += 1;
    h.n = n;
    return h;
  };
  // Each guard as on a Problem, harder the bigger the job and the better your last Raids went.
  const heistTough = h => gangFor(plainHp(h.n), guardGang(h.size, h.n), 0.1, HEIST_SIZES[h.size].hard * Math.sqrt(S.heistEdge) * rookieEase());
  const newHeist = size => tuneHeist({ id: newSeed(), size, seed: newSeed(), n: 1 });
  // Five tips at a time, each at a random difficulty. A Raid won leaves its slot empty, and a fresh tip
  // fills it once it has charged back up. Never while a fight is on; new tips wait until it is over.
  const heistRoll = () => {
    const roll = Math.random();
    return roll < 0.3 ? 0 : roll < 0.6 ? 1 : roll < 0.85 ? 2 : 3;
  };
  const refillHeists = now => {
    if (!heistOpen() || raid) return;
    S.heistBack = S.heistBack.filter(at => at > now);
    const tips = Array.from({ length: HEIST_MAX - S.heists.length - S.heistBack.length }, () => newHeist(heistRoll()));
    if (!tips.length) return;
    S.heists.push(...tips);
    onHeistTip(tips);
  };
  // A Raid's ground is drawn up from its tip by fixed rules, so the same tip always looks alike and no two tips do.
  // The lot is as wide as the gang is big and three chunks deep; it is paved and fenced one way or another. The
  // stash stands along the back in one of a few setups, cover stands about the edges, marks lie on the ground.
  // The battle lines follow the stash: the gang a few steps in front of it, the crew a few steps further.
  const HEIST_DEEP = 3;
  const HEIST_GROUNDS = ['asphalt', 'slabs', 'cobble', 'gravel'];
  const HEIST_FENCES = ['chain', 'wall', 'tin'];
  const STASH = { van: [2.4, 1.1], shed: [3, 2], hall: [5, 2.6], pallet: [1.1, 1.1] };
  const COVER = { container: [3, 1.25], crates: [1.1, 1.1], barrels: [1.1, 0.7], wreck: [1.7, 0.85], barrier: [1.8, 0.5], tires: [0.8, 0.8] };
  // What stands where: big things at the back, small ones on the wings, cars and barriers out front.
  const COVER_ZONES = {
    back: ['container', 'container', 'crates', 'barrels', 'tires'],
    wing: ['crates', 'barrels', 'tires', 'barrier', 'wreck'],
    front: ['wreck', 'wreck', 'barrier', 'crates', 'container', 'tires']
  };
  const heistField = h => {
    const rand = mulberry32(h.seed ^ 0x2f6b45);
    const between = (lo, hi) => lo + rand() * (hi - lo);
    const count = ([lo, hi]) => lo + Math.floor(rand() * (hi - lo + 1));
    const s = HEIST_SIZES[h.size];
    const x0 = HEIST_CX * CHUNK;
    const x1 = x0 + count(s.wide) * CHUNK;
    const y1 = HEIST_DEEP * CHUNK;
    const back = between(0.8, 1.8);
    const props = [];
    const cover = [];
    const clear = (x, y, w, d, gap) => x >= x0 + 0.5 && x + w <= x1 - 0.5 && y >= 0.3 && y + d <= y1 - 0.5
      && [...props, ...cover].every(o => x + w + gap <= o.x || o.x + o.w + gap <= x || y + d + gap <= o.y || o.y + o.d + gap <= y);
    const stash = (kind, x, y) => {
      const [w, d] = STASH[kind];
      if (!clear(x, y, w, d, 0.7)) return false;
      props.push({ kind, x, y, w, d, tone: Math.floor(rand() * 4), id: props.length });
      return true;
    };
    // Tries spots in a box until one is free.
    const place = (kind, box, add) => {
      const [w, d] = (STASH[kind] || COVER[kind]);
      for (let k = 0; k < 24; k += 1) {
        const x = between(box.x0, Math.max(box.x0, box.x1 - w));
        const y = between(box.y0, Math.max(box.y0, box.y1 - d));
        if (add(kind, x, y)) return true;
      }
      return false;
    };
    const kinds = s.stash.map(options => oneOf(rand, options));
    const pallets = count(s.pallets);
    const layout = oneOf(rand, ['row', 'court', 'flank']);
    const deepest = Math.max(...kinds.map(kind => STASH[kind][1]));
    const band = { x0: x0 + 0.8, x1: x1 - 0.8, y0: back, y1: back + Math.max(deepest, 2.2) + 0.4 };
    if (layout === 'row') {
      // Everything in one row along the back, in a shuffled order with uneven gaps.
      const all = [...kinds, ...Array(pallets).fill('pallet')].sort(() => rand() - 0.5);
      const span = band.x1 - band.x0;
      while (all.reduce((sum, kind) => sum + STASH[kind][0] + 0.8, 0.8) > span && all.includes('pallet')) all.splice(all.lastIndexOf('pallet'), 1);
      const weights = [...all, null].map(() => 0.2 + rand());
      const extra = Math.max(0, span - all.reduce((sum, kind) => sum + STASH[kind][0] + 0.8, 0.8));
      const gap = i => 0.8 + (extra * weights[i]) / weights.reduce((sum, w) => sum + w, 0);
      let x = band.x0 + gap(0) - 0.8;
      all.forEach((kind, i) => {
        stash(kind, x, back + between(0, deepest - STASH[kind][1]));
        x += STASH[kind][0] + gap(i + 1);
      });
    } else {
      // The main building sets the yard: in the middle with the rest on its flanks, or off to one side.
      const [main, ...rest] = kinds;
      const w = STASH[main][0];
      const mid = (x0 + x1) / 2;
      const left = rand() < 0.5;
      const mx = layout === 'court' ? mid - w / 2 + between(-1.5, 1.5) : left ? x0 + between(1, 2.5) : x1 - w - between(1, 2.5);
      stash(main, mx, back);
      rest.forEach((kind, i) => {
        const side = layout === 'court' ? (i % 2 ? !left : left) : !left;
        const zone = side ? { x0: x0 + 0.8, x1: mx - 0.6 } : { x0: mx + w + 0.6, x1: x1 - 0.8 };
        place(kind, { ...zone, y0: back + (layout === 'court' ? 1 : 0), y1: band.y1 + (layout === 'court' ? 1.2 : 0) }, stash);
      });
      for (let i = 0; i < pallets; i += 1) place('pallet', { ...band, y1: band.y1 + 0.8 }, stash);
    }
    const front = Math.max(...props.map(p => p.y + p.d));
    const lines = { stop: front + 1.6, enemy: front + 2.9, crew: front + 6.1 };
    // Cover: some along the back fence, some on the wings, some out front where the crew comes in.
    const shelter = (kind, x, y) => {
      const [w, d] = COVER[kind];
      if (!clear(x, y, w, d, 0.9)) return false;
      cover.push({ kind, x, y, w, d, tone: Math.floor(rand() * 4), stack: kind === 'container' && rand() < 0.35 ? 2 : 1, flip: rand() < 0.5 });
      return true;
    };
    const zones = [
      ['back', { x0: x0 + 0.5, x1: x1 - 0.5, y0: 0.3, y1: front }, count([1, 3])],
      ['wing', { x0: x0 + 0.5, x1: x0 + 3.6, y0: front + 0.6, y1: lines.crew + 2 }, count([1, 3])],
      ['wing', { x0: x1 - 3.6, x1: x1 - 0.5, y0: front + 0.6, y1: lines.crew + 2 }, count([1, 3])],
      ['front', { x0: x0 + 0.5, x1: x1 - 0.5, y0: lines.crew + 3, y1: y1 - 1.6 }, count([2, 4])]
    ];
    zones.forEach(([zone, box, n]) => {
      for (let i = 0; i < n; i += 1) place(oneOf(rand, COVER_ZONES[zone]), box, shelter);
    });
    // Marks on the ground: oil, puddles, a drain or two, the gang's tag, and hazard stripes before every gate.
    const marks = [];
    const spot = () => ({ x: between(x0 + 1, x1 - 1), y: between(0.6, y1 - 0.6) });
    for (let i = count([4, 7]); i > 0; i -= 1) marks.push({ kind: 'oil', ...spot(), r: between(0.25, 0.6) });
    for (let i = count([1, 3]); i > 0; i -= 1) marks.push({ kind: 'puddle', ...spot(), r: between(0.5, 1.1) });
    for (let i = count([1, 2]); i > 0; i -= 1) marks.push({ kind: 'drain', ...spot(), r: 0.3 });
    for (let i = count([3, 6]); i > 0; i -= 1) marks.push({ kind: 'crack', ...spot(), r: between(0.6, 1.4), turn: rand() });
    marks.push({ kind: 'tag', x: between(x0 + 3, x1 - 3), y: between(lines.enemy, lines.crew), r: between(1.2, 1.8), turn: between(-0.25, 0.25), text: oneOf(rand, ['FAKE', 'B00TLEG', 'KOPIE', 'REPLIKA']) });
    props.filter(p => p.kind !== 'pallet').forEach(p => marks.push({ kind: 'hazard', x: p.x + p.w * 0.15, y: p.y + p.d + 0.1, w: p.w * 0.7, d: 0.45 }));
    const ground = oneOf(rand, HEIST_GROUNDS);
    if (ground === 'asphalt' || ground === 'slabs') {
      // Parking bays painted along one wing.
      const left = rand() < 0.5;
      const bx = left ? x0 + 0.6 : x1 - 3.6;
      for (let k = 0; k < 4; k += 1) marks.push({ kind: 'bay', x: bx, y: front + 1 + k * 1.6, w: 3, d: 1.6 });
    }
    return { x0, x1, y1, ground, fence: oneOf(rand, HEIST_FENCES), props, cover, marks, lines };
  };
  // The gang's army: the first wave stands in the line from the start, every later one comes out of the stash
  // as reinforcements, squad by squad.
  const heistPlan = h => {
    const s = HEIST_SIZES[h.size];
    const count = s.guards(h.n);
    const plan = [];
    const squads = [];
    let at = REINFORCE_AT;
    // Most of the army stands in the line; the rest is held back as reinforcements.
    const main = s.waves > 1 ? Math.ceil(count * 0.6) : count;
    for (let w = 0; w < s.waves; w += 1) {
      const size = w ? waveSize(count - main, s.waves - 1, w - 1) : main;
      if (size <= 0) continue;
      const wave = formSquads(size, w ? 0 : s.bosses(h.n), h.n, w, squads.length);
      squads.push(...wave);
      if (!w) {
        formLine(wave, heist.x0, heist.x1);
        wave.forEach(squad => squad.slots.forEach(slot => plan.push({ type: squad.type, lane: { slot }, at: 0, wave: 0, squad })));
        continue;
      }
      wave.forEach((squad, k) => {
        const gate = heist.doors[(k + w) % heist.doors.length];
        for (let i = 0; i < squad.size; i += 1) {
          plan.push({ type: squad.type, lane: { door: gate }, at, wave: w, squad });
          at += 0.25 + Math.random() * 0.35;
        }
        at += 1.2;
      });
      at += 6;
    }
    heist.squads = squads;
    return plan;
  };
  const heistBlock = () => {
    if (heist || travel || (raid && raid.phase !== 'end')) return 'Kampf läuft';
    if (!S.items.some(isHero)) return 'Erst einen Helden holen';
    if (problemSoon()) return 'Problem kommt gleich';
    return '';
  };
  const startHeist = h => {
    tuneHeist(h);
    const s = HEIST_SIZES[h.size];
    const crew = S.items.filter(item => kindOf(item) === 'crew');
    const field = heistField(h);
    const { x0, x1, y1, props, lines } = field;
    const land = [];
    for (let cx = 0; cx < (x1 - x0) / CHUNK; cx += 1) for (let cy = 0; cy < HEIST_DEEP; cy += 1) land.push(chunkKey(HEIST_CX + cx, cy));
    // The toughest in front.
    const order = [...crew].sort((a, b) => hpOf(b.type, b.level) - hpOf(a.type, a.level));
    const perRow = Math.max(1, Math.min(order.length, Math.floor((x1 - x0 - 2) / 1.3)));
    const posts = new Map();
    order.forEach((item, i) => {
      const row = Math.floor(i / perRow);
      const inRow = Math.min(perRow, order.length - row * perRow);
      const post = { x: (x0 + x1) / 2 + ((i % perRow) - (inRow - 1) / 2) * 1.3, y: lines.crew + row * 1.1 };
      posts.set(item.id, post);
      // They come in from the street below.
      const state = crewState(item);
      state.x = post.x;
      state.y = y1 + 0.8 + row * 0.6;
      item.hp = maxHp(item);
    });
    heist = {
      ...field, target: h, items: crew, items0: crew.length, land, posts,
      doors: props.map(p => ({ x: p.x + p.w / 2, y: p.y + p.d + 0.35 })),
      center: { x: (x0 + x1) / 2, y: (lines.enemy + lines.crew) / 2 + 0.6 },
      cam: { x: cam.x, y: cam.y, z: cam.z },
      color: gangColor(h), squads: [], order: 'halten', front: 0, charge: 0, chargeCd: 0, share: 0.5, think: 0, said: 0
    };
    refreshLand();
    [enemies, shots, zones, walkers, parts, decals, floats, moments].forEach(list => {
      list.length = 0;
    });
    raid = { n: h.n, ...heistTough(h), heist: h, phase: 'warn', t: 4.5, plan: [], clock: 0, held: 0, total: 0, waves: s.waves, wave: 1, killed: 0, battle: 0, earned: 0, stolen: 0, lost: [], result: null, streak: 0, streakAt: -9, bonus: 0, fans: 0 };
    raid.plan = heistPlan(h);
    raid.total = raid.plan.length;
    // Cover never stands where anyone lines up, in the way out of a gate, or across the crew's way in.
    const keep = [
      ...heist.squads.flatMap(squad => squad.slots).map(slot => ({ x: slot.x - 0.6, y: slot.y - 0.6, w: 1.2, d: 1.2 })),
      ...heist.doors.map(gate => ({ x: gate.x - 0.9, y: gate.y - 0.4, w: 1.8, d: lines.enemy - gate.y + 0.4 })),
      ...[...posts.values()].map(post => ({ x: post.x - 0.9, y: post.y - 0.9, w: 1.8, d: y1 + 2 - post.y }))
    ];
    heist.cover = heist.cover.filter(c => keep.every(k => c.x + c.w <= k.x || k.x + k.w <= c.x || c.y + c.d <= k.y || k.y + k.d <= c.y));
    while (raid.plan.length && !raid.plan[0].wave) {
      const next = raid.plan.shift();
      spawnEnemy(next.type, next.lane, next.squad);
    }
    play('drop');
    onHeistStart(h);
  };
  const endHeist = result => {
    const h = raid.heist;
    raid.phase = 'end';
    raid.result = result;
    raid.t = 3.8;
    S.heistEdge = clamp(result === 'won' ? S.heistEdge * HEIST_EDGE.win : S.heistEdge / HEIST_EDGE.loss, HEIST_EDGE.min, HEIST_EDGE.max);
    enemies.forEach(enemy => {
      enemy.fleeing = true;
    });
    if (result === 'won') {
      const loot = heistLoot(h);
      raid.bonus = loot.cash;
      raid.fans = loot.fans;
      S.cash += loot.cash;
      S.earned += loot.cash;
      S.fans += loot.fans;
      S.stats.heists += 1;
      S.stats.loot += loot.cash;
      S.heists = S.heists.filter(one => one.id !== h.id);
      S.heistBack.push(Date.now() + HEIST_GAP);
      scheduleRevenge(h);
      heist.props.forEach((prop, i) => {
        prop.looted = true;
        const c = { x: prop.x + prop.w / 2, y: prop.y + prop.d / 2 };
        burst(c.x, c.y, 1, 18, [YELLOW, MINT, PAPER, '#c9a36b'], { speed: 2.6, up: 3.4, g: 5, size: 0.07, life: 1.4 + i * 0.1, kind: 'debris' });
      });
      hitstop = 0.3;
    }
    shots.length = 0;
    zones.length = 0;
    drones.length = 0;
    flare = 0;
    calmAnime();
    play(result === 'won' ? 'win' : 'calm');
    persist();
    onHeistEnd(raid);
  };
  const comeHome = () => {
    Object.assign(cam, heist.cam);
    heist = null;
    raid = null;
    refreshLand();
    S.items.forEach(item => {
      if (kindOf(item) !== 'crew') return;
      const state = crewState(item);
      state.x = item.x + 0.5;
      state.y = item.y + 0.5;
      state.moving = false;
      item.hp = maxHp(item);
    });
    [enemies, shots, zones, parts, decals, floats, moments].forEach(list => {
      list.length = 0;
    });
    // A raid that fell due while the crew was out gives it a moment to get back to their posts.
    S.raidAt = Math.max(S.raidAt, Date.now() + 20 * SECOND);
    onHeistHome();
  };
  // ---------------------------------------------------------------------------------------------
  // Battles: a Raid is fought like a field battle, and it fights itself. The gang stands in squads in front of
  // its stash; the crew lines up across the lot. The crew reads the field on its own:
  // it holds while the gang walks into its fire, advances when the gang waits, and charges when the gap is
  // short and the odds are good. Nobody runs: every squad fights to its last Bootleg.

  const SQUAD_MAX = 6;
  const REINFORCE_AT = 18;
  const HOLD_GUARD = 0.8;
  const CHARGE_HIT = 1.3;
  const CHARGE_LEN = 4;
  const CHARGE_COOL = 14;
  const GANG_COLORS = ['#c8102e', '#6a1b9a', '#1e7a3c', '#1f4fbf', '#e0601a', '#3d4a52', '#b0127a', '#0f7c8a'];
  const gangColor = h => GANG_COLORS[Math.floor(h.seed / 7) % GANG_COLORS.length];
  // Front first: shields in the middle, brawlers next to them, the quick ones out on the wings.
  const LINE_ORDER = { shield: 0, brute: 1, copy: 2, kid: 3, roller: 4 };
  const chargeRoam = () => (heist && heist.charge > 0 ? 3 : 1);
  const brawler = item => Boolean(HERO_MOVES[item.type].melee);
  // A squad is one kind of Bootleg, three to six of them; the boss stands with whoever else is boss.
  const formSquads = (count, bosses, n, wave, first) => {
    const squads = [];
    const add = (type, size) => squads.push({
      id: first + squads.length + 1, type, boss: type === 'boss', wave, name: type === 'boss' ? 'Der Pate' : GANG[type].name,
      size, state: wave ? 'charge' : 'hold', patience: 0, slots: []
    });
    let left = count;
    while (left > 0) {
      const size = left <= SQUAD_MAX ? left : Math.min(left - 3, 3 + Math.floor(Math.random() * (SQUAD_MAX - 2)));
      add(rollGang(n), size);
      left -= size;
    }
    if (bosses) add('boss', bosses);
    return squads;
  };
  // A block per squad, two ranks deep from four up; the fighters in front, the ones who hang back and the boss behind them,
  // each line built out from the middle.
  const formLine = (squads, x0, x1) => {
    const centred = list => {
      const out = [];
      list.forEach((squad, i) => (i % 2 ? out.unshift(squad) : out.push(squad)));
      return out;
    };
    const front = centred(squads.filter(squad => LINE_ORDER[squad.type] !== undefined).sort((a, b) => LINE_ORDER[a.type] - LINE_ORDER[b.type]));
    const back = centred(squads.filter(squad => LINE_ORDER[squad.type] === undefined).sort((a, b) => b.boss - a.boss));
    [[front, heist.lines.enemy], [back, heist.lines.enemy - 1.4]].forEach(([line, y0]) => {
      const cols = line.map(squad => Math.ceil(squad.size / (squad.size >= 4 ? 2 : 1)));
      const want = cols.reduce((sum, c) => sum + (c - 1) * 0.72, 0) + Math.max(0, line.length - 1) * 1.5;
      const k = Math.min(1, (x1 - x0 - 1.6) / Math.max(0.01, want));
      let x = (x0 + x1) / 2 - (want * k) / 2;
      line.forEach((squad, i) => {
        for (let j = 0; j < squad.size; j += 1) squad.slots.push({ x: x + (j % cols[i]) * 0.72 * k, y: y0 - Math.floor(j / cols[i]) * 0.62 });
        x += ((cols[i] - 1) * 0.72 + 1.5) * k;
        // Nobody waits for ever: the wings go first, the back line after them, the boss last.
        squad.patience = squad.boss ? 22 : GANG[squad.type].back ? 11 : 8 - Math.abs(i - (line.length - 1) / 2) * 0.8 + Math.random() * 2;
      });
    });
  };
  const membersOf = squad => enemies.filter(enemy => enemy.squad === squad);
  const middleOf = list => ({ x: list.reduce((sum, e) => sum + e.x, 0) / list.length, y: list.reduce((sum, e) => sum + e.y, 0) / list.length });
  const advance = squad => {
    if (squad.state !== 'hold') return;
    squad.state = 'charge';
    const members = membersOf(squad);
    if (!members.length) return;
    const c = middleOf(members);
    floatText(c.x, c.y, 1.8, 'ANGRIFF!', heist.color, 0.8);
  };
  // The boss going down is called out over the whole field.
  const bossDown = enemy => {
    moments.push({ kind: 'callout', x: enemy.x, y: enemy.y, h: 1.9, text: 'DER PATE FÄLLT!', color: GOLD, t: 0, dur: 1.8 });
    onBossDown();
  };
  // Holding and advancing show in the balance bar; a charge is called out loud over the line.
  const call = order => {
    heist.order = order;
    heist.said = 2.5;
    if (order !== 'sturm') return;
    moments.push({ kind: 'callout', x: heist.center.x, y: heist.lines.crew - heist.front, h: 1.7, text: 'STURM!', color: SIGNAL, t: 0, dur: 1.2 });
    heist.charge = CHARGE_LEN;
    heist.chargeCd = CHARGE_COOL;
    // On the charge nobody holds back any longer.
    heist.squads.forEach(advance);
    play('stampede');
    shake = Math.max(shake, 0.25);
  };
  const updateBattle = dt => {
    raid.battle += dt;
    heist.charge = Math.max(0, heist.charge - dt);
    heist.chargeCd = Math.max(0, heist.chargeCd - dt);
    heist.said = Math.max(0, heist.said - dt);
    // The balance of power: health times punch on each side.
    const crew = world().filter(item => kindOf(item) === 'crew');
    const upright = enemies.filter(enemy => !enemy.fleeing);
    const hpOfItem = item => (item.hp === undefined ? maxHp(item) : item.hp);
    const ours = Math.sqrt(crew.reduce((sum, item) => sum + crewDps(item), 0) * crew.reduce((sum, item) => sum + hpOfItem(item), 0));
    const theirs = Math.sqrt(upright.reduce((sum, enemy) => sum + enemy.dps, 0) * upright.reduce((sum, enemy) => sum + Math.max(0, enemy.hp), 0));
    const share = ours + theirs > 0 ? ours / (ours + theirs) : 1;
    heist.share = ease(heist.share, share, 2, dt);
    // The squads hold the line until the crew comes close or patience runs out.
    heist.squads.forEach(squad => {
      const members = membersOf(squad).filter(enemy => !enemy.fleeing);
      if (squad.state === 'hold' && members.length && (raid.battle >= squad.patience || crew.some(item => members.some(enemy => dist(enemy.x, enemy.y, item.crew.x, item.crew.y) < 2.6)))) advance(squad);
    });
    // The line moves up toward the gang but stops short of it, and never runs into the stash.
    const front = heist.lines.crew - heist.front;
    const nearest = upright.reduce((y, enemy) => Math.max(y, enemy.y), -Infinity);
    const pace = heist.charge > 0 ? 2.6 : heist.order === 'halten' ? 0 : 0.9;
    const room = front - Math.max(heist.lines.stop, nearest + 1.3);
    if (pace && room > 0) heist.front += Math.min(room, pace * dt);
    // The crew's own call, once a second: outmatched or full of shooters facing a waiting gang, it holds;
    // otherwise it moves up, and charges when the gap is short and the odds are on its side.
    heist.think -= dt;
    if (heist.think > 0 || heist.said > 0 || !upright.length) return;
    heist.think = 1;
    const punch = crew.reduce((sum, item) => sum + crewDps(item), 0) || 1;
    const melee = crew.filter(brawler).reduce((sum, item) => sum + crewDps(item), 0) / punch;
    const waiting = heist.squads.some(squad => squad.state === 'hold' && squad.wave === 0 && membersOf(squad).length);
    const gap = front - nearest;
    let order = 'vorruecken';
    if (share < 0.42 || (waiting && melee < 0.5 && raid.battle < 6)) order = 'halten';
    if (heist.chargeCd <= 0 && share >= 0.5 && gap < 2.6 && melee >= 0.25) order = 'sturm';
    if (order !== heist.order || order === 'sturm') call(order);
  };

  // ---------------------------------------------------------------------------------------------
  // Tricks: the player's own moves. Each one hits hard, looks loud and finds its own target, at full strength from the
  // first go.

  const zones = [];
  const drones = [];
  let flare = 0;
  let flareColor = PAPER;
  // Anime beats for the aces: a cut-in with the ace's name, focus lines and a zoom punch on the big hit,
  // and an impact frame that flips the screen to ink for a blink.
  const anime = { cut: null, focus: 0, zoom: 0, impact: 0, x: 0, y: 0 };
  // `delay` holds it back, for one that would otherwise go by behind the clouds of a trip.
  const cutIn = (text, color, sub = '', dur = 1.3, delay = 0) => {
    anime.cut = { text, color, sub, t: -delay, dur };
  };
  const bigHit = (x, y, power = 1) => {
    anime.x = x;
    anime.y = y;
    anime.focus = Math.max(anime.focus, 0.45 * power);
    if (reducedMotion.matches) return;
    anime.zoom = Math.max(anime.zoom, Math.min(1, power));
    anime.impact = Math.max(anime.impact, 0.08 * Math.min(1, power));
  };
  const calmAnime = () => {
    anime.cut = null;
    anime.focus = 0;
    anime.zoom = 0;
    anime.impact = 0;
  };
  let droneId = 0;
  const baseCenter = () => {
    if (heist) return heist.center;
    const home = hq();
    return home && standing(home) ? center(home) : { x: K.x, y: K.y };
  };
  // Where it hurts most: the Bootleg with the most others around it, a boss counting three.
  const hotSpot = (r = 1.6) => {
    let best = null;
    let most = 0;
    enemies.forEach(a => {
      if (a.fly) return;
      const n = enemies.reduce((sum, b) => sum + (!b.fly && dist(a.x, a.y, b.x, b.y) <= r ? (GANG[b.type].boss ? 3 : 1) : 0), 0);
      if (n > most) {
        most = n;
        best = a;
      }
    });
    return best ? { x: best.x, y: best.y } : null;
  };
  const nearestEnemy = (x, y, reach = Infinity, skip = null) => {
    let best = null;
    let bestD = reach;
    enemies.forEach(enemy => {
      if (enemy.fly || enemy === skip) return;
      const d = dist(enemy.x, enemy.y, x, y);
      if (d < bestD) {
        bestD = d;
        best = enemy;
      }
    });
    return best;
  };
  const knock = (enemy, fx, fy, push, stun) => {
    if (!enemies.includes(enemy) || GANG[enemy.type].boss || enemy.fly) return;
    const n = dist(enemy.x, enemy.y, fx, fy) || 1;
    const to = inArena(enemy.x + ((enemy.x - fx) / n) * push, enemy.y + ((enemy.y - fy) / n) * push, enemy);
    enemy.fly = { t: 0, dur: 0.45, fx: enemy.x, fy: enemy.y, tx: to.x, ty: to.y, arc: 0.9, stun };
  };
  const later = (delay, apply) => moments.push({ kind: 'later', t: -delay, dur: 0, apply });
  // An ace going off: a fireball in its colour, a shock ring over the ground, sparks, smoke and a scorch mark.
  const explode = (x, y, r, color, power = 1) => {
    moments.push({ kind: 'boom', x, y, r, color, t: 0, dur: 0.4 + r * 0.12, spin: Math.random() * TAU });
    burst(x, y, 0.4, Math.round((8 + r * 8) * power), [color, YELLOW, PAPER, SIGNAL], { speed: 2 + r * 1.6, up: 3 + r, size: 0.06, kind: 'spark', life: 0.7 });
    burst(x, y, 0.2, Math.round((3 + r * 3) * power), ['rgba(60,56,60,0.45)', 'rgba(205,200,190,0.55)'], { speed: 0.8 + r * 0.6, up: 0.8, g: -0.4, size: 0.12 + r * 0.04, life: 1.1, kind: 'smoke' });
    splat(x, y, r * 0.5, 'rgba(30,26,30,0.3)', 5);
    shake = Math.max(shake, Math.min(0.75, 0.14 + r * 0.15 * power));
    if (power >= 1) hitstop = Math.max(hitstop, 0.07);
  };
  // Goldfaust: the player's own fist, free and always at hand. A tap on a Bootleg lands a hit, no faster than a
  // fist can. Hits close together make a combo that hits harder the longer it runs, every crit sends a shock wave
  // into the Bootlegs next to it, and every fifteenth hit of a combo brings the big Goldfaust down on the crowd.
  const PUNCH_GAP = 0.12;
  const COMBO_GAP = 1.2;
  const MEGA_AT = 15;
  let punchCd = 0;
  let combo = 0;
  let comboLeft = 0;
  const megaFist = (x, y) => {
    const r = 2.2;
    splashAt(x, y, r, punchDamage() * 8);
    enemies.forEach(enemy => {
      if (dist(enemy.x, enemy.y, x, y) <= r) knock(enemy, x, y, 1.6, 1.2);
    });
    moments.push({ kind: 'slap', x, y, t: 0, dur: 0.7, crit: true, mega: true, tilt: 1 });
    moments.push({ kind: 'pow', x, y, t: 0, dur: 0.8, text: 'GOLDFAUST!', boss: true, tilt: 0.12 });
    explode(x, y, r, GOLD, 1.3);
    bigHit(x, y, 1.2);
    flare = Math.max(flare, 0.35);
    flareColor = PAPER;
    play('megafaust');
  };
  const punchAt = point => {
    const reach = cw() * (compact ? 1.2 : 0.9);
    let enemy = null;
    let best = reach;
    enemies.forEach(one => {
      if (one.fly || fled(one)) return;
      const at = proj(one.x, one.y, 0.45);
      const d = Math.hypot(point.x - at.x, point.y - at.y);
      if (d < best) {
        best = d;
        enemy = one;
      }
    });
    if (!enemy || punchCd > 0) return;
    punchCd = PUNCH_GAP;
    combo += 1;
    comboLeft = COMBO_GAP;
    S.stats.punches += 1;
    noteCombo(combo);
    const { x, y } = enemy;
    const tilt = point.x < proj(x, y).x ? 1 : -1;
    const crit = combo % 5 === 0;
    const dmg = punchDamage() * Math.min(2, 1 + combo * 0.05) * (crit ? 2 : 1);
    hurtEnemy(enemy, dmg, true);
    if (crit) {
      [...enemies].forEach(one => {
        if (one !== enemy && !one.fly && dist(one.x, one.y, x, y) <= 1.2) hurtEnemy(one, dmg * 0.5, true);
      });
      moments.push({ kind: 'ring', x, y, r: 1.2, t: 0, dur: 0.3, color: GOLD });
    }
    moments.push({ kind: 'slap', x: x + (Math.random() - 0.5) * 0.2, y, t: 0, dur: crit ? 0.42 : 0.28, crit, tilt });
    burst(x, y, 0.5, crit ? 10 : 4, crit ? [YELLOW, SIGNAL, PAPER] : [PAPER, YELLOW], { speed: crit ? 2.4 : 1.3, up: 2, size: 0.05, kind: 'spark' });
    moments.push({ kind: 'ring', x, y, r: crit ? 1.2 : 0.7, t: 0, dur: 0.25, color: crit ? SIGNAL : PAPER });
    if (combo >= 3) floatText(x, y, 1.5, `${combo}er Combo`, combo >= MEGA_AT - 5 ? SIGNAL : INK, Math.min(1.3, 0.7 + combo * 0.03));
    shake = Math.max(shake, crit ? 0.18 : 0.08);
    play('jab');
    if (combo % MEGA_AT === 0) megaFist(x, y);
  };
  const updateTricks = dt => {
    punchCd = Math.max(0, punchCd - dt);
    comboLeft = Math.max(0, comboLeft - dt);
    if (!comboLeft && combo) {
      onComboEnd(combo);
      combo = 0;
    }
    flare = Math.max(0, flare - dt * 2.4);
    if (anime.cut) {
      anime.cut.t += dt;
      if (anime.cut.t >= anime.cut.dur) anime.cut = null;
    }
    anime.focus = Math.max(0, anime.focus - dt);
    anime.zoom = Math.max(0, anime.zoom - dt * 3);
    anime.impact = Math.max(0, anime.impact - dt);
    for (let i = zones.length - 1; i >= 0; i -= 1) {
      const zone = zones[i];
      zone.life -= dt;
      if (zone.life <= 0) zones.splice(i, 1);
    }
    updateDrones(dt);
    moments.forEach(f => {
      if (f.t < 0) return;
      if (f.kind === 'stampede') trample(f);
      else if (f.kind === 'wreck') swingWreck(f);
      else if (f.kind === 'twister') spinTwister(f, dt);
      else if (f.kind === 'beam') burnBeam(f, dt);
    });
  };
  // While an ace plays out, the camera rides along with it; afterwards it goes back to where the player had it.
  // `follow` keeps it on something that moves and ends the ride when it returns nothing.
  let camLock = null;
  const lockCam = (x, y, dur, follow = null) => {
    camLock = { x, y, until: clock + dur, follow, back: camLock ? camLock.back : { x: cam.x, y: cam.y } };
    return camLock;
  };
  // Mörser: two volleys of heavy shells lobbed high from the Base, one for each Bootleg it can find.
  const useShirts = () => {
    const spot = hotSpot(3);
    if (!spot) return false;
    const from = baseCenter();
    lockCam(spot.x, spot.y, 2.1);
    const pool = enemies.filter(enemy => !enemy.fly).sort((a, b) => dist(a.x, a.y, spot.x, spot.y) - dist(b.x, b.y, spot.x, spot.y));
    [0, 0.45].forEach(delay => {
      for (let i = 0; i < SHIRT_COUNT; i += 1) {
        const target = pool[i % pool.length];
        shots.push({ kind: 'shell', fx: from.x, fy: from.y, x: from.x, y: from.y, h: 2, tx: target.x, ty: target.y, target, t: -delay - i * 0.07, dur: 0.85 + Math.random() * 0.15, dmg: shirtDamage(), splash: 1.5 });
      }
      later(delay, () => {
        burst(from.x, from.y, 2.2, 8, [YELLOW, '#ff8a1a', PAPER], { speed: 2.4, up: 3, size: 0.05, kind: 'spark' });
        burst(from.x, from.y, 2.3, 6, ['rgba(70,66,64,0.5)', 'rgba(170,166,160,0.55)'], { speed: 0.6, up: 1.4, g: -0.5, size: 0.14, life: 1.2, kind: 'smoke' });
        play('cannon');
      });
      later(delay + 0.95, () => bigHit(spot.x, spot.y, 0.6));
    });
    return true;
  };
  // Every shell goes off where it lands: fire, dirt and smoke, a black scorch, and everyone close by takes the hit.
  const landShell = shot => {
    splashAt(shot.tx, shot.ty, shot.splash, shot.dmg);
    burst(shot.tx, shot.ty, 0.3, 8, ['#5a4a3c', '#7d6a58', '#3a3a3d'], { speed: 2.6, up: 3.4, g: 7, size: 0.05, kind: 'debris', life: 0.8 });
    explode(shot.tx, shot.ty, shot.splash, '#ff8a1a', 0.55);
    play('shellhit');
  };
  // Blackout: a big black swarm lifts off from the Base, each drone hunting the Bootleg closest to it and zapping it and
  // two more down a chain again and again; at the end they dive into the crowd one after another.
  const useDrone = () => {
    if (!enemies.length) return false;
    const c = baseCenter();
    const n = 18;
    const mine = [];
    for (let i = 0; i < n; i += 1) {
      droneId += 1;
      const a = (i / n) * TAU;
      mine.push({ id: droneId, x: c.x + Math.cos(a) * 0.9, y: c.y + Math.sin(a) * 0.6, h: 0.4 + (i % 3) * 0.2, life: DRONE_TIME + i * 0.1, cd: 0.5 + i * 0.06, tilt: 0, zap: 0, power: 2.4 / n, size: 0.5, angle: a, ring: 0.5 + (i % 3) * 0.25, alt: 1.7 + (i % 4) * 0.3, last: i === n - 1 });
    }
    drones.push(...mine);
    // The camera stays with the swarm, through the last dive.
    lockCam(c.x, c.y, DRONE_TIME + 6, () => {
      const up = mine.filter(one => drones.includes(one) && one.h < 8);
      return up.length ? { x: up.reduce((sum, one) => sum + one.x, 0) / up.length, y: up.reduce((sum, one) => sum + one.y, 0) / up.length } : null;
    });
    play('drone');
    return true;
  };
  const updateDrones = dt => {
    for (let i = drones.length - 1; i >= 0; i -= 1) {
      const drone = drones[i];
      drone.life -= dt;
      drone.zap = Math.max(0, drone.zap - dt);
      if (drone.life <= 0) {
        // Its last act: a dive into the thickest crowd and a blast; with nothing left to hit it flies home.
        if (!drone.dive) {
          drone.dive = hotSpot(1.6);
          if (drone.dive) play('dive');
        }
        if (!drone.dive) {
          drone.h += dt * 5;
          if (drone.h > 9) drones.splice(i, 1);
          continue;
        }
        const ddx = drone.dive.x - drone.x;
        const ddy = drone.dive.y - drone.y;
        const dd = Math.hypot(ddx, ddy);
        const step = Math.min(dd, 9 * dt);
        if (dd > 0.01) {
          drone.x += (ddx / dd) * step;
          drone.y += (ddy / dd) * step;
        }
        drone.h = Math.max(0, drone.h - dt * 6);
        drone.tilt = ease(drone.tilt, clamp(ddx, -1, 1) * 0.8, 8, dt);
        if (dd > 0.1 || drone.h > 0.3) continue;
        drones.splice(i, 1);
        const blast = 1.3;
        splashAt(drone.x, drone.y, blast, droneDamage() * 9 * drone.power);
        enemies.forEach(enemy => {
          if (dist(enemy.x, enemy.y, drone.x, drone.y) <= blast) knock(enemy, drone.x, drone.y, 1, 0.6);
        });
        explode(drone.x, drone.y, blast, DRONE_RED, drone.last ? 1.1 : 0.5);
        if (drone.last) bigHit(drone.x, drone.y, 0.8);
        burst(drone.x, drone.y, 0.5, 6, ['#141416', '#2a2a2e', DRONE_RED], { speed: 2.6, up: 2.6, g: 7, size: 0.05, kind: 'debris', life: 0.8 });
        if (drone.last) moments.push({ kind: 'pow', x: drone.x, y: drone.y, t: 0, dur: 0.6, text: 'BLACKOUT!', boss: false, tilt: -0.1 });
        shake = Math.max(shake, 0.15);
        play('dronekill');
        continue;
      }
      drone.h = ease(drone.h, (drone.alt || 2.3) + Math.sin(clock * 2 + drone.id) * 0.15, 3, dt);
      const target = nearestEnemy(drone.x, drone.y);
      if (!target) continue;
      // Each one holds its own place in the ring round the target, so the swarm surrounds it.
      const a = drone.angle === undefined ? (drone.id % 2 ? 0 : Math.PI) : drone.angle + clock * 0.6;
      const ring = drone.ring || 0.6;
      const dx = target.x + Math.cos(a) * ring - drone.x;
      const dy = target.y - 0.3 + Math.sin(a) * ring * 0.65 - drone.y;
      const d = Math.hypot(dx, dy);
      const step = Math.min(d, 4.5 * dt);
      if (d > 0.01) {
        drone.x += (dx / d) * step;
        drone.y += (dy / d) * step;
      }
      drone.tilt = ease(drone.tilt, clamp(dx, -1, 1) * 0.35, 6, dt);
      drone.cd -= dt;
      if (drone.cd > 0 || dist(drone.x, drone.y, target.x, target.y) > 1.6) continue;
      drone.cd = 0.4;
      drone.zap = 0.12;
      const hit = [target];
      let last = target;
      for (let k = 0; k < 2; k += 1) {
        const next = nearestEnemy(last.x, last.y, 2, null);
        if (!next || hit.includes(next)) break;
        hit.push(next);
        last = next;
      }
      let from = { x: drone.x, y: drone.y, h: drone.h - 0.15 };
      hit.forEach((enemy, k) => {
        moments.push({ kind: 'zap', x: from.x, y: from.y, h: from.h, tx: enemy.x, ty: enemy.y, t: 0, dur: 0.2, color: DRONE_RED });
        burst(enemy.x, enemy.y, 0.5, 4, [DRONE_RED, PAPER], { speed: 1.4, up: 1.6, size: 0.04, kind: 'spark', life: 0.4 });
        hurtEnemy(enemy, droneDamage() * (drone.power || 1) * (k ? 0.6 : 1), true);
        from = { x: enemy.x, y: enemy.y, h: 0.5 };
      });
      play('zap');
    }
  };
  // Moshpit: two crowds of your fans run across the thickest part of the fight, one each way, and flatten every
  // Bootleg they meet; whoever the wall of death catches stays down a while.
  const useFans = () => {
    const spot = hotSpot(3);
    if (!spot) return false;
    const dir = spot.x >= baseCenter().x ? 1 : -1;
    lockCam(spot.x, spot.y, 3.6);
    for (let w = 0; w < 2; w += 1) {
      const rand = mulberry32(newSeed());
      const runners = Array.from({ length: 30 }, (_, k) => ({ dy: (rand() - 0.5) * 6, lag: rand() * 0.45, jump: rand() * TAU, cheer: k % 4 === 0, look: { ...makeHustler(Math.floor(rand() * 2 ** 31)), chain: rand() < 0.2, hold: rand() < 0.3 ? 'phone' : 'none' } }));
      moments.push({ kind: 'stampede', x: spot.x, y: spot.y + (w ? 0.6 : 0), dir: w ? -dir : dir, t: -w * 1.2, dur: 2.6, runners, hit: new Set() });
      later(w * 1.2, () => {
        play('stampede');
        bigHit(spot.x, spot.y, 0.5);
      });
    }
    return true;
  };
  const STAMPEDE_RUN = 7;
  const MOSH_STUN = 2.2;
  const runnerAt = (f, r) => ({ x: f.x - f.dir * STAMPEDE_RUN + f.dir * (f.t - r.lag) * STAMPEDE_RUN * 0.9, y: f.y + r.dy });
  const trample = f => {
    f.runners.forEach(r => {
      if (f.t < r.lag) return;
      const at = runnerAt(f, r);
      enemies.forEach(enemy => {
        if (f.hit.has(enemy) || enemy.fly || dist(enemy.x, enemy.y, at.x, at.y) > 0.55) return;
        f.hit.add(enemy);
        hurtEnemy(enemy, fansDamage());
        knock(enemy, at.x - f.dir, enemy.y, 1.4, MOSH_STUN);
        if (enemies.includes(enemy) && GANG[enemy.type].boss) enemy.stun = Math.max(enemy.stun, MOSH_STUN);
        burst(enemy.x, enemy.y, 0.4, 8, [PAPER, MOSH, SIGNAL], { speed: 2.2, up: 2.6, size: 0.05, kind: 'spark', life: 0.6 });
        moments.push({ kind: 'ring', x: enemy.x, y: enemy.y, r: 0.8, t: 0, dur: 0.25, color: MOSH });
        shake = Math.max(shake, 0.12);
        play('trample');
      });
    });
  };
  // Airdrop: three cases land where the most Bootlegs stand, the big one in the middle.
  const useDrop = () => {
    const spot = hotSpot(BOMBE_RADIUS);
    if (!spot) return false;
    lockCam(spot.x, spot.y, 2.2);
    dropCase(spot.x, spot.y);
    return true;
  };
  // Flightcase: it whistles down, slams in, flattens whatever stands underneath and leaves a crater that slows the
  // Bootlegs down.
  const dropCase = (x, y) => {
    [[0, 0, 1], [-0.9, 0.5, 0.7], [0.9, 0.4, 0.7]].forEach(([dx, dy, k], i) => shots.push({ kind: 'case', fx: x + dx - 0.6, fy: y + dy - 0.2, tx: x + dx, ty: y + dy, x: x + dx, y: y + dy, h: 10, t: 0, dur: 0.6 + i * 0.14, dmg: bombeDamage() * k, splash: BOMBE_RADIUS * (k < 1 ? 0.8 : 1), size: k, spin: (Math.random() - 0.5) * 0.6 }));
    play('whistle');
  };
  const landCase = shot => {
    const { tx, ty } = shot;
    splashAt(tx, ty, shot.splash, shot.dmg);
    splat(tx, ty, shot.splash * 0.5, 'rgba(40,40,44,0.35)', 6);
    burst(tx, ty, 0.2, 16, ['#2a2a2e', '#c9ccd1', PAPER, SIGNAL], { speed: 3, up: 2.6, g: 6, size: 0.06, kind: 'debris', life: 0.9 });
    burst(tx, ty, 0.1, 9, ['rgba(205,200,190,0.55)'], { speed: 1.7, up: 0.4, g: -0.2, size: 0.12, life: 0.9, kind: 'smoke' });
    moments.push({ kind: 'ring', x: tx, y: ty, r: shot.splash, t: 0, dur: 0.4, color: INK });
    moments.push({ kind: 'ring', x: tx, y: ty, r: shot.splash * 1.5, t: -0.08, dur: 0.5, color: PAPER });
    explode(tx, ty, shot.splash * shot.size, SIGNAL, shot.size);
    enemies.forEach(enemy => {
      if (dist(enemy.x, enemy.y, tx, ty) <= shot.splash) knock(enemy, tx, ty, 1.3, 0.8);
    });
    if (shot.size === 1) {
      flare = Math.max(flare, 0.3);
      flareColor = PAPER;
      bigHit(tx, ty, 1);
    }
    moments.push({ kind: 'crate', x: tx, y: ty, size: shot.size, spin: shot.spin, t: 0, dur: 5 });
    if (shot.size === 1) moments.push({ kind: 'pow', x: tx, y: ty, t: 0, dur: 0.55, text: 'AIRDROP!', boss: false, tilt: shot.spin * 0.3 });
    zones.push({ x: tx, y: ty, r: shot.splash * 0.9, life: 5 });
    shake = Math.max(shake, 0.5);
    hitstop = Math.max(hitstop, 0.08);
    play('slam');
  };

  // Raketenhagel: two salvos of guided missiles climb off the roof of the Base, each one curving in on a Bootleg of its
  // own and bursting into three bomblets.
  const useRockets = () => {
    const pool = enemies.filter(enemy => !enemy.fly && !fled(enemy));
    if (!pool.length) return false;
    const from = baseCenter();
    const spot = hotSpot(3) || pool[0];
    lockCam(spot.x, spot.y, 2.9);
    [0, 0.9].forEach(delay => {
      const targets = [...pool].sort((a, b) => dist(a.x, a.y, spot.x, spot.y) - dist(b.x, b.y, spot.x, spot.y));
      for (let i = 0; i < ROCKET_COUNT; i += 1) {
        const target = targets[i % targets.length];
        shots.push({ kind: 'missile', fx: from.x, fy: from.y, x: from.x, y: from.y, h: 2.4, tx: target.x, ty: target.y, target, t: -delay - i * 0.06, dur: 1 + Math.random() * 0.25, side: (i % 2 ? 1 : -1) * (0.6 + Math.random() * 1.2), dmg: rocketDamage(), splash: 0.9 });
      }
      later(delay, () => {
        burst(from.x, from.y, 2.4, 10, [TRICK_COLOR.rakete, YELLOW, PAPER], { speed: 2.4, up: 3.4, size: 0.05, kind: 'spark' });
        burst(from.x, from.y, 2.5, 8, ['rgba(70,66,64,0.45)', 'rgba(200,196,190,0.6)'], { speed: 0.8, up: 1.6, g: -0.5, size: 0.15, life: 1.3, kind: 'smoke' });
        play('missile');
      });
      later(delay + 1.1, () => bigHit(spot.x, spot.y, 0.7));
    });
    return true;
  };
  const landMissile = shot => {
    splashAt(shot.tx, shot.ty, shot.splash, shot.dmg);
    explode(shot.tx, shot.ty, shot.splash, TRICK_COLOR.rakete, 0.6);
    burst(shot.tx, shot.ty, 0.3, 6, ['#5a4a3c', '#3a3a3d', PAPER], { speed: 2.4, up: 3, g: 7, size: 0.05, kind: 'debris', life: 0.7 });
    for (let k = 0; k < 3; k += 1) {
      const a = (k / 3) * TAU + Math.random();
      const r = 0.8 + Math.random() * 0.5;
      shots.push({ kind: 'bomblet', fx: shot.tx, fy: shot.ty, tx: shot.tx + Math.cos(a) * r, ty: shot.ty + Math.sin(a) * r, x: shot.tx, y: shot.ty, h: 0.3, t: -0.05 - k * 0.05, dur: 0.3 + Math.random() * 0.1, dmg: shot.dmg * 0.35 });
    }
    play('missilehit');
  };
  // Abrissbirne: a huge steel ball on a chain from high above swings through the thickest crowd and back again,
  // flattening a lane each way.
  const WRECK_CHAIN = 6;
  const WRECK_ARC = 0.9;
  const WRECK_SWING = 1.1;
  const useWreck = () => {
    const spot = hotSpot(2);
    if (!spot) return false;
    const dir = spot.x >= baseCenter().x ? 1 : -1;
    const swings = 2;
    moments.push({ kind: 'wreck', x: spot.x, y: spot.y, dir, swings, r: 0.8 * WRECK_RADIUS, t: 0, dur: swings * WRECK_SWING + 0.5, hit: [new Set(), new Set()], swing: -1 });
    lockCam(spot.x, spot.y, swings * WRECK_SWING + 0.6);
    return true;
  };
  const wreckAt = f => {
    const s = Math.min(f.swings - 1, Math.floor(f.t / WRECK_SWING));
    const u = clamp((f.t - s * WRECK_SWING) / WRECK_SWING, 0, 1);
    const sign = s % 2 ? -f.dir : f.dir;
    const a = sign * WRECK_ARC * -Math.cos(u * Math.PI);
    return { x: f.x + Math.sin(a) * WRECK_CHAIN, y: f.y + 0.2, h: WRECK_CHAIN * (1 - Math.cos(a)) + f.r * 0.9, a, s, sign };
  };
  const swingWreck = f => {
    const ball = wreckAt(f);
    if (ball.s !== f.swing && f.t < f.swings * WRECK_SWING) {
      f.swing = ball.s;
      play('wreck');
    }
    if (ball.h > f.r + 1 || f.t > f.swings * WRECK_SWING) return;
    const hit = f.hit[ball.s];
    [...enemies].forEach(enemy => {
      if (hit.has(enemy) || enemy.fly || dist(enemy.x, enemy.y, ball.x, ball.y) > f.r + 0.8) return;
      hit.add(enemy);
      hurtEnemy(enemy, wreckDamage());
      knock(enemy, ball.x - ball.sign, enemy.y, 2.6, 1.3);
      if (enemies.includes(enemy) && GANG[enemy.type].boss) enemy.stun = Math.max(enemy.stun, 0.8);
      burst(enemy.x, enemy.y, 0.5, 10, [PAPER, YELLOW, TRICK_COLOR.birne, '#3a3f47'], { speed: 2.8, up: 2.6, size: 0.05, kind: 'spark', life: 0.6 });
      moments.push({ kind: 'ring', x: enemy.x, y: enemy.y, r: 0.9, t: 0, dur: 0.3, color: TRICK_COLOR.birne });
      shake = Math.max(shake, 0.25);
      hitstop = Math.max(hitstop, 0.04);
      if (hit.size === 1) {
        moments.push({ kind: 'pow', x: enemy.x, y: enemy.y, t: 0, dur: 0.6, text: 'ABRISS!', boss: false, tilt: ball.sign * 0.12 });
        bigHit(enemy.x, enemy.y, 0.7);
      }
      play('wreckhit');
    });
    if (Math.random() < 0.5) burst(ball.x, ball.y, 0.1, 2, ['#8a7a62', '#c9c3b5'], { speed: 1.2, up: 1, g: 6, size: 0.05, kind: 'debris', life: 0.5 });
  };
  // Tornado: a twister wanders after the crowd, sucks the Bootlegs in, spins them round and, when it dies down,
  // flings them all away. Bosses stand their ground but get the wind too. Two of them, each a supercell.
  const TWISTER_TICK = 0.25;
  const useTwister = () => {
    const spot = hotSpot(TWISTER_RADIUS);
    if (!spot) return false;
    const dur = TWISTER_TIME;
    const mine = [];
    for (let k = 0; k < 2; k += 1) {
      const at = inArena(spot.x + (k ? 1.6 : -1.6), spot.y + (k ? 0.5 : 0));
      const f = { kind: 'twister', x: at.x, y: at.y, r: TWISTER_RADIUS, t: -k * 0.4, dur, tick: 0, caught: new Set(), seed: Math.random() * TAU };
      moments.push(f);
      mine.push(f);
    }
    moments.push({ kind: 'pow', x: spot.x, y: spot.y, t: 0, dur: 0.7, text: 'TORNADO!', boss: true, tilt: -0.1 });
    lockCam(spot.x, spot.y, dur + 0.8, () => {
      const on = mine.filter(f => moments.includes(f));
      return on.length ? { x: on.reduce((sum, f) => sum + f.x, 0) / on.length, y: on.reduce((sum, f) => sum + f.y, 0) / on.length } : null;
    });
    play('twister');
    return true;
  };
  const spinTwister = (f, dt) => {
    const goal = hotSpot(f.r) || f;
    const to = inArena(goal.x, goal.y);
    const d = dist(f.x, f.y, to.x, to.y);
    if (d > 0.05) {
      const step = Math.min(d, 1.3 * dt);
      f.x += ((to.x - f.x) / d) * step;
      f.y += ((to.y - f.y) / d) * step;
    }
    const ending = f.t >= f.dur - dt * 1.5;
    f.tick -= dt;
    const hurt = f.tick <= 0;
    if (hurt) f.tick = TWISTER_TICK;
    [...enemies].forEach(enemy => {
      if (enemy.fly || fled(enemy)) return;
      const ed = dist(enemy.x, enemy.y, f.x, f.y);
      if (ed > f.r * 1.5) {
        if (f.caught.delete(enemy)) enemy.h = 0;
        return;
      }
      if (hurt && ed <= f.r) hurtEnemy(enemy, twisterDamage() * TWISTER_TICK);
      if (!enemies.includes(enemy) || GANG[enemy.type].boss) return;
      // Pulled in and round: a little closer and a little further along the circle every frame.
      f.caught.add(enemy);
      const a = Math.atan2(enemy.y - f.y, enemy.x - f.x) + dt * 3.2;
      const r = ed + (f.r * 0.45 - ed) * Math.min(1, dt * 2.5);
      const at = inArena(f.x + Math.cos(a) * r, f.y + Math.sin(a) * r, enemy);
      enemy.x = at.x;
      enemy.y = at.y;
      enemy.h = 0.4 + Math.sin(clock * 6 + enemy.id) * 0.25;
      enemy.stun = Math.max(enemy.stun || 0, 0.3);
    });
    if (Math.random() < dt * 14) burst(f.x + (Math.random() - 0.5) * f.r, f.y + (Math.random() - 0.5) * f.r * 0.6, 0.1, 1, ['rgba(200,190,170,0.6)', 'rgba(120,110,95,0.5)'], { speed: 1.4, up: 2.4, g: -0.6, size: 0.12, life: 0.9, kind: 'smoke' });
    if (!ending) return;
    // It dies down and lets go: everyone it held flies off.
    f.caught.forEach(enemy => {
      if (!enemies.includes(enemy)) return;
      enemy.h = 0;
      hurtEnemy(enemy, twisterDamage() * 1.5);
      knock(enemy, f.x, f.y, 2.8, 1.2);
    });
    f.caught.clear();
    f.t = f.dur;
    explode(f.x, f.y, f.r * 0.7, TRICK_COLOR.tornado, 0.8);
    moments.push({ kind: 'ring', x: f.x, y: f.y, r: f.r * 1.4, t: 0, dur: 0.5, color: TRICK_COLOR.tornado, wide: true });
    bigHit(f.x, f.y, 0.8);
    play('slam');
  };
  // Orbitallaser: a beam from orbit locks on to the thickest crowd and follows it, burning everyone it touches.
  // It aims for a blink, then fires, and a second beam hunts on its own.
  const BEAM_AIM = 0.4;
  const BEAM_SPEED = 2.2;
  const useLaser = () => {
    const spot = hotSpot(1.5);
    if (!spot) return false;
    [[0, 0], [1.4, 0.35]].forEach(([off, delay]) => {
      const at = inArena(spot.x + off, spot.y + off * 0.3);
      moments.push({ kind: 'beam', x: at.x, y: at.y, r: LASER_RADIUS, t: -delay, dur: BEAM_AIM + 3, scorch: 0, fired: false, other: off > 0 });
    });
    lockCam(spot.x, spot.y, BEAM_AIM + 3.4, () => {
      const on = moments.find(f => f.kind === 'beam');
      return on ? { x: on.x, y: on.y } : null;
    });
    play('laser');
    return true;
  };
  const burnBeam = (f, dt) => {
    if (f.t < BEAM_AIM) return;
    // The first beam stays on the thickest crowd; the second goes for the Bootleg nearest to it that the first is not on.
    const first = moments.find(one => one.kind === 'beam' && !one.other);
    const aim = f.other ? enemies.filter(enemy => !enemy.fly && !fled(enemy) && (!first || dist(enemy.x, enemy.y, first.x, first.y) > 1.5)).sort((a, b) => dist(a.x, a.y, f.x, f.y) - dist(b.x, b.y, f.x, f.y))[0] : hotSpot(1.5);
    if (aim) {
      const d = dist(f.x, f.y, aim.x, aim.y);
      const step = Math.min(d, BEAM_SPEED * dt);
      if (d > 0.01) {
        f.x += ((aim.x - f.x) / d) * step;
        f.y += ((aim.y - f.y) / d) * step;
      }
    }
    if (!f.fired) {
      f.fired = true;
      flare = Math.max(flare, 0.5);
      flareColor = '#d9ffe0';
      bigHit(f.x, f.y, 0.9);
      moments.push({ kind: 'pow', x: f.x, y: f.y, t: 0, dur: 0.6, text: 'LASER!', boss: true, tilt: 0.1 });
      play('laserfire');
    }
    [...enemies].forEach(enemy => {
      if (enemy.fly || dist(enemy.x, enemy.y, f.x, f.y) > f.r + 0.25) return;
      hurtEnemy(enemy, laserDamage() * dt);
      if (enemies.includes(enemy) && !GANG[enemy.type].boss) enemy.stun = Math.max(enemy.stun || 0, 0.25);
      if (Math.random() < dt * 20) burst(enemy.x, enemy.y, 0.6, 2, [TRICK_COLOR.laser, PAPER, YELLOW], { speed: 1.6, up: 2.4, size: 0.04, kind: 'spark', life: 0.4 });
    });
    f.scorch -= dt;
    if (f.scorch <= 0) {
      f.scorch = 0.08;
      splat(f.x, f.y, f.r * 0.7, 'rgba(26,30,24,0.4)', 6);
    }
    burst(f.x, f.y, 0.1, 2, [TRICK_COLOR.laser, PAPER, YELLOW], { speed: 2.2, up: 3, size: 0.04, kind: 'spark', life: 0.45 });
    if (Math.random() < dt * 10) burst(f.x, f.y, 0.2, 1, ['rgba(60,64,58,0.5)'], { speed: 0.4, up: 1, g: -0.5, size: 0.14, life: 1, kind: 'smoke' });
    shake = Math.max(shake, 0.08);
  };
  // Erdbeben: the ground cracks open and shock waves roll out over the fight, each wider than the last; everyone
  // they reach is thrown up and lies dazed a good while, and an aftershock follows.
  const QUAKE_WAVES = [[0, 3], [0.6, 4.5], [1.2, 6], [1.9, 5]];
  const useQuake = () => {
    const spot = hotSpot(3);
    if (!spot) return false;
    lockCam(spot.x, spot.y, QUAKE_WAVES[QUAKE_WAVES.length - 1][0] + 1.2);
    splat(spot.x, spot.y, 2.2, 'rgba(40,28,16,0.55)', 9, true);
    QUAKE_WAVES.forEach(([delay, r], k) => later(delay, () => {
      [...enemies].forEach(enemy => {
        if (enemy.fly || dist(enemy.x, enemy.y, spot.x, spot.y) > r) return;
        hurtEnemy(enemy, quakeDamage());
        if (!enemies.includes(enemy)) return;
        if (GANG[enemy.type].boss) enemy.stun = Math.max(enemy.stun, 0.5);
        else knock(enemy, spot.x, spot.y, 0.5, QUAKE_STUN);
      });
      moments.push({ kind: 'ring', x: spot.x, y: spot.y, r: r * 1.1, t: 0, dur: 0.6, color: TRICK_COLOR.beben, wide: true, fill: 'rgba(161,98,7,0.14)' });
      moments.push({ kind: 'ring', x: spot.x, y: spot.y, r: r * 0.8, t: -0.06, dur: 0.5, color: PAPER });
      for (let i = 0; i < 4 + k * 2; i += 1) {
        const a = Math.random() * TAU;
        const d = r * (0.3 + Math.random() * 0.6);
        const x = spot.x + Math.cos(a) * d;
        const y = spot.y + Math.sin(a) * d * 0.8;
        splat(x, y, 0.5 + Math.random() * 0.5, 'rgba(40,28,16,0.5)', 7, true);
        burst(x, y, 0.1, 5, ['#6b4a2b', '#8a6a44', '#3a2a1a'], { speed: 1.8, up: 3.6, g: 8, size: 0.07, kind: 'debris', life: 0.8 });
      }
      burst(spot.x, spot.y, 0.2, 12, ['rgba(160,130,90,0.5)', 'rgba(110,90,60,0.45)'], { speed: r * 0.6, up: 0.6, g: -0.3, size: 0.16, life: 1.2, kind: 'smoke' });
      if (!k) moments.push({ kind: 'pow', x: spot.x, y: spot.y, t: 0, dur: 0.7, text: 'BEBEN!', boss: true, tilt: -0.12 });
      shake = Math.max(shake, 0.6);
      hitstop = Math.max(hitstop, 0.06);
      bigHit(spot.x, spot.y, k ? 0.6 : 1);
      play('quake');
    }));
    return true;
  };
  // ---------------------------------------------------------------------------------------------
  // People on the street walk to your stands and stages, look around, buy and leave with a bag.
  // Tap one before they buy, and you make the sale yourself.

  const walkers = [];
  let walkerId = 0;
  let walkerClock = 0;
  // Only one buyer on the street at a time, and the next one a while after. The wait is kept in the save, so a
  // reload does not bring the next buyer any sooner.
  const BUYER_GAP = [45, 75];
  const WALKER_TOPS = ['#2f6fff', '#ff4fa3', YELLOW, MINT_D, SIGNAL, '#8a5cff', '#2b2b2e', PAPER, '#6a2c3a', '#d9b26a'];
  const walkerCap = () => Math.min(16, 4 + Math.floor(Math.log2(1 + S.fans / 15))) + vibe();
  const walkerTarget = () => {
    const spots = S.items.filter(item => working(item) && ((kindOf(item) === 'sale' && !info(item).online) || kindOf(item) === 'promo') && !hiddenAt(door(item).x, door(item).y + 0.5));
    const shows = spots.filter(item => showLive(item));
    const pool = shows.length && Math.random() < 0.75 ? shows : spots;
    return pool.length ? pool[Math.floor(Math.random() * pool.length)] : null;
  };
  const spawnWalker = () => {
    const from = lanePoint({ side: RAID_SIDES[Math.floor(Math.random() * RAID_SIDES.length)], at: Math.random() }, true);
    const target = walkerTarget();
    walkerId += 1;
    const rand = mulberry32(walkerId * 7919 + S.seed);
    const wants = Date.now() >= S.buyerAt && !walkers.some(w => w.wants && !w.bought && w.state !== 'out');
    if (wants) {
      S.buyerAt = Date.now() + (BUYER_GAP[0] + Math.random() * (BUYER_GAP[1] - BUYER_GAP[0])) * 1000;
      persist();
    }
    walkers.push({
      id: walkerId, x: from.x, y: from.y, ex: from.x, ey: from.y, r: 0.14, target: target ? target.id : null,
      state: 'in', wait: 0, face: 1, walk: 0, bought: false, wants, price: wants ? streetPrice() : 0, pop: 0, speed: 0.75 + rand() * 0.35,
      look: { ...makeHustler(Math.floor(rand() * 2 ** 31)), chain: rand() < 0.2, hold: rand() < 0.35 ? 'phone' : 'none' }
    });
  };
  // A spot in front of the door, spread out so the crowd does not stand in one place; where a neighbour
  // stands right in front of it, the spot moves further out until there is room.
  const insideBuilding = (x, y) => world().some(item => {
    if (kindOf(item) === 'crew' || !isSolid(item)) return false;
    const f = hitBox(item);
    return x > f.x - 0.2 && x < f.x + f.w + 0.2 && y > f.y - 0.2 && y < f.y + f.d + 0.2;
  });
  const walkerGoal = walker => {
    const item = walker.target && itemById(walker.target);
    if (!item || !working(item)) return null;
    const at = door(item);
    const spread = info(item).w * 0.4;
    const x = at.x + (((walker.id * 37) % 11) / 10 - 0.5) * 2 * spread;
    const y = at.y + 0.3 + ((walker.id * 13) % 4) * 0.2;
    for (let k = 0; k < 6; k += 1) {
      const spot = { x: x + (k % 2 ? -1 : 1) * Math.ceil(k / 2) * 0.35, y: y + Math.ceil(k / 2) * 0.45 };
      if (!insideBuilding(spot.x, spot.y)) return spot;
    }
    return null;
  };
  // Somebody who makes no headway for a few seconds gives up: a buyer who is nearly there stops where he is,
  // anyone else turns round, and whoever cannot find a way out either slips away.
  const STUCK = 3;
  const headway = (w, gx, gy, dt) => {
    const d = dist(w.x, w.y, gx, gy);
    if (w.bestFor !== w.state || d < w.best - 0.1) {
      w.bestFor = w.state;
      w.best = d;
      w.stuck = 0;
    } else w.stuck += dt;
    return w.stuck > STUCK ? d : -1;
  };
  const updateWalkers = dt => {
    const fighting = raid && raid.phase !== 'end';
    walkerClock -= dt;
    if (!fighting && founded() && walkerClock <= 0 && walkers.length < walkerCap()) {
      spawnWalker();
      walkerClock = 1.4 + Math.random() * 1.6;
    }
    refreshHangers();
    updatePigeons(dt, fighting);
    for (let i = walkers.length - 1; i >= 0; i -= 1) {
      const w = walkers[i];
      w.pop = Math.max(0, w.pop - dt);
      if (w.gone !== undefined) {
        w.gone += dt;
        if (w.gone >= 0.6) walkers.splice(i, 1);
        continue;
      }
      if (fighting) w.state = 'out';
      // A happy buyer stops for a moment to cheer.
      else if (w.pop > 0) continue;
      if (w.state === 'in') {
        const goal = walkerGoal(w);
        if (!goal) w.state = 'out';
        else {
          moveBody(w, goal.x, goal.y, w.speed, dt);
          const stuck = headway(w, goal.x, goal.y, dt);
          if (dist(w.x, w.y, goal.x, goal.y) < 0.15 || (stuck >= 0 && stuck < 1.6)) {
            const item = itemById(w.target);
            w.state = 'stay';
            w.wait = item && showLive(item) ? 5 + Math.random() * 5 : 1.4 + Math.random() * 2;
          } else if (stuck >= 0) w.state = 'out';
        }
      } else if (w.state === 'stay') {
        w.wait -= dt;
        const item = itemById(w.target);
        if (item) w.face = center(item).x >= w.x ? 1 : -1;
        if (w.wait <= 0) {
          if (!w.bought && item && kindOf(item) === 'sale') {
            w.bought = true;
            w.pop = 0.7;
          }
          w.state = 'out';
        }
      } else {
        moveBody(w, w.ex, w.ey, w.speed * (fighting ? 2.4 : 1), dt);
        if (dist(w.x, w.y, w.ex, w.ey) < 0.3) walkers.splice(i, 1);
        else if (headway(w, w.ex, w.ey, dt) >= 0) w.gone = 0;
      }
    }
  };

  // The neighbourhood comes alive with the Base: at every tier more people about and more pigeons on the concrete,
  // then graffiti on the ground, and a party at the top.
  const vibe = () => (founded() ? Math.min(5, tierOf(hqLevel())) : 0);
  const VIBE_TEXT = ['', 'Mehr Leute und Tauben auf dem Platz', 'Noch mehr Leute hängen bei dir ab', 'Graffiti auf dem Platz', 'Das Viertel brummt', 'Das ganze Viertel feiert mit'];
  // Groups of two or three hanging about on open ground in the yard, one more with every tier.
  let hangers = [];
  let hangFor = null;
  const openGround = (x, y) => {
    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dy = -1; dy <= 1; dy += 1) if (!owned(x + dx, y + dy) || occ.has(`${x + dx},${y + dy}`)) return false;
    }
    return true;
  };
  const refreshHangers = () => {
    const want = vibe();
    if (heist || (hangFor && hangFor.occ === occ && hangFor.land === landKeys && hangFor.want === want && hangFor.seed === S.seed)) return;
    hangFor = { occ, land: landKeys, want, seed: S.seed };
    const spots = [];
    S.land.forEach(key => {
      const [cx, cy] = key.split(',').map(Number);
      for (let x = cx * CHUNK; x < (cx + 1) * CHUNK; x += 1) {
        for (let y = cy * CHUNK; y < (cy + 1) * CHUNK; y += 1) if (openGround(x, y)) spots.push({ x, y, h: hashText(`${S.seed}:${x},${y}`) });
      }
    });
    spots.sort((a, b) => a.h - b.h);
    const picked = [];
    spots.forEach(spot => {
      if (picked.length < want && picked.every(p => dist(p.x, p.y, spot.x, spot.y) >= 3)) picked.push(spot);
    });
    hangers = picked.map(spot => {
      const rand = mulberry32(spot.h);
      const n = rand() < 0.5 ? 3 : 2;
      const turn = rand() * TAU;
      return {
        x: spot.x + 0.5,
        y: spot.y + 0.5,
        people: Array.from({ length: n }, (_, i) => {
          const a = turn + (i / n) * TAU;
          return { dx: Math.cos(a) * 0.34, dy: Math.sin(a) * 0.24, seed: Math.floor(rand() * 1e6), look: makeHustler(Math.floor(rand() * 2 ** 31)) };
        })
      };
    });
  };
  // Pigeons peck about the yard and the lots, and take off when somebody comes close or a fight starts.
  const pigeons = [];
  let pigeonId = 0;
  let pigeonClock = 0;
  const pigeonCap = () => 2 + vibe() * 2;
  const pigeonSpot = () => {
    const keys = [...S.land, ...forSale().map(lot => lot.key)];
    const [cx, cy] = keys[Math.floor(Math.random() * keys.length)].split(',').map(Number);
    const x = cx * CHUNK + 0.5 + Math.random() * (CHUNK - 1);
    const y = cy * CHUNK + 0.5 + Math.random() * (CHUNK - 1);
    return occ.has(`${Math.floor(x)},${Math.floor(y)}`) ? null : { x, y };
  };
  const nearPeople = (x, y) => (!K.inside && dist(K.x, K.y, x, y) < 1.1) || walkers.some(w => dist(w.x, w.y, x, y) < 0.9);
  const updatePigeons = (dt, fighting) => {
    if (heist) {
      pigeons.length = 0;
      return;
    }
    pigeonClock -= dt;
    if (!fighting && founded() && pigeonClock <= 0 && pigeons.filter(p => !p.up).length < pigeonCap()) {
      const at = pigeonSpot();
      if (at && !nearPeople(at.x, at.y)) {
        pigeonId += 1;
        pigeons.push({ id: pigeonId, x: at.x - 1.5, y: at.y, z: 2.5, tx: at.x, ty: at.y, face: 1, peck: 0, hop: 0, up: false, vx: 0 });
      }
      pigeonClock = 1.5 + Math.random() * 3;
    }
    for (let i = pigeons.length - 1; i >= 0; i -= 1) {
      const p = pigeons[i];
      if (p.up) {
        p.x += p.vx * dt;
        p.z += 2.6 * dt;
        if (p.z > 6) pigeons.splice(i, 1);
        continue;
      }
      if (fighting || nearPeople(p.x, p.y)) {
        p.up = true;
        p.vx = (Math.random() < 0.5 ? -1 : 1) * (1.6 + Math.random());
        p.face = Math.sign(p.vx);
        continue;
      }
      // Gliding in to land, then a hop now and then and pecking in between.
      if (p.z > 0) {
        p.z = Math.max(0, p.z - 2.2 * dt);
        p.x += (p.tx - p.x) * Math.min(1, dt * 2.2);
        p.face = p.tx >= p.x ? 1 : -1;
        continue;
      }
      p.peck = Math.max(0, p.peck - dt);
      p.hop = Math.max(0, p.hop - dt);
      if (!p.peck && !p.hop && Math.random() < dt * 0.8) {
        if (Math.random() < 0.6) p.peck = 0.5;
        else {
          p.hop = 0.25;
          p.face = Math.random() < 0.5 ? -1 : 1;
        }
      }
      if (p.hop > 0 && !occ.has(`${Math.floor(p.x + p.face * dt)},${Math.floor(p.y)}`)) p.x += p.face * dt * 0.9;
    }
  };
  // Scaffolds finish on their own; the frame loop runs production by the clock.
  const updateItems = dt => {
    S.items.forEach(item => {
      item.shake = Math.max(0, (item.shake || 0) - dt);
      item.fired = Math.max(0, (item.fired || 0) - dt);
      item.pop = Math.max(0, (item.pop || 0) - dt);
      if (item.rebuild) {
        const before = item.rebuild;
        item.rebuild -= dt;
        if (item.rebuild <= 0) {
          item.rebuild = 0;
          item.pop = 0.4;
          play('fixed');
        } else if (Math.floor(before / 0.7) !== Math.floor(item.rebuild / 0.7)) play('hammer');
      }
    });
  };

  // ---------------------------------------------------------------------------------------------
  // Drawing helpers. Everything stands on the grid as boxes: a roof seen from above, a front face.

  const rect = (x, y, w, h, color) => {
    ctx.fillStyle = color;
    ctx.fillRect(x, y, w, h);
  };
  const poly = (points, color) => {
    ctx.beginPath();
    points.forEach(([px, py], i) => (i ? ctx.lineTo(px, py) : ctx.moveTo(px, py)));
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
  };
  const circle = (x, y, r, color) => {
    ctx.beginPath();
    ctx.arc(x, y, Math.max(0.1, r), 0, TAU);
    ctx.fillStyle = color;
    ctx.fill();
  };
  const oval = (x, y, rx, ry, color, rot = 0) => {
    ctx.beginPath();
    ctx.ellipse(x, y, Math.max(0.1, rx), Math.max(0.1, ry), rot, 0, TAU);
    ctx.fillStyle = color;
    ctx.fill();
  };
  const line = (x0, y0, x1, y1, color, width) => {
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.stroke();
  };
  const block = (x, y, w, d, h, roof, front, base = 0) => {
    const a = proj(x, y, h);
    const b = proj(x + w, y + d, h);
    const g = proj(x + w, y + d, base);
    const box = { l: a.x, r: b.x, t: a.y, m: b.y, b: g.y, u: cw() };
    box.w = box.r - box.l;
    box.fh = box.b - box.m;
    box.at = (fx, fy) => ({ x: box.l + box.w * fx, y: box.m + box.fh * fy });
    box.top = (fx, fy) => ({ x: box.l + box.w * fx, y: box.t + (box.m - box.t) * fy });
    rect(box.l, box.t, box.w, box.m - box.t, roof);
    rect(box.l, box.m, box.w, box.fh, front);
    const shade = ctx.createLinearGradient(0, box.m, 0, box.b);
    shade.addColorStop(0, 'rgba(0,0,0,0)');
    shade.addColorStop(1, 'rgba(0,0,0,0.13)');
    rect(box.l, box.m, box.w, box.fh, shade);
    rect(box.l, box.m, box.w, Math.max(1, box.u * 0.012), 'rgba(255,255,255,0.28)');
    return box;
  };
  const label = (text, x, y, size, color, align = 'center', weight = 700) => {
    ctx.font = `${weight} ${Math.max(0.5, size)}px ${FONT}`;
    ctx.textAlign = align;
    ctx.textBaseline = 'middle';
    ctx.fillStyle = color;
    ctx.fillText(text, x, y);
  };
  const glowAt = (x, y, radius, color, alpha) => {
    const g = ctx.createRadialGradient(x, y, 0, x, y, radius);
    g.addColorStop(0, color.replace('ALPHA', alpha));
    g.addColorStop(1, color.replace('ALPHA', 0));
    ctx.fillStyle = g;
    ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
  };
  const starPath = (c, x, y, outer, inner, points = 5, rotation = -Math.PI / 2) => {
    for (let i = 0; i <= points * 2; i += 1) {
      const radius = i % 2 ? inner : outer;
      const angle = rotation + (i * Math.PI) / points;
      const px = x + Math.cos(angle) * radius;
      const py = y + Math.sin(angle) * radius;
      if (i === 0) c.moveTo(px, py);
      else c.lineTo(px, py);
    }
    c.closePath();
  };
  // Art that does not move, painted once into its own picture and from then on only laid down, which costs a
  // fraction of painting it; the pictures used least recently go first. Pictures come in sizes a twelfth of an
  // octave apart, so a zoom lays the next larger one down instead of painting a new one every frame.
  const PICTURE_MAX = 96;
  const PICTURE_STEP = 12;
  const artPictures = new Map();
  const cachedArt = (key, w, h, paint) => {
    const grade = Math.ceil(Math.log2(Math.max(1, w * dpr)) * PICTURE_STEP);
    const full = `${key}:${grade}:${Math.round((h / w) * 1000)}`;
    let pic = artPictures.get(full);
    if (pic) artPictures.delete(full);
    else {
      if (artPictures.size >= PICTURE_MAX) artPictures.delete(artPictures.keys().next().value);
      const k = 2 ** (grade / PICTURE_STEP) / w;
      pic = document.createElement('canvas');
      pic.width = Math.max(1, Math.ceil(w * k));
      pic.height = Math.max(1, Math.ceil(h * k));
      const main = ctx;
      ctx = pic.getContext('2d');
      ctx.setTransform(k, 0, 0, k, 0, 0);
      try {
        paint();
      } finally {
        ctx = main;
      }
    }
    artPictures.set(full, pic);
    return pic;
  };
  // A second canvas the size of the one being painted, for a layer that is finished on its own and then laid on.
  const layerLike = () => {
    const c = document.createElement('canvas');
    c.width = ctx.canvas.width;
    c.height = ctx.canvas.height;
    const lc = c.getContext('2d');
    lc.setTransform(ctx.getTransform());
    lc.font = ctx.font;
    lc.textAlign = ctx.textAlign;
    lc.textBaseline = ctx.textBaseline;
    lc.lineJoin = ctx.lineJoin;
    lc.lineCap = ctx.lineCap;
    return { c, lc };
  };
  // The tilted ring from the MIXEDOWN wordmark.
  const logoRing = (x, y, rx, ry, width, color) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(-0.42);
    ctx.beginPath();
    ctx.ellipse(0, 0, rx, ry, 0, 0, TAU);
    ctx.lineWidth = width;
    ctx.strokeStyle = color;
    ctx.stroke();
    ctx.restore();
  };
  // The MIXEDOWN wordmark, traced from the shop's vector logo (1124 × 81 units, the O ring a little more).
  const LOGO = new Path2D('M2.9 102.8h44.2v-42.7h1.6l17.1 42.7h34.8l16.5-42.7h1.8v42.7h43.8v-80.8h-66.7l-10.5 31.3h-2.7l-11.3-31.3h-68.6v80.8zM176.9 22h46.2v80.8h-46.2zM226.9 22l36.3 39v1.7l-36.3 40.1h56.8l12.6-17.4h2l13.1 17.4h56.3l-36.4-40.1v-1.6l36.4-39.1h-58.1l-11.2 15.6h-2.1l-11.1-15.6h-58.3zM371.9 22v80.8h115.7v-25.8h-69.8v-6h67.1v-19h-67v-5.1h69.7v-24.9h-115.7zM795.4 22l27.3 80.8h54.3l11.3-38.5h2.9l11.9 38.5h53.8l27.3-80.8h-49.9l-10.4 37.7h-2.2l-11.3-37.7h-41.1l-11.1 37.6h-2.4l-10.5-37.6h-49.9zM987.1 102.8l0.6-80.8h57.3l31.9 27.8h2.3v-27.8h47.3l-0.6 80.8h-56.7l-31.3-26h-2.6l-0.6 26h-47.6zM734.6 23.8c-45.6-13.3-88.1-5.5-94.7 17.5-6.7 22.9 24.9 52.3 70.6 65.6 45.6 13.3 88.1 5.5 94.7-17.5 6.7-23-24.9-52.3-70.6-65.6zm22.9 45.5c-3.6 5.9-20.7 5.6-38.2-0.7-17.5-6.3-28.8-16.1-25.3-22 3.6-5.9 20.7-5.5 38.2 0.7 17.5 6.3 28.8 16.2 25.3 22zM588.1 22h-88.5v80.3c0 0 39.8 0 88.5 0 45.9 0 43.9-36.4 43.9-40.2 0-3.7 2-40.1-43.9-40.1zm-42.5 30.7h36.1v19.1l-36.1-0.1z');
  const LOGO_RATIO = 0.072;
  const drawLogo = (x, y, width, color, outline = 0) => {
    const k = width / 1124;
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(k, k);
    ctx.translate(-564.7, -62.4);
    if (outline) {
      ctx.lineJoin = 'round';
      ctx.lineWidth = (outline * 2) / k;
      ctx.strokeStyle = INK;
      ctx.stroke(LOGO);
    }
    ctx.fillStyle = color;
    ctx.fill(LOGO, 'evenodd');
    ctx.restore();
  };
  // A glow is a blur, and a phone pays for it on every frame it is painted: what glows is painted once with its
  // glow into a picture, for each look and size, and from then on only laid down. The picture keeps the part of
  // a pixel it was first painted on, so it lands exactly where the live paint would. `x, y, w, h` frame what
  // `draw` paints, without the glow.
  const GLOW_MAX = 120;
  const glowPictures = new Map();
  const glowing = (key, glow, blur, x, y, w, h, draw) => {
    const m = ctx.getTransform();
    if (m.b || m.c || m.a !== m.d || w <= 0 || h <= 0) {
      ctx.save();
      ctx.shadowColor = glow;
      ctx.shadowBlur = blur;
      draw();
      ctx.restore();
      return;
    }
    const k = m.a;
    const pad = (blur * 1.5) / k + 1;
    const left = (x - pad) * k + m.e;
    const top = (y - pad) * k + m.f;
    const full = `${key}:${glow}:${Math.round(blur * 10)}:${Math.round(w * k)}x${Math.round(h * k)}:${Math.round(k * 100)}`;
    let pic = glowPictures.get(full);
    if (pic) glowPictures.delete(full);
    else {
      if (glowPictures.size >= GLOW_MAX) glowPictures.delete(glowPictures.keys().next().value);
      const c = document.createElement('canvas');
      c.width = Math.ceil((w + pad * 2) * k) + 1;
      c.height = Math.ceil((h + pad * 2) * k) + 1;
      pic = { c, fx: left - Math.floor(left), fy: top - Math.floor(top) };
      const main = ctx;
      ctx = c.getContext('2d');
      ctx.setTransform(k, 0, 0, k, pic.fx - (x - pad) * k, pic.fy - (y - pad) * k);
      ctx.shadowColor = glow;
      ctx.shadowBlur = blur;
      try {
        draw();
      } finally {
        ctx = main;
      }
    }
    glowPictures.set(full, pic);
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.drawImage(pic.c, left - pic.fx, top - pic.fy);
    ctx.restore();
  };
  // A label, a wordmark or a strip of neon, glowing when `glow` is set; the text settings stay as they were.
  const glowLabel = (glow, blur, text, x, y, size, color, align = 'center', weight = 700) => {
    ctx.save();
    if (glow) {
      ctx.font = `${weight} ${Math.max(0.5, size)}px ${FONT}`;
      const w = ctx.measureText(text).width;
      const l = align === 'center' ? x - w / 2 : align === 'right' ? x - w : x;
      glowing(`label:${text}:${size.toFixed(2)}:${color}:${align}:${weight}`, glow, blur, l, y - size * 0.65, w, size * 1.3, () => label(text, x, y, size, color, align, weight));
    } else label(text, x, y, size, color, align, weight);
    ctx.restore();
  };
  const glowLogo = (glow, blur, x, y, width, color) => {
    if (!glow) return drawLogo(x, y, width, color);
    return glowing(`logo:${width.toFixed(2)}:${color}`, glow, blur, x - width * 0.51, y - width * 0.05, width * 1.02, width * 0.1, () => drawLogo(x, y, width, color));
  };
  const glowRect = (glow, blur, x, y, w, h, color) => glowing(`rect:${color}`, glow, blur, x, y, w, h, () => rect(x, y, w, h, color));
  // Cash: a little stack of notes with a diamond on the top one, no currency sign. `s` is half its width.
  const drawCash = (c, x, y, s, outline = true) => {
    const w = s * 2;
    const h = s * 1.2;
    const note = (dx, dy, fill, rot) => {
      c.save();
      c.translate(x + dx, y + dy);
      c.rotate(rot);
      if (outline) {
        c.fillStyle = INK;
        c.fillRect(-w / 2 - Math.max(1, s * 0.12), -h / 2 - Math.max(1, s * 0.12), w + Math.max(2, s * 0.24), h + Math.max(2, s * 0.24));
      }
      c.fillStyle = fill;
      c.fillRect(-w / 2, -h / 2, w, h);
      c.fillStyle = 'rgba(255,255,255,0.35)';
      c.fillRect(-w / 2 + s * 0.14, -h / 2 + s * 0.14, w - s * 0.28, Math.max(1, s * 0.08));
      c.restore();
    };
    note(-s * 0.12, s * 0.16, '#5fae5b', -0.12);
    note(0, 0, '#9fdf9b', 0.06);
    c.save();
    c.translate(x, y);
    c.rotate(0.06);
    c.beginPath();
    c.arc(0, 0, s * 0.42, 0, TAU);
    c.fillStyle = '#e6f9e4';
    c.fill();
    c.beginPath();
    c.moveTo(0, -s * 0.2);
    c.lineTo(s * 0.2, 0);
    c.lineTo(0, s * 0.2);
    c.lineTo(-s * 0.2, 0);
    c.closePath();
    c.strokeStyle = '#2e6b2b';
    c.lineWidth = Math.max(1, s * 0.09);
    c.stroke();
    c.restore();
  };
  // Fans: a head and shoulders in signal pink. `s` is half its width.
  const drawFan = (c, x, y, s, outline = true) => {
    const body = () => {
      c.beginPath();
      c.arc(x, y - s * 0.38, s * 0.42, 0, TAU);
      c.moveTo(x + s * 0.95, y + s * 0.9);
      c.quadraticCurveTo(x + s * 0.95, y + s * 0.05, x, y + s * 0.05);
      c.quadraticCurveTo(x - s * 0.95, y + s * 0.05, x - s * 0.95, y + s * 0.9);
      c.closePath();
    };
    if (outline) {
      body();
      c.lineWidth = Math.max(2, s * 0.3);
      c.lineJoin = 'round';
      c.strokeStyle = INK;
      c.stroke();
    }
    body();
    c.fillStyle = '#5aa9ff';
    c.fill();
    c.beginPath();
    c.arc(x - s * 0.12, y - s * 0.5, s * 0.12, 0, TAU);
    c.fillStyle = 'rgba(255,255,255,0.7)';
    c.fill();
  };

  // Materials by look tier: wood, concrete, steel, black, gold, neon.
  const MATS = [
    { wall: '#dcc8a4', roof: '#c5a679', trim: '#8a6a45', dark: '#6b4f33', line: 'rgba(107,79,51,0.28)' },
    { wall: '#efeee9', roof: '#dddcd6', trim: '#a9a8a2', dark: '#7d7c77', line: 'rgba(0,0,0,0.06)' },
    { wall: '#cdd2d7', roof: '#b4bac0', trim: '#6f767d', dark: '#4f565c', line: 'rgba(0,0,0,0.1)' },
    { wall: '#2a2a2e', roof: '#1d1d20', trim: MINT, dark: '#121214', line: 'rgba(255,255,255,0.05)' },
    { wall: '#2a2a2e', roof: '#1d1d20', trim: GOLD, dark: '#121214', line: 'rgba(255,255,255,0.05)' },
    { wall: '#1b1b1f', roof: '#151518', trim: MINT, dark: '#0d0d0f', line: 'rgba(186,236,183,0.1)', neon: true }
  ];
  const matOf = level => MATS[Math.min(5, tierOf(level))];
  const stars = level => Math.max(0, tierOf(level) - 5);
  // Planks, panels or ribs on a front face, so the material reads at a glance.
  const surface = (g, level) => {
    const tier = Math.min(5, tierOf(level));
    const mat = MATS[tier];
    if (tier === 0) {
      for (let x = g.l + g.w / 7; x < g.r - 2; x += g.w / 7) rect(x, g.m, 1, g.fh, mat.line);
    } else if (tier === 1) {
      for (let y = g.m + g.fh / 3; y < g.b - 2; y += g.fh / 3) rect(g.l, y, g.w, 1, mat.line);
    } else if (tier === 2) {
      for (let x = g.l + 3; x < g.r - 1; x += Math.max(3, g.u * 0.06)) rect(x, g.m, 1, g.fh, mat.line);
    } else {
      rect(g.l, g.b - Math.max(2, g.fh * 0.06), g.w, Math.max(2, g.fh * 0.06), mat.trim);
      if (mat.neon) glowRect(MINT, g.u * 0.2, g.l, g.b - Math.max(2, g.fh * 0.06), g.w, Math.max(2, g.fh * 0.06), MINT);
    }
  };
  const roofEdge = (g, level) => {
    const mat = matOf(level);
    rect(g.l, g.m - Math.max(1.5, g.u * 0.03), g.w, Math.max(1.5, g.u * 0.03), mat.trim);
  };
  const starRow = (x, y, size, count) => {
    for (let i = 0; i < count; i += 1) {
      ctx.beginPath();
      starPath(ctx, x + (i - (count - 1) / 2) * size * 2.3, y, size, size * 0.45);
      ctx.fillStyle = YELLOW;
      ctx.fill();
    }
  };
  const windowRect = (x, y, w, h, lit = false) => {
    rect(x, y, w, h, lit ? '#ffe9a8' : '#2c3338');
    if (!lit) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(x, y, w, h);
      ctx.clip();
      line(x + w * 0.2, y + h + 2, x + w * 0.9, y - 2, 'rgba(255,255,255,0.12)', Math.max(1, w * 0.18));
      ctx.restore();
    }
  };

  const smoke = (x, y, u, seed, color = '255,255,255') => {
    for (let i = 0; i < 3; i += 1) {
      const p = (clock * 0.45 + i / 3 + seed * 0.13) % 1;
      circle(x + Math.sin(p * 6 + i + seed) * u * 0.06, y - p * u * 0.7, u * (0.05 + p * 0.08), `rgba(${color},${0.65 * (1 - p)})`);
    }
  };

  // ---------------------------------------------------------------------------------------------
  // Buildings. Each one changes its build at every look tier and keeps small details per level.

  // Brick courses on a face: staggered joints, so a wall reads as brick at a glance.
  const bricks = (l, t, w, h, joint) => {
    const rowH = Math.max(3, cw() * 0.07);
    ctx.drawImage(cachedArt(`bricks:${joint}:${(rowH / w).toFixed(3)}`, w, h, () => {
      const brickW = rowH * 2.6;
      ctx.fillStyle = joint;
      for (let y = rowH, row = 0; y < h; y += rowH, row += 1) {
        ctx.fillRect(0, y, w, 1);
        for (let x = row % 2 ? brickW / 2 : 0; x < w; x += brickW) ctx.fillRect(x, y - rowH + 1, 1, rowH - 1);
      }
    }), l, t, w, h);
  };
  // ---- Street art -------------------------------------------------------------------------------
  // Letters the way writers do them: each one turned and lifted a little on its own, overlapping the next.
  const letterSet = (word, size, rand, lean = 0) => {
    ctx.font = `900 ${size}px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    let x = 0;
    return [...word].map(ch => {
      const w = ctx.measureText(ch).width * 0.82;
      const one = { ch, x: x + w / 2, w, dy: (rand() - 0.5) * size * 0.14, rot: (rand() - 0.5) * 0.24 + lean, k: 1 + (rand() - 0.5) * 0.16 };
      x += w;
      return one;
    });
  };
  const letterWidth = letters => letters.reduce((sum, one) => sum + one.w, 0);
  // Runs `paint` for every letter with the canvas moved onto it: the letter's foot is at (0, 0).
  const eachLetter = (letters, size, paint) => letters.forEach(one => {
    ctx.save();
    ctx.translate(one.x, one.dy - size * 0.34);
    ctx.rotate(one.rot);
    ctx.scale(one.k, one.k);
    ctx.translate(0, size * 0.34);
    paint(one.ch);
    ctx.restore();
  });
  // A four-point sparkle.
  const sparkle = (x, y, r, color) => {
    ctx.beginPath();
    starPath(ctx, x, y, r, r * 0.22, 4, 0);
    ctx.fillStyle = color;
    ctx.fill();
  };
  // A crown of three points over a letter: ink outline, a colour inside.
  const crownMark = (x, y, w, color) => {
    const pts = [[x - w / 2, y], [x - w / 2, y - w * 0.55], [x - w / 4, y - w * 0.25], [x, y - w * 0.7], [x + w / 4, y - w * 0.25], [x + w / 2, y - w * 0.55], [x + w / 2, y]];
    ctx.lineJoin = 'round';
    ctx.beginPath();
    pts.forEach(([px, py], i) => (i ? ctx.lineTo(px, py) : ctx.moveTo(px, py)));
    ctx.closePath();
    ctx.lineWidth = Math.max(1.5, w * 0.16);
    ctx.strokeStyle = INK;
    ctx.stroke();
    ctx.fillStyle = color;
    ctx.fill();
  };
  // A tag: a name written fast with a fat marker, leaning forward, the line under it swung out into an arrow and a
  // crown on the first letter.
  const TAG_WORDS = ['MXD', 'KIEZ', 'DROP', 'HUSL', 'MIXE', 'BLOK', 'TEE'];
  const paintTag = (w, h, colours, seed) => {
    const rand = mulberry32(seed);
    const word = TAG_WORDS[Math.floor(rand() * TAG_WORDS.length)];
    let size = h * 0.62;
    let letters = letterSet(word, size, rand, 0.05);
    const fit = Math.min(1, (w * 0.78) / letterWidth(letters));
    size *= fit;
    letters = letterSet(word, size, mulberry32(seed), 0.05);
    const total = letterWidth(letters);
    ctx.save();
    ctx.translate(w * 0.08, h * 0.66);
    // Leaning forward like a quick hand.
    ctx.transform(1, 0, -0.28, 1, 0, 0);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    const swoosh = () => {
      ctx.beginPath();
      ctx.moveTo(-size * 0.05, size * 0.14);
      ctx.quadraticCurveTo(total * 0.5, size * 0.34, total + size * 0.22, -size * 0.02);
    };
    swoosh();
    ctx.lineWidth = size * 0.17;
    ctx.strokeStyle = INK;
    ctx.stroke();
    eachLetter(letters, size, ch => {
      ctx.lineWidth = size * 0.17;
      ctx.strokeStyle = INK;
      ctx.strokeText(ch, 0, 0);
    });
    swoosh();
    ctx.lineWidth = size * 0.07;
    ctx.strokeStyle = colours[0];
    ctx.stroke();
    const tip = { x: total + size * 0.22, y: -size * 0.02 };
    poly([[tip.x + size * 0.14, tip.y - size * 0.1], [tip.x - size * 0.12, tip.y - size * 0.12], [tip.x + size * 0.02, tip.y + size * 0.12]], INK);
    poly([[tip.x + size * 0.08, tip.y - size * 0.07], [tip.x - size * 0.05, tip.y - size * 0.08], [tip.x + size * 0.02, tip.y + size * 0.05]], colours[1]);
    eachLetter(letters, size, ch => {
      ctx.fillStyle = colours[0];
      ctx.fillText(ch, 0, 0);
    });
    crownMark(letters[0].x - size * 0.05, -size * 0.78, size * 0.34, colours[1]);
    ctx.restore();
  };
  // A tag `w` wide with its middle at height `y`, from `x` on.
  const graffiti = (x, y, w, colours, seed) => {
    const h = w * 0.56;
    ctx.drawImage(cachedArt(`tag:${seed}:${colours.join()}`, w, h, () => paintTag(w, h, colours, seed)), x, y - h / 2, w, h);
  };
  // A piece: the word in fat letters with a 3D block behind them, a black outline and a second one round it,
  // the fill fading from one colour to the next with a shine on top, paint dripping off the bottom, a haze of
  // spray behind, and sparkles, a crown and arrows as the wall gets better. Painted into a box `w` × `h`.
  const paintPiece = (w, h, word, look, seed) => {
    const rand = mulberry32(seed);
    (look.mist || []).forEach((color, i) => {
      const mx = w * (0.22 + (i / Math.max(1, look.mist.length - 1)) * 0.56) + (rand() - 0.5) * w * 0.1;
      const my = h * (0.45 + (rand() - 0.5) * 0.2);
      const r = h * (0.55 + rand() * 0.2);
      const g = ctx.createRadialGradient(mx, my, 0, mx, my, r);
      g.addColorStop(0, color);
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.save();
      ctx.globalAlpha = 0.5;
      ctx.fillStyle = g;
      ctx.fillRect(mx - r, my - r, r * 2, r * 2);
      ctx.restore();
    });
    let size = h * 0.62;
    let letters = letterSet(word, size, rand);
    size *= Math.min(1, (w * 0.82) / letterWidth(letters));
    const shape = mulberry32(seed * 3 + 1);
    letters = letterSet(word, size, shape);
    const total = letterWidth(letters);
    const ox = (w - total) / 2;
    const base = h * 0.5 + size * 0.36;
    ctx.save();
    ctx.translate(ox, base);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    const outer = size * 0.24;
    // The block behind, stepped down and to the right.
    const depth = Math.max(2, Math.round(size * 0.12));
    for (let d = depth; d >= 1; d -= 1) {
      ctx.save();
      ctx.translate(d * 0.9, d);
      eachLetter(letters, size, ch => {
        ctx.lineWidth = outer;
        ctx.strokeStyle = d === depth ? INK : look.block;
        ctx.strokeText(ch, 0, 0);
      });
      ctx.restore();
    }
    eachLetter(letters, size, ch => {
      ctx.lineWidth = outer;
      ctx.strokeStyle = INK;
      ctx.strokeText(ch, 0, 0);
    });
    eachLetter(letters, size, ch => {
      ctx.lineWidth = size * 0.13;
      ctx.strokeStyle = look.line;
      ctx.strokeText(ch, 0, 0);
    });
    eachLetter(letters, size, ch => {
      ctx.lineWidth = size * 0.045;
      ctx.strokeStyle = INK;
      ctx.strokeText(ch, 0, 0);
    });
    // The fill on a layer of its own, so the fade and the shine stay inside the letters.
    const { c: layer, lc } = layerLike();
    const main = ctx;
    ctx = lc;
    try {
      const fade = ctx.createLinearGradient(0, -size * 0.75, 0, 0);
      look.fill.forEach((color, i) => fade.addColorStop(i / (look.fill.length - 1), color));
      if (look.glow) {
        ctx.shadowColor = look.glow;
        ctx.shadowBlur = size * 0.35;
      }
      eachLetter(letters, size, ch => {
        ctx.fillStyle = fade;
        ctx.fillText(ch, 0, 0);
      });
      ctx.shadowBlur = 0;
      ctx.globalCompositeOperation = 'source-atop';
      // A band of light across the top half, and a few bubbles of shine.
      ctx.save();
      ctx.globalAlpha = 0.35;
      poly([[-size, -size * 0.62], [total + size, -size * 0.8], [total + size, -size * 0.55], [-size, -size * 0.38]], PAPER);
      ctx.restore();
      letters.forEach(one => {
        circle(one.x - one.w * 0.18, one.dy - size * 0.56, size * 0.05, 'rgba(255,255,255,0.85)');
        circle(one.x - one.w * 0.05, one.dy - size * 0.6, size * 0.025, 'rgba(255,255,255,0.85)');
      });
      // A few cutting lines through the letters, the way a piece is split into its parts.
      ctx.strokeStyle = look.cut || 'rgba(0,0,0,0.18)';
      ctx.lineWidth = Math.max(1, size * 0.025);
      ctx.beginPath();
      ctx.moveTo(-size, -size * 0.2);
      ctx.lineTo(total + size, -size * 0.32);
      ctx.stroke();
    } finally {
      ctx = main;
    }
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.drawImage(layer, 0, 0);
    ctx.restore();
    // Paint running off the bottom of some letters.
    letters.forEach(one => {
      if (rand() < 0.45) return;
      const x = one.x + (rand() - 0.5) * one.w * 0.5;
      const y = one.dy + size * 0.02;
      const len = size * (0.14 + rand() * 0.32);
      const wd = Math.max(1.5, size * 0.055);
      rect(x - wd / 2 - 1, y, wd + 2, len, INK);
      circle(x, y + len, wd * 0.8 + 1, INK);
      rect(x - wd / 2, y - 1, wd, len, look.fill[look.fill.length - 1]);
      circle(x, y + len, wd * 0.8, look.fill[look.fill.length - 1]);
    });
    if (look.crown) crownMark(letters[0].x, letters[0].dy - size * 0.88, size * 0.42, look.crown);
    if (look.arrows) {
      // An arrow swinging out of the last letter and back over the top.
      const end = letters[letters.length - 1];
      ctx.beginPath();
      ctx.moveTo(end.x + end.w * 0.3, end.dy - size * 0.3);
      ctx.quadraticCurveTo(total + size * 0.45, -size * 0.9, total - size * 0.1, -size * 1.0);
      ctx.lineWidth = size * 0.14;
      ctx.strokeStyle = INK;
      ctx.stroke();
      ctx.lineWidth = size * 0.07;
      ctx.strokeStyle = look.arrows;
      ctx.stroke();
      const tx = total - size * 0.1;
      const ty = -size * 1.0;
      poly([[tx - size * 0.16, ty], [tx + size * 0.04, ty - size * 0.13], [tx + size * 0.04, ty + size * 0.13]], INK);
      poly([[tx - size * 0.09, ty], [tx + size * 0.01, ty - size * 0.07], [tx + size * 0.01, ty + size * 0.07]], look.arrows);
    }
    for (let k = 0; k < (look.sparkles || 0); k += 1) {
      const one = letters[Math.floor(rand() * letters.length)];
      sparkle(one.x + (rand() - 0.5) * one.w, one.dy - size * (0.3 + rand() * 0.6), size * (0.1 + rand() * 0.08), PAPER);
    }
    ctx.restore();
  };

  // The Base: a tagged garage, then a corner shop, a brick walk-up with fire escape and water tank,
  // and at the top a dark tower with the MIXEDOWN wordmark standing on the roof.
  const hqHeight = level => [0.95, 1.15, 1.9, 2.5, 2.8, 3.1][Math.min(5, tierOf(level))] + Math.min(0.4, (level - 1) * 0.01);
  const drawHq = item => {
    const u = cw();
    const L = item.level;
    const tier = Math.min(5, tierOf(L));
    const brand = hustlerLook.brand;
    const h = hqHeight(L);
    const trim = tier >= 4 ? GOLD : tier >= 3 ? MINT : '#8d9399';
    const ROOFS = ['#b5b4ae', '#6f4a3a', '#5a3d32', '#2a2424', '#2a2424', '#1d1a1a'];
    const WALLS = ['#c9c8c3', '#b8573a', '#a4472f', '#3a2b29', '#2f2524', '#231d1d'];
    const tall = tier >= 2;
    const g = block(item.x + 0.3, item.y + (tall ? 1.25 : 1.55), 2.4, tall ? 1.45 : 1.15, h, ROOFS[tier], WALLS[tier]);
    if (tier) bricks(g.l, g.m, g.w, g.fh, tier >= 3 ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.16)');
    else for (let y = g.m + g.fh / 3; y < g.b - 2; y += g.fh / 3) rect(g.l, y, g.w, 1, 'rgba(0,0,0,0.08)');
    // Roof: tar inside a parapet, coping along the front, a vent.
    rect(g.l + 3, g.t + 3, g.w - 6, g.m - g.t - 5, 'rgba(0,0,0,0.12)');
    rect(g.l, g.m - Math.max(1.5, u * 0.03), g.w, Math.max(1.5, u * 0.03), trim);
    block(item.x + 2.2, item.y + (tall ? 1.4 : 1.7), 0.28, 0.22, h + 0.16, '#a9aeb3', '#8d9399', h);
    const dx = g.l + g.w / 2;
    const inside = K.inside === item.id;
    const glow = `rgba(255,217,138,${0.8 + Math.sin(clock * 5) * 0.15})`;
    const shopH = Math.min(g.fh, u * 0.95);
    const sTop = g.b - shopH;
    const awning = sTop + shopH * 0.1;
    if (tier === 0) {
      // A roll door in the middle, tagged; a bare bulb, a crate and a boombox outside.
      const dl = dx - g.w * 0.24;
      const dr = dx + g.w * 0.24;
      const dt = g.m + g.fh * 0.2;
      rect(dl - 2, dt - 2, dr - dl + 4, g.b - dt + 2, '#8d8c87');
      rect(dl, dt, dr - dl, g.b - dt, '#a9a8a2');
      for (let y = dt + 3; y < g.b; y += Math.max(3, u * 0.05)) rect(dl, y, dr - dl, 1, 'rgba(0,0,0,0.16)');
      if (inside) rect(dl, g.b - (g.b - dt) * 0.22, dr - dl, (g.b - dt) * 0.22, glow);
      graffiti(dl + (dr - dl) * 0.12, dt + (g.b - dt) * 0.45, (dr - dl) * 0.76, [brand, YELLOW], item.id * 5);
      // The wordmark on a plate screwed over the door.
      const pw = g.w * 0.6;
      const py = (g.m + dt) / 2;
      const ph = Math.min(dt - g.m - 4, pw * LOGO_RATIO * 2);
      rect(dx - pw / 2 - 1.5, py - ph / 2 - 1.5, pw + 3, ph + 3, INK);
      rect(dx - pw / 2, py - ph / 2, pw, ph, PAPER);
      drawLogo(dx, py, Math.min(pw * 0.86, (ph * 0.62) / LOGO_RATIO), INK);
      line(g.l + g.w * 0.12, g.m + 2, g.l + g.w * 0.12, g.m + u * 0.08, INK, 1);
      circle(g.l + g.w * 0.12, g.m + u * 0.1, u * 0.03, '#ffe2a0');
      const crate = block(item.x + 0.4, item.y + 2.78, 0.3, 0.18, 0.22, '#c5a679', '#b08d5e');
      line(crate.l, crate.m, crate.r, crate.b, 'rgba(0,0,0,0.2)', 1);
      const box = block(item.x + 0.44, item.y + 2.74, 0.24, 0.1, 0.36, '#2b2b2e', '#1d1d20', 0.22);
      circle(box.l + box.w * 0.27, (box.m + box.b) / 2, box.fh * 0.32, '#4a4f55');
      circle(box.r - box.w * 0.27, (box.m + box.b) / 2, box.fh * 0.32, '#4a4f55');
    } else {
      // Street level: a dark shop front, windows full of shirts, a striped awning, a neon sign.
      rect(g.l + g.w * 0.04, sTop + shopH * 0.2, g.w * 0.92, shopH * 0.8, '#1d1f22');
      [0.08, 0.62].forEach((fx, i) => {
        const wl = g.l + g.w * fx;
        const ww = g.w * 0.3;
        const wt = sTop + shopH * 0.3;
        rect(wl, wt, ww, g.b - wt - shopH * 0.08, inside ? '#ffe9b8' : '#f0e6cf');
        for (let k = 0; k < 3; k += 1) shirt(wl + ww * (0.2 + k * 0.3), wt + shopH * 0.12, ww * 0.24, [brand, INK, PAPER, MINT][(k + i + L) % 4]);
        rect(wl, g.b - shopH * 0.08 - 2, ww, 2, '#8d9399');
      });
      const dw = g.w * 0.16;
      const dt = sTop + shopH * 0.28;
      rect(dx - dw / 2, dt, dw, g.b - dt, inside ? glow : '#2c3338');
      rect(dx - 0.5, dt, 1, g.b - dt, 'rgba(255,255,255,0.35)');
      // Awning: stripes in the brand colour.
      const at = awning;
      const ah = shopH * 0.16;
      for (let i = 0; i < 12; i += 1) {
        const x0 = g.l + (g.w / 12) * i;
        poly([[x0, at], [x0 + g.w / 12, at], [x0 + g.w / 12 + 1, at + ah], [x0 - 1, at + ah]], i % 2 ? PAPER : brand);
      }
      for (let i = 0; i < 12; i += 1) circle(g.l + (g.w / 12) * (i + 0.5), at + ah, g.w / 24, i % 2 ? PAPER : brand);
      rect(g.l, at, g.w, 1, 'rgba(0,0,0,0.25)');
    }
    const logoTone = tier >= 4 ? GOLD : tier >= 3 ? MINT : PAPER;
    // From tier 3 the wordmark also stands on the roof, on a steel frame; neon at tier 5.
    let markY = g.t - u * 0.35;
    if (tier >= 3) {
      const base = g.top(0.5, 0.3);
      const lw = g.w * 0.94;
      const lh = lw * LOGO_RATIO;
      const ly = base.y - u * 0.5;
      const frame = tier >= 4 ? GOLD : '#2b2b2e';
      const posts = [-0.4, -0.13, 0.13, 0.4].map(fx => base.x + lw * fx);
      posts.forEach(px => rect(px - 1, ly + lh * 0.5, 2, base.y - ly - lh * 0.5, frame));
      posts.slice(1).forEach((px, i) => line(posts[i], base.y - 1, px, ly + lh * 0.7, frame, 1));
      rect(posts[0] - 2, ly + lh * 0.7, posts[3] - posts[0] + 4, Math.max(1.5, u * 0.03), frame);
      if (tier >= 5) glowing(`hqlogo:${lw.toFixed(2)}`, MINT, u * 0.3, base.x - lw * 0.51 - u * 0.03, ly - lw * 0.05 - u * 0.03, lw * 1.02 + u * 0.06, lw * 0.1 + u * 0.06, () => drawLogo(base.x, ly, lw, MINT, Math.max(1, u * 0.025)));
      else drawLogo(base.x, ly, lw, logoTone === MINT ? PAPER : logoTone, Math.max(1, u * 0.025));
      posts.forEach(px => circle(px, ly + lh * 0.7 + u * 0.05, Math.max(1, u * 0.025), '#ffe9a8'));
      markY = ly - lh - u * 0.3;
      if (tier >= 5) {
        starRow(base.x, ly - lh - u * 0.1, u * 0.06, stars(L));
        markY -= u * 0.15;
      }
    }
    // Floors above the shop: windows, and a fire escape zig-zagging down the right side.
    if (tall) {
      const floorH = u * 0.52;
      const floors = Math.max(1, Math.floor((sTop - g.m - u * 0.12) / floorH));
      const rand = mulberry32(item.id * 31 + L);
      for (let f = 0; f < floors; f += 1) {
        const fy = sTop - (f + 1) * floorH;
        for (let c = 0; c < 4; c += 1) {
          const wx = g.l + g.w * (0.07 + c * 0.15);
          const lit = tier >= 3 ? rand() < 0.5 : rand() < 0.15;
          windowRect(wx, fy + floorH * 0.18, g.w * 0.1, floorH * 0.58, lit);
          rect(wx - 1, fy + floorH * 0.18 - 2, g.w * 0.1 + 2, 2, tier >= 3 ? trim : '#d9d4c7');
          rect(wx - 1, fy + floorH * 0.76, g.w * 0.1 + 2, 2, tier >= 3 ? trim : '#d9d4c7');
        }
        const el = g.l + g.w * 0.67;
        const er = g.l + g.w * 0.95;
        windowRect(el + (er - el) * 0.3, fy + floorH * 0.12, (er - el) * 0.4, floorH * 0.66, tier >= 3 && rand() < 0.6);
        rect(el, fy + floorH * 0.8, er - el, Math.max(1.5, u * 0.03), '#2b2b2e');
        for (let k = 0; k <= 6; k += 1) rect(el + ((er - el) / 6) * k - 0.5, fy + floorH * 0.62, 1, floorH * 0.18, '#2b2b2e');
        rect(el, fy + floorH * 0.62, er - el, 1, '#2b2b2e');
        if (f) line(f % 2 ? el + 2 : er - 2, fy + floorH * 0.82, f % 2 ? er - 2 : el + 2, fy + floorH * 1.62, '#2b2b2e', Math.max(1, u * 0.02));
      }
      // Water tank on legs.
      const tank = g.top(0.13, 0.92);
      const tw = u * 0.36;
      [-1, 1].forEach(side => rect(tank.x + side * tw * 0.35 - 1, tank.y - u * 0.25, 2, u * 0.25, '#4a3a2c'));
      rect(tank.x - tw / 2, tank.y - u * 0.25 - tw * 0.95, tw, tw * 0.95, tier >= 4 ? GOLD : '#9a6b45');
      for (let k = 1; k < 4; k += 1) rect(tank.x - tw / 2, tank.y - u * 0.25 - tw * 0.95 * (k / 4), tw, 1.5, 'rgba(0,0,0,0.3)');
      poly([[tank.x - tw / 2 - 2, tank.y - u * 0.25 - tw * 0.95], [tank.x + tw / 2 + 2, tank.y - u * 0.25 - tw * 0.95], [tank.x, tank.y - u * 0.25 - tw * 1.35]], tier >= 3 ? '#2b2b2e' : '#6b4f33');
    }
    // The fascia over the awning carries the wordmark: paper on ink, mint from tier 3, gold from tier 4.
    if (tier) {
      const ft = tall ? sTop - u * 0.11 : g.m + 2;
      const fh = awning - ft;
      rect(g.l, ft, g.w, fh, INK);
      rect(g.l, ft, g.w, 1, 'rgba(255,255,255,0.18)');
      glowLogo(tier >= 3 && logoTone, u * 0.12, dx, ft + fh / 2, Math.min(g.w * 0.8, (fh * 0.6) / LOGO_RATIO), logoTone);
    }
    // Steps
    for (let i = 0; i < 2; i += 1) rect(dx - g.w * (0.13 + i * 0.03), g.b + i * u * 0.05, g.w * (0.26 + i * 0.06), u * 0.05, i ? '#bdbcb6' : '#d6d5cf');
    if (inside) drawShelterMark(dx, markY);
  };
  // Over the building the hustler hides in: a bobbing badge with the cash notes.
  const drawShelterMark = (x, y) => {
    const u = cw();
    const bob = Math.sin(clock * 4) * u * 0.05;
    const r = u * 0.2;
    circle(x, y + bob, r + 2, PAPER);
    circle(x, y + bob, r, INK);
    poly([[x - r * 0.3, y + bob + r * 0.95], [x + r * 0.3, y + bob + r * 0.95], [x, y + bob + r * 1.4]], INK);
    circle(x, y + bob, r * 0.72, YELLOW);
    drawCash(ctx, x, y + bob, r * 0.42, false);
  };

  // Shared bits for the workshops.
  const workshopShell = (item, h) => {
    const L = item.level;
    const mat = matOf(L);
    const g = block(item.x + 0.1, item.y + 0.32, 1.8, 1.48, h, mat.roof, mat.wall);
    surface(g, L);
    roofEdge(g, L);
    roofDeck(g, L);
    return g;
  };
  // A flat roof with a parapet: the inner deck sits a touch darker, the rim catches the light.
  const roofDeck = (g, level) => {
    const dark = tierOf(level) >= 3;
    const rim = Math.max(2, g.w * 0.035);
    const deck = { l: g.l + rim, t: g.t + rim * 0.8, w: g.w - rim * 2, h: g.m - g.t - rim * 1.6 };
    if (deck.h < rim) return;
    rect(deck.l, deck.t, deck.w, deck.h, dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.07)');
    rect(deck.l, deck.t, deck.w, Math.max(1, g.u * 0.015), dark ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.12)');
    rect(g.l, g.t, g.w, Math.max(1, g.u * 0.012), dark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.35)');
  };
  // A rooftop AC unit with a fan grille, for roofs that would otherwise sit empty.
  const roofUnit = (x, y, u, dark) => {
    const w = u * 0.34;
    const h = u * 0.2;
    rect(x - w / 2, y - h, w, h, dark ? '#3a3a3f' : '#d9d8d3');
    rect(x - w / 2, y - h, w, Math.max(1, h * 0.12), dark ? '#4a4a50' : '#efeee9');
    circle(x - w * 0.18, y - h * 0.5, h * 0.32, dark ? '#202024' : '#9ea3a8');
    ctx.save();
    ctx.translate(x - w * 0.18, y - h * 0.5);
    ctx.rotate(clock * 6);
    rect(-h * 0.28, -h * 0.05, h * 0.56, h * 0.1, dark ? '#5a5a60' : '#6f767d');
    rect(-h * 0.05, -h * 0.28, h * 0.1, h * 0.56, dark ? '#5a5a60' : '#6f767d');
    ctx.restore();
    for (let k = 0; k < 3; k += 1) rect(x + w * 0.08, y - h * (0.75 - k * 0.22), w * 0.32, Math.max(1, h * 0.08), dark ? '#202024' : '#b4bac0');
  };
  const signBand = (g, text, L) => {
    const tier = tierOf(L);
    const mat = matOf(L);
    const sh = Math.min(g.fh * 0.16, g.u * 0.22);
    const sy = g.m + g.fh * 0.06;
    rect(g.l + g.w * 0.08, sy, g.w * 0.84, sh, tier >= 3 ? '#0c0c0d' : INK);
    const tc = tier >= 4 ? GOLD : tier >= 3 ? MINT : PAPER;
    glowLabel(tier >= 5 && MINT, g.u * 0.15, text, g.l + g.w / 2, sy + sh / 2 + 0.5, sh * 0.62, tc);
    if (tier >= 1) rect(g.l + g.w * 0.08, sy + sh, g.w * 0.84, Math.max(1, g.u * 0.015), mat.trim);
    return sy + sh;
  };
  const shirt = (cx, top, w, color) => {
    const hw = w / 2;
    poly([[cx - hw * 0.45, top], [cx - hw, top + hw * 0.35], [cx - hw * 0.85, top + hw * 0.75], [cx - hw * 0.62, top + hw * 0.62], [cx - hw * 0.62, top + w * 1.05], [cx + hw * 0.62, top + w * 1.05], [cx + hw * 0.62, top + hw * 0.62], [cx + hw * 0.85, top + hw * 0.75], [cx + hw, top + hw * 0.35], [cx + hw * 0.45, top]], color);
    oval(cx, top + hw * 0.05, hw * 0.28, hw * 0.12, 'rgba(0,0,0,0.25)');
  };
  const cap = (cx, cy, w, color, brimColor) => {
    ctx.beginPath();
    ctx.ellipse(cx, cy, w / 2, w * 0.42, 0, Math.PI, 0);
    ctx.fillStyle = color;
    ctx.fill();
    oval(cx + w * 0.38, cy, w * 0.35, w * 0.09, brimColor || color);
    circle(cx, cy - w * 0.42, Math.max(0.8, w * 0.06), brimColor || INK);
  };
  // ---------------------------------------------------------------------------------------------
  // Promo and sales: posters, a graffiti wall, a party truck, a stage, a corner stand,
  // a pop-up container and the On-Demand Shop. Each builds up with every look tier.

  const POSTER_COLORS = [SIGNAL, '#2f6fff', YELLOW, MINT, '#ff4fa3', PAPER, INK];
  // Words on a poster: heavy, centred, and never wider than the room they get.
  const posterText = (text, x, y, maxW, size, color, weight = 900) => {
    ctx.font = `${weight} ${size}px ${FONT}`;
    const fit = Math.min(size, (size * maxW) / Math.max(1, ctx.measureText(text).width));
    ctx.font = `${weight} ${fit}px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = color;
    ctx.fillText(text, x, y);
  };
  // Print dots that shrink towards the bottom, the way a screen-printed fade looks.
  const halftone = (x, y, w, h, color, step) => {
    ctx.fillStyle = color;
    ctx.beginPath();
    for (let j = 0; j * step < h; j += 1) {
      const r = step * 0.46 * (1 - (j * step) / h);
      if (r < 0.2) continue;
      for (let i = 0; i * step < w; i += 1) {
        const cx = x + i * step + (j % 2 ? step / 2 : 0);
        ctx.moveTo(cx + r, y + j * step);
        ctx.arc(cx, y + j * step, r, 0, TAU);
      }
    }
    ctx.fill();
  };
  // Whether ink reads better than paper on a colour.
  const lightColour = hex => {
    const n = parseInt(hex.slice(1), 16);
    return ((n >> 16) * 299 + ((n >> 8) & 255) * 587 + (n & 255) * 114) / 1000 > 160;
  };
  // One poster at the origin, w by h: a paper margin and one of six designs, then the creases of the paste.
  const POSTER_DESIGNS = 6;
  const paintPoster = (w, h, design, colour, rand) => {
    const ink = colour === INK || colour === '#2f6fff' ? PAPER : INK;
    const pop = colour === YELLOW ? SIGNAL : YELLOW;
    const m = Math.max(0.8, w * 0.05);
    const x = m;
    const y = m;
    const iw = w - m * 2;
    const ih = h - m * 2;
    const cx = x + iw / 2;
    rect(0, 0, w, h, PAPER);
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, iw, ih);
    ctx.clip();
    if (design === 0) {
      // Drop: a shirt over a halftone fade, the word, the day.
      rect(x, y, iw, ih, colour);
      halftone(x, y, iw, ih * 0.62, 'rgba(0,0,0,0.16)', Math.max(1.6, iw * 0.08));
      const sw = Math.min(iw * 0.62, ih * 0.38);
      shirt(cx, y + ih * 0.1, sw, colour === PAPER ? INK : PAPER);
      rect(cx - sw * 0.14, y + ih * 0.1 + sw * 0.38, sw * 0.28, sw * 0.1, pop);
      posterText('DROP', cx, y + ih * 0.66, iw * 0.86, ih * 0.2, ink);
      rect(x + iw * 0.2, y + ih * 0.78, iw * 0.6, Math.max(0.6, ih * 0.015), ink);
      posterText('FR 18 UHR', cx, y + ih * 0.88, iw * 0.72, ih * 0.08, ink, 700);
    } else if (design === 1) {
      // The short mark in a ring on big print dots, a band along the foot.
      rect(x, y, iw, ih, colour);
      halftone(x, y, iw, ih * 0.8, ink === PAPER ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.14)', Math.max(2, iw * 0.12));
      logoRing(cx, y + ih * 0.4, iw * 0.46, iw * 0.22, Math.max(0.8, iw * 0.04), pop);
      posterText('MXD', cx, y + ih * 0.41, iw * 0.7, ih * 0.26, ink);
      rect(x, y + ih * 0.76, iw, ih * 0.24, ink);
      posterText('JETZT ONLINE', cx, y + ih * 0.88, iw * 0.8, ih * 0.09, colour === INK ? PAPER : colour, 800);
    } else if (design === 2) {
      // A face in a cap against the sun, a red tag underneath.
      rect(x, y, iw, ih, colour);
      ctx.save();
      ctx.globalAlpha = 0.85;
      circle(cx, y + ih * 0.42, iw * 0.36, pop);
      ctx.restore();
      const hr = iw * 0.15;
      const hy = y + ih * 0.38;
      roundRect(cx - iw * 0.3, y + ih * 0.56, iw * 0.6, ih * 0.5, iw * 0.2, INK);
      circle(cx, hy, hr, INK);
      ctx.beginPath();
      ctx.arc(cx, hy - hr * 0.1, hr * 1.08, Math.PI, 0);
      ctx.fillStyle = SIGNAL;
      ctx.fill();
      rect(cx, hy - hr * 0.16, hr * 1.5, hr * 0.26, SIGNAL);
      rect(x + iw * 0.12, y + ih * 0.8, iw * 0.76, ih * 0.12, PAPER);
      posterText('LIVE · SA', cx, y + ih * 0.86, iw * 0.66, ih * 0.08, INK, 800);
    } else if (design === 3) {
      // Type only, printed a little off register.
      rect(x, y, iw, ih, PAPER);
      const tint = colour === PAPER ? SIGNAL : colour;
      const off = Math.max(0.6, iw * 0.03);
      ['NEU', 'DA'].forEach((word, i) => {
        posterText(word, cx + off, y + ih * (0.3 + i * 0.3) + off, iw * 0.84, ih * 0.3, INK);
        posterText(word, cx, y + ih * (0.3 + i * 0.3), iw * 0.84, ih * 0.3, tint);
      });
      [0.3, 0.5, 0.7].forEach((fx, i) => circle(x + iw * fx, y + ih * 0.86, Math.max(0.6, iw * 0.05), POSTER_COLORS[(i + 1) % 5]));
    } else if (design === 4) {
      // Sale: a starburst with the cut.
      rect(x, y, iw, ih, colour);
      ctx.beginPath();
      starPath(ctx, cx, y + ih * 0.52, iw * 0.46, iw * 0.34, 12, rand() * 0.3);
      ctx.fillStyle = pop;
      ctx.fill();
      ctx.lineWidth = Math.max(0.5, iw * 0.02);
      ctx.strokeStyle = INK;
      ctx.stroke();
      posterText('-30%', cx, y + ih * 0.53, iw * 0.6, ih * 0.18, INK);
      posterText('SALE', cx, y + ih * 0.13, iw * 0.7, ih * 0.11, ink, 800);
    } else {
      // Block party: rays from the foot, a speaker, the night.
      rect(x, y, iw, ih, INK);
      const tint = colour === INK || colour === PAPER ? MINT : colour;
      ctx.save();
      ctx.globalAlpha = 0.35;
      for (let k = 0; k < 9; k += 2) {
        const a0 = Math.PI + (k / 9) * Math.PI;
        const a1 = Math.PI + ((k + 1) / 9) * Math.PI;
        poly([[cx, y + ih], [cx + Math.cos(a0) * ih * 1.4, y + ih + Math.sin(a0) * ih * 1.4], [cx + Math.cos(a1) * ih * 1.4, y + ih + Math.sin(a1) * ih * 1.4]], tint);
      }
      ctx.restore();
      roundRect(cx - iw * 0.17, y + ih * 0.4, iw * 0.34, ih * 0.38, iw * 0.04, PAPER);
      circle(cx, y + ih * 0.5, iw * 0.07, INK);
      circle(cx, y + ih * 0.66, iw * 0.11, INK);
      circle(cx, y + ih * 0.66, iw * 0.04, tint);
      posterText('PARTY', cx, y + ih * 0.2, iw * 0.84, ih * 0.16, tint);
      posterText('SA 22 UHR', cx, y + ih * 0.9, iw * 0.7, ih * 0.08, PAPER, 700);
    }
    ctx.restore();
    // Sun on the top corner and the creases left by the brush.
    const sun = ctx.createLinearGradient(0, 0, w, h);
    sun.addColorStop(0, 'rgba(255,255,255,0.18)');
    sun.addColorStop(0.5, 'rgba(255,255,255,0)');
    rect(0, 0, w, h, sun);
    ctx.save();
    ctx.lineWidth = Math.max(0.4, w * 0.012);
    for (let k = 0; k < 2; k += 1) {
      const x0 = w * (0.15 + rand() * 0.7);
      const x1 = x0 + (rand() - 0.5) * w * 0.5;
      ctx.globalAlpha = 0.28;
      line(x0, 0, x1, h, PAPER, ctx.lineWidth);
      ctx.globalAlpha = 0.1;
      line(x0 + ctx.lineWidth, 0, x1 + ctx.lineWidth, h, INK, ctx.lineWidth);
    }
    ctx.restore();
  };
  // A corner that came loose and curls over.
  const dogEar = (w, h, size) => {
    poly([[w - size, h], [w, h - size], [w, h]], 'rgba(0,0,0,0.25)');
    poly([[w - size, h], [w, h - size], [w - size * 0.85, h - size * 0.85]], '#dcd6c6');
  };
  const PLAKAT_BOARDS = ['#b08d5e', '#4f5a52', '#3c4046'];
  // The hoarding face, painted once: boards with old layers under the posters at first, then a neat steel frame,
  // and from tier 3 one lit board with the drop.
  const paintHoarding = (w, h, tier, brand, trim, seed) => {
    const rand = mulberry32(seed);
    const pad = w * 0.04;
    if (tier >= 3) {
      rect(0, 0, w, h, trim);
      const bx = pad;
      const by = pad;
      const bw = w - pad * 2;
      const bh = h - pad * 2;
      const lit = lightColour(brand) ? INK : PAPER;
      const look = [
        { bg: [brand, tone(brand, -0.25)], ink: lit, sub: lit, dots: 'rgba(255,255,255,0.14)', halo: 'rgba(255,255,255,0.22)' },
        { bg: ['#1d1d20', INK], ink: GOLD, sub: PAPER, dots: 'rgba(226,179,60,0.22)', halo: 'rgba(226,179,60,0.18)' },
        { bg: ['#16261d', INK], ink: MINT, sub: PAPER, dots: 'rgba(186,236,183,0.16)', halo: 'rgba(186,236,183,0.2)', glow: MINT }
      ][tier - 3];
      const bg = ctx.createLinearGradient(0, by, 0, by + bh);
      bg.addColorStop(0, look.bg[0]);
      bg.addColorStop(1, look.bg[1]);
      rect(bx, by, bw, bh, bg);
      ctx.save();
      ctx.beginPath();
      ctx.rect(bx, by, bw, bh);
      ctx.clip();
      halftone(bx, by, bw * 0.45, bh, look.dots, Math.max(2, bh * 0.08));
      circle(bx + bw * 0.2, by + bh * 0.52, bh * 0.42, look.halo);
      ctx.restore();
      const sw = Math.min(bh * 0.62, bw * 0.28);
      shirt(bx + bw * 0.2, by + bh * 0.2, sw, tier === 3 ? lit : look.ink);
      rect(bx + bw * 0.2 - sw * 0.14, by + bh * 0.2 + sw * 0.38, sw * 0.28, sw * 0.1, tier === 3 ? brand : INK);
      if (tier >= 4) {
        rect(bx + bw * 0.4, by + bh * 0.12, bw * 0.5, Math.max(0.8, bh * 0.02), look.ink);
        rect(bx + bw * 0.4, by + bh * 0.92, bw * 0.5, Math.max(0.8, bh * 0.02), look.ink);
      }
      const tx = bx + bw * 0.65;
      ctx.save();
      if (look.glow) {
        ctx.shadowColor = look.glow;
        ctx.shadowBlur = bh * 0.25 * ctx.getTransform().a;
      }
      drawLogo(tx, by + bh * 0.3, bw * 0.48, look.ink);
      posterText('NEW DROP', tx, by + bh * 0.6, bw * 0.5, bh * 0.24, look.ink);
      ctx.restore();
      posterText('FR 18 UHR · ONLINE', tx, by + bh * 0.8, bw * 0.46, bh * 0.09, look.sub, 700);
      return;
    }
    // The boards: planks with grain and nails, a painted sheet, then steel with rivets.
    const base = PLAKAT_BOARDS[tier];
    if (tier === 0) {
      const n = 7;
      for (let i = 0; i < n; i += 1) {
        const px = (i * w) / n;
        rect(px, 0, w / n + 0.5, h, tone(base, (rand() - 0.5) * 0.18));
        for (let k = 0; k < 3; k += 1) rect(px + (w / n) * (0.15 + rand() * 0.7), 0, 0.6, h, 'rgba(0,0,0,0.08)');
        rect(px, 0, 1, h, 'rgba(0,0,0,0.25)');
        [0.07, 0.93].forEach(fy => circle(px + w / n / 2, h * fy, Math.max(0.6, w * 0.006), '#3a3a3d'));
      }
    } else if (tier === 1) {
      rect(0, 0, w, h, base);
      [0.33, 0.66].forEach(fx => rect(w * fx, 0, 1, h, 'rgba(0,0,0,0.3)'));
    } else {
      rect(0, 0, w, h, base);
      const edge = Math.max(1.5, w * 0.018);
      rect(0, 0, w, edge, '#585d64');
      rect(0, h - edge, w, edge, '#2c2f33');
      for (let k = 0; k <= 8; k += 1) [edge / 2, h - edge / 2].forEach(ry => circle((w * k) / 8, ry, Math.max(0.5, edge * 0.28), '#8d9399'));
    }
    // Older posters underneath, faded and torn off, only on the boards that nobody cleans.
    if (tier <= 1) {
      for (let k = 0; k < 6; k += 1) {
        const sx = rand() * w * 0.9;
        const sy = rand() * h * 0.7;
        const sw = w * (0.12 + rand() * 0.12);
        const sh = h * (0.25 + rand() * 0.25);
        const jag = Array.from({ length: 6 }, (_, j) => [sx + sw * (1 - j / 5), sy + sh * (0.55 + rand() * 0.45)]);
        poly([[sx, sy], [sx + sw, sy], ...jag], tone(oneOf(rand, POSTER_COLORS.slice(0, 5)), 0.55));
      }
    }
    // The posters in rows: more and neater with every tier, crooked and taped on the planks.
    const cols = 3 + tier;
    const rows = 2;
    const gw = (w - pad * 2) / cols;
    const ph = (h - pad * 2.6) / rows;
    const pw = gw * 0.9;
    // Every design before any comes twice, in an order of this board's own.
    const deck = Array.from({ length: POSTER_DESIGNS }, (_, i) => i).sort(() => rand() - 0.5);
    let dealt = 0;
    for (let r = 0; r < rows; r += 1) {
      for (let c = 0; c < cols; c += 1) {
        const tilt = (rand() - 0.5) * [0.09, 0.035, 0][tier];
        const jx = tier ? 0 : (rand() - 0.5) * pad;
        const jy = tier ? 0 : (rand() - 0.5) * pad;
        ctx.save();
        ctx.translate(pad + c * gw + gw / 2 + jx, pad + r * (ph + pad * 0.6) + ph / 2 + jy);
        ctx.rotate(tilt);
        ctx.translate(-pw / 2, -ph / 2);
        rect(pw * 0.03, ph * 0.03, pw, ph, 'rgba(0,0,0,0.28)');
        paintPoster(pw, ph, deck[dealt++ % POSTER_DESIGNS], oneOf(rand, POSTER_COLORS), rand);
        if (rand() < 0.3) dogEar(pw, ph, Math.min(pw, ph) * 0.2);
        if (tier === 0) {
          // Tape across two corners.
          [[0, -1], [pw, 1]].forEach(([tx, turn]) => {
            ctx.save();
            ctx.translate(tx, 0);
            ctx.rotate(turn * 0.7);
            rect(-pw * 0.13, -ph * 0.025, pw * 0.26, ph * 0.07, 'rgba(240,228,190,0.8)');
            ctx.restore();
          });
        } else if (tier === 1) {
          [[0.06, 0.04], [0.94, 0.04], [0.06, 0.96], [0.94, 0.96]].forEach(([fx, fy]) => rect(pw * fx - 1, ph * fy - 0.4, 2, 0.8, '#55595f'));
        } else rect(pw * 0.35, -ph * 0.03, pw * 0.3, ph * 0.05, '#2c2f33');
        ctx.restore();
      }
    }
    // A sticker slapped across a poster now and then.
    if (tier >= 1) {
      const sx = pad + gw * (0.5 + Math.floor(rand() * cols)) + gw * 0.3;
      const sy = pad + ph * 0.2;
      circle(sx, sy, gw * 0.13, SIGNAL);
      posterText('NEU', sx, sy, gw * 0.18, gw * 0.1, PAPER);
    }
  };

  // Wildplakate: a hoarding full of posters; steel and lamps later, then a lit board and a screen.
  const drawPlakat = item => {
    const u = cw();
    const L = item.level;
    const tier = Math.min(5, tierOf(L));
    const mat = matOf(L);
    const h = 0.85 + Math.min(0.45, (L - 1) * 0.02);
    const g = block(item.x + 0.08, item.y + 0.5, 1.84, 0.26, h, mat.roof, tier ? mat.wall : PLAKAT_BOARDS[0]);
    const brand = hustlerLook.brand === INK ? SIGNAL : hustlerLook.brand;
    ctx.drawImage(cachedArt(`plakat:${item.id}:${tier}:${brand}:${mat.trim}`, g.w, g.fh, () => paintHoarding(g.w, g.fh, tier, brand, mat.trim, item.id * 41 + tier)), g.l, g.m, g.w, g.fh);
    if (tier >= 5) {
      // A sheen runs over the lit board now and then.
      const pad = g.w * 0.04;
      const run = ((clock * 0.3 + item.id * 0.37) % 1.8) - 0.3;
      ctx.save();
      ctx.beginPath();
      ctx.rect(g.l + pad, g.m + pad, g.w - pad * 2, g.fh - pad * 2);
      ctx.clip();
      ctx.globalAlpha = 0.16;
      const sx = g.l + g.w * run;
      poly([[sx, g.m], [sx + g.w * 0.1, g.m], [sx - g.w * 0.04, g.b], [sx - g.w * 0.14, g.b]], PAPER);
      ctx.restore();
    }
    // Legs, lamps on arms from tier 2, a paste bucket with a brush at the start.
    [0.12, 0.88].forEach(fx => rect(g.l + g.w * fx - 1.5, g.b, 3, u * 0.08, mat.dark));
    if (tier >= 2) {
      [0.25, 0.75].forEach(fx => {
        const x = g.l + g.w * fx;
        line(x, g.t, x + u * 0.08, g.t - u * 0.16, '#4a4d52', Math.max(1.5, u * 0.03));
        oval(x + u * 0.1, g.t - u * 0.16, u * 0.07, u * 0.035, '#2b2b2e');
        if (tier >= 3) {
          ctx.save();
          ctx.globalAlpha = 0.25;
          poly([[x + u * 0.05, g.t - u * 0.14], [x + u * 0.15, g.t - u * 0.14], [x + u * 0.3, g.m + g.fh * 0.4], [x - u * 0.1, g.m + g.fh * 0.4]], '#fff2b8');
          ctx.restore();
        }
      });
    }
    if (tier <= 1) {
      const at = proj(item.x + 1.75, item.y + 0.95);
      rect(at.x - u * 0.07, at.y - u * 0.14, u * 0.14, u * 0.14, '#d9d8d3');
      oval(at.x, at.y - u * 0.14, u * 0.07, u * 0.025, '#f4f3ef');
      line(at.x + u * 0.02, at.y - u * 0.13, at.x + u * 0.08, at.y - u * 0.3, '#a07a4a', Math.max(1.5, u * 0.03));
    }
    if (tier >= 5) starRow(g.l + g.w / 2, g.t - u * 0.22, u * 0.05, stars(L));
  };

  // Bricks the way they really sit in a wall: courses that scale with it, joints, and bricks that are each a
  // little lighter or darker than the next.
  const brickFace = (w, h, color, joint, rand, rows = 9) => {
    rect(0, 0, w, h, color);
    const rowH = h / rows;
    const brickW = rowH * 2.3;
    for (let r = 0; r < rows; r += 1) {
      for (let x = r % 2 ? -brickW / 2 : 0; x < w; x += brickW) {
        const k = rand();
        if (k < 0.35) rect(x + 1, r * rowH + 1, brickW - 1, rowH - 1, tone(color, k < 0.18 ? -0.1 : 0.08));
      }
      rect(0, r * rowH, w, Math.max(1, rowH * 0.1), joint);
      for (let x = r % 2 ? brickW / 2 : 0; x < w; x += brickW) rect(x, r * rowH, Math.max(1, rowH * 0.1), rowH, joint);
    }
  };
  // What each tier of the wall shows: the brick, and the piece on it.
  const WALL_LOOKS = [
    { brick: '#a5543a', joint: 'rgba(60,30,20,0.35)' },
    { brick: '#b0583b', joint: 'rgba(60,30,20,0.3)', word: 'MXD', piece: brand => ({ fill: [tone(brand, 0.25), brand, tone(brand, -0.25)], line: PAPER, block: '#2b2b2e' }) },
    { brick: '#9c4f37', joint: 'rgba(60,30,20,0.3)', word: 'MIXEDOWN', piece: () => ({ fill: [YELLOW, '#ffb21a', '#ff6a1a'], line: PAPER, block: '#2f6fff', mist: ['#2f6fff', '#ff4fa3', '#2f6fff'], sparkles: 2, arrows: '#ff4fa3' }) },
    { brick: '#3a2b29', joint: 'rgba(255,255,255,0.07)', word: 'MIXEDOWN', piece: () => ({ fill: ['#e6fff4', MINT, '#2fc4ff'], line: PAPER, block: '#6a1b9a', mist: ['#6a1b9a', '#ff4fa3', '#6a1b9a'], crown: YELLOW, arrows: YELLOW, sparkles: 3 }) },
    { brick: '#2c2423', joint: 'rgba(255,255,255,0.06)', word: 'MIXEDOWN', piece: () => ({ fill: ['#fff4c2', GOLD, '#8a6414', '#f7d774', '#a87c1c'], line: '#1d1d20', block: '#5a4310', mist: [GOLD, '#ff6a1a', GOLD], crown: GOLD, arrows: PAPER, sparkles: 5, cut: 'rgba(255,255,255,0.35)' }) },
    { brick: '#1c1a1a', joint: 'rgba(255,255,255,0.05)', word: 'MIXEDOWN', piece: () => ({ fill: [PAPER, MINT, '#18b98a'], line: '#141415', block: '#0f3d30', mist: [MINT, '#3fd0ff', MINT], glow: MINT, crown: MINT, arrows: '#3fd0ff', sparkles: 5 }) }
  ];
  // The whole front of a Graffiti-Wall, painted once: brick, patches the city painted over, tags, old posters,
  // and from the second tier on a piece across the middle.
  const paintWallFace = (w, h, tier, brand, seed) => {
    const look = WALL_LOOKS[tier];
    const rand = mulberry32(seed);
    brickFace(w, h, look.brick, look.joint, rand);
    if (tier <= 1) {
      // Grey and brown where the city rolled over old tags.
      [['#8c847c', 0.06, 0.12, 0.34, 0.5], ['#7a5a48', 0.6, 0.06, 0.3, 0.42]].forEach(([color, x, y, bw, bh]) => {
        ctx.save();
        ctx.globalAlpha = 0.85;
        rect(w * x, h * y, w * bw, h * bh, color);
        ctx.restore();
      });
    }
    if (tier === 0) {
      // The scraps of a poster, torn off with a ragged edge.
      const px = w * 0.72;
      const py = h * 0.52;
      const pw = w * 0.16;
      const ph = h * 0.3;
      poly([[px, py], [px + pw, py], [px + pw, py + ph * 0.55], [px + pw * 0.75, py + ph * 0.4], [px + pw * 0.5, py + ph * 0.7], [px + pw * 0.2, py + ph * 0.5], [px, py + ph * 0.8]], '#e9e4d6');
      rect(px + pw * 0.12, py + ph * 0.1, pw * 0.76, ph * 0.18, SIGNAL);
      // A silver throw-up and tags all over.
      paintPiece(w * 0.42, h * 0.62, 'MX', { fill: ['#f2f3f5', '#c9ccd1', '#9ea3a8'], line: INK, block: '#3a3a3d' }, seed + 1);
      [[0.46, 0.12, 0.26, [INK, brand]], [0.5, 0.58, 0.22, ['#2f6fff', PAPER]], [0.06, 0.62, 0.2, [SIGNAL, YELLOW]], [0.78, 0.1, 0.18, [PAPER, INK]]].forEach(([x, y, tw, colours], i) => {
        ctx.save();
        ctx.translate(w * x, h * y);
        paintTag(w * tw, w * tw * 0.56, colours, seed * 7 + i);
        ctx.restore();
      });
      return;
    }
    const piece = look.piece(brand);
    ctx.save();
    ctx.translate(w * 0.03, h * 0.04);
    paintPiece(w * 0.94, h * 0.84, look.word, piece, seed + tier);
    ctx.restore();
    // A tag in the corner, signed.
    ctx.save();
    ctx.translate(w * 0.03, h * 0.72);
    paintTag(w * 0.17, w * 0.17 * 0.56, [tier >= 3 ? PAPER : INK, tier >= 4 ? GOLD : brand], seed * 5);
    ctx.restore();
  };
  // Graffiti-Wall: a brick wall with a piece that gets bigger and better; cans, a ladder, spotlights.
  const SPRAY_CANS = [SIGNAL, '#2f6fff', YELLOW, MINT_D, '#ff4fa3'];
  const drawWall = item => {
    const u = cw();
    const L = item.level;
    const tier = Math.min(5, tierOf(L));
    const mat = matOf(L);
    const h = 1 + Math.min(0.5, (L - 1) * 0.02);
    const look = WALL_LOOKS[tier];
    const g = block(item.x + 0.04, item.y + 0.52, 1.92, 0.26, h, tone(look.brick, -0.25), look.brick);
    const brand = hustlerLook.brand === INK ? SIGNAL : hustlerLook.brand;
    ctx.drawImage(cachedArt(`wall:${item.id}:${tier}:${brand}`, g.w, g.fh, () => paintWallFace(g.w, g.fh, tier, brand, item.id * 7 + 3)), g.l, g.m, g.w, g.fh);
    // The coping along the top and a little grime at the foot.
    rect(g.l, g.m - Math.max(1.5, u * 0.03), g.w, Math.max(1.5, u * 0.03), mat.trim);
    const grime = ctx.createLinearGradient(0, g.b - g.fh * 0.25, 0, g.b);
    grime.addColorStop(0, 'rgba(0,0,0,0)');
    grime.addColorStop(1, 'rgba(0,0,0,0.22)');
    rect(g.l, g.b - g.fh * 0.25, g.w, g.fh * 0.25, grime);
    if (tier >= 5) {
      // The neon piece twinkles.
      for (let k = 0; k < 3; k += 1) {
        const p = (clock * 0.6 + k / 3 + item.id * 0.13) % 1;
        ctx.save();
        ctx.globalAlpha = Math.sin(p * Math.PI);
        const sx = g.l + g.w * (0.2 + ((k * 0.37 + item.id * 0.11) % 0.6));
        const sy = g.m + g.fh * (0.25 + ((k * 0.23) % 0.4));
        ctx.beginPath();
        starPath(ctx, sx, sy, u * 0.07 * (0.6 + p * 0.4), u * 0.015, 4, 0);
        ctx.fillStyle = PAPER;
        ctx.fill();
        ctx.restore();
      }
    }
    // Spray cans on the pavement, more with the tier: a body in its colour, a white band, the cap.
    SPRAY_CANS.slice(0, Math.min(5, 2 + tier)).forEach((colour, i) => {
      const at = proj(item.x + 0.3 + i * 0.17, item.y + 0.92 + (i % 2) * 0.04);
      const cw2 = u * 0.07;
      const ch2 = u * 0.14;
      oval(at.x, at.y, cw2 * 0.7, cw2 * 0.22, 'rgba(0,0,0,0.2)');
      roundRect(at.x - cw2 / 2, at.y - ch2, cw2, ch2, cw2 * 0.2, colour);
      rect(at.x - cw2 / 2, at.y - ch2 * 0.62, cw2, ch2 * 0.14, PAPER);
      rect(at.x - cw2 / 2, at.y - ch2, cw2 * 0.28, ch2, 'rgba(255,255,255,0.3)');
      rect(at.x - cw2 * 0.28, at.y - ch2 - u * 0.03, cw2 * 0.56, u * 0.03, '#d9d8d3');
      rect(at.x - cw2 * 0.1, at.y - ch2 - u * 0.05, cw2 * 0.2, u * 0.02, INK);
    });
    // A ladder leaning on the wall at the first tiers.
    if (tier >= 1 && tier < 3) {
      const foot = { x: g.l + g.w * 0.84, y: g.b + u * 0.06 };
      const top = { x: foot.x + u * 0.12, y: g.m + g.fh * 0.08 };
      const gap = u * 0.16;
      line(foot.x, foot.y, top.x, top.y, '#a07a4a', Math.max(1.5, u * 0.03));
      line(foot.x + gap, foot.y, top.x + gap, top.y, '#a07a4a', Math.max(1.5, u * 0.03));
      for (let k = 1; k < 6; k += 1) {
        const p = k / 6;
        const x = lerp(foot.x, top.x, p);
        const y = lerp(foot.y, top.y, p);
        line(x, y, x + gap, y, '#8a6a40', Math.max(1, u * 0.02));
      }
    }
    // Spotlights on the coping from tier 3.
    if (tier >= 3) {
      [0.2, 0.8].forEach(fx => {
        const x = g.l + g.w * fx;
        rect(x - u * 0.05, g.m - u * 0.1, u * 0.1, u * 0.08, '#2b2b2e');
        ctx.save();
        ctx.globalAlpha = 0.16;
        poly([[x - u * 0.05, g.m - u * 0.02], [x + u * 0.05, g.m - u * 0.02], [x + u * 0.35 * (fx < 0.5 ? 1 : -1) + u * 0.2, g.b], [x - u * 0.2 + u * 0.35 * (fx < 0.5 ? 1 : -1), g.b]], '#fff2b8');
        ctx.restore();
      });
    }
    if (tier >= 5) starRow(g.l + g.w / 2, g.t - u * 0.15, u * 0.05, stars(L));
  };

  // A speaker cabinet standing on the ground at (x, y); the cones pump with the beat.
  const speaker = (x, y, w, h, beat, trim) => {
    const pump = 1 + beat * 0.08;
    rect(x - w / 2, y - h, w, h, '#1d1d20');
    rect(x - w / 2, y - h, w, Math.max(1, h * 0.04), trim);
    [0.3, 0.7].forEach(fy => {
      circle(x, y - h * (1 - fy), w * 0.32 * pump, '#3a3d42');
      circle(x, y - h * (1 - fy), w * 0.14 * pump, '#5a5f66');
    });
  };
  // Block Party: a truck with its side open, a DJ inside, speaker stacks, bunting and lights.
  const drawParty = item => {
    const u = cw();
    const L = item.level;
    const tier = Math.min(5, tierOf(L));
    const mat = matOf(L);
    const beat = Math.max(0, Math.sin(clock * 9 + item.id));
    const body = tier >= 3 ? '#1d1d20' : tier === 0 ? '#e9e7e1' : hustlerLook.brand === INK ? SIGNAL : hustlerLook.brand;
    const truck = block(item.x + 0.2, item.y + 0.45, 1.25, 0.8, 0.8 + Math.min(0.3, tier * 0.06), tier >= 3 ? '#2a2a2e' : '#d6d5cf', body, 0.12);
    const cab = block(item.x + 1.45, item.y + 0.6, 0.38, 0.65, 0.62, tier >= 3 ? '#2a2a2e' : '#d6d5cf', body, 0.12);
    rect(cab.l + cab.w * 0.12, cab.m + cab.fh * 0.12, cab.w * 0.76, cab.fh * 0.38, '#5f7480');
    rect(cab.l + cab.w * 0.12, cab.m + cab.fh * 0.12, cab.w * 0.76, 1.5, 'rgba(255,255,255,0.4)');
    // The open side: a lit booth, decks, and the DJ nodding along.
    const open = { l: truck.l + truck.w * 0.08, r: truck.r - truck.w * 0.08, t: truck.m + truck.fh * 0.1, b: truck.b - truck.fh * 0.3 };
    rect(open.l, open.t, open.r - open.l, open.b - open.t, tier >= 5 ? '#16261d' : '#2b2b2e');
    const dj = { x: (open.l + open.r) / 2, y: open.b };
    circle(dj.x, dj.y - u * 0.33 + beat * u * 0.02, u * 0.08, '#c99572');
    rect(dj.x - u * 0.1, dj.y - u * 0.25, u * 0.2, u * 0.25, tier >= 4 ? GOLD : MINT);
    ctx.beginPath();
    ctx.arc(dj.x, dj.y - u * 0.34 + beat * u * 0.02, u * 0.09, Math.PI, TAU);
    ctx.strokeStyle = INK;
    ctx.lineWidth = Math.max(1.5, u * 0.025);
    ctx.stroke();
    rect(open.l + 2, open.b - u * 0.07, open.r - open.l - 4, u * 0.07, '#4a4d52');
    [0.25, 0.75].forEach(fx => oval(lerp(open.l, open.r, fx), open.b - u * 0.07, u * 0.08, u * 0.025, '#1d1d20'));
    rect(open.l, open.t, open.r - open.l, Math.max(1.5, u * 0.03), mat.trim);
    if (tier >= 3) for (let k = 0; k < 8; k += 1) circle(lerp(open.l, open.r, k / 7), open.t - u * 0.03, Math.max(1, u * 0.02), [MINT, '#ff4fa3', YELLOW, '#4fd1ff'][(k + Math.floor(clock * 4)) % 4]);
    // Wheels
    [[truck, 0.2], [truck, 0.8], [cab, 0.55]].forEach(([box, fx]) => {
      const w = box.at(fx, 1);
      oval(w.x, w.y, u * 0.12, u * 0.09, INK);
      oval(w.x, w.y - 1, u * 0.05, u * 0.035, '#8d9399');
    });
    // Speaker stacks either side, more with the tier.
    const stacks = tier >= 2 ? 2 : 1;
    for (let i = 0; i < stacks; i += 1) {
      const at = proj(item.x + 0.18 + i * 0.3, item.y + 1.85);
      speaker(at.x, at.y, u * 0.24, u * (0.36 + tier * 0.05), beat, mat.trim);
      if (tier >= 4) speaker(at.x, at.y - u * (0.36 + tier * 0.05), u * 0.2, u * 0.26, beat, mat.trim);
    }
    if (tier >= 1) {
      const at = proj(item.x + 1.8, item.y + 1.85);
      speaker(at.x, at.y, u * 0.24, u * (0.36 + tier * 0.05), beat, mat.trim);
    }
    // Bunting from tier 1, string lights from tier 2, lasers at tier 5.
    if (tier >= 1) {
      const a = proj(item.x + 0.05, item.y + 0.45, 1.25);
      const b = proj(item.x + 1.95, item.y + 0.45, 1.25);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.quadraticCurveTo((a.x + b.x) / 2, a.y + u * 0.2, b.x, b.y);
      ctx.strokeStyle = '#4a4d52';
      ctx.lineWidth = 1;
      ctx.stroke();
      for (let k = 1; k < 10; k += 1) {
        const p = k / 10;
        const x = lerp(a.x, b.x, p);
        const y = a.y + Math.sin(p * Math.PI) * u * 0.1;
        if (tier >= 2) circle(x, y + u * 0.02, Math.max(1.2, u * 0.025), (k + Math.floor(clock * 3)) % 2 ? '#ffe9a8' : '#fff7d6');
        else poly([[x - u * 0.05, y], [x + u * 0.05, y], [x, y + u * 0.1]], POSTER_COLORS[k % 5]);
      }
      [a, b].forEach(p => rect(p.x - 1, p.y, 2, u * 1.25, '#4a4d52'));
    }
    if (tier >= 5) {
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      for (let k = 0; k < 3; k += 1) {
        const ang = Math.sin(clock * 1.3 + k * 2) * 0.6 - Math.PI / 2;
        const from = truck.top(0.3 + k * 0.2, 0.2);
        line(from.x, from.y, from.x + Math.cos(ang) * u * 2, from.y + Math.sin(ang) * u * 2, ['rgba(186,236,183,0.55)', 'rgba(255,79,163,0.5)', 'rgba(79,209,255,0.5)'][k], Math.max(1, u * 0.02));
      }
      ctx.restore();
      starRow(truck.l + truck.w / 2, truck.t - u * 0.12, u * 0.05, stars(L));
    }
    if (beat > 0.9 && Math.random() < 0.3) burst(item.x + 0.8, item.y + 1.2, 1.2, 1, [POSTER_COLORS[Math.floor(Math.random() * 5)]], { speed: 0.6, up: 1.5, g: 1, size: 0.035, life: 0.8 });
  };

  // A tiny crowd figure for shows and queues: legs, a top, a head; arms up when the music hits.
  // `hype` is true for a crowd going wild, or how much from 0 to 1.
  const crowdFigure = (x, y, s, top, skin, hype) => {
    const jump = hype ? Math.max(0, Math.sin(clock * 9 + x * 0.13)) * s * 0.12 * hype : 0;
    oval(x, y, s * 0.18, s * 0.05, 'rgba(0,0,0,0.15)');
    rect(x - s * 0.09, y - s * 0.28 - jump, s * 0.07, s * 0.28, '#2b2b2e');
    rect(x + s * 0.02, y - s * 0.28 - jump, s * 0.07, s * 0.28, '#2b2b2e');
    rect(x - s * 0.13, y - s * 0.62 - jump, s * 0.26, s * 0.36, top);
    if (hype) {
      const up = s * lerp(-0.1, 0.27, +hype);
      line(x - s * 0.12, y - s * 0.58 - jump, x - s * 0.2, y - s * 0.58 - up - jump, top, Math.max(1.5, s * 0.07));
      line(x + s * 0.12, y - s * 0.58 - jump, x + s * 0.2, y - s * 0.58 - up - jump, top, Math.max(1.5, s * 0.07));
    }
    circle(x, y - s * 0.74 - jump, s * 0.13, skin);
  };
  // Festival: a stage with a backdrop, truss and lights; during a show the beams sweep and the crowd jumps.
  const drawBuehne = item => {
    const u = cw();
    const L = item.level;
    const tier = Math.min(5, tierOf(L));
    const mat = matOf(L);
    const live = showLive(item);
    const back = block(item.x + 0.2, item.y + 0.2, 2.6, 0.12, 1.45 + Math.min(0.4, tier * 0.08), mat.roof, tier >= 3 ? '#141415' : '#2b2b2e');
    glowLogo((live || tier >= 5) && (MINT), u * 0.25, back.l + back.w / 2, back.m + back.fh * 0.3, back.w * 0.7, live ? MINT : tier >= 4 ? GOLD : PAPER);
    if (tier >= 3) {
      [0.12, 0.88].forEach(fx => {
        const x = back.l + back.w * fx;
        rect(x - back.w * 0.08, back.m + back.fh * 0.5, back.w * 0.16, back.fh * 0.3, '#0c0c0d');
        rect(x - back.w * 0.07, back.m + back.fh * 0.51, back.w * 0.14, back.fh * 0.28, live ? ['#2f6fff', '#ff4fa3', MINT][Math.floor(clock * 2) % 3] : '#1f3d2e');
      });
    }
    const deck = block(item.x + 0.1, item.y + 0.32, 2.8, 1.18, 0.34, tier >= 3 ? '#2a2a2e' : '#8a6a45', tier >= 3 ? '#141415' : '#6b4f33');
    for (let x = deck.l + deck.w / 10; x < deck.r - 1; x += deck.w / 10) rect(x, deck.t, 1, deck.m - deck.t, 'rgba(0,0,0,0.15)');
    rect(deck.l, deck.m, deck.w, Math.max(1.5, u * 0.03), mat.trim);
    // The act on stage: a performer with a mic, jumping when the show is on.
    const jump = live ? Math.abs(Math.sin(clock * 6)) * u * 0.12 : 0;
    const p = deck.top(0.5, 0.55);
    oval(p.x, p.y, u * 0.12, u * 0.04, 'rgba(0,0,0,0.2)');
    crowdFigure(p.x, p.y - jump, u * 0.95, hustlerLook.brand === INK ? SIGNAL : hustlerLook.brand, '#a87553', live);
    line(p.x + u * 0.12, p.y - u * 0.55 - jump, p.x + u * 0.08, p.y - u * 0.7 - jump, INK, Math.max(1.5, u * 0.025));
    // Truss: two towers and a beam with lights.
    // The truss stands clear above the backdrop, so the lamps never cover the logo.
    const height = 2.35 + Math.min(0.4, tier * 0.08);
    const towers = [item.x + 0.25, item.x + 2.75].map(x => ({ base: proj(x, item.y + 1.4, 0.34), top: proj(x, item.y + 1.4, height) }));
    towers.forEach(({ base, top }) => {
      rect(top.x - u * 0.04, top.y, u * 0.08, base.y - top.y, tier >= 4 ? GOLD : '#8d9399');
      for (let y = top.y + u * 0.1; y < base.y; y += u * 0.16) line(top.x - u * 0.04, y, top.x + u * 0.04, y + u * 0.08, '#5a5f66', 1);
    });
    const beamY = towers[0].top.y;
    rect(towers[0].top.x - u * 0.04, beamY - u * 0.06, towers[1].top.x - towers[0].top.x + u * 0.08, u * 0.08, tier >= 4 ? GOLD : '#8d9399');
    const lamps = 3 + Math.min(4, tier);
    const beams = ['rgba(186,236,183,0.35)', 'rgba(255,79,163,0.3)', 'rgba(79,209,255,0.3)', 'rgba(255,210,60,0.3)'];
    for (let k = 0; k < lamps; k += 1) {
      const x = lerp(towers[0].top.x, towers[1].top.x, (k + 0.5) / lamps);
      if (live) {
        const sway = Math.sin(clock * 1.6 + k * 1.3) * u * 0.9;
        ctx.save();
        ctx.globalCompositeOperation = 'lighter';
        poly([[x - u * 0.04, beamY + u * 0.04], [x + u * 0.04, beamY + u * 0.04], [x + sway + u * 0.35, deck.m + u * 1.2], [x + sway - u * 0.35, deck.m + u * 1.2]], beams[k % 4]);
        ctx.restore();
      }
      rect(x - u * 0.05, beamY + u * 0.02, u * 0.1, u * 0.08, '#1d1d20');
      circle(x, beamY + u * 0.1, Math.max(1.2, u * 0.03), live ? '#fff7d6' : '#6f6f74');
    }
    // Speaker stacks at the front corners.
    [item.x + 0.05, item.x + 2.95].forEach(x => {
      const at = proj(x, item.y + 1.85);
      speaker(at.x, at.y, u * 0.28, u * (0.5 + tier * 0.06), live ? Math.max(0, Math.sin(clock * 9)) : 0, mat.trim);
    });
    // The crowd in front while the show is on.
    if (live) {
      const n = 5 + tier * 2;
      const rand = mulberry32(item.id * 17);
      for (let i = 0; i < n; i += 1) {
        const at = proj(item.x + 0.4 + rand() * 2.2, item.y + 1.75 + rand() * 0.35);
        crowdFigure(at.x, at.y, u * 0.7, oneOf(rand, WALKER_TOPS), oneOf(rand, SKINS), true);
      }
      if (Math.random() < 0.15) burst(item.x + 0.4 + Math.random() * 2.2, item.y + 1.2, 1.5, 3, [MINT, '#ff4fa3', YELLOW, PAPER], { speed: 1, up: 2, g: 2, size: 0.035, life: 1 });
    }
    if (tier >= 5) starRow(back.l + back.w / 2, back.t - u * 0.15, u * 0.05, stars(L));
  };

  // A rack of shirts on a rail between (l, y) and (r, y).
  const rack = (l, r, y, n, seed) => {
    const u = cw();
    line(l, y, r, y, '#4a4d52', Math.max(1.5, u * 0.025));
    const colours = [SIGNAL, PAPER, INK, '#2f6fff', YELLOW, MINT, '#ff4fa3'];
    for (let i = 0; i < n; i += 1) shirt(lerp(l, r, (i + 0.5) / n), y, Math.min(u * 0.24, ((r - l) / n) * 0.95), colours[(seed + i) % colours.length]);
  };
  // Corner-Stand: a table and a rack under a parasol, then a stall with an awning, then a kiosk.
  const drawStand = item => {
    const u = cw();
    const L = item.level;
    const tier = Math.min(5, tierOf(L));
    const mat = matOf(L);
    const brand = hustlerLook.brand === INK ? SIGNAL : hustlerLook.brand;
    if (tier >= 2) {
      // Kiosk booth: back wall with a rack inside, posts and a sign board.
      const booth = block(item.x + 0.12, item.y + 0.2, 1.76, 0.3, 1.05, mat.roof, tier >= 3 ? '#1d1d20' : mat.wall);
      rack(booth.l + booth.w * 0.1, booth.r - booth.w * 0.1, booth.m + booth.fh * 0.25, 5 + Math.min(2, tier - 2), item.id + L);
      surface(booth, L);
    } else {
      const rail = proj(item.x + 0.3, item.y + 0.4, 0.8);
      const end = proj(item.x + 1.7, item.y + 0.4, 0.8);
      [rail, end].forEach(p => rect(p.x - 1, p.y, 2, proj(item.x, item.y + 0.4).y - p.y, '#4a4d52'));
      rack(rail.x, end.x, rail.y, 4 + tier, item.id + L);
    }
    // The table with a cloth in the brand colour, folded shirts and caps on it, and the card reader.
    const table = block(item.x + 0.2, item.y + 0.5, 1.6, 0.32, 0.38, tier >= 3 ? '#2a2a2e' : '#e9e7e1', brand);
    rect(table.l, table.m + table.fh * 0.7, table.w, table.fh * 0.3, 'rgba(0,0,0,0.15)');
    if (tier >= 1) drawLogo(table.l + table.w / 2, table.m + table.fh * 0.42, table.w * 0.6, PAPER);
    const stacks = [PAPER, INK, YELLOW, MINT];
    for (let i = 0; i < 3; i += 1) {
      const at = table.top(0.18 + i * 0.22, 0.55);
      for (let k = 0; k < 2 + (tier >= 1 ? 1 : 0); k += 1) rect(at.x - u * 0.1, at.y - u * 0.04 * (k + 1), u * 0.2, u * 0.035, stacks[(i + k + L) % 4]);
    }
    cap(table.top(0.82, 0.5).x, table.top(0.82, 0.5).y, u * 0.16, brand, INK);
    const reader = table.top(0.94, 0.4);
    rect(reader.x - u * 0.04, reader.y - u * 0.06, u * 0.08, u * 0.06, INK);
    rect(reader.x - u * 0.03, reader.y - u * 0.05, u * 0.06, u * 0.02, MINT);
    // Shade: a parasol, then a striped awning, a lit sign from tier 2.
    if (tier === 0) {
      const pole = proj(item.x + 1.75, item.y + 0.75);
      const top = proj(item.x + 1.75, item.y + 0.75, 1.3);
      rect(pole.x - 1, top.y, 2, pole.y - top.y, '#4a4d52');
      for (let k = 0; k < 6; k += 1) {
        ctx.beginPath();
        ctx.moveTo(top.x, top.y - u * 0.05);
        ctx.ellipse(top.x, top.y + u * 0.08, u * 0.55, u * 0.16, 0, Math.PI + (k / 6) * Math.PI, Math.PI + ((k + 1) / 6) * Math.PI);
        ctx.closePath();
        ctx.fillStyle = k % 2 ? PAPER : brand;
        ctx.fill();
      }
    } else {
      const a = proj(item.x + 0.1, item.y + 0.85, 1.25);
      const b = proj(item.x + 1.9, item.y + 0.85, 1.25);
      [a, b].forEach(p => rect(p.x - 1.5, p.y, 3, proj(item.x, item.y + 0.85).y - p.y, tier >= 4 ? GOLD : '#4a4d52'));
      const ah = u * 0.18;
      for (let i = 0; i < 10; i += 1) {
        const x0 = lerp(a.x, b.x, i / 10);
        const x1 = lerp(a.x, b.x, (i + 1) / 10);
        poly([[x0, a.y], [x1, a.y], [x1 + 1, a.y + ah], [x0 - 1, a.y + ah]], i % 2 ? PAPER : tier >= 3 ? INK : brand);
        circle((x0 + x1) / 2, a.y + ah, (x1 - x0) / 2, i % 2 ? PAPER : tier >= 3 ? INK : brand);
      }
      if (tier >= 2) {
        const sw = (b.x - a.x) * 0.5;
        const sh = u * 0.2;
        rect((a.x + b.x) / 2 - sw / 2, a.y - sh - 2, sw, sh, INK);
        glowLabel(tier >= 3 && (tier >= 4 ? GOLD : MINT), u * 0.12, 'MERCH', (a.x + b.x) / 2, a.y - sh / 2 - 1.5, sh * 0.62, tier >= 4 ? GOLD : tier >= 3 ? MINT : PAPER);
      }
    }
    if (tier >= 5) starRow(proj(item.x + 1, item.y + 0.85, 1.6).x, proj(item.x + 1, item.y + 0.85, 1.6).y, u * 0.05, stars(L));
  };

  // Pop-up Store: a shipping container with its side cut open; a second one stacked from tier 2.
  const CONTAINER = ['#c8642e', '#2f6fff', '#2e6b4a', '#1d1d20', '#1d1d20', '#141415'];
  const container = (x, y, w, d, h, base, colour, trim) => {
    const u = cw();
    const g = block(x, y, w, d, h, colour, colour, base);
    rect(g.l, g.t, g.w, g.m - g.t, 'rgba(255,255,255,0.12)');
    for (let k = g.l + 2; k < g.r - 1; k += Math.max(3, u * 0.06)) rect(k, g.m, 1, g.fh, 'rgba(0,0,0,0.18)');
    rect(g.l, g.m, g.w, Math.max(1.5, u * 0.03), trim);
    rect(g.l, g.b - Math.max(1.5, u * 0.03), g.w, Math.max(1.5, u * 0.03), trim);
    return g;
  };
  const drawPopup = item => {
    const u = cw();
    const L = item.level;
    const tier = Math.min(5, tierOf(L));
    const mat = matOf(L);
    const colour = CONTAINER[tier];
    const trim = tier >= 4 ? GOLD : tier >= 3 ? MINT : 'rgba(0,0,0,0.25)';
    const g = container(item.x + 0.1, item.y + 0.5, 1.8, 1.05, 0.85, 0, colour, trim);
    // The opening with a lit shop inside.
    const o = { l: g.l + g.w * 0.12, r: g.r - g.w * 0.12, t: g.m + g.fh * 0.16, b: g.b - g.fh * 0.06 };
    rect(o.l, o.t, o.r - o.l, o.b - o.t, '#f3ecdc');
    rect(o.l, o.t, o.r - o.l, (o.b - o.t) * 0.12, 'rgba(0,0,0,0.12)');
    rack(o.l + (o.r - o.l) * 0.08, o.r - (o.r - o.l) * 0.08, o.t + (o.b - o.t) * 0.22, 5, item.id + L);
    rect(o.l, o.b - (o.b - o.t) * 0.12, o.r - o.l, (o.b - o.t) * 0.12, '#c9c1ad');
    // A sign plate over the opening.
    const sw = g.w * 0.5;
    const sh = Math.max(6, g.fh * 0.13);
    rect(g.l + g.w / 2 - sw / 2, g.m + 1, sw, sh, INK);
    glowLabel(tier >= 3 && (tier >= 4 ? GOLD : MINT), u * 0.12, 'POP-UP', g.l + g.w / 2, g.m + 1 + sh / 2, sh * 0.66, tier >= 4 ? GOLD : tier >= 3 ? MINT : PAPER);
    // Queue posts with a rope from tier 1; a planter at the corner.
    if (tier >= 1) {
      const posts = [0.25, 0.75, 1.25].map(fx => proj(item.x + 0.35 + fx, item.y + 1.85));
      posts.forEach(p => {
        rect(p.x - 1.5, p.y - u * 0.26, 3, u * 0.26, tier >= 4 ? GOLD : '#8d9399');
        circle(p.x, p.y - u * 0.27, Math.max(1.5, u * 0.03), tier >= 4 ? GOLD : '#8d9399');
      });
      for (let k = 0; k < posts.length - 1; k += 1) {
        ctx.beginPath();
        ctx.moveTo(posts[k].x, posts[k].y - u * 0.22);
        ctx.quadraticCurveTo((posts[k].x + posts[k + 1].x) / 2, posts[k].y - u * 0.12, posts[k + 1].x, posts[k + 1].y - u * 0.22);
        ctx.strokeStyle = tier >= 3 ? '#6a2c3a' : SIGNAL;
        ctx.lineWidth = Math.max(1.5, u * 0.025);
        ctx.stroke();
      }
    }
    const pot = proj(item.x + 0.2, item.y + 1.85);
    rect(pot.x - u * 0.09, pot.y - u * 0.14, u * 0.18, u * 0.14, '#c9c5bb');
    circle(pot.x, pot.y - u * 0.26, u * 0.13, '#7fb07a');
    circle(pot.x + u * 0.06, pot.y - u * 0.32, u * 0.08, '#9cc98f');
    // Tier 2: a second container on top with a window and a flag.
    if (tier >= 2) {
      const top = container(item.x + 0.25, item.y + 0.55, 1.4, 0.95, 0.65, 0.85, tier >= 3 ? '#2a2a2e' : CONTAINER[(tier + 1) % 3], trim);
      windowRect(top.l + top.w * 0.12, top.m + top.fh * 0.25, top.w * 0.3, top.fh * 0.5, tier >= 3);
      windowRect(top.l + top.w * 0.58, top.m + top.fh * 0.25, top.w * 0.3, top.fh * 0.5, true);
      const mast = top.top(0.85, 0.4);
      rect(mast.x - 1, mast.y - u * 0.55, 2, u * 0.55, INK);
      const wave = Math.sin(clock * 4 + item.id) * u * 0.03;
      poly([[mast.x + 1, mast.y - u * 0.55], [mast.x + u * 0.38, mast.y - u * 0.5 + wave], [mast.x + 1, mast.y - u * 0.32]], hustlerLook.brand === INK ? MINT : hustlerLook.brand);
      if (tier >= 5) starRow(top.l + top.w / 2, top.t - u * 0.15, u * 0.05, stars(L));
    }
    if (mat.neon) glowRect(MINT, u * 0.2, g.l, g.b - 2, g.w, 2, MINT);
  };

  // On-Demand Shop: a studio with a giant phone on the front showing the shop; orders pop up,
  // parcels slide down a chute to the MIXEDOWN van.
  const drawShop = item => {
    const u = cw();
    const L = item.level;
    const tier = Math.min(5, tierOf(L));
    const mat = matOf(L);
    const h = 1.05 + Math.min(0.7, (L - 1) * 0.03);
    const g = workshopShell(item, h);
    const below = signBand(g, 'ON-DEMAND SHOP', L);
    // The phone: body, screen, product tiles, a buy button.
    const ph = Math.min(g.b - below - g.fh * 0.06, g.w * 0.62);
    const pw = ph * 0.56;
    const px = g.l + g.w * 0.08;
    const py = below + (g.b - below - ph) * 0.5;
    rect(px - 2, py - 2, pw + 4, ph + 4, INK);
    rect(px, py, pw, ph, tier >= 3 ? '#16261d' : '#f7f6f2');
    rect(px + pw * 0.35, py + ph * 0.03, pw * 0.3, Math.max(1, ph * 0.02), '#4a4d52');
    const tiles = [SIGNAL, '#2f6fff', YELLOW, MINT];
    for (let k = 0; k < 4; k += 1) {
      const tx = px + pw * (0.1 + (k % 2) * 0.42);
      const ty = py + ph * (0.1 + Math.floor(k / 2) * 0.3);
      rect(tx, ty, pw * 0.38, ph * 0.26, tier >= 3 ? '#1f3d2e' : '#e3e1da');
      shirt(tx + pw * 0.19, ty + ph * 0.05, pw * 0.2, tiles[(k + L) % 4]);
    }
    rect(px + pw * 0.1, py + ph * 0.76, pw * 0.8, ph * 0.12, MINT_D);
    label('KAUFEN', px + pw / 2, py + ph * 0.82, ph * 0.07, INK);
    // An order notification every few seconds.
    const cycle = (clock * 0.5 + item.id * 0.3) % 1;
    if (cycle < 0.5) {
      const rise = cycle * 2;
      ctx.save();
      ctx.globalAlpha = Math.min(1, (1 - rise) * 3);
      const nx = px + pw + u * 0.05;
      const ny = py + ph * 0.3 - rise * u * 0.4;
      rect(nx, ny, u * 0.5, u * 0.2, PAPER);
      rect(nx, ny, u * 0.5, u * 0.2, 'rgba(0,0,0,0.04)');
      drawCash(ctx, nx + u * 0.1, ny + u * 0.1, u * 0.06, false);
      label('+1', nx + u * 0.32, ny + u * 0.1, u * 0.12, INK);
      ctx.restore();
    }
    // The chute and parcels on the right, the van in front.
    const cx = g.l + g.w * 0.72;
    rect(cx, below + g.fh * 0.12, g.w * 0.22, g.b - below - g.fh * 0.12, tier >= 3 ? '#3a3a3f' : '#c9c8c3');
    for (let k = 0; k < 3; k += 1) {
      const p = (clock * 0.4 + k / 3 + item.id * 0.2) % 1;
      const bx = cx + g.w * 0.03;
      const by = lerp(below + g.fh * 0.18, g.b - u * 0.14, p);
      rect(bx, by, g.w * 0.16, u * 0.12, '#c5a679');
      rect(bx, by + u * 0.05, g.w * 0.16, u * 0.025, INK);
    }
    const van = block(item.x + 1.3, item.y + 1.82, 0.62, 0.16, 0.34, PAPER, PAPER, 0.06);
    drawLogo(van.l + van.w * 0.45, van.m + van.fh * 0.45, van.w * 0.7, INK);
    rect(van.r - van.w * 0.22, van.m + van.fh * 0.1, van.w * 0.16, van.fh * 0.4, '#5f7480');
    [0.2, 0.8].forEach(fx => {
      const w = van.at(fx, 1);
      oval(w.x, w.y, u * 0.06, u * 0.045, INK);
    });
    // Roof: an AC unit until the dish arrives at tier 2, blinking server lights from tier 3.
    if (tier < 2) {
      const at = g.top(0.7, 0.62);
      roofUnit(at.x, at.y, u, false);
    }
    if (tier >= 2) {
      const at = g.top(0.25, 0.5);
      rect(at.x - 1, at.y - u * 0.25, 2, u * 0.25, '#4a4d52');
      oval(at.x, at.y - u * 0.3, u * 0.18, u * 0.1, tier >= 4 ? GOLD : '#d9d8d3', -0.5);
    }
    if (tier >= 3) {
      for (let k = 0; k < 4; k += 1) circle(g.l + g.w * (0.75 + k * 0.05), g.m + g.fh * 0.12, Math.max(1, u * 0.02), Math.sin(clock * 6 + k * 1.7) > 0 ? MINT : '#2e6b4a');
    }
    if (tier >= 5) starRow(g.l + g.w / 2, g.t - u * 0.15, u * 0.05, stars(L));
    void mat;
  };

  // Stickers slapped on a face: round, square, some with the ring, seeded so they stay put.
  const stickers = (l, t, w, h, n, seed) => {
    const rand = mulberry32(seed);
    for (let i = 0; i < n; i += 1) {
      const k = Math.min(w, h) * (0.12 + rand() * 0.1);
      const x = l + k * 0.6 + rand() * (w - k * 1.2);
      const y = t + k * 0.6 + rand() * (h - k * 1.2);
      const colour = oneOf(rand, POSTER_COLORS);
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate((rand() - 0.5) * 0.8);
      if (rand() < 0.45) circle(0, 0, k * 0.55, colour);
      else rect(-k * 0.6, -k * 0.4, k * 1.2, k * 0.8, colour);
      if (rand() < 0.5) logoRing(0, 0, k * 0.32, k * 0.16, Math.max(0.6, k * 0.1), colour === INK ? PAPER : INK);
      ctx.restore();
    }
  };
  // Sticker-Spot: a junction box and a lamp post slapped full; the box gets wrapped, the lamp lights up.
  const drawSticker = item => {
    const u = cw();
    const L = item.level;
    const tier = Math.min(5, tierOf(L));
    const mat = matOf(L);
    const brand = hustlerLook.brand === INK ? SIGNAL : hustlerLook.brand;
    const box = block(item.x + 0.15, item.y + 0.45, 0.8, 0.38, 0.72 + tier * 0.04, tier >= 3 ? '#2a2a2e' : '#a3aca4', tier >= 3 ? '#1d1d20' : '#bcc5bd');
    if (tier >= 3) {
      // Wrapped in the brand: one big print, a few stickers on top anyway.
      rect(box.l + 2, box.m + 2, box.w - 4, box.fh - 4, tier >= 5 ? '#141415' : brand);
      drawLogo(box.l + box.w / 2, box.m + box.fh * 0.45, box.w * 0.78, tier >= 4 ? GOLD : PAPER, Math.max(1, u * 0.015));
      stickers(box.l, box.m + box.fh * 0.62, box.w, box.fh * 0.38, 3, item.id * 13 + L);
    } else {
      rect(box.l + box.w * 0.48, box.m + 2, 1, box.fh - 4, 'rgba(0,0,0,0.2)');
      stickers(box.l, box.m, box.w, box.fh, 6 + tier * 4, item.id * 13 + L);
    }
    rect(box.l, box.m - Math.max(1.5, u * 0.03), box.w, Math.max(1.5, u * 0.03), mat.trim);
    // The lamp post, plastered up to head height.
    const foot = proj(item.x + 1.5, item.y + 0.68);
    const top = proj(item.x + 1.5, item.y + 0.68, 1.75 + tier * 0.05);
    const pw = Math.max(3, u * 0.07);
    rect(foot.x - pw / 2, top.y, pw, foot.y - top.y, tier >= 4 ? '#2b2b2e' : '#5a6168');
    const rand = mulberry32(item.id * 29 + L);
    for (let k = 0; k < 4 + tier * 2; k += 1) {
      const y = foot.y - u * (0.12 + rand() * 0.75);
      rect(foot.x - pw * 0.7, y, pw * 1.4, pw * (0.7 + rand() * 0.6), oneOf(rand, POSTER_COLORS));
    }
    line(foot.x, top.y, foot.x - u * 0.28, top.y - u * 0.08, tier >= 4 ? GOLD : '#5a6168', Math.max(1.5, u * 0.035));
    oval(foot.x - u * 0.32, top.y - u * 0.06, u * 0.12, u * 0.05, '#2b2b2e');
    if (tier >= 2) {
      ctx.save();
      ctx.globalAlpha = 0.22;
      poly([[foot.x - u * 0.4, top.y - u * 0.04], [foot.x - u * 0.24, top.y - u * 0.04], [foot.x - u * 0.05, foot.y], [foot.x - u * 0.6, foot.y]], '#fff2b8');
      ctx.restore();
    }
    // A box of fresh stickers on the ground, a whole crate later.
    const crate = proj(item.x + 1.05, item.y + 0.95);
    const cw2 = u * (tier >= 2 ? 0.3 : 0.2);
    rect(crate.x - cw2 / 2, crate.y - u * 0.12, cw2, u * 0.12, '#c5a679');
    for (let k = 0; k < 3; k += 1) circle(crate.x - cw2 * 0.3 + k * cw2 * 0.3, crate.y - u * 0.13, u * 0.035, [brand, PAPER, YELLOW][k]);
    if (mat.neon) glowRect(MINT, u * 0.2, box.l, box.b - 2, box.w, 2, MINT);
    if (tier >= 5) starRow(box.l + box.w / 2, box.t - u * 0.15, u * 0.05, stars(L));
  };

  // Content-Studio: a glass box with a ring light, a camera on a tripod and a creator going live.
  const drawStudio = item => {
    const u = cw();
    const L = item.level;
    const tier = Math.min(5, tierOf(L));
    const mat = matOf(L);
    const h = 1 + Math.min(0.6, (L - 1) * 0.025);
    const g = workshopShell(item, h);
    const below = signBand(g, 'STUDIO', L);
    // The set: a backdrop in the brand colour, the creator, the ring light, the camera.
    const s = { l: g.l + g.w * 0.08, r: g.r - g.w * 0.08, t: below + g.fh * 0.05, b: g.b - g.fh * 0.05 };
    rect(s.l, s.t, s.r - s.l, s.b - s.t, tier >= 3 ? '#16261d' : '#f3ecdc');
    const brand = hustlerLook.brand === INK ? SIGNAL : hustlerLook.brand;
    rect(s.l + (s.r - s.l) * 0.35, s.t, (s.r - s.l) * 0.4, (s.b - s.t) * 0.85, tier >= 3 ? '#1f3d2e' : brand);
    if (tier >= 1) drawLogo(s.l + (s.r - s.l) * 0.55, s.t + (s.b - s.t) * 0.25, (s.r - s.l) * 0.32, tier >= 4 ? GOLD : PAPER);
    const cx = s.l + (s.r - s.l) * 0.55;
    const pose = Math.sin(clock * 3 + item.id);
    crowdFigure(cx, s.b - u * 0.02, u * 0.62, oneOf(mulberry32(item.id + L), [SIGNAL, MINT, YELLOW, '#2f6fff']), SKINS[item.id % SKINS.length], pose > 0.6);
    const ring = { x: cx - (s.r - s.l) * 0.28, y: s.b - u * 0.55 };
    rect(ring.x - 1, ring.y, 2, s.b - ring.y, '#4a4d52');
    ctx.save();
    if (tier >= 2) {
      ctx.shadowColor = tier >= 5 ? MINT : '#fff7d6';
      ctx.shadowBlur = u * 0.25;
    }
    ctx.beginPath();
    ctx.arc(ring.x, ring.y, u * 0.16, 0, TAU);
    ctx.strokeStyle = tier >= 5 ? MINT : '#fff7d6';
    ctx.lineWidth = Math.max(2, u * 0.05);
    ctx.stroke();
    ctx.restore();
    const lens = { x: cx + (s.r - s.l) * 0.3, y: s.b - u * 0.3 };
    line(lens.x, lens.y, lens.x - u * 0.08, s.b, '#4a4d52', 1.5);
    line(lens.x, lens.y, lens.x + u * 0.08, s.b, '#4a4d52', 1.5);
    rect(lens.x - u * 0.09, lens.y - u * 0.08, u * 0.18, u * 0.11, INK);
    circle(lens.x - u * 0.1, lens.y - u * 0.025, u * 0.04, '#3a3d42');
    if (Math.sin(clock * 4) > 0) circle(lens.x + u * 0.06, lens.y - u * 0.06, Math.max(1, u * 0.015), SIGNAL);
    // LIVE badge with a viewer count that only goes up, hearts floating off the stream.
    const lx = s.l + u * 0.04;
    const ly = s.t + u * 0.04;
    rect(lx, ly, u * 0.3, u * 0.13, SIGNAL);
    label('LIVE', lx + u * 0.15, ly + u * 0.068, u * 0.09, PAPER);
    label(fmt(Math.floor(40 + L * 25 + (clock * (2 + L)) % 900)), lx + u * 0.15, ly + u * 0.22, u * 0.09, tier >= 3 ? PAPER : INK);
    for (let k = 0; k < 2 + Math.min(3, tier); k += 1) {
      const p = (clock * 0.5 + k / (2 + Math.min(3, tier)) + item.id * 0.1) % 1;
      ctx.save();
      ctx.globalAlpha = 1 - p;
      const hx = s.r - u * 0.12 + Math.sin(p * 7 + k) * u * 0.05;
      const hy = s.b - u * 0.1 - p * (s.b - s.t) * 0.8;
      circle(hx - u * 0.025, hy, u * 0.03, ['#ff4fa3', SIGNAL, YELLOW][k % 3]);
      circle(hx + u * 0.025, hy, u * 0.03, ['#ff4fa3', SIGNAL, YELLOW][k % 3]);
      poly([[hx - u * 0.055, hy + u * 0.005], [hx + u * 0.055, hy + u * 0.005], [hx, hy + u * 0.06]], ['#ff4fa3', SIGNAL, YELLOW][k % 3]);
      ctx.restore();
    }
    // Roof: an antenna from tier 2, a second camera rig from tier 4.
    if (tier >= 2) {
      const at = g.top(0.8, 0.5);
      rect(at.x - 1, at.y - u * 0.4, 2, u * 0.4, '#4a4d52');
      circle(at.x, at.y - u * 0.42, Math.max(1.5, u * 0.03), Math.sin(clock * 5) > 0 ? SIGNAL : '#6a2c3a');
    }
    if (tier >= 4) {
      const at = g.top(0.3, 0.5);
      rect(at.x - u * 0.12, at.y - u * 0.14, u * 0.24, u * 0.14, GOLD);
      circle(at.x - u * 0.13, at.y - u * 0.07, u * 0.05, INK);
    }
    if (tier >= 5) starRow(g.l + g.w / 2, g.t - u * 0.15, u * 0.05, stars(L));
    void mat;
  };

  // LED-Billboard: a board on stilts; paper first, lit later, then a screen running the drops.
  const drawBillboard = item => {
    const u = cw();
    const L = item.level;
    const tier = Math.min(5, tierOf(L));
    const mat = matOf(L);
    const lift = 0.7 + tier * 0.08;
    const board = block(item.x + 0.05, item.y + 0.45, 1.9, 0.18, lift + 0.9 + tier * 0.05, mat.roof, tier >= 3 ? '#141415' : PAPER, lift);
    [0.25, 0.75].forEach(fx => {
      const x = board.l + board.w * fx;
      const ground = proj(item.x + 0.05 + 1.9 * fx, item.y + 0.63).y;
      rect(x - u * 0.04, board.b, u * 0.08, ground - board.b, tier >= 4 ? GOLD : '#5a6168');
      if (tier >= 1) for (let y = board.b + u * 0.1; y < ground - u * 0.05; y += u * 0.14) line(x - u * 0.04, y, x + u * 0.04, y + u * 0.07, 'rgba(0,0,0,0.3)', 1);
    });
    const pad = board.w * 0.03;
    const sx = board.l + pad;
    const sy = board.m + pad;
    const sw = board.w - pad * 2;
    const sh = board.fh - pad * 2;
    const brand = hustlerLook.brand === INK ? SIGNAL : hustlerLook.brand;
    if (tier < 3) {
      // A pasted poster: the logo, a shirt, the drop date.
      rect(sx, sy, sw, sh, tier >= 1 ? brand : '#e9e7e1');
      shirt(sx + sw * 0.18, sy + sh * 0.15, sh * 0.6, tier >= 1 ? PAPER : brand);
      drawLogo(sx + sw * 0.62, sy + sh * 0.4, sw * 0.55, tier >= 1 ? PAPER : INK);
      label('DROP FR 18 UHR', sx + sw * 0.62, sy + sh * 0.75, sh * 0.16, tier >= 1 ? PAPER : INK);
    } else {
      // The screen cycles: logo, a product, a countdown.
      const slide = Math.floor((clock / 2.5 + item.id) % 3);
      rect(sx, sy, sw, sh, slide === 1 ? brand : '#141415');
      ctx.save();
      if (tier >= 4) {
        ctx.shadowColor = tier >= 5 ? MINT : GOLD;
        ctx.shadowBlur = u * 0.15;
      }
      if (slide === 0) drawLogo(sx + sw / 2, sy + sh * 0.45, sw * 0.8, tier >= 5 ? MINT : PAPER);
      else if (slide === 1) {
        shirt(sx + sw * 0.25, sy + sh * 0.12, sh * 0.7, PAPER);
        label('NEW DROP', sx + sw * 0.66, sy + sh * 0.5, sh * 0.22, PAPER);
      } else label(`00:${String(59 - Math.floor(clock % 60)).padStart(2, '0')}`, sx + sw / 2, sy + sh * 0.5, sh * 0.45, tier >= 4 ? GOLD : MINT);
      ctx.restore();
      for (let y = sy; y < sy + sh; y += 3) rect(sx, y, sw, 1, 'rgba(0,0,0,0.12)');
    }
    // Lamps over the board from tier 1, a catwalk from tier 2.
    if (tier >= 1 && tier < 3) {
      [0.2, 0.5, 0.8].forEach(fx => {
        const x = board.l + board.w * fx;
        line(x, board.m, x, board.m - u * 0.12, '#4a4d52', 1.5);
        oval(x, board.m - u * 0.13, u * 0.06, u * 0.03, '#2b2b2e');
        if (tier >= 2) {
          ctx.save();
          ctx.globalAlpha = 0.2;
          poly([[x - u * 0.05, board.m - u * 0.12], [x + u * 0.05, board.m - u * 0.12], [x + u * 0.18, board.m + board.fh * 0.6], [x - u * 0.18, board.m + board.fh * 0.6]], '#fff2b8');
          ctx.restore();
        }
      });
    }
    if (tier >= 2) rect(board.l - u * 0.05, board.b, board.w + u * 0.1, Math.max(2, u * 0.04), tier >= 4 ? GOLD : '#4a4d52');
    if (mat.neon) glowRect(MINT, u * 0.25, board.l, board.m - 2, board.w, 2, MINT);
    if (tier >= 5) starRow(board.l + board.w / 2, board.t - u * 0.15, u * 0.05, stars(L));
  };

  // Merch-Truck: a van with the side hatch up, a counter full of merch and a menu board.
  const drawTruck = item => {
    const u = cw();
    const L = item.level;
    const tier = Math.min(5, tierOf(L));
    const mat = matOf(L);
    const brand = hustlerLook.brand === INK ? SIGNAL : hustlerLook.brand;
    const body = tier >= 3 ? '#1d1d20' : tier >= 1 ? brand : '#e9e7e1';
    const van = block(item.x + 0.1, item.y + 0.3, 1.45, 0.5, 0.82 + tier * 0.03, tier >= 3 ? '#2a2a2e' : '#d6d5cf', body, 0.12);
    const cab = block(item.x + 1.55, item.y + 0.38, 0.38, 0.42, 0.62, tier >= 3 ? '#2a2a2e' : '#d6d5cf', body, 0.12);
    rect(cab.l + cab.w * 0.12, cab.m + cab.fh * 0.12, cab.w * 0.76, cab.fh * 0.38, '#5f7480');
    // The hatch: open window, lit inside, merch on a rail, the counter in front.
    const o = { l: van.l + van.w * 0.1, r: van.r - van.w * 0.1, t: van.m + van.fh * 0.14, b: van.m + van.fh * 0.62 };
    rect(o.l, o.t, o.r - o.l, o.b - o.t, '#f3ecdc');
    rack(o.l + (o.r - o.l) * 0.06, o.r - (o.r - o.l) * 0.06, o.t + (o.b - o.t) * 0.15, 4 + Math.min(2, tier), item.id + L);
    rect(o.l - u * 0.03, o.b, o.r - o.l + u * 0.06, Math.max(2, u * 0.05), tier >= 4 ? GOLD : '#c9c8c3');
    // The flap up top, striped like an awning.
    const flap = u * 0.14;
    for (let i = 0; i < 8; i += 1) {
      const x0 = lerp(o.l - u * 0.05, o.r + u * 0.05, i / 8);
      const x1 = lerp(o.l - u * 0.05, o.r + u * 0.05, (i + 1) / 8);
      poly([[x0, o.t], [x1, o.t], [x1 + 1, o.t - flap], [x0 - 1, o.t - flap]], i % 2 ? PAPER : tier >= 3 ? MINT_D : INK);
    }
    if (tier >= 1) drawLogo(van.l + van.w / 2, van.m + van.fh * 0.8, van.w * 0.5, tier >= 3 ? (tier >= 4 ? GOLD : MINT) : PAPER);
    [[van, 0.2], [van, 0.75], [cab, 0.55]].forEach(([box, fx]) => {
      const w = box.at(fx, 1);
      oval(w.x, w.y, u * 0.1, u * 0.08, INK);
      oval(w.x, w.y - 1, u * 0.045, u * 0.03, tier >= 4 ? GOLD : '#8d9399');
    });
    // A chalk board on the pavement, string lights from tier 2, a roof sign from tier 3.
    const board = proj(item.x + 1.75, item.y + 0.98);
    poly([[board.x - u * 0.12, board.y], [board.x - u * 0.08, board.y - u * 0.34], [board.x + u * 0.08, board.y - u * 0.34], [board.x + u * 0.12, board.y]], '#2b2b2e');
    for (let k = 0; k < 3; k += 1) rect(board.x - u * 0.06, board.y - u * (0.28 - k * 0.07), u * (0.12 - k * 0.02), Math.max(1, u * 0.015), PAPER);
    if (tier >= 2) for (let k = 0; k < 7; k += 1) circle(lerp(van.l, van.r, k / 6), van.m - u * 0.02 + Math.sin((k / 6) * Math.PI) * u * 0.05, Math.max(1.2, u * 0.022), (k + Math.floor(clock * 3)) % 2 ? '#ffe9a8' : '#fff7d6');
    if (tier >= 3) {
      const at = van.top(0.5, 0.5);
      rect(at.x - u * 0.35, at.y - u * 0.2, u * 0.7, u * 0.16, INK);
      glowLabel(tier >= 4 ? GOLD : MINT, u * 0.12, 'MERCH', at.x, at.y - u * 0.12, u * 0.11, tier >= 4 ? GOLD : MINT);
    }
    if (tier >= 5) starRow(van.l + van.w / 2, van.t - u * 0.3, u * 0.05, stars(L));
    void mat;
  };

  // Merch-Zelt: the festival tent, a peaked roof, tables piled high, flags on the poles.
  const drawZelt = item => {
    const u = cw();
    const L = item.level;
    const tier = Math.min(5, tierOf(L));
    const mat = matOf(L);
    const brand = hustlerLook.brand === INK ? SIGNAL : hustlerLook.brand;
    const cloth = tier >= 3 ? '#1d1d20' : PAPER;
    const stripe = tier >= 4 ? GOLD : tier >= 3 ? MINT_D : brand;
    const h = 0.95 + tier * 0.05;
    const back = block(item.x + 0.12, item.y + 0.3, 1.76, 1.2, h, cloth, tier >= 3 ? '#2a2a2e' : '#e9e7e1');
    // Inside: racks and a merch wall.
    rack(back.l + back.w * 0.1, back.r - back.w * 0.1, back.m + back.fh * 0.22, 6, item.id + L);
    // The peaked roof in stripes.
    const peak = proj(item.x + 1, item.y + 0.9, h + 0.55);
    const left = { x: back.l - u * 0.08, y: back.m };
    const right = { x: back.r + u * 0.08, y: back.m };
    const n = 8;
    for (let i = 0; i < n; i += 1) {
      const a = lerp(left.x, right.x, i / n);
      const b = lerp(left.x, right.x, (i + 1) / n);
      poly([[peak.x, peak.y], [a, left.y], [b, left.y]], i % 2 ? cloth : stripe);
    }
    for (let i = 0; i < 10; i += 1) {
      const x0 = lerp(left.x, right.x, i / 10);
      const x1 = lerp(left.x, right.x, (i + 1) / 10);
      circle((x0 + x1) / 2, left.y, (x1 - x0) / 2, i % 2 ? cloth : stripe);
    }
    // The flag on the peak.
    rect(peak.x - 1, peak.y - u * 0.4, 2, u * 0.4, INK);
    const wave = Math.sin(clock * 4 + item.id) * u * 0.03;
    poly([[peak.x + 1, peak.y - u * 0.4], [peak.x + u * 0.36, peak.y - u * 0.34 + wave], [peak.x + 1, peak.y - u * 0.22]], tier >= 4 ? GOLD : brand);
    // The counter: tables with stacks, the banner, the card reader.
    const table = block(item.x + 0.25, item.y + 1.45, 1.5, 0.35, 0.36, tier >= 3 ? '#2a2a2e' : '#e9e7e1', INK);
    label('MERCH', table.l + table.w / 2, table.m + table.fh * 0.5, table.fh * 0.5, tier >= 4 ? GOLD : tier >= 3 ? MINT : PAPER);
    for (let i = 0; i < 4; i += 1) {
      const at = table.top(0.14 + i * 0.24, 0.5);
      for (let k = 0; k < 2 + Math.min(2, tier); k += 1) rect(at.x - u * 0.1, at.y - u * 0.035 * (k + 1), u * 0.2, u * 0.03, POSTER_COLORS[(i + k + L) % 7]);
    }
    // Festoon lights from tier 2, a crowd waiting from tier 1.
    if (tier >= 2) {
      for (let k = 0; k <= 8; k += 1) {
        const p = k / 8;
        circle(lerp(left.x, peak.x, p), lerp(left.y, peak.y, p) + u * 0.04, Math.max(1.2, u * 0.022), (k + Math.floor(clock * 3)) % 2 ? '#ffe9a8' : '#fff7d6');
        circle(lerp(peak.x, right.x, p), lerp(peak.y, right.y, p) + u * 0.04, Math.max(1.2, u * 0.022), (k + Math.floor(clock * 3) + 1) % 2 ? '#ffe9a8' : '#fff7d6');
      }
    }
    if (tier >= 1) {
      const rand = mulberry32(item.id * 7);
      for (let k = 0; k < Math.min(4, tier + 1); k += 1) {
        const at = proj(item.x + 0.35 + k * 0.4, item.y + 1.95);
        crowdFigure(at.x, at.y, u * 0.5, oneOf(rand, POSTER_COLORS), oneOf(rand, SKINS), tier >= 3);
      }
    }
    if (mat.neon) glowRect(MINT, u * 0.2, table.l, table.b - 2, table.w, 2, MINT);
    if (tier >= 5) starRow(peak.x, peak.y - u * 0.55, u * 0.05, stars(L));
  };

  // Flagship Store: a real shop on the high street; glass front, mannequins, the logo across the top.
  const drawFlagship = item => {
    const u = cw();
    const L = item.level;
    const tier = Math.min(5, tierOf(L));
    const mat = matOf(L);
    const h = 1.6 + Math.min(1, tier * 0.18 + (L - 1) * 0.01);
    const g = block(item.x + 0.1, item.y + 0.25, 2.8, 1.5, h, mat.roof, mat.wall);
    surface(g, L);
    roofEdge(g, L);
    // Upper floor windows, the logo band, then the shop window and the door.
    const band = g.m + g.fh * 0.36;
    for (let k = 0; k < 5; k += 1) windowRect(g.l + g.w * (0.06 + k * 0.19), g.m + g.fh * 0.06, g.w * 0.13, g.fh * 0.16, tier >= 2 || (k + item.id) % 2 === 0);
    rect(g.l, band - g.fh * 0.12, g.w, g.fh * 0.12, tier >= 3 ? '#0c0c0d' : INK);
    glowLogo((tier >= 3) && (tier >= 4 ? GOLD : MINT), u * 0.15, g.l + g.w / 2, band - g.fh * 0.06, g.w * 0.45, tier >= 4 ? GOLD : tier >= 3 ? MINT : PAPER);
    const win = { l: g.l + g.w * 0.05, r: g.r - g.w * 0.05, t: band + g.fh * 0.04, b: g.b - g.fh * 0.04 };
    rect(win.l - 2, win.t - 2, win.r - win.l + 4, win.b - win.t + 4, tier >= 4 ? GOLD : mat.trim);
    rect(win.l, win.t, win.r - win.l, win.b - win.t, '#f6f1e6');
    // Mannequins in the window, each in a different piece.
    const rand = mulberry32(item.id * 31 + tier);
    const doorL = lerp(win.l, win.r, 0.42);
    const doorR = lerp(win.l, win.r, 0.58);
    [[win.l, doorL], [doorR, win.r]].forEach(([a, b]) => {
      for (let k = 0; k < 2; k += 1) {
        const x = lerp(a, b, (k + 0.5) / 2);
        const foot = win.b - u * 0.04;
        rect(x - 1, foot - u * 0.1, 2, u * 0.1, '#8d9399');
        oval(x, foot, u * 0.08, u * 0.02, '#8d9399');
        shirt(x, foot - u * 0.42, u * 0.24, oneOf(rand, [INK, SIGNAL, MINT, '#2f6fff', YELLOW, PAPER]));
        circle(x, foot - u * 0.48, u * 0.06, '#d9d8d3');
      }
    });
    // Glass shine and the door.
    ctx.save();
    ctx.beginPath();
    ctx.rect(win.l, win.t, win.r - win.l, win.b - win.t);
    ctx.clip();
    line(win.l + (win.r - win.l) * 0.1, win.b + 4, win.l + (win.r - win.l) * 0.25, win.t - 4, 'rgba(255,255,255,0.35)', Math.max(2, u * 0.08));
    ctx.restore();
    rect(doorL, win.t, doorR - doorL, win.b - win.t, tier >= 3 ? '#1f3d2e' : '#5f7480');
    rect((doorL + doorR) / 2 - 0.5, win.t, 1, win.b - win.t, 'rgba(255,255,255,0.4)');
    // Planters by the door, a queue rope from tier 2, flags on the roof from tier 3.
    [0.08, 0.92].forEach(fx => {
      const at = proj(item.x + 0.1 + 2.8 * fx, item.y + 1.92);
      rect(at.x - u * 0.09, at.y - u * 0.16, u * 0.18, u * 0.16, tier >= 4 ? '#2a2a2e' : '#c9c5bb');
      circle(at.x, at.y - u * 0.28, u * 0.13, '#7fb07a');
    });
    if (tier >= 2) {
      const posts = [0.6, 1.1, 1.6].map(fx => proj(item.x + fx, item.y + 1.95));
      posts.forEach(p => rect(p.x - 1.5, p.y - u * 0.24, 3, u * 0.24, tier >= 4 ? GOLD : '#8d9399'));
      for (let k = 0; k < posts.length - 1; k += 1) {
        ctx.beginPath();
        ctx.moveTo(posts[k].x, posts[k].y - u * 0.2);
        ctx.quadraticCurveTo((posts[k].x + posts[k + 1].x) / 2, posts[k].y - u * 0.1, posts[k + 1].x, posts[k + 1].y - u * 0.2);
        ctx.strokeStyle = '#6a2c3a';
        ctx.lineWidth = Math.max(1.5, u * 0.025);
        ctx.stroke();
      }
    }
    if (tier >= 3) {
      [0.2, 0.8].forEach((fx, i) => {
        const at = g.top(fx, 0.5);
        rect(at.x - 1, at.y - u * 0.5, 2, u * 0.5, INK);
        const wave = Math.sin(clock * 4 + i + item.id) * u * 0.03;
        poly([[at.x + 1, at.y - u * 0.5], [at.x + u * 0.34, at.y - u * 0.45 + wave], [at.x + 1, at.y - u * 0.3]], tier >= 4 ? GOLD : MINT);
      });
    }
    if (tier >= 5) starRow(g.l + g.w / 2, g.t - u * 0.15, u * 0.05, stars(L));
  };

  // Merch-Automat: a vending machine on the corner, caps and socks behind the glass; one drops now and then.
  const drawAutomat = item => {
    const u = cw();
    const L = item.level;
    const tier = Math.min(5, tierOf(L));
    const mat = matOf(L);
    const brand = hustlerLook.brand === INK ? SIGNAL : hustlerLook.brand;
    const body = [PAPER, brand, '#cdd2d7', '#1d1d20', '#1d1d20', '#141415'][tier];
    const g = block(item.x + 0.12, item.y + 0.32, 0.76, 0.4, 1.05 + Math.min(0.2, tier * 0.04), tier >= 3 ? '#2a2a2e' : '#dddcd6', body);
    const trim = tier >= 4 ? GOLD : tier >= 3 ? MINT : 'rgba(0,0,0,0.25)';
    // The header: a plain strip first, then lit letters.
    const head = { t: g.m + g.fh * 0.03, h: g.fh * 0.1 };
    rect(g.l + g.w * 0.06, head.t, g.w * 0.88, head.h, tier >= 1 ? INK : brand);
    glowLabel(tier >= 3 && (tier >= 4 ? GOLD : MINT), u * 0.12, 'MERCH', g.l + g.w / 2, head.t + head.h / 2 + 0.5, head.h * 0.7, tier >= 4 ? GOLD : tier >= 3 ? MINT : PAPER);
    // The glass: four shelves of merch, lit from tier 2.
    const glass = { l: g.l + g.w * 0.07, r: g.l + g.w * 0.66, t: head.t + head.h + g.fh * 0.04, b: g.m + g.fh * 0.72 };
    rect(glass.l - 1.5, glass.t - 1.5, glass.r - glass.l + 3, glass.b - glass.t + 3, trim);
    rect(glass.l, glass.t, glass.r - glass.l, glass.b - glass.t, tier >= 2 ? '#fff7e2' : '#dfe4e6');
    const rows = 4;
    const cols = 3;
    const gw = glass.r - glass.l;
    const rh = (glass.b - glass.t) / rows;
    const goods = [PAPER, INK, YELLOW, MINT_D, '#2f6fff', '#ff4fa3', brand];
    for (let r = 0; r < rows; r += 1) {
      const shelf = glass.t + rh * (r + 1) - Math.max(1, u * 0.015);
      rect(glass.l, shelf, gw, Math.max(1, u * 0.015), '#8d9399');
      for (let c = 0; c < cols; c += 1) {
        const x = glass.l + gw * ((c + 0.5) / cols);
        const colour = goods[(r * 3 + c + item.id) % goods.length];
        const k = Math.min(gw / cols, rh) * 0.7;
        if (r === 0) cap(x, shelf - 1, k, colour, INK);
        else if (r === 3) rect(x - k * 0.25, shelf - k * 0.75, k * 0.5, k * 0.75, colour);
        else rect(x - k * 0.45, shelf - k * 0.5, k * 0.9, k * 0.5, colour);
      }
    }
    // Every few seconds a piece drops down to the tray.
    const p = (clock * 0.3 + item.id * 0.37) % 1;
    if (p < 0.18) {
      const fall = p / 0.18;
      const k = Math.min(gw / cols, rh) * 0.55;
      rect(glass.l + gw * 0.5 - k / 2, lerp(glass.t + rh, glass.b - k * 0.6, fall * fall), k, k * 0.55, goods[(item.id + Math.floor(clock * 0.3)) % goods.length]);
    }
    ctx.save();
    ctx.beginPath();
    ctx.rect(glass.l, glass.t, gw, glass.b - glass.t);
    ctx.clip();
    line(glass.l + gw * 0.15, glass.b + 3, glass.l + gw * 0.45, glass.t - 3, 'rgba(255,255,255,0.45)', Math.max(1.5, u * 0.05));
    ctx.restore();
    // The panel: a little screen, the keypad and the card reader.
    const px = g.l + g.w * 0.72;
    const pw = g.w * 0.22;
    rect(px, glass.t, pw, g.fh * 0.12, tier >= 3 ? '#16261d' : '#5f7480');
    if (tier >= 2) rect(px + pw * 0.15, glass.t + g.fh * 0.04, pw * 0.7, Math.max(1, g.fh * 0.03), MINT);
    for (let r = 0; r < 3; r += 1) {
      for (let c = 0; c < 2; c += 1) rect(px + pw * (0.12 + c * 0.46), glass.t + g.fh * (0.18 + r * 0.07), pw * 0.32, g.fh * 0.045, tier >= 3 ? '#3a3d42' : '#8d9399');
    }
    rect(px + pw * 0.2, glass.t + g.fh * 0.42, pw * 0.6, g.fh * 0.06, INK);
    rect(px + pw * 0.3, glass.t + g.fh * 0.44, pw * 0.4, g.fh * 0.02, Math.sin(clock * 3 + item.id) > 0 ? MINT : '#3a3d42');
    // The tray at the bottom.
    rect(g.l + g.w * 0.1, g.m + g.fh * 0.78, g.w * 0.56, g.fh * 0.12, '#0c0c0d');
    rect(g.l + g.w * 0.1, g.m + g.fh * 0.78, g.w * 0.56, Math.max(1, g.fh * 0.02), trim);
    if (tier === 0) stickers(g.l + g.w * 0.66, g.m + g.fh * 0.62, g.w * 0.32, g.fh * 0.36, 3, item.id * 19);
    if (mat.neon) glowRect(MINT, u * 0.2, g.l, g.b - 2, g.w, 2, MINT);
    if (tier >= 5) starRow(g.l + g.w / 2, g.t - u * 0.15, u * 0.045, stars(L));
  };

  // A bike leaning on its stand, seen from the side.
  const bike = (x, y, s, colour) => {
    const r = s * 0.2;
    ctx.lineWidth = Math.max(1, s * 0.035);
    ctx.strokeStyle = INK;
    [x - s * 0.3, x + s * 0.3].forEach(cx => {
      ctx.beginPath();
      ctx.arc(cx, y - r, r, 0, TAU);
      ctx.stroke();
    });
    const seat = { x: x - s * 0.08, y: y - r * 2.6 };
    const bar = { x: x + s * 0.2, y: y - r * 2.8 };
    [[x - s * 0.3, y - r, x, y - r], [x, y - r, seat.x, seat.y], [seat.x, seat.y, x - s * 0.3, y - r], [seat.x, seat.y, bar.x, bar.y], [x, y - r, bar.x, bar.y], [bar.x, bar.y, x + s * 0.3, y - r]].forEach(([x0, y0, x1, y1]) => line(x0, y0, x1, y1, colour, Math.max(1.2, s * 0.045)));
    rect(seat.x - s * 0.07, seat.y - s * 0.03, s * 0.14, s * 0.04, INK);
    line(bar.x, bar.y, bar.x + s * 0.06, bar.y - s * 0.05, INK, Math.max(1.2, s * 0.04));
  };
  // Concept Store: the ground floor of an old building; a big window with a few chosen pieces on plinths,
  // a bench and a bike out front. Brick first, then plaster, concrete, black with neon and gold.
  const drawConcept = item => {
    const u = cw();
    const L = item.level;
    const tier = Math.min(5, tierOf(L));
    const mat = matOf(L);
    const brand = hustlerLook.brand === INK ? SIGNAL : hustlerLook.brand;
    const h = 1.35 + Math.min(0.45, tier * 0.07 + (L - 1) * 0.004);
    const g = block(item.x + 0.1, item.y + 0.3, 1.8, 1.2, h, tier === 0 ? '#8f4b33' : mat.roof, tier === 0 ? '#b8573a' : mat.wall);
    if (tier === 0) bricks(g.l, g.m, g.w, g.fh, 'rgba(0,0,0,0.16)');
    else surface(g, L);
    roofEdge(g, L);
    // Upper floor: two tall windows with plants on the sills.
    [0.14, 0.62].forEach((fx, i) => {
      const wx = g.l + g.w * fx;
      const ww = g.w * 0.24;
      windowRect(wx, g.m + g.fh * 0.07, ww, g.fh * 0.25, tier >= 2 || (i + item.id) % 2 === 0);
      rect(wx - 1, g.m + g.fh * 0.32, ww + 2, Math.max(1.5, u * 0.03), mat.trim);
      [0.25, 0.5, 0.75].forEach(k => circle(wx + ww * k, g.m + g.fh * 0.31, ww * 0.12, k === 0.5 ? '#9cc98f' : '#7fb07a'));
    });
    // The name across the facade, painted, then lit.
    const nameY = g.m + g.fh * 0.43;
    rect(g.l, g.m + g.fh * 0.36, g.w, Math.max(2, u * 0.04), tier === 0 ? '#8f4b33' : mat.trim);
    glowLabel(tier >= 3 && (tier >= 4 ? GOLD : MINT), u * 0.15, 'CONCEPT STORE', g.l + g.w / 2, nameY, g.fh * 0.075, tier >= 4 ? GOLD : tier >= 3 ? MINT : tier === 0 ? PAPER : INK, 'center', 600);
    // The shop floor: one big window on the left, the door on the right.
    const win = { l: g.l + g.w * 0.06, r: g.l + g.w * 0.66, t: g.m + g.fh * 0.5, b: g.b - g.fh * 0.05 };
    rect(win.l - 2, win.t - 2, win.r - win.l + 4, win.b - win.t + 4, tier >= 4 ? GOLD : tier >= 3 ? '#0c0c0d' : '#3a3d42');
    rect(win.l, win.t, win.r - win.l, win.b - win.t, tier >= 3 ? '#efe7d6' : '#f6f1e6');
    const ww = win.r - win.l;
    const floorY = win.b - (win.b - win.t) * 0.08;
    rect(win.l, floorY, ww, win.b - floorY, '#c9c1ad');
    if (tier >= 2) {
      [0.2, 0.5, 0.8].forEach(fx => {
        ctx.save();
        ctx.globalAlpha = 0.35;
        poly([[win.l + ww * fx - u * 0.02, win.t], [win.l + ww * fx + u * 0.02, win.t], [win.l + ww * fx + u * 0.12, floorY], [win.l + ww * fx - u * 0.12, floorY]], '#fff2b8');
        ctx.restore();
      });
    }
    // Three pieces, each on its own: a cap on a plinth, a shirt on a hanger, a box on a plinth.
    const plinth = (x, w, ph) => {
      rect(x - w / 2, floorY - ph, w, ph, tier >= 3 ? '#2a2a2e' : PAPER);
      rect(x - w / 2, floorY - ph, w, Math.max(1, u * 0.015), 'rgba(0,0,0,0.12)');
      return floorY - ph;
    };
    const capTop = plinth(win.l + ww * 0.2, ww * 0.18, (win.b - win.t) * 0.3);
    cap(win.l + ww * 0.2, capTop - 1, ww * 0.13, brand, INK);
    const hang = win.t + (win.b - win.t) * 0.12;
    line(win.l + ww * 0.5, win.t, win.l + ww * 0.5, hang, '#8d9399', 1);
    shirt(win.l + ww * 0.5, hang + 1, Math.min(ww * 0.2, u * 0.28), [INK, PAPER, MINT_D, '#2f6fff', YELLOW, brand][(item.id + tier) % 6]);
    const boxTop = plinth(win.l + ww * 0.8, ww * 0.16, (win.b - win.t) * 0.2);
    rect(win.l + ww * 0.8 - ww * 0.06, boxTop - ww * 0.07, ww * 0.12, ww * 0.07, tier >= 4 ? GOLD : SIGNAL);
    ctx.save();
    ctx.beginPath();
    ctx.rect(win.l, win.t, ww, win.b - win.t);
    ctx.clip();
    line(win.l + ww * 0.08, win.b + 4, win.l + ww * 0.24, win.t - 4, 'rgba(255,255,255,0.4)', Math.max(2, u * 0.07));
    ctx.restore();
    const gate = { l: g.l + g.w * 0.72, r: g.l + g.w * 0.92, t: win.t };
    rect(gate.l - 1.5, gate.t - 1.5, gate.r - gate.l + 3, g.b - gate.t + 1.5, tier >= 4 ? GOLD : '#3a3d42');
    rect(gate.l, gate.t, gate.r - gate.l, g.b - gate.t, tier === 0 ? '#6b4f33' : tier >= 3 ? '#1f3d2e' : '#5f7480');
    rect(gate.r - (gate.r - gate.l) * 0.25, gate.t + (g.b - gate.t) * 0.5, Math.max(1.5, u * 0.025), u * 0.08, tier >= 4 ? GOLD : '#d9d8d3');
    // A blade sign over the door with the ring from the wordmark.
    const arm = { x: gate.r + u * 0.04, y: gate.t - u * 0.06 };
    line(arm.x - u * 0.04, arm.y, arm.x + u * 0.22, arm.y, tier >= 4 ? GOLD : INK, Math.max(1.2, u * 0.025));
    circle(arm.x + u * 0.15, arm.y + u * 0.12, u * 0.1, tier >= 3 ? INK : PAPER);
    logoRing(arm.x + u * 0.15, arm.y + u * 0.12, u * 0.06, u * 0.03, Math.max(1, u * 0.018), tier >= 4 ? GOLD : tier >= 3 ? MINT : brand);
    // Out front: a bike first, a bench and planters later, a roof garden with lights at the top.
    const front = item.y + 1.85;
    if (tier < 2) {
      const at = proj(item.x + 1.55, front);
      bike(at.x, at.y, u * 0.75, tier >= 1 ? brand : '#2f6fff');
    }
    // On the roof: a chimney on the old house, an AC box once it is done up.
    if (tier < 3) {
      const at = g.top(0.2, 0.45);
      if (tier === 0) {
        rect(at.x - u * 0.1, at.y - u * 0.3, u * 0.2, u * 0.34, '#8f4b33');
        rect(at.x - u * 0.12, at.y - u * 0.34, u * 0.24, u * 0.06, '#6b3a28');
      } else {
        rect(at.x - u * 0.2, at.y - u * 0.16, u * 0.4, u * 0.22, '#b4bac0');
        circle(at.x, at.y - u * 0.05, u * 0.07, '#6f767d');
      }
    }
    if (tier >= 1) {
      const at = proj(item.x + 0.55, front);
      rect(at.x - u * 0.3, at.y - u * 0.16, u * 0.6, u * 0.05, tier >= 4 ? GOLD : '#a07a4a');
      [-0.25, 0.25].forEach(k => rect(at.x + u * k - 1, at.y - u * 0.12, 2, u * 0.12, '#4a4d52'));
    }
    if (tier >= 2) {
      [item.x + 0.12, item.x + 1.88].forEach(x => {
        const at = proj(x, front);
        rect(at.x - u * 0.08, at.y - u * 0.18, u * 0.16, u * 0.18, tier >= 3 ? '#2a2a2e' : '#c9c5bb');
        circle(at.x, at.y - u * 0.3, u * 0.12, '#7fb07a');
        circle(at.x + u * 0.05, at.y - u * 0.36, u * 0.07, '#9cc98f');
      });
    }
    if (tier >= 3) {
      for (let k = 0; k < 5; k += 1) circle(g.l + g.w * (0.12 + k * 0.19), g.t + (g.m - g.t) * 0.55, u * 0.1, k % 2 ? '#7fb07a' : '#9cc98f');
      for (let k = 0; k <= 10; k += 1) circle(lerp(g.l, g.r, k / 10), g.t + (g.m - g.t) * 0.2 + Math.sin((k / 10) * Math.PI) * u * 0.04, Math.max(1.2, u * 0.02), (k + Math.floor(clock * 3)) % 2 ? '#ffe9a8' : '#fff7d6');
    }
    if (tier >= 5) starRow(g.l + g.w / 2, g.t - u * 0.15, u * 0.05, stars(L));
  };

  // An arched window: a rectangle under a half oval, with a bar down the middle.
  const archWindow = (x, y, w, h, fill, frame) => {
    const r = w / 2;
    ctx.beginPath();
    ctx.moveTo(x, y + h);
    ctx.lineTo(x, y + r * 0.8);
    ctx.ellipse(x + r, y + r * 0.8, r, r * 0.8, 0, Math.PI, TAU);
    ctx.lineTo(x + w, y + h);
    ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.lineWidth = Math.max(1, w * 0.08);
    ctx.strokeStyle = frame;
    ctx.stroke();
  };
  // Markthalle: an old market hall with a gable over the gate; stalls under awnings inside,
  // a clock up top, banners on the facade and a crowd at the door.
  const drawHalle = item => {
    const u = cw();
    const L = item.level;
    const tier = Math.min(5, tierOf(L));
    const mat = matOf(L);
    const brand = hustlerLook.brand === INK ? SIGNAL : hustlerLook.brand;
    const h = 1.4 + Math.min(0.5, tier * 0.08 + (L - 1) * 0.004);
    const wall = tier === 0 ? '#b8573a' : tier === 1 ? '#d9c7a6' : mat.wall;
    const roof = tier === 0 ? '#8f4b33' : tier === 1 ? '#7d8a8f' : mat.roof;
    const g = block(item.x + 0.08, item.y + 0.3, 2.84, 1.35, h, roof, wall);
    if (tier === 0) bricks(g.l, g.m, g.w, g.fh, 'rgba(0,0,0,0.16)');
    else surface(g, L);
    // Glass ribs along the roof from tier 2.
    const ribs = tier >= 2 ? 14 : 8;
    for (let k = 1; k < ribs; k += 1) rect(g.l + (g.w * k) / ribs, g.t, Math.max(1, u * 0.015), g.m - g.t, tier >= 2 ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.15)');
    if (tier >= 2) rect(g.l + g.w * 0.05, g.t + (g.m - g.t) * 0.35, g.w * 0.9, (g.m - g.t) * 0.3, tier >= 3 ? 'rgba(186,236,183,0.18)' : 'rgba(207,227,234,0.55)');
    roofEdge(g, L);
    // The gable over the gate, with a round clock.
    const cx = g.l + g.w / 2;
    const gr = g.w * 0.2;
    const gh = u * 0.42;
    ctx.beginPath();
    ctx.moveTo(cx - gr, g.m + 1);
    ctx.ellipse(cx, g.m + 1, gr, gh, 0, Math.PI, TAU);
    ctx.closePath();
    ctx.fillStyle = wall;
    ctx.fill();
    ctx.lineWidth = Math.max(1.5, u * 0.03);
    ctx.strokeStyle = tier === 0 ? '#8f4b33' : mat.trim;
    ctx.stroke();
    circle(cx, g.m - gh * 0.42, u * 0.13, tier >= 3 ? '#0c0c0d' : PAPER);
    ctx.lineWidth = Math.max(1, u * 0.02);
    ctx.strokeStyle = tier >= 4 ? GOLD : tier >= 3 ? MINT : INK;
    ctx.beginPath();
    ctx.arc(cx, g.m - gh * 0.42, u * 0.13, 0, TAU);
    ctx.stroke();
    const hand = clock * 0.2 + item.id;
    line(cx, g.m - gh * 0.42, cx + Math.cos(hand) * u * 0.09, g.m - gh * 0.42 + Math.sin(hand) * u * 0.09, tier >= 3 ? MINT : INK, Math.max(1, u * 0.02));
    line(cx, g.m - gh * 0.42, cx + Math.cos(hand / 12 - 1) * u * 0.06, g.m - gh * 0.42 + Math.sin(hand / 12 - 1) * u * 0.06, tier >= 3 ? MINT : INK, Math.max(1.2, u * 0.03));
    // The name band over the gate.
    const band = { t: g.m + g.fh * 0.06, h: Math.min(g.fh * 0.13, u * 0.22) };
    rect(cx - g.w * 0.22, band.t, g.w * 0.44, band.h, tier >= 3 ? '#0c0c0d' : INK);
    glowLabel(tier >= 3 && (tier >= 4 ? GOLD : MINT), u * 0.15, 'MARKTHALLE', cx, band.t + band.h / 2 + 0.5, band.h * 0.6, tier >= 4 ? GOLD : tier >= 3 ? MINT : PAPER);
    // Four tall arched windows and the open gate in the middle with stalls inside.
    const frame = tier >= 4 ? GOLD : tier >= 3 ? '#0c0c0d' : tier === 0 ? '#8f4b33' : '#4f565c';
    const winT = band.t + band.h + g.fh * 0.08;
    [0.05, 0.21, 0.67, 0.83].forEach((fx, i) => archWindow(g.l + g.w * fx, winT, g.w * 0.12, g.b - winT - g.fh * 0.12, tier >= 2 || (i + item.id) % 3 === 0 ? '#ffe9a8' : '#2c3338', frame));
    const gate = { l: cx - g.w * 0.13, w: g.w * 0.26, t: band.t + band.h + g.fh * 0.05 };
    archWindow(gate.l, gate.t, gate.w, g.b - gate.t, tier >= 2 ? '#f3ecdc' : '#e8dcc4', frame);
    const floorY = g.b - 1;
    for (let k = 0; k < 2; k += 1) {
      const sx = gate.l + gate.w * (0.08 + k * 0.48);
      const sw = gate.w * 0.4;
      const st = floorY - (g.b - gate.t) * 0.42;
      rect(sx, st + u * 0.08, sw, floorY - st - u * 0.08, tier >= 3 ? '#2a2a2e' : '#c5a679');
      for (let i = 0; i < 4; i += 1) poly([[sx + (sw * i) / 4, st], [sx + (sw * (i + 1)) / 4, st], [sx + (sw * (i + 1)) / 4, st + u * 0.08], [sx + (sw * i) / 4, st + u * 0.08]], i % 2 ? PAPER : [brand, MINT_D][k]);
      shirt(sx + sw / 2, st + u * 0.11, Math.min(sw * 0.5, u * 0.18), [YELLOW, '#2f6fff'][k]);
    }
    // Banners between the windows from tier 3, lamps from tier 2.
    if (tier >= 3) {
      [0.18, 0.79].forEach(fx => {
        const bx = g.l + g.w * fx;
        rect(bx - u * 0.06, winT, u * 0.12, g.fh * 0.4, tier >= 5 ? '#141415' : brand);
        logoRing(bx, winT + g.fh * 0.2, u * 0.045, u * 0.022, Math.max(1, u * 0.015), tier >= 4 ? GOLD : MINT);
      });
    }
    if (tier >= 2) {
      [0.34, 0.66].forEach(fx => {
        const lx = g.l + g.w * fx;
        line(lx, band.t + band.h * 0.5, lx + (fx < 0.5 ? -u * 0.1 : u * 0.1), band.t + band.h * 0.5, '#4a4d52', Math.max(1, u * 0.02));
        circle(lx + (fx < 0.5 ? -u * 0.1 : u * 0.1), band.t + band.h * 0.5 + u * 0.04, Math.max(1.5, u * 0.035), '#ffe9a8');
      });
    }
    // A crowd at the gate from tier 1, crates of fresh merch out front.
    const front = item.y + 1.9;
    const crate = proj(item.x + 0.3, front);
    rect(crate.x - u * 0.13, crate.y - u * 0.16, u * 0.26, u * 0.16, '#c5a679');
    rect(crate.x - u * 0.13, crate.y - u * 0.1, u * 0.26, 1, 'rgba(0,0,0,0.2)');
    for (let k = 0; k < 3; k += 1) circle(crate.x - u * 0.07 + k * u * 0.07, crate.y - u * 0.17, u * 0.035, [brand, PAPER, YELLOW][k]);
    if (tier >= 1) {
      const rand = mulberry32(item.id * 23);
      for (let k = 0; k < Math.min(5, tier + 2); k += 1) {
        const at = proj(item.x + 0.85 + k * 0.38, front + (k % 2) * 0.06);
        crowdFigure(at.x, at.y, u * 0.5, oneOf(rand, WALKER_TOPS), oneOf(rand, SKINS), tier >= 3 && k % 2 === 0);
      }
    }
    if (mat.neon) glowRect(MINT, u * 0.2, g.l, g.b - 2, g.w, 2, MINT);
    if (tier >= 5) starRow(cx, g.m - gh - u * 0.15, u * 0.05, stars(L));
  };

  const smoothStep = k => k * k * (3 - 2 * k);
  // One round on the court, in seconds.
  const COURT_LOOP = 4.2;
  // A streetballer in the plain style of the crowd: legs that stride with the run, a jump, and arms that go from
  // the dribble up over the head for the shot. Returns where the ball sits in the hands; without colours it only
  // says that.
  const baller = (x, y, s, top, skin, pose) => {
    const lift = pose.jump * s * 0.18;
    const yb = y - lift;
    const a = lerp(0.5, Math.PI - 0.3, pose.arms);
    const shoulder = { x: x + s * 0.1, y: yb - s * 0.56 };
    const reach = s * 0.3;
    const hand = { x: shoulder.x + Math.sin(a) * reach, y: shoulder.y + Math.cos(a) * reach - pose.arms * s * 0.06 };
    if (!top) return hand;
    oval(x, y, s * 0.18 * (1 - pose.jump * 0.3), s * 0.05 * (1 - pose.jump * 0.3), 'rgba(0,0,0,0.15)');
    const stride = Math.sin(pose.step) * pose.run * s * 0.07;
    rect(x - s * 0.09 + stride, yb - s * 0.28, s * 0.07, s * 0.28, '#2b2b2e');
    rect(x + s * 0.02 - stride, yb - s * 0.28, s * 0.07, s * 0.28, '#2b2b2e');
    const back = lerp(0.3, Math.PI - 0.45, pose.arms);
    line(x - s * 0.1, yb - s * 0.56, x - s * 0.1 + Math.sin(back) * reach * 0.9, yb - s * 0.56 + Math.cos(back) * reach * 0.9, top, Math.max(1.5, s * 0.07));
    rect(x - s * 0.13, yb - s * 0.62, s * 0.26, s * 0.36, top);
    line(shoulder.x, shoulder.y, hand.x, hand.y, top, Math.max(1.5, s * 0.07));
    circle(x, yb - s * 0.74, s * 0.13, skin);
    return hand;
  };
  // Streetball-Court: a painted court in a cage, the hoop at the back, two players going one on one.
  const COURT = [['#7a7f85', '#686d73', PAPER], ['#c8642e', '#a85226', PAPER], ['#2f6fff', '#c8642e', PAPER], ['#1d1d20', '#2a2a2e', MINT], ['#1d1d20', '#2a2a2e', GOLD], ['#141415', '#16261d', MINT]];
  const drawCourt = item => {
    const u = cw();
    const L = item.level;
    const tier = Math.min(5, tierOf(L));
    const mat = matOf(L);
    const brand = hustlerLook.brand === INK ? SIGNAL : hustlerLook.brand;
    const [ground, paint, lines] = COURT[tier];
    const a = proj(item.x + 0.06, item.y + 0.12);
    const b = proj(item.x + 1.94, item.y + 1.92);
    rect(a.x, a.y, b.x - a.x, b.y - a.y, ground);
    rect(a.x, b.y, b.x - a.x, Math.max(2, u * 0.05), 'rgba(0,0,0,0.25)');
    const mid = (a.x + b.x) / 2;
    const ft = (b.y - a.y);
    // The key under the hoop and the lines; glowing at the top tier.
    const back = proj(item.x + 1, item.y + 0.45);
    rect(mid - u * 0.3, a.y, u * 0.6, ft * 0.42, paint);
    ctx.save();
    if (mat.neon) {
      ctx.shadowColor = MINT;
      ctx.shadowBlur = u * 0.15;
    }
    ctx.strokeStyle = lines;
    ctx.lineWidth = Math.max(1, u * 0.025);
    ctx.strokeRect(a.x + u * 0.06, a.y + u * 0.04, b.x - a.x - u * 0.12, ft - u * 0.08);
    ctx.strokeRect(mid - u * 0.3, a.y + u * 0.04, u * 0.6, ft * 0.42 - u * 0.04);
    ctx.beginPath();
    ctx.ellipse(mid, a.y + ft * 0.42, u * 0.3, u * 0.3 * TILT, 0, 0, Math.PI);
    ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(mid, back.y, u * 0.82, u * 0.82 * TILT * 1.1, 0, 0, Math.PI);
    ctx.stroke();
    ctx.restore();
    if (tier >= 1) {
      ctx.save();
      ctx.globalAlpha = tier >= 3 ? 0.8 : 0.55;
      drawLogo(mid, a.y + ft * 0.86, (b.x - a.x) * 0.5, tier >= 4 ? GOLD : tier >= 3 ? MINT : PAPER);
      ctx.restore();
    } else {
      line(a.x + u * 0.3, a.y + ft * 0.7, a.x + u * 0.55, a.y + ft * 0.82, 'rgba(0,0,0,0.25)', 1);
      line(b.x - u * 0.5, a.y + ft * 0.55, b.x - u * 0.2, a.y + ft * 0.62, 'rgba(0,0,0,0.25)', 1);
    }
    // The cage at the back from tier 1: posts and a chain-link mesh.
    const fenceH = u * (1 + Math.min(0.3, tier * 0.06));
    if (tier >= 1) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(a.x, a.y - fenceH, b.x - a.x, fenceH);
      ctx.clip();
      const step = Math.max(4, u * 0.1);
      ctx.strokeStyle = tier >= 3 ? 'rgba(186,236,183,0.35)' : 'rgba(80,86,92,0.45)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let x = a.x - fenceH; x < b.x + fenceH; x += step) {
        ctx.moveTo(x, a.y);
        ctx.lineTo(x + fenceH, a.y - fenceH);
        ctx.moveTo(x, a.y - fenceH);
        ctx.lineTo(x + fenceH, a.y);
      }
      ctx.stroke();
      ctx.restore();
      for (let k = 0; k <= 4; k += 1) rect(lerp(a.x, b.x, k / 4) - 1.5, a.y - fenceH, 3, fenceH, tier >= 4 ? GOLD : '#5a6168');
      rect(a.x, a.y - fenceH, b.x - a.x, Math.max(1.5, u * 0.03), tier >= 4 ? GOLD : '#5a6168');
    }
    // Floodlights at the back corners from tier 3.
    if (tier >= 3) {
      [a.x + u * 0.1, b.x - u * 0.1].forEach((x, i) => {
        rect(x - 1.5, a.y - fenceH - u * 0.5, 3, fenceH + u * 0.5, '#4a4d52');
        rect(x - u * 0.12, a.y - fenceH - u * 0.6, u * 0.24, u * 0.12, '#2b2b2e');
        ctx.save();
        ctx.globalAlpha = 0.18;
        poly([[x - u * 0.1, a.y - fenceH - u * 0.48], [x + u * 0.1, a.y - fenceH - u * 0.48], [mid + (i ? -u * 0.1 : u * 0.1) + (i ? -u * 0.4 : u * 0.4), b.y], [mid + (i ? -u * 0.1 : u * 0.1) - (i ? -u * 0.4 : u * 0.4), b.y]], '#fff2b8');
        ctx.restore();
      });
    }
    // The hoop: pole, arm, backboard, rim.
    const pole = proj(item.x + 1, item.y + 0.2);
    const top = proj(item.x + 1, item.y + 0.2, 1.45);
    rect(pole.x - u * 0.035, top.y, u * 0.07, pole.y - top.y, tier >= 4 ? GOLD : tier >= 3 ? '#2b2b2e' : '#4a4d52');
    const board = { x: mid - u * 0.32, y: top.y - u * 0.02, w: u * 0.64, h: u * 0.4 };
    rect(board.x, board.y, board.w, board.h, tier >= 3 ? 'rgba(220,235,240,0.35)' : PAPER);
    ctx.strokeStyle = tier >= 4 ? GOLD : tier >= 3 ? MINT : '#4a4d52';
    ctx.lineWidth = Math.max(1, u * 0.03);
    ctx.strokeRect(board.x, board.y, board.w, board.h);
    ctx.strokeStyle = tier >= 3 ? lines : SIGNAL;
    ctx.lineWidth = Math.max(1, u * 0.02);
    ctx.strokeRect(mid - u * 0.11, board.y + board.h * 0.42, u * 0.22, board.h * 0.42);
    const rim = { x: mid, y: board.y + board.h + u * 0.02 };
    // Two players, one on one, in a loop that ends where it starts: a drive with the dribble, the jump shot, the
    // swish, and a bounce pass back to the start. Each round the shot comes from somewhere else.
    const rand = mulberry32(item.id * 37);
    const tops = [brand, oneOf(rand, WALKER_TOPS)];
    const skins = [oneOf(rand, SKINS), oneOf(rand, SKINS)];
    const run = clock / COURT_LOOP + item.id * 0.17;
    const p = run - Math.floor(run);
    const start = { x: item.x + 0.45, y: item.y + 1.55 };
    const s = u * 0.62;
    // Where the shooter is at a moment of play: a round's shot spot holds only for that round.
    const playerAt = time => {
      const q = time - Math.floor(time);
      const spot = mulberry32(Math.floor(time) * 7919 + item.id);
      const shot = { x: item.x + 0.55 + spot() * 0.95, y: item.y + 0.95 + spot() * 0.3 };
      const k = q < 0.55 ? smoothStep(q / 0.55) : q < 0.82 ? 1 : 1 - smoothStep((q - 0.82) / 0.18);
      const speed = q < 0.55 ? Math.sin((q / 0.55) * Math.PI) : q < 0.82 ? 0 : Math.sin(((q - 0.82) / 0.18) * Math.PI);
      const tx = lerp(start.x, shot.x, k);
      const ty = lerp(start.y, shot.y, k);
      const at = proj(tx, ty);
      return { x: at.x, y: at.y, tx, ty, speed, k };
    };
    const shooter = playerAt(run);
    const pose = {
      jump: p > 0.6 && p < 0.74 ? Math.sin(((p - 0.6) / 0.14) * Math.PI) : 0,
      arms: p < 0.55 ? 0 : p < 0.62 ? smoothStep((p - 0.55) / 0.07) : p < 0.76 ? 1 : p < 0.86 ? 1 - smoothStep((p - 0.76) / 0.1) : 0,
      run: shooter.speed,
      step: shooter.k * 9 * Math.PI
    };
    // The defender stays between the ball and the hoop, a beat behind, and goes up against the shot.
    const lag = playerAt(run - 0.04);
    const hoop = proj(item.x + 1, item.y + 0.4);
    const gap = dist(lag.tx, lag.ty, item.x + 1, item.y + 0.4) || 1;
    const mark = proj(lag.tx + ((item.x + 1 - lag.tx) / gap) * 0.45, lag.ty + ((item.y + 0.4 - lag.ty) / gap) * 0.45);
    const guard = {
      x: mark.x,
      y: mark.y,
      pose: {
        jump: p > 0.63 && p < 0.75 ? Math.sin(((p - 0.63) / 0.12) * Math.PI) * 0.7 : 0,
        arms: p > 0.58 && p < 0.84 ? Math.sin(((p - 0.58) / 0.26) * Math.PI) : 0,
        run: lag.speed,
        step: lag.k * 9 * Math.PI + 1.3
      }
    };
    baller(guard.x, guard.y, s, tops[1], skins[1], guard.pose);
    const hand = baller(shooter.x, shooter.y, s, tops[0], skins[0], pose);
    const ball = { x: hand.x, y: hand.y };
    const r = Math.max(2, u * 0.065);
    if (p < 0.55) {
      // Five bounces on the drive, the ball back in the hand at the end of each.
      ball.y = lerp(hand.y, shooter.y - r, Math.abs(Math.sin((p / 0.55) * Math.PI * 5)));
    } else if (p >= 0.7 && p < 0.82) {
      const from = baller(shooter.x, shooter.y, s, null, null, { ...pose, jump: Math.sin((0.1 / 0.14) * Math.PI), arms: 1 });
      const k = (p - 0.7) / 0.12;
      ball.x = lerp(from.x, rim.x, k);
      ball.y = lerp(from.y, rim.y - r, k) - Math.sin(k * Math.PI) * u * 0.55;
    } else if (p >= 0.82 && p < 0.92) {
      const k = (p - 0.82) / 0.1;
      ball.x = rim.x;
      ball.y = lerp(rim.y - r, hoop.y - r, k * k);
    } else if (p >= 0.92) {
      // The bounce pass back lands in the hand just as the next drive begins.
      const k = (p - 0.92) / 0.08;
      ball.x = lerp(rim.x, hand.x, k);
      ball.y = lerp(hoop.y - r, hand.y, k) - Math.sin(k * Math.PI) * u * 0.22;
    }
    circle(ball.x, ball.y, r, '#e0782c');
    line(ball.x - r, ball.y, ball.x + r, ball.y, 'rgba(0,0,0,0.4)', 1);
    // The rim and net go in front of the ball; the net swings out after the swish.
    ctx.strokeStyle = tier >= 4 ? GOLD : SIGNAL;
    ctx.lineWidth = Math.max(1.5, u * 0.03);
    ctx.beginPath();
    ctx.ellipse(rim.x, rim.y, u * 0.13, u * 0.04, 0, 0, TAU);
    ctx.stroke();
    const swish = p > 0.82 && p < 0.97 ? Math.sin(((p - 0.82) / 0.15) * Math.PI) : 0;
    const sway = swish * Math.sin(((p - 0.82) / 0.15) * Math.PI * 4) * u * 0.03;
    for (let k = 0; k <= 4; k += 1) line(rim.x - u * 0.13 + (k * u * 0.26) / 4, rim.y, rim.x - u * 0.07 + (k * u * 0.14) / 4 + sway, rim.y + u * (0.18 + swish * 0.03), 'rgba(255,255,255,0.85)', 1);
    const cheer = p > 0.82 && p < 0.99 ? Math.sin(((p - 0.82) / 0.17) * Math.PI) : 0;
    // A boombox on the bench from tier 2, people watching from tier 4.
    if (tier >= 2) {
      const at = proj(item.x + 1.7, item.y + 1.97);
      rect(at.x - u * 0.28, at.y - u * 0.14, u * 0.56, u * 0.05, tier >= 4 ? GOLD : '#a07a4a');
      [-0.22, 0.22].forEach(k => rect(at.x + u * k - 1, at.y - u * 0.1, 2, u * 0.1, '#4a4d52'));
      const beat = 1 + Math.max(0, Math.sin(clock * 9 + item.id)) * 0.08;
      rect(at.x - u * 0.16, at.y - u * 0.28, u * 0.24, u * 0.14, INK);
      circle(at.x - u * 0.1, at.y - u * 0.21, u * 0.04 * beat, '#5a5f66');
      circle(at.x + u * 0.02, at.y - u * 0.21, u * 0.04 * beat, '#5a5f66');
    }
    if (tier >= 4) {
      for (let k = 0; k < 3; k += 1) {
        const at = proj(item.x + 0.25 + k * 0.3, item.y + 1.98);
        crowdFigure(at.x, at.y, u * 0.5, oneOf(rand, WALKER_TOPS), oneOf(rand, SKINS), cheer);
      }
    }
    if (tier >= 5) starRow(mid, board.y - u * 0.18, u * 0.05, stars(L));
  };

  // Skate-Spot: a mini ramp seen from the side, a rail in front, a skater rolling back and forth.
  const SKATE = [['#c5a679', '#dcc8a4', '#8d9399'], ['#2f6fff', '#e9e7e1', '#8d9399'], ['#b4bac0', '#d6d5cf', '#6f767d'], ['#1d1d20', '#4a4d52', MINT], ['#1d1d20', '#4a4d52', GOLD], ['#141415', '#33363b', MINT]];
  const drawSkate = item => {
    const u = cw();
    const L = item.level;
    const tier = Math.min(5, tierOf(L));
    const mat = matOf(L);
    const brand = hustlerLook.brand === INK ? SIGNAL : hustlerLook.brand;
    const [side, ride, coping] = SKATE[tier];
    const x0 = item.x + 0.08;
    const x1 = item.x + 1.92;
    const yB = item.y + 0.2;
    const yF = item.y + 1.15;
    const H = 0.55 + Math.min(0.2, tier * 0.04);
    // The profile: a deck, a curved wall down to the flat, and up again on the other side.
    const zAt = x => {
      const s = (x - x0) / (x1 - x0);
      const edge = s < 0.5 ? s : 1 - s;
      if (edge < 0.08) return H;
      if (edge > 0.36) return 0;
      const t = (edge - 0.08) / 0.28;
      return H * (1 - Math.sqrt(1 - (1 - t) * (1 - t)));
    };
    const N = 28;
    const xs = [];
    for (let i = 0; i <= N; i += 1) xs.push(lerp(x0, x1, i / N));
    // The riding surface, shaded by how steep it is.
    for (let i = 0; i < N; i += 1) {
      const za = zAt(xs[i]);
      const zb = zAt(xs[i + 1]);
      const fa = proj(xs[i], yF, za);
      const fb = proj(xs[i + 1], yF, zb);
      const ba = proj(xs[i], yB, za);
      const bb = proj(xs[i + 1], yB, zb);
      poly([[fa.x, fa.y], [fb.x + 0.5, fb.y], [bb.x + 0.5, bb.y], [ba.x, ba.y]], ride);
      const steep = Math.min(1, Math.abs(zb - za) / ((x1 - x0) / N) / 3);
      if (steep > 0.02) poly([[fa.x, fa.y], [fb.x + 0.5, fb.y], [bb.x + 0.5, bb.y], [ba.x, ba.y]], `rgba(0,0,0,${(steep * 0.22).toFixed(3)})`);
    }
    if (tier >= 3) {
      const fa = proj(lerp(x0, x1, 0.4), yF);
      const ba = proj(lerp(x0, x1, 0.6), yB);
      ctx.save();
      ctx.globalAlpha = 0.6;
      drawLogo((fa.x + ba.x) / 2, (fa.y + ba.y) / 2, (ba.x - fa.x) * 1.4, tier >= 4 ? GOLD : MINT);
      ctx.restore();
    }
    // The side of the ramp, facing us.
    const pts = xs.map(x => {
      const p = proj(x, yF, zAt(x));
      return [p.x, p.y];
    });
    const g0 = proj(x0, yF);
    const g1 = proj(x1, yF);
    poly([...pts, [g1.x, g1.y], [g0.x, g0.y]], side);
    if (tier === 0) {
      for (let k = 1; k < 9; k += 1) {
        const x = lerp(x0, x1, k / 9);
        const top = proj(x, yF, zAt(x));
        line(top.x, g0.y, top.x, top.y, 'rgba(0,0,0,0.15)', 1);
      }
    }
    if (tier >= 1) graffiti(g0.x + (g1.x - g0.x) * 0.06, g0.y - u * 0.3, (g1.x - g0.x) * 0.22, [tier >= 3 ? MINT : YELLOW, brand], item.id * 9 + tier);
    if (tier >= 2) graffiti(g1.x - (g1.x - g0.x) * 0.28, g0.y - u * 0.28, (g1.x - g0.x) * 0.22, [tier >= 4 ? GOLD : '#ff4fa3', PAPER], item.id * 13 + tier);
    // Coping on both decks.
    ctx.save();
    if (mat.neon) {
      ctx.shadowColor = MINT;
      ctx.shadowBlur = u * 0.15;
    }
    [lerp(x0, x1, 0.08), lerp(x0, x1, 0.92)].forEach(x => {
      const f = proj(x, yF, H);
      const bk = proj(x, yB, H);
      line(f.x, f.y, bk.x, bk.y, coping, Math.max(2, u * 0.05));
    });
    ctx.restore();
    // The skater rides the ramp, a little air at each top.
    const phase = clock * 1.1 + item.id;
    const sx = lerp(x0, x1, 0.5 - Math.sin(phase) * 0.41);
    const air = Math.max(0, Math.abs(Math.sin(phase)) - 0.94) * 4;
    const sy = (yB + yF) / 2;
    const on = proj(sx, sy, zAt(sx) + air);
    const ahead = proj(sx + 0.05, sy, zAt(sx + 0.05));
    const behind = proj(sx - 0.05, sy, zAt(sx - 0.05));
    const tilt = Math.atan2(ahead.y - behind.y, ahead.x - behind.x);
    const rand = mulberry32(item.id * 41);
    ctx.save();
    ctx.translate(on.x, on.y);
    ctx.rotate(tilt);
    rect(-u * 0.16, -u * 0.04, u * 0.32, u * 0.035, tier >= 4 ? GOLD : brand);
    circle(-u * 0.1, 0, Math.max(1, u * 0.025), PAPER);
    circle(u * 0.1, 0, Math.max(1, u * 0.025), PAPER);
    crowdFigure(0, -u * 0.04, u * 0.6, oneOf(rand, WALKER_TOPS), oneOf(rand, SKINS), air > 0);
    ctx.restore();
    // The rail in front, a cone at the start, a spectator with a board later.
    const ra = proj(item.x + 0.4, item.y + 1.6, 0.22);
    const rb = proj(item.x + 1.4, item.y + 1.6, 0.22);
    [ra, rb].forEach(p => rect(p.x - 1, p.y, 2, u * 0.22, '#4a4d52'));
    line(ra.x - u * 0.05, ra.y, rb.x + u * 0.05, rb.y, coping, Math.max(2, u * 0.045));
    if (tier === 0) {
      const at = proj(item.x + 1.75, item.y + 1.8);
      poly([[at.x - u * 0.08, at.y], [at.x, at.y - u * 0.24], [at.x + u * 0.08, at.y]], SIGNAL);
      rect(at.x - u * 0.05, at.y - u * 0.12, u * 0.1, u * 0.03, PAPER);
    }
    if (tier >= 2) {
      const at = proj(item.x + 1.72, item.y + 1.9);
      crowdFigure(at.x, at.y, u * 0.55, oneOf(rand, WALKER_TOPS), oneOf(rand, SKINS), air > 0);
      ctx.save();
      ctx.translate(at.x + u * 0.16, at.y - u * 0.18);
      ctx.rotate(-1.3);
      rect(-u * 0.15, -u * 0.03, u * 0.3, u * 0.05, tier >= 4 ? GOLD : brand);
      ctx.restore();
    }
    if (tier >= 3) {
      const at = proj(item.x + 0.12, item.y + 1.9);
      speaker(at.x, at.y, u * 0.22, u * 0.36, Math.max(0, Math.sin(clock * 9)), mat.trim);
    }
    if (tier >= 5) starRow(proj(item.x + 1, yB, H).x, proj(item.x + 1, yB, H).y - u * 0.4, u * 0.05, stars(L));
  };

  // Piratensender: a shack with a lattice mast; the signal rings out over the whole block.
  const drawRadio = item => {
    const u = cw();
    const L = item.level;
    const tier = Math.min(5, tierOf(L));
    const mat = matOf(L);
    const brand = hustlerLook.brand === INK ? SIGNAL : hustlerLook.brand;
    const shed = block(item.x + 0.12, item.y + 0.75, 1.15, 0.95, 0.85 + Math.min(0.2, tier * 0.04), tier >= 3 ? mat.roof : '#7d8a8f', tier === 0 ? '#9aa3a8' : tier === 1 ? brand : mat.wall);
    if (tier <= 1) for (let x = shed.l + 2; x < shed.r - 1; x += Math.max(3, u * 0.06)) rect(x, shed.m, 1, shed.fh, 'rgba(0,0,0,0.15)');
    else surface(shed, L);
    roofEdge(shed, L);
    // The sign, the ON AIR lamp, a lit window with the desk inside and the door.
    const sh = Math.min(shed.fh * 0.16, u * 0.2);
    rect(shed.l + shed.w * 0.08, shed.m + shed.fh * 0.06, shed.w * 0.84, sh, tier >= 3 ? '#0c0c0d' : INK);
    glowLabel(tier >= 3 && (tier >= 4 ? GOLD : MINT), u * 0.12, `MXD ${(87.5 + ((item.id * 7) % 200) / 10).toFixed(1)}`, shed.l + shed.w / 2, shed.m + shed.fh * 0.06 + sh / 2 + 0.5, sh * 0.62, tier >= 4 ? GOLD : tier >= 3 ? MINT : PAPER);
    const lit = Math.sin(clock * 3 + item.id) > -0.3;
    const lamp = { x: shed.l + shed.w * 0.73, y: shed.m + shed.fh * 0.32 };
    rect(lamp.x - u * 0.14, lamp.y, u * 0.28, u * 0.1, lit ? SIGNAL : '#6a2c3a');
    label('ON AIR', lamp.x, lamp.y + u * 0.052, u * 0.065, PAPER);
    if (lit) glowAt(lamp.x, lamp.y + u * 0.05, u * 0.3, 'rgba(255,75,31,ALPHA)', 0.25);
    const win = { l: shed.l + shed.w * 0.08, t: shed.m + shed.fh * 0.32, w: shed.w * 0.42, h: shed.fh * 0.34 };
    rect(win.l, win.t, win.w, win.h, '#ffe9a8');
    rect(win.l, win.t + win.h * 0.62, win.w, win.h * 0.38, '#2b2b2e');
    for (let k = 0; k < 5; k += 1) rect(win.l + win.w * (0.1 + k * 0.17), win.t + win.h * 0.66, Math.max(1, win.w * 0.06), win.h * (0.12 + Math.abs(Math.sin(clock * 6 + k)) * 0.15), [MINT, YELLOW, SIGNAL][k % 3]);
    ctx.lineWidth = Math.max(1, u * 0.025);
    ctx.strokeStyle = tier >= 4 ? GOLD : '#4a4d52';
    ctx.strokeRect(win.l, win.t, win.w, win.h);
    rect(shed.l + shed.w * 0.62, shed.m + shed.fh * 0.52, shed.w * 0.24, shed.b - shed.m - shed.fh * 0.52, tier >= 3 ? '#1f3d2e' : '#4a4d52');
    // The mast: a tapering lattice with guy wires and a blinking light on top.
    const mh = 1.9 + Math.min(0.4, tier * 0.08);
    const foot = proj(item.x + 1.6, item.y + 1.15);
    const tip = proj(item.x + 1.6, item.y + 1.15, mh);
    const steel = tier >= 4 ? GOLD : tier >= 3 ? '#3a3d42' : tier >= 1 ? '#c8642e' : '#8d9399';
    const half = y => lerp(u * 0.16, u * 0.03, (foot.y - y) / (foot.y - tip.y));
    line(foot.x - half(foot.y), foot.y, tip.x - half(tip.y), tip.y, steel, Math.max(1.5, u * 0.03));
    line(foot.x + half(foot.y), foot.y, tip.x + half(tip.y), tip.y, steel, Math.max(1.5, u * 0.03));
    const segs = 8;
    for (let k = 0; k < segs; k += 1) {
      const y0 = lerp(foot.y, tip.y, k / segs);
      const y1 = lerp(foot.y, tip.y, (k + 1) / segs);
      line(foot.x - half(y0), y0, foot.x + half(y1), y1, steel, 1);
      line(foot.x + half(y0), y0, foot.x - half(y1), y1, steel, 1);
      if (tier >= 1 && tier < 3 && k % 2 === 0) rect(foot.x - half(y1), y1 - 1, half(y1) * 2, 2, PAPER);
    }
    [[item.x + 1.95, item.y + 1.9], [item.x + 1.3, item.y + 1.95], [item.x + 1.95, item.y + 0.5]].forEach(([gx, gy]) => {
      const at = proj(gx, gy);
      line(tip.x, lerp(foot.y, tip.y, 0.6), at.x, at.y, 'rgba(60,64,70,0.45)', 1);
    });
    if (tier >= 2) {
      const dish = { x: foot.x - u * 0.18, y: lerp(foot.y, tip.y, 0.55) };
      oval(dish.x, dish.y, u * 0.08, u * 0.16, tier >= 4 ? GOLD : PAPER, -0.3);
      circle(dish.x - u * 0.02, dish.y, Math.max(1, u * 0.025), INK);
    }
    // The signal: rings going out from the tip.
    const colour = tier >= 4 ? '226,179,60' : tier >= 3 ? '186,236,183' : '255,75,31';
    for (let k = 0; k < 3; k += 1) {
      const p = (clock * 0.55 + k / 3 + item.id * 0.1) % 1;
      const r = u * (0.12 + p * 0.7);
      ctx.strokeStyle = `rgba(${colour},${(0.7 * (1 - p)).toFixed(3)})`;
      ctx.lineWidth = Math.max(1.5, u * 0.035);
      [0, Math.PI].forEach(start => {
        ctx.beginPath();
        ctx.arc(tip.x, tip.y, r, start - 0.6, start + 0.6);
        ctx.stroke();
      });
    }
    const blink = Math.sin(clock * 4 + item.id) > 0;
    circle(tip.x, tip.y - 1, Math.max(1.8, u * 0.04), blink ? SIGNAL : '#6a2c3a');
    if (blink) glowAt(tip.x, tip.y - 1, u * 0.25, 'rgba(255,75,31,ALPHA)', 0.35);
    // Ground clutter: a car battery and cables first, a generator later, a flag at the top tier.
    const at = proj(item.x + 0.3, item.y + 1.95);
    if (tier < 2) {
      rect(at.x - u * 0.1, at.y - u * 0.12, u * 0.2, u * 0.12, INK);
      rect(at.x - u * 0.07, at.y - u * 0.15, u * 0.04, u * 0.03, SIGNAL);
      rect(at.x + u * 0.03, at.y - u * 0.15, u * 0.04, u * 0.03, '#2f6fff');
      ctx.beginPath();
      ctx.moveTo(at.x + u * 0.08, at.y - u * 0.13);
      ctx.quadraticCurveTo(at.x + u * 0.3, at.y + u * 0.02, foot.x - u * 0.1, foot.y);
      ctx.strokeStyle = INK;
      ctx.lineWidth = Math.max(1, u * 0.02);
      ctx.stroke();
    } else {
      const gen = block(item.x + 1.38, item.y + 1.55, 0.5, 0.35, 0.32, tier >= 3 ? '#2a2a2e' : '#d6d5cf', tier >= 4 ? GOLD : tier >= 3 ? '#1d1d20' : YELLOW);
      for (let k = 1; k < 4; k += 1) rect(gen.l + (gen.w * k) / 4, gen.m + gen.fh * 0.2, 1, gen.fh * 0.6, 'rgba(0,0,0,0.3)');
      smoke(gen.top(0.2, 0.5).x, gen.top(0.2, 0.5).y, u, item.id, '120,120,120');
    }
    if (tier >= 3) {
      const mast = shed.top(0.15, 0.5);
      rect(mast.x - 1, mast.y - u * 0.5, 2, u * 0.5, INK);
      const wave = Math.sin(clock * 4 + item.id) * u * 0.03;
      poly([[mast.x + 1, mast.y - u * 0.5], [mast.x + u * 0.36, mast.y - u * 0.45 + wave], [mast.x + 1, mast.y - u * 0.3]], tier >= 4 ? GOLD : MINT);
    }
    if (mat.neon) glowRect(MINT, u * 0.2, shed.l, shed.b - 2, shed.w, 2, MINT);
    if (tier >= 5) starRow(tip.x, tip.y - u * 0.3, u * 0.05, stars(L));
  };

  // People on the street: every one dressed from head to toe like the hustler, each in their own look.
  // Some want merch and carry a price tag over the head; tap it and they buy, cheer and walk off with the bag.
  const walkerSize = () => cw() * 0.8;
  const walkerTag = w => {
    const u = cw();
    const s = walkerSize();
    const at = proj(w.x, w.y);
    const size = clamp(u * 0.3, 18, 24);
    const text = fmt(w.price);
    ctx.font = `700 ${Math.round(size * 0.5)}px ${FONT}`;
    const tw = size * 1.15 + ctx.measureText(text).width + size * 0.4;
    const bob = Math.sin(clock * 3 + w.id) * 2;
    return { x: at.x - tw / 2, y: at.y - s * 1.08 - size - 8 + bob, w: tw, h: size, size, text };
  };
  const drawWalker = w => {
    const at = proj(w.x, w.y);
    const s = walkerSize();
    const item = w.state === 'stay' && itemById(w.target);
    const dancing = item && showLive(item);
    const cheer = w.pop > 0;
    const jump = dancing ? Math.max(0, Math.sin(clock * 9 + w.id)) * s * 0.12 : cheer ? Math.sin((w.pop / 0.7) * Math.PI) * s * 0.14 : 0;
    // A ring on the ground when the sale lands.
    if (cheer) {
      const p = 1 - w.pop / 0.7;
      ctx.beginPath();
      ctx.ellipse(at.x, at.y, s * (0.15 + p * 0.45), s * (0.15 + p * 0.45) * TILT, 0, 0, TAU);
      ctx.strokeStyle = `rgba(124,200,120,${1 - p})`;
      ctx.lineWidth = 3;
      ctx.stroke();
    }
    oval(at.x, at.y, s * 0.2, s * 0.055, 'rgba(0,0,0,0.15)');
    const m = motion(w);
    stampPerson(at.x, at.y - jump, s, { look: w.look, owner: w, seed: w.id, face: m.turn, walk: w.walk, gait: m.gait, moving: w.state !== 'stay' && !cheer, pose: cheer || dancing ? 'cheer' : null, bag: w.bought, noShadow: true });
    // Wants merch: the price they would pay, on the same tag the buildings use.
    const fighting = raid && raid.phase !== 'end';
    if ((w.wants && !w.bought && w.state !== 'out' && !fighting) || (cheer && w.wants)) {
      const tag = walkerTag(w);
      const p = cheer ? 1 - w.pop / 0.7 : 0;
      ctx.save();
      if (cheer) {
        const cx = tag.x + tag.w / 2;
        const cy = tag.y + tag.h / 2 - p * cw() * 0.5;
        ctx.globalAlpha = Math.max(0, 1 - p * 1.4);
        ctx.translate(cx, cy);
        ctx.scale(1 + p * 0.5, 1 + p * 0.5);
        ctx.translate(-cx, -tag.y - tag.h / 2);
      }
      drawTag({ ...tag, full: cheer, fans: false });
      ctx.restore();
    }
  };
  // A group hanging about: each one turned to the middle, now on the phone, now waving, now with a coffee;
  // from the top tier on they dance.
  // They hardly move, so each one is painted once per pose and size and then only stamped on the map.
  const HANG_POSES = ['phone', null, 'coffee', 'wave', null];
  const hangSprites = new Map();
  const hangSprite = (one, pose, s) => {
    const face = one.dx > 0 ? -1 : 1;
    const key = `${one.seed}:${pose}:${face}:${Math.round(s * 4)}:${dpr}`;
    let sprite = hangSprites.get(key);
    if (sprite) return sprite;
    if (hangSprites.size > 160) hangSprites.clear();
    const w = Math.ceil(s * 1.4);
    const h = Math.ceil(s * 1.5);
    const c = document.createElement('canvas');
    c.width = Math.ceil(w * dpr);
    c.height = Math.ceil(h * dpr);
    const main = ctx;
    ctx = c.getContext('2d');
    ctx.scale(dpr, dpr);
    try {
      drawPerson(w / 2, h - s * 0.05, s, { look: one.look, owner: one, seed: one.seed, face, pose, noShadow: true });
    } finally {
      ctx = main;
    }
    sprite = { c, w, h, foot: h - s * 0.05 };
    hangSprites.set(key, sprite);
    return sprite;
  };
  const drawHanger = (group, one) => {
    const s = walkerSize();
    const at = proj(group.x + one.dx, group.y + one.dy);
    const party = vibe() >= 5;
    const pose = party ? 'cheer' : HANG_POSES[(Math.floor(clock / 6 + one.seed / 97) + one.seed) % HANG_POSES.length];
    const jump = party ? Math.max(0, Math.sin(clock * 8 + one.seed)) * s * 0.1 : Math.sin(clock * 1.6 + one.seed) * s * 0.008;
    oval(at.x, at.y, s * 0.2, s * 0.055, 'rgba(0,0,0,0.15)');
    const sprite = hangSprite(one, pose, s);
    ctx.drawImage(sprite.c, at.x - sprite.w / 2, at.y - jump - sprite.foot, sprite.w, sprite.h);
  };
  // A pigeon: grey, a green sheen on the neck, the head bobbing down to peck; wings beating once it is up.
  const drawPigeon = p => {
    const s = cw() * 0.17;
    const ground = proj(p.x, p.y);
    const at = proj(p.x, p.y, p.z);
    const flying = p.up || p.z > 0;
    if (p.z < 2) oval(ground.x, ground.y, s * 0.45 * (1 - p.z / 2), s * 0.13 * (1 - p.z / 2), 'rgba(0,0,0,0.16)');
    ctx.save();
    ctx.translate(at.x, at.y - (p.hop > 0 ? Math.sin((p.hop / 0.25) * Math.PI) * s * 0.25 : 0));
    ctx.scale(p.face, 1);
    const down = p.peck > 0 ? Math.max(0, Math.sin((p.peck / 0.5) * Math.PI * 2)) : 0;
    poly([[-s * 0.35, -s * 0.5], [-s * 0.82, -s * 0.66], [-s * 0.78, -s * 0.42]], '#666b72');
    oval(0, -s * 0.47, s * 0.5, s * 0.3, '#8e939a');
    if (flying) {
      const flap = Math.sin(clock * 28 + p.id) * s * 0.55;
      poly([[-s * 0.2, -s * 0.55], [s * 0.2, -s * 0.55], [-s * 0.1, -s * 0.6 - flap]], '#a9aeb4');
    } else {
      oval(-s * 0.08, -s * 0.5, s * 0.32, s * 0.16, '#a9aeb4');
      line(-s * 0.05, -s * 0.18, -s * 0.05, 0, '#d9534f', Math.max(1, s * 0.06));
      line(s * 0.12, -s * 0.18, s * 0.12, 0, '#d9534f', Math.max(1, s * 0.06));
    }
    const hx = s * 0.42 + down * s * 0.12;
    const hy = -s * 0.78 + down * s * 0.42;
    circle(s * 0.3, -s * 0.62 + down * s * 0.2, s * 0.13, '#5f8f7e');
    circle(hx, hy, s * 0.16, '#6b7077');
    poly([[hx + s * 0.13, hy - s * 0.03], [hx + s * 0.3, hy + s * 0.03], [hx + s * 0.13, hy + s * 0.06]], '#3a3a3d');
    circle(hx + s * 0.05, hy - s * 0.04, Math.max(0.7, s * 0.035), '#ffb347');
    ctx.restore();
  };

  // ---------------------------------------------------------------------------------------------
  // The heroes on the map: their post, their aura and their figure, at rest laid down from pictures.

  const PAINTS = [SIGNAL, '#2f6fff', YELLOW, '#ff4fa3', MINT_D];
  // The MIXEDOWN heroes, superheroes from the block, built on the rig (see HERO_SPECS) and twice as big
  // as the crew. `glow` is the colour of his aura, `scale` his size against the crew.
  const HERO_LOOKS = {
    caruso: { glow: '255,96,72', scale: 1.95 },
    retro: { glow: '255,79,163', scale: 2 },
    zola: { glow: '255,138,31', scale: 2.5 },
    kuza: { glow: '255,64,96', scale: 1.85 },
    blade: { glow: '214,24,52', scale: 2.1 },
    capi: { glow: '178,90,255', scale: 2 },
    johno: { glow: '255,214,140', scale: 2 },
    maliq: { glow: '160,110,255', scale: 1.95 },
    polarise: { glow: '150,215,255', scale: 2.05 },
    bigtoe: { glow: '255,196,64', scale: 2.05 }
  };
  const heroGlow = (x, y, r, rgb, alpha) => {
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    glowAt(x, y, r, `rgba(${rgb},ALPHA)`, alpha);
    ctx.restore();
  };
  // A cluster grenade `r` tall standing on (x, y): an olive body with a red band, the fuse cap, its lever and ring.
  const grenade = (x, y, r, spin = 0) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(spin);
    oval(0, -r * 0.4, r * 0.33, r * 0.4, '#56652f');
    rect(-r * 0.33, -r * 0.48, r * 0.66, r * 0.11, '#d6182f');
    oval(-r * 0.1, -r * 0.55, r * 0.09, r * 0.14, 'rgba(255,255,255,0.25)');
    rect(-r * 0.12, -r * 0.94, r * 0.24, r * 0.16, '#9a9da3');
    poly([[r * 0.1, -r * 0.94], [r * 0.3, -r * 0.88], [r * 0.36, -r * 0.42], [r * 0.27, -r * 0.44]], '#b9bcc1');
    ctx.beginPath();
    ctx.arc(-r * 0.24, -r * 0.92, r * 0.11, 0, TAU);
    ctx.strokeStyle = '#b9bcc1';
    ctx.lineWidth = Math.max(0.6, r * 0.05);
    ctx.stroke();
    ctx.restore();
  };
  // A four-pointed throwing star, big enough to come back like a boomerang.
  const shuriken = (x, y, r, color, spin) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(spin);
    ctx.beginPath();
    starPath(ctx, 0, 0, r, r * 0.3, 4, 0);
    ctx.fillStyle = INK;
    ctx.fill();
    ctx.beginPath();
    starPath(ctx, 0, 0, r * 0.84, r * 0.24, 4, 0);
    ctx.fillStyle = '#d9dee4';
    ctx.fill();
    ctx.beginPath();
    starPath(ctx, 0, 0, r * 0.84, r * 0.12, 4, 0);
    ctx.fillStyle = color;
    ctx.fill();
    circle(0, 0, r * 0.2, INK);
    circle(0, 0, r * 0.1, '#d9dee4');
    ctx.restore();
  };
  // A playing card, `h` tall, its suit in its colour.
  const playCard = (x, y, h, color, rot, text = '') => {
    const w = h * 0.68;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rot);
    roundRect(-w / 2 - h * 0.04, -h / 2 - h * 0.04, w + h * 0.08, h * 1.08, h * 0.12, INK);
    roundRect(-w / 2, -h / 2, w, h, h * 0.09, PAPER);
    roundRect(-w / 2 + h * 0.07, -h / 2 + h * 0.07, w - h * 0.14, h - h * 0.14, h * 0.05, color);
    if (text) label(text, 0, h * 0.02, h * 0.42, PAPER, 'center', 800);
    else {
      ctx.beginPath();
      starPath(ctx, 0, 0, h * 0.2, h * 0.08, 4);
      ctx.fillStyle = PAPER;
      ctx.fill();
    }
    ctx.restore();
  };
  const crewSize = level => cw() * (0.82 + Math.min(5, tierOf(level)) * 0.02);
  const localAim = (aim, face) => {
    let local = face === 1 ? aim : Math.PI - aim;
    while (local > Math.PI) local -= TAU;
    while (local < -Math.PI) local += TAU;
    return local;
  };
  // Behind every hero: an aura in his colour with motes of light rising, and his own signature around him.
  const heroBase = (item, at, s, face) => {
    const look = HERO_LOOKS[item.type];
    const state = item.crew || {};
    const power = state.special || 0;
    heroGlow(at.x, at.y - s * 0.5, s * (0.75 + power * 0.5), look.glow, 0.22 + Math.sin(clock * 2.4 + item.id) * 0.05 + power * 0.35);
    if (power > 0) {
      // Rays of light while a special goes off.
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.translate(at.x, at.y - s * 0.5);
      ctx.rotate(clock * 0.8);
      for (let k = 0; k < 8; k += 1) {
        ctx.rotate(TAU / 8);
        poly([[0, 0], [s * 1.3, -s * 0.08], [s * 1.3, s * 0.08]], `rgba(${look.glow},${(0.18 * Math.min(1, power)).toFixed(3)})`);
      }
      ctx.restore();
    }
    for (let k = 0; k < 6; k += 1) {
      const q = (clock * 0.45 + k / 6 + item.id * 0.11) % 1;
      const mx = at.x + Math.sin(k * 2.3 + clock * 0.9) * s * 0.32;
      const my = at.y - s * (0.05 + q * 1.1);
      circle(mx, my, Math.max(1, s * 0.016 * (1 - q)), `rgba(${look.glow},${(0.8 * Math.sin(q * Math.PI)).toFixed(3)})`);
    }
    const type = item.type;
    if (type === 'retro' || type === 'zola') {
      // The ground cracks under the weight.
      ctx.save();
      ctx.strokeStyle = 'rgba(60,50,40,0.35)';
      ctx.lineWidth = Math.max(1, s * 0.008);
      ctx.beginPath();
      [[-0.3, 0.02, -0.18, 0.05, -0.1, 0.01], [0.32, -0.01, 0.2, 0.04, 0.1, 0.0], [0.05, 0.07, 0.12, 0.1, 0.22, 0.09]].forEach(([x0, y0, x1, y1, x2, y2]) => {
        ctx.moveTo(at.x + x0 * s, at.y + y0 * s);
        ctx.lineTo(at.x + x1 * s, at.y + y1 * s);
        ctx.lineTo(at.x + x2 * s, at.y + y2 * s);
      });
      ctx.stroke();
      ctx.restore();
    }
    if (type === 'zola') {
      // Embers rising off him.
      for (let k = 0; k < 5; k += 1) {
        const q = (clock * 0.7 + k / 5 + item.id * 0.3) % 1;
        circle(at.x + Math.sin(k * 1.7 + clock * 2) * s * 0.25, at.y - s * (0.2 + q * 1.0), Math.max(0.8, s * 0.014 * (1 - q)), `rgba(255,${120 + k * 20},40,${(0.9 * (1 - q)).toFixed(3)})`);
      }
    } else if (type === 'caruso' && state.moving) {
      // Speed lines trailing behind the run.
      for (let k = 0; k < 3; k += 1) line(at.x - face * s * (0.25 + k * 0.05), at.y - s * (0.3 + k * 0.18), at.x - face * s * (0.55 + k * 0.08), at.y - s * (0.3 + k * 0.18), 'rgba(255,255,255,0.6)', Math.max(1, s * 0.012));
    } else if (type === 'kuza') {
      // Two small stars circling the brothers.
      for (let k = 0; k < 2; k += 1) {
        const a = clock * 2.2 + k * Math.PI;
        shuriken(at.x + Math.cos(a) * s * 0.45, at.y - s * 0.45 + Math.sin(a) * s * 0.12, s * 0.045, k ? YELLOW : '#ff2d55', clock * 9);
      }
    } else if (type === 'blade') {
      // A low red mist around his boots.
      for (let k = 0; k < 5; k += 1) {
        const q = (clock * 0.5 + k / 5 + item.id * 0.2) % 1;
        circle(at.x + (k - 2) * s * 0.1 + Math.sin(clock + k) * s * 0.03, at.y - s * q * 0.15, s * (0.06 + q * 0.05), `rgba(120,10,24,${(0.3 * (1 - q)).toFixed(3)})`);
      }
    } else if (type === 'capi') {
      // Lilac blossom drifting down around him.
      for (let k = 0; k < 5; k += 1) {
        const q = (clock * 0.25 + k / 5 + item.id * 0.17) % 1;
        oval(at.x + Math.sin(k * 2.1 + q * 6) * s * 0.4, at.y - s * (1.2 - q * 1.15), s * 0.022, s * 0.013, `rgba(214,170,255,${(0.9 * Math.sin(q * Math.PI)).toFixed(3)})`, q * 6 + k);
      }
    } else if (type === 'johno') {
      // Smoke curling up from the cigarette.
      const cx = at.x + face * s * 0.15;
      const cy = at.y - s * 0.85;
      for (let k = 0; k < 4; k += 1) {
        const q = (clock * 0.4 + k / 4 + item.id * 0.13) % 1;
        circle(cx + face * Math.sin(q * 5 + k) * s * 0.05, cy - q * s * 0.45, s * (0.02 + q * 0.04), `rgba(225,225,230,${(0.45 * (1 - q)).toFixed(3)})`);
      }
    } else if (type === 'polarise') {
      // Ice cold: frost crystals drifting down round him.
      for (let k = 0; k < 5; k += 1) {
        const q = (clock * 0.2 + k / 5 + item.id * 0.19) % 1;
        const fx = at.x + Math.sin(k * 2.4 + q * 4) * s * 0.38;
        const fy = at.y - s * (1.15 - q * 1.1);
        const r = s * 0.024;
        const color = `rgba(214,240,255,${(0.9 * Math.sin(q * Math.PI)).toFixed(3)})`;
        [0, 1, 2].forEach(i => line(fx - Math.cos(q * 3 + (i * Math.PI) / 3) * r, fy - Math.sin(q * 3 + (i * Math.PI) / 3) * r, fx + Math.cos(q * 3 + (i * Math.PI) / 3) * r, fy + Math.sin(q * 3 + (i * Math.PI) / 3) * r, color, Math.max(0.8, s * 0.006)));
      }
    } else if (type === 'bigtoe') {
      // The diamonds flash: little stars of light winking on and off round him.
      for (let k = 0; k < 4; k += 1) {
        const q = (clock * 0.9 + k * 0.27 + item.id * 0.13) % 1;
        if (q > 0.35) continue;
        const r = s * 0.05 * Math.sin((q / 0.35) * Math.PI);
        ctx.beginPath();
        starPath(ctx, at.x + Math.sin(k * 2.1 + item.id) * s * 0.36, at.y - s * (0.35 + ((k * 0.37) % 1) * 0.7), r, r * 0.22, 4, 0);
        ctx.fillStyle = k % 2 ? '#ffffff' : '#ffe28a';
        ctx.fill();
      }
    } else if (type === 'maliq') {
      // Three cards float round him, glowing.
      for (let k = 0; k < 3; k += 1) {
        const a = clock * 1.3 + (k * TAU) / 3;
        const front = Math.sin(a) > 0;
        if (front) continue;
        heroGlow(at.x + Math.cos(a) * s * 0.42, at.y - s * 0.6 + Math.sin(a) * s * 0.1, s * 0.12, look.glow, 0.4);
        playCard(at.x + Math.cos(a) * s * 0.42, at.y - s * 0.6 + Math.sin(a) * s * 0.1, s * 0.12, CARD_COLORS[k], Math.cos(a) * 0.3);
      }
    }
  };
  // What floats in front of a hero after he is drawn: Maliq's cards on their way round.
  const heroFront = (item, at, s) => {
    if (item.type !== 'maliq') return;
    for (let k = 0; k < 3; k += 1) {
      const a = clock * 1.3 + (k * TAU) / 3;
      if (Math.sin(a) <= 0) continue;
      heroGlow(at.x + Math.cos(a) * s * 0.42, at.y - s * 0.6 + Math.sin(a) * s * 0.1, s * 0.12, HERO_LOOKS.maliq.glow, 0.4);
      playCard(at.x + Math.cos(a) * s * 0.42, at.y - s * 0.6 + Math.sin(a) * s * 0.1, s * 0.12, CARD_COLORS[k], Math.cos(a) * 0.3);
    }
  };
  // Outside a fight the crew only stands and breathes, which hardly changes from one frame to the next. At rest
  // each member keeps a picture per step of the breath, painted afresh after a while; while a flourish plays or
  // an eye blinks it is painted ten times a second into a picture of its own, the members out of step so they
  // never all fall on one frame, and in between the picture is laid down again.
  const CALM_FPS = 10;
  const BREATH_STEPS = 6;
  const REST_KEEP = 20;
  const REST_MAX = 160;
  const calmSprites = new Map();
  const restSprites = new Map();
  // The step of the breath a member at rest shows now, or null while a flourish plays or an eye blinks. The others
  // of a group keep their own time.
  const resting = (spec, t, seed) => !(spec.idle && (t + seed * 0.37) % spec.idle.every < clipEnd(spec.idle.clip)) && !blinkAt(clock, seed);
  const restFrame = item => {
    const spec = HERO_SPECS[item.type];
    const t = clock + item.id * 1.7;
    if (!resting(spec, t, item.id) || (spec.group || []).some(m => !resting(m.spec, t + m.phase, item.id + m.seed))) return null;
    return Math.floor((((t * 2.1) / TAU) % 1) * BREATH_STEPS);
  };
  // A member standing still keeps within this box round the feet, the longest blade and the tallest hat included.
  const CALM_BOX = { l: 0.7, r: 1, t: 1.45, b: 0.25 };
  const paintCalm = (item, state, s, opts, old) => {
    const left = Math.ceil(s * CALM_BOX.l);
    const top = Math.ceil(s * CALM_BOX.t);
    const w = left + Math.ceil(s * CALM_BOX.r);
    const h = top + Math.ceil(s * CALM_BOX.b);
    const c = old || document.createElement('canvas');
    c.width = Math.ceil(w * dpr);
    c.height = Math.ceil(h * dpr);
    const main = ctx;
    ctx = c.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, left * dpr, top * dpr);
    let muzzle;
    try {
      muzzle = drawHero(item, state, s, opts);
    } finally {
      ctx = main;
    }
    return { c, left, top, w, h, muzzle, at: clock };
  };
  const stampCrew = (item, state, s, opts) => {
    const size = `${item.type}:${item.level}:${Math.round(s * dpr)}:${dpr}`;
    const rest = restFrame(item);
    let sprite;
    if (rest !== null) {
      const key = `${item.id}:${size}:${rest}`;
      sprite = restSprites.get(key);
      if (sprite) restSprites.delete(key);
      if (!sprite || clock - sprite.at > REST_KEEP) sprite = paintCalm(item, state, s, opts, sprite && sprite.c);
      restSprites.set(key, sprite);
      if (restSprites.size > REST_MAX) restSprites.delete(restSprites.keys().next().value);
    } else {
      const step = Math.floor(clock * CALM_FPS + ((item.id * 0.618) % 1));
      sprite = calmSprites.get(item.id);
      if (!sprite || sprite.size !== size || sprite.step !== step) {
        sprite = { ...paintCalm(item, state, s, opts, sprite && sprite.size === size ? sprite.c : null), size, step };
        calmSprites.set(item.id, sprite);
      }
    }
    ctx.drawImage(sprite.c, -sprite.left, -sprite.top, sprite.w, sprite.h);
    return sprite.muzzle;
  };
  const drawCrew = item => {
    const u = cw();
    const tier = Math.min(5, tierOf(item.level));
    const state = item.crew || { aim: 0.35, face: item.id % 2 ? 1 : -1, recoil: 0, x: item.x + 0.5, y: item.y + 0.5, walk: 0 };
    const c = proj(item.x + 0.5, item.y + 0.55);
    // The figure stands where the hero is now, which can be off the post during a fight.
    const at = proj(state.x, state.y + 0.05);
    // The post: a glowing gold ring, a wide one round it with a slow turning dash inside, glowing in the hero's
    // colour. Away from home there is none.
    if (!heist) {
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      glowAt(c.x, c.y, u * 0.6, 'rgba(186,236,183,ALPHA)', 0.35);
      ctx.restore();
      ctx.beginPath();
      ctx.ellipse(c.x, c.y, u * 0.38, u * 0.38 * TILT, 0, 0, TAU);
      ctx.fillStyle = 'rgba(20,20,21,0.06)';
      ctx.fill();
      ctx.strokeStyle = GOLD;
      ctx.lineWidth = Math.max(1.5, u * 0.03);
      ctx.stroke();
      const look = HERO_LOOKS[item.type];
      heroGlow(c.x, c.y, u * 1.1, look.glow, 0.25);
      ctx.save();
      ctx.beginPath();
      ctx.ellipse(c.x, c.y, u * 0.62, u * 0.62 * TILT, 0, 0, TAU);
      ctx.strokeStyle = GOLD;
      ctx.lineWidth = Math.max(2, u * 0.05);
      ctx.stroke();
      ctx.setLineDash([u * 0.12, u * 0.08]);
      ctx.lineDashOffset = -clock * u * 0.2;
      ctx.beginPath();
      ctx.ellipse(c.x, c.y, u * 0.5, u * 0.5 * TILT, 0, 0, TAU);
      ctx.strokeStyle = `rgba(${look.glow},0.8)`;
      ctx.lineWidth = Math.max(1.5, u * 0.025);
      ctx.stroke();
      ctx.restore();
    }
    // Flight cases on both sides of the post from tier 2, with gold corners from tier 4.
    const cases = () => {
      if (tier < 2 || heist) return;
      const trim = tier >= 4 ? GOLD : '#b9bcc2';
      const w = u * 0.24;
      const h = u * 0.15;
      const cs = u * 0.04;
      [-1, 1].forEach(k => {
        const x = c.x + k * u * 0.27 - w / 2;
        const y = c.y + u * 0.16 - h;
        rect(x + u * 0.02, y + h - u * 0.01, w, u * 0.04, 'rgba(0,0,0,0.16)');
        rect(x, y, w, h, '#1d1d20');
        rect(x, y, w, h * 0.22, '#2e2e33');
        rect(x, y + h * 0.22, w, Math.max(1, u * 0.012), trim);
        [x, x + w - cs].forEach(cx => {
          rect(cx, y, cs, cs, trim);
          rect(cx, y + h - cs, cs, cs, trim);
        });
        rect(x + w * 0.34, y + h * 0.52, w * 0.32, h * 0.14, trim);
      });
    };
    const behind = state.y < item.y + 0.7;
    if (!behind) cases();
    const s = crewSize(item.level) * HERO_LOOKS[item.type].scale;
    const fighting = Boolean(raid && raid.phase === 'fight');
    const m = motion(state);
    // The rig walks and breathes on its own; only a leap lifts the whole figure.
    let bob = 0;
    // A leap for a special: up in an arc, the shadow shrinking underneath.
    const leap = state.hop ? Math.sin(clamp(state.hop.t / state.hop.dur, 0, 1) * Math.PI) * s * state.hop.h : 0;
    oval(at.x, at.y, s * 0.2 * (1 - leap / (s * 3)), s * 0.06 * (1 - leap / (s * 3)), 'rgba(0,0,0,0.18)');
    heroBase(item, at, s, m.turn >= 0 ? 1 : -1);
    bob += leap;
    ctx.save();
    ctx.translate(at.x, at.y - bob);
    // A spin turns him round and round, five times a second.
    const turn = state.spin > 0 ? Math.cos((state.spinFor - state.spin) * TAU * 5) * state.face : m.turn;
    ctx.scale(Math.abs(turn) < 0.2 ? Math.sign(turn || 1) * 0.2 : turn, 1);
    const opts = { fight: fighting, gait: m.gait, aim: localAim(state.aim, state.face) };
    const calm = !fighting && m.gait < 0.01 && !state.hop && !(state.special > 0) && !(state.spin > 0) && ctx === view;
    const muzzle = calm ? stampCrew(item, state, s, opts) : drawHero(item, state, s, opts);
    ctx.restore();
    heroFront(item, at, s);
    if (item.crew) item.crew.muzzle = { x: at.x + state.face * muzzle.x, y: at.y - bob + muzzle.y };
    if (behind) cases();
    if (stars(item.level)) starRow(at.x, at.y + u * 0.3, u * 0.035, stars(item.level));
  };
  // A hero who fell: down, still, then gone, with a small halo rising.
  const drawFallen = f => {
    const s = crewSize(f.level) * HERO_LOOKS[f.type].scale;
    const at = proj(f.x, f.y + 0.05);
    const down = Math.min(1, f.t / 0.35);
    const fade = f.t > 1.4 ? Math.max(0, 1 - (f.t - 1.4) / 1.2) : 1;
    ctx.save();
    ctx.globalAlpha = fade;
    ctx.translate(at.x, at.y);
    ctx.scale(f.face || 1, 1);
    drawHero(f, f, s, { down: true, fall: down });
    ctx.restore();
    // Fallen for good: a halo rises.
    if (f.t > 0.5) {
      ctx.save();
      ctx.globalAlpha = fade;
      ctx.beginPath();
      ctx.ellipse(at.x, at.y - s * (0.4 + (f.t - 0.5) * 0.5), s * 0.12, s * 0.04, 0, 0, TAU);
      ctx.strokeStyle = YELLOW;
      ctx.lineWidth = Math.max(1.5, s * 0.025);
      ctx.stroke();
      ctx.restore();
    }
  };

  // Ruins and scaffolds take the place of a building while it is down.
  const drawRuin = item => {
    const u = cw();
    const f = footprint(item);
    const mat = matOf(item.level);
    const rand = mulberry32(item.id * 97);
    // Scorched ground under the debris.
    const a = proj(f.x + 0.1, f.y + 0.15);
    const b = proj(f.x + f.w - 0.1, f.y + f.d - 0.05);
    oval((a.x + b.x) / 2, (a.y + b.y) / 2, (b.x - a.x) / 2, (b.y - a.y) / 2, 'rgba(40,32,26,0.16)');
    oval((a.x + b.x) / 2, (a.y + b.y) / 2, (b.x - a.x) * 0.3, (b.y - a.y) * 0.3, 'rgba(40,32,26,0.12)');
    // What is left of the back wall: two broken stubs in the building's own material, rebar sticking out.
    [[0.1, 0.42, 0.55], [f.w * 0.55, f.w * 0.3, 0.32]].forEach(([ox, w, h]) => {
      const g = block(f.x + ox, f.y + 0.2, w, 0.12, h, mat.roof, mat.wall);
      rect(g.l, g.m, g.w, g.fh, 'rgba(30,25,20,0.25)');
      for (let k = 0; k < 3; k += 1) {
        const x = g.l + g.w * (0.15 + rand() * 0.7);
        rect(x, g.m, Math.max(1, u * 0.03), g.fh * (0.3 + rand() * 0.5), 'rgba(20,18,16,0.3)');
      }
      for (let k = 0; k < 2; k += 1) {
        const x = g.l + g.w * (0.25 + k * 0.45);
        line(x, g.t + 1, x + (rand() - 0.5) * u * 0.2, g.t - u * (0.12 + rand() * 0.1), '#5a4a3c', Math.max(1, u * 0.02));
      }
    });
    // Rubble: concrete and pieces of the old walls, biggest at the back.
    const n = 4 + f.w * f.d * 2;
    const chunks = [];
    for (let i = 0; i < n; i += 1) {
      const s = 0.12 + rand() * 0.24;
      chunks.push({ x: f.x + 0.15 + rand() * (f.w - 0.4), y: f.y + 0.35 + rand() * (f.d - 0.55), s, h: s * (0.35 + rand() * 0.55), own: i % 3 === 0 });
    }
    chunks.sort((p, q) => p.y - q.y).forEach((c, i) => {
      const top = c.own ? mat.roof : ['#c9c8c3', '#a9a8a2', '#d9d8d3'][i % 3];
      const front = c.own ? mat.wall : ['#9ea3a8', '#85858a', '#b4b3ad'][i % 3];
      block(c.x, c.y, c.s, c.s * 0.7, c.h, top, front);
    });
    // A charred beam across the pile.
    const c = proj(f.x + f.w / 2, f.y + f.d / 2, 0.3);
    line(c.x - u * 0.35, c.y + u * 0.05, c.x + u * 0.25, c.y - u * 0.25, '#3d2e22', Math.max(2, u * 0.06));
    line(c.x - u * 0.35, c.y + u * 0.05, c.x + u * 0.25, c.y - u * 0.25, 'rgba(255,255,255,0.12)', Math.max(1, u * 0.015));
    // Embers glow while the raid is on, smoke rises.
    if (raid && raid.phase !== 'end') {
      glowAt(c.x, c.y, u * 0.35, 'rgba(255,120,40,ALPHA)', 0.25 + Math.sin(clock * 5 + item.id) * 0.08);
      smoke(c.x, c.y - u * 0.2, u * 1.4, item.id, '90,90,95');
    }
  };
  const drawScaffold = item => {
    const u = cw();
    const f = footprint(item);
    const t = TYPES[item.type];
    const p = 1 - item.rebuild / REBUILD;
    const h = 1.2 * (0.3 + p * 0.7);
    const g = block(f.x + 0.1, f.y + 0.1, f.w - 0.2, f.d - 0.2, h * p, '#e7e6e1', '#d6d5cf');
    const top = proj(f.x + 0.1, f.y + f.d - 0.1, h);
    ctx.strokeStyle = '#a07a4a';
    ctx.lineWidth = Math.max(1.5, u * 0.03);
    for (let x = g.l; x <= g.r + 1; x += g.w / Math.max(2, t.w * 2)) line(x, top.y, x, g.b, '#a07a4a', Math.max(1.5, u * 0.03));
    for (let y = top.y; y < g.b; y += u * 0.3) line(g.l, y, g.r, y, '#a07a4a', Math.max(1, u * 0.02));
    rect(g.l, top.y - u * 0.12, g.w, u * 0.06, 'rgba(20,20,21,0.15)');
    rect(g.l, top.y - u * 0.12, g.w * p, u * 0.06, MINT_D);
  };

  const DRAWERS = {
    hq: drawHq, plakat: drawPlakat, sticker: drawSticker, wall: drawWall, studio: drawStudio, party: drawParty, billboard: drawBillboard, buehne: drawBuehne,
    court: drawCourt, skate: drawSkate, radio: drawRadio,
    stand: drawStand, shop: drawShop, truck: drawTruck, popup: drawPopup, zelt: drawZelt, flagship: drawFlagship,
    automat: drawAutomat, concept: drawConcept, halle: drawHalle,
    caruso: drawCrew, retro: drawCrew, zola: drawCrew, kuza: drawCrew, blade: drawCrew, capi: drawCrew, johno: drawCrew, maliq: drawCrew, polarise: drawCrew, bigtoe: drawCrew };
  // While the map is dragged, only where things stand changes from one frame to the next: each building is laid
  // down from a picture of itself, pixel for pixel, repainted up to twenty-four times a second and out of step with the
  // others. At rest, in a fight and while zooming every building is painted live. A few pictures a frame at most keep
  // a drag from stalling: a building still waiting for its first is painted live, one due a new one keeps its last.
  const MOVE_FPS = 24;
  const MOVE_NEW = 6;
  const MOVE_MAX = 80;
  const movePictures = new Map();
  let moveBudget = 0;
  const stampItem = item => {
    const u = cw();
    const size = `${item.type}:${item.level}:${u}:${dpr}:${hustlerLook.brand}`;
    const step = Math.floor(clock * MOVE_FPS + ((item.id * 0.618) % 1));
    let pic = movePictures.get(item.id);
    const kept = pic && pic.size === size;
    if (!kept && moveBudget <= 0) return false;
    const at = proj(item.x, item.y);
    if (!kept || (pic.step !== step && moveBudget > 0)) {
      moveBudget -= 1;
      const t = TYPES[item.type];
      const top = u * (heightOf(item.type, item.level) + 1.4);
      const c = kept ? pic.c : document.createElement('canvas');
      if (!kept) {
        c.width = Math.ceil(u * (t.w + 1.4) * dpr);
        c.height = Math.ceil((top + u * (t.d * TILT + 0.6)) * dpr);
      }
      // The picture starts on a whole pixel of the screen, so it lands on the pixels it was painted for.
      const x0 = Math.round((at.x - u * 0.7) * dpr) / dpr;
      const y0 = Math.round((at.y - top) * dpr) / dpr;
      const main = ctx;
      ctx = c.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, c.width, c.height);
      ctx.setTransform(dpr, 0, 0, dpr, -x0 * dpr, -y0 * dpr);
      try {
        DRAWERS[item.type](item);
      } finally {
        ctx = main;
      }
      pic = { c, size, step, dx: at.x - x0, dy: at.y - y0 };
    }
    movePictures.delete(item.id);
    movePictures.set(item.id, pic);
    if (movePictures.size > MOVE_MAX) movePictures.delete(movePictures.keys().next().value);
    ctx.drawImage(pic.c, Math.round((at.x - pic.dx) * dpr) / dpr, Math.round((at.y - pic.dy) * dpr) / dpr, pic.c.width / dpr, pic.c.height / dpr);
    return true;
  };
  const drawBuilding = item => {
    if (ctx !== view || item.id < 0 || kindOf(item) === 'crew' || raid || heist || !mapDragged() || !stampItem(item)) DRAWERS[item.type](item);
  };
  const drawItem = item => {
    if (item.ruin) return drawRuin(item);
    if (item.rebuild) return drawScaffold(item);
    const f = footprint(item);
    // Buildings cast a soft block shadow; the crew only has its post ring.
    if (kindOf(item) !== 'crew') {
      const a = proj(f.x + 0.04, f.y + 0.2);
      const b = proj(f.x + f.w + 0.12, f.y + f.d + 0.08);
      rect(a.x, a.y, b.x - a.x, b.y - a.y, 'rgba(0,0,0,0.07)');
    }
    const wobble = item.shake > 0 ? Math.sin(clock * 80) * cw() * 0.02 : 0;
    const pop = item.pop > 0 ? 1 + Math.sin((item.pop / 0.4) * Math.PI) * 0.06 : 1;
    if (wobble || pop !== 1) {
      const c = proj(f.x + f.w / 2, f.y + f.d);
      ctx.save();
      ctx.translate(c.x + wobble, c.y);
      ctx.scale(pop, pop);
      ctx.translate(-c.x, -c.y);
      drawBuilding(item);
      ctx.restore();
    } else drawBuilding(item);
  };

  // ---------------------------------------------------------------------------------------------
  // The hustler: streetwear head to toe, white sneakers, maybe a chain, phone or cash in hand.
  // Drawn standing on (x, y).

  // A MIXEDOWN bag hanging from the hand at (x, y): black with the ring logo, rope handles and tissue in the brand colour.
  const shoppingBag = (x, y, s, swing, brand) => {
    const w = s * 0.17;
    const h = s * 0.19;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(swing);
    ctx.strokeStyle = '#3a3a3d';
    ctx.lineWidth = Math.max(1, s * 0.012);
    ctx.beginPath();
    ctx.moveTo(-w * 0.32, s * 0.05);
    ctx.lineTo(0, 0);
    ctx.lineTo(w * 0.32, s * 0.05);
    ctx.stroke();
    poly([[-w * 0.38, s * 0.05], [-w * 0.05, s * 0.0], [w * 0.1, s * 0.05]], brand);
    poly([[-w * 0.1, s * 0.05], [w * 0.2, s * 0.01], [w * 0.4, s * 0.05]], PAPER);
    rect(-w / 2, s * 0.05, w, h, INK);
    rect(-w / 2, s * 0.05, w * 0.18, h, 'rgba(255,255,255,0.08)');
    rect(-w / 2, s * 0.05, w, Math.max(1, s * 0.012), '#3a3a3d');
    logoRing(0, s * 0.05 + h * 0.52, w * 0.26, w * 0.13, Math.max(0.8, s * 0.012), PAPER);
    ctx.restore();
  };
  // Bodies turn and fall into step over a few frames instead of snapping: the facing squeezes through
  // the turn, and the stride swells in and out with how much they really move.
  const motion = body => {
    const dt = clamp(clock - (body.seenAt === undefined ? clock : body.seenAt), 0, 0.1);
    body.seenAt = clock;
    const face = body.face || 1;
    body.turn = body.turn === undefined ? face : ease(body.turn, face, 16, dt);
    const going = clock - (body.movedAt === undefined ? -9 : body.movedAt) < 0.12 ? 1 : 0;
    body.gait = body.gait === undefined ? going : ease(body.gait, going, 9, dt);
    return { turn: Math.abs(body.turn) < 0.2 ? (body.turn < 0 ? -0.2 : 0.2) : body.turn, gait: body.gait };
  };
  const EMOTES = {
    inspect: (x, y, r) => {
      ctx.strokeStyle = INK;
      ctx.lineWidth = Math.max(1.2, r * 0.18);
      ctx.beginPath();
      ctx.arc(x - r * 0.1, y - r * 0.1, r * 0.38, 0, TAU);
      ctx.stroke();
      line(x + r * 0.17, y + r * 0.17, x + r * 0.45, y + r * 0.45, INK, Math.max(1.5, r * 0.22));
    },
    count: (x, y, r) => drawCash(ctx, x, y, r * 0.5, false),
    wave: (x, y, r) => label('Hallo', x, y + 0.5, r * 0.62, INK),
    phone: (x, y, r) => {
      rect(x - r * 0.24, y - r * 0.42, r * 0.48, r * 0.84, INK);
      rect(x - r * 0.17, y - r * 0.32, r * 0.34, r * 0.56, MINT);
      drawCash(ctx, x, y - r * 0.04, r * 0.22, false);
    },
    coffee: (x, y, r) => {
      rect(x - r * 0.25, y - r * 0.2, r * 0.42, r * 0.5, INK);
      ctx.beginPath();
      ctx.arc(x + r * 0.2, y + r * 0.03, r * 0.13, -1.4, 1.4);
      ctx.strokeStyle = INK;
      ctx.lineWidth = Math.max(1, r * 0.1);
      ctx.stroke();
    }
  };
  const drawHustler = () => {
    if (K.inside) return;
    const u = cw();
    const at = proj(K.x, K.y);
    const s = u * 0.95;
    if (K.dead) {
      const p = Math.min(1, K.dead / 0.6);
      ctx.save();
      ctx.translate(at.x, at.y);
      ctx.rotate(-p * Math.PI / 2);
      ctx.globalAlpha = Math.max(0.35, 1 - K.dead * 0.2);
      drawPerson(0, 0, s, { dead: true, noShadow: true, owner: K });
      ctx.restore();
      return;
    }
    const exposed = raid && raid.phase !== 'end';
    if (exposed) {
      ctx.save();
      ctx.globalCompositeOperation = 'multiply';
      oval(at.x, at.y, u * 0.42, u * 0.42 * TILT, `rgba(255,75,31,${0.25 + Math.sin(clock * 8) * 0.12})`);
      ctx.restore();
    }
    const m = motion(K);
    stampPerson(at.x, at.y, s, { owner: K, face: m.turn, walk: K.walk, gait: m.gait, moving: K.moving || m.gait > 0.05, pose: K.goal ? null : K.emote });
    if (K.hurt > 0) {
      ctx.save();
      ctx.globalCompositeOperation = 'source-atop';
      ctx.restore();
      oval(at.x, at.y - s * 0.5, s * 0.3, s * 0.5, 'rgba(255,75,31,0.25)');
    }
    // Health while the hustler is out in a raid.
    if (exposed || K.hp < hustlerMaxHp()) {
      const bw = u * 0.6;
      const by = at.y - s * 1.12;
      rect(at.x - bw / 2 - 1, by - 1, bw + 2, 6, 'rgba(20,20,21,0.75)');
      rect(at.x - bw / 2, by, bw * clamp(K.hp / hustlerMaxHp(), 0, 1), 4, K.hp / hustlerMaxHp() < 0.35 ? SIGNAL : MINT);
    }
    if (!K.goal && K.emote && EMOTES[K.emote] && !exposed) {
      const r = Math.max(9, u * 0.17);
      const bx = at.x + u * 0.22 * K.face;
      const by = at.y - s * 1.2;
      circle(bx, by, r, 'rgba(255,255,255,0.94)');
      poly([[bx - r * 0.3 * K.face, by + r * 0.8], [bx - r * 0.65 * K.face, by + r * 1.3], [bx - r * 0.1 * K.face, by + r * 0.95]], 'rgba(255,255,255,0.94)');
      EMOTES[K.emote](bx, by, r);
    }
  };

  // ---------------------------------------------------------------------------------------------
  // The Bootlegs: small knock-off gangsters in hoodies, with a fake logo on the chest.

  const HOODIES = ['#4b5a3c', '#55575c', '#6a2c3a', '#2f3c58', '#5d4a36'];
  const drawEnemy = enemy => {
    const u = cw();
    const g = GANG[enemy.type];
    const s = u * g.size * 2.1 * (enemy.elite ? ELITE.size : 1) * (enemy.copy ? COPY.size : 1);
    const ground = proj(enemy.x, enemy.y);
    const at = proj(enemy.x, enemy.y, enemy.h || 0);
    oval(ground.x, ground.y, s * 0.24, s * 0.07, 'rgba(0,0,0,0.2)');
    if (!enemy.fly) drawFoeRing(enemy, g, ground, s, u);
    const m = motion(enemy);
    const gait = enemy.stun || enemy.swing > 0 ? 0 : m.gait;
    // A hit squashes the Bootleg, knocks it back a little and sparks on the chest.
    const hit = enemy.hit > 0 ? enemy.hit / 0.12 : 0;
    ctx.save();
    // A copy is a little see-through.
    if (enemy.copy) ctx.globalAlpha *= 0.72;
    ctx.translate(at.x, at.y);
    if (enemy.fly) ctx.rotate(enemy.fly.t * 12);
    ctx.scale(enemy.fly ? enemy.face : m.turn, 1);
    if (hit) {
      ctx.rotate(-hit * 0.18);
      ctx.scale(1 + hit * 0.14, 1 - hit * 0.12);
    }
    if (enemy.type === 'roller') {
      // An e-scooter: deck, wheels, a tall stem with handlebars.
      rect(-s * 0.3, -s * 0.06, s * 0.6, s * 0.04, '#2a2a2e');
      circle(-s * 0.26, -s * 0.02, s * 0.045, INK);
      circle(s * 0.26, -s * 0.02, s * 0.045, INK);
      line(s * 0.26, -s * 0.05, s * 0.2, -s * 0.6, '#5a6168', Math.max(1.5, s * 0.03));
      rect(s * 0.14, -s * 0.62, s * 0.12, s * 0.03, INK);
      rect(-s * 0.3, -s * 0.07, s * 0.08, s * 0.02, MINT);
      ctx.translate(0, -s * 0.06);
    }
    stampBootleg(enemy, s * 0.9, gait);
    ctx.restore();
    if (enemy.gold && !enemy.fly) {
      // A glint that runs over the golden Bootleg now and then.
      const k = (clock * 0.8 + enemy.id * 0.37) % 1;
      if (k < 0.3) {
        ctx.save();
        ctx.globalAlpha = Math.sin((k / 0.3) * Math.PI);
        ctx.beginPath();
        starPath(ctx, at.x + s * 0.12, at.y - s * (0.3 + k * 1.2), s * 0.09, s * 0.02, 4, 0);
        ctx.fillStyle = '#fff6c8';
        ctx.fill();
        ctx.restore();
      }
    }
    if (hit) {
      ctx.save();
      ctx.globalAlpha = hit;
      ctx.beginPath();
      starPath(ctx, at.x, at.y - s * 0.42, s * 0.24 * (1.4 - hit * 0.4), s * 0.06, 4, clock * 3);
      ctx.fillStyle = PAPER;
      ctx.fill();
      ctx.restore();
    }
    if (enemy.stun > 0 && !enemy.fly) {
      for (let i = 0; i < 3; i += 1) {
        const a = clock * 5 + (i / 3) * TAU;
        ctx.beginPath();
        starPath(ctx, at.x + Math.cos(a) * s * 0.2, at.y - s * 0.95 + Math.sin(a) * s * 0.06, s * 0.05, s * 0.02);
        ctx.fillStyle = YELLOW;
        ctx.fill();
      }
    }
    if (enemy.slow > 0) {
      // Frozen: an ice crystal turns over the head.
      const k = Math.max(3, s * 0.07);
      const ix = at.x;
      const iy = at.y - s * 1.12;
      for (let i = 0; i < 3; i += 1) {
        const a = clock * 1.5 + (i * Math.PI) / 3;
        line(ix - Math.cos(a) * k, iy - Math.sin(a) * k, ix + Math.cos(a) * k, iy + Math.sin(a) * k, '#9fe4ff', Math.max(1.2, k * 0.28));
      }
      circle(ix, iy, k * 0.3, '#eafaff');
    }
    if (enemy.hyped && !enemy.fly) {
      // Driven on: two red chevrons over the head.
      const k = Math.max(3, s * 0.08);
      const y = at.y - s * 1.12 - Math.abs(Math.sin(clock * 6 + enemy.id)) * k * 0.6;
      [0, 1].forEach(i => polyline([[at.x - k, y + i * k * 0.8], [at.x, y - k * 0.7 + i * k * 0.8], [at.x + k, y + i * k * 0.8]], Math.max(1.5, k * 0.35), SIGNAL));
    }
    if (enemy.hp < enemy.max || enemy.elite) {
      const w = Math.max(16, s * 0.5);
      const y = at.y - s * (g.boss ? 1.3 : 1.02);
      rect(at.x - w / 2 - 1, y - 1, w + 2, 5, 'rgba(20,20,21,0.6)');
      rect(at.x - w / 2, y, w * clamp(enemy.hp / enemy.max, 0, 1), 3, g.boss || enemy.elite ? SIGNAL : PAPER);
    }
  };
  // On the ground round a Bootleg: the red ring of an elite, the green one of a Flicker, the beat of an Einpeitscher.
  const footRing = (ground, rx, color, width) => {
    ctx.beginPath();
    ctx.ellipse(ground.x, ground.y, rx, rx * TILT, 0, 0, TAU);
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.stroke();
  };
  const drawFoeRing = (enemy, g, ground, s, u) => {
    if (enemy.elite) footRing(ground, s * (0.32 + Math.sin(clock * 5 + enemy.id) * 0.03), 'rgba(200,16,46,0.75)', Math.max(2, s * 0.05));
    if (g.heal) footRing(ground, u * g.heal.r, 'rgba(95,211,90,0.22)', Math.max(1, u * 0.03));
    if (g.aura) {
      const p = (clock * 0.7 + enemy.id * 0.13) % 1;
      footRing(ground, u * g.aura.r * (0.3 + p * 0.7), `rgba(200,16,46,${(0.45 * (1 - p)).toFixed(2)})`, Math.max(1.5, u * 0.04));
    }
  };
  // A KO: the Bootleg tumbles off in an arc, spinning, and is gone before it lands.
  const drawKo = f => {
    const p = f.t / f.dur;
    ctx.save();
    ctx.globalAlpha = p > 0.7 ? Math.max(0, 1 - (p - 0.7) / 0.3) : 1;
    drawEnemy({ ...f.enemy, x: f.x + f.vx * f.t, y: f.y + f.vy * f.t, h: Math.max(0, 0.1 + f.t * 3.4 - f.t * f.t * 4.5), fly: { t: f.t * (f.enemy.face > 0 ? -1.1 : 1.1) } });
    ctx.restore();
  };

  // A road case: black body, a lid seam, aluminium edges, corner caps and a handle. (x, y) is the bottom centre.
  const flightcase = (x, y, k, angle = 0) => {
    const w = k * 1.25;
    const h = k * 0.8;
    const top = k * 0.3;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    poly([[-w / 2, -h], [-w / 2 + top, -h - top], [w / 2 + top, -h - top], [w / 2, -h]], '#3a3a40');
    poly([[w / 2, 0], [w / 2, -h], [w / 2 + top, -h - top], [w / 2 + top, -top]], '#141417');
    rect(-w / 2, -h, w, h, '#232327');
    const edge = Math.max(1, k * 0.06);
    const metal = '#c9ccd1';
    rect(-w / 2, -h * 0.62, w, edge, metal);
    rect(-w / 2, -h, w, edge, metal);
    rect(-w / 2, -edge, w, edge, metal);
    [[-w / 2, -h], [w / 2 - k * 0.16, -h], [-w / 2, -k * 0.16], [w / 2 - k * 0.16, -k * 0.16]].forEach(([cx, cy]) => rect(cx, cy, k * 0.16, k * 0.16, metal));
    rect(-k * 0.2, -h * 0.4, k * 0.4, Math.max(1.5, k * 0.07), '#8d9399');
    label('MXD', 0, -h * 0.8, k * 0.16, 'rgba(255,255,255,0.75)');
    ctx.restore();
  };
  // A landed Flightcase: it squashes on impact, sits there, then sinks away.
  const drawCrate = f => {
    const u = cw();
    const p = f.t / f.dur;
    const at = proj(f.x, f.y);
    const k = u * 0.75 * f.size;
    const squash = f.t < 0.15 ? 1 - Math.sin((f.t / 0.15) * Math.PI) * 0.18 : 1;
    ctx.save();
    ctx.globalAlpha = p > 0.8 ? Math.max(0, 1 - (p - 0.8) / 0.2) : 1;
    oval(at.x + k * 0.15, at.y, k * 0.9, k * 0.28, 'rgba(0,0,0,0.25)');
    ctx.translate(at.x, at.y);
    ctx.scale(1 + (1 - squash) * 0.6, squash);
    flightcase(0, 0, k, f.spin * 0.25);
    ctx.restore();
  };
  // The other gang's stash: cardboard boxes of fakes on pallets, a van, a garage or a hall. Cleared out once you win.
  const BOX_TONES = [['#d9b27a', '#c49a62'], ['#cfa56c', '#b88d55'], ['#e0bd88', '#c9a26c'], ['#c79d66', '#ad854f']];
  const fakeBox = (x, y, w, d, h, base, tone) => {
    const [roof, front] = BOX_TONES[tone % BOX_TONES.length];
    const box = block(x, y, w, d, base + h, roof, front, base);
    rect(box.l + box.w * 0.45, box.t, box.w * 0.1, box.m - box.t, 'rgba(255,255,255,0.35)');
    rect(box.l, box.m, box.w, Math.max(1, box.u * 0.012), 'rgba(0,0,0,0.12)');
    return box;
  };
  const sprayTag = (x, y, size, text, color) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(-0.08);
    label(text, 0, 0, size, color, 'center', 800);
    ctx.restore();
  };
  const drawStash = prop => {
    const u = cw();
    const { x, y, w, d } = prop;
    const foot = proj(x + w / 2, y + d);
    oval(foot.x, foot.y, (w / 2 + 0.15) * u, u * 0.14, 'rgba(0,0,0,0.16)');
    if (prop.kind === 'pallet') {
      block(x, y, w, d, 0.12, '#b88d55', '#8f6a3c');
      if (prop.looted) {
        fakeBox(x + 0.55, y + 0.4, 0.45, 0.45, 0.22, 0.12, prop.tone);
        return;
      }
      const rows = 2 + (prop.tone % 2);
      for (let k = 0; k < rows; k += 1) {
        const base = 0.12 + k * 0.3;
        fakeBox(x + 0.05, y + 0.05, 0.48, d - 0.1, 0.3, base, prop.tone + k);
        const front = fakeBox(x + 0.55, y + 0.05, 0.5, d - 0.1, 0.3, base, prop.tone + k + 1);
        if (k === 0) sprayTag(front.l + front.w * 0.5, front.m + front.fh * 0.5, Math.max(6, u * 0.13), 'FAKE', '#ff4fa3');
      }
      return;
    }
    if (prop.kind === 'van') {
      const tones = [['#e9e7e1', '#d6d3cb'], ['#9aa3ab', '#858e96'], ['#2b3a55', '#22304a'], ['#e9e7e1', '#d6d3cb']][prop.tone];
      const body = block(x, y + 0.15, w * 0.72, d - 0.15, 0.95, tones[0], tones[1], 0.12);
      const cab = block(x + w * 0.72, y + 0.25, w * 0.28, d - 0.25, 0.7, tones[0], tones[1], 0.12);
      rect(cab.l + cab.w * 0.1, cab.m + cab.fh * 0.08, cab.w * 0.8, cab.fh * 0.36, '#5f7480');
      rect(cab.r - cab.w * 0.12, cab.m + cab.fh * 0.55, cab.w * 0.1, cab.fh * 0.12, '#ffe9a8');
      // The back is open and full of boxes.
      rect(body.l + body.w * 0.06, body.m + body.fh * 0.12, body.w * 0.62, body.fh * 0.8, '#1d1d20');
      if (!prop.looted) {
        for (let k = 0; k < 3; k += 1) {
          for (let j = 0; j < 2; j += 1) {
            const bx = body.l + body.w * (0.09 + k * 0.2);
            const by = body.m + body.fh * (0.5 - j * 0.34);
            rect(bx, by, body.w * 0.18, body.fh * 0.32, BOX_TONES[(prop.tone + k + j) % 4][1]);
            rect(bx + body.w * 0.08, by, body.w * 0.02, body.fh * 0.32, 'rgba(255,255,255,0.3)');
          }
        }
      }
      sprayTag(body.l + body.w * 0.85, body.m + body.fh * 0.35, Math.max(6, u * 0.12), 'B00TLEG', '#ff4fa3');
      [0.16, 0.86].forEach(fx => {
        const at = proj(x + w * fx, y + d);
        oval(at.x, at.y - u * 0.04, u * 0.14, u * 0.1, INK);
        oval(at.x, at.y - u * 0.05, u * 0.05, u * 0.04, '#8d9399');
      });
      return;
    }
    // A garage or a hall: corrugated walls, the door half up, the stash showing inside.
    const hall = prop.kind === 'hall';
    const tall = hall ? 1.9 : 1.35;
    const g = block(x, y, w, d, tall, hall ? '#5b6670' : '#66727d', hall ? '#77838e' : '#7d8a96');
    for (let k = 1; k < 14; k += 1) rect(g.l + (g.w * k) / 14, g.m, 1, g.fh, 'rgba(0,0,0,0.12)');
    if (hall) for (let k = 1; k < 5; k += 1) rect(g.l + (g.w * k) / 5, g.t, Math.max(1, u * 0.02), g.m - g.t, 'rgba(0,0,0,0.18)');
    const gate = { l: g.l + g.w * (hall ? 0.32 : 0.18), w: g.w * (hall ? 0.36 : 0.64) };
    const open = g.fh * (prop.looted ? 0.9 : 0.55);
    rect(gate.l, g.b - open, gate.w, open, '#18181a');
    rect(gate.l, g.m + g.fh * 0.12, gate.w, g.b - open - g.m - g.fh * 0.12, '#a7b0b8');
    for (let k = 0; k < 6; k += 1) rect(gate.l, g.m + g.fh * 0.12 + k * u * 0.06, gate.w, 1, 'rgba(0,0,0,0.18)');
    if (!prop.looted) {
      for (let k = 0; k < 4; k += 1) {
        const bw = gate.w * 0.2;
        rect(gate.l + gate.w * 0.06 + k * bw * 1.18, g.b - open * (k % 2 ? 0.55 : 0.75), bw, open * (k % 2 ? 0.55 : 0.75), BOX_TONES[(prop.tone + k) % 4][1]);
      }
    }
    // Their sign over the door, and a tag sprayed beside it.
    const sign = { w: gate.w * 0.9, h: Math.max(8, u * (hall ? 0.32 : 0.24)) };
    rect(gate.l + (gate.w - sign.w) / 2, g.m + g.fh * 0.02, sign.w, sign.h * 0.9, INK);
    label(hall ? 'FAKE FABRIK' : 'B00TLEG', gate.l + gate.w / 2, g.m + g.fh * 0.02 + sign.h * 0.45, sign.h * 0.55, '#ff4fa3', 'center', 800);
    sprayTag(g.l + g.w * (hall ? 0.15 : 0.08) + u * 0.2, g.m + g.fh * 0.55, Math.max(6, u * 0.16), 'FAKE', '#4fd1ff');
    const lamp = proj(x + w / 2, y + d, tall - 0.05);
    const flicker = 0.25 + (Math.sin(clock * 13 + prop.id) > 0.85 ? 0 : 0.25);
    glowAt(lamp.x, lamp.y + u * 0.3, u * 0.7, 'rgba(255,233,168,ALPHA)', flicker);
  };
  // What else stands on a rival's lot, for cover: shipping containers, crates, oil drums, a parked car,
  // concrete barriers and stacks of tyres.
  const COVER_TONES = [['#b5452f', '#9c3a27'], ['#2f6fa8', '#285f90'], ['#3c7d4f', '#336b44'], ['#c98a2e', '#ad7627']];
  const drawCover = c => {
    const u = cw();
    const { x, y, w, d } = c;
    const foot = proj(x + w / 2, y + d);
    oval(foot.x, foot.y, (w / 2 + 0.1) * u, u * 0.12, 'rgba(0,0,0,0.16)');
    if (c.kind === 'container') {
      for (let k = 0; k < c.stack; k += 1) {
        const [roof, face] = COVER_TONES[(c.tone + k * 3) % 4];
        const box = block(x, y, w, d, 1.25 * (k + 1), roof, face, 1.25 * k);
        for (let i = 1; i < 16; i += 1) rect(box.l + (box.w * i) / 16, box.m, Math.max(1, u * 0.015), box.fh, 'rgba(0,0,0,0.12)');
        rect(box.l + box.w * 0.03, box.m + box.fh * 0.12, box.w * 0.94, Math.max(1, u * 0.02), 'rgba(255,255,255,0.18)');
        rect(box.r - box.w * 0.2, box.m + box.fh * 0.25, Math.max(1.5, u * 0.03), box.fh * 0.6, 'rgba(0,0,0,0.3)');
      }
      return;
    }
    if (c.kind === 'crates') {
      // Two crates side by side and one on top, planked and cross-braced.
      [[x, y + 0.2, 0.52, 0], [x + 0.56, y + 0.2, 0.52, 0], [x + 0.28, y + 0.25, 0.52, 0.5]].forEach(([bx, by, s, base]) => {
        const box = block(bx, by, s, s * 1.4, base + 0.5, '#c39462', '#a8794a', base);
        line(box.l, box.m, box.r, box.b, 'rgba(60,40,20,0.4)', Math.max(1, u * 0.03));
        line(box.r, box.m, box.l, box.b, 'rgba(60,40,20,0.4)', Math.max(1, u * 0.03));
        ctx.strokeStyle = 'rgba(60,40,20,0.5)';
        ctx.lineWidth = Math.max(1, u * 0.02);
        ctx.strokeRect(box.l, box.m, box.w, box.fh);
      });
      return;
    }
    if (c.kind === 'barrels') {
      // Three oil drums: a body, two ribs and a lid seen from above.
      [[0.22, 0.25], [0.6, 0.45], [0.88, 0.2]].forEach(([fx, fy], k) => {
        const [lid, body] = COVER_TONES[(c.tone + k) % 4];
        const at = proj(x + fx, y + fy);
        const r = u * 0.2;
        const hgt = u * 0.62;
        rect(at.x - r, at.y - hgt, r * 2, hgt, body);
        rect(at.x - r, at.y - hgt, r * 0.5, hgt, 'rgba(255,255,255,0.15)');
        [0.33, 0.66].forEach(f => rect(at.x - r, at.y - hgt * f, r * 2, Math.max(1, u * 0.025), 'rgba(0,0,0,0.25)'));
        oval(at.x, at.y, r, r * TILT, body);
        oval(at.x, at.y - hgt, r, r * TILT, lid);
        oval(at.x, at.y - hgt, r * 0.75, r * TILT * 0.75, 'rgba(0,0,0,0.15)');
      });
      return;
    }
    if (c.kind === 'wreck') {
      drawProp({ kind: 'car', x, y, tone: ['#55575c', '#2b2b2e', '#c2330f', '#f4f3ef'][c.tone], flip: c.flip, s: 1 });
      return;
    }
    if (c.kind === 'barrier') {
      // A concrete barrier with red and white warning stripes.
      const box = block(x, y, w, d, 0.55, '#dcd8d0', '#c6c2b9');
      const n = 6;
      for (let i = 0; i < n; i += 2) rect(box.l + (box.w * i) / n, box.m + box.fh * 0.2, box.w / n, box.fh * 0.35, SIGNAL);
      return;
    }
    // A stack of tyres.
    const at = proj(x + w / 2, y + d / 2);
    const r = u * 0.32;
    for (let k = 0; k < 3; k += 1) {
      const ty = at.y - k * u * 0.18;
      oval(ty === at.y ? at.x : at.x + (k - 1) * u * 0.03, ty, r, r * TILT, '#1d1d20');
      rect(at.x - r, ty - u * 0.18, r * 2, u * 0.18, '#26262a');
    }
    oval(at.x, at.y - u * 0.54, r, r * TILT, '#2b2b2e');
    oval(at.x, at.y - u * 0.54, r * 0.45, r * TILT * 0.45, '#141416');
  };
  // Marks on the ground of a rival's lot, under everything that stands.
  const drawMark = m => {
    const u = cw();
    const at = proj(m.x, m.y);
    if (m.kind === 'oil') {
      oval(at.x, at.y, m.r * u, m.r * u * TILT * 0.7, 'rgba(20,20,21,0.13)');
      oval(at.x + m.r * u * 0.2, at.y, m.r * u * 0.5, m.r * u * TILT * 0.35, 'rgba(20,20,21,0.12)');
    } else if (m.kind === 'puddle') {
      oval(at.x, at.y, m.r * u, m.r * u * TILT * 0.6, 'rgba(110,135,160,0.3)');
      oval(at.x - m.r * u * 0.25, at.y - m.r * u * 0.08, m.r * u * 0.4, m.r * u * 0.06, 'rgba(255,255,255,0.35)');
    } else if (m.kind === 'drain') {
      const s = u * 0.32;
      rect(at.x - s, at.y - s * TILT, s * 2, s * 2 * TILT, '#3a3d42');
      for (let k = 1; k < 5; k += 1) rect(at.x - s + (s * 2 * k) / 5, at.y - s * TILT, Math.max(1, u * 0.03), s * 2 * TILT, '#5b6066');
    } else if (m.kind === 'crack') {
      ctx.strokeStyle = 'rgba(20,20,21,0.25)';
      ctx.lineWidth = Math.max(1, u * 0.025);
      ctx.beginPath();
      const angle = m.turn * TAU;
      for (let k = 0; k <= 4; k += 1) {
        const f = (k / 4 - 0.5) * m.r * u;
        const jag = Math.sin(k * 2.7 + m.turn * 9) * u * 0.12;
        const px = at.x + Math.cos(angle) * f - Math.sin(angle) * jag;
        const py = at.y + (Math.sin(angle) * f + Math.cos(angle) * jag) * TILT;
        if (k) ctx.lineTo(px, py);
        else ctx.moveTo(px, py);
      }
      ctx.stroke();
    } else if (m.kind === 'tag') {
      ctx.save();
      ctx.translate(at.x, at.y);
      ctx.scale(1, TILT);
      ctx.rotate(m.turn);
      ctx.globalAlpha = 0.22;
      label(m.text, 0, 0, m.r * u * 0.7, heist.color, 'center', 800);
      ctx.restore();
    } else if (m.kind === 'hazard') {
      // Yellow and black stripes before a gate.
      const a = proj(m.x, m.y);
      const b = proj(m.x + m.w, m.y + m.d);
      ctx.save();
      ctx.beginPath();
      ctx.rect(a.x, a.y, b.x - a.x, b.y - a.y);
      ctx.clip();
      rect(a.x, a.y, b.x - a.x, b.y - a.y, 'rgba(255,210,60,0.75)');
      const step = u * 0.3;
      for (let sx = a.x - (b.y - a.y); sx < b.x; sx += step * 2) poly([[sx, b.y], [sx + step, b.y], [sx + step + (b.y - a.y), a.y], [sx + (b.y - a.y), a.y]], 'rgba(20,20,21,0.7)');
      ctx.restore();
    } else if (m.kind === 'bay') {
      const a = proj(m.x, m.y);
      const b = proj(m.x + m.w, m.y + m.d);
      const lw = Math.max(1.5, u * 0.05);
      rect(a.x, a.y, b.x - a.x, lw, 'rgba(255,255,255,0.55)');
      rect(m.x < heist.x0 + CHUNK ? b.x - lw : a.x, a.y, lw, b.y - a.y, 'rgba(255,255,255,0.55)');
    }
  };
  // The fence round a rival's lot: chain link on posts, a brick wall, or corrugated tin. The sides lie under
  // everything; the back and the front, left and right of the gap the crew comes in through, stand in the scene.
  const FENCE_HIGH = { chain: 1.1, wall: 0.95, tin: 1.15 };
  const fenceRun = (xa, xb, y) => {
    const u = cw();
    const style = heist.fence;
    const tall = FENCE_HIGH[style];
    if (style === 'chain') {
      const a = proj(xa, y, tall);
      const b = proj(xb, y);
      ctx.save();
      ctx.beginPath();
      ctx.rect(a.x, a.y, b.x - a.x, b.y - a.y);
      ctx.clip();
      ctx.strokeStyle = 'rgba(70,74,80,0.35)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      const step = u * 0.16;
      const span = b.y - a.y;
      for (let sx = a.x - span; sx < b.x; sx += step) {
        ctx.moveTo(sx, b.y);
        ctx.lineTo(sx + span, a.y);
        ctx.moveTo(sx + span, b.y);
        ctx.lineTo(sx, a.y);
      }
      ctx.stroke();
      ctx.restore();
      line(a.x, a.y, b.x, a.y, '#5b6066', Math.max(1.5, u * 0.04));
      for (let px = xa; px <= xb + 0.01; px += Math.max(0.5, (xb - xa) / Math.max(1, Math.round((xb - xa) / 1.5)))) {
        const p = proj(px, y);
        line(p.x, p.y, p.x, p.y - tall * u, '#4a4d52', Math.max(1.5, u * 0.05));
      }
      return;
    }
    const wall = style === 'wall';
    const box = block(xa, y - 0.25, xb - xa, 0.25, tall, wall ? '#8c5040' : '#8995a0', wall ? '#a85f4a' : '#7d8a96');
    if (wall) {
      const row = u * 0.16;
      for (let k = 1; k * row < box.fh; k += 1) {
        rect(box.l, box.m + k * row, box.w, 1, 'rgba(0,0,0,0.18)');
        for (let sx = box.l + (k % 2) * u * 0.18; sx < box.r; sx += u * 0.36) rect(sx, box.m + (k - 1) * row, 1, row, 'rgba(0,0,0,0.14)');
      }
    } else {
      for (let sx = box.l; sx < box.r; sx += u * 0.1) rect(sx, box.m, Math.max(1, u * 0.025), box.fh, 'rgba(0,0,0,0.13)');
      rect(box.l, box.m + box.fh * 0.7, box.w, box.fh * 0.3, 'rgba(150,80,40,0.18)');
    }
  };
  const fenceSide = x => {
    const u = cw();
    const tall = FENCE_HIGH[heist.fence];
    if (heist.fence === 'chain') {
      const a = proj(x, 0, tall);
      const b = proj(x, heist.y1, tall);
      line(a.x, a.y, b.x, b.y, '#5b6066', Math.max(1.5, u * 0.04));
      for (let y = 0; y <= heist.y1; y += 1.5) {
        const p = proj(x, y);
        line(p.x, p.y, p.x, p.y - tall * u, '#4a4d52', Math.max(1.5, u * 0.05));
      }
      return;
    }
    const wall = heist.fence === 'wall';
    block(x - 0.12, 0, 0.24, heist.y1, tall, wall ? '#8c5040' : '#8995a0', wall ? '#a85f4a' : '#7d8a96');
  };
  const drawRivalYard = () => {
    heist.marks.forEach(drawMark);
    fenceSide(heist.x0);
    fenceSide(heist.x1);
  };
  // The front fence leaves a gap as wide as the crew's line, plus a step on either side.
  const frontFence = () => {
    const xs = [...heist.posts.values()].map(post => post.x);
    const gl = Math.max(heist.x0 + 1, Math.min(...xs) - 1.6);
    const gr = Math.min(heist.x1 - 1, Math.max(...xs) + 1.6);
    return [[heist.x0, gl], [gr, heist.x1]];
  };
  const drawShot = shot => {
    const u = cw();
    if (shot.t < 0) return;
    const at = proj(shot.x, shot.y, shot.h);
    if (shot.kind === 'shell') {
      // A mortar shell: olive drab with a brass band and tail fins, nose along its flight, a thin smoke trail behind.
      const ground = proj(shot.x, shot.y);
      oval(ground.x, ground.y, u * 0.08, u * 0.025, 'rgba(0,0,0,0.14)');
      const k = Math.max(4, u * 0.13);
      const q = Math.max(0, shot.t / shot.dur - 0.05);
      const back = proj(lerp(shot.fx, shot.tx, q), lerp(shot.fy, shot.ty, q), lerp(2, 0.3, q) + Math.sin(q * Math.PI) * 4.2);
      ctx.save();
      ctx.globalAlpha = 0.3;
      line(back.x, back.y, at.x, at.y, '#8a8580', k * 0.35);
      ctx.restore();
      ctx.save();
      ctx.translate(at.x, at.y);
      ctx.rotate(Math.atan2(at.y - back.y, at.x - back.x));
      poly([[-k * 0.75, -k * 0.34], [-k * 0.5, -k * 0.16], [-k * 0.5, k * 0.16], [-k * 0.75, k * 0.34]], INK);
      roundRect(-k * 0.58, -k * 0.27, k * 1.2, k * 0.54, k * 0.27, INK);
      roundRect(-k * 0.5, -k * 0.2, k * 1.04, k * 0.4, k * 0.2, '#56603a');
      rect(-k * 0.5, -k * 0.2, k * 1.04, k * 0.1, 'rgba(255,255,255,0.22)');
      rect(-k * 0.06, -k * 0.2, k * 0.14, k * 0.4, '#c9a24a');
      ctx.restore();
      return;
    }
    if (shot.kind === 'missile') {
      // A guided missile: white nose, body in the ace's colour, fins, a flame at the tail and a long smoke trail.
      const ground = proj(shot.x, shot.y);
      oval(ground.x, ground.y, u * 0.07, u * 0.022, 'rgba(0,0,0,0.12)');
      const back = proj(shot.px === undefined ? shot.fx : shot.px, shot.py === undefined ? shot.fy : shot.py, shot.ph === undefined ? shot.h : shot.ph);
      const k = Math.max(4, u * 0.12);
      if (Math.random() < 0.8) burst(shot.x, shot.y, shot.h, 1, ['rgba(225,222,215,0.7)', 'rgba(170,166,160,0.55)'], { speed: 0.1, up: 0.1, g: -0.2, size: 0.07, life: 0.7, kind: 'smoke' });
      ctx.save();
      ctx.translate(at.x, at.y);
      ctx.rotate(Math.atan2(at.y - back.y, at.x - back.x));
      const flame = 0.7 + Math.random() * 0.6;
      poly([[-k * 0.8, -k * 0.2], [-k * (0.8 + flame), 0], [-k * 0.8, k * 0.2]], YELLOW);
      poly([[-k * 0.8, -k * 0.1], [-k * (0.8 + flame * 0.5), 0], [-k * 0.8, k * 0.1]], PAPER);
      poly([[-k * 0.75, -k * 0.42], [-k * 0.35, -k * 0.18], [-k * 0.35, k * 0.18], [-k * 0.75, k * 0.42]], INK);
      roundRect(-k * 0.85, -k * 0.24, k * 1.5, k * 0.48, k * 0.2, INK);
      roundRect(-k * 0.78, -k * 0.17, k * 1.36, k * 0.34, k * 0.15, TRICK_COLOR.rakete);
      poly([[k * 0.35, -k * 0.17], [k * 0.85, 0], [k * 0.35, k * 0.17]], PAPER);
      rect(-k * 0.78, -k * 0.17, k * 1.2, k * 0.08, 'rgba(255,255,255,0.35)');
      ctx.restore();
      return;
    }
    if (shot.kind === 'star') {
      // A throwing star spinning flat, a blur of itself behind.
      const ground = proj(shot.x, shot.y);
      oval(ground.x, ground.y, u * 0.1, u * 0.03, 'rgba(0,0,0,0.15)');
      const r = Math.max(4, u * 0.2);
      ctx.save();
      ctx.globalAlpha = 0.25;
      circle(at.x, at.y, r * 0.9, shot.color);
      ctx.restore();
      ctx.save();
      ctx.translate(at.x, at.y);
      ctx.scale(1, 0.62);
      shuriken(0, 0, r, shot.color, shot.t * 26);
      ctx.restore();
      return;
    }
    if (shot.kind === 'card') {
      // A magic card, spinning, trailing light in its colour.
      if (shot.t < 0) return;
      const k = Math.max(5, u * 0.22);
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      glowAt(at.x, at.y, k * 1.4, 'rgba(160,110,255,ALPHA)', 0.45);
      ctx.restore();
      playCard(at.x, at.y, k, shot.color, shot.t * 14 + shot.spin);
      return;
    }
    if (shot.kind === 'bigcard') {
      // A giant card coming down out of the sky, its target glowing on the ground.
      if (shot.t < 0) return;
      const p = Math.min(1, shot.t / shot.dur);
      const ground = proj(shot.tx, shot.ty);
      ctx.beginPath();
      ctx.ellipse(ground.x, ground.y, u * shot.splash * (1.2 - p * 0.2), u * shot.splash * (1.2 - p * 0.2) * TILT, 0, 0, TAU);
      ctx.strokeStyle = shot.color;
      ctx.setLineDash([5, 4]);
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.setLineDash([]);
      oval(ground.x, ground.y, u * (0.3 + p * 0.5), u * (0.1 + p * 0.15), `rgba(0,0,0,${(0.08 + p * 0.2).toFixed(2)})`);
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      glowAt(at.x, at.y, u * 1.4, 'rgba(160,110,255,ALPHA)', 0.5);
      ctx.restore();
      playCard(at.x, at.y - u * 0.5, u * 1.1, shot.color, Math.sin(shot.t * 8) * 0.15 * (1 - p), shot.rank);
      return;
    }
    if (shot.kind === 'grenade' || shot.kind === 'bomblet') {
      // A grenade turning over in its arc with a thin trail of smoke from the fuse; a bomblet is a small dark ball, and
      // what it splits into a smaller pink one.
      const ground = proj(shot.x, shot.y);
      oval(ground.x, ground.y, u * 0.06, u * 0.02, 'rgba(0,0,0,0.14)');
      if (shot.kind === 'grenade') {
        if (Math.random() < 0.4) burst(shot.x, shot.y, shot.h, 1, ['rgba(120,120,125,0.45)'], { speed: 0.1, up: 0.2, g: -0.3, size: 0.05, life: 0.4, kind: 'smoke' });
        const k = Math.max(6, u * 0.26);
        grenade(at.x, at.y + k * 0.5, k, shot.t * shot.spin);
      } else {
        const k = Math.max(2, u * (shot.stage === 3 ? 0.032 : 0.05));
        circle(at.x, at.y, k, shot.stage === 3 ? '#c2185b' : '#2c3018');
        circle(at.x - k * 0.3, at.y - k * 0.3, k * 0.35, 'rgba(255,255,255,0.3)');
      }
    } else if (shot.kind === 'case') {
      // A Flightcase in free fall: its shadow and the target ring tighten as it comes down.
      const p = Math.min(1, shot.t / shot.dur);
      const ground = proj(shot.tx, shot.ty);
      const k = u * 0.75 * shot.size;
      oval(ground.x, ground.y, k * (0.3 + p * 0.6), k * (0.1 + p * 0.18), `rgba(0,0,0,${(0.08 + p * 0.22).toFixed(2)})`);
      ctx.beginPath();
      ctx.ellipse(ground.x, ground.y, u * shot.splash * (1.25 - p * 0.25), u * shot.splash * (1.25 - p * 0.25) * TILT, 0, 0, TAU);
      ctx.strokeStyle = `rgba(255,75,31,${(0.35 + p * 0.5).toFixed(2)})`;
      ctx.setLineDash([5, 4]);
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.setLineDash([]);
      [-0.35, 0, 0.35].forEach(dx => line(at.x + dx * k, at.y - k * 1.3, at.x + dx * k, at.y - k * (1.3 + 1.2 * (1 - p)), 'rgba(255,255,255,0.7)', Math.max(1, u * 0.025)));
      flightcase(at.x, at.y, k, shot.spin * (1 - p) + Math.sin(shot.t * 9) * 0.05);
    } else if (shot.kind === 'cracker') {
      // A firecracker tumbling through the air, its fuse spitting sparks.
      const k = u * 0.16;
      ctx.save();
      ctx.translate(at.x, at.y);
      ctx.rotate(shot.t * 14);
      rect(-k * 0.5, -k * 0.14, k, k * 0.28, SIGNAL);
      rect(-k * 0.12, -k * 0.14, k * 0.16, k * 0.28, PAPER);
      line(k * 0.5, 0, k * 0.72, -k * 0.16, INK, Math.max(1, u * 0.015));
      circle(k * 0.74, -k * 0.18, Math.max(1.5, u * 0.03) * (0.7 + Math.random() * 0.6), YELLOW);
      ctx.restore();
    } else if (shot.kind === 'spray') {
      for (let i = 0; i < 3; i += 1) circle(at.x + (Math.random() - 0.5) * u * 0.1, at.y + (Math.random() - 0.5) * u * 0.1, Math.max(1, u * 0.025), shot.color);
    }
  };
  const drawParts = () => {
    parts.forEach(p => {
      const at = proj(p.x, p.y, p.h);
      const u = cw();
      const a = clamp(p.life / p.max, 0, 1);
      ctx.save();
      ctx.globalAlpha = p.kind === 'smoke' ? a * 0.8 : Math.min(1, a * 2);
      if (p.kind === 'debris') {
        ctx.translate(at.x, at.y);
        ctx.rotate(p.spin);
        rect(-p.size * u / 2, -p.size * u / 3, p.size * u, p.size * u * 0.66, p.color);
      } else if (p.kind === 'smoke') circle(at.x, at.y, p.size * u * (1.6 - a * 0.6), p.color);
      else if (p.kind === 'plus') {
        const k = p.size * u;
        rect(at.x - k, at.y - k * 0.3, k * 2, k * 0.6, p.color);
        rect(at.x - k * 0.3, at.y - k, k * 0.6, k * 2, p.color);
      }
      else if (p.kind === 'shirt') {
        // A fake tee from the Bootleg's stash, logo a little off.
        const k = p.size * u;
        ctx.translate(at.x, at.y);
        ctx.rotate(p.spin * 0.6);
        poly([[-k * 0.5, -k * 0.4], [-k * 0.18, -k * 0.5], [k * 0.18, -k * 0.5], [k * 0.5, -k * 0.4], [k * 0.62, -k * 0.12], [k * 0.36, -k * 0.04], [k * 0.36, k * 0.5], [-k * 0.36, k * 0.5], [-k * 0.36, -k * 0.04], [-k * 0.62, -k * 0.12]], p.color);
        circle(k * 0.04, k * 0.05, k * 0.14, 'rgba(255,255,255,0.65)');
      } else if (p.kind === 'spark') {
        ctx.globalCompositeOperation = 'lighter';
        circle(at.x, at.y, p.size * u, p.color);
      } else circle(at.x, at.y, p.size * u, p.color);
      ctx.restore();
    });
  };
  const drawDecals = () => {
    const u = cw();
    decals.forEach(d => {
      const at = proj(d.x, d.y);
      const a = clamp(d.life / d.max, 0, 1) * 0.85;
      const rand = mulberry32(Math.floor(d.seed));
      ctx.save();
      ctx.globalAlpha = a;
      if (d.crack) {
        // A crack in the ground: jagged lines running out from where it split.
        ctx.strokeStyle = d.color;
        ctx.lineJoin = 'round';
        for (let i = 0; i < 5; i += 1) {
          const ang = rand() * TAU;
          ctx.beginPath();
          ctx.moveTo(at.x, at.y);
          for (let j = 1; j <= 4; j += 1) {
            const rr = (d.r * j) / 4;
            const wob = ang + (rand() - 0.5) * 0.9;
            ctx.lineTo(at.x + Math.cos(wob) * rr * u, at.y + Math.sin(wob) * rr * u * TILT);
          }
          ctx.lineWidth = Math.max(1.5, u * 0.07);
          ctx.stroke();
        }
        ctx.restore();
        return;
      }
      oval(at.x, at.y, d.r * u, d.r * u * TILT, d.color);
      for (let i = 0; i < 6; i += 1) {
        const ang = rand() * TAU;
        const rr = d.r * (0.8 + rand() * 0.6);
        circle(at.x + Math.cos(ang) * rr * u, at.y + Math.sin(ang) * rr * u * TILT, d.r * u * (0.12 + rand() * 0.15), d.color);
      }
      ctx.restore();
    });
  };
  // The wrecking ball: a chain running up out of the picture, the steel ball with a band in the ace's colour, and a
  // smear of its own outline behind while it swings fast.
  const drawWreck = f => {
    const u = cw();
    const ball = wreckAt(f);
    const fade = clamp(Math.min(f.t / 0.15, (f.dur - f.t) / 0.4), 0, 1);
    const at = proj(ball.x, ball.y, ball.h);
    const ground = proj(ball.x, ball.y);
    const k = f.r * u;
    const top = proj(f.x, f.y, WRECK_CHAIN + f.r);
    ctx.save();
    ctx.globalAlpha = fade;
    oval(ground.x, ground.y, k * 0.9 * clamp(1.4 - ball.h / 4, 0.3, 1), k * 0.3 * clamp(1.4 - ball.h / 4, 0.3, 1), 'rgba(0,0,0,0.22)');
    line(top.x, top.y, at.x, at.y - k * 0.8, INK, Math.max(3, u * 0.09));
    ctx.setLineDash([Math.max(3, u * 0.12), Math.max(2, u * 0.06)]);
    line(top.x, top.y, at.x, at.y - k * 0.8, '#9aa1ab', Math.max(1.5, u * 0.05));
    ctx.setLineDash([]);
    const swing = Math.abs(Math.cos(ball.a / WRECK_ARC * Math.PI / 2));
    [3, 2, 1].forEach(n => {
      const ghost = wreckAt({ ...f, t: Math.max(0, f.t - n * 0.035) });
      const g = proj(ghost.x, ghost.y, ghost.h);
      ctx.globalAlpha = fade * 0.12 * swing * (4 - n);
      circle(g.x, g.y, k, '#3a3f47');
    });
    ctx.globalAlpha = fade;
    roundRect(at.x - k * 0.3, at.y - k * 1.15, k * 0.6, k * 0.4, k * 0.1, INK);
    circle(at.x, at.y, k + Math.max(1.5, u * 0.04), INK);
    circle(at.x, at.y, k, '#3a3f47');
    ctx.beginPath();
    ctx.arc(at.x, at.y, k, Math.PI * 1.08, Math.PI * 1.92);
    ctx.closePath();
    ctx.fillStyle = TRICK_COLOR.birne;
    ctx.fill();
    circle(at.x - k * 0.35, at.y - k * 0.1, k * 0.22, 'rgba(255,255,255,0.35)');
    circle(at.x - k * 0.42, at.y - k * 0.18, k * 0.08, 'rgba(255,255,255,0.8)');
    ctx.restore();
  };
  // The twister: a funnel of whirling bands from the ground up, wider and paler towards the top, swaying as it goes,
  // with dust round its foot and bits of the block flying round in it.
  const drawTwister = f => {
    const u = cw();
    const fade = clamp(Math.min(f.t / 0.4, (f.dur - f.t) / 0.3), 0, 1);
    const c = proj(f.x, f.y);
    const R = f.r * u;
    const LAYERS = 12;
    ctx.save();
    ctx.globalAlpha = fade * 0.5;
    oval(c.x, c.y, R * 0.9, R * 0.9 * TILT, 'rgba(120,104,80,0.35)');
    for (let i = 0; i < LAYERS; i += 1) {
      const q = i / (LAYERS - 1);
      const sway = Math.sin(clock * 2.4 + f.seed + q * 2.2) * R * 0.25 * q;
      const y = c.y - q * u * 4.2;
      const rx = R * (0.18 + 0.82 * q ** 1.4);
      ctx.globalAlpha = fade * (0.75 - q * 0.35);
      ctx.beginPath();
      ctx.ellipse(c.x + sway, y, rx, rx * TILT * 0.55, 0, 0, TAU);
      ctx.fillStyle = i % 2 ? 'rgba(140,200,192,0.35)' : 'rgba(220,240,236,0.35)';
      ctx.fill();
      ctx.strokeStyle = i % 2 ? TRICK_COLOR.tornado : 'rgba(255,255,255,0.8)';
      ctx.lineWidth = Math.max(1.5, u * 0.05);
      ctx.beginPath();
      const spin = clock * 7 + f.seed + i;
      ctx.ellipse(c.x + sway, y, rx, rx * TILT * 0.55, 0, spin % TAU, spin % TAU + 3.6);
      ctx.stroke();
    }
    ctx.globalAlpha = fade;
    for (let i = 0; i < 8; i += 1) {
      const q = ((i * 0.37 + clock * 0.25) % 1);
      const a = clock * (5 + i * 0.4) + i * 1.7 + f.seed;
      const rx = R * (0.25 + 0.75 * q ** 1.4) * 1.05;
      const x = c.x + Math.cos(a) * rx + Math.sin(clock * 2.4 + f.seed + q * 2.2) * R * 0.25 * q;
      const y = c.y - q * u * 4.2 + Math.sin(a) * rx * TILT * 0.55;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(a * 1.5);
      rect(-u * 0.06, -u * 0.04, u * 0.12, u * 0.08, i % 3 ? '#6b5e4e' : PAPER);
      ctx.restore();
    }
    ctx.restore();
  };
  // The attack drone: four spinning rotors, a black body with a red stripe, the red eye under it flaring on a zap.
  const drawDrone = drone => {
    const u = cw();
    const at = proj(drone.x, drone.y, drone.h);
    const ground = proj(drone.x, drone.y);
    const k = u * 0.34 * (drone.size || 1);
    const far = clamp(1 - drone.h / 9, 0, 1);
    oval(ground.x, ground.y, k * 0.7 * far, k * 0.2 * far, `rgba(0,0,0,${(0.16 * far).toFixed(3)})`);
    ctx.save();
    ctx.translate(at.x, at.y);
    ctx.rotate(drone.tilt);
    const rotors = [[-0.95, -0.32], [0.95, -0.32], [-0.8, 0.2], [0.8, 0.2]];
    rotors.forEach(([rx, ry]) => line(0, 0, rx * k, ry * k, '#18181b', Math.max(1.5, k * 0.1)));
    rotors.forEach(([rx, ry], i) => {
      oval(rx * k, ry * k - k * 0.08, k * 0.42, k * 0.1, 'rgba(150,150,156,0.35)');
      const a = clock * 40 + i * 1.3;
      line(rx * k - Math.cos(a) * k * 0.4, ry * k - k * 0.08 - Math.sin(a) * k * 0.08, rx * k + Math.cos(a) * k * 0.4, ry * k - k * 0.08 + Math.sin(a) * k * 0.08, 'rgba(40,44,52,0.7)', Math.max(1, k * 0.05));
      circle(rx * k, ry * k - k * 0.08, Math.max(1.2, k * 0.06), '#18181b');
    });
    roundRect(-k * 0.5, -k * 0.28, k, k * 0.46, k * 0.18, INK);
    roundRect(-k * 0.45, -k * 0.24, k * 0.9, k * 0.38, k * 0.15, '#1b1b1e');
    rect(-k * 0.45, -k * 0.1, k * 0.9, k * 0.06, DRONE_RED);
    rect(-k * 0.3, -k * 0.22, k * 0.6, k * 0.05, 'rgba(255,255,255,0.14)');
    circle(0, k * 0.24, k * 0.16, INK);
    circle(0, k * 0.26, k * 0.09, drone.zap > 0 ? PAPER : DRONE_RED);
    circle(k * 0.38, -k * 0.12, Math.max(1, k * 0.04), Math.sin(clock * 10 + drone.id) > 0 ? DRONE_RED : '#3a0a10');
    ctx.restore();
    if (drone.zap > 0) {
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      glowAt(at.x, at.y + k * 0.26, k * 0.9, 'rgba(255,40,60,ALPHA)', 0.6);
      ctx.restore();
    }
  };
  // A fan in the stampede: full sprint, some cheering, fading in and out at the ends of the run.
  const drawRunner = (f, r, pos) => {
    const s = walkerSize();
    const at = proj(pos.x, pos.y);
    const run = (f.t - r.lag) / (f.dur - 0.5);
    ctx.save();
    ctx.globalAlpha = clamp(Math.min(run / 0.12, (1 - run) / 0.15), 0, 1);
    const jump = Math.abs(Math.sin(clock * 14 + r.jump)) * s * 0.08;
    oval(at.x, at.y, s * 0.2, s * 0.055, 'rgba(0,0,0,0.15)');
    stampPerson(at.x, at.y - jump, s, { look: r.look, owner: r, face: f.dir, walk: (f.t - r.lag) * 2.4, moving: !r.cheer, pose: r.cheer ? 'cheer' : null, noShadow: true });
    ctx.restore();
  };
  const drawFloats = () => {
    floats.forEach(f => {
      const at = proj(f.x, f.y, f.h);
      ctx.save();
      ctx.globalAlpha = clamp(f.life / f.max * 2, 0, 1);
      const size = Math.max(12, cw() * 0.26) * f.big;
      ctx.font = `700 ${size}px ${FONT}`;
      ctx.lineWidth = 3;
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      // Cash shows as the bill and the number, never a currency sign.
      const shift = f.mark === 'cash' ? size * 0.42 : 0;
      if (shift) drawCash(ctx, at.x - ctx.measureText(f.text).width / 2 - size * 0.05, at.y, size * 0.42);
      ctx.strokeText(f.text, at.x + shift, at.y);
      ctx.fillStyle = f.color;
      ctx.fillText(f.text, at.x + shift, at.y);
      ctx.restore();
    });
  };

  // ---------------------------------------------------------------------------------------------
  // Figures. Every character is one skeleton with real joints, posed by animation clips and dressed
  // from a library of parts. A figure faces right with its feet on (0, 0); `H` is its height in pixels.
  //
  //   skeleton  hip -> chest -> neck -> head, shoulders -> elbows -> wrists, hips -> knees -> ankles
  //   pose      joint angles and foot targets; legs and gripping hands are solved with two-bone IK
  //   clips     keyframes in seconds around the moment of impact: anticipation, strike, follow, recover
  //   parts     torso, sleeves, pants, shoes, heads, faces, hair, hats, gear and props, each in its bone's frame
  //
  // Angles of limbs are measured from straight down, positive toward the way the figure faces.

  // Proportions as fractions of the height. Each build is a body type; characters tweak from there.
  const BUILDS = {
    regular: { head: 0.21, neck: 0.025, torso: 0.27, upper: 0.15, fore: 0.14, hand: 0.058, thigh: 0.21, shin: 0.2, ankle: 0.045, foot: 0.14, shoulder: 0.125, chest: 0.12, waist: 0.1, hips: 0.105, belly: 0, arm: 0.062, wrist: 0.046, leg: 0.082, calf: 0.064, stance: 0.05 },
    lean: { head: 0.21, neck: 0.03, torso: 0.27, upper: 0.155, fore: 0.145, hand: 0.054, thigh: 0.215, shin: 0.205, ankle: 0.045, foot: 0.135, shoulder: 0.115, chest: 0.105, waist: 0.085, hips: 0.092, belly: 0, arm: 0.054, wrist: 0.042, leg: 0.07, calf: 0.058, stance: 0.045 },
    heavy: { head: 0.2, neck: 0.02, torso: 0.3, upper: 0.145, fore: 0.13, hand: 0.064, thigh: 0.195, shin: 0.185, ankle: 0.045, foot: 0.145, shoulder: 0.155, chest: 0.16, waist: 0.16, hips: 0.145, belly: 0.05, arm: 0.082, wrist: 0.06, leg: 0.1, calf: 0.08, stance: 0.065 },
    hulk: { head: 0.18, neck: 0.03, torso: 0.29, upper: 0.16, fore: 0.15, hand: 0.07, thigh: 0.215, shin: 0.2, ankle: 0.045, foot: 0.145, shoulder: 0.18, chest: 0.165, waist: 0.11, hips: 0.115, belly: 0, arm: 0.095, wrist: 0.068, leg: 0.098, calf: 0.078, stance: 0.07 },
    athletic: { head: 0.2, neck: 0.03, torso: 0.28, upper: 0.155, fore: 0.145, hand: 0.06, thigh: 0.215, shin: 0.205, ankle: 0.045, foot: 0.14, shoulder: 0.14, chest: 0.13, waist: 0.095, hips: 0.1, belly: 0, arm: 0.068, wrist: 0.05, leg: 0.084, calf: 0.066, stance: 0.055 },
    kid: { head: 0.26, neck: 0.02, torso: 0.25, upper: 0.13, fore: 0.12, hand: 0.055, thigh: 0.18, shin: 0.17, ankle: 0.04, foot: 0.125, shoulder: 0.11, chest: 0.11, waist: 0.1, hips: 0.1, belly: 0.01, arm: 0.056, wrist: 0.045, leg: 0.074, calf: 0.062, stance: 0.045 }
  };
  // A build with a character's own tweaks, measured once.
  const buildOf = spec => {
    if (spec.bodyCache) return spec.bodyCache;
    const b = { ...BUILDS[spec.build || 'regular'], ...(spec.body || {}) };
    b.legs = b.thigh + b.shin + b.ankle;
    spec.bodyCache = b;
    return b;
  };

  // The rest pose every clip starts from. Feet are targets in figure space, y up is negative.
  const REST = {
    x: 0, y: 0, lean: 0, head: 0, sq: 1,
    aF: 0.18, eF: 0.3, wF: 0, aB: -0.12, eB: 0.35, wB: 0,
    fFx: 0.045, fFy: 0, fBx: -0.045, fBy: 0, tF: 0, tB: 0,
    reach: 1, grip: 0, gripB: 0, both: 0, propLen: 1, mouth: 0, brow: 0, eyes: 0, look: 0
  };

  // Colours: a tone a little darker or lighter, cached, so the far side of a body reads as further away.
  const toneCache = new Map();
  const tone = (hex, k) => {
    const key = hex + k;
    let out = toneCache.get(key);
    if (out) return out;
    // Takes '#rrggbb' or the 'rgb(r,g,b)' this returns, so tones can be stacked.
    const rgb = hex[0] === '#' ? [16, 8, 0].map(shift => (parseInt(hex.slice(1), 16) >> shift) & 255) : hex.slice(4, -1).split(',').map(Number);
    const mix = ch => Math.round(k < 0 ? ch * (1 + k) : ch + (255 - ch) * k);
    out = `rgb(${rgb.map(mix).join(',')})`;
    toneCache.set(key, out);
    return out;
  };

  // Two-bone IK: the middle joint for a chain from `a` reaching for `t`. With `bend` +1 the joint turns
  // anticlockwise off the line (a knee under a hip goes forward), with -1 clockwise (an elbow reaching
  // ahead drops below). Out of reach, the chain straightens toward the target.
  const solve2 = (ax, ay, tx, ty, l1, l2, bend) => {
    const dx = tx - ax;
    const dy = ty - ay;
    const d = clamp(Math.hypot(dx, dy), Math.abs(l1 - l2) + 0.001, l1 + l2 - 0.001);
    const base = Math.atan2(dy, dx);
    const turn = Math.acos(clamp((l1 * l1 + d * d - l2 * l2) / (2 * l1 * d), -1, 1));
    const a1 = base - bend * turn;
    const jx = ax + Math.cos(a1) * l1;
    const jy = ay + Math.sin(a1) * l1;
    const a2 = Math.atan2(ty - jy, tx - jx);
    return { jx, jy, ex: jx + Math.cos(a2) * l2, ey: jy + Math.sin(a2) * l2 };
  };
  // A direction from an angle measured from straight down, positive forward.
  const dirDown = a => ({ x: Math.sin(a), y: Math.cos(a) });

  // Lays the skeleton out for a pose: every joint in figure space, in pixels.
  const skeleton = (spec, pose, H) => {
    const b = buildOf(spec);
    const sk = { H, b };
    const hipH = b.legs * H;
    sk.hip = { x: pose.x * H, y: -hipH + pose.y * H };
    const lean = pose.lean;
    sk.lean = lean;
    const up = { x: Math.sin(lean), y: -Math.cos(lean) };
    const side = { x: Math.cos(lean), y: Math.sin(lean) };
    sk.up = up;
    sk.side = side;
    const at = (o, along, across) => ({ x: o.x + up.x * along + side.x * across, y: o.y + up.y * along + side.y * across });
    sk.at = at;
    sk.chest = at(sk.hip, b.torso * H, 0);
    // The head sits on the neck, tilted with the torso and its own nod.
    const headTilt = lean + pose.head;
    sk.headTilt = headTilt;
    const neckTop = at(sk.chest, b.neck * H, 0);
    sk.neck = neckTop;
    sk.head = { x: neckTop.x + Math.sin(headTilt) * b.head * H * 0.5, y: neckTop.y - Math.cos(headTilt) * b.head * H * 0.5, r: b.head * H * 0.5 };
    // Shoulders: the near one a little forward, the far one tucked behind; both hang from the chest line.
    const shoulderDrop = b.head * H * 0.18;
    sk.shF = at(sk.chest, -shoulderDrop, b.shoulder * H * 0.45);
    sk.shB = at(sk.chest, -shoulderDrop * 0.6, -b.shoulder * H * 0.7);
    const arm = (sh, a, e, w, side2, target) => {
      const l1 = b.upper * H;
      const l2 = b.fore * H * (side2 === 'F' ? pose.reach : 1);
      let el;
      let wr;
      if (target) {
        const s2 = solve2(sh.x, sh.y, target.x, target.y, l1, l2, -1);
        el = { x: s2.jx, y: s2.jy };
        wr = { x: s2.ex, y: s2.ey };
      } else {
        const d1 = dirDown(a + lean);
        el = { x: sh.x + d1.x * l1, y: sh.y + d1.y * l1 };
        const d2 = dirDown(a + lean + e);
        wr = { x: el.x + d2.x * l2, y: el.y + d2.y * l2 };
      }
      const fore = Math.atan2(wr.y - el.y, wr.x - el.x);
      const handA = fore + w;
      return { sh, el, wr, fore, handA, hand: { x: wr.x + Math.cos(handA) * b.hand * H * 0.6, y: wr.y + Math.sin(handA) * b.hand * H * 0.6 } };
    };
    sk.armF = arm(sk.shF, pose.aF, pose.eF, pose.wF, 'F', pose.handF && { x: pose.handF.x * H, y: pose.handF.y * H });
    sk.armB = arm(sk.shB, pose.aB, pose.eB, pose.wB, 'B', pose.handB && { x: pose.handB.x * H, y: pose.handB.y * H });
    // Both hands on one handle: with `both` up to 1, the far hand closes on what the near one holds, just above it.
    if (pose.both > 0.01) {
      const a = sk.armF.handA - Math.PI / 2 + pose.grip;
      const grip = { x: sk.armF.hand.x + Math.cos(a) * H * 0.05, y: sk.armF.hand.y + Math.sin(a) * H * 0.05 };
      const w = clamp(pose.both, 0, 1);
      sk.armB = arm(sk.shB, 0, 0, pose.wB, 'B', { x: lerp(sk.armB.wr.x, grip.x, w), y: lerp(sk.armB.wr.y, grip.y, w) });
    }
    // Legs: hips on the pelvis, feet on their targets, knees solved forward.
    const leg = (dx, fx, fy, toe) => {
      const hp = at(sk.hip, 0, dx * H);
      const ankle = { x: fx * H, y: fy * H - b.ankle * H };
      const s2 = solve2(hp.x, hp.y, ankle.x, ankle.y, b.thigh * H, b.shin * H, 1);
      return { hip: hp, knee: { x: s2.jx, y: s2.jy }, ankle: { x: s2.ex, y: s2.ey }, toe };
    };
    sk.legF = leg(b.hips * 0.42, pose.fFx, pose.fFy, pose.tF);
    sk.legB = leg(-b.hips * 0.42, pose.fBx, pose.fBy, pose.tB);
    return sk;
  };

  // ---- Clips --------------------------------------------------------------------------------------
  // Easing into a key: 'in' builds up speed (a strike), 'out' brakes (a follow-through), 'io' both.
  const EASES = {
    in: t => t * t * t,
    out: t => 1 - (1 - t) ** 3,
    io: t => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2),
    back: t => 1 + 2.4 * (t - 1) ** 3 + 1.4 * (t - 1) ** 2
  };
  // A clip is a list of [time, channels, ease]; channels left out of a key fall back to the base pose,
  // so a clip blends in from and out to whatever the body was doing. Times are seconds from impact.
  const sampleClip = (clip, t, base, out) => {
    const keys = clip.keys;
    if (t <= keys[0][0] || t >= keys[keys.length - 1][0]) return false;
    let i = 0;
    while (i < keys.length - 2 && t > keys[i + 1][0]) i += 1;
    const [t0, k0] = keys[i];
    const [t1, k1, how] = keys[i + 1];
    const p = EASES[how || 'io'](clamp((t - t0) / (t1 - t0), 0, 1));
    const names = new Set([...Object.keys(k0), ...Object.keys(k1)]);
    names.forEach(name => {
      const a = k0[name] === undefined ? base[name] : k0[name];
      const b = k1[name] === undefined ? base[name] : k1[name];
      // Switches (a free hand, a target for the grip) hold from the key that set them.
      if (typeof a !== 'number' || typeof b !== 'number') out[name] = a;
      else out[name] = lerp(a, b, p);
    });
    return true;
  };
  const clipStart = clip => clip.keys[0][0];
  const clipEnd = clip => clip.keys[clip.keys.length - 1][0];

  // ---- Locomotion ---------------------------------------------------------------------------------
  // A walk cycle from the phase: the planted foot slides back under the hip, the free one lifts and
  // swings forward; the hip dips at contact and rises when the legs pass; the arms swing against the legs.
  const locomote = (pose, phase, gait, run, gaitStyle) => {
    if (gait <= 0.001) return;
    const g = gait;
    const stride = lerp(0.1, 0.17, run) * (gaitStyle.stride || 1);
    const lift = lerp(0.05, 0.11, run) * (gaitStyle.lift || 1);
    const foot = ph => {
      const s = Math.sin(ph);
      const c = Math.cos(ph);
      return { x: s * stride, y: -Math.max(0, c) * lift, toe: c > 0 ? -0.35 * c : 0.25 * Math.max(0, -s) * (1 - run) };
    };
    const f = foot(phase);
    const bk = foot(phase + Math.PI);
    pose.fFx = lerp(pose.fFx, f.x + 0.012, g);
    pose.fFy = lerp(pose.fFy, f.y, g);
    pose.tF = lerp(pose.tF, f.toe, g);
    pose.fBx = lerp(pose.fBx, bk.x - 0.012, g);
    pose.fBy = lerp(pose.fBy, bk.y, g);
    pose.tB = lerp(pose.tB, bk.toe, g);
    const bob = Math.abs(Math.cos(phase));
    pose.y += g * (0.012 - bob * lerp(0.016, 0.03, run) * (gaitStyle.bob || 1));
    pose.lean += g * lerp(0.05, 0.2, run) * (gaitStyle.lean || 1);
    const swing = Math.sin(phase) * lerp(0.4, 0.75, run) * (gaitStyle.swing === undefined ? 1 : gaitStyle.swing) * g;
    pose.aF -= swing;
    pose.aB += swing;
    pose.eF = lerp(pose.eF, lerp(0.45, 1.6, run), g * (gaitStyle.swing === undefined ? 1 : gaitStyle.swing));
    pose.eB = lerp(pose.eB, lerp(0.5, 1.6, run), g * (gaitStyle.swing === undefined ? 1 : gaitStyle.swing));
    pose.head -= g * lerp(0.03, 0.12, run);
  };
  // Breathing and weight: the chest rises, the head settles, the hands drift. Never quite still.
  const breathe = (pose, t, amount = 1) => {
    const br = Math.sin(t * 2.1);
    pose.y += br * 0.004 * amount;
    pose.sq *= 1 + br * 0.008 * amount;
    pose.aF += Math.sin(t * 2.1 + 0.6) * 0.03 * amount;
    pose.aB -= Math.sin(t * 2.1 + 0.9) * 0.03 * amount;
    pose.head += Math.sin(t * 0.7) * 0.02 * amount;
  };
  // Blinks every few seconds, each figure on its own clock.
  const blinkAt = (t, seed) => {
    const p = (t + seed * 1.37) % (3.2 + (seed % 3) * 0.7);
    return p < 0.12 ? 1 : 0;
  };

  // ---- Springs ------------------------------------------------------------------------------------
  // Secondary motion for coat tails, locs, chains and sashes: each follows the body with a little lag.
  const rigMemory = new WeakMap();
  const memoryOf = owner => {
    let m = rigMemory.get(owner);
    if (!m) {
      m = { t: clock, x: 0, vx: 0, springs: {} };
      rigMemory.set(owner, m);
    }
    return m;
  };
  // Set while a figure is drawn a second time in the same frame: its springs hold still for it.
  let replaying = false;
  const spring = (mem, key, target, stiff = 60, damp = 8) => {
    const s = mem.springs[key] || (mem.springs[key] = { v: target, vel: 0 });
    const dt = replaying ? 0 : mem.dt;
    s.vel += ((target - s.v) * stiff - s.vel * damp) * dt;
    s.v += s.vel * dt;
    return s.v;
  };

  // ---- Drawing primitives -------------------------------------------------------------------------
  // A tapered capsule from a to b: the shape of every limb segment, joints rounded so bends stay clean.
  const capsule = (ax, ay, ar, bx, by, br, color) => {
    const len = Math.hypot(bx - ax, by - ay);
    ctx.fillStyle = color;
    ctx.beginPath();
    if (len < 0.5) {
      ctx.arc(ax, ay, Math.max(ar, br), 0, TAU);
    } else {
      const th = Math.atan2(by - ay, bx - ax);
      ctx.arc(ax, ay, ar, th + Math.PI / 2, th - Math.PI / 2);
      ctx.arc(bx, by, br, th - Math.PI / 2, th + Math.PI / 2);
      ctx.closePath();
    }
    ctx.fill();
  };
  const midPoint = (a, b, t) => ({ x: lerp(a.x, b.x, t), y: lerp(a.y, b.y, t) });
  // A two-segment limb, a -> b -> c, with radii at each joint.
  const bentLimb = (a, b, c, ra, rb, rc, color) => {
    capsule(a.x, a.y, ra, b.x, b.y, rb, color);
    capsule(b.x, b.y, rb, c.x, c.y, rc, color);
  };
  // Covers a limb from the root down to `cover` (0..1 by length): sleeves and trouser legs.
  // Returns the point and the direction where the cover ends, for a cuff or a hem.
  const coverLimb = (a, b, c, ra, rb, rc, cover, color, fit = 1.12) => {
    const l1 = Math.hypot(b.x - a.x, b.y - a.y);
    const l2 = Math.hypot(c.x - b.x, c.y - b.y);
    const reach = cover * (l1 + l2);
    if (reach <= l1) {
      const t = l1 ? reach / l1 : 0;
      const e = midPoint(a, b, t);
      capsule(a.x, a.y, ra * fit, e.x, e.y, lerp(ra, rb, t) * fit, color);
      return { at: e, dir: Math.atan2(b.y - a.y, b.x - a.x), r: lerp(ra, rb, t) * fit };
    }
    const t = l2 ? (reach - l1) / l2 : 1;
    const e = midPoint(b, c, t);
    capsule(a.x, a.y, ra * fit, b.x, b.y, rb * fit, color);
    capsule(b.x, b.y, rb * fit, e.x, e.y, lerp(rb, rc, t) * fit, color);
    return { at: e, dir: Math.atan2(c.y - b.y, c.x - b.x), r: lerp(rb, rc, t) * fit };
  };
  // A band across a limb at `end`: a cuff, a hem, a wrap.
  const cuffBand = (end, width, color, thick) => {
    ctx.save();
    ctx.translate(end.at.x, end.at.y);
    ctx.rotate(end.dir);
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.ellipse(-thick / 2, 0, thick / 2 + 0.3, width * 1.02, 0, 0, TAU);
    ctx.fill();
    ctx.restore();
  };
  const polyline = (points, width, color) => {
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(0.6, width);
    ctx.beginPath();
    points.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.stroke();
  };
  // Runs `fn` with the canvas moved into a frame: origin, rotation.
  const inFrame = (x, y, a, fn) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(a);
    fn();
    ctx.restore();
  };

  // ---- Torso --------------------------------------------------------------------------------------
  // The torso outline in the spine frame (origin at the hip joint line, up is -y), from the neckline to
  // `below` under the hips. The front (+x) carries the chest and the belly, the back stays straighter.
  const torsoPath = (b, H, below = 0.03, spread = 0) => {
    const T = b.torso * H;
    const SW = b.shoulder * H;
    const C = b.chest * H;
    const W = b.waist * H;
    const P = b.hips * H;
    const B = b.belly * H;
    const bot = below * H;
    const fl = spread * H;
    ctx.beginPath();
    ctx.moveTo(-SW * 0.3, -T - SW * 0.08);
    ctx.quadraticCurveTo(-SW * 0.95, -T - SW * 0.04, -SW * 1.0, -T + SW * 0.32);
    ctx.quadraticCurveTo(-W * 1.08 - B * 0.2, -T * 0.42, -P - B * 0.25 - fl, bot * 0.4);
    ctx.lineTo(-P - B * 0.25 - fl, bot);
    ctx.quadraticCurveTo(0, bot + H * 0.012, P + B * 0.7 + fl, bot);
    ctx.quadraticCurveTo(W + B * 1.9, -T * 0.28, C * 0.98 + B * 0.4, -T * 0.66);
    ctx.quadraticCurveTo(SW * 1.02, -T + SW * 0.06, SW * 0.62, -T - SW * 0.06);
    ctx.quadraticCurveTo(SW * 0.1, -T - SW * 0.16, -SW * 0.3, -T - SW * 0.08);
    ctx.closePath();
  };
  // Runs `fn` in the torso's frame.
  const inTorso = (sk, fn) => inFrame(sk.hip.x, sk.hip.y, sk.lean, fn);
  // Shades the far side of whatever path is current, so the torso has a turn to it.
  const shadeBack = (b, H, k = 0.12) => {
    ctx.save();
    ctx.clip();
    ctx.fillStyle = `rgba(0,0,0,${k})`;
    ctx.fillRect(-b.shoulder * H * 1.6, -b.torso * H * 1.3, b.shoulder * H * 0.95, b.torso * H * 2.4);
    ctx.fillStyle = 'rgba(255,255,255,0.08)';
    ctx.fillRect(b.chest * H * 0.25, -b.torso * H * 1.3, b.chest * H * 0.35, b.torso * H * 0.9);
    ctx.restore();
  };

  // ---- Faces --------------------------------------------------------------------------------------
  // The head is drawn in its own frame: centre of the skull at (0, 0), r its half height, facing +x
  // in a three-quarter view. Eyes sit on the middle line, the mouth at three quarters down.
  const skull = (r, skin, jaw) => {
    ctx.fillStyle = skin;
    ctx.beginPath();
    ctx.ellipse(-r * 0.04, -r * 0.06, r * 0.86, r * 0.92, 0, 0, TAU);
    ctx.fill();
    // The jaw and chin, square or soft.
    const sq = jaw === 'square' ? 1 : jaw === 'round' ? -0.6 : 0;
    ctx.beginPath();
    ctx.moveTo(-r * 0.62, r * 0.25);
    ctx.quadraticCurveTo(-r * 0.45, r * (0.86 + sq * 0.06), r * (0.18 + sq * 0.06), r * (0.98 + sq * 0.03));
    ctx.quadraticCurveTo(r * (0.62 + sq * 0.12), r * (0.98 + sq * 0.05), r * (0.78 + sq * 0.04), r * 0.55);
    ctx.lineTo(r * 0.86, r * 0.05);
    ctx.lineTo(-r * 0.2, -r * 0.1);
    ctx.closePath();
    ctx.fill();
    // Nose: a bump off the profile line, a shade under it.
    ctx.beginPath();
    ctx.moveTo(r * 0.8, -r * 0.08);
    ctx.quadraticCurveTo(r * 1.04, r * 0.24, r * 0.82, r * 0.32);
    ctx.lineTo(r * 0.74, r * 0.2);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = tone(skin, -0.22);
    ctx.beginPath();
    ctx.ellipse(r * 0.78, r * 0.3, r * 0.07, r * 0.035, 0, 0, TAU);
    ctx.fill();
    // Ear, with its inner fold.
    ctx.fillStyle = skin;
    ctx.beginPath();
    ctx.ellipse(-r * 0.12, r * 0.08, r * 0.16, r * 0.22, 0.15, 0, TAU);
    ctx.fill();
    ctx.fillStyle = tone(skin, -0.2);
    ctx.beginPath();
    ctx.ellipse(-r * 0.1, r * 0.09, r * 0.08, r * 0.13, 0.15, 0, TAU);
    ctx.fill();
    // Cheek shade toward the back, a touch of light on the brow.
    ctx.fillStyle = 'rgba(0,0,0,0.06)';
    ctx.beginPath();
    ctx.ellipse(-r * 0.35, r * 0.25, r * 0.4, r * 0.55, 0, 0, TAU);
    ctx.fill();
  };
  // Eyes, brows and mouth. `pose` carries the expression: brow (-1 angry .. 1 raised), mouth (open),
  // eyes (closed); `face` the character's resting look: 'grin', 'smirk', 'flat', 'frown'.
  const features = (r, skin, face, pose, small) => {
    const shut = clamp(pose.eyes, 0, 1);
    const ink = INK;
    const eye = (x, wid) => {
      if (small) {
        if (shut > 0.6) polyline([[x - r * 0.08, -r * 0.02], [x + r * 0.08, -r * 0.02]], r * 0.07, ink);
        else {
          ctx.fillStyle = ink;
          ctx.beginPath();
          ctx.ellipse(x + r * 0.02, -r * 0.03, r * 0.075 * wid, r * 0.1 * (1 - shut * 0.8), 0, 0, TAU);
          ctx.fill();
        }
        return;
      }
      if (shut > 0.6) {
        polyline([[x - r * 0.11 * wid, -r * 0.02], [x + r * 0.11 * wid, -r * 0.0]], r * 0.05, ink);
        return;
      }
      ctx.fillStyle = PAPER;
      ctx.beginPath();
      ctx.ellipse(x, -r * 0.03, r * 0.12 * wid, r * 0.13 * (1 - shut * 0.85), 0, 0, TAU);
      ctx.fill();
      ctx.fillStyle = ink;
      ctx.beginPath();
      ctx.ellipse(x + r * 0.045 * wid + pose.look * r * 0.03, -r * 0.02, r * 0.065 * wid, r * 0.085 * (1 - shut * 0.85), 0, 0, TAU);
      ctx.fill();
      ctx.fillStyle = PAPER;
      ctx.beginPath();
      ctx.arc(x + r * 0.07 * wid, -r * 0.06, r * 0.022, 0, TAU);
      ctx.fill();
      // A heavy upper lid hangs over the top of the eye.
      const lid = (pose.lid || 0) * r * 0.12;
      if (lid) {
        ctx.save();
        ctx.beginPath();
        ctx.ellipse(x, -r * 0.03, r * 0.13 * wid, r * 0.14, 0, 0, TAU);
        ctx.clip();
        rect(x - r * 0.14 * wid, -r * 0.18, r * 0.28 * wid, r * 0.07 + lid, tone(skin, -0.06));
        ctx.restore();
      }
      // Upper lid line for weight.
      polyline([[x - r * 0.13 * wid, -r * 0.1 + lid], [x + r * 0.13 * wid, -r * 0.12 + lid]], r * 0.035, tone(skin, -0.45));
    };
    eye(r * 0.2, 0.72);
    eye(r * 0.56, 1);
    // Brows: angry pulls the inner ends down, raised lifts them.
    const bw = r * (small ? 0.09 : 0.075);
    const lift = pose.brow * r * 0.08;
    const brow = (x, w) => polyline([[x - r * 0.13 * w, -r * 0.24 - lift * 0.5], [x + r * 0.13 * w, -r * 0.24 + (pose.brow < 0 ? -pose.brow * r * 0.12 : -lift)]], bw, pose.browInk || tone(skin, -0.62));
    brow(r * 0.2, 0.72);
    brow(r * 0.56, 1);
    // Mouth: the resting look, opened up for a shout.
    const open = clamp(pose.mouth, 0, 1);
    const mx = r * 0.5;
    const my = r * 0.56;
    if (open > 0.1) {
      ctx.fillStyle = '#3a1414';
      ctx.beginPath();
      ctx.ellipse(mx, my, r * 0.17, r * (0.06 + open * 0.14), 0, 0, TAU);
      ctx.fill();
      if (!small) {
        rect(mx - r * 0.13, my - r * (0.05 + open * 0.12), r * 0.26, r * 0.05, PAPER);
        ctx.fillStyle = '#d0505a';
        ctx.beginPath();
        ctx.ellipse(mx, my + r * open * 0.1, r * 0.09, r * 0.04, 0, 0, TAU);
        ctx.fill();
      }
      return;
    }
    if (face === 'grin') {
      ctx.fillStyle = '#3a1414';
      ctx.beginPath();
      ctx.moveTo(mx - r * 0.3, my - r * 0.06);
      ctx.quadraticCurveTo(mx, my + r * 0.3, mx + r * 0.3, my - r * 0.1);
      ctx.closePath();
      ctx.fill();
      if (!small) {
        ctx.fillStyle = PAPER;
        ctx.beginPath();
        ctx.moveTo(mx - r * 0.26, my - r * 0.05);
        ctx.quadraticCurveTo(mx, my + r * 0.1, mx + r * 0.27, my - r * 0.09);
        ctx.closePath();
        ctx.fill();
      }
    } else if (face === 'smirk') {
      polyline([[mx - r * 0.16, my + r * 0.02], [mx + r * 0.04, my + r * 0.03], [mx + r * 0.18, my - r * 0.05]], r * 0.055, tone(skin, -0.55));
    } else if (face === 'frown') {
      polyline([[mx - r * 0.15, my + r * 0.04], [mx, my - r * 0.01], [mx + r * 0.15, my + r * 0.04]], r * 0.055, tone(skin, -0.55));
    } else {
      polyline([[mx - r * 0.14, my + r * 0.01], [mx + r * 0.15, my]], r * 0.05, tone(skin, -0.55));
    }
  };

  // ---- Hair ---------------------------------------------------------------------------------------
  // The hair cap over the skull: from the hairline at the brow, over the crown, down to the nape.
  // `lift` gives it volume on top, `line` moves the hairline back.
  const hairShape = (r, lift = 0, recede = 0) => {
    ctx.beginPath();
    ctx.moveTo(r * (0.8 - recede), -r * (0.3 + recede * 0.3));
    ctx.quadraticCurveTo(r * (0.78 - recede * 0.3), -r * (1.08 + lift), -r * 0.12, -r * (1.06 + lift));
    ctx.quadraticCurveTo(-r * (1.0 + lift * 0.5), -r * (0.98 + lift * 0.5), -r * 0.94, r * 0.12);
    ctx.lineTo(-r * 0.5, r * 0.22);
    ctx.quadraticCurveTo(-r * 0.32, -r * 0.24, r * 0.12, -r * 0.4);
    ctx.closePath();
  };
  const HAIR = {
    none: {},
    // Rows braided straight back, a lighter glint on each plait.
    cornrows: {
      front: (r, color, P) => {
        hairShape(r, 0.02);
        ctx.fillStyle = color;
        ctx.fill();
        if (P.small) return;
        ctx.save();
        hairShape(r, 0.02);
        ctx.clip();
        for (let k = 0; k < 5; k += 1) {
          const y0 = -r * (0.42 + k * 0.15);
          ctx.strokeStyle = tone(color, -0.5);
          ctx.lineWidth = Math.max(0.6, r * 0.035);
          ctx.beginPath();
          ctx.moveTo(r * 0.85, y0 + r * 0.1);
          ctx.quadraticCurveTo(-r * 0.1, y0 - r * 0.45 + k * r * 0.12, -r * 1.0, y0 + r * 0.55 + k * r * 0.1);
          ctx.stroke();
          ctx.strokeStyle = tone(color, 0.28);
          ctx.setLineDash([r * 0.06, r * 0.07]);
          ctx.lineWidth = Math.max(0.5, r * 0.03);
          ctx.beginPath();
          ctx.moveTo(r * 0.8, y0 + r * 0.02);
          ctx.quadraticCurveTo(-r * 0.1, y0 - r * 0.5 + k * r * 0.12, -r * 1.0, y0 + r * 0.45 + k * r * 0.1);
          ctx.stroke();
          ctx.setLineDash([]);
        }
        ctx.restore();
        // The plaits end in short tails at the nape.
        for (let k = 0; k < 4; k += 1) polyline([[-r * 0.86, r * (0.0 + k * 0.08)], [-r * 1.0, r * (0.22 + k * 0.08)]], r * 0.07, color);
      }
    },
    // Tight curls with volume on top.
    curls: {
      front: (r, color, P) => {
        hairShape(r, 0.16, 0.04);
        ctx.fillStyle = color;
        ctx.fill();
        const bumps = P.small ? 5 : 9;
        for (let k = 0; k < bumps; k += 1) {
          const a = Math.PI * (1.05 + (k / (bumps - 1)) * 0.95);
          const x = Math.cos(a) * r * 0.92 - r * 0.06;
          const y = Math.sin(a) * r * 1.08 - r * 0.12;
          circle(x, y, r * 0.17, color);
          if (!P.small) circle(x + r * 0.04, y - r * 0.05, r * 0.06, tone(color, 0.18));
        }
      }
    },
    // Pulled into a bun at the crown.
    bun: {
      front: (r, color, P) => {
        hairShape(r, 0.02);
        ctx.fillStyle = color;
        ctx.fill();
        circle(-r * 0.2, -r * 1.22, r * 0.32, color);
        if (!P.small) {
          circle(-r * 0.12, -r * 1.3, r * 0.1, tone(color, 0.22));
          polyline([[-r * 0.5, -r * 1.0], [r * 0.1, -r * 1.02]], r * 0.07, tone(color, -0.4));
        }
      }
    },
    // Slicked back with a shine, a little height at the front.
    slick: {
      front: (r, color, P) => {
        hairShape(r, 0.1, -0.02);
        ctx.fillStyle = color;
        ctx.fill();
        if (P.small) return;
        for (let k = 0; k < 4; k += 1) polyline([[r * (0.72 - k * 0.06), -r * (0.55 + k * 0.12)], [-r * (0.5 + k * 0.08), -r * (0.9 - k * 0.12)]], r * 0.035, tone(color, 0.35));
      }
    },
    // A short fade with a sharp line-up.
    fade: {
      front: (r, color) => {
        hairShape(r, -0.02, 0.06);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.save();
        hairShape(r, -0.02, 0.06);
        ctx.clip();
        const g = ctx.createLinearGradient(0, -r * 0.6, 0, r * 0.2);
        g.addColorStop(0, 'rgba(0,0,0,0)');
        g.addColorStop(1, 'rgba(255,255,255,0.25)');
        ctx.fillStyle = g;
        ctx.fillRect(-r * 1.2, -r * 1.2, r * 2.4, r * 1.5);
        ctx.restore();
      }
    }
  };

  // ---- Headwear and eyewear -----------------------------------------------------------------------
  const HEADWEAR = {
    // A fitted cap, flat brim slightly off to the side, the size sticker still on.
    fitted: (r, o, P) => {
      ctx.fillStyle = o.color;
      ctx.beginPath();
      ctx.moveTo(r * 0.9, -r * 0.42);
      ctx.quadraticCurveTo(r * 0.85, -r * 1.25, -r * 0.08, -r * 1.22);
      ctx.quadraticCurveTo(-r * 0.98, -r * 1.12, -r * 0.95, -r * 0.3);
      ctx.closePath();
      ctx.fill();
      rect(-r * 0.95, -r * 0.42, r * 1.86, r * 0.1, tone(o.color, -0.2));
      // Brim
      ctx.fillStyle = tone(o.color, -0.12);
      ctx.beginPath();
      ctx.moveTo(r * 0.55, -r * 0.42);
      ctx.lineTo(r * 1.55, -r * 0.5);
      ctx.lineTo(r * 1.5, -r * 0.34);
      ctx.lineTo(r * 0.55, -r * 0.3);
      ctx.closePath();
      ctx.fill();
      if (!P.small) {
        circle(r * 1.2, -r * 0.42, r * 0.07, GOLD);
        circle(-r * 0.05, -r * 1.21, r * 0.07, tone(o.color, -0.2));
        polyline([[r * 0.2, -r * 1.2], [r * 0.45, -r * 0.45]], r * 0.03, tone(o.color, -0.25));
        if (o.logo) logoRing(r * 0.35, -r * 0.8, r * 0.22, r * 0.11, r * 0.06, o.logo);
      }
    }
  };
  const SPECS = {
    // Narrow black wraparounds.
    shades: (r, P) => {
      ctx.fillStyle = '#0b0b0c';
      ctx.beginPath();
      ctx.moveTo(-r * 0.15, -r * 0.12);
      ctx.lineTo(r * 0.85, -r * 0.14);
      ctx.lineTo(r * 0.8, r * 0.06);
      ctx.quadraticCurveTo(r * 0.38, r * 0.12, r * 0.02, r * 0.03);
      ctx.closePath();
      ctx.fill();
      if (!P.small) polyline([[r * 0.3, -r * 0.08], [r * 0.6, -r * 0.09]], r * 0.03, 'rgba(255,255,255,0.45)');
    },
    // White shutter shades, 2008.
    shutter: (r, P) => {
      ctx.fillStyle = PAPER;
      ctx.beginPath();
      ctx.moveTo(-r * 0.12, -r * 0.16);
      ctx.lineTo(r * 0.86, -r * 0.18);
      ctx.lineTo(r * 0.82, r * 0.1);
      ctx.lineTo(-r * 0.05, r * 0.08);
      ctx.closePath();
      ctx.fill();
      if (!P.small) for (let k = 0; k < 4; k += 1) rect(-r * 0.05, -r * (0.1 - k * 0.055), r * 0.86, r * 0.025, '#2b2b2e');
    }
  };
  const FACIAL_HAIR = {
    // A short full beard along the jaw, a moustache over the lip.
    full: (r, color) => {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(-r * 0.55, r * 0.1);
      ctx.quadraticCurveTo(-r * 0.5, r * 0.95, r * 0.2, r * 1.08);
      ctx.quadraticCurveTo(r * 0.75, r * 1.05, r * 0.82, r * 0.5);
      ctx.lineTo(r * 0.62, r * 0.42);
      ctx.quadraticCurveTo(r * 0.5, r * 0.78, r * 0.2, r * 0.8);
      ctx.quadraticCurveTo(-r * 0.2, r * 0.75, -r * 0.35, r * 0.1);
      ctx.closePath();
      ctx.fill();
      polyline([[r * 0.3, r * 0.44], [r * 0.72, r * 0.42]], r * 0.09, color);
    },
    goatee: (r, color) => {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(r * 0.3, r * 0.68);
      ctx.quadraticCurveTo(r * 0.45, r * 1.08, r * 0.7, r * 0.7);
      ctx.closePath();
      ctx.fill();
      polyline([[r * 0.3, r * 0.44], [r * 0.7, r * 0.42]], r * 0.06, color);
    },
    stubble: (r, color) => {
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.28;
      ctx.beginPath();
      ctx.moveTo(-r * 0.5, r * 0.2);
      ctx.quadraticCurveTo(-r * 0.45, r * 0.9, r * 0.2, r * 0.98);
      ctx.quadraticCurveTo(r * 0.7, r * 0.98, r * 0.8, r * 0.4);
      ctx.lineTo(r * 0.3, r * 0.38);
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = 1;
    }
  };

  // ---- Clothes ------------------------------------------------------------------------------------
  // Tops dress the torso in its own frame; `sleeve` says how far down the arm they go (0..1), `fit` how loose.
  const armholes = (b, H, skin, trim) => {
    const SW = b.shoulder * H;
    const T = b.torso * H;
    [[-SW * 0.95, 1], [SW * 0.88, 1]].forEach(([x]) => {
      ctx.fillStyle = skin;
      ctx.beginPath();
      ctx.ellipse(x, -T * 0.82, SW * 0.36, T * 0.3, 0, 0, TAU);
      ctx.fill();
      if (trim) {
        ctx.strokeStyle = trim;
        ctx.lineWidth = Math.max(0.8, H * 0.008);
        ctx.stroke();
      }
    });
  };
  const neckline = (b, H, color, depth = 0.08, width = 0.45) => {
    const SW = b.shoulder * H;
    const T = b.torso * H;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(-SW * width * 0.6, -T - SW * 0.1);
    ctx.quadraticCurveTo(SW * 0.15, -T + depth * H, SW * width, -T - SW * 0.08);
    ctx.closePath();
    ctx.fill();
  };
  const TOP_STYLES = {
    // A sleeveless hoodie, hood down behind the neck, pocket, drawstrings and a print.
    hoodie: {
      sleeve: 0,
      back: (P, o) => {
        const { b, H, sk } = P;
        inTorso(sk, () => {
          ctx.fillStyle = tone(o.color, -0.12);
          ctx.beginPath();
          ctx.ellipse(-b.shoulder * H * 0.45, -b.torso * H + H * 0.005, b.shoulder * H * 0.5, H * 0.04, -0.15, 0, TAU);
          ctx.fill();
        });
      },
      body: (P, o) => {
        const { b, H } = P;
        const T = b.torso * H;
        torsoPath(b, H, 0.05);
        ctx.fillStyle = o.color;
        ctx.fill();
        ctx.save();
        torsoPath(b, H, 0.05);
        ctx.clip();
        armholes(b, H, P.skin, tone(o.color, -0.35));
        poly([[-b.waist * H * 0.4, -T * 0.32], [b.chest * H * 0.85, -T * 0.32], [b.chest * H * 1.0, 0.03 * H], [-b.waist * H * 0.6, 0.03 * H]], tone(o.color, -0.18));
        rect(-b.hips * H * 1.6, 0.03 * H, b.hips * H * 3.4, H * 0.025, tone(o.color, -0.3));
        if (!P.small && o.print) logoRing(b.chest * H * 0.25, -T * 0.62, H * 0.04, H * 0.022, H * 0.012, o.print);
        ctx.restore();
        torsoPath(b, H, 0.05);
        shadeBack(b, H, 0.12);
        neckline(b, H, tone(o.color, -0.3), 0.03, 0.55);
        if (!P.small) {
          [0.05, 0.22].forEach(x => polyline([[b.chest * H * x, -T - H * 0.005], [b.chest * H * (x + 0.04), -T * 0.62]], H * 0.007, PAPER));
        }
      }
    },
    // A bomber: ribbed collar, cuffs and hem in the accent, a zip, a patch on the chest.
    bomber: {
      sleeve: 1,
      fit: 1.22,
      cuff: o => o.accent,
      body: (P, o) => {
        const { b, H } = P;
        const T = b.torso * H;
        torsoPath(b, H, 0.03, 0.012);
        ctx.fillStyle = o.color;
        ctx.fill();
        ctx.save();
        torsoPath(b, H, 0.03, 0.012);
        ctx.clip();
        rect(-b.hips * H * 1.6, 0.0, b.hips * H * 3.4, H * 0.04, o.accent);
        if (!P.small) {
          for (let x = -b.hips * H * 1.5; x < b.hips * H * 1.8; x += H * 0.012) rect(x, 0, H * 0.003, H * 0.04, 'rgba(0,0,0,0.18)');
          polyline([[b.chest * H * 0.3, -T], [b.chest * H * 0.38, 0]], H * 0.008, '#c9ccd1');
          if (o.patch) circle(b.chest * H * 0.62, -T * 0.66, H * 0.022, o.patch);
        }
        ctx.restore();
        torsoPath(b, H, 0.03, 0.012);
        shadeBack(b, H, 0.12);
        ctx.fillStyle = o.accent;
        ctx.beginPath();
        ctx.ellipse(b.shoulder * H * 0.1, -T - b.shoulder * H * 0.07, b.shoulder * H * 0.5, H * 0.022, 0, 0, TAU);
        ctx.fill();
      }
    },
    // A long leather coat, open over a strapped vest; the skirt reaches the knees and its tail flies.
    coat: {
      sleeve: 1,
      fit: 1.2,
      back: (P, o) => {
        const { b, H, sk, mem } = P;
        const fly = mem ? spring(mem, 'coat', P.motion * 1.1 + 0.08 * Math.sin(clock * 2.2), 30, 5) : 0.1;
        inTorso(sk, () => {
          ctx.fillStyle = tone(o.color, -0.2);
          ctx.beginPath();
          ctx.moveTo(-b.shoulder * H * 0.9, -b.torso * H * 0.9);
          ctx.lineTo(-b.hips * H * 0.6, 0);
          ctx.quadraticCurveTo(-b.hips * H * (1.4 + fly * 2.5), H * 0.15, -b.hips * H * (1.6 + fly * 4.2), H * (0.3 - fly * 0.12));
          ctx.lineTo(b.hips * H * (0.6 - fly * 1.2), H * 0.34);
          ctx.lineTo(b.hips * H * 0.5, 0);
          ctx.closePath();
          ctx.fill();
        });
      },
      body: (P, o) => {
        const { b, H } = P;
        const T = b.torso * H;
        torsoPath(b, H, 0.06, 0.01);
        ctx.fillStyle = o.color;
        ctx.fill();
        torsoPath(b, H, 0.06, 0.01);
        shadeBack(b, H, 0.15);
        // The vest between the open lapels, straps crossing with a buckle.
        poly([[b.chest * H * 0.05, -T], [b.chest * H * 0.75, -T * 0.96], [b.chest * H * 0.85, 0.04 * H], [b.chest * H * 0.0, 0.04 * H]], '#222226');
        if (!P.small) {
          polyline([[b.chest * H * 0.1, -T * 0.92], [b.chest * H * 0.8, -T * 0.25]], H * 0.012, '#3a3a40');
          polyline([[b.chest * H * 0.75, -T * 0.92], [b.chest * H * 0.08, -T * 0.25]], H * 0.012, '#3a3a40');
          rect(b.chest * H * 0.36, -T * 0.62, H * 0.022, H * 0.022, '#c9ccd1');
        }
        // Lapels
        poly([[-b.shoulder * H * 0.2, -T - H * 0.01], [b.chest * H * 0.12, -T * 0.4], [b.chest * H * 0.0, -T * 0.95]], tone(o.color, 0.12));
        poly([[b.shoulder * H * 0.65, -T - H * 0.005], [b.chest * H * 0.78, -T * 0.4], [b.chest * H * 0.62, -T * 0.92]], tone(o.color, 0.12));
      },
      // The front of the skirt hangs over the thighs, drawn after the legs.
      skirt: (P, o) => {
        const { b, H } = P;
        const sway = P.mem ? spring(P.mem, 'skirt', P.motion * 0.5, 40, 6) : 0;
        ctx.fillStyle = o.color;
        ctx.beginPath();
        ctx.moveTo(-b.hips * H * 1.1, 0);
        ctx.lineTo(-b.hips * H * (1.25 + sway), H * 0.27);
        ctx.lineTo(-b.hips * H * (0.1 + sway), H * 0.28);
        ctx.lineTo(b.hips * H * 0.15, 0.03 * H);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = tone(o.color, 0.06);
        ctx.beginPath();
        ctx.moveTo(b.hips * H * 0.85, 0);
        ctx.lineTo(b.hips * H * (1.1 - sway * 0.5), H * 0.26);
        ctx.lineTo(b.hips * H * (0.55 - sway * 0.5), H * 0.27);
        ctx.lineTo(b.hips * H * 0.6, 0.03 * H);
        ctx.closePath();
        ctx.fill();
      }
    },
    // A white ribbed tank.
    tank: {
      sleeve: 0,
      body: (P, o) => {
        const { b, H } = P;
        const T = b.torso * H;
        torsoPath(b, H, 0.03);
        ctx.fillStyle = P.skin;
        ctx.fill();
        torsoPath(b, H, 0.03);
        shadeBack(b, H, 0.1);
        ctx.save();
        torsoPath(b, H, 0.03);
        ctx.clip();
        ctx.fillStyle = o.color;
        ctx.beginPath();
        ctx.moveTo(-b.shoulder * H * 0.55, -T - H * 0.01);
        ctx.lineTo(-b.shoulder * H * 0.35, -T - H * 0.01);
        ctx.quadraticCurveTo(b.shoulder * H * 0.05, -T * 0.72, b.shoulder * H * 0.42, -T - H * 0.005);
        ctx.lineTo(b.shoulder * H * 0.62, -T - H * 0.005);
        ctx.quadraticCurveTo(b.chest * H * 0.7, -T * 0.65, b.chest * H * 1.2, -T * 0.55);
        ctx.lineTo(b.hips * H * 1.6, 0.05 * H);
        ctx.lineTo(-b.hips * H * 1.6, 0.05 * H);
        ctx.lineTo(-b.waist * H * 1.4, -T * 0.6);
        ctx.quadraticCurveTo(-b.shoulder * H * 0.75, -T * 0.7, -b.shoulder * H * 0.55, -T - H * 0.01);
        ctx.fill();
        if (!P.small) for (let x = -b.hips * H * 1.2; x < b.chest * H * 1.2; x += H * 0.012) rect(x, -T * 0.7, H * 0.003, T * 0.75, 'rgba(0,0,0,0.07)');
        ctx.restore();
      }
    },
    // A tall white tee down to the thighs, wide short sleeves.
    tallTee: {
      sleeve: 0.45,
      fit: 1.4,
      body: (P, o) => {
        const { b, H } = P;
        const T = b.torso * H;
        torsoPath(b, H, 0.17, 0.03);
        ctx.fillStyle = o.color;
        ctx.fill();
        torsoPath(b, H, 0.17, 0.03);
        shadeBack(b, H, 0.1);
        if (!P.small) {
          polyline([[-b.hips * H * 0.4, -T * 0.2], [-b.hips * H * 0.6, 0.15 * H]], H * 0.005, 'rgba(0,0,0,0.1)');
          polyline([[b.hips * H * 0.6, -T * 0.1], [b.hips * H * 0.9, 0.15 * H]], H * 0.005, 'rgba(0,0,0,0.1)');
        }
        neckline(b, H, tone(o.color, -0.12), 0.02, 0.5);
      }
    }
  };

  // Trousers: how loose, and the details that make them what they are.
  const BOTTOMS = {
    joggers: { fit: 1.22, cuff: true },
    track: { fit: 1.25, cuff: true },
    cargo: { fit: 1.2, pocket: true, stack: true },
    jeans: { fit: 1.4, stack: true, seam: true },
    slacks: { fit: 1.12, crease: true },
    wide: { fit: 1.7, crease: true }
  };
  const pantsLeg = (P, L, far, o) => {
    const kind = BOTTOMS[o.style];
    const { b, H } = P;
    const col = far ? tone(o.color, -0.2) : o.color;
    const ra = b.leg * H * 0.5;
    const rb = b.leg * H * 0.4;
    const rc = b.calf * H * 0.42;
    const end = coverLimb(L.hip, L.knee, L.ankle, ra, rb, rc, 1, col, kind.fit);
    if (P.small) return;
    if (o.stripe) polyline([[L.hip.x, L.hip.y], [L.knee.x, L.knee.y], [end.at.x, end.at.y]], H * 0.012, far ? tone(o.stripe, -0.2) : o.stripe);
    if (kind.cuff) cuffBand(end, end.r * 0.95, tone(col, -0.2), H * 0.02);
    if (kind.stack) {
      for (let k = 1; k <= 3; k += 1) {
        const q = midPoint(L.knee, L.ankle, 0.62 + k * 0.1);
        inFrame(q.x, q.y, end.dir, () => polyline([[-H * 0.003, -end.r * 0.9], [H * 0.006, end.r * 0.2], [-H * 0.002, end.r * 0.9]], H * 0.005, tone(col, -0.25)));
      }
    }
    if (kind.pocket) {
      const q = midPoint(L.hip, L.knee, 0.55);
      inFrame(q.x, q.y, Math.atan2(L.knee.y - L.hip.y, L.knee.x - L.hip.x), () => {
        roundRect(-H * 0.03, -ra * 0.2, H * 0.06, ra * 1.0, H * 0.008, tone(col, 0.07));
        rect(-H * 0.03, -ra * 0.2, H * 0.06, H * 0.01, tone(col, -0.18));
      });
    }
    if (kind.crease) polyline([[midPoint(L.hip, L.knee, 0.3).x + ra * 0.3, midPoint(L.hip, L.knee, 0.3).y], [L.knee.x + ra * 0.3, L.knee.y], [end.at.x + rc * 0.3, end.at.y]], H * 0.004, tone(col, 0.15));
    if (kind.seam) polyline([[L.hip.x, L.hip.y], [L.knee.x, L.knee.y], [end.at.x, end.at.y]], H * 0.004, 'rgba(255,215,120,0.45)');
    // A fold behind the knee.
    polyline([[L.knee.x - rb * 0.7, L.knee.y - rb * 0.2], [L.knee.x - rb * 0.2, L.knee.y + rb * 0.4]], H * 0.005, tone(col, -0.28));
  };
  const pelvis = (P, o) => {
    const { b, H, sk } = P;
    const kind = BOTTOMS[o.style];
    inTorso(sk, () => {
      const w = b.hips * H * (kind.fit > 1.3 ? 1.12 : 1.02);
      roundRect(-w, -0.045 * H, w * 2 + b.belly * H * 0.6, 0.1 * H, 0.035 * H, o.color);
      if (o.belt) rect(-w, -0.045 * H, w * 2 + b.belly * H * 0.6, 0.02 * H, o.belt);
    });
  };

  // Shoes in the ankle's frame, the sole on the ground when the foot is planted.
  const SHOES = {
    // The ankle sits `h` above the ground; every shoe reaches down to it.
    sneaker: (P, o, far, len, h) => {
      const col = far ? tone(o.color, -0.18) : o.color;
      roundRect(-len * 0.3, -h * 0.55, len, h * 1.55, h * 0.55, col);
      roundRect(-len * 0.32, h * 0.5, len * 1.03, h * 0.5, h * 0.22, far ? tone(o.sole || PAPER, -0.15) : (o.sole || PAPER));
      if (!P.small) {
        if (o.accent) polyline([[-len * 0.12, h * 0.3], [len * 0.22, -h * 0.05], [len * 0.42, h * 0.32]], h * 0.2, far ? tone(o.accent, -0.2) : o.accent);
        polyline([[len * 0.08, -h * 0.4], [len * 0.38, -h * 0.18]], h * 0.12, 'rgba(255,255,255,0.55)');
      }
    },
    boot: (P, o, far, len, h) => {
      const col = far ? tone(o.color, -0.18) : o.color;
      roundRect(-len * 0.3, -h * 2.1, len * 0.64, h * 2.8, h * 0.35, col);
      roundRect(-len * 0.3, -h * 0.4, len * 1.02, h * 1.2, h * 0.5, col);
      roundRect(-len * 0.33, h * 0.68, len * 1.07, h * 0.32, h * 0.12, o.sole || '#3a2a1c');
      if (!P.small) for (let k = 0; k < 3; k += 1) rect(len * 0.14, -h * (1.8 - k * 0.5), len * 0.16, h * 0.13, tone(col, 0.3));
    },
    dress: (P, o, far, len, h) => {
      const col = far ? tone(o.color, -0.18) : o.color;
      roundRect(-len * 0.3, -h * 0.4, len * 1.04, h * 1.25, h * 0.55, col);
      rect(-len * 0.3, h * 0.8, len * 1.04, h * 0.2, '#0a0a0a');
      if (!P.small) polyline([[len * 0.22, -h * 0.22], [len * 0.52, -h * 0.08]], h * 0.12, 'rgba(255,255,255,0.4)');
    }
  };
  const drawShoe = (P, L, far) => {
    const o = P.spec.shoes;
    const len = P.b.foot * P.H;
    const h = P.b.ankle * P.H;
    inFrame(L.ankle.x, L.ankle.y, L.toe, () => SHOES[o.style](P, o, far, len, h));
  };

  // Hands in the wrist's frame, pointing along +x.
  const HANDS = {
    fist: (P, col, s) => {
      roundRect(-s * 0.15, -s * 0.52, s * 1.05, s * 1.04, s * 0.42, col);
      if (!P.small) polyline([[s * 0.62, -s * 0.38], [s * 0.62, s * 0.38]], s * 0.1, 'rgba(0,0,0,0.18)');
      roundRect(s * 0.05, -s * 0.66, s * 0.5, s * 0.3, s * 0.15, tone(col, -0.08));
    },
    wrap: (P, col, s) => {
      HANDS.fist(P, col, s);
      for (let k = 0; k < 3; k += 1) rect(-s * 0.25 + k * s * 0.32, -s * 0.55, s * 0.18, s * 1.1, '#f2f0ea');
    },
    glove: (P, col, s) => HANDS.fist(P, '#1b1b1e', s),
    open: (P, col, s) => {
      roundRect(-s * 0.1, -s * 0.45, s * 0.85, s * 0.9, s * 0.35, col);
      for (let k = 0; k < 4; k += 1) capsule(s * 0.6, -s * 0.33 + k * s * 0.22, s * 0.1, s * 1.05, -s * 0.36 + k * s * 0.24, s * 0.09, col);
      capsule(s * 0.2, -s * 0.45, s * 0.12, s * 0.55, -s * 0.8, s * 0.1, col);
    }
  };

  // Props held in a hand. Each draws along +x from the grip and returns its length, for the tip.
  const PROPS = {
    // A straight silver blade with a blood-red edge.
    sword: (P, o) => {
      const H = P.H;
      const len = (o.len || 0.6) * H;
      roundRect(-H * 0.08, -H * 0.013, H * 0.11, H * 0.026, H * 0.008, '#1b1b1e');
      rect(H * 0.025, -H * 0.045, H * 0.018, H * 0.09, '#c9ccd1');
      poly([[H * 0.045, -H * 0.016], [len - H * 0.06, -H * 0.016], [len, 0], [len - H * 0.06, H * 0.016], [H * 0.045, H * 0.016]], '#dfe3e8');
      polyline([[H * 0.05, -H * 0.012], [len - H * 0.05, -H * 0.012]], H * 0.008, '#d6182f');
      if (!P.small) polyline([[H * 0.07, H * 0.004], [len * 0.7, H * 0.004]], H * 0.004, 'rgba(255,255,255,0.8)');
      return len;
    },
    // A throwing star as big as a head.
    star: (P, o) => {
      const r = P.H * (o.size || 0.11);
      shuriken(r * 0.6, 0, r, o.color || '#ff2d55', clock * (o.spin || 0));
      return r * 0.6;
    },
    // A fan of magic cards between the fingers.
    cards: P => {
      const h = P.H * 0.1;
      [-0.35, 0, 0.35].forEach((a, i) => playCard(h * 0.5 + Math.cos(a) * h * 0.2, Math.sin(a) * h * 0.5, h, CARD_COLORS[i % CARD_COLORS.length], a + Math.PI / 2));
      return h * 0.6;
    }
  };

  // ---- Gear ---------------------------------------------------------------------------------------
  // Worn on the chest in the torso's frame: chains and medallions hang from the neck.
  const GEAR = {
    iced: (P, o) => {
      const { b, H } = P;
      const T = b.torso * H;
      ctx.strokeStyle = '#dfe6ee';
      ctx.lineWidth = Math.max(1, H * 0.01);
      ctx.beginPath();
      ctx.moveTo(-b.shoulder * H * 0.15, -T - H * 0.01);
      ctx.quadraticCurveTo(b.chest * H * 0.2, -T * 0.25, b.chest * H * 0.62, -T - H * 0.005);
      ctx.stroke();
      const mx = b.chest * H * 0.25;
      const my = -T * 0.38;
      ctx.save();
      ctx.shadowColor = 'rgba(200,230,255,0.9)';
      ctx.shadowBlur = H * 0.03;
      ctx.beginPath();
      starPath(ctx, mx, my, H * 0.045, H * 0.018, 4);
      ctx.fillStyle = '#eef4fa';
      ctx.fill();
      ctx.restore();
      if (!P.small) circle(mx, my, H * 0.01, o.stone || '#a06bff');
    }
  };
  // Worn on the head in its frame, after the face.
  const HEADGEAR = {
    // The cigarette in the corner of the mouth, the tip glowing.
    cigarette: (r, P) => {
      const tilt = 0.25;
      ctx.save();
      ctx.translate(r * 0.62, r * 0.58);
      ctx.rotate(tilt);
      rect(0, -r * 0.045, r * 0.42, r * 0.09, PAPER);
      rect(0, -r * 0.045, r * 0.1, r * 0.09, '#e0a35c');
      const glow = 0.6 + Math.sin(clock * 3) * 0.3;
      rect(r * 0.4, -r * 0.045, r * 0.06, r * 0.09, `rgba(255,${Math.round(90 + glow * 80)},40,1)`);
      if (!P.small) {
        ctx.globalCompositeOperation = 'lighter';
        circle(r * 0.44, 0, r * 0.1 * glow, 'rgba(255,120,40,0.5)');
      }
      ctx.restore();
    }
  };

  // ---- Assembly -----------------------------------------------------------------------------------
  const drawLeg = (P, L, far) => {
    const { b, H, spec } = P;
    bentLimb(L.hip, L.knee, L.ankle, b.leg * H * 0.5, b.leg * H * 0.4, b.calf * H * 0.42, far ? tone(P.skin, -0.16) : P.skin);
    pantsLeg(P, L, far, spec.bottom);
    drawShoe(P, L, far);
  };
  const drawArm = (P, A, far) => {
    const { b, H, spec, pose } = P;
    const skin = far ? tone(P.skin, -0.16) : P.skin;
    const r1 = b.arm * H * 0.5;
    const r2 = b.arm * H * 0.42;
    const r3 = b.wrist * H * 0.5;
    const top = TOP_STYLES[spec.top.style];
    // A stub only: the rest of the arm is out on its way to the target.
    if (!far && pose.stub) {
      capsule(A.sh.x, A.sh.y, r1, A.el.x, A.el.y, r2, skin);
      if (top.sleeve > 0) coverLimb(A.sh, A.el, A.wr, r1, r2, r3, Math.min(0.5, top.sleeve), spec.top.sleeves || spec.top.color, top.fit || 1.15);
      return A.el;
    }
    bentLimb(A.sh, A.el, A.wr, r1, r2, r3, skin);
    if (top.sleeve === 0 && !P.small) {
      // Bare arms: the swell of the biceps and a shade along the forearm.
      const q = midPoint(A.sh, A.el, 0.45);
      ctx.fillStyle = tone(P.skin, 0.1);
      ctx.beginPath();
      ctx.ellipse(q.x, q.y, r1 * 0.55, r1 * 0.35, Math.atan2(A.el.y - A.sh.y, A.el.x - A.sh.x), 0, TAU);
      ctx.fill();
    }
    if (top.sleeve > 0) {
      const sleeves = spec.top.sleeves || spec.top.color;
      const col = far ? tone(sleeves, -0.18) : sleeves;
      const end = coverLimb(A.sh, A.el, A.wr, r1, r2, r3, top.sleeve, col, top.fit || 1.15);
      if (top.cuff) {
        const cuff = top.cuff(spec.top);
        cuffBand(end, end.r, far ? tone(cuff, -0.18) : cuff, H * 0.022);
      }
      if (top.sleeveDetail) top.sleeveDetail(P, end, spec.top, far, A);
    }
    if (spec.armFx) spec.armFx(P, A, far);
    const hands = spec.hands || 'fist';
    inFrame(A.wr.x, A.wr.y, A.handA, () => HANDS[hands](P, skin, b.hand * H));
    let tip = A.hand;
    const prop = spec.props && spec.props[far ? 'B' : 'F'];
    if (prop && !pose[far ? 'freeB' : 'freeF']) {
      // A gun or a tube is pointed where the pose aims it; anything else turns with the hand.
      const a = prop.abs && !far ? pose.propAim : A.handA - Math.PI / 2 + (far ? pose.gripB : pose.grip) + (prop.grip || 0);
      P.propA = a;
      // `propLen` below 1 foreshortens it, swung round toward or away from the viewer.
      const short = far ? 1 : clamp(pose.propLen, 0.15, 1);
      inFrame(A.hand.x, A.hand.y, a, () => {
        ctx.scale(short, 1);
        const len = PROPS[prop.kind](P, prop) * short;
        tip = { x: A.hand.x + Math.cos(a) * len, y: A.hand.y + Math.sin(a) * len };
      });
    }
    return tip;
  };
  const drawHead = (P, blink) => {
    const { sk, spec, H, pose } = P;
    const h = sk.head;
    const r = h.r;
    const neckR = r * (spec.neck || 0.36);
    const base = { x: h.x - Math.sin(sk.headTilt) * r * 0.5, y: h.y + Math.cos(sk.headTilt) * r * 0.5 };
    capsule(sk.chest.x, sk.chest.y + H * 0.01, neckR, base.x, base.y, neckR * 0.92, tone(P.skin, -0.12));
    // A turtleneck or a funnel collar wraps the neck, under the chin.
    if (spec.collar) {
      const c = spec.collar;
      capsule(sk.chest.x, sk.chest.y + H * 0.01, neckR * c.wide, base.x, base.y, neckR * c.wide * 0.94, c.color);
      if (!P.small) for (let k = 1; k < 4; k += 1) {
        const q = k / 4;
        polyline([[lerp(sk.chest.x, base.x, q) - neckR * c.wide * 0.8, lerp(sk.chest.y, base.y, q)], [lerp(sk.chest.x, base.x, q) + neckR * c.wide * 0.8, lerp(sk.chest.y, base.y, q) - H * 0.004]], H * 0.004, tone(c.color, -0.25));
      }
    }
    inFrame(h.x, h.y, sk.headTilt, () => {
      skull(r, P.skin, spec.jaw);
      if (spec.beard) FACIAL_HAIR[spec.beard.style](r, spec.beard.color);
      features(r, P.skin, spec.face || 'flat', { ...pose, eyes: Math.max(pose.eyes, blink), lid: spec.lids || 0, browInk: spec.brows }, P.small);
      const hair = HAIR[spec.hair.style];
      if (hair.front) hair.front(r, spec.hair.color, P);
      if (spec.hat) HEADWEAR[spec.hat.style](r, spec.hat, P);
      if (spec.specs) SPECS[spec.specs](r, P);
      (spec.headgear || []).forEach(g => (typeof g === 'string' ? HEADGEAR[g](r, P, {}) : HEADGEAR[g.kind](r, P, g)));
    });
  };
  // Draws a figure from its spec in a pose, `H` tall, feet on (0, 0), facing right. `mem` keeps its springs
  // between frames. Returns the points effects come from: the front hand, the tip of what it holds, the head.
  const drawRig = (spec, pose, H, mem = null, seed = 0) => {
    const sk = skeleton(spec, pose, H);
    const P = { sk, b: sk.b, H, spec, pose, mem, small: H < 46, skin: spec.skin, motion: pose.motion || 0 };
    const top = TOP_STYLES[spec.top.style];
    ctx.save();
    if (pose.sq !== 1) ctx.scale(1 / Math.sqrt(pose.sq), pose.sq);
    if (top.back) top.back(P, spec.top);
    const hair = HAIR[spec.hair.style];
    if (hair.back) inFrame(sk.head.x, sk.head.y, sk.headTilt, () => hair.back(sk.head.r, spec.hair.color, P));
    drawArm(P, sk.armB, true);
    drawLeg(P, sk.legB, true);
    drawLeg(P, sk.legF, false);
    pelvis(P, spec.bottom);
    if (top.skirt) inTorso(sk, () => top.skirt(P, spec.top));
    inTorso(sk, () => {
      top.body(P, spec.top);
      (spec.gear || []).filter(g => GEAR[g.kind]).forEach(g => GEAR[g.kind](P, g));
    });
    drawHead(P, blinkAt(clock, seed));
    const tip = drawArm(P, sk.armF, false);
    ctx.restore();
    return { hand: sk.armF.hand, tip, head: sk.head, chest: sk.chest, sk };
  };

  // Every figure gets an ink outline round its silhouette, like a sprite, so it reads on any ground.
  // It is drawn twice, straight onto the canvas: first every shape in ink and grown by a few pixels all round,
  // then in colour on top. Only the ring outside is left of the ink, sharp at any zoom and any screen.
  const OUTLINE = 'rgba(24,20,30,0.9)';
  const OUTLINE_ALPHA = 0.9;
  // How opaque a colour is: rgba() carries it, everything else the canvas reports is solid.
  const alphaOf = style => (typeof style !== 'string' ? 0 : style.startsWith('rgba') ? Number(style.slice(style.lastIndexOf(',') + 1, -1)) : 1);
  // The ink pass: the real canvas in disguise. Fills and strokes come out in ink, grown by `grow` device pixels;
  // glows that add light and gradient sheens leave none, and everything else goes straight through.
  const ink = { real: null, grow: 1, unit: 0, bound: new Map() };
  const inkGrow = () => {
    if (!ink.unit) {
      const m = ink.real.getTransform();
      ink.unit = ink.grow / (Math.hypot(m.a, m.b) || 1);
    }
    return ink.unit;
  };
  const inkDraw = (paint, style, width) => {
    const c = ink.real;
    const alpha = alphaOf(style);
    if (!alpha || c.globalCompositeOperation !== 'source-over') return;
    const saved = c.globalAlpha;
    c.save();
    c.shadowColor = 'transparent';
    c.globalAlpha = saved * alpha * OUTLINE_ALPHA;
    c.fillStyle = OUTLINE;
    c.strokeStyle = OUTLINE;
    c.lineJoin = 'round';
    c.lineCap = 'round';
    c.lineWidth = width + inkGrow() * 2;
    paint(c);
    c.restore();
  };
  const INK_MOVES = new Set(['translate', 'rotate', 'scale', 'transform', 'setTransform', 'resetTransform', 'restore']);
  const INK_OVERRIDES = {
    fill: (...args) => inkDraw(c => {
      c.fill(...args);
      if (!(args[0] instanceof Path2D)) c.stroke();
      else c.stroke(args[0]);
    }, ink.real.fillStyle, 0),
    stroke: (...args) => inkDraw(c => c.stroke(...args), ink.real.strokeStyle, ink.real.lineWidth),
    fillRect: (x, y, w, h) => inkDraw(c => {
      c.fillRect(x, y, w, h);
      c.strokeRect(x, y, w, h);
    }, ink.real.fillStyle, 0),
    strokeRect: (x, y, w, h) => inkDraw(c => c.strokeRect(x, y, w, h), ink.real.strokeStyle, ink.real.lineWidth),
    fillText: (...args) => inkDraw(c => {
      c.fillText(...args);
      c.strokeText(...args);
    }, ink.real.fillStyle, 0),
    strokeText: (...args) => inkDraw(c => c.strokeText(...args), ink.real.strokeStyle, ink.real.lineWidth)
  };
  const inkCtx = new Proxy({}, {
    get: (_, key) => {
      if (INK_OVERRIDES[key]) return INK_OVERRIDES[key];
      const value = ink.real[key];
      if (typeof value !== 'function') return value;
      let fn = ink.bound.get(key);
      if (!fn) {
        fn = (...args) => {
          if (INK_MOVES.has(key)) ink.unit = 0;
          return ink.real[key](...args);
        };
        ink.bound.set(key, fn);
      }
      return fn;
    },
    set: (_, key, value) => {
      ink.real[key] = value;
      return true;
    }
  });
  // A see-through figure would show its own ink through itself, so it is drawn whole into a scratch canvas,
  // ringed there and laid on in one piece.
  const scratch = { art: null, ink: null };
  const scratchCanvas = (key, w, h) => {
    let c = scratch[key];
    if (!c) c = scratch[key] = document.createElement('canvas');
    if (c.width < w || c.height < h) {
      c.width = Math.max(c.width, w);
      c.height = Math.max(c.height, h);
    }
    return c;
  };
  const OUTLINE_STEPS = [[1, 0], [-1, 0], [0, 1], [0, -1], [0.7, 0.7], [-0.7, 0.7], [0.7, -0.7], [-0.7, -0.7]];
  const outlinedFaded = (s, k, d, draw) => {
    const m = ctx.getTransform();
    // Room for a raised weapon or a tall hat above, a long reach either side and the shadow below.
    const half = Math.ceil(s * k * 1.3);
    const w = half * 2;
    const h = Math.ceil(s * k * 2.05);
    const top = Math.ceil(s * k * 1.7);
    if (w > 2400) return draw();
    // Laid on whole device pixels, so it is never smeared between two.
    const ox = Math.round(m.e);
    const oy = Math.round(m.f);
    const art = scratchCanvas('art', w, h);
    const rim = scratchCanvas('ink', w, h);
    const main = ctx;
    const ac = art.getContext('2d');
    ac.setTransform(1, 0, 0, 1, 0, 0);
    ac.clearRect(0, 0, w + 2, h + 2);
    ac.setTransform(m.a, m.b, m.c, m.d, half + m.e - ox, top + m.f - oy);
    ctx = ac;
    let out;
    try {
      out = draw();
    } finally {
      ctx = main;
    }
    const rc = rim.getContext('2d');
    rc.setTransform(1, 0, 0, 1, 0, 0);
    rc.clearRect(0, 0, w + 2, h + 2);
    rc.globalCompositeOperation = 'source-over';
    rc.drawImage(art, 0, 0, w, h, 0, 0, w, h);
    rc.globalCompositeOperation = 'source-in';
    rc.fillStyle = OUTLINE;
    rc.fillRect(0, 0, w, h);
    rc.globalCompositeOperation = 'source-over';
    const x = ox - half;
    const y = oy - top;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    OUTLINE_STEPS.forEach(([dx, dy]) => ctx.drawImage(rim, 0, 0, w, h, x + Math.round(dx * d), y + Math.round(dy * d), w, h));
    ctx.drawImage(art, 0, 0, w, h, x, y, w, h);
    ctx.restore();
    return out;
  };
  // `s` is the figure's height in local units; `draw` paints it with the feet on (0, 0). Returns what draw returns.
  const outlined = (s, draw) => {
    if (ctx === inkCtx) return draw();
    const m = ctx.getTransform();
    const k = Math.hypot(m.a, m.b);
    if (s * k < 4) return draw();
    const d = clamp(s * k * 0.015, 1, 4);
    if (ctx.globalAlpha < 1) return outlinedFaded(s, k, d, draw);
    const main = ctx;
    ink.real = main;
    ink.grow = d;
    ink.unit = 0;
    ctx = inkCtx;
    main.save();
    try {
      draw();
    } finally {
      main.restore();
      ctx = main;
    }
    replaying = true;
    try {
      return draw();
    } finally {
      replaying = false;
    }
  };

  // ---- The MIXEDOWN heroes ------------------------------------------------------------------------
  // Each hero is a spec for the rig: build, skin, clothes, hair and gear, plus how he moves: his guard
  // in a fight, his gait, his attack and his special as clips, and what he does at home to show off.
  // Parts only the heroes wear.
  Object.assign(HAIR, {
    // An undercut: the sides faded to stubble, the top combed back in one smooth sweep, fuller with `len`.
    undercut: {
      front: (r, color, P) => {
        const len = P.spec.hair.len || 1;
        hairShape(r, -0.02, 0.06);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.save();
        hairShape(r, -0.02, 0.06);
        ctx.clip();
        const g = ctx.createLinearGradient(0, -r * 0.7, 0, r * 0.2);
        g.addColorStop(0, 'rgba(255,255,255,0)');
        g.addColorStop(1, 'rgba(255,255,255,0.3)');
        ctx.fillStyle = g;
        ctx.fillRect(-r * 1.2, -r * 1.2, r * 2.4, r * 1.5);
        ctx.restore();
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(r * 0.8, -r * 0.62);
        ctx.quadraticCurveTo(r * 0.92, -r * (1.05 + len * 0.2), r * 0.4, -r * (1.14 + len * 0.22));
        ctx.quadraticCurveTo(-r * 0.3, -r * (1.16 + len * 0.18), -r * 0.82, -r * 0.82);
        ctx.quadraticCurveTo(-r * 0.2, -r * 0.92, r * 0.8, -r * 0.62);
        ctx.closePath();
        ctx.fill();
        if (!P.small) for (let k = 0; k < 3; k += 1) polyline([[r * (0.74 - k * 0.1), -r * (0.8 + k * 0.1)], [r * (0.3 - k * 0.1), -r * (1.06 + len * 0.14 + k * 0.03)], [-r * (0.55 + k * 0.08), -r * (0.95 + k * 0.04)]], r * 0.03, tone(color, 0.32));
      }
    },
    // A skin fade: the sides buzzed down to a shadow, the top combed straight back with a little lift in front.
    skinfade: {
      front: (r, color, P) => {
        hairShape(r, -0.04, 0.06);
        ctx.fillStyle = color;
        ctx.globalAlpha = 0.42;
        ctx.fill();
        ctx.globalAlpha = 1;
        const top = () => {
          ctx.beginPath();
          ctx.moveTo(r * 0.74, -r * 0.42);
          ctx.quadraticCurveTo(r * 0.9, -r * 0.78, r * 0.66, -r * 1.12);
          ctx.quadraticCurveTo(r * 0.05, -r * 1.24, -r * 0.6, -r * 0.98);
          ctx.quadraticCurveTo(-r * 0.86, -r * 0.82, -r * 0.84, -r * 0.5);
          ctx.quadraticCurveTo(-r * 0.1, -r * 0.62, r * 0.74, -r * 0.42);
          ctx.closePath();
        };
        top();
        ctx.fillStyle = color;
        ctx.fill();
        if (P.small) return;
        ctx.save();
        top();
        ctx.clip();
        for (let k = 0; k < 3; k += 1) polyline([[r * (0.74 - k * 0.04), -r * (0.62 + k * 0.14)], [r * (0.3 - k * 0.05), -r * (1.0 + k * 0.05)], [-r * (0.5 + k * 0.06), -r * (0.86 + k * 0.03)]], r * 0.03, tone(color, 0.3));
        ctx.restore();
      }
    },
    // A short brush cut standing straight up.
    brush: {
      front: (r, color) => {
        hairShape(r, 0.08, 0.02);
        ctx.fillStyle = color;
        ctx.fill();
        for (let k = 0; k < 7; k += 1) {
          const x = r * (0.7 - k * 0.24);
          const y = -r * (1.1 - Math.abs(k - 2.5) * 0.04);
          poly([[x - r * 0.12, y + r * 0.08], [x + r * 0.02, y - r * 0.12], [x + r * 0.1, y + r * 0.08]], color);
        }
      }
    }
  });
  // Short dark stubble: a strap along the jaw, the chin and the lip, the cheeks left clear.
  FACIAL_HAIR.shadow = (r, color) => {
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.34;
    ctx.beginPath();
    ctx.moveTo(-r * 0.08, r * 0.3);
    ctx.quadraticCurveTo(-r * 0.34, r * 0.88, r * 0.26, r * 0.98);
    ctx.quadraticCurveTo(r * 0.7, r * 0.97, r * 0.8, r * 0.52);
    ctx.lineTo(r * 0.82, r * 0.38);
    ctx.quadraticCurveTo(r * 0.56, r * 0.35, r * 0.34, r * 0.44);
    ctx.quadraticCurveTo(r * 0.28, r * 0.62, r * 0.36, r * 0.74);
    ctx.quadraticCurveTo(r * 0.02, r * 0.76, r * 0.02, r * 0.3);
    ctx.closePath();
    ctx.fill();
    ctx.globalAlpha = 1;
  };
  // A silk durag pulled tight, the knot at the back and two short tails over the nape.
  HEADWEAR.durag = (r, o, P) => {
    const sway = P.mem ? spring(P.mem, 'durag', -P.motion * 0.5, 40, 6) : 0;
    ctx.fillStyle = tone(o.color, -0.15);
    [[-0.9, 0], [-0.78, 0.1]].forEach(([x, w]) => {
      ctx.beginPath();
      ctx.moveTo(r * x, -r * 0.4);
      ctx.quadraticCurveTo(r * (x - 0.25 - sway), -r * 0.1, r * (x - 0.32 - sway * 1.6), r * (0.3 + w));
      ctx.lineTo(r * (x - 0.1 - sway * 1.4), r * (0.3 + w));
      ctx.quadraticCurveTo(r * (x - 0.05 - sway * 0.6), -r * 0.05, r * (x + 0.2), -r * 0.4);
      ctx.closePath();
      ctx.fill();
    });
    ctx.fillStyle = o.color;
    ctx.beginPath();
    ctx.moveTo(r * 0.86, -r * 0.42);
    ctx.quadraticCurveTo(r * 0.86, -r * 1.2, -r * 0.08, -r * 1.16);
    ctx.quadraticCurveTo(-r * 0.98, -r * 1.08, -r * 0.94, -r * 0.18);
    ctx.lineTo(-r * 0.66, -r * 0.3);
    ctx.quadraticCurveTo(r * 0.1, -r * 0.62, r * 0.86, -r * 0.42);
    ctx.closePath();
    ctx.fill();
    if (P.small) return;
    // The seam down the middle and the satin sheen.
    polyline([[r * 0.5, -r * 1.02], [-r * 0.4, -r * 1.05], [-r * 0.9, -r * 0.5]], r * 0.04, tone(o.color, -0.25));
    polyline([[r * 0.62, -r * 0.82], [r * 0.1, -r * 1.02]], r * 0.07, 'rgba(255,255,255,0.28)');
    polyline([[r * 0.86, -r * 0.42], [r * 0.1, -r * 0.6], [-r * 0.66, -r * 0.3]], r * 0.05, tone(o.color, 0.18));
    circle(-r * 0.86, -r * 0.34, r * 0.13, tone(o.color, -0.1));
  };
  // Locs: ropes hanging from the crown to the shoulders, or past them with `len`, swinging as he moves.
  HAIR.locs = {
    back: (r, color, P) => {
      const sway = P.mem ? spring(P.mem, 'locs', -P.motion * 0.5, 36, 6) : 0;
      const long = P.spec.hair.len || 1;
      for (let k = 0; k < 7; k += 1) {
        const x = -r * (0.05 + k * 0.13);
        const len = r * (1.15 + (k % 3) * 0.16) * long;
        const tip = [x - r * (0.18 + k * 0.03) - sway * r * 0.6, len];
        polyline([[x, -r * 0.7], [x - r * 0.08 - sway * r * 0.2, r * 0.3], tip], r * (P.spec.hair.thin ? 0.12 : 0.17), k % 2 ? color : tone(color, -0.15));
        if (!P.small) circle(tip[0], tip[1], r * 0.1, tone(color, 0.15));
      }
    },
    front: (r, color, P) => {
      hairShape(r, 0.06, 0.02);
      ctx.fillStyle = color;
      ctx.fill();
      const sway = P.mem ? spring(P.mem, 'locsF', -P.motion * 0.4, 40, 6) : 0;
      const long = P.spec.hair.len || 1;
      const thick = P.spec.hair.thin ? 0.1 : 0.15;
      [[-0.2, 0.95], [-0.4, 1.05], [-0.6, 0.9]].forEach(([x, len], k) => {
        polyline([[r * x, -r * 0.6], [r * (x - 0.05) - sway * r * 0.15, r * 0.35], [r * (x - 0.12) - sway * r * 0.45, r * len * long]], r * thick, k % 2 ? tone(color, 0.1) : color);
        if (!P.small) polyline([[r * (x + 0.03), -r * 0.2], [r * (x - 0.02), r * 0.2]], r * 0.03, tone(color, 0.3));
      });
    }
  };
  // Short twists packed tight on top, each a little stack of curls, the sides faded low.
  HAIR.twists = {
    front: (r, color, P) => {
      hairShape(r, -0.02, 0.06);
      ctx.fillStyle = tone(color, 0.06);
      ctx.fill();
      for (let row = 0; row < 2; row += 1) {
        for (let k = 0; k < 8; k += 1) {
          const x = r * (0.62 - k * 0.19 - row * 0.09);
          const y = -r * (0.88 + row * 0.05 - Math.abs(k - 3) * 0.03);
          const n = 2 + ((k + row) % 2);
          const shade = (k + row) % 2 ? color : tone(color, 0.12);
          for (let i = 0; i < n; i += 1) circle(x - r * 0.02 * i * (k % 2 ? 1 : -1), y - r * 0.09 * i, r * (0.085 - i * 0.012), shade);
          if (!P.small) polyline([[x - r * 0.05, y - r * 0.02], [x + r * 0.04, y - r * 0.08]], r * 0.018, tone(color, 0.22));
        }
      }
    }
  };
  // Small oval shades, dark lenses in a thin silver rim.
  SPECS.oval = (r, P) => {
    [[0.2, 0.17], [0.6, 0.22]].forEach(([x, w]) => {
      ctx.beginPath();
      ctx.ellipse(r * x, -r * 0.03, r * w, r * 0.12, 0, 0, TAU);
      ctx.fillStyle = '#1e2126';
      ctx.fill();
      ctx.strokeStyle = '#c9ced3';
      ctx.lineWidth = Math.max(0.7, r * 0.035);
      ctx.stroke();
      if (!P.small) polyline([[r * (x - w * 0.4), -r * 0.08], [r * (x + w * 0.1), -r * 0.11]], r * 0.03, 'rgba(160,200,255,0.55)');
    });
    polyline([[r * 0.03, -r * 0.05], [-r * 0.55, -r * 0.02]], r * 0.035, '#c9ced3');
  };
  // Thin rectangular wire glasses in silver, the lenses smoked so the eyes only just show.
  SPECS.wire = (r, P) => {
    [[0.2, 0.3], [0.58, 0.36]].forEach(([x, w]) => {
      ctx.beginPath();
      ctx.rect(r * (x - w / 2), -r * 0.17, r * w, r * 0.28);
      ctx.fillStyle = 'rgba(14,16,22,0.84)';
      ctx.fill();
      ctx.strokeStyle = '#a4abb2';
      ctx.lineWidth = Math.max(0.6, r * 0.022);
      ctx.stroke();
      if (!P.small) polyline([[r * (x - w * 0.35), -r * 0.08], [r * (x - w * 0.05), -r * 0.11]], r * 0.03, 'rgba(255,255,255,0.45)');
    });
    if (!P.small) polyline([[r * 0.06, -r * 0.05], [-r * 0.55, -r * 0.02]], r * 0.025, '#a4abb2');
  };
  // A washed-out band tee, worn big, a faded circle print on the chest.
  TOP_STYLES.band = {
    sleeve: 0.42,
    fit: 1.3,
    body: (P, o) => {
      const { b, H } = P;
      const T = b.torso * H;
      const C = b.chest * H;
      torsoPath(b, H, 0.05, 0.015);
      ctx.fillStyle = o.color;
      ctx.fill();
      if (!P.small) {
        const print = tone(o.color, 0.42);
        ctx.save();
        torsoPath(b, H, 0.05, 0.015);
        ctx.clip();
        // The print: a faded ring with a figure in it, its edges worn away.
        ctx.strokeStyle = print;
        ctx.lineWidth = H * 0.008;
        ctx.beginPath();
        ctx.arc(C * 0.26, -T * 0.6, T * 0.2, 0, TAU);
        ctx.stroke();
        circle(C * 0.26, -T * 0.67, T * 0.065, print);
        ctx.fillStyle = print;
        ctx.beginPath();
        ctx.ellipse(C * 0.26, -T * 0.46, T * 0.13, T * 0.09, 0, Math.PI, TAU);
        ctx.fill();
        for (let k = 0; k < 14; k += 1) circle(C * (0.02 + ((k * 0.37) % 0.5)), -T * (0.4 + ((k * 0.53) % 0.42)), H * 0.004, o.color);
        ctx.restore();
      }
      torsoPath(b, H, 0.05, 0.015);
      shadeBack(b, H, 0.1);
      neckline(b, H, tone(o.color, -0.15), 0.03, 0.45);
      if (o.belt) rect(-b.hips * H * 1.1, H * 0.035, b.hips * H * 2.3, H * 0.018, o.belt);
    }
  };
  // A flat cap in grey herringbone tweed: the crown pulled flat and forward over a short stiff peak,
  // low over the brow, with the snap in front.
  HEADWEAR.flatcap = (r, o, P) => {
    const crown = () => {
      ctx.beginPath();
      ctx.moveTo(-r * 0.98, -r * 0.34);
      ctx.quadraticCurveTo(-r * 1.02, -r * 1.02, -r * 0.2, -r * 1.1);
      ctx.quadraticCurveTo(r * 0.8, -r * 1.12, r * 1.28, -r * 0.7);
      ctx.quadraticCurveTo(r * 1.44, -r * 0.56, r * 1.3, -r * 0.46);
      ctx.lineTo(r * 0.55, -r * 0.4);
      ctx.lineTo(-r * 0.98, -r * 0.34);
      ctx.closePath();
    };
    crown();
    ctx.fillStyle = o.color;
    ctx.fill();
    // The peak under the overhang, in shadow.
    poly([[r * 0.55, -r * 0.48], [r * 1.36, -r * 0.5], [r * 1.38, -r * 0.4], [r * 0.5, -r * 0.36]], tone(o.color, -0.32));
    if (P.small) return;
    ctx.save();
    crown();
    ctx.clip();
    // Herringbone: rows of little chevrons in a lighter and a darker thread.
    const step = r * 0.13;
    for (let y = -r * 1.12, row = 0; y < -r * 0.4; y += step * 0.75, row += 1) {
      for (let x = -r * 1.05; x < r * 1.4; x += step) {
        polyline([[x, y], [x + step * 0.5, y + step * 0.32 * (row % 2 ? 1 : -1)], [x + step, y]], r * 0.022, row % 2 ? tone(o.color, 0.14) : tone(o.color, -0.12));
      }
    }
    // The seam where the crown is pulled over the peak, and the snap.
    polyline([[-r * 0.2, -r * 1.06], [r * 0.7, -r * 0.92], [r * 1.22, -r * 0.62]], r * 0.04, tone(o.color, -0.28));
    circle(r * 1.08, -r * 0.66, r * 0.05, tone(o.color, -0.35));
    ctx.restore();
    polyline([[-r * 0.98, -r * 0.36], [r * 0.55, -r * 0.42]], r * 0.05, tone(o.color, -0.35));
  };
  // A full tattoo sleeve from shoulder to wrist: the skin shaded with ink, rows of scales over it.
  const tattooSleeve = (P, A, far) => {
    const { b, H } = P;
    const tint = far ? 'rgba(14,18,24,0.62)' : 'rgba(14,18,24,0.5)';
    [[A.sh, A.el, b.arm * H * 0.5, b.arm * H * 0.42, 0.18], [A.el, A.wr, b.arm * H * 0.42, b.wrist * H * 0.5, 0.05]].forEach(([p, q, r1, r2, start]) => {
      capsule(p.x, p.y, r1 * 0.88, q.x, q.y, r2 * 0.82, 'rgba(30,40,52,0.3)');
      if (P.small) return;
      const len = Math.hypot(q.x - p.x, q.y - p.y);
      inFrame(p.x, p.y, Math.atan2(q.y - p.y, q.x - p.x), () => {
        ctx.strokeStyle = tint;
        ctx.lineWidth = Math.max(0.6, H * 0.0035);
        for (let i = 0, x = len * start; x < len * 0.9; i += 1, x += r1 * 0.5) {
          const rr = lerp(r1, r2, x / len) * 0.75;
          for (let k = -1; k <= 1; k += 1) {
            const y = (k + (i % 2) * 0.5 - 0.25) * rr * 0.62;
            if (Math.abs(y) > rr * 0.8) continue;
            ctx.beginPath();
            ctx.arc(x, y, rr * 0.3, Math.PI * 0.6, Math.PI * 1.4);
            ctx.stroke();
          }
        }
      });
    });
  };
  // A slim steel watch on the wrist.
  const steelWatch = (P, A) => inFrame(A.wr.x, A.wr.y, A.fore + Math.PI / 2, () => {
    roundRect(-P.b.wrist * P.H * 0.62, -P.H * 0.012, P.b.wrist * P.H * 1.24, P.H * 0.024, P.H * 0.006, '#c9ced3');
    if (!P.small) roundRect(-P.b.wrist * P.H * 0.3, -P.H * 0.016, P.b.wrist * P.H * 0.6, P.H * 0.032, P.H * 0.008, '#1d2026');
  });
  // Alien goggles: a silver frame swept up into a point on the brow, round red lenses.
  SPECS.alien = (r, P) => {
    ctx.fillStyle = '#c9ced3';
    ctx.beginPath();
    ctx.moveTo(-r * 0.12, r * 0.02);
    ctx.quadraticCurveTo(-r * 0.05, -r * 0.3, r * 0.3, -r * 0.5);
    ctx.lineTo(r * 0.42, -r * 0.95);
    ctx.lineTo(r * 0.52, -r * 0.5);
    ctx.quadraticCurveTo(r * 0.95, -r * 0.35, r * 0.88, r * 0.05);
    ctx.quadraticCurveTo(r * 0.4, r * 0.3, -r * 0.12, r * 0.02);
    ctx.closePath();
    ctx.fill();
    [[0.17, 0.16], [0.6, 0.21]].forEach(([x, rr]) => {
      circle(r * x, -r * 0.04, r * rr, '#b5121e');
      if (!P.small) {
        circle(r * x, -r * 0.04, r * rr * 0.6, '#e2283a');
        circle(r * (x + 0.05), -r * 0.1, r * 0.05, 'rgba(255,255,255,0.75)');
      }
    });
    if (!P.small) {
      polyline([[r * 0.32, -r * 0.55], [r * 0.42, -r * 0.88]], r * 0.04, 'rgba(255,255,255,0.7)');
      [[0.05, -0.2], [0.82, -0.2], [0.38, 0.12]].forEach(([x, y]) => circle(r * x, r * y, r * 0.025, '#8d949b'));
    }
    polyline([[-r * 0.12, -r * 0.02], [-r * 0.6, 0]], r * 0.07, '#9aa1a8');
  };
  // Big square shades: thick black frames, a tortoise bar across the top.
  SPECS.square = (r, P) => {
    roundRect(-r * 0.1, -r * 0.22, r * 0.98, r * 0.36, r * 0.06, '#0b0b0c');
    rect(-r * 0.1, -r * 0.24, r * 0.98, r * 0.08, '#5a3518');
    if (!P.small) {
      rect(r * 0.08, -r * 0.14, r * 0.26, r * 0.22, '#1d1a1a');
      rect(r * 0.48, -r * 0.14, r * 0.34, r * 0.22, '#1d1a1a');
      polyline([[r * 0.55, -r * 0.1], [r * 0.7, -r * 0.1]], r * 0.03, 'rgba(255,255,255,0.4)');
    }
    polyline([[-r * 0.1, -r * 0.1], [-r * 0.6, -r * 0.06]], r * 0.07, '#0b0b0c');
  };
  FACIAL_HAIR.mustache = (r, color) => polyline([[r * 0.34, r * 0.43], [r * 0.52, r * 0.4], [r * 0.72, r * 0.42]], r * 0.06, color);
  Object.assign(HEADGEAR, {
    // A full silver grill, over the grin and over the open mouth alike.
    grill: (r, P) => {
      const open = clamp(P.pose.mouth, 0, 1);
      const mx = r * 0.5;
      const my = r * 0.56;
      if (open > 0.1) rect(mx - r * 0.13, my - r * (0.05 + open * 0.12), r * 0.26, r * 0.06, '#dfe4e8');
      else {
        ctx.fillStyle = '#dfe4e8';
        ctx.beginPath();
        ctx.moveTo(mx - r * 0.26, my - r * 0.05);
        ctx.quadraticCurveTo(mx, my + r * 0.1, mx + r * 0.27, my - r * 0.09);
        ctx.closePath();
        ctx.fill();
      }
      if (!P.small) circle(mx + r * 0.05, my - r * 0.02 - (open > 0.1 ? r * open * 0.12 : 0), r * 0.025, '#ffffff');
    }
  });
  // A short jacket: leather with a shine and a turtleneck under it, or a technical anorak with a cord.
  TOP_STYLES.jacket = {
    sleeve: 1,
    fit: 1.24,
    cuff: o => tone(o.color, -0.18),
    body: (P, o) => {
      const { b, H } = P;
      const T = b.torso * H;
      const C = b.chest * H;
      const SW = b.shoulder * H;
      torsoPath(b, H, 0.04, 0.015);
      ctx.fillStyle = o.color;
      ctx.fill();
      ctx.save();
      torsoPath(b, H, 0.04, 0.015);
      ctx.clip();
      // Open at the front over the shirt, or zipped to the collar.
      if (o.inner) poly([[C * 0.15, -T], [C * 0.62, -T], [C * 0.5, H * 0.05], [C * 0.22, H * 0.05]], o.inner);
      else polyline([[C * 0.38, -T], [C * 0.42, H * 0.05]], H * 0.007, tone(o.color, -0.3));
      if (!P.small) {
        if (o.shine) {
          polyline([[C * 0.7, -T * 0.92], [C * 0.85, -T * 0.4]], H * 0.012, 'rgba(255,255,255,0.22)');
          polyline([[-SW * 0.6, -T * 0.8], [-SW * 0.75, -T * 0.2]], H * 0.008, 'rgba(255,255,255,0.12)');
        }
        if (o.pockets) [-0.1, 0.58].forEach(x => rect(C * x, -T * 0.35, C * 0.32, H * 0.008, tone(o.color, -0.25)));
      }
      rect(-SW * 1.6, 0, SW * 3.4, H * 0.04, tone(o.color, -0.1));
      ctx.restore();
      torsoPath(b, H, 0.04, 0.015);
      shadeBack(b, H, 0.14);
      // The collar: leather points folded down, or a tall funnel with its cord hanging.
      if (o.lapels) {
        poly([[-SW * 0.35, -T - H * 0.01], [C * 0.2, -T * 0.72], [C * 0.12, -T - H * 0.01]], tone(o.color, 0.1));
        poly([[C * 0.62, -T - H * 0.005], [C * 0.92, -T * 0.7], [C * 0.75, -T - H * 0.005]], tone(o.color, 0.1));
      }
      if (o.cord && !P.small) {
        polyline([[C * 0.3, -T - H * 0.01], [C * 0.34, -T * 0.6]], H * 0.005, o.cord);
        circle(C * 0.34, -T * 0.6, H * 0.008, o.cord);
      }
    }
  };
  // A long silk tunic with a standing collar, closed on a curve to the armpit with knot buttons, and
  // panels to the knee, slit up the sides; a woven pattern in the silk catches the light.
  const silkPanel = (P, o, side) => {
    const { b, H } = P;
    const len = o.len || 0.3;
    const sway = P.mem ? spring(P.mem, side > 0 ? 'panelF' : 'panelB', P.motion * (side > 0 ? 0.35 : 0.7), 36, 6) : 0;
    const x0 = side > 0 ? -b.hips * H * 0.35 : -b.hips * H * 1.05;
    const x1 = side > 0 ? b.hips * H * 1.05 : b.hips * H * 0.3;
    ctx.fillStyle = side > 0 ? o.color : tone(o.color, -0.2);
    ctx.beginPath();
    ctx.moveTo(x0, 0);
    ctx.quadraticCurveTo(x0 - b.hips * H * sway, H * len * 0.5, x0 - b.hips * H * (0.1 + sway * 2), H * len);
    ctx.lineTo(x1 - b.hips * H * sway * 2, H * (len + 0.01));
    ctx.quadraticCurveTo(x1 - b.hips * H * sway, H * len * 0.5, x1, 0);
    ctx.closePath();
    ctx.fill();
    if (side > 0 && !P.small) polyline([[x0 + b.hips * H * 0.2, H * 0.02], [x0 + b.hips * H * (0.1 - sway * 2), H * (len - 0.01)]], H * 0.004, tone(o.color, 0.12));
  };
  TOP_STYLES.aodai = {
    sleeve: 1,
    fit: 1.18,
    cuff: o => tone(o.color, -0.12),
    back: (P, o) => inTorso(P.sk, () => silkPanel(P, o, -1)),
    skirt: (P, o) => silkPanel(P, o, 1),
    body: (P, o) => {
      const { b, H } = P;
      const T = b.torso * H;
      const C = b.chest * H;
      const SW = b.shoulder * H;
      torsoPath(b, H, 0.05, 0.01);
      ctx.fillStyle = o.color;
      ctx.fill();
      if (!P.small) {
        ctx.save();
        torsoPath(b, H, 0.05, 0.01);
        ctx.clip();
        const step = H * 0.045;
        for (let y = -T; y < H * 0.05; y += step) {
          for (let x = -SW * 1.2, k = 0; x < SW * 1.3; x += step, k += 1) {
            ctx.strokeStyle = tone(o.color, 0.06);
            ctx.lineWidth = Math.max(0.5, H * 0.0025);
            ctx.beginPath();
            ctx.arc(x + ((Math.round(y / step) % 2) * step) / 2, y, H * 0.008, 0, Math.PI * 1.5);
            ctx.stroke();
          }
        }
        const g = ctx.createLinearGradient(-SW, 0, C, 0);
        g.addColorStop(0, 'rgba(255,255,255,0)');
        g.addColorStop(0.62, 'rgba(255,255,255,0.12)');
        g.addColorStop(0.8, 'rgba(255,255,255,0)');
        ctx.fillStyle = g;
        ctx.fillRect(-SW * 1.3, -T * 1.2, SW * 2.8, T * 1.4);
        ctx.restore();
      }
      torsoPath(b, H, 0.05, 0.01);
      shadeBack(b, H, 0.14);
      // The closing: from the collar round across the chest to under the arm.
      polyline([[C * 0.42, -T - H * 0.005], [C * 0.62, -T * 0.88], [C * 0.92, -T * 0.66]], Math.max(1, H * 0.006), tone(o.color, -0.35));
      if (!P.small) [[0.5, -0.95], [0.7, -0.82], [0.88, -0.7]].forEach(([x, y]) => circle(C * x, T * y, H * 0.009, GOLD));
      // The standing collar.
      roundRect(-SW * 0.32, -T - H * 0.045, SW * 0.95, H * 0.05, H * 0.012, tone(o.color, -0.1));
      rect(-SW * 0.32, -T - H * 0.045, SW * 0.95, H * 0.006, tone(o.color, 0.18));
    }
  };
  // A retro basketball sneaker: a white mid-top, cement-grey speckled wings and heel, the mesh
  // window, red on the tongue and the eyelets, and the visible air bubble in the heel.
  SHOES.retro = (P, o, far, len, h) => {
    const t = c => (far ? tone(c, -0.18) : c);
    roundRect(-len * 0.3, -h * 1.3, len * 0.62, h * 2, h * 0.4, t(o.color));
    roundRect(-len * 0.3, -h * 0.55, len, h * 1.4, h * 0.55, t(o.color));
    roundRect(-len * 0.32, h * 0.45, len * 1.03, h * 0.55, h * 0.22, t(o.sole || PAPER));
    if (P.small) return;
    const speckled = (x, y, w, hh) => {
      roundRect(x, y, w, hh, h * 0.2, t(o.wing));
      for (let k = 0; k < 6; k += 1) circle(x + w * ((k * 0.37) % 1), y + hh * ((k * 0.61) % 1), h * 0.05, t(INK));
    };
    speckled(-len * 0.31, -h * 0.35, len * 0.3, h * 0.82);
    speckled(len * 0.48, -h * 0.05, len * 0.22, h * 0.5);
    rect(len * 0.08, -h * 0.4, len * 0.26, h * 0.4, t('#d9dadc'));
    for (let k = 1; k < 4; k += 1) rect(len * (0.08 + k * 0.065), -h * 0.4, Math.max(0.5, h * 0.04), h * 0.4, t('rgba(20,20,21,0.25)'));
    poly([[len * 0.02, -h * 0.75], [-len * 0.16, -h * 1.25], [-len * 0.02, -h * 1.2], [len * 0.12, -h * 0.55]], t(o.wing));
    rect(-len * 0.06, -h * 1.5, len * 0.14, h * 0.32, t(o.accent));
    [0.02, 0.16, 0.3].forEach(x => circle(len * x, -h * (0.62 - x), h * 0.07, t(o.accent)));
    roundRect(-len * 0.24, h * 0.55, len * 0.2, h * 0.3, h * 0.1, 'rgba(205,220,232,0.95)');
    rect(-len * 0.32, h * 0.45, len * 1.03, h * 0.08, t(o.accent));
  };
  const RUBBER = '#ffd23c';
  // Retro's bandolier: a strap over the front shoulder, across the chest to the back hip, with cluster grenades
  // clipped on in a row down it, clear of the arm hanging in front.
  GEAR.grenades = P => {
    const { b, H } = P;
    const T = b.torso * H;
    const from = { x: b.chest * H * 0.45, y: -T - H * 0.01 };
    const to = { x: -(b.hips + b.belly * 0.25) * H * 0.95, y: -H * 0.02 };
    const a = Math.atan2(to.y - from.y, to.x - from.x);
    ctx.save();
    torsoPath(b, H, 0.05, 0.025);
    ctx.clip();
    polyline([[from.x, from.y], [to.x, to.y]], H * 0.03, '#6b4f2e');
    if (!P.small) polyline([[from.x, from.y], [to.x, to.y]], H * 0.006, 'rgba(255,255,255,0.14)');
    ctx.restore();
    [0.3, 0.48, 0.66, 0.84].forEach(q => {
      const x = lerp(from.x, to.x, q);
      const y = lerp(from.y, to.y, q);
      grenade(x, y + H * 0.03, H * 0.06, a - Math.PI / 2 + 0.25);
    });
  };
  // A dad cap in monogram canvas: a soft crown in beige, the green-red-green web round the band and a
  // curved peak.
  HEADWEAR.dadcap = (r, o, P) => {
    const crown = () => {
      ctx.beginPath();
      ctx.moveTo(r * 0.94, -r * 0.4);
      ctx.quadraticCurveTo(r * 0.92, -r * 1.2, -r * 0.06, -r * 1.19);
      ctx.quadraticCurveTo(-r * 0.98, -r * 1.12, -r * 0.97, -r * 0.32);
      ctx.closePath();
    };
    crown();
    ctx.fillStyle = o.color;
    ctx.fill();
    if (!P.small) {
      ctx.save();
      crown();
      ctx.clip();
      // The monogram: rows of little interlocked rings in ebony, staggered.
      ctx.strokeStyle = tone(o.color, -0.42);
      ctx.lineWidth = Math.max(0.6, r * 0.035);
      for (let y = -r * 1.2, row = 0; y < -r * 0.35; y += r * 0.2, row += 1) {
        for (let x = -r * 1.05 + (row % 2) * r * 0.16; x < r; x += r * 0.32) {
          [-1, 1].forEach(side => {
            ctx.beginPath();
            ctx.arc(x + side * r * 0.035, y, r * 0.055, 0, TAU);
            ctx.stroke();
          });
        }
      }
      // The panel seams.
      polyline([[-r * 0.06, -r * 1.18], [r * 0.4, -r * 0.42]], r * 0.03, tone(o.color, -0.28));
      polyline([[-r * 0.06, -r * 1.18], [-r * 0.5, -r * 0.38]], r * 0.03, tone(o.color, -0.28));
      ctx.restore();
    }
    rect(-r * 0.97, -r * 0.46, r * 1.91, r * 0.15, '#1d6b3c');
    rect(-r * 0.97, -r * 0.41, r * 1.91, r * 0.05, '#c8282f');
    // The peak, pre-curved and shading the eyes.
    ctx.fillStyle = tone(o.color, -0.16);
    ctx.beginPath();
    ctx.moveTo(r * 0.6, -r * 0.46);
    ctx.quadraticCurveTo(r * 1.32, -r * 0.52, r * 1.56, -r * 0.3);
    ctx.quadraticCurveTo(r * 1.22, -r * 0.22, r * 0.6, -r * 0.3);
    ctx.closePath();
    ctx.fill();
    circle(-r * 0.06, -r * 1.18, r * 0.07, tone(o.color, -0.25));
  };
  // A scythe: a long black shaft and a curved blade that glows (`glow`, r,g,b).
  PROPS.scythe = (P, o) => {
    const H = P.H;
    const len = (o.len || 0.9) * H;
    roundRect(-len * 0.3, -H * 0.014, len * 1.3, H * 0.028, H * 0.012, '#1c1a20');
    if (!P.small) [-0.1, 0.35].forEach(q => rect(len * q, -H * 0.018, H * 0.03, H * 0.036, '#6a6470'));
    const blade = () => {
      ctx.beginPath();
      ctx.moveTo(len * 0.98, -H * 0.02);
      ctx.quadraticCurveTo(len * 1.0, len * 0.42, len * 0.42, len * 0.56);
      ctx.quadraticCurveTo(len * 0.82, len * 0.32, len * 0.86, H * 0.02);
      ctx.closePath();
    };
    if (o.glow && !P.small) {
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.shadowColor = `rgba(${o.glow},0.9)`;
      ctx.shadowBlur = H * 0.06;
      blade();
      ctx.fillStyle = `rgba(${o.glow},0.35)`;
      ctx.fill();
      ctx.restore();
    }
    blade();
    ctx.fillStyle = o.blade || '#cfd6dc';
    ctx.fill();
    if (o.glow) {
      ctx.beginPath();
      ctx.moveTo(len * 0.97, H * 0.01);
      ctx.quadraticCurveTo(len * 0.97, len * 0.4, len * 0.42, len * 0.56);
      ctx.strokeStyle = `rgb(${o.glow})`;
      ctx.lineWidth = Math.max(0.8, H * 0.012);
      ctx.stroke();
    }
    return len;
  };
  // A huge baseball bat in dark stained wood: the knob, the taped handle, a fat barrel wrapped in barbed wire and
  // driven through with long nails, points out on both sides.
  const NAILS = Array.from({ length: 12 }, (_, k) => [0.45 + k * 0.045, k % 2 ? 1 : -1, Math.sin(k * 2.3) * 0.35]);
  PROPS.nailbat = (P, o) => {
    const H = P.H;
    const len = (o.len || 0.6) * H;
    const thin = H * 0.015;
    const fat = H * 0.046;
    const end = -H * 0.07;
    ctx.fillStyle = '#6e4a2c';
    ctx.beginPath();
    ctx.moveTo(end, -thin);
    ctx.lineTo(len * 0.3, -thin);
    ctx.quadraticCurveTo(len * 0.52, -fat, len * 0.7, -fat);
    ctx.lineTo(len - fat, -fat);
    ctx.arc(len - fat, 0, fat, -Math.PI / 2, Math.PI / 2);
    ctx.lineTo(len * 0.7, fat);
    ctx.quadraticCurveTo(len * 0.52, fat, len * 0.3, thin);
    ctx.lineTo(end, thin);
    ctx.closePath();
    ctx.fill();
    roundRect(end - H * 0.014, -thin * 1.8, H * 0.018, thin * 3.6, H * 0.007, '#4e331e');
    if (!P.small) {
      polyline([[len * 0.38, -thin * 0.5], [len * 0.95, -fat * 0.45]], H * 0.005, 'rgba(255,225,180,0.35)');
      polyline([[len * 0.48, fat * 0.45], [len * 0.9, fat * 0.6]], H * 0.004, 'rgba(40,22,10,0.45)');
      rect(end, -thin * 1.1, len * 0.24 - end, thin * 2.2, '#1f1f22');
      for (let k = 1; k < 6; k += 1) rect(end + k * H * 0.025, -thin * 1.1, H * 0.003, thin * 2.2, 'rgba(255,255,255,0.18)');
    }
    // The barbed wire: wound round the barrel, a barb at every crossing.
    const wire = [];
    for (let k = 0; k <= 9; k += 1) wire.push([len * (0.58 + k * 0.04), (k % 2 ? 1 : -1) * fat * 0.95]);
    polyline(wire, Math.max(0.8, H * 0.0045), '#5d636a');
    if (!P.small) wire.forEach(([x], k) => k && polyline([[lerp(wire[k - 1][0], x, 0.5) - H * 0.006, -H * 0.006], [lerp(wire[k - 1][0], x, 0.5) + H * 0.006, H * 0.006]], H * 0.0035, '#5d636a'));
    // The nails: in through the barrel, the points standing out on both sides.
    NAILS.forEach(([q, side, tilt]) => {
      const x = len * q;
      const rr = q > 0.7 ? fat : lerp(thin, fat, clamp((q - 0.3) / 0.4, 0, 1));
      const out = H * (0.05 + (q % 0.1) * 0.12);
      polyline([[x, side * rr * 0.4], [x + Math.sin(tilt) * out, side * (rr + out)]], Math.max(0.9, H * 0.007), '#c3c8ce');
      if (!P.small) circle(x, side * rr * 0.55, H * 0.007, '#7b8086');
    });
    return len;
  };
  // A big steel watch: the jubilee bracelet, the fluted bezel and a dark dial.
  const bigWatch = (P, A) => inFrame(A.wr.x, A.wr.y, A.fore + Math.PI / 2, () => {
    const w = P.b.wrist * P.H;
    const H = P.H;
    roundRect(-w * 0.66, -H * 0.014, w * 1.32, H * 0.028, H * 0.008, '#cfd4d9');
    if (P.small) return;
    for (let k = -2; k <= 2; k += 1) rect(k * w * 0.24 - H * 0.002, -H * 0.014, H * 0.004, H * 0.028, '#9aa1a8');
    roundRect(-w * 0.46, -H * 0.026, w * 0.92, H * 0.052, H * 0.014, '#dfe3e7');
    roundRect(-w * 0.34, -H * 0.018, w * 0.68, H * 0.036, H * 0.01, '#16202e');
    circle(-w * 0.12, -H * 0.008, H * 0.004, 'rgba(255,255,255,0.7)');
  });
  // Silver bracelets stacked loose on the wrist.
  const bracelets = (P, A) => inFrame(A.wr.x, A.wr.y, A.fore + Math.PI / 2, () => {
    const w = P.b.wrist * P.H;
    [-0.012, 0.006].forEach(y => roundRect(-w * 0.62, P.H * y, w * 1.24, P.H * 0.009, P.H * 0.004, '#d9dee3'));
  });
  // A diamond, `r` from its middle to a point: a white stone with an ice-blue facet and a glint.
  const diamond = (x, y, r) => {
    poly([[x, y - r], [x + r, y], [x, y + r], [x - r, y]], '#eef8ff');
    poly([[x, y - r], [x + r, y], [x, y]], '#9fd8ff');
    circle(x - r * 0.3, y - r * 0.3, r * 0.2, '#ffffff');
  };
  // A heavy gold knuckle duster over the fist, a diamond in each of its four rings.
  HANDS.dusters = (P, col, s) => {
    HANDS.fist(P, col, s);
    roundRect(s * 0.52, -s * 0.8, s * 0.44, s * 1.6, s * 0.2, GOLD);
    if (P.small) return;
    roundRect(s * 0.6, -s * 0.72, s * 0.1, s * 1.44, s * 0.05, '#ffe28a');
    for (let k = 0; k < 4; k += 1) diamond(s * 0.8, -s * 0.54 + k * s * 0.36, s * 0.13);
  };
  // A thick curb link chain in gold, link by link round the neck.
  GEAR.curb = P => {
    const { b, H } = P;
    const T = b.torso * H;
    const p0 = { x: -b.shoulder * H * 0.15, y: -T - H * 0.01 };
    const c = { x: b.chest * H * 0.22, y: -T * 0.4 };
    const p1 = { x: b.chest * H * 0.64, y: -T - H * 0.005 };
    const at = q => ({ x: (1 - q) ** 2 * p0.x + 2 * (1 - q) * q * c.x + q * q * p1.x, y: (1 - q) ** 2 * p0.y + 2 * (1 - q) * q * c.y + q * q * p1.y });
    if (P.small) {
      polyline([0, 0.25, 0.5, 0.75, 1].map(q => [at(q).x, at(q).y]), H * 0.024, GOLD);
      return;
    }
    for (let k = 0; k <= 12; k += 1) {
      const a = at(k / 12);
      const n = at(Math.min(1, k / 12 + 0.02));
      oval(a.x, a.y, H * 0.021, H * 0.014, k % 2 ? GOLD : '#c48f26', Math.atan2(n.y - a.y, n.x - a.x) + (k % 2 ? 0.5 : -0.5));
      circle(a.x - H * 0.004, a.y - H * 0.004, H * 0.004, 'rgba(255,250,220,0.8)');
    }
  };
  // Johno's two blows. The uppercut drops low and drives up through the chin; the flying kick leaves the
  // ground with the knee tucked and lands the front foot at head height.
  const JOHNO_UPPERCUT = { keys: [[-0.24, { aF: 0.15, eF: 1.5, lean: 0.25, y: 0.07, x: -0.01, head: 0.15, brow: -1 }], [0, { aF: 2.55, eF: 0.9, lean: -0.15, y: -0.05, fBy: -0.02, tB: 0.6, mouth: 0.2, brow: -1 }, 'in'], [0.2, { aF: 2.65, eF: 0.75, y: -0.04, lean: -0.12 }], [0.5, {}, 'out']] };
  const JOHNO_FLYKICK = { keys: [[-0.3, { y: 0.06, lean: 0.2, fFx: 0.06, fBx: -0.08, aF: 0.6, eF: 2, aB: 0.4, eB: 2.1, brow: -1 }], [-0.12, { y: -0.2, lean: -0.05, fFx: 0.14, fFy: -0.36, tF: 0.3, fBx: -0.04, fBy: -0.3, tB: 0.4, aF: 1.4, eF: 1.6, aB: 0.2, eB: 1.8 }, 'out'], [0, { y: -0.24, lean: -0.38, x: 0.04, fFx: 0.52, fFy: -0.56, tF: -0.9, fBx: -0.04, fBy: -0.34, tB: 0.6, aF: 1.6, eF: 0.8, aB: -0.9, eB: 0.5, mouth: 0.8, brow: -1 }, 'in'], [0.16, { y: -0.18, lean: -0.32, x: 0.04, fFx: 0.48, fFy: -0.46, tF: -0.8, fBx: -0.05, fBy: -0.26, tB: 0.5, aF: 1.5, eF: 0.9, aB: -0.8, eB: 0.5 }], [0.42, { y: 0.05, lean: 0.12, sq: 0.94 }, 'in'], [0.62, {}, 'out']] };
  const POLARISE_STANCE = { aF: -0.45, eF: 2.85, grip: 0.3, both: 1, aB: 0.3, eB: 1.6, lean: -0.04, y: 0.03, fFx: 0.1, fBx: -0.1, head: 0.05, brow: -0.3 };
  const HOMERUN_BASE = { ...POLARISE_STANCE, x: 0, fFy: 0, tB: 0, mouth: 0, propLen: 1 };
  const HOMERUN = { keys: [[0, {}], [0.3, { aF: -0.8, eF: 2.9, grip: -0.25, lean: -0.2, x: -0.03, y: 0.01, fFx: 0.05, fFy: -0.07, brow: -0.8 }, 'io'], [0.45, { aF: -0.82, eF: 2.9, grip: -0.28, lean: -0.21, x: -0.03, y: 0.01, fFx: 0.05, fFy: -0.08, brow: -1 }], [0.55, { aF: -0.3, eF: 2.4, grip: -0.75, lean: -0.08, x: -0.01, fFx: 0.17, fFy: 0, brow: -1 }, 'in'], [0.61, { aF: 0.7, eF: 1.2, grip: 0.55, propLen: 0.5, lean: 0.08, x: 0.02, fFx: 0.17, tB: 0.2 }, 'in'], [0.65, { aF: 1.35, eF: 0.1, grip: 1.6, propLen: 1, lean: 0.18, x: 0.04, fFx: 0.17, tB: 0.4, mouth: 0.8, brow: -1 }], [0.71, { aF: 1.75, eF: 0.8, grip: 4.0, propLen: 0.5, lean: 0.14, x: 0.04, fFx: 0.17, tB: 0.5, mouth: 0.8 }], [0.8, { aF: 1.95, eF: 1.6, grip: 6.48, propLen: 1, lean: 0.08, x: 0.04, fFx: 0.17, tB: 0.5, mouth: 0.6 }, 'out'], [1.1, { aF: 2.0, eF: 1.65, grip: 6.45, lean: 0.06, x: 0.04, fFx: 0.17, tB: 0.5 }], [1.3, { grip: 0.3 + Math.PI * 2 }, 'io']] };
  const HERO_SPECS = {
    // Caruso: a wiry speedster with short bleached-blond hair, small oval shades, a red leather jacket zipped up
    // and a silver chain round his wrist; his arms stretch like springs.
    caruso: {
      build: 'lean', body: { arm: 0.1, wrist: 0.085, hand: 0.085 }, skin: '#efc8a4', jaw: 'soft', face: 'smirk',
      hair: { style: 'brush', color: '#e9cf7a' },
      specs: 'oval',
      top: { style: 'jacket', color: '#c9342b', shine: true, lapels: true },
      bottom: { style: 'jeans', color: '#22242a' },
      shoes: { style: 'sneaker', color: PAPER, accent: '#c9342b', sole: PAPER },
      hands: 'fist',
      // A silver chain round the front wrist.
      armFx: (P, A, far) => {
        if (far) return;
        const q = midPoint(A.el, A.wr, 0.85);
        inFrame(q.x, q.y, A.fore + Math.PI / 2, () => {
          roundRect(-P.b.wrist * P.H * 0.6, -P.H * 0.008, P.b.wrist * P.H * 1.2, P.H * 0.016, P.H * 0.006, '#d7dbe0');
          if (!P.small) for (let k = -1; k <= 1; k += 1) circle(k * P.b.wrist * P.H * 0.35, 0, P.H * 0.005, '#8d949b');
        });
      },
      gait: { stride: 1.25, lift: 1.2, lean: 1.4, run: 0.8 },
      guard: { aF: 1.0, eF: 1.9, aB: 0.75, eB: 2.0, lean: 0.12, y: 0.025, fFx: 0.08, fBx: -0.075, tB: 0.2, brow: -0.3 },
      attack: aim => ({ keys: [[-0.22, { aF: -0.5, eF: 2.0, lean: -0.12, x: -0.02, brow: -0.6 }], [0, { aF: aim, eF: 0.05, lean: 0.18, x: 0.03, mouth: 0.7, brow: -0.8 }, 'in'], [0.22, { aF: aim, eF: 0.05, lean: 0.15 }], [0.5, {}, 'out']] }),
      // Spinning: both arms stretched out wide.
      spin: { aF: 1.6, eF: 0.05, aB: -1.5, eB: 0.05, reach: 1.7, lean: 0, mouth: 0.8, brow: -1 },
      // Gummi-Tornado: spinning first, then both fists drawn back for the big double punch.
      special: t => (t < 1.1 ? {} : { aF: -0.4, eF: 1.9, aB: -0.3, eB: 1.9, lean: -0.1, mouth: 1, brow: -1 }),
      idle: { every: 5.5, clip: { keys: [[0, {}], [0.3, { aF: 2.9, eF: 0.1, reach: 1.9, lean: -0.08, mouth: 0.4 }, 'out'], [0.7, { aF: 2.9, eF: 0.1, reach: 2.4, lean: -0.1, eyes: 1 }], [1.05, { aF: 2.8, reach: 1 }, 'back'], [1.4, {}]] } }
    },
    // Retro: a stocky rapper in retro sneakers. Long thin locs, big square shades, a thick beard and
    // a cigarette, a hooded puffer printed with gold leaves, a silver curb chain, a bandolier of cluster grenades,
    // black denim over white sneakers.
    retro: {
      build: 'heavy', body: { belly: 0.022, waist: 0.145, hips: 0.135 },
      skin: '#5a3624', jaw: 'square', face: 'flat',
      hair: { style: 'locs', color: '#1a1410', len: 1.45, thin: true },
      beard: { style: 'full', color: '#120e0b' },
      specs: 'square',
      headgear: ['cigarette'],
      top: { style: 'puffer', color: '#26291f', print: ['#a8862c', '#7a4a22', '#4f6a3a'], hood: true },
      bottom: { style: 'jeans', color: '#1c1d21' },
      shoes: { style: 'retro', color: '#f7f6f2', wing: '#a9abaf', accent: '#d6182f', sole: '#f7f6f2' },
      gear: [{ kind: 'chain', color: '#d7dbe0', heavy: true }, { kind: 'grenades' }],
      gait: { stride: 0.9, bob: 1.6, swing: 0.7, lean: 0.6 },
      guard: { aF: 0.7, eF: 1.7, aB: 0.5, eB: 1.8, lean: -0.04, y: 0.03, fFx: 0.09, fBx: -0.08, brow: -0.4 },
      // The throw: the arm swings back and down, then over the top, and the grenade leaves at the high point.
      attack: () => ({ keys: [[-0.3, { lean: -0.18, aF: -0.8, eF: 1.4, aB: 0.9, eB: 1.2, brow: -0.6 }], [0, { lean: 0.16, x: 0.03, aF: 2.3, eF: 0.25, aB: 0.2, eB: 1.4, mouth: 0.8, brow: -1 }, 'in'], [0.15, { lean: 0.12, x: 0.02, aF: 1.6, eF: 0.3, aB: 0.3, eB: 1.4 }], [0.5, {}, 'out']] }),
      special: t => {
        const up = t < 0.55 ? Math.sin((t / 0.55) * Math.PI * 0.5) : Math.max(0, 1 - (t - 0.55) / 0.12);
        const land = t > 0.6 ? Math.max(0, 1 - (t - 0.6) / 0.5) : 0;
        return { y: -up * 0.1 + land * 0.06, aF: 2.9 - land * 2, eF: 0.3, aB: 2.8 - land * 2, eB: 0.3, sq: 1 + up * 0.06 - land * 0.12, lean: -0.1 + land * 0.3, fFy: -up * 0.06, fBy: -up * 0.08, mouth: 1, brow: -1 };
      },
      // At rest: nodding to a beat in his head, then a slow drag on the cigarette and the head back to blow it out.
      idle: { every: 6, clip: { keys: [[0, {}], [0.25, { head: 0.1, y: 0.01, sq: 0.98 }, 'io'], [0.5, { head: -0.04 }, 'io'], [0.75, { head: 0.1, y: 0.01, sq: 0.98 }, 'io'], [1, { head: -0.04 }, 'io'], [1.4, { aF: 0.9, eF: 2.7, head: -0.06 }, 'io'], [2.1, { aF: 0.9, eF: 2.7, head: -0.08, eyes: 0.6 }], [2.5, { aF: 0.6, eF: 1.5, head: -0.16, mouth: 0.3 }, 'io'], [3.1, {}, 'io']] } }
    },
    // Zola: well over two metres, long-legged and broad in the shoulders. Short twists, thin glasses with
    // smoked lenses, a washed-out grey band tee, wide grey trousers on a black belt and black shoes. He fights
    // with his feet: the straight front kick that sends a Bootleg down the street.
    zola: {
      build: 'lean', body: { head: 0.165, torso: 0.28, upper: 0.165, fore: 0.155, thigh: 0.24, shin: 0.23, shoulder: 0.145, chest: 0.125, waist: 0.092, hips: 0.1, arm: 0.062, wrist: 0.047, leg: 0.078, calf: 0.062, stance: 0.06 },
      skin: '#3e2416', jaw: 'square', face: 'flat',
      hair: { style: 'twists', color: '#120e0b' },
      specs: 'wire',
      top: { style: 'band', color: '#55575c', belt: '#111113' },
      bottom: { style: 'wide', color: '#6a6862' },
      shoes: { style: 'dress', color: '#121214' },
      hands: 'fist',
      gait: { stride: 1.05, bob: 0.8, swing: 0.5, lean: 0.4 },
      rest: { aF: -0.04, eF: 0.22, aB: 0.02, eB: 0.22, head: -0.04 },
      guard: { aF: 0.75, eF: 1.95, aB: 0.55, eB: 2.05, lean: -0.03, y: 0.01, fFx: 0.11, fBx: -0.1, head: -0.06, brow: -0.7 },
      // The kick: the knee comes up, the leg shoots out straight with the sole first, and he leans back into it.
      attack: () => ({ keys: [[-0.32, { fFx: 0.13, fFy: -0.24, tF: 0.4, lean: -0.08, aF: 0.9, eF: 1.9, aB: 0.6, eB: 2.0, brow: -1 }], [0, { fFx: 0.5, fFy: -0.36, tF: -1.25, lean: -0.34, x: 0.03, aF: 1.3, eF: 1.3, aB: -0.5, eB: 0.6, fBx: -0.12, mouth: 0.6, brow: -1 }, 'in'], [0.2, { fFx: 0.48, fFy: -0.34, tF: -1.2, lean: -0.32, x: 0.03, aF: 1.25, eF: 1.35, aB: -0.45, eB: 0.6, fBx: -0.12 }], [0.6, {}, 'out']] }),
      // This is Sparta: a run-up step, the knee chambered high, then the kick with everything behind it.
      special: t => {
        const load = clamp(t / 0.32, 0, 1);
        const hit = clamp((t - 0.32) / 0.08, 0, 1);
        const settle = clamp((t - 0.85) / 0.4, 0, 1);
        const k = hit * (1 - settle);
        return { fFx: lerp(lerp(0.06, 0.16, load), 0.56, k), fFy: lerp(lerp(0, -0.3, load), -0.42, k) * (1 - settle), tF: lerp(lerp(0, 0.5, load), -1.3, k) * (1 - settle), fBx: lerp(-0.06, -0.14, k), lean: lerp(lerp(0, -0.12, load), -0.4, k) * (1 - settle), x: k * 0.05, y: load * 0.02 * (1 - hit), aF: lerp(lerp(0.6, 1.0, load), 1.5, k), eF: lerp(1.9, 0.9, k), aB: lerp(0.4, -0.9, k), eB: lerp(2, 0.4, k), mouth: k > 0.3 ? 1 : 0, brow: -1 };
      },
      // At home: arms hanging loose at his sides, now and then a slow look along the street.
      idle: { every: 7, clip: { keys: [[0, {}], [0.6, { head: 0.12, look: 0.6 }, 'io'], [2.2, { head: 0.12, look: 0.6 }], [2.8, {}, 'io']] } }
    },
    // 2lade: silver alien goggles with red lenses, a full silver grill, a thin moustache and silver studs,
    // a mustard anorak zipped up to the funnel collar, and a silver blade.
    blade: {
      build: 'athletic', skin: '#3b2316', jaw: 'square', face: 'grin',
      hair: { style: 'buzz', color: '#0d0b0a' },
      beard: { style: 'mustache', color: '#120d0a' },
      specs: 'alien',
      headgear: ['grill', { kind: 'earring', color: '#d7dbe0' }],
      top: { style: 'jacket', color: '#b07a2c', pockets: true, cord: '#efe7d2' },
      collar: { color: '#9c6a22', wide: 1.7 },
      bottom: { style: 'cargo', color: '#26272b' },
      shoes: { style: 'boot', color: '#0f0f11', sole: '#050505' },
      hands: 'glove',
      props: { F: { kind: 'sword', len: 0.72 } },
      gait: { stride: 1.05, swing: 0.6 },
      rest: { aF: 0.25, eF: 0.15, grip: 1.2 },
      guard: { aF: 0.7, eF: 0.55, aB: 0.3, eB: 1.2, lean: 0.08, y: 0.02, fFx: 0.09, fBx: -0.08, grip: 0.2, brow: -0.5 },
      // Three cuts in one flow: down across, back up the other way, then the arm shoots out straight in a thrust.
      attack: () => ({ keys: [[-0.24, { aF: 2.7, eF: 1.1, grip: 1.5, lean: -0.12, brow: -0.8 }], [0, { aF: 0.55, eF: 0.1, grip: 1.45, lean: 0.28, x: 0.04, mouth: 0.3, brow: -1 }, 'in'], [0.07, { aF: 0.2, eF: 1.9, grip: -0.35, lean: 0.15, x: 0.04, brow: -1 }, 'io'], [0.15, { aF: 1.7, eF: 0.15, grip: 1.6, lean: 0.25, x: 0.05, brow: -1 }, 'in'], [0.24, { aF: 0.9, eF: 2.0, grip: 2.85, lean: -0.05, x: 0.02, brow: -1 }, 'io'], [0.32, { aF: 1.55, eF: 0, grip: 1.9, lean: 0.35, x: 0.08, fFx: 0.17, mouth: 0.4, brow: -1 }, 'in'], [0.46, { aF: 1.55, eF: 0, grip: 1.9, lean: 0.33, x: 0.08, fFx: 0.17 }], [0.8, {}, 'io']] }),
      special: t => {
        const up = clamp(t / 0.35, 0, 1);
        return { aF: lerp(0.7, 3.05, up), eF: 0.1, grip: lerp(0.2, 0.0, up), aB: lerp(0.3, 2.6, up), eB: 0.6, lean: -0.08 * up, y: 0.02, mouth: up > 0.9 ? 0.6 : 0, brow: -1 };
      },
      idle: { every: 7, clip: { keys: [[0, {}], [0.25, { aF: 1.5, eF: 0.3, grip: 0 }], [0.75, { aF: 1.5, eF: 0.3, grip: Math.PI * 2 }, 'io'], [1.0, {}]] } }
    },
    // Prettyfacecapi: the prettiest face on the block, clean-shaven under a black bucket hat. A black puffer
    // over a black tee, a gold curb chain, a silver stud, baggy denim, wheat boots, and a scythe glowing purple.
    capi: {
      build: 'athletic', body: { shoulder: 0.145, chest: 0.135, arm: 0.07, wrist: 0.052 }, skin: '#3d2417', jaw: 'soft', face: 'flat',
      hair: { style: 'buzz', color: '#0f0c0b' },
      hat: { style: 'bucket', color: '#161618' },
      headgear: [{ kind: 'earring', color: '#d7dbe0' }],
      top: { style: 'puffer', color: '#1c1d20' },
      bottom: { style: 'jeans', color: '#1f2a3a' },
      shoes: { style: 'boot', color: '#c98a3a' },
      gear: [{ kind: 'chain', color: GOLD, heavy: true }],
      props: { F: { kind: 'scythe', len: 0.85, glow: '178,90,255', blade: '#cf9dff' } },
      gait: { stride: 1.05, lean: 1.1, swing: 0.7 },
      rest: { aF: 0.3, eF: 2.0, grip: 0.3, aB: -0.05, eB: 0.4 },
      guard: { aF: 1.0, eF: 0.8, grip: 1.0, both: 1, aB: 0.9, eB: 1.5, lean: 0.12, y: 0.035, fFx: 0.1, fBx: -0.09, brow: -0.5 },
      // The turn: the scythe drawn back over the shoulder, then all the way round him.
      attack: () => ({ keys: [[-0.25, { aF: 2.4, eF: 0.6, grip: 0.9, both: 0, lean: -0.15, brow: -0.8 }], [0, { aF: 1.5, eF: 0.1, grip: 1.5, both: 0, lean: 0.2, x: 0.03, mouth: 0.4, brow: -1 }, 'in'], [0.16, { aF: 1.5, eF: 0.1, grip: 1.5 + Math.PI * 2, both: 0, lean: 0.15, x: 0.03 }], [0.5, {}, 'out']] }),
      // Sensensturm: the arm out and the scythe turning round and round.
      special: t => ({ aF: 1.55, eF: 0.1, grip: 1.5 + t * 24, both: 0, aB: 1.2, eB: 0.8, lean: 0.15, mouth: 0.6, brow: -1 }),
      // At home: the scythe twirled once round the hand.
      idle: { every: 7, clip: { keys: [[0, {}], [0.25, { aF: 1.5, eF: 0.3, grip: 0.3 }], [0.85, { aF: 1.5, eF: 0.3, grip: 0.3 + Math.PI * 2 }, 'io'], [1.15, {}]] } }
    },
    // Johno: a white ribbed tank top over a boxer's arms, a flat cap in grey tweed low over the eyes, a short beard, black
    // slacks and boots, wrapped fists and a cigarette always lit. Uppercuts and flying kicks, turn about.
    johno: {
      build: 'athletic', body: { shoulder: 0.155, chest: 0.14, waist: 0.1, arm: 0.078, wrist: 0.056, upper: 0.16 }, skin: '#c99068', jaw: 'square', face: 'flat',
      hair: { style: 'fade', color: '#17110d' },
      hat: { style: 'flatcap', color: '#5d5b57' },
      beard: { style: 'full', color: '#17110d' },
      headgear: ['cigarette'],
      top: { style: 'tank', color: '#f4f2ec' },
      bottom: { style: 'slacks', color: '#18181b' },
      shoes: { style: 'boot', color: '#141414', sole: '#050505' },
      hands: 'wrap',
      gait: { stride: 0.95, swing: 0.5 },
      guard: { aF: 0.85, eF: 2.1, aB: 0.6, eB: 2.2, lean: 0.14, y: 0.03, fFx: 0.085, fBx: -0.08, tB: 0.3, head: 0.1, brow: -0.5 },
      // The blow he is about to throw winds up from the next move, the one that landed follows through.
      attack: (aim, st = {}) => {
        const move = kick => (kick ? JOHNO_FLYKICK : JOHNO_UPPERCUT);
        const next = move(st.kickNext).keys.filter(([t]) => t <= 0);
        const done = move(st.kick).keys.filter(([t]) => t >= 0).map(([t, k, how], i) => [i ? t : 0.001, k, how]);
        return { keys: [...next, ...done] };
      },
      special: t => {
        const p = clamp(t / 0.4, 0, 1);
        const fall = t > 0.6 ? clamp((t - 0.6) / 0.4, 0, 1) : 0;
        return { aF: lerp(0.2, 3.1, EASES.out(p)), eF: lerp(1.5, 0.2, p), aB: 0.4, eB: 1.8, lean: lerp(0.25, -0.2, p) * (1 - fall), y: lerp(0.07, -0.22, EASES.out(p)) * (1 - fall), fFy: -p * 0.12 * (1 - fall), fBy: -p * 0.06 * (1 - fall), tF: 0.5 * p, mouth: 1, brow: -1 };
      },
      // At home: a drag on the cigarette, then he blows the smoke out.
      idle: { every: 5, clip: { keys: [[0, {}], [0.35, { aF: 1.7, eF: 2.65, wF: -0.6, head: -0.1 }], [0.9, { aF: 1.7, eF: 2.7, wF: -0.6, head: -0.15, eyes: 1 }], [1.2, { head: -0.25, mouth: 0.35 }], [1.6, {}]] } }
    },
    // Polarise: always cool, never in a hurry. A monogram dad cap, a ginger beard, a silver stud, a light grey
    // tracksuit that fits just right, a thick steel watch, and a huge baseball bat full of nails resting on his
    // shoulder.
    polarise: {
      build: 'athletic', body: { shoulder: 0.15, chest: 0.135 }, skin: '#f1c9ab', jaw: 'square', face: 'flat',
      hair: { style: 'fade', color: '#8a5a36' },
      hat: { style: 'dadcap', color: '#cdb88c' },
      beard: { style: 'full', color: '#a65d31' },
      headgear: [{ kind: 'earring', color: '#eef2f5' }],
      top: { style: 'pullover', color: '#d4d4cf', strings: '#bdbdb7' },
      bottom: { style: 'joggers', color: '#cdcdc8' },
      shoes: { style: 'sneaker', color: PAPER, accent: '#c6c6c1', sole: PAPER },
      armFx: (P, A, far) => (far ? bracelets(P, A) : bigWatch(P, A)),
      props: { F: { kind: 'nailbat', len: 0.85 } },
      gait: { stride: 0.85, swing: 0.45, bob: 0.7, lean: 0.3 },
      rest: { aF: 0.05, eF: 2.2, grip: -0.75, aB: -0.06, eB: 0.3, head: -0.05 },
      // A batter's stance: knees soft, both hands on the handle by the back shoulder, the bat up behind his head.
      guard: POLARISE_STANCE,
      // The swing: he loads back and strides, the hands lead with the bat still back, the barrel whips through
      // flat in front of him, and he finishes with the bat wrapped round over his shoulder.
      attack: () => ({ keys: [[-0.32, { aF: -0.7, eF: 2.9, grip: -0.15, lean: -0.14, x: -0.02, fFx: 0.07, fFy: -0.03, brow: -0.6 }], [-0.1, { aF: -0.3, eF: 2.4, grip: -0.7, lean: -0.05, fFx: 0.14, fFy: 0 }, 'io'], [-0.04, { aF: 0.7, eF: 1.2, grip: 0.55, propLen: 0.5, lean: 0.05, x: 0.015, fFx: 0.14, tB: 0.2 }, 'in'], [0, { aF: 1.35, eF: 0.12, grip: 1.6, propLen: 1, lean: 0.12, x: 0.03, fFx: 0.14, tB: 0.35, mouth: 0.4, brow: -1 }], [0.06, { aF: 1.7, eF: 0.8, grip: 4.0, propLen: 0.5, lean: 0.1, x: 0.03, fFx: 0.14, tB: 0.45 }], [0.14, { aF: 1.9, eF: 1.6, grip: 6.48, propLen: 1, lean: 0.05, x: 0.03, fFx: 0.14, tB: 0.45 }, 'out'], [0.3, { aF: 1.95, eF: 1.65, grip: 6.45, lean: 0.04, x: 0.03, fFx: 0.14, tB: 0.45 }], [0.6, { grip: 0.3 + Math.PI * 2 }, 'io']] }),
      // Homerun: the front leg comes up, a beat in the load, then a full stride and everything into one swing
      // that he holds at the end.
      special: t => {
        const out = {};
        sampleClip(HOMERUN, t, HOMERUN_BASE, out);
        // The bat goes once all the way round: told as the same angle near the stance, it blends out the short way.
        if (out.grip !== undefined) out.grip = POLARISE_STANCE.grip + Math.atan2(Math.sin(out.grip - POLARISE_STANCE.grip), Math.cos(out.grip - POLARISE_STANCE.grip));
        return out;
      },
      // At home: the bat on his shoulder, a slow look down at the watch, then he leans back and lets the head roll.
      idle: { every: 7, clip: { keys: [[0, {}], [0.6, { aF: 0.25, eF: 2.35, grip: -0.6, head: 0.22 }, 'io'], [1.6, { aF: 0.25, eF: 2.35, grip: -0.6, head: 0.24, eyes: 0.4 }], [2.1, {}, 'io'], [3.0, { lean: -0.06, head: -0.14, y: 0.005 }, 'io'], [3.9, { lean: -0.06, head: -0.14, y: 0.005, eyes: 0.5 }], [4.6, {}, 'io']] } }
    },
    // Bigtoe: a bordeaux velour tracksuit zipped to the chin, a thick curb chain in gold, arms like tree trunks
    // and a gold knuckle duster set with diamonds on each fist. He fights in combinations.
    bigtoe: {
      build: 'athletic', body: { shoulder: 0.165, chest: 0.148, waist: 0.112, upper: 0.16, arm: 0.104, wrist: 0.07 }, skin: '#ecc3a1', jaw: 'square', face: 'flat',
      hair: { style: 'skinfade', color: '#1b1512' },
      beard: { style: 'shadow', color: '#2a1f19' },
      lids: 0.4,
      brows: '#1f1814',
      top: { style: 'trackjacket', color: '#6b1b2f', stripe: '#d9d1ea', inner: '#5c1627', velvet: true },
      bottom: { style: 'track', color: '#661a2d', stripe: '#d9d1ea' },
      shoes: { style: 'sneaker', color: PAPER, accent: '#6b1b2f', sole: PAPER },
      gear: [{ kind: 'curb' }],
      hands: 'dusters',
      gait: { stride: 0.95, swing: 0.8, bob: 1.2, lean: 0.7 },
      rest: { aF: 0.3, eF: 0.5, aB: -0.02, eB: 0.55 },
      guard: { aF: 0.9, eF: 2.25, aB: 0.75, eB: 2.35, lean: 0.14, y: 0.035, fFx: 0.09, fBx: -0.085, tB: 0.3, head: 0.1, brow: -0.6 },
      // A combination: jab with the front fist, cross with the back one, and the front hook to finish.
      attack: () => ({ keys: [[-0.18, { aF: 0.8, eF: 2.3, aB: 0.7, eB: 2.4, lean: 0.05, brow: -0.8 }], [0, { aF: 1.55, eF: 0.1, aB: 0.8, eB: 2.3, lean: 0.18, x: 0.02, mouth: 0.3, brow: -1 }, 'in'], [0.07, { aF: 0.9, eF: 2.2, aB: 0.9, eB: 2.2, lean: 0.14 }], [0.13, { aF: 0.85, eF: 2.25, aB: 1.6, eB: 0.1, lean: 0.26, x: 0.035, mouth: 0.5 }, 'in'], [0.2, { aF: 0.9, eF: 2.2, aB: 0.8, eB: 2.3, lean: 0.12 }], [0.27, { aF: 1.4, eF: 0.85, aB: 0.75, eB: 2.3, lean: 0.3, x: 0.04, y: 0.02, mouth: 0.8 }, 'in'], [0.6, {}, 'out']] }),
      // Diamanthagel: both fists going like pistons.
      special: t => {
        const p = Math.sin(t * 34);
        return { aF: 1.45 + p * 0.15, eF: 0.15 + Math.max(0, -p) * 2.1, aB: 1.45 - p * 0.15, eB: 0.15 + Math.max(0, p) * 2.1, lean: 0.24, x: 0.02, y: 0.035, fFx: 0.11, fBx: -0.1, mouth: 1, brow: -1 };
      },
      // At home: he knocks the dusters together, rolls his shoulders, and the diamonds flash.
      idle: { every: 6, clip: { keys: [[0, {}], [0.3, { aF: 1.25, eF: 1.6, aB: 1.35, eB: 1.5 }, 'io'], [0.42, { aF: 1.3, eF: 1.45, aB: 1.4, eB: 1.4 }, 'in'], [0.54, { aF: 1.25, eF: 1.6, aB: 1.35, eB: 1.5 }], [0.66, { aF: 1.3, eF: 1.45, aB: 1.4, eB: 1.4 }, 'in'], [1.0, { aF: 1.2, eF: 1.7, aB: 1.3, eB: 1.6, mouth: 0.2 }], [1.4, {}, 'io'], [1.8, { y: 0.012, head: 0.08, sq: 0.98 }, 'io'], [2.3, {}, 'io']] } }
    },
    // Maliq: 2008 all the way. Shoulder-length locs under a red fitted, white shutter shades, a tall white tee,
    // baggy denim, an iced-out chain, and a deck of magic cards in his hand.
    maliq: {
      build: 'regular', skin: '#4f3221', jaw: 'soft', face: 'grin',
      hair: { style: 'locs', color: '#15100c' },
      hat: { style: 'fitted', color: '#d6182f' },
      specs: 'shutter',
      top: { style: 'tallTee', color: '#f6f5f1' },
      bottom: { style: 'jeans', color: '#2f4f86' },
      shoes: { style: 'sneaker', color: PAPER, accent: '#d6182f' },
      gear: [{ kind: 'iced', stone: '#a06bff' }],
      hands: 'open',
      props: { F: { kind: 'cards' } },
      gait: { stride: 0.95, swing: 0.8, bob: 1.2 },
      guard: { aF: 0.9, eF: 1.9, wF: 0.4, aB: 0.35, eB: 0.6, lean: 0.04, y: 0.015, fFx: 0.07, fBx: -0.06 },
      attack: aim => ({ keys: [[-0.2, { aF: 0.6, eF: 2.4, wF: 0.8, lean: -0.05 }], [0, { aF: aim, eF: 0.12, wF: -0.2, lean: 0.08, freeF: true }, 'in'], [0.25, { freeF: true }], [0.4, {}]] }),
      special: t => {
        const p = clamp(t / 0.45, 0, 1);
        return { aF: lerp(0.9, 2.3, p), eF: 0.2, aB: lerp(0.35, 2.4, p), eB: 0.2, wF: -0.3, lean: -0.1 * p, y: -0.02 * p, mouth: 0.6 * p, brow: 0.6 };
      },
      idle: { every: 4.5, clip: { keys: [[0, {}], [0.2, { aF: 1.4, eF: 1.9, wF: 0.9 }], [0.4, { aF: 2.0, eF: 0.6, wF: -0.4, freeF: true }, 'in'], [0.8, { aF: 1.3, eF: 1.9, wF: 0.6 }], [1.0, {}]] } }
    }
  };
  // Kuza & Kamua: two brothers in black silk tunics, each with a throwing star as big as his head. Kuza is the lean
  // one: an undercut swept up, black shades, a silver chain. Kamua is the stocky one: a brush cut,
  // round tinted glasses and a gold watch.
  const TWIN_MOVES = {
    gait: { stride: 1.1, lean: 1.1 },
    guard: { aF: 0.6, eF: 1.7, aB: 0.4, eB: 1.5, lean: 0.1, y: 0.03, fFx: 0.09, fBx: -0.08, brow: -0.4 },
    // A volley of three: the front arm, the back arm, the front arm again, each snapping out at the target.
    attack: aim => ({ keys: [[-0.18, { aF: -0.9, eF: 1.7, aB: -0.6, eB: 1.6, lean: -0.15, brow: -0.8 }], [0, { aF: aim, eF: 0.1, aB: -0.5, eB: 1.5, lean: 0.14, x: 0.02, freeF: true, mouth: 0.5, brow: -1 }, 'in'], [0.07, { aF: -0.6, eF: 1.6, aB: aim, eB: 0.1, lean: 0.16, x: 0.025, freeF: true, brow: -1 }, 'in'], [0.14, { aF: aim, eF: 0.1, aB: -0.3, eB: 1.2, lean: 0.18, x: 0.03, freeF: true, mouth: 0.5, brow: -1 }, 'in'], [0.4, { freeF: true }], [0.55, {}]] }),
    special: t => ({ lean: 0.05, aF: 1.6 + Math.sin(t * 20) * 0.2, eF: 0.1, aB: 1.6 - Math.sin(t * 20) * 0.2, eB: 0.1, freeF: t > 0.3, mouth: 0.6, brow: -1, y: -0.04 * Math.sin(Math.min(1, t / 0.6) * Math.PI) }),
    idle: { every: 4.5, clip: { keys: [[0, {}], [0.2, { aF: 1.7, eF: 0.4 }], [0.9, { aF: 1.7, eF: 0.4 }], [1.1, {}]] } }
  };
  // Kuza and Kamua, after the real pair: East Asian, undercuts, black tanks over full tattoo sleeves, steel
  // watches and chains, black trousers. Kuza is the slimmer one with the slicked-back top and the in-ear.
  HERO_SPECS.kuza = {
    ...TWIN_MOVES, build: 'athletic', body: { arm: 0.07, wrist: 0.052 }, jaw: 'square', skin: '#ddb088', face: 'smirk',
    hair: { style: 'undercut', color: '#121212', len: 1.2 },
    headgear: [{ kind: 'earring', color: '#2a2c31' }],
    top: { style: 'tank', color: '#141416' },
    bottom: { style: 'slacks', color: '#1d1e22' },
    shoes: { style: 'sneaker', color: '#141416', sole: '#e9e7e1' },
    gear: [{ kind: 'chain', color: '#d7dbe0' }],
    armFx: (P, A, far) => {
      tattooSleeve(P, A, far);
      if (!far) steelWatch(P, A);
    },
    props: { F: { kind: 'star', color: '#ff2d55', size: 0.13 } }
  };
  // Kamua keeps his arms to himself: they hang loose at his sides, and he only lifts the star to his chest.
  const KAMUA = {
    ...TWIN_MOVES,
    guard: { aF: 0.04, eF: 0.3, aB: -0.05, eB: 0.35, lean: 0.04, y: 0.01, fFx: 0.07, fBx: -0.06, brow: -0.3 },
    idle: { every: 6, clip: { keys: [[0, {}], [0.3, { aF: 0.3, eF: 1.5 }], [1.1, { aF: 0.3, eF: 1.5 }], [1.4, {}]] } },
    build: 'heavy', body: { belly: 0.03, head: 0.215 }, jaw: 'round', skin: '#d8ab83', face: 'flat',
    hair: { style: 'undercut', color: '#121212', len: 0.6 },
    top: { style: 'tank', color: '#18181b' },
    bottom: { style: 'slacks', color: '#1d1e22' },
    shoes: { style: 'sneaker', color: '#18181b', sole: '#e9e7e1' },
    gear: [{ kind: 'chain', color: '#d7dbe0' }],
    armFx: (P, A, far) => {
      tattooSleeve(P, A, far);
      if (!far) steelWatch(P, A);
    },
    props: { F: { kind: 'star', color: YELLOW, size: 0.13 } }
  };
  HERO_SPECS.kuza.group = [{ spec: KAMUA, dx: -0.44, dy: -0.015, k: 0.96, lag: 0.14, phase: 0.8, seed: 3 }];

  // ---- Posing a hero ------------------------------------------------------------------------------
  // The arm angle that points along a local aim (0 ahead, positive down), net of the torso's lean.
  const aimArm = (aimLocal, lean = 0) => clamp(Math.PI / 2 - aimLocal - lean, 0.2, 3.0);
  // Lying flat after a knockout: limbs loose, eyes shut.
  const LIMP = { aF: 2.5, eF: 0.4, aB: 2.9, eB: 0.3, fFx: 0.09, fBx: -0.02, fFy: -0.02, tF: -0.5, eyes: 1, mouth: 0.3, head: -0.2, brow: 0.3 };
  // Builds the pose for a frame from the crew state: rest, the guard while fighting, breath and gait, then
  // the clip that is playing: an attack around its moment of impact, a special, or a flourish at home.
  const heroPose = (spec, go) => {
    const { st, mem, fight, gait, t, aim } = go;
    const b = buildOf(spec);
    const pose = { ...REST, fFx: b.stance * 0.9, fBx: -b.stance * 0.9, ...(spec.rest || {}) };
    if (go.down) return Object.assign(pose, LIMP);
    const guard = spring(mem, 'guard', fight ? 1 : 0, 40, 11);
    if (spec.guard && guard > 0.001) Object.keys(spec.guard).forEach(k => {
      pose[k] = lerp(pose[k], spec.guard[k], clamp(guard, 0, 1));
    });
    breathe(pose, t, fight ? 1.4 : 1);
    const style = spec.gait || {};
    locomote(pose, (st.walk || 0) * (style.rate || 6), gait, style.run || 0, style);
    // The attack: anticipation while the next blow is coming, the strike and follow-through after it landed.
    const attack = spec.attack(aimArm(aim, 0.15), st);
    const since = st.since === undefined ? 9 : st.since;
    let tau = null;
    if (since < clipEnd(attack)) tau = since;
    else if (fight && st.engaged && st.cd > 0 && st.cd < -clipStart(attack)) tau = -st.cd;
    if (tau !== null) sampleClip(attack, tau, { ...pose }, pose);
    // A special takes the whole body, blended in and out at its ends.
    if (st.special > 0 && spec.special) {
      const time = 1.3 - st.special;
      const w = clamp(Math.min(time / 0.1, st.special / 0.2), 0, 1);
      const move = spec.special(time);
      Object.keys(move).forEach(k => {
        pose[k] = typeof move[k] === 'number' ? lerp(pose[k] === undefined ? 0 : pose[k], move[k], w) : move[k];
      });
    } else if (!fight && gait < 0.05 && spec.idle) {
      sampleClip(spec.idle.clip, (t + (go.seed || 0) * 0.37) % spec.idle.every, { ...pose }, pose);
    }
    // A spin takes the arms out wide, whatever else is going on.
    if (st.spin > 0 && spec.spin) Object.assign(pose, spec.spin);
    pose.stub = st.arm > 0;
    pose.motion = gait;
    return pose;
  };
  const springsOf = (owner, key) => {
    const mem = memoryOf(owner);
    if (key) {
      mem[key] = mem[key] || { t: clock, springs: {}, dt: 0 };
      return mem[key];
    }
    return mem;
  };
  const tickMemory = mem => {
    mem.dt = clamp(clock - mem.t, 0, 0.05);
    mem.t = clock;
    return mem;
  };
  // One hero facing right with the feet on (0, 0), `s` tall. Returns where his attack leaves from.
  // Some come as a group (`group`): the others stand a step away, each `k` of `s` tall and `lag` seconds behind
  // the leader, those further back drawn first. Each is outlined on his own, so they read as separate people.
  // Knocked out, `fall` lays each of them down on his own feet.
  const drawHero = (item, st, s, opts = {}) => {
    const spec = HERO_SPECS[item.type];
    const mem = tickMemory(springsOf(st));
    const go = { st, mem, fight: Boolean(opts.fight), gait: opts.gait || 0, t: clock + item.id * 1.7, aim: opts.aim || 0.3, seed: item.id, down: opts.down };
    const fall = opts.fall || 0;
    const figure = (x, y, h, paint) => outlined(h, () => {
      ctx.save();
      ctx.translate(x, y);
      if (fall) ctx.rotate(-fall * Math.PI / 2);
      const out = paint();
      ctx.restore();
      return out && { x: x + out.x, y: y + out.y };
    });
    const member = (m, i) => {
      const own = tickMemory(springsOf(st, `member${i}`));
      const late = { ...st, since: (st.since === undefined ? 9 : st.since) - m.lag, cd: (st.cd || 0) + m.lag };
      figure(s * m.dx, s * m.dy, s * m.k, () => {
        drawRig(m.spec, heroPose(m.spec, { ...go, st: late, mem: own, t: go.t + m.phase, seed: item.id + m.seed }), s * m.k, own, item.id + m.seed);
      });
    };
    const group = spec.group || [];
    group.forEach((m, i) => m.dy <= 0 && member(m, i));
    const tip = figure(0, 0, s, () => drawRig(spec, heroPose(spec, go), s, mem, item.id).tip);
    group.forEach((m, i) => m.dy > 0 && member(m, i));
    return tip;
  };

  // ---- Parts for the street ------------------------------------------------------------------------
  // Worn and held by the Bootlegs and the people on the block.
  Object.assign(HAIR, {
    // The hood pulled up: the back of it rests behind the head, the rim frames the face.
    hood: {
      back: (r, color) => circle(-r * 0.15, -r * 0.02, r * 1.22, tone(color, -0.18)),
      front: (r, color, P) => {
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(-r * 0.1, -r * 0.05, r * 1.2, Math.PI * 0.42, Math.PI * 1.82);
        ctx.arc(r * 0.2, -r * 0.02, r * 0.92, Math.PI * 1.75, Math.PI * 0.5, true);
        ctx.closePath();
        ctx.fill();
        if (P.small) return;
        ctx.beginPath();
        ctx.arc(r * 0.2, -r * 0.02, r * 0.92, Math.PI * 1.75, Math.PI * 0.5, true);
        ctx.strokeStyle = tone(color, -0.4);
        ctx.lineWidth = Math.max(0.8, r * 0.07);
        ctx.stroke();
      }
    }
  });
  Object.assign(HEADWEAR, {
    // A knit beanie with the cuff rolled up.
    beanie: (r, o, P) => {
      ctx.fillStyle = o.color;
      ctx.beginPath();
      ctx.moveTo(r * 0.9, -r * 0.3);
      ctx.quadraticCurveTo(r * 0.92, -r * 1.32, -r * 0.1, -r * 1.3);
      ctx.quadraticCurveTo(-r * 1.02, -r * 1.22, -r * 0.97, -r * 0.24);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = tone(o.color, 0.12);
      ctx.beginPath();
      ctx.moveTo(r * 0.92, -r * 0.54);
      ctx.quadraticCurveTo(-r * 0.05, -r * 0.76, -r * 0.99, -r * 0.5);
      ctx.lineTo(-r * 0.98, -r * 0.22);
      ctx.quadraticCurveTo(-r * 0.05, -r * 0.48, r * 0.92, -r * 0.26);
      ctx.closePath();
      ctx.fill();
      if (!P.small) for (let k = -3; k <= 3; k += 1) polyline([[k * r * 0.24, -r * (0.66 - Math.abs(k) * 0.02)], [k * r * 0.24, -r * (0.4 - Math.abs(k) * 0.02)]], r * 0.04, tone(o.color, -0.15));
    },
    // A bucket hat pulled low.
    bucket: (r, o, P) => {
      poly([[-r * 0.86, -r * 0.4], [-r * 0.72, -r * 1.2], [r * 0.74, -r * 1.2], [r * 0.9, -r * 0.4]], o.color);
      poly([[-r * 1.3, -r * 0.12], [-r * 0.92, -r * 0.5], [r * 0.96, -r * 0.5], [r * 1.42, -r * 0.1]], tone(o.color, -0.08));
      if (!P.small) {
        polyline([[-r * 0.84, -r * 0.52], [r * 0.9, -r * 0.52]], r * 0.05, tone(o.color, -0.25));
        polyline([[-r * 0.78, -r * 0.86], [r * 0.82, -r * 0.86]], r * 0.04, tone(o.color, -0.12));
      }
    }
  });
  HEADGEAR.earring = (r, P, o) => circle(-r * 0.12, r * 0.32, r * 0.09, o.color || GOLD);

  // Tops for everyone off the street.
  Object.assign(TOP_STYLES, {
    // A hoodie with sleeves: the pouch, the ribbed hem, the strings, a print.
    pullover: {
      sleeve: 1,
      fit: 1.22,
      cuff: o => tone(o.color, -0.25),
      back: (P, o) => {
        if (!o.up) TOP_STYLES.hoodie.back(P, o);
      },
      body: (P, o) => {
        const { b, H } = P;
        const T = b.torso * H;
        torsoPath(b, H, 0.05, 0.01);
        ctx.fillStyle = o.color;
        ctx.fill();
        ctx.save();
        torsoPath(b, H, 0.05, 0.01);
        ctx.clip();
        poly([[-b.waist * H * 0.3, -T * 0.34], [b.chest * H * 0.85, -T * 0.34], [b.chest * H * 1.0, 0.02 * H], [-b.waist * H * 0.5, 0.02 * H]], tone(o.color, -0.16));
        rect(-b.hips * H * 1.6, 0.03 * H, b.hips * H * 3.4, H * 0.025, tone(o.color, -0.3));
        if (!P.small) {
          if (o.print) logoRing(b.chest * H * 0.3, -T * 0.62, H * 0.04, H * 0.022, H * 0.012, o.print);
        }
        ctx.restore();
        torsoPath(b, H, 0.05, 0.01);
        shadeBack(b, H, 0.12);
        if (!o.up) neckline(b, H, tone(o.color, -0.3), 0.03, 0.55);
        if (!P.small && o.strings) [0.05, 0.22].forEach(x => polyline([[b.chest * H * x, -T - H * 0.005], [b.chest * H * (x + 0.04), -T * 0.66]], H * 0.007, o.strings));
      }
    },
    // A long coat in two cuts: a medic's coat with a collar and a cross, a coat with a fur collar.
    longcoat: {
      sleeve: 1,
      fit: 1.2,
      cuff: o => o.fur || tone(o.color, -0.15),
      back: (P, o) => {
        const { b, H, sk, mem } = P;
        const fly = mem ? spring(mem, 'coat', P.motion * 0.8 + 0.05 * Math.sin(clock * 2.2), 30, 5) : 0.05;
        const len = o.len || 0.26;
        inTorso(sk, () => {
          ctx.fillStyle = tone(o.color, -0.18);
          ctx.beginPath();
          ctx.moveTo(-b.shoulder * H * 0.9, -b.torso * H * 0.9);
          ctx.lineTo(-b.hips * H * 0.7, 0);
          ctx.quadraticCurveTo(-b.hips * H * (1.3 + fly * 2), H * len * 0.5, -b.hips * H * (1.4 + fly * 3), H * (len - fly * 0.08));
          ctx.lineTo(b.hips * H * (0.6 - fly), H * len);
          ctx.lineTo(b.hips * H * 0.5, 0);
          ctx.closePath();
          ctx.fill();
        });
      },
      body: (P, o) => {
        const { b, H } = P;
        const T = b.torso * H;
        const C = b.chest * H;
        torsoPath(b, H, 0.06, 0.01);
        ctx.fillStyle = o.color;
        ctx.fill();
        torsoPath(b, H, 0.06, 0.01);
        shadeBack(b, H, 0.14);
        // What shows under it: a turtleneck or a shirt.
        if (o.inner) poly([[C * 0.12, -T - H * 0.01], [C * 0.66, -T * 0.98], [C * 0.52, 0.03 * H], [C * 0.22, 0.03 * H]], o.inner);
        // Lapels, and a collar turned up.
        poly([[-b.shoulder * H * 0.15, -T - H * 0.01], [C * 0.22, -T * 0.4], [C * 0.1, -T * 0.92]], tone(o.color, 0.1));
        poly([[b.shoulder * H * 0.62, -T - H * 0.005], [C * 0.62, -T * 0.4], [C * 0.56, -T * 0.92]], tone(o.color, 0.1));
        if (o.collar) {
          poly([[-b.shoulder * H * 0.4, -T - H * 0.01], [-b.shoulder * H * 0.1, -T - H * 0.06], [b.shoulder * H * 0.05, -T + H * 0.01]], tone(o.color, -0.25));
          poly([[b.shoulder * H * 0.35, -T + H * 0.01], [b.shoulder * H * 0.7, -T - H * 0.06], [b.shoulder * H * 0.8, -T * 0.97]], tone(o.color, -0.25));
        }
        if (o.cross) {
          rect(C * 0.72, -T * 0.6, H * 0.04, H * 0.014, o.cross);
          rect(C * 0.733, -T * 0.6 - H * 0.013, H * 0.014, H * 0.04, o.cross);
        }
        // A fur collar over the shoulders.
        if (o.fur) {
          for (let k = 0; k <= 6; k += 1) circle(-b.shoulder * H * 0.85 + k * b.shoulder * H * 0.27, -T - H * (0.005 + (k % 2) * 0.008), H * 0.03, k % 2 ? tone(o.fur, 0.1) : o.fur);
        }
      },
      skirt: (P, o) => {
        const { b, H } = P;
        const len = o.len || 0.26;
        const sway = P.mem ? spring(P.mem, 'skirt', P.motion * 0.4, 40, 6) : 0;
        ctx.fillStyle = o.color;
        ctx.beginPath();
        ctx.moveTo(-b.hips * H * 1.1, 0);
        ctx.lineTo(-b.hips * H * (1.2 + sway), H * len);
        ctx.lineTo(-b.hips * H * (0.05 + sway), H * (len + 0.01));
        ctx.lineTo(b.hips * H * 0.15, 0.03 * H);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = tone(o.color, 0.05);
        ctx.beginPath();
        ctx.moveTo(b.hips * H * 0.85, 0);
        ctx.lineTo(b.hips * H * (1.05 - sway * 0.5), H * (len - 0.01));
        ctx.lineTo(b.hips * H * (0.5 - sway * 0.5), H * len);
        ctx.lineTo(b.hips * H * 0.55, 0.03 * H);
        ctx.closePath();
        ctx.fill();
      },
      sleeveDetail: (P, end, o, far) => {
        if (o.fur && !P.small) cuffBand(end, end.r * 1.15, far ? tone(o.fur, -0.15) : o.fur, P.H * 0.035);
      }
    },
    // A loud tracksuit jacket: the tee at the neck, the zip, stripes down the sides and the sleeves.
    trackjacket: {
      sleeve: 1,
      fit: 1.25,
      cuff: o => o.stripe,
      body: (P, o) => {
        const { b, H } = P;
        const T = b.torso * H;
        torsoPath(b, H, 0.04, 0.01);
        ctx.fillStyle = o.color;
        ctx.fill();
        ctx.save();
        torsoPath(b, H, 0.04, 0.01);
        ctx.clip();
        rect(b.chest * H * 0.85, -T * 1.1, H * 0.02, T * 1.3, o.stripe);
        rect(-b.hips * H * 1.6, 0.02 * H, b.hips * H * 3.4, H * 0.025, o.stripe);
        // Velour: the nap catches the light in a soft band down the front.
        if (o.velvet && !P.small) {
          const g = ctx.createLinearGradient(-b.shoulder * H, 0, b.chest * H, 0);
          g.addColorStop(0, 'rgba(255,255,255,0)');
          g.addColorStop(0.6, 'rgba(255,215,230,0.18)');
          g.addColorStop(0.8, 'rgba(255,255,255,0)');
          ctx.fillStyle = g;
          ctx.fillRect(-H, -T * 1.2, H * 2, T * 1.4);
        }
        ctx.restore();
        torsoPath(b, H, 0.04, 0.01);
        shadeBack(b, H, 0.12);
        poly([[b.chest * H * 0.15, -T - H * 0.01], [b.chest * H * 0.65, -T * 0.98], [b.chest * H * 0.42, -T * 0.78]], o.inner || PAPER);
        if (!P.small) polyline([[b.chest * H * 0.42, -T * 0.78], [b.chest * H * 0.45, 0.03 * H]], H * 0.007, 'rgba(255,255,255,0.75)');
      },
      sleeveDetail: (P, end, o, far, A) => {
        if (P.small) return;
        polyline([[A.sh.x, A.sh.y], [A.el.x, A.el.y], [end.at.x, end.at.y]], P.H * 0.012, far ? tone(o.stripe, -0.18) : o.stripe);
      }
    }
  });

  // Worn on the chest: the MIXEDOWN patch, a chain, a medallion with the logo.
  Object.assign(GEAR, {
    patch: (P, o) => {
      if (P.small) return;
      const { b, H } = P;
      logoRing(b.chest * H * 0.58, -b.torso * H * 0.6, H * 0.022, H * 0.012, H * 0.007, o.color || PAPER);
    },
    chain: (P, o) => {
      const { b, H } = P;
      const T = b.torso * H;
      const SW = b.shoulder * H;
      ctx.strokeStyle = o.color;
      ctx.lineWidth = Math.max(1, H * (o.heavy ? 0.018 : 0.012));
      ctx.beginPath();
      ctx.moveTo(-SW * 0.15, -T - H * 0.01);
      ctx.quadraticCurveTo(b.chest * H * 0.2, -T * 0.5, b.chest * H * 0.62, -T - H * 0.005);
      ctx.stroke();
      if (!P.small) {
        ctx.setLineDash([H * 0.008, H * 0.01]);
        ctx.strokeStyle = 'rgba(255,255,255,0.55)';
        ctx.lineWidth = Math.max(0.6, H * 0.006);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      if (o.medal) {
        const mx = b.chest * H * 0.22;
        const my = -T * 0.56;
        circle(mx, my, H * 0.03, o.color);
        if (!P.small) logoRing(mx, my, H * 0.017, H * 0.009, H * 0.005, INK);
      }
    }
  });
  // Held in a hand. Things that hang or stand upright turn back against the hand.
  const upright = (P, fn) => {
    ctx.save();
    ctx.rotate(-P.propA);
    fn();
    ctx.restore();
  };
  Object.assign(PROPS, {
    // A spray can, the nozzle forward.
    spray: (P, o) => {
      const H = P.H;
      roundRect(-H * 0.03, -H * 0.042, H * 0.12, H * 0.084, H * 0.02, o.color);
      if (!P.small) {
        rect(-H * 0.02, -H * 0.042, H * 0.1, H * 0.018, 'rgba(255,255,255,0.4)');
        rect(H * 0.05, -H * 0.042, H * 0.012, H * 0.084, 'rgba(0,0,0,0.2)');
      }
      poly([[H * 0.09, -H * 0.04], [H * 0.12, -H * 0.026], [H * 0.12, H * 0.026], [H * 0.09, H * 0.04]], '#cfccc4');
      rect(H * 0.12, -H * 0.014, H * 0.022, H * 0.028, PAPER);
      return H * 0.145;
    },
    // An auto-injector: a slim black pen, the dose glowing in its window.
    injector: (P, o) => {
      const H = P.H;
      const kick = P.pose.kick || 0;
      roundRect(-H * 0.03, -H * 0.018, H * 0.16, H * 0.036, H * 0.012, '#18191c');
      rect(H * 0.03, -H * 0.011, H * 0.06, H * 0.022, o.color);
      rect(H * 0.13, -H * 0.006, H * 0.03, H * 0.012, '#c9ced3');
      if (!P.small) {
        ctx.save();
        ctx.globalCompositeOperation = 'lighter';
        glowAt(H * 0.06, 0, H * (0.05 + kick * 0.08), `rgba(${o.glow},ALPHA)`, 0.35 + kick * 0.5);
        ctx.restore();
      }
      return H * 0.16;
    }
  });

  // ---- The Bootlegs -------------------------------------------------------------------------------
  // The fake gang on the same rig: cheap hoodies with the logo a little off, shades on everyone, and
  // whatever they found to hit things with.
  // A colour as 'r,g,b' for glows.
  const rgbOf = hex => tone(hex, 0).slice(4, -1);
  // Where the front shoulder sits in a pose, in fractions of the height.
  const shoulderAt = (b, pose) => {
    const along = b.torso - b.head * 0.18;
    const across = b.shoulder * 0.45;
    return { x: pose.x + Math.sin(pose.lean) * along + Math.cos(pose.lean) * across, y: -b.legs + pose.y - Math.cos(pose.lean) * along + Math.sin(pose.lean) * across };
  };
  // A figure holding something to aim poses like the heroes and points it with the hand on a line from the shoulder.
  const aimPose = (spec, go) => {
    const pose = heroPose(spec, go);
    if (go.down || !spec.hold) return pose;
    const ready = clamp(spring(go.mem, 'ready', go.fight ? 1 : 0, 40, 10), 0, 1);
    const hold = spec.hold(go.aim, ready);
    const sh = shoulderAt(buildOf(spec), pose);
    const kick = go.st.recoil || 0;
    const reach = hold.reach - kick * 0.025;
    pose.handF = { x: sh.x + Math.cos(hold.dir) * reach, y: sh.y + Math.sin(hold.dir) * reach };
    pose.propAim = (hold.prop === undefined ? hold.dir : hold.prop) - kick * 0.12;
    return pose;
  };
  Object.assign(HEADWEAR, {
    // A bike helmet with vents.
    helmet: (r, o, P) => {
      ctx.fillStyle = o.color;
      ctx.beginPath();
      ctx.moveTo(r * 1.0, -r * 0.3);
      ctx.quadraticCurveTo(r * 0.95, -r * 1.3, -r * 0.15, -r * 1.25);
      ctx.quadraticCurveTo(-r * 1.1, -r * 1.15, -r * 1.02, -r * 0.2);
      ctx.closePath();
      ctx.fill();
      if (!P.small) for (let k = -1; k <= 1; k += 1) roundRect(k * r * 0.4 - r * 0.08, -r * 1.12, r * 0.16, r * 0.45, r * 0.06, 'rgba(0,0,0,0.25)');
      polyline([[r * 0.2, -r * 0.28], [r * 0.2, r * 0.6]], r * 0.06, INK);
    },
    // A dented pot for a helmet.
    pot: (r, o, P) => {
      ctx.fillStyle = '#8d9399';
      ctx.beginPath();
      ctx.ellipse(-r * 0.05, -r * 0.5, r * 1.05, r * 0.72, 0, Math.PI, TAU);
      ctx.fill();
      rect(-r * 1.15, -r * 0.55, r * 2.3, r * 0.12, '#6f747a');
      rect(r * 1.0, -r * 0.6, r * 0.35, r * 0.1, '#6f747a');
      if (!P.small) circle(r * 0.3, -r * 0.95, r * 0.12, '#a9aeb3');
    },
    // A cap turned backwards.
    snapback: (r, o) => {
      ctx.fillStyle = o.color;
      ctx.beginPath();
      ctx.moveTo(r * 0.92, -r * 0.4);
      ctx.quadraticCurveTo(r * 0.85, -r * 1.25, -r * 0.08, -r * 1.22);
      ctx.quadraticCurveTo(-r * 0.98, -r * 1.12, -r * 0.95, -r * 0.3);
      ctx.closePath();
      ctx.fill();
      poly([[-r * 0.6, -r * 0.42], [-r * 1.6, -r * 0.5], [-r * 1.55, -r * 0.32], [-r * 0.6, -r * 0.28]], tone(o.color, -0.15));
    },
    // A tall top hat with a gold band.
    tophat: (r, o) => {
      rect(-r * 0.78, -r * 1.75, r * 1.56, r * 1.2, o.color);
      rect(-r * 0.78, -r * 0.82, r * 1.56, r * 0.2, o.band);
      ctx.fillStyle = o.color;
      ctx.beginPath();
      ctx.ellipse(0, -r * 0.58, r * 1.3, r * 0.16, 0, 0, TAU);
      ctx.fill();
    }
  });
  Object.assign(PROPS, {
    // A baseball bat, taped at the grip.
    bat: (P, o) => {
      const H = P.H;
      const len = H * (o.len || 0.5);
      ctx.fillStyle = o.color || '#c5a679';
      ctx.beginPath();
      ctx.moveTo(-H * 0.06, -H * 0.012);
      ctx.lineTo(len * 0.55, -H * 0.018);
      ctx.quadraticCurveTo(len, -H * 0.04, len, 0);
      ctx.quadraticCurveTo(len, H * 0.04, len * 0.55, H * 0.018);
      ctx.lineTo(-H * 0.06, H * 0.012);
      ctx.closePath();
      ctx.fill();
      rect(-H * 0.06, -H * 0.014, H * 0.08, H * 0.028, '#2b2b2e');
      if (!P.small) polyline([[len * 0.3, -H * 0.012], [len * 0.9, -H * 0.025]], H * 0.007, 'rgba(255,255,255,0.35)');
      return len;
    },
    // A crowbar with its hook.
    crowbar: (P) => {
      const H = P.H;
      const len = H * 0.36;
      polyline([[-H * 0.04, 0], [len, 0], [len + H * 0.03, -H * 0.04]], H * 0.024, '#5a6168');
      return len;
    },
    // A black cane with a gold knob.
    cane: (P) => {
      const H = P.H;
      const len = H * 0.48;
      polyline([[-H * 0.02, 0], [len, 0]], H * 0.022, INK);
      circle(-H * 0.02, 0, H * 0.026, GOLD);
      return len;
    },
    // A megaphone: a black grip, a cone in the gang's red, a white rim.
    megaphone: (P, o) => {
      const H = P.H;
      rect(-H * 0.02, -H * 0.018, H * 0.06, H * 0.036, '#2b2b2e');
      poly([[H * 0.03, -H * 0.03], [H * 0.17, -H * 0.075], [H * 0.17, H * 0.075], [H * 0.03, H * 0.03]], o.color);
      if (!P.small) poly([[H * 0.04, -H * 0.03], [H * 0.16, -H * 0.068], [H * 0.16, -H * 0.04], [H * 0.04, -H * 0.012]], 'rgba(255,255,255,0.3)');
      rect(H * 0.16, -H * 0.078, H * 0.022, H * 0.156, PAPER);
      return H * 0.18;
    },
    // A firecracker ready to throw: red, a white band, the fuse lit.
    cracker: (P) => {
      const H = P.H;
      rect(-H * 0.01, -H * 0.022, H * 0.11, H * 0.044, SIGNAL);
      rect(H * 0.03, -H * 0.022, H * 0.02, H * 0.044, PAPER);
      polyline([[H * 0.1, 0], [H * 0.14, -H * 0.03]], H * 0.008, INK);
      if (!P.small) circle(H * 0.14, -H * 0.03, H * 0.014 * (0.7 + Math.random() * 0.6), YELLOW);
      return H * 0.12;
    },
    // A dented trash-can lid held up as a shield.
    lid: (P) => {
      const H = P.H;
      upright(P, () => {
        oval(H * 0.03, 0, H * 0.09, H * 0.2, '#9ea3a8');
        oval(H * 0.04, 0, H * 0.065, H * 0.16, '#c9ced3');
        if (!P.small) polyline([[H * 0.02, -H * 0.12], [H * 0.06, H * 0.1]], H * 0.008, 'rgba(255,255,255,0.6)');
      });
      return 0;
    }
  });
  // The fake logo on the chest: the ring, a little off.
  GEAR.fake = (P, o) => {
    const { b, H } = P;
    logoRing(b.chest * H * 0.35, -b.torso * H * 0.52, H * 0.045, H * 0.024, H * 0.014, o.color);
  };
  const COPY_WHITE = '#e8e6df';
  const BOOTLEG_SKINS = ['#d8a57c', '#c48d66', '#a87553', '#e6b796', '#7c5238'];
  const swingClip = () => ({ keys: [[-0.22, { aF: 2.9, eF: 0.8, grip: -0.6, lean: -0.15, brow: -0.8 }], [0, { aF: 0.7, eF: 0.1, grip: 0.6, lean: 0.25, x: 0.03, mouth: 0.6, brow: -1 }, 'in'], [0.2, { aF: 0.5, grip: 0.8, lean: 0.18 }], [0.3, {}]] });
  const GANG_SPECS = {
    // Läufer: a kid with the hood up, a crowbar in hand.
    kid: B => ({
      build: 'lean', face: 'smirk',
      hair: { style: 'hood', color: B.hood },
      specs: 'shades',
      top: { style: 'pullover', color: B.hood, up: true },
      bottom: { style: 'jeans', color: '#3d4a66' },
      shoes: { style: 'sneaker', color: PAPER },
      gear: [{ kind: 'fake', color: 'rgba(255,255,255,0.75)' }],
      props: { F: { kind: 'crowbar', grip: 1.2 } },
      attack: swingClip
    }),
    // Schläger: a heavy in black, a beanie, a bat.
    brute: B => ({
      build: 'hulk', face: 'frown', jaw: 'square',
      hair: { style: 'none', color: B.hair },
      hat: { style: 'beanie', color: '#3a3a3d' },
      specs: 'shades',
      beard: { style: 'stubble', color: '#1a1512' },
      top: { style: 'pullover', color: '#1d1d20' },
      bottom: { style: 'cargo', color: '#3d4a66' },
      shoes: { style: 'boot', color: '#3a3a3d', sole: '#1d1d20' },
      gear: [{ kind: 'fake', color: SIGNAL }],
      props: { F: { kind: 'bat', grip: 1.0 } },
      attack: swingClip
    }),
    // Sprayer: a tracksuit, a snapback turned round, a can of paint pointed at you.
    spray: B => ({
      build: 'lean', face: 'grin',
      hair: { style: 'fade', color: B.hair },
      hat: { style: 'snapback', color: SIGNAL },
      specs: 'shades',
      top: { style: 'trackjacket', color: '#26314a', stripe: PAPER },
      bottom: { style: 'track', color: '#1d1d20', stripe: PAPER },
      shoes: { style: 'sneaker', color: PAPER },
      props: { F: { kind: 'spray', abs: true, color: B.paint } },
      hold: (aim, ready) => ({ dir: lerp(0.9, 0.05, ready), reach: lerp(0.2, 0.26, ready) }),
      attack: () => ({ keys: [[-0.1, {}], [0, { lean: 0.05 }], [0.25, {}]] })
    }),
    // Kopierer: a hoodie white as copier paper, the fake logo printed on it twice over.
    copy: B => ({
      build: 'regular', face: 'smirk',
      hair: { style: 'hood', color: B.paper },
      specs: 'shades',
      top: { style: 'pullover', color: B.paper, up: true },
      bottom: { style: 'jeans', color: '#2b2b2e' },
      shoes: { style: 'sneaker', color: '#4fd1ff' },
      gear: [{ kind: 'fake', color: INK }, { kind: 'patch', color: '#4fd1ff' }],
      props: { F: { kind: 'crowbar', grip: 1.2 } },
      attack: swingClip
    }),
    // Einpeitscher: a red beanie, a bomber jacket, a megaphone at the mouth.
    shouter: () => ({
      build: 'heavy', face: 'grin', jaw: 'square',
      hair: { style: 'none', color: '#1a1512' },
      hat: { style: 'beanie', color: SIGNAL },
      specs: 'shades',
      beard: { style: 'stubble', color: '#1a1512' },
      top: { style: 'bomber', color: '#1d1d20', accent: SIGNAL },
      bottom: { style: 'cargo', color: '#3d4a66' },
      shoes: { style: 'boot', color: '#3a3a3d', sole: '#1d1d20' },
      gear: [{ kind: 'fake', color: SIGNAL }],
      props: { F: { kind: 'megaphone', abs: true, color: SIGNAL } },
      hold: () => ({ dir: 0.25, reach: 0.17 }),
      attack: () => ({ keys: [[-0.1, {}], [0, { lean: 0.15, mouth: 1, brow: -1 }], [0.35, { mouth: 0.8 }], [0.45, {}]] })
    }),
    // Flicker: a white coat with a green cross, an injector in hand.
    patch: B => ({
      build: 'lean', face: 'smirk',
      hair: { style: 'fade', color: B.hair },
      hat: { style: 'snapback', color: MINT_D },
      specs: 'shades',
      top: { style: 'longcoat', color: '#e8e6df', inner: '#3d4a66', collar: true, cross: '#2fa35f', len: 0.2 },
      bottom: { style: 'cargo', color: '#2b2b2e' },
      shoes: { style: 'sneaker', color: PAPER },
      props: { F: { kind: 'injector', color: MINT, glow: rgbOf(MINT) } },
      rest: { aF: 0.5, eF: 1.1 },
      attack: () => ({ keys: [[-0.12, { aF: 0.3, eF: 0.6 }], [0, { aF: 2.3, eF: 0.3, brow: 0.4, mouth: 0.3 }, 'out'], [0.3, {}]] })
    }),
    // Knallkopf: an orange bike helmet, a tracksuit, a firecracker to throw.
    bomber: B => ({
      build: 'lean', face: 'grin',
      hair: { style: 'none', color: B.hair },
      hat: { style: 'helmet', color: '#e0601a' },
      specs: 'shades',
      top: { style: 'trackjacket', color: '#6a2c3a', stripe: YELLOW },
      bottom: { style: 'track', color: '#1d1d20', stripe: YELLOW },
      shoes: { style: 'sneaker', color: PAPER },
      gear: [{ kind: 'fake', color: YELLOW }],
      props: { F: { kind: 'cracker', grip: 0.6 } },
      attack: swingClip
    }),
    // Schildträger: a pot on the head, a trash-can lid up front, a pipe behind it.
    shield: B => ({
      build: 'regular', face: 'frown',
      hair: { style: 'none', color: B.hair },
      hat: { style: 'pot' },
      specs: 'shades',
      top: { style: 'pullover', color: '#3d3f45' },
      bottom: { style: 'cargo', color: '#3d4a66' },
      shoes: { style: 'boot', color: '#4a4a4f', sole: '#1d1d20' },
      gear: [{ kind: 'fake', color: 'rgba(255,255,255,0.7)' }],
      props: { F: { kind: 'lid' }, B: { kind: 'crowbar', grip: 1.4 } },
      rest: { aF: 1.25, eF: 0.7 },
      attack: () => ({ keys: [[-0.2, { aB: 2.6, eB: 0.6, lean: -0.1 }], [0, { aB: 1.0, eB: 0.1, lean: 0.2, x: 0.02, mouth: 0.5 }, 'in'], [0.25, {}]] })
    }),
    // Rollerfahrer: on an e-scooter, a bike helmet, both hands on the bar.
    roller: B => ({
      build: 'lean', face: 'grin',
      hair: { style: 'none', color: B.hair },
      hat: { style: 'helmet', color: MINT_D },
      specs: 'shades',
      top: { style: 'bomber', color: '#5b2a6e', accent: MINT },
      bottom: { style: 'joggers', color: '#1d1d20' },
      shoes: { style: 'sneaker', color: PAPER, accent: MINT },
      rest: { fFx: 0.06, fBx: -0.06, lean: 0.12, handF: { x: 0.25, y: -0.64 }, handB: { x: 0.21, y: -0.62 } },
      attack: () => ({ keys: [[-0.1, {}], [0, { lean: 0.25, mouth: 0.6 }], [0.25, {}]] })
    }),
    // Der Pate: a brown fur-collared coat, a gold chain, a top hat and a cane.
    boss: B => ({
      build: 'heavy', body: { belly: 0.035 }, face: 'smirk', jaw: 'square',
      hair: { style: 'slick', color: B.hair },
      hat: { style: 'tophat', color: '#1d1d20', band: GOLD },
      specs: 'shades',
      beard: { style: 'goatee', color: B.hair },
      headgear: [{ kind: 'earring', color: GOLD }],
      top: { style: 'longcoat', color: '#6b4a2e', inner: '#1d1d20', fur: '#c9b08a', len: 0.24 },
      bottom: { style: 'slacks', color: '#2b2b2e' },
      shoes: { style: 'dress', color: '#141415' },
      gear: [{ kind: 'chain', color: GOLD, medal: true, heavy: true }],
      props: { F: { kind: 'cane', grip: 1.55 } },
      rest: { aF: 0.35, eF: 0.1 },
      attack: () => ({ keys: [[-0.22, { aF: 2.7, eF: 0.4, grip: 0.4, lean: -0.15, brow: -1 }], [0, { aF: 0.9, eF: 0.0, grip: 1.6, lean: 0.25, mouth: 0.7, brow: -1 }, 'in'], [0.25, {}]] })
    })
  };
  // Five looks of each kind: hoodie, hair, skin and paint all follow from the one number.
  const BOOTLEG_LOOKS = 5;
  const BOOTLEG_PAINTS = ['#ff4fa3', '#4fd1ff', YELLOW, MINT_D];
  const gangKey = enemy => `${enemy.type}:${enemy.id % BOOTLEG_LOOKS}:${enemy.gold ? 1 : 0}:${enemy.elite ? 1 : 0}:${enemy.copy ? 1 : 0}`;
  const gangSpecCache = new Map();
  const gangSpec = enemy => {
    const key = gangKey(enemy);
    let spec = gangSpecCache.get(key);
    if (spec) return spec;
    const look = enemy.id % BOOTLEG_LOOKS;
    // A copy wears the Kopierer's white.
    const B = { hood: enemy.gold ? GOLD : enemy.copy ? COPY_WHITE : HOODIES[look], hair: HAIRS[(look * 3) % 5], paint: BOOTLEG_PAINTS[look % BOOTLEG_PAINTS.length], paper: COPY_WHITE };
    spec = { skin: BOOTLEG_SKINS[look], gait: { rate: 12, run: 0.5, lean: 1.4 }, ...GANG_SPECS[enemy.type](B) };
    // A golden Bootleg: gold from the hood to the trousers, a chain on top.
    if (enemy.gold) Object.assign(spec, {
      top: { ...spec.top, color: GOLD },
      bottom: { ...spec.bottom, color: '#9c7a22' },
      gear: [...(spec.gear || []), { kind: 'chain', color: YELLOW, medal: true }]
    });
    // An elite: a blood-red top over black.
    else if (enemy.elite) Object.assign(spec, {
      top: { ...spec.top, color: '#8a1020' },
      bottom: { ...spec.bottom, color: '#141415' }
    });
    const base = { ...BUILDS[spec.build], ...(spec.body || {}) };
    spec.body = { ...base, head: base.head * 1.12, arm: base.arm * 1.15, wrist: base.wrist * 1.12, hand: base.hand * 1.12, leg: base.leg * 1.12, calf: base.calf * 1.12, foot: base.foot * 1.1 };
    gangSpecCache.set(key, spec);
    return spec;
  };
  // A Bootleg facing right with the feet on (0, 0), `s` tall: walking in, swinging, reeling from a hit.
  const drawBootleg = (enemy, s, gait, mem = tickMemory(springsOf(enemy)), t = clock + enemy.id) => {
    const spec = gangSpec(enemy);
    // The swing runs 0.45 s; the blow lands in the middle of it.
    const since = enemy.swing > 0 ? 0.45 - enemy.swing - 0.22 : 9;
    const st = { walk: enemy.walk, since: since >= 0 ? since : 9, cd: since < 0 ? -since : 9, engaged: since < 0, recoil: 0 };
    const go = { st, mem, fight: true, gait: enemy.type === 'roller' ? 0 : gait, t, aim: 0.1, seed: enemy.id, down: false };
    return outlined(s, () => {
      const pose = spec.hold ? aimPose(spec, go) : heroPose(spec, go);
      if (enemy.hit > 0) Object.assign(pose, { brow: 0.8, eyes: 1, mouth: 0.8 });
      if (enemy.stun > 0 || enemy.fly) Object.assign(pose, { eyes: 1, mouth: 0.6, aF: 2.6, eF: 0.4, aB: 2.8, eB: 0.4 });
      return drawRig(spec, pose, s, mem, enemy.id).tip;
    });
  };
  // Bootlegs come by the dozen, so on the map each frame of a look is painted once and then only stamped:
  // eight steps of the walk, six of the swing, standing, flinching and reeling, in sizes a few percent apart.
  const WALK_STEPS = 8;
  const SWING_STEPS = 6;
  const SPRITE_GROW = 1.08;
  const SPRITE_MAX = 360;
  // Painting a new frame costs as much as drawing the Bootleg; only a few are made per frame, the rest wait their turn.
  const SPRITES_PER_FRAME = 4;
  const bootlegSprites = new Map();
  const spriteBudget = { at: -1, left: 0 };
  const bootlegFrame = (enemy, gait) => {
    if (enemy.stun > 0 || enemy.fly) return { key: 'r', stun: 1 };
    if (enemy.hit > 0) return { key: 'h', hit: 0.1 };
    if (enemy.swing > 0) {
      const q = Math.min(SWING_STEPS - 1, Math.floor((enemy.swing / 0.45) * SWING_STEPS));
      return { key: `s${q}`, swing: ((q + 0.5) / SWING_STEPS) * 0.45 };
    }
    if (gait < 0.5 || enemy.type === 'roller') return { key: 'i' };
    const rate = gangSpec(enemy).gait.rate;
    const q = Math.floor((((enemy.walk * rate) / TAU) % 1 + 1) % 1 * WALK_STEPS) % WALK_STEPS;
    return { key: `w${q}`, walk: (((q + 0.5) / WALK_STEPS) * TAU) / rate, gait: 1 };
  };
  const paintSprite = (enemy, frame, size) => {
    const left = size * 1.05;
    const top = size * 1.6;
    const w = size * 2.1;
    const h = size * 1.8;
    const c = document.createElement('canvas');
    c.width = Math.ceil(w * dpr);
    c.height = Math.ceil(h * dpr);
    const main = ctx;
    ctx = c.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, left * dpr, top * dpr);
    try {
      const still = { ...enemy, walk: frame.walk || 0, swing: frame.swing || 0, hit: frame.hit || 0, stun: frame.stun || 0, fly: null };
      drawBootleg(still, size, frame.gait || 0, { t: clock, dt: 0, springs: {} }, 0);
    } finally {
      ctx = main;
    }
    return { c, left, top, w, h, size };
  };
  // Draws the Bootleg from its stamp when on the map; anywhere else, or while the stamp waits its turn, in full.
  const stampBootleg = (enemy, s, gait) => {
    if (ctx !== view) return drawBootleg(enemy, s, gait);
    const frame = bootlegFrame(enemy, gait);
    const step = Math.round(Math.log(s * dpr) / Math.log(SPRITE_GROW));
    const key = `${gangKey(enemy)}:${frame.key}:${step}:${dpr}`;
    let sprite = bootlegSprites.get(key);
    if (sprite) bootlegSprites.delete(key);
    else {
      if (spriteBudget.at !== clock) {
        spriteBudget.at = clock;
        spriteBudget.left = SPRITES_PER_FRAME;
      }
      if (spriteBudget.left <= 0) return drawBootleg(enemy, s, gait);
      spriteBudget.left -= 1;
      sprite = paintSprite(enemy, frame, SPRITE_GROW ** step / dpr);
      if (bootlegSprites.size >= SPRITE_MAX) bootlegSprites.delete(bootlegSprites.keys().next().value);
    }
    // The most recently used stay; the oldest go first.
    bootlegSprites.set(key, sprite);
    const k = s / sprite.size;
    ctx.drawImage(sprite.c, -sprite.left * k, -sprite.top * k, sprite.w * k, sprite.h * k);
    return null;
  };

  // ---- The hustler and the people on the street ---------------------------------------------------
  // Everybody on the block is dressed from a look: cut, hat, top, colours, beard, shades, a chain.
  Object.assign(HAIR, {
    // Long hair down to the shoulders.
    long: {
      back: (r, color, P) => {
        const sway = P.mem ? spring(P.mem, 'hair', -P.motion * 0.4, 40, 6) : 0;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(-r * 0.95, -r * 0.4);
        ctx.quadraticCurveTo(-r * (1.2 + sway * 0.4), r * 1.0, -r * (0.75 + sway * 0.6), r * 1.6);
        ctx.lineTo(r * 0.05, r * 1.2);
        ctx.lineTo(r * 0.1, -r * 0.2);
        ctx.closePath();
        ctx.fill();
      },
      front: (r, color) => {
        hairShape(r, 0.1);
        ctx.fillStyle = color;
        ctx.fill();
      }
    },
    // Buzzed short: just a shadow of hair on the skull.
    buzz: {
      front: (r, color) => {
        ctx.globalAlpha = 0.55;
        hairShape(r, -0.04, 0.02);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.globalAlpha = 1;
      }
    }
  });
  // A quilted puffer jacket with a high collar, a hood down behind it and a print of leaves if it has them.
  TOP_STYLES.puffer = {
    sleeve: 1,
    fit: 1.4,
    cuff: o => tone(o.color, -0.2),
    back: (P, o) => {
      if (!o.hood) return;
      const { b, H } = P;
      inTorso(P.sk, () => circle(-b.shoulder * H * 0.45, -b.torso * H - H * 0.03, H * 0.065, tone(o.color, -0.2)));
    },
    body: (P, o) => {
      const { b, H } = P;
      const T = b.torso * H;
      torsoPath(b, H, 0.05, 0.025);
      ctx.fillStyle = o.color;
      ctx.fill();
      ctx.save();
      torsoPath(b, H, 0.05, 0.025);
      ctx.clip();
      if (o.print && !P.small) for (let k = 0; k < 14; k += 1) {
        const x = -b.shoulder * H * 1.1 + ((k * 0.37) % 1) * b.shoulder * H * 2.2;
        const y = -T + ((k * 0.61) % 1) * T * 1.05;
        ctx.fillStyle = o.print[k % o.print.length];
        ctx.beginPath();
        ctx.ellipse(x, y, H * 0.016, H * 0.007, (k * 1.3) % Math.PI, 0, TAU);
        ctx.fill();
      }
      for (let k = 1; k < 4; k += 1) rect(-b.shoulder * H * 1.6, -T + k * T * 0.27, b.shoulder * H * 3.4, H * 0.01, tone(o.color, -0.22));
      polyline([[b.chest * H * 0.35, -T], [b.chest * H * 0.4, 0.05 * H]], H * 0.006, tone(o.color, -0.3));
      ctx.restore();
      torsoPath(b, H, 0.05, 0.025);
      shadeBack(b, H, 0.12);
      roundRect(-b.shoulder * H * 0.55, -T - H * 0.045, b.shoulder * H * 1.25, H * 0.06, H * 0.02, tone(o.color, -0.08));
    }
  };
  Object.assign(PROPS, {
    // A fold of notes with a band round the middle.
    cash: (P) => {
      const H = P.H;
      upright(P, () => {
        ctx.rotate(-0.3);
        rect(-H * 0.05, -H * 0.06, H * 0.1, H * 0.05, '#6fae6a');
        rect(-H * 0.05, -H * 0.06, H * 0.1, H * 0.012, '#9fd39a');
        rect(-H * 0.01, -H * 0.06, H * 0.02, H * 0.05, YELLOW);
      });
      return 0;
    },
    // A phone in the hand, the screen lit.
    handphone: (P) => {
      const H = P.H;
      upright(P, () => {
        roundRect(-H * 0.02, -H * 0.085, H * 0.04, H * 0.075, H * 0.008, INK);
        rect(-H * 0.014, -H * 0.078, H * 0.028, H * 0.058, '#bfe8ef');
      });
      return 0;
    },
    // A coffee to go, steaming.
    coffee: (P, o) => {
      const H = P.H;
      upright(P, () => {
        poly([[-H * 0.032, -H * 0.085], [H * 0.032, -H * 0.085], [H * 0.026, 0], [-H * 0.026, 0]], PAPER);
        rect(-H * 0.03, -H * 0.06, H * 0.06, H * 0.025, o.color);
        rect(-H * 0.036, -H * 0.095, H * 0.072, H * 0.016, INK);
        smoke(0, -H * 0.1, H * 0.35, 1);
      });
      return 0;
    },
    // A MIXEDOWN bag hanging from the hand.
    bag: (P, o) => {
      upright(P, () => shoppingBag(0, 0, P.H * 1.05, Math.sin(clock * 3) * 0.08, o.color));
      return 0;
    }
  });
  const HUSTLER_CUTS = { fade: 'fade', locken: 'curls', zoepfe: 'cornrows', dutt: 'bun', lang: 'long', buzz: 'buzz' };
  const HUSTLER_BEARDS = { voll: 'full', drei: 'stubble', kinn: 'goatee' };
  // A look's spec, built once per look.
  const lookSpecs = new WeakMap();
  const lookSpec = look => {
    let spec = lookSpecs.get(look);
    if (spec) return spec;
    const accent = look.brand === look.color ? PAPER : look.brand;
    const tops = {
      hoodie: { style: 'pullover', color: look.color, strings: PAPER, print: accent },
      bomber: { style: 'bomber', color: look.color, accent: INK, patch: accent },
      track: { style: 'trackjacket', color: look.color, stripe: accent },
      puffer: { style: 'puffer', color: look.color }
    };
    const hats = {
      cap: { style: 'fitted', color: look.brand },
      kappe: { style: 'snapback', color: look.brand },
      muetze: { style: 'beanie', color: look.brand },
      bucket: { style: 'bucket', color: look.color === PAPER ? INK : PAPER }
    };
    const base = BUILDS.regular;
    spec = {
      build: 'regular', skin: look.skin, face: 'smirk',
      body: { head: base.head * 1.14, arm: base.arm * 1.15, wrist: base.wrist * 1.12, hand: base.hand * 1.12, leg: base.leg * 1.15, calf: base.calf * 1.12, foot: base.foot * 1.1, belly: Math.max(0, look.belly - 0.95) * 0.2 },
      hair: { style: HUSTLER_CUTS[look.cut] || 'fade', color: look.hair },
      hat: hats[look.hat] || null,
      beard: HUSTLER_BEARDS[look.beard] ? { style: HUSTLER_BEARDS[look.beard], color: look.hair } : null,
      specs: look.shades ? 'shades' : null,
      top: tops[look.top] || tops.hoodie,
      bottom: { style: 'joggers', color: look.pants },
      shoes: { style: 'sneaker', color: '#fbfaf7', accent },
      gear: look.chain ? [{ kind: 'chain', color: GOLD, medal: true }] : [],
      accent,
      gait: { rate: 9, stride: 1.05 },
      attack: () => ({ keys: [[0, {}], [0.1, {}]] })
    };
    // Hair under a cap shows as a short cut.
    if (spec.hat && ['long', 'bun', 'curls'].includes(spec.hair.style)) spec.hair = { style: 'fade', color: look.hair };
    lookSpecs.set(look, spec);
    return spec;
  };
  // What the hands are doing: cheering, waving, on the phone, a coffee, a bag, cash or a phone.
  const HUSTLER_POSES = {
    cheer: t => ({ aF: 2.8 + Math.sin(t * 14) * 0.15, eF: 0.3, aB: 2.6 + Math.sin(t * 14 + 1) * 0.15, eB: 0.3, mouth: 0.6, brow: 0.6 }),
    wave: t => ({ aF: 2.6, eF: 0.5 + Math.sin(t * 10) * 0.35, mouth: 0.3 }),
    phone: () => ({ aF: 0.6, eF: 2.5, wF: -0.6 })
  };
  // The hustler, a passer-by or a runner standing on (x, y), `s` tall; `owner` keeps their springs.
  const drawPerson = (x, y, s, opts = {}) => {
    const look = opts.look || hustlerLook;
    const base = lookSpec(look);
    const gait = opts.gait === undefined ? (opts.moving ? 1 : 0) : opts.moving ? opts.gait : 0;
    let prop = null;
    if (opts.bag) prop = { kind: 'bag', color: base.accent };
    else if (opts.pose === 'coffee') prop = { kind: 'coffee', color: base.accent };
    else if (opts.pose === 'phone') prop = { kind: 'handphone' };
    else if (!opts.pose) prop = { kind: look.hold === 'cash' ? 'cash' : 'handphone' };
    const spec = { ...base, props: prop ? { F: prop } : null };
    const owner = opts.owner || look;
    const mem = tickMemory(springsOf(owner));
    const st = { walk: opts.walk || 0, since: 9, cd: 9, engaged: false };
    const go = { st, mem, fight: false, gait, t: clock + (opts.seed || 0), aim: 0.3, seed: opts.seed || 0, down: Boolean(opts.dead) };
    if (!opts.noShadow) oval(x, y, s * 0.24, s * 0.07, 'rgba(0,0,0,0.18)');
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(opts.face || 1, 1);
    outlined(s, () => {
      const pose = heroPose(spec, go);
      const extra = !opts.dead && HUSTLER_POSES[opts.pose];
      if (extra) Object.assign(pose, extra(clock + (opts.seed || 0)));
      // Carrying something: the hand comes up a little in front.
      else if (prop && !opts.dead && prop.kind !== 'bag') Object.assign(pose, { aF: lerp(0.5, pose.aF, gait), eF: lerp(1.3, pose.eF, gait) });
      drawRig(spec, pose, s, mem, opts.seed || 0);
    });
    ctx.restore();
  };
  // People on the map come by the dozen, and painting one costs as much as a building: each look is painted once
  // per step of its walk, its breath or its cheer, blinking or not, in sizes a few percent apart, and from then on
  // only stamped. A stamp is painted afresh after a while, so the coat and the bag that swing on springs keep up.
  const PERSON_STEPS = 8;
  const PERSON_GROW = 1.08;
  const PERSON_MAX = 320;
  const PERSON_KEEP = 30;
  const PERSONS_PER_FRAME = 4;
  const personSprites = new Map();
  const personBudget = { at: -1, left: 0 };
  const lookIds = new WeakMap();
  let lookCount = 0;
  const lookId = look => {
    if (!lookIds.has(look)) lookIds.set(look, (lookCount += 1));
    return lookIds.get(look);
  };
  // The step of a cycle `period` seconds long that the time `t` falls in.
  const cycleStep = (t, period, steps) => Math.floor(((((t / period) % 1) + 1) % 1) * steps) % steps;
  const personFrame = (opts, look) => {
    const t = clock + (opts.seed || 0);
    const blink = blinkAt(clock, opts.seed || 0);
    const timed = opts.pose === 'cheer' || opts.pose === 'wave';
    if (opts.moving && (opts.gait === undefined || opts.gait >= 0.5)) {
      if (timed) return null;
      const rate = lookSpec(look).gait.rate;
      return { key: `w${cycleStep(opts.walk || 0, TAU / rate, PERSON_STEPS)}${blink}`, moving: true, gait: 1 };
    }
    if (opts.pose === 'cheer') return { key: `c${cycleStep(t, TAU / 14, 4)}${blink}`, moving: false, gait: 0 };
    if (opts.pose === 'wave') return { key: `v${cycleStep(t, TAU / 10, 4)}${blink}`, moving: false, gait: 0 };
    return { key: `b${cycleStep(t, TAU / 2.1, 6)}${blink}`, moving: false, gait: 0 };
  };
  // Draws a person from a stamp on the map; anywhere else, fallen, or while the stamp waits its turn, in full.
  const stampPerson = (x, y, s, opts = {}) => {
    if (ctx !== view || opts.dead) return drawPerson(x, y, s, opts);
    const look = opts.look || hustlerLook;
    const frame = personFrame(opts, look);
    if (!frame) return drawPerson(x, y, s, opts);
    const step = Math.round(Math.log(s * dpr) / Math.log(PERSON_GROW));
    const key = `${lookId(look)}:${opts.bag ? 1 : 0}:${opts.pose || ''}:${frame.key}:${step}:${dpr}`;
    let sprite = personSprites.get(key);
    if (sprite) personSprites.delete(key);
    if (!sprite || clock - sprite.at > PERSON_KEEP) {
      if (personBudget.at !== clock) {
        personBudget.at = clock;
        personBudget.left = PERSONS_PER_FRAME;
      }
      if (personBudget.left > 0) {
        personBudget.left -= 1;
        const size = PERSON_GROW ** step / dpr;
        const left = size * 0.85;
        const top = size * 1.55;
        const c = sprite ? sprite.c : document.createElement('canvas');
        c.width = Math.ceil(left * 2 * dpr);
        c.height = Math.ceil((top + size * 0.15) * dpr);
        const main = ctx;
        ctx = c.getContext('2d');
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        try {
          drawPerson(left, top, size, { ...opts, face: 1, noShadow: true, moving: frame.moving, gait: frame.gait });
        } finally {
          ctx = main;
        }
        sprite = { c, left, top, w: c.width / dpr, h: c.height / dpr, size, at: clock };
        if (personSprites.size >= PERSON_MAX) personSprites.delete(personSprites.keys().next().value);
      } else if (!sprite) return drawPerson(x, y, s, opts);
    }
    personSprites.set(key, sprite);
    if (!opts.noShadow) oval(x, y, s * 0.24, s * 0.07, 'rgba(0,0,0,0.18)');
    const k = s / sprite.size;
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(opts.face || 1, 1);
    ctx.drawImage(sprite.c, -sprite.left * k, -sprite.top * k, sprite.w * k, sprite.h * k);
    ctx.restore();
    return null;
  };
  // ---------------------------------------------------------------------------------------------
  // Ground: your turf is a concrete yard behind a stone curb, the lots around it are staked out for sale, and the
  // city fills everything else.

  // The city around your block: always the same big-city hood. Asphalt with oil spots and cracks, white lane
  // markings, hydrants, lamps, parked cars, a hoop on the corner and neon in the windows.
  const CITY = {
    name: 'Hood', ground: '#a9acb1', lane: 'rgba(255,255,255,0.62)', lot: ['#bdb8ab', '#cdc8bb'],
    specks: [['rgba(20,20,21,0.08)', 16, 0.06], ['rgba(255,255,255,0.12)', 10, 0.04], ['rgba(20,20,21,0.12)', 3, 0.16]],
    props: { lamp: 2, hydrant: 1, bin: 1, cone: 1, car: 3, tree: 1, hoop: 1, neon: 1, vending: 1, dumpster: 1 },
    neon: ['#ff2d95', '#3ee8ff', '#ffd23c']
  };
  // Your yard: concrete slabs in two greys, darker than the street, the same at the first level as at the last.
  const YARD = ['#85888d', '#7d8085'];
  const YARD_EDGE = '#ece6d8';
  // The other gangs' ground is worn and dirtier than yours, and paved however each gang found it.
  const RIVAL_GROUND = { asphalt: '#6f7277', slabs: '#d8d3c6', cobble: '#a8a196', gravel: '#bdb29c' };
  const CURB = '#cfccc4';
  const CURB_H = 0.24;
  const visibleChunks = () => {
    const a = unproj(-cw() * 2, -cw() * 3);
    const b = unproj(W + cw() * 2, H + cw() * 2);
    return { cx0: Math.floor(a.x / CHUNK), cy0: Math.floor(a.y / CHUNK), cx1: Math.floor(b.x / CHUNK), cy1: Math.floor(b.y / CHUNK) };
  };
  const ownedChunk = (cx, cy) => landKeys.has(chunkKey(cx, cy));
  // Edges land on whole device pixels, so neighbouring chunks meet without a seam or an overlap.
  const snap = value => Math.round(value * dpr) / dpr;
  const chunkBox = (cx, cy) => {
    const a = proj(cx * CHUNK, cy * CHUNK);
    const b = proj((cx + 1) * CHUNK, (cy + 1) * CHUNK);
    return { l: snap(a.x), t: snap(a.y), r: snap(b.x), b: snap(b.y) };
  };
  const inView = (cx, cy, v) => cx >= v.cx0 && cx <= v.cx1 && cy >= v.cy0 && cy <= v.cy1;
  const drawGround = lots => {
    const u = cw();
    const v = visibleChunks();
    const t = CITY;
    rect(0, 0, W, H, t.ground);
    // The neighbourhood's own ground, chunk by chunk; land and lots cover the ones they sit on.
    const lotKeys = new Set(lots.map(lot => lot.key));
    for (let cy = v.cy0; cy <= v.cy1; cy += 1) {
      for (let cx = v.cx0; cx <= v.cx1; cx += 1) {
        if (ownedChunk(cx, cy) || lotKeys.has(chunkKey(cx, cy))) continue;
        const box = chunkBox(cx, cy);
        const w = box.r - box.l;
        const h = box.b - box.t;
        ctx.drawImage(groundPicture(w, h, t, (cx + cy) & 1), box.l, box.t, w, h);
      }
    }
    // Lane markings along every chunk border, dashed.
    const rows = [];
    const cols = [];
    for (let cy = v.cy0; cy <= v.cy1 + 1; cy += 1) rows.push(snap(proj(0, cy * CHUNK).y));
    for (let cx = v.cx0; cx <= v.cx1 + 1; cx += 1) cols.push(snap(proj(cx * CHUNK, 0).x));
    {
      const o = proj(0, 0);
      ctx.save();
      ctx.strokeStyle = t.lane;
      ctx.lineWidth = Math.max(1.5, u * 0.05);
      ctx.setLineDash([u * 0.5, u * 0.5]);
      ctx.lineDashOffset = -o.x - u * 0.25;
      ctx.beginPath();
      rows.forEach(y => {
        ctx.moveTo(0, y);
        ctx.lineTo(W, y);
      });
      ctx.stroke();
      ctx.setLineDash([u * TILT * 0.5, u * TILT * 0.5]);
      ctx.lineDashOffset = -o.y - u * TILT * 0.25;
      ctx.beginPath();
      cols.forEach(x => {
        ctx.moveTo(x, 0);
        ctx.lineTo(x, H);
      });
      ctx.stroke();
      ctx.restore();
    }
    // Lots for sale: a rim, then the ground raked into the same pattern on every tile; the chosen one tinted.
    const pad = Math.max(3, snap(u * 0.14));
    const dot = Math.max(1, snap(u * 0.05));
    lots.forEach(lot => {
      if (!inView(lot.cx, lot.cy, v)) return;
      const box = chunkBox(lot.cx, lot.cy);
      const w = box.r - box.l;
      const h = box.b - box.t;
      ctx.drawImage(lotPicture(w, h, pad, dot, t), box.l, box.t, w, h);
      if (selected && selected.kind === 'plot' && selected.key === lot.key) rect(box.l, box.t, w, h, 'rgba(186,236,183,0.45)');
    });
    // Owned land: a curb face where the block ends toward the viewer, then the yard on top.
    const curb = snap(u * CURB_H);
    for (let cy = v.cy0; cy <= v.cy1; cy += 1) {
      for (let cx = v.cx0; cx <= v.cx1; cx += 1) {
        if (!ownedChunk(cx, cy)) continue;
        const box = chunkBox(cx, cy);
        const w = box.r - box.l;
        if (!ownedChunk(cx, cy + 1)) {
          rect(box.l, box.b + curb, w, snap(u * 0.3), 'rgba(20,20,21,0.1)');
          rect(box.l, box.b, w, curb, CURB);
          rect(box.l, box.b + curb - 1, w, 1, 'rgba(20,20,21,0.25)');
        }
        const h = box.b - box.t;
        ctx.drawImage(heist ? rivalPicture(heist.ground, w, h, (cx + cy) & 1) : yardPicture(w, h), box.l, box.t, w, h);
      }
    }
    // Stone curbs along every outer edge of the land.
    for (let cy = v.cy0; cy <= v.cy1; cy += 1) {
      for (let cx = v.cx0; cx <= v.cx1; cx += 1) {
        if (!ownedChunk(cx, cy)) continue;
        const box = chunkBox(cx, cy);
        const w = box.r - box.l;
        const h = box.b - box.t;
        const side = Math.max(2, snap(u * 0.1));
        const curbs = [
          [!ownedChunk(cx, cy - 1), box.l, box.t, w, side, box.l, box.t + side, w, 1],
          [!ownedChunk(cx, cy + 1), box.l, box.b - side, w, side, box.l, box.b - side - 1, w, 1],
          [!ownedChunk(cx - 1, cy), box.l, box.t, side, h, box.l + side, box.t, 1, h],
          [!ownedChunk(cx + 1, cy), box.r - side, box.t, side, h, box.r - side - 1, box.t, 1, h]
        ];
        curbs.forEach(([open, x, y, rw, rh, sx, sy, sw, sh]) => {
          if (!open) return;
          rect(x, y, rw, rh, YARD_EDGE);
          rect(sx, sy, sw, sh, 'rgba(20,20,21,0.12)');
        });
      }
    }
  };
  // Pictures painted once per size and kept: the oldest goes when the cache is full.
  const cachedPicture = (cache, key, w, h, paint) => {
    if (cache.has(key)) return cache.get(key);
    let pic;
    if (cache.size >= 8) {
      const [oldest] = cache.keys();
      pic = cache.get(oldest);
      cache.delete(oldest);
    } else pic = document.createElement('canvas');
    pic.width = Math.round(w * dpr);
    pic.height = Math.round(h * dpr);
    const saved = ctx;
    ctx = pic.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    try {
      paint();
    } finally {
      ctx = saved;
    }
    cache.set(key, pic);
    return pic;
  };
  // The city's ground for one chunk: asphalt with grit, light chips and oil spots. Two variants alternate,
  // so the pattern does not march in lockstep.
  const groundPics = new Map();
  const groundPicture = (w, h, t, variant) => cachedPicture(groundPics, `${t.name}:${variant}:${Math.round(w * dpr)}x${Math.round(h * dpr)}`, w, h, () => {
    const rand = mulberry32(hashText(`${t.name}:${variant}`));
    const tw = w / CHUNK;
    rect(0, 0, w, h, t.ground);
    t.specks.forEach(([color, count, size]) => {
      ctx.fillStyle = color;
      for (let k = 0; k < count; k += 1) {
        const x = rand() * w;
        const y = rand() * h;
        const s = tw * size * (0.7 + rand() * 0.6);
        ctx.beginPath();
        ctx.ellipse(x, y, s, s * TILT, 0, 0, TAU);
        ctx.fill();
      }
    });
  });
  // One chunk of your yard: concrete slabs with dark joints, a soft light along their top edge, grit, a few
  // cracks and oil stains.
  const yardPics = new Map();
  const yardPicture = (w, h) => cachedPicture(yardPics, `${Math.round(w * dpr)}x${Math.round(h * dpr)}`, w, h, () => {
    const rand = mulberry32(4711);
    const tw = w / CHUNK;
    const th = h / CHUNK;
    const joint = Math.max(1, snap(tw * 0.025));
    rect(0, 0, w, h, YARD[0]);
    for (let j = 0; j < CHUNK; j += 1) {
      for (let i = 0; i < CHUNK; i += 1) {
        const x = snap(tw * i);
        const y = snap(th * j);
        const cw1 = snap(tw * (i + 1)) - x;
        const ch1 = snap(th * (j + 1)) - y;
        if ((i + j) % 2) rect(x, y, cw1, ch1, YARD[1]);
        rect(x, y, cw1, joint, 'rgba(20,20,21,0.22)');
        rect(x, y, joint, ch1, 'rgba(20,20,21,0.22)');
        rect(x, y + joint, cw1, Math.max(1, snap(th * 0.05)), 'rgba(255,255,255,0.08)');
        for (let k = 0; k < 7; k += 1) {
          const s = tw * (0.012 + rand() * 0.02);
          rect(x + rand() * cw1, y + rand() * ch1, s, s * TILT, rand() < 0.5 ? 'rgba(20,20,21,0.16)' : 'rgba(255,255,255,0.14)');
        }
        const mark = rand();
        if (mark < 0.12) {
          ctx.fillStyle = 'rgba(20,20,21,0.13)';
          ctx.beginPath();
          ctx.ellipse(x + cw1 * (0.3 + rand() * 0.4), y + ch1 * (0.3 + rand() * 0.4), tw * (0.12 + rand() * 0.1), th * 0.1, 0, 0, TAU);
          ctx.fill();
        } else if (mark < 0.26) {
          ctx.strokeStyle = 'rgba(20,20,21,0.26)';
          ctx.lineWidth = Math.max(1, tw * 0.012);
          ctx.beginPath();
          let cx = x + cw1 * (0.15 + rand() * 0.3);
          let cy = y + ch1 * (0.15 + rand() * 0.3);
          ctx.moveTo(cx, cy);
          for (let n = 0; n < 3; n += 1) {
            cx += tw * (0.08 + rand() * 0.14);
            cy += th * (rand() * 0.3 - 0.05);
            ctx.lineTo(cx, cy);
          }
          ctx.stroke();
        }
      }
    }
  });
  // One chunk of a rival's lot: dark asphalt, light slabs with a grid of joints, a cobbled yard in
  // staggered rows, or a gravel yard; grit over all of it. Two variants alternate.
  const rivalPics = new Map();
  const rivalPicture = (ground, w, h, variant) => cachedPicture(rivalPics, `${ground}:${variant}:${Math.round(w * dpr)}x${Math.round(h * dpr)}`, w, h, () => {
    const rand = mulberry32(hashText(`${ground}:${variant}`));
    const tw = w / CHUNK;
    const th = h / CHUNK;
    const dark = 'rgba(20,20,21,0.1)';
    rect(0, 0, w, h, RIVAL_GROUND[ground]);
    if (ground === 'slabs') {
      ctx.fillStyle = 'rgba(20,20,21,0.07)';
      for (let i = 1; i < CHUNK; i += 1) {
        ctx.fillRect(snap(w * (i / CHUNK)), 0, 1, h);
        ctx.fillRect(0, snap(h * (i / CHUNK)), w, 1);
      }
    } else if (ground === 'cobble') {
      // Stones half a tile wide in rows a third of a tile deep, every other row shifted by half a stone.
      const sw = tw / 2;
      const sh = th / 3;
      for (let j = 0; j < CHUNK * 3; j += 1) {
        for (let i = -1; i < CHUNK * 2; i += 1) {
          const x = snap(i * sw + (j % 2) * sw * 0.5);
          const y = snap(j * sh);
          const shade = rand();
          if (shade < 0.3) rect(x, y, snap(sw), snap(sh), shade < 0.15 ? 'rgba(20,20,21,0.06)' : 'rgba(255,255,255,0.07)');
          rect(x, y, 1, snap(sh), dark);
        }
        rect(0, snap(j * sh), w, 1, dark);
      }
    }
    const grit = ground === 'gravel' ? 260 : 60;
    for (let k = 0; k < grit; k += 1) {
      const s = tw * (0.012 + rand() * (ground === 'gravel' ? 0.035 : 0.02));
      rect(rand() * w, rand() * h, s, s * TILT, rand() < 0.55 ? 'rgba(20,20,21,0.16)' : 'rgba(255,255,255,0.2)');
    }
  });
  // Every lot looks the same, so it is painted once per size and copied; a few sizes are kept for zooming.
  const lotPics = new Map();
  const lotPicture = (w, h, pad, dot, t) => cachedPicture(lotPics, `${t.name}:${Math.round(w * dpr)}x${Math.round(h * dpr)}:${pad}:${dot}`, w, h, () => {
    rect(0, 0, w, h, t.lot[0]);
    rect(pad, pad, w - pad * 2, h - pad * 2, t.lot[1]);
    ctx.strokeStyle = 'rgba(20,20,21,0.16)';
    ctx.lineWidth = 1;
    ctx.strokeRect(pad + 0.5, pad + 0.5, w - pad * 2 - 1, h - pad * 2 - 1);
    ctx.strokeStyle = 'rgba(255,255,255,0.4)';
    ctx.strokeRect(0.5, 0.5, w - 1, h - 1);
    const tw = w / CHUNK;
    const th = h / CHUNK;
    for (let j = 0; j < CHUNK; j += 1) {
      for (let i = 0; i < CHUNK; i += 1) {
        const tl = tw * i;
        const tt = th * j;
        rect(snap(tl + tw / 2) - dot / 2, snap(tt + th / 2) - dot / 2, dot, dot, 'rgba(20,20,21,0.13)');
        [[0.22, 0.25], [0.78, 0.25], [0.22, 0.75], [0.78, 0.75]].forEach(([fx, fy]) => rect(snap(tl + tw * fx) - dot / 2, snap(tt + th * fy) - dot / 2, dot, dot, 'rgba(255,255,255,0.42)'));
      }
    }
  });
  // Four traffic cones stake out the corners of every lot, mirrored around its middle.
  const LOT_MARKS = [[0.45, 0.55], [CHUNK - 0.45, 0.55], [0.45, CHUNK - 0.3], [CHUNK - 0.45, CHUNK - 0.3]];
  const lotMarks = lots => lots.flatMap(({ cx, cy }) => LOT_MARKS.map(([x, y]) => ({ x: cx * CHUNK + x, y: cy * CHUNK + y })));
  const drawLotMark = mark => {
    const at = proj(mark.x, mark.y);
    const x = snap(at.x);
    const y = snap(at.y);
    const s = cw() * 0.9;
    oval(x, y, s * 0.14, s * 0.045, 'rgba(0,0,0,0.14)');
    rect(x - snap(s * 0.11), y - snap(s * 0.04), snap(s * 0.11) * 2, snap(s * 0.04), '#2b2b2e');
    poly([[x - s * 0.085, y - snap(s * 0.04)], [x - s * 0.022, y - s * 0.36], [x + s * 0.022, y - s * 0.36], [x + s * 0.085, y - snap(s * 0.04)]], '#ff7a1a');
    rect(x - s * 0.06, y - s * 0.2, s * 0.12, s * 0.05, PAPER);
  };

  // Land for sale: the lot is tappable and carries a sign with the price, outside raids and building.
  const plotRects = () => {
    if (!founded() || heist || (raid && raid.phase !== 'end') || ghost) return [];
    const price = landPrice();
    return forSale().map(plot => {
      const box = chunkBox(plot.cx, plot.cy);
      return { ...plot, a: { x: box.l, y: box.t }, b: { x: box.r, y: box.b }, price };
    });
  };
  // HUD-style plates on the map: ink outline, a hard edge underneath, like the buttons.
  const plate = (x, y, w, h, r, fill, edge = 3) => {
    roundRect(x, y + edge, w, h, r, INK);
    roundRect(x, y, w, h, r, INK);
    roundRect(x + 2, y + 2, w - 4, h - 4, Math.max(1, r - 2), fill);
  };
  // A small for-sale stake in the middle of every plot: quiet while out of reach, mint once you can pay.
  const drawSaleSign = plot => {
    if (raid && raid.phase !== 'end') return;
    const u = cw();
    const at = proj(plot.cx * CHUNK + CHUNK / 2, plot.cy * CHUNK + CHUNK / 2);
    const afford = S.cash >= plot.price;
    const size = clamp(u * 0.28, 15, 20);
    ctx.font = `700 ${Math.round(size * 0.6)}px ${FONT}`;
    const text = fmt(plot.price);
    const w = size * 1.45 + ctx.measureText(text).width;
    const top = at.y - size * 1.55 + (afford ? Math.sin(clock * 2.4 + plot.cx + plot.cy) * 1.5 : 0);
    ctx.save();
    ctx.globalAlpha = afford ? 1 : 0.72;
    oval(at.x, at.y, size * 0.4, size * 0.1, 'rgba(0,0,0,0.12)');
    rect(at.x - 1, top + size, 2, at.y - top - size, 'rgba(20,20,21,0.45)');
    roundRect(at.x - w / 2, top, w, size, size * 0.3, afford ? MINT_D : 'rgba(20,20,21,0.2)');
    roundRect(at.x - w / 2 + 1.5, top + 1.5, w - 3, size - 3, size * 0.3 - 1.5, afford ? MINT : PAPER);
    drawCash(ctx, at.x - w / 2 + size * 0.55, top + size / 2, size * 0.2, false);
    label(text, at.x - w / 2 + size * 0.98, top + size / 2 + 1, Math.round(size * 0.6), afford ? INK : 'rgba(20,20,21,0.6)', 'left');
    ctx.restore();
  };
  const roundRect = (x, y, w, h, r, color) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
  };

  // From the fourth tier the yard carries street art sprayed flat on the slabs: the wordmark outlined in ink, the
  // ring of the logo in two colours, or a row of stencil stars; one more piece per chunk at the fifth tier.
  const TURF_ART = {
    logo: (w, [paint]) => drawLogo(0, 0, w * 1.6, paint, w * 0.012),
    ring: (w, [paint, rim]) => {
      logoRing(0, 0, w * 0.42, w * 0.3, w * 0.09, rim);
      logoRing(0, 0, w * 0.42, w * 0.3, w * 0.05, paint);
    },
    stars: (w, [paint, rim]) => [-1, 0, 1].forEach(k => {
      ctx.beginPath();
      starPath(ctx, k * w * 0.36, k ? w * 0.06 : -w * 0.04, w * (k ? 0.14 : 0.2), w * (k ? 0.06 : 0.085));
      ctx.fillStyle = k ? rim : paint;
      ctx.fill();
    })
  };
  // Each piece is sprayed once into its own picture, at the size of the zoom and a few turns, and then laid on the
  // ground at its own size; the pictures used least recently go first.
  const ART_TURNS = 5;
  const ART_MAX = 120;
  const artSprites = new Map();
  const artSprite = (kind, w, paint, turn) => {
    const key = `${kind}:${Math.round(w)}:${paint.join()}:${turn}:${dpr}`;
    let sprite = artSprites.get(key);
    if (sprite) {
      artSprites.delete(key);
      artSprites.set(key, sprite);
      return sprite;
    }
    if (artSprites.size >= ART_MAX) artSprites.delete(artSprites.keys().next().value);
    const rot = ((turn / (ART_TURNS - 1)) - 0.5) * 0.5;
    const size = Math.ceil(w * 1.9);
    const c = document.createElement('canvas');
    c.width = Math.ceil(size * dpr);
    c.height = Math.ceil(size * TILT * dpr);
    const main = ctx;
    ctx = c.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.translate(size / 2, (size * TILT) / 2);
    ctx.scale(1, TILT);
    ctx.rotate(rot);
    try {
      TURF_ART[kind](w, paint);
    } finally {
      ctx = main;
    }
    sprite = { c, w: size, h: size * TILT };
    artSprites.set(key, sprite);
    return sprite;
  };
  const drawTurfArt = () => {
    const tier = vibe();
    if (tier < 3) return;
    const u = cw();
    const brand = hustlerLook.brand === INK ? SIGNAL : hustlerLook.brand;
    const paints = [[brand, INK], ['#ff4fa3', '#4fd1ff'], [YELLOW, INK], ['#4fd1ff', PAPER]];
    const kinds = Object.keys(TURF_ART);
    const v = visibleChunks();
    ctx.save();
    ctx.globalAlpha = 0.55;
    S.land.forEach(key => {
      const [cx, cy] = key.split(',').map(Number);
      if (!inView(cx, cy, v)) return;
      const rand = mulberry32(hashText(`${S.seed}:art:${key}`));
      for (let k = 0; k < (tier >= 4 ? 2 : 1); k += 1) {
        const at = proj(cx * CHUNK + 1.5 + rand() * (CHUNK - 3), cy * CHUNK + 1.2 + rand() * (CHUNK - 2.4));
        const grow = 1 + rand() * 0.3;
        const turn = Math.floor(rand() * ART_TURNS);
        const sprite = artSprite(kinds[Math.floor(rand() * kinds.length)], u * 1.4, paints[Math.floor(rand() * paints.length)], turn);
        ctx.drawImage(sprite.c, at.x - (sprite.w * grow) / 2, at.y - (sprite.h * grow) / 2, sprite.w * grow, sprite.h * grow);
      }
    });
    ctx.restore();
  };
  // What stands about the city: picked by weight, and the same every visit.
  const props = new Map();
  const CAR_COLORS = ['#f4f3ef', '#2b2b2e', '#c2330f', '#2f4f7a', '#d9b26a', '#55575c'];
  const propsOf = (cx, cy) => {
    const t = CITY;
    const key = chunkKey(cx, cy);
    if (!props.has(key)) {
      const rand = mulberry32(hashText(`${S.seed}:${key}`));
      const kinds = Object.entries(t.props);
      const total = kinds.reduce((sum, [, weight]) => sum + weight, 0);
      const pickKind = () => {
        let left = rand() * total;
        return (kinds.find(([, weight]) => (left -= weight) < 0) || kinds[0])[0];
      };
      const list = [];
      const n = 2 + Math.floor(rand() * 4);
      for (let i = 0; i < n; i += 1) {
        const kind = pickKind();
        // Cars park in a row just above the lane marking, everything else stands where there is room.
        const x = cx * CHUNK + (kind === 'car' ? 0.4 + rand() * (CHUNK - 2.2) : 0.6 + rand() * (CHUNK - 1.2));
        const y = cy * CHUNK + (kind === 'car' ? CHUNK - 1.05 : 0.8 + rand() * (CHUNK - 2.4));
        if (list.some(p => Math.abs(p.x - x) < 1.8 && Math.abs(p.y - y) < 1)) continue;
        list.push({ kind, x, y, tone: oneOf(rand, CAR_COLORS), glow: oneOf(rand, t.neon), flip: rand() < 0.5, s: 0.85 + rand() * 0.3 });
      }
      props.set(key, list);
    }
    return props.get(key);
  };
  const PROP_DRAW = {
    lamp: (p, at, s) => {
      const dir = p.flip ? -1 : 1;
      rect(at.x - s * 0.03, at.y - s * 1.45, s * 0.06, s * 1.45, '#4a4d52');
      rect(at.x - s * 0.05, at.y - s * 0.2, s * 0.1, s * 0.2, '#3a3d42');
      line(at.x, at.y - s * 1.42, at.x + dir * s * 0.3, at.y - s * 1.42, '#4a4d52', Math.max(1.5, s * 0.04));
      oval(at.x + dir * s * 0.33, at.y - s * 1.38, s * 0.12, s * 0.05, '#2b2b2e');
      oval(at.x + dir * s * 0.33, at.y - s * 1.35, s * 0.08, s * 0.03, '#ffe9a8');
      oval(at.x + dir * s * 0.33, at.y - s * 0.02, s * 0.3, s * 0.1, 'rgba(255,233,168,0.25)');
    },
    hydrant: (p, at, s) => {
      rect(at.x - s * 0.07, at.y - s * 0.3, s * 0.14, s * 0.3, '#d8432a');
      rect(at.x - s * 0.07, at.y - s * 0.3, s * 0.04, s * 0.3, 'rgba(255,255,255,0.18)');
      circle(at.x, at.y - s * 0.3, s * 0.07, '#d8432a');
      rect(at.x - s * 0.11, at.y - s * 0.21, s * 0.22, s * 0.05, '#b5321c');
      rect(at.x - s * 0.09, at.y - s * 0.03, s * 0.18, s * 0.03, '#b5321c');
      circle(at.x, at.y - s * 0.37, s * 0.025, '#b5321c');
    },
    bin: (p, at, s) => {
      rect(at.x - s * 0.12, at.y - s * 0.34, s * 0.24, s * 0.34, '#f07c2a');
      rect(at.x - s * 0.12, at.y - s * 0.34, s * 0.06, s * 0.34, 'rgba(255,255,255,0.15)');
      rect(at.x - s * 0.14, at.y - s * 0.38, s * 0.28, s * 0.05, '#2b2b2e');
      rect(at.x - s * 0.06, at.y - s * 0.25, s * 0.12, s * 0.03, '#2b2b2e');
    },
    cone: (p, at, s) => {
      rect(at.x - s * 0.1, at.y - s * 0.03, s * 0.2, s * 0.03, '#2b2b2e');
      poly([[at.x - s * 0.08, at.y - s * 0.03], [at.x - s * 0.02, at.y - s * 0.34], [at.x + s * 0.02, at.y - s * 0.34], [at.x + s * 0.08, at.y - s * 0.03]], '#ff7a1a');
      rect(at.x - s * 0.055, at.y - s * 0.19, s * 0.11, s * 0.05, PAPER);
    },
    // A young tree in a concrete planter.
    tree: (p, at, s) => {
      const pot = block(p.x - 0.28, p.y - 0.4, 0.56, 0.4, 0.26, '#a39e92', '#c9c5bb');
      rect(pot.l + 2, pot.t + 2, pot.w - 4, pot.m - pot.t - 2, '#6b5a43');
      const sway = Math.sin(clock * 0.8 + p.x) * s * 0.015;
      const ty = pot.t + (pot.m - pot.t) / 2;
      rect(at.x - s * 0.03, ty - s * 0.45, s * 0.06, s * 0.45, '#8a7356');
      circle(at.x + sway, ty - s * 0.62, s * 0.26, '#7fb07a');
      circle(at.x - s * 0.1 + sway, ty - s * 0.72, s * 0.19, '#9cc98f');
      circle(at.x + s * 0.09 + sway, ty - s * 0.78, s * 0.15, '#9cc98f');
    },
    // A streetball hoop: pole, backboard, orange rim and the net.
    hoop: (p, at, s) => {
      const d = p.flip ? -1 : 1;
      rect(at.x - s * 0.035, at.y - s * 1.5, s * 0.07, s * 1.5, '#3a3d42');
      const bx = at.x + d * s * 0.16;
      rect(bx - s * 0.24, at.y - s * 1.78, s * 0.48, s * 0.32, PAPER);
      ctx.strokeStyle = SIGNAL;
      ctx.lineWidth = Math.max(1, s * 0.025);
      ctx.strokeRect(bx - s * 0.09, at.y - s * 1.68, s * 0.18, s * 0.13);
      ctx.strokeStyle = INK;
      ctx.strokeRect(bx - s * 0.24, at.y - s * 1.78, s * 0.48, s * 0.32);
      const ry = at.y - s * 1.5;
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.lineWidth = Math.max(1, s * 0.015);
      ctx.beginPath();
      [-1, -0.33, 0.33, 1].forEach(k => {
        ctx.moveTo(bx + k * s * 0.11, ry);
        ctx.lineTo(bx + k * s * 0.06, ry + s * 0.16);
      });
      ctx.stroke();
      oval(bx, ry, s * 0.12, s * 0.04, 'rgba(0,0,0,0)');
      ctx.strokeStyle = '#ff7a1a';
      ctx.lineWidth = Math.max(1.5, s * 0.035);
      ctx.beginPath();
      ctx.ellipse(bx, ry, s * 0.12, s * 0.04, 0, 0, TAU);
      ctx.stroke();
    },
    neon: (p, at, s) => {
      rect(at.x - s * 0.22, at.y - s * 0.5, s * 0.03, s * 0.5, '#2b2b2e');
      rect(at.x + s * 0.19, at.y - s * 0.5, s * 0.03, s * 0.5, '#2b2b2e');
      glowAt(at.x, at.y - s * 0.68, s * 0.6, 'rgba(255,255,255,ALPHA)', 0.12);
      roundRect(at.x - s * 0.32, at.y - s * 0.86, s * 0.64, s * 0.36, s * 0.05, '#141416');
      glowLabel(p.glow, s * 0.12, p.flip ? 'MXD' : 'OPEN', at.x, at.y - s * 0.68, s * 0.17, p.glow, 'center', 800);
    },
    vending: p => {
      const b = block(p.x - 0.25, p.y - 0.35, 0.5, 0.35, 0.85, '#d9dde3', p.glow === '#3ee8ff' ? '#2f6fff' : '#e8402f');
      rect(b.l + b.w * 0.1, b.m + b.fh * 0.08, b.w * 0.55, b.fh * 0.55, '#dff6ff');
      [0.2, 0.36, 0.52].forEach(fy => rect(b.l + b.w * 0.14, b.m + b.fh * fy, b.w * 0.47, Math.max(1, b.fh * 0.04), 'rgba(20,20,21,0.25)'));
      rect(b.l + b.w * 0.74, b.m + b.fh * 0.12, b.w * 0.14, b.fh * 0.3, '#141416');
      rect(b.l + b.w * 0.1, b.m + b.fh * 0.74, b.w * 0.55, b.fh * 0.12, '#141416');
    },
    // A green skip with its lid propped open and a bag on top.
    dumpster: p => {
      const b = block(p.x - 0.5, p.y - 0.45, 1, 0.45, 0.42, '#2f6b45', '#2a5e3d');
      rect(b.l, b.m, b.w, Math.max(1, b.fh * 0.12), 'rgba(255,255,255,0.12)');
      [0.3, 0.7].forEach(fx => rect(b.l + b.w * fx, b.m + b.fh * 0.2, Math.max(1, b.w * 0.02), b.fh * 0.6, 'rgba(20,20,21,0.25)'));
      poly([[b.l, b.t], [b.r, b.t], [b.r - b.w * 0.05, b.t - (b.m - b.t) * 0.9], [b.l + b.w * 0.05, b.t - (b.m - b.t) * 0.9]], '#24543a');
      circle(b.l + b.w * 0.62, b.t + (b.m - b.t) * 0.2, b.w * 0.14, '#2b2b2e');
    }
  };
  const drawProp = p => {
    const u = cw();
    const at = proj(p.x, p.y);
    const s = u * p.s;
    if (p.kind === 'car') {
      const body = block(p.x, p.y, 1.6, 0.8, 0.32, p.tone, p.tone, 0.1);
      rect(body.l, body.m, body.w, body.fh, 'rgba(0,0,0,0.14)');
      rect(body.l + body.w * 0.04, body.m + body.fh * 0.25, body.w * 0.1, body.fh * 0.3, p.flip ? '#ffe9a8' : SIGNAL);
      rect(body.r - body.w * 0.14, body.m + body.fh * 0.25, body.w * 0.1, body.fh * 0.3, p.flip ? SIGNAL : '#ffe9a8');
      const cab = block(p.x + 0.35, p.y + 0.12, 0.9, 0.6, 0.58, p.tone, '#5f7480', 0.32);
      rect(cab.l + cab.w * 0.48, cab.m, cab.w * 0.04, cab.fh, p.tone);
      rect(cab.l, cab.m, cab.w, 1.5, 'rgba(255,255,255,0.35)');
      [0.18, 0.82].forEach(fx => {
        const w = body.at(fx, 1);
        oval(w.x, w.y, u * 0.13, u * 0.1, INK);
        oval(w.x, w.y - 1, u * 0.05, u * 0.04, '#8d9399');
      });
      return;
    }
    oval(at.x, at.y, s * 0.2, s * 0.06, 'rgba(0,0,0,0.13)');
    PROP_DRAW[p.kind](p, at, s);
  };

  // ---------------------------------------------------------------------------------------------
  // The scene, sorted back to front, then shots, effects and tags on top.

  const HEIGHT = { plakat: 1.1, sticker: 1.8, wall: 1.2, studio: 1.4, party: 1.3, billboard: 2.1, buehne: 2.5, court: 1.7, skate: 1.2, radio: 2.4, stand: 1.3, shop: 1.6, truck: 1.3, popup: 1.5, zelt: 1.8, flagship: 2.3, automat: 1.3, concept: 1.9, halle: 2.1, caruso: 2.15, retro: 2.2, zola: 2.7, kuza: 2.0, blade: 2.3, capi: 2.25, johno: 2.2, maliq: 2.2, polarise: 2.25, bigtoe: 2.2 };
  // How tall a thing stands, in tiles: the Base grows with its tier, the rest a little per level.
  const heightOf = (type, level) => {
    if (type === 'hq') return hqHeight(level) + (tierOf(level) >= 3 ? 0.75 : 0.1);
    return (HEIGHT[type] || 1) + Math.min(1, level * 0.03);
  };
  const screenBox = item => {
    const c = kindOf(item) === 'crew' ? center(item) : null;
    const f = c ? { x: c.x - 0.5, y: c.y - 0.5, w: 1, d: 1 } : footprint(item);
    const hgt = heightOf(item.type, item.level);
    const a = proj(f.x, f.y, hgt);
    const b = proj(f.x + f.w, f.y + f.d);
    return { l: a.x, t: a.y, r: b.x, b: b.y };
  };
  // Where a thing stands in the back-to-front order of the scene: its front edge.
  const depthOf = item => (kindOf(item) === 'crew' ? center(item).y + 0.5 : item.y + info(item).d);
  // Promo collects fans and a stand collects cash until you pick it up; the On-Demand Shop pays in by itself.
  // The tag only shows once a good haul has built up, a twenty-fifth of what fits, a few minutes' worth.
  const TAG_SHARE = 0.04;
  const tagOf = item => {
    const kind = kindOf(item);
    if (item.ruin) return raid && raid.phase !== 'end' ? null : repairTag(item);
    if ((kind !== 'promo' && kind !== 'sale') || TYPES[item.type].online || !standing(item) || (raid && raid.phase !== 'end')) return null;
    const stock = Math.floor(item.stock || 0);
    if (stock < Math.max(1, capOf(item) * TAG_SHARE)) return null;
    const box = screenBox(item);
    const size = clamp(cw() * 0.38, 22, 32);
    ctx.font = `700 ${Math.round(size * 0.5)}px ${FONT}`;
    const text = fmt(stock);
    const w = size * 1.15 + ctx.measureText(text).width + size * 0.45;
    const bob = Math.sin(clock * 2.6 + item.id) * 2;
    const x = (box.l + box.r) / 2 - w / 2;
    // The tip of the tag sits just above the roof line, not above the full hit box.
    const y = box.t + cw() * 0.6 - size - 9 + bob;
    return { x, y, w, h: size, text, full: stock >= capOf(item) - 1, size, fans: kind === 'promo' };
  };
  // A ruin carries its repair price: tap it, pay, and it rebuilds.
  const repairTag = item => {
    const box = screenBox(item);
    const size = clamp(cw() * 0.38, 22, 32);
    ctx.font = `700 ${Math.round(size * 0.5)}px ${FONT}`;
    const text = fmt(repairCost(item));
    const w = size * 1.15 + ctx.measureText(text).width + size * 0.45;
    const bob = Math.sin(clock * 2.6 + item.id) * 2;
    return { x: (box.l + box.r) / 2 - w / 2, y: box.t + cw() * 0.9 - size + bob, w, h: size, text, size, repair: true };
  };
  const drawWrench = (x, y, r) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(-Math.PI / 4);
    roundRect(-r * 0.22, -r * 0.2, r * 0.44, r * 1.25, r * 0.2, PAPER);
    circle(0, -r * 0.55, r * 0.55, PAPER);
    rect(-r * 0.18, -r * 1.15, r * 0.36, r * 0.55, SIGNAL);
    ctx.restore();
  };
  const drawTag = tag => {
    if (tag.repair) {
      poly([[tag.x + tag.w / 2 - 6, tag.y + tag.h], [tag.x + tag.w / 2 + 6, tag.y + tag.h], [tag.x + tag.w / 2, tag.y + tag.h + 9]], INK);
      plate(tag.x, tag.y, tag.w, tag.h, tag.h * 0.32, SIGNAL);
      poly([[tag.x + tag.w / 2 - 3.5, tag.y + tag.h - 2], [tag.x + tag.w / 2 + 3.5, tag.y + tag.h - 2], [tag.x + tag.w / 2, tag.y + tag.h + 3.5]], SIGNAL);
      drawWrench(tag.x + tag.size * 0.6, tag.y + tag.h * 0.5, tag.size * 0.3);
      label(tag.text, tag.x + tag.size * 1.02, tag.y + tag.h / 2 + 1, Math.round(tag.size * 0.5), PAPER, 'left');
      return;
    }
    const fill = tag.full ? MINT : '#ffffff';
    poly([[tag.x + tag.w / 2 - 6, tag.y + tag.h], [tag.x + tag.w / 2 + 6, tag.y + tag.h], [tag.x + tag.w / 2, tag.y + tag.h + 9]], INK);
    plate(tag.x, tag.y, tag.w, tag.h, tag.h * 0.32, fill);
    poly([[tag.x + tag.w / 2 - 3.5, tag.y + tag.h - 2], [tag.x + tag.w / 2 + 3.5, tag.y + tag.h - 2], [tag.x + tag.w / 2, tag.y + tag.h + 3.5]], fill);
    (tag.fans ? drawFan : drawCash)(ctx, tag.x + tag.size * 0.6, tag.y + tag.h * 0.5, tag.size * 0.24);
    label(tag.text, tag.x + tag.size * 1.02, tag.y + tag.h / 2 + 1, Math.round(tag.size * 0.5), INK, 'left');
  };
  const drawHpBar = item => {
    if (!raid || raid.phase === 'end' || !standing(item)) return;
    const hp = item.hp === undefined ? maxHp(item) : item.hp;
    const max = maxHp(item);
    if (hp >= max) return;
    const box = screenBox(item);
    const w = clamp((box.r - box.l) * 0.6, 24, 90);
    const x = (box.l + box.r) / 2 - w / 2;
    const y = box.t - 10;
    roundRect(x - 2, y - 2, w + 4, 9, 4.5, INK);
    roundRect(x, y, w, 5, 2.5, '#4a4a4f');
    roundRect(x, y, Math.max(3, w * clamp(hp / max, 0, 1)), 5, 2.5, hp / max < 0.35 ? SIGNAL : MINT_D);
  };
  const drawFootprint = (x, y, w, d, fill, stroke) => {
    const a = proj(x, y);
    const b = proj(x + w, y + d);
    rect(a.x, a.y, b.x - a.x, b.y - a.y, fill);
    ctx.strokeStyle = stroke;
    ctx.lineWidth = 2;
    ctx.strokeRect(a.x + 1, a.y + 1, b.x - a.x - 2, b.y - a.y - 2);
  };
  // Where the ghost would land: every tile green or red, the old spot dashed, a shadow while lifted.
  const drawGhostGround = () => {
    const t = TYPES[ghost.type];
    const from = ghost.moving && itemById(ghost.moving);
    if (from && (from.x !== ghost.x || from.y !== ghost.y)) {
      const a = proj(from.x, from.y);
      const b = proj(from.x + t.w, from.y + t.d);
      ctx.setLineDash([6, 5]);
      ctx.strokeStyle = 'rgba(20,20,21,0.4)';
      ctx.lineWidth = 2;
      ctx.strokeRect(a.x + 2, a.y + 2, b.x - a.x - 4, b.y - a.y - 4);
      ctx.setLineDash([]);
    }
    for (let tx = ghost.x; tx < ghost.x + t.w; tx += 1) {
      for (let ty = ghost.y; ty < ghost.y + t.d; ty += 1) {
        const there = occ.get(`${tx},${ty}`);
        const free = owned(tx, ty) && (!there || there === from);
        const a = proj(tx, ty);
        const b = proj(tx + 1, ty + 1);
        rect(a.x + 1, a.y + 1, b.x - a.x - 2, b.y - a.y - 2, free ? 'rgba(186,236,183,0.55)' : 'rgba(255,75,31,0.38)');
      }
    }
    const a = proj(ghost.x, ghost.y);
    const b = proj(ghost.x + t.w, ghost.y + t.d);
    ctx.strokeStyle = ghost.ok ? MINT_D : SIGNAL;
    ctx.lineWidth = 2.5;
    ctx.strokeRect(a.x + 1, a.y + 1, b.x - a.x - 2, b.y - a.y - 2);
    if (ghost.lift > 0.02) {
      const c = proj(ghost.vx + t.w / 2, ghost.vy + t.d / 2);
      ctx.beginPath();
      ctx.ellipse(c.x, c.y, t.w * cw() * 0.42, t.d * cw() * TILT * 0.42, 0, 0, TAU);
      ctx.fillStyle = `rgba(20,20,21,${0.16 * ghost.lift})`;
      ctx.fill();
    }
    if (t.range) drawRange({ type: ghost.type, level: ghost.level }, ghost.x, ghost.y);
  };
  const drawRange = (item, x = item.x, y = item.y) => {
    const t = TYPES[item.type];
    if (!t.range) return;
    const c = proj(x + 0.5, y + 0.5);
    const r = rangeOf(item.type, item.level) * cw();
    // The beat: how far the hero walks out to meet a Bootleg, shown as a faint outer ring.
    if (t.roam) {
      const beat = r + t.roam * cw();
      ctx.beginPath();
      ctx.ellipse(c.x, c.y, beat, beat * TILT, 0, 0, TAU);
      ctx.setLineDash([2, 6]);
      ctx.strokeStyle = 'rgba(20,20,21,0.22)';
      ctx.lineWidth = 1.2;
      ctx.stroke();
      ctx.setLineDash([]);
    }
    ctx.beginPath();
    ctx.ellipse(c.x, c.y, r, r * TILT, 0, 0, TAU);
    ctx.fillStyle = 'rgba(186,236,183,0.18)';
    ctx.fill();
    ctx.setLineDash([5, 5]);
    ctx.strokeStyle = 'rgba(20,20,21,0.35)';
    ctx.lineWidth = 1.2;
    ctx.stroke();
    ctx.setLineDash([]);
    if (t.near) {
      ctx.beginPath();
      ctx.ellipse(c.x, c.y, t.near * cw(), t.near * cw() * TILT, 0, 0, TAU);
      ctx.fillStyle = 'rgba(255,75,31,0.08)';
      ctx.fill();
    }
  };
  const levelPill = item => {
    const box = screenBox(item);
    const size = clamp(cw() * 0.32, 18, 26);
    const text = String(item.level);
    ctx.font = `700 ${Math.round(size * 0.58)}px ${FONT}`;
    const w = Math.max(size, ctx.measureText(text).width + size * 0.7);
    const x = (box.l + box.r) / 2 - w / 2;
    const y = box.b + 6;
    plate(x, y, w, size, size / 2, YELLOW, 2);
    label(text, x + w / 2, y + size / 2 + 1, Math.round(size * 0.58), INK);
  };

  // Where the puffs of an explosion sit around its middle: angle, distance and size, all against its radius.
  const BOOM_PUFFS = [[0, 0.55, 0.42], [0.9, 0.62, 0.36], [1.8, 0.5, 0.44], [2.7, 0.64, 0.34], [3.6, 0.55, 0.4], [4.5, 0.6, 0.38], [5.4, 0.52, 0.43]];
  // Trick effects: a cartoon slap, a chain spark, shock rings.
  const drawMoments = () => {
    const u = cw();
    moments.forEach(f => {
      if (f.t < 0) return;
      const p = clamp(f.t / f.dur, 0, 1);
      if (f.kind === 'pow') {
        // A comic burst over the KO: a jagged star and a word.
        const at = proj(f.x, f.y, 1.4 + p * 0.4);
        const k = u * (f.boss ? 0.75 : 0.48) * Math.min(1, p * 5) * (1 + (1 - p) * 0.1);
        ctx.save();
        ctx.globalAlpha = p > 0.75 ? (1 - p) / 0.25 : 1;
        ctx.translate(at.x, at.y);
        ctx.rotate(f.tilt);
        ctx.beginPath();
        starPath(ctx, 0, 0, k, k * 0.62, 10);
        ctx.fillStyle = INK;
        ctx.fill();
        ctx.beginPath();
        starPath(ctx, 0, 0, k * 0.9, k * 0.56, 10);
        ctx.fillStyle = f.boss ? SIGNAL : YELLOW;
        ctx.fill();
        label(f.text, 0, 1, k * 0.42, f.boss ? PAPER : INK);
        ctx.restore();
        return;
      }
      if (f.kind === 'callout') {
        // A hero's special: the name on a small slanted ribbon that pops in, holds, and lifts away.
        const at = proj(f.x, f.y, f.h + p * 0.4);
        const size = Math.max(10, u * 0.26);
        ctx.font = `800 ${Math.round(size)}px ${FONT}`;
        const w = ctx.measureText(f.text).width + size * 1.2;
        const k = p < 0.12 ? 1.25 - (p / 0.12) * 0.25 : 1;
        ctx.save();
        ctx.globalAlpha = (p > 0.75 ? (1 - p) / 0.25 : 1) * 0.92;
        ctx.translate(at.x, at.y);
        ctx.rotate(-0.08);
        ctx.scale(k, k);
        poly([[-w / 2 - size * 0.3, -size * 0.75], [w / 2 + size * 0.1, -size * 0.75], [w / 2 + size * 0.3, size * 0.75], [-w / 2 - size * 0.1, size * 0.75]], INK);
        poly([[-w / 2 - size * 0.18, -size * 0.6], [w / 2, -size * 0.6], [w / 2 + size * 0.18, size * 0.6], [-w / 2, size * 0.6]], f.color);
        rect(-w / 2, -size * 0.6, w, size * 0.14, 'rgba(255,255,255,0.25)');
        label(f.text, 0, size * 0.05, Math.round(size), f.color === GOLD ? INK : PAPER, 'center', 800);
        ctx.restore();
        return;
      }
      if (f.kind === 'stretch') {
        // Caruso's rubber arm: from his shoulder all the way out to the fist, wobbling, then snapping back.
        const hero = itemById(f.id);
        const from = hero && hero.crew && hero.crew.muzzle;
        if (!from) return;
        const alive = f.enemy && enemies.includes(f.enemy);
        const to = proj(alive ? f.enemy.x : f.x, alive ? f.enemy.y : f.y, 0.6);
        const out = p < 0.45 ? p / 0.45 : 1 - (p - 0.45) / 0.55;
        const ex = lerp(from.x, to.x, out);
        const ey = lerp(from.y, to.y, out);
        const width = u * (f.big ? 0.17 : 0.1);
        const n = 10;
        const nx = -(ey - from.y);
        const ny = ex - from.x;
        const len = Math.hypot(nx, ny) || 1;
        ctx.save();
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        for (let k = 0; k <= n; k += 1) {
          const q = k / n;
          const wob = Math.sin(q * Math.PI * 4 + clock * 30) * u * 0.05 * Math.sin(q * Math.PI);
          const x = lerp(from.x, ex, q) + (nx / len) * wob;
          const y = lerp(from.y, ey, q) + (ny / len) * wob;
          if (k) ctx.lineTo(x, y);
          else ctx.moveTo(x, y);
        }
        const skin = HERO_SPECS.caruso.skin;
        ctx.strokeStyle = skin;
        ctx.lineWidth = width;
        ctx.stroke();
        // The rubber coils from his forearms, stretched far apart.
        ctx.setLineDash([width * 0.3, width * 1.4]);
        ctx.lineCap = 'butt';
        ctx.strokeStyle = RUBBER;
        ctx.stroke();
        ctx.restore();
        const fist = width * (f.big ? 1.1 : 0.95);
        circle(ex, ey, fist, skin);
        line(ex + fist * 0.3, ey - fist * 0.6, ex + fist * 0.3, ey + fist * 0.6, 'rgba(0,0,0,0.25)', Math.max(1, fist * 0.12));
        if (out > 0.85) {
          ctx.beginPath();
          starPath(ctx, ex, ey, fist * 2.1, fist * 1.1, 8);
          ctx.fillStyle = 'rgba(255,210,60,0.55)';
          ctx.fill();
        }
        return;
      }
      if (f.kind === 'arc') {
        // A sweep of a blade: a wide crescent over the ground in front of it, or a whole ring for a turn all round.
        const c = proj(f.x, f.y, 0.4);
        const r = f.r * u;
        const span = f.span || 1.25;
        ctx.save();
        ctx.globalAlpha = (1 - p) * 0.9;
        ctx.translate(c.x, c.y);
        ctx.scale(1, TILT * 1.4);
        ctx.beginPath();
        ctx.arc(0, 0, r, f.a - span * (0.4 + p * 0.6), f.a + span * (0.4 + p * 0.6));
        ctx.arc(0, 0, r * 0.72, f.a + span * (0.4 + p * 0.6), f.a - span * (0.4 + p * 0.6), true);
        ctx.closePath();
        ctx.fillStyle = f.fill || 'rgba(200,250,255,0.75)';
        ctx.fill();
        ctx.strokeStyle = f.color;
        ctx.lineWidth = Math.max(2, u * 0.05);
        ctx.stroke();
        ctx.restore();
        return;
      }
      if (f.kind === 'moon') {
        // The blood moon: a red disc rising over the fight, the street washed in red underneath.
        const fade = Math.min(1, p * 5, (1 - p) * 4);
        const c = proj(f.x, f.y, 4.2);
        const r = u * 1.3;
        ctx.save();
        ctx.globalAlpha = fade;
        ctx.globalCompositeOperation = 'lighter';
        glowAt(c.x, c.y, r * 3, 'rgba(200,20,40,ALPHA)', 0.45);
        ctx.restore();
        ctx.save();
        ctx.globalAlpha = fade;
        circle(c.x, c.y, r, '#b0102a');
        circle(c.x - r * 0.25, c.y - r * 0.2, r * 0.28, 'rgba(255,255,255,0.08)');
        circle(c.x + r * 0.35, c.y + r * 0.3, r * 0.18, 'rgba(0,0,0,0.15)');
        const g = proj(f.x, f.y);
        ctx.beginPath();
        ctx.ellipse(g.x, g.y, u * 5, u * 5 * TILT, 0, 0, TAU);
        ctx.fillStyle = 'rgba(176,16,42,0.12)';
        ctx.fill();
        ctx.restore();
        return;
      }
      if (f.kind === 'smokering') {
        // Johno blows a ring of smoke after the show.
        const c = proj(f.x, f.y, f.h + p * 1.4);
        const r = u * (0.2 + p * 0.5);
        ctx.save();
        ctx.globalAlpha = (1 - p) * 0.7;
        ctx.beginPath();
        ctx.ellipse(c.x, c.y, r, r * 0.45, 0, 0, TAU);
        ctx.strokeStyle = 'rgba(230,230,235,1)';
        ctx.lineWidth = Math.max(2, u * 0.08 * (1 - p * 0.5));
        ctx.stroke();
        ctx.restore();
        return;
      }
      if (f.kind === 'slash') {
        // A cut: a bright crescent through the Bootleg, red at the edge.
        const at = proj(f.x, f.y, 0.5);
        const r = u * 0.55;
        ctx.save();
        ctx.globalAlpha = 1 - p;
        ctx.translate(at.x, at.y);
        ctx.rotate(f.a);
        ctx.beginPath();
        ctx.ellipse(0, 0, r * (0.8 + p * 0.4), r * 0.18, 0, 0, TAU);
        ctx.fillStyle = '#ff2d55';
        ctx.fill();
        ctx.beginPath();
        ctx.ellipse(0, 0, r * (0.75 + p * 0.4), r * 0.07, 0, 0, TAU);
        ctx.fillStyle = PAPER;
        ctx.fill();
        ctx.restore();
        return;
      }
      if (f.kind === 'thrust') {
        // A thrust: a red spike shooting out straight ahead, white at the core.
        const from = proj(f.fx, f.fy, 0.5);
        const to = proj(f.x, f.y, 0.5);
        const reach = Math.min(1, p * 4);
        const tip = { x: lerp(from.x, to.x, reach), y: lerp(from.y, to.y, reach) };
        const len = Math.hypot(tip.x - from.x, tip.y - from.y) || 1;
        const nx = -(tip.y - from.y) / len;
        const ny = (tip.x - from.x) / len;
        ctx.save();
        ctx.globalAlpha = 1 - p;
        [[u * 0.09, '#ff2d55'], [u * 0.035, PAPER]].forEach(([w, color]) => {
          ctx.beginPath();
          ctx.moveTo(from.x + nx * w, from.y + ny * w);
          ctx.lineTo(tip.x, tip.y);
          ctx.lineTo(from.x - nx * w, from.y - ny * w);
          ctx.closePath();
          ctx.fillStyle = color;
          ctx.fill();
        });
        ctx.restore();
        return;
      }
      if (f.kind === 'beam') {
        // The orbital laser: a thin aiming line first, then a column of light down from the sky with a white-hot core.
        const at = proj(f.x, f.y);
        const sky = { x: at.x + u * 1.5, y: -u };
        const aim = f.t < BEAM_AIM;
        const fade = aim ? 0.5 + 0.5 * Math.sin(f.t * 40) : Math.min(1, (f.dur - f.t) / 0.25);
        const w = f.r * u * (aim ? 0.08 : 1 + Math.sin(clock * 50) * 0.08);
        ctx.save();
        ctx.globalAlpha = fade;
        ctx.lineCap = 'round';
        if (aim) {
          line(sky.x, sky.y, at.x, at.y, TRICK_COLOR.laser, Math.max(1.5, w));
          ctx.beginPath();
          ctx.ellipse(at.x, at.y, f.r * u, f.r * u * TILT, 0, 0, TAU);
          ctx.strokeStyle = TRICK_COLOR.laser;
          ctx.setLineDash([4, 4]);
          ctx.lineWidth = 2;
          ctx.stroke();
          ctx.restore();
          return;
        }
        ctx.globalCompositeOperation = 'lighter';
        glowAt(at.x, at.y, f.r * u * 2.4, 'rgba(57,211,83,ALPHA)', 0.7);
        line(sky.x, sky.y, at.x, at.y, 'rgba(57,211,83,0.35)', w * 2.6);
        line(sky.x, sky.y, at.x, at.y, 'rgba(57,211,83,0.8)', w * 1.4);
        ctx.globalCompositeOperation = 'source-over';
        line(sky.x, sky.y, at.x, at.y, '#eaffef', w * 0.55);
        oval(at.x, at.y, f.r * u * 1.1, f.r * u * 1.1 * TILT, 'rgba(234,255,239,0.85)');
        oval(at.x, at.y, f.r * u * 0.6, f.r * u * 0.6 * TILT, PAPER);
        ctx.restore();
        return;
      }
      if (f.kind === 'boom') {
        // An ace going off: a shock ring racing over the ground, rays of light and a fireball in the ace's colour.
        const c = proj(f.x, f.y);
        const R = f.r * u;
        const out = 1 - (1 - p) ** 3;
        const fade = (1 - p) ** 1.5;
        const g = R * (0.3 + out * 1.1);
        ctx.save();
        ctx.globalAlpha = fade * 0.3;
        ctx.beginPath();
        ctx.ellipse(c.x, c.y, g, g * TILT, 0, 0, TAU);
        ctx.fillStyle = f.color;
        ctx.fill();
        ctx.globalAlpha = fade;
        ctx.strokeStyle = PAPER;
        ctx.lineWidth = Math.max(2, R * 0.12 * (1 - p));
        ctx.stroke();
        const at = proj(f.x, f.y, 0.3 + out * 0.5);
        const k = R * (0.45 + out * 0.8);
        ctx.globalAlpha = fade * 0.85;
        ctx.beginPath();
        starPath(ctx, at.x, at.y, k, k * 0.3, 12, f.spin + p * 0.6);
        ctx.fillStyle = YELLOW;
        ctx.fill();
        const ball = R * 0.62 * Math.sin(Math.min(1, p * 4) * Math.PI / 2) * (1 - p * 0.6);
        glowAt(at.x, at.y, ball * 2.4, 'rgba(255,214,110,ALPHA)', fade * 0.7);
        // The fireball is a cloud of puffs that billows out: inked edge, the ace's colour, a hot yellow heart.
        const puffs = (r, grow, color) => {
          circle(at.x, at.y, r * 0.62 + grow, color);
          BOOM_PUFFS.forEach(([a, d, size]) => circle(at.x + Math.cos(f.spin + a) * r * d, at.y + Math.sin(f.spin + a) * r * d * 0.8, r * size + grow, color));
        };
        ctx.globalAlpha = 1 - p * p;
        puffs(ball, Math.max(1.5, u * 0.04), INK);
        puffs(ball, 0, f.color);
        puffs(ball * 0.62, 0, YELLOW);
        circle(at.x - ball * 0.1, at.y - ball * 0.12, ball * 0.3, PAPER);
        ctx.restore();
        return;
      }
      if (f.kind === 'slap') {
        const at = proj(f.x, f.y, f.mega ? 0.9 : 0.55);
        const k = u * (f.mega ? 1.5 : f.crit ? 0.7 : 0.5) * (1 + Math.sin(p * Math.PI) * 0.25);
        ctx.save();
        ctx.globalAlpha = 1 - p * p;
        ctx.beginPath();
        starPath(ctx, at.x, at.y, k * (0.9 + p * 0.6), k * 0.45, 8);
        ctx.fillStyle = f.crit ? SIGNAL : YELLOW;
        ctx.fill();
        ctx.strokeStyle = INK;
        ctx.lineWidth = 2;
        ctx.stroke();
        // The Goldfaust punches in from the side, knuckles first, and pulls back.
        const reach = p < 0.3 ? 1 - p / 0.3 : (p - 0.3) * 0.5;
        if (f.mega) {
          // The Goldfaust comes straight down from the sky.
          ctx.translate(at.x, at.y - k * (0.4 + reach * 2.4));
          ctx.rotate(Math.PI / 2);
          ctx.scale(1.25, 1.25);
        } else {
          ctx.translate(at.x - f.tilt * k * (0.35 + reach * 1.3), at.y - k * 0.15);
          ctx.scale(f.tilt * 1.25, 1.25);
        }
        if (p < 0.3) [-0.22, 0, 0.22].forEach(dy => line(-k * 1.4, k * dy, -k * 0.7, k * dy, 'rgba(255,255,255,0.85)', Math.max(1.5, k * 0.06)));
        const skin = hustlerLook.skin;
        const fist = (x, y, w, h, color) => roundRect(x, y, w, h, Math.min(w, h) * 0.3, color);
        fist(-k * 0.75, -k * 0.2, k * 0.4, k * 0.42, INK);
        fist(-k * 0.71, -k * 0.16, k * 0.36, k * 0.34, f.crit ? SIGNAL : '#2b2b2e');
        fist(-k * 0.45, -k * 0.42, k * 0.86, k * 0.84, INK);
        fist(-k * 0.4, -k * 0.37, k * 0.76, k * 0.74, skin);
        [-0.28, -0.1, 0.08, 0.26].forEach(fy => {
          fist(k * 0.18, k * fy - k * 0.01, k * 0.24, k * 0.2, INK);
          fist(k * 0.2, k * fy + k * 0.01, k * 0.2, k * 0.16, skin);
        });
        rect(k * 0.12, -k * 0.34, k * 0.12, k * 0.7, INK);
        rect(k * 0.14, -k * 0.32, k * 0.08, k * 0.66, GOLD);
        rect(k * 0.15, -k * 0.3, k * 0.025, k * 0.62, 'rgba(255,255,255,0.6)');
        fist(-k * 0.3, k * 0.08, k * 0.42, k * 0.18, INK);
        fist(-k * 0.27, k * 0.1, k * 0.36, k * 0.13, skin);
        ctx.restore();
      } else if (f.kind === 'zap') {
        const a = proj(f.x, f.y, f.h || 0.5);
        const b = proj(f.tx, f.ty, 0.5);
        ctx.save();
        ctx.globalAlpha = 1 - p;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        for (let i = 1; i < 5; i += 1) ctx.lineTo(lerp(a.x, b.x, i / 5) + (i % 2 ? 6 : -6), lerp(a.y, b.y, i / 5));
        ctx.lineTo(b.x, b.y);
        ctx.strokeStyle = f.color || YELLOW;
        ctx.lineWidth = 3;
        ctx.stroke();
        ctx.restore();
      } else if (f.kind === 'ring') {
        const c = proj(f.x, f.y);
        const r = f.r * u * (0.15 + p * 0.85);
        ctx.save();
        ctx.globalAlpha = 1 - p;
        ctx.beginPath();
        ctx.ellipse(c.x, c.y, r, r * TILT, 0, 0, TAU);
        if (f.fill) {
          ctx.fillStyle = f.fill;
          ctx.fill();
        }
        ctx.strokeStyle = f.color;
        ctx.lineWidth = Math.max(2, u * (f.wide ? 0.16 : 0.08) * (1 - p));
        ctx.stroke();
        ctx.restore();
      }
    });
  };
  // Holding a finger on a building fills a ring around it; when it closes, the building comes up.
  const drawHold = () => {
    if (!press || !press.hold || press.moved || ghost) return;
    const p = (performance.now() - press.hold.at) / HOLD_MS;
    // A quick tap shows nothing; the ring only starts once the finger stays down.
    if (p < 0.4 || p > 1) return;
    const r = 22;
    ctx.save();
    ctx.beginPath();
    ctx.arc(press.x, press.y, r, 0, TAU);
    ctx.fillStyle = 'rgba(255,255,255,0.35)';
    ctx.fill();
    ctx.beginPath();
    ctx.arc(press.x, press.y, r, -Math.PI / 2, -Math.PI / 2 + TAU * ((p - 0.4) / 0.6));
    ctx.strokeStyle = MINT_D;
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.stroke();
    ctx.restore();
  };
  // While a fight is on, fog rolls in from the edges: the middle of the map stays clear, drifting banks
  // of haze frame it, and in a fight they glow faintly red. Shots and effects are drawn after, so they stay crisp.
  const FOG_BANKS = Array.from({ length: 9 }, (_, i) => ({ side: i % 4, at: (i * 0.37) % 1, size: 0.3 + ((i * 7) % 5) * 0.06, speed: 0.012 + (i % 3) * 0.006 }));
  let fog = 0;
  let fogAt = 0;
  const drawFog = () => {
    const now = performance.now();
    const dt = Math.min(0.1, (now - fogAt) / 1000);
    fogAt = now;
    const target = heist || (raid && raid.phase !== 'end') ? 1 : 0;
    fog = clamp(fog + Math.sign(target - fog) * dt * 0.7, 0, 1);
    if (fog <= 0) return;
    const k = fog * fog * (3 - 2 * fog);
    const fight = raid && raid.phase === 'fight';
    const tint = fight ? 0.5 + Math.sin(clock * 1.6) * 0.5 : 0;
    const haze = (r, g, b2, a) => `rgba(${r},${g},${b2},${a.toFixed(3)})`;
    const short = Math.min(W, H);
    ctx.save();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // A thin veil over everything, then the thick of it toward the edges.
    ctx.fillStyle = haze(226, 230, 238, 0.07 * k);
    ctx.fillRect(0, 0, W, H);
    const edge = ctx.createRadialGradient(W / 2, H / 2, short * 0.28, W / 2, H / 2, Math.hypot(W, H) * 0.58);
    edge.addColorStop(0, haze(232, 234, 240, 0));
    edge.addColorStop(0.65, haze(228, 230, 238, 0.22 * k));
    edge.addColorStop(1, haze(236 - tint * 10, 222 - tint * 30, 228 - tint * 30, (0.62 + tint * 0.08) * k));
    ctx.fillStyle = edge;
    ctx.fillRect(0, 0, W, H);
    // Banks of fog drifting along the borders.
    FOG_BANKS.forEach((bank, i) => {
      const run = (bank.at + clock * bank.speed) % 1;
      const along = run * 1.4 - 0.2;
      const x = bank.side === 0 ? along * W : bank.side === 1 ? W * 1.02 : bank.side === 2 ? (1 - along) * W : -W * 0.02;
      const y = bank.side === 0 ? -H * 0.02 : bank.side === 1 ? along * H : bank.side === 2 ? H * 1.02 : (1 - along) * H;
      const r = short * bank.size * (1 + Math.sin(clock * 0.5 + i) * 0.08);
      const puff = ctx.createRadialGradient(x, y, 0, x, y, r);
      puff.addColorStop(0, haze(240, 241, 245, 0.42 * k));
      puff.addColorStop(1, haze(240, 241, 245, 0));
      ctx.fillStyle = puff;
      ctx.fillRect(x - r, y - r, r * 2, r * 2);
    });
    ctx.restore();
  };
  // The aces' anime beats over the whole screen: manga focus lines, the ink impact frame, the cut-in.
  const drawAnime = () => {
    const c = proj(anime.x, anime.y);
    const far = Math.hypot(W, H);
    if (anime.focus > 0) {
      const rand = mulberry32(Math.floor(clock * 24));
      const hole = Math.min(W, H) * 0.2;
      ctx.save();
      ctx.globalAlpha = Math.min(1, anime.focus * 2.5) * 0.5;
      ctx.beginPath();
      for (let i = 0; i < 56; i += 1) {
        const a = (i / 56) * TAU + rand() * 0.08;
        const w = 0.004 + rand() * 0.012;
        const r = hole * (0.9 + rand() * 0.8);
        ctx.moveTo(c.x + Math.cos(a) * r, c.y + Math.sin(a) * r);
        ctx.lineTo(c.x + Math.cos(a - w) * far, c.y + Math.sin(a - w) * far);
        ctx.lineTo(c.x + Math.cos(a + w) * far, c.y + Math.sin(a + w) * far);
        ctx.closePath();
      }
      ctx.fillStyle = INK;
      ctx.fill();
      ctx.restore();
    }
    if (anime.impact > 0) {
      ctx.save();
      ctx.globalAlpha = 0.82;
      ctx.fillStyle = INK;
      ctx.fillRect(0, 0, W, H);
      ctx.beginPath();
      starPath(ctx, c.x, c.y, far * 0.6, Math.min(W, H) * 0.1, 18, clock * 7);
      ctx.fillStyle = PAPER;
      ctx.fill();
      ctx.restore();
    }
    const cut = anime.cut;
    if (!cut || cut.t < 0) return;
    // The cut-in: a slanted band in the ace's colour shoots in, holds with streaks racing through it, then drifts
    // on, thins out and fades. A Problem or a Raid starting gets one too, with a line under its name.
    const enter = 1 - (1 - clamp(cut.t / 0.22, 0, 1)) ** 3;
    const out = clamp((cut.t - (cut.dur - 0.4)) / 0.4, 0, 1);
    const leave = out * out * (3 - 2 * out);
    const h = clamp(H * 0.12, 60, 112) * (cut.sub ? 1.3 : 1);
    const size = Math.min(clamp(H * 0.12, 60, 112) * 0.62, W * 0.11);
    ctx.save();
    ctx.globalAlpha = 1 - leave;
    ctx.translate(W / 2 + (enter - 1) * W * 1.2 + leave * W * 0.12, H * 0.3);
    ctx.rotate(-0.09);
    ctx.scale(1, 1 - leave * 0.55);
    rect(-W, -h / 2 - 7, W * 2, h + 14, INK);
    rect(-W, -h / 2, W * 2, h, cut.color);
    rect(-W, -h / 2, W * 2, h * 0.16, 'rgba(255,255,255,0.3)');
    ctx.globalAlpha = 0.55 * (1 - leave);
    for (let i = 0; i < 16; i += 1) {
      const x = ((i * 373 + cut.t * 2600) % (W * 2.4)) - W * 1.2;
      const y = (((i * 0.618) % 1) - 0.5) * h * 0.8;
      rect(x, y, W * (0.12 + (i % 3) * 0.06), i % 2 ? 2 : 4, PAPER);
    }
    ctx.globalAlpha = 1 - leave;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = INK;
    ctx.fillStyle = PAPER;
    const shift = (0.5 - cut.t / cut.dur) * W * 0.06;
    const top = cut.sub ? -h * 0.14 : size * 0.04;
    // A long name shrinks to fit the screen.
    const text = cut.text.toUpperCase();
    ctx.font = `italic 900 ${Math.round(size)}px ${FONT}`;
    const big = size * Math.min(1, (W * 0.84) / ctx.measureText(text).width);
    ctx.font = `italic 900 ${Math.round(big)}px ${FONT}`;
    ctx.lineWidth = big * 0.2;
    ctx.strokeText(text, shift, top);
    ctx.fillText(text, shift, top);
    if (cut.sub) {
      ctx.font = `800 ${Math.round(size * 0.34)}px ${FONT}`;
      const small = Math.round(size * 0.34 * Math.min(1, (W * 0.84) / ctx.measureText(cut.sub).width));
      ctx.font = `800 ${small}px ${FONT}`;
      ctx.lineWidth = small * 0.28;
      ctx.strokeText(cut.sub, shift, h * 0.3);
      ctx.fillText(cut.sub, shift, h * 0.3);
    }
    ctx.restore();
  };
  // A trick that goes off big lights up the whole screen for a beat.
  const drawFlare = () => {
    if (flare <= 0) return;
    ctx.save();
    ctx.globalAlpha = Math.min(1, flare) ** 2 * 0.9;
    ctx.fillStyle = flareColor;
    ctx.fillRect(0, 0, W, H);
    ctx.restore();
  };

  // The clouds of a trip: a bank of fat, ink-rimmed cumulus over the whole view. Each cloud drifts in from the
  // side it sits on, the outer ones first, and they leave from the middle out, swelling as if flown through.
  const CLOUD = { fill: '#fbfaf6', shade: '#e3e1da', deep: '#cfccc3' };
  const cloudBank = seed => {
    const rand = mulberry32(seed);
    const list = [];
    for (let j = -1; j <= 5; j += 1) {
      for (let i = -1; i <= 6; i += 1) {
        const puffs = [[0, 0, 0.62], [-0.58, 0.12, 0.44], [0.6, 0.1, 0.46], [-0.26, -0.36, 0.46], [0.3, -0.32, 0.5], [0.02, 0.24, 0.5]]
          .map(([x, y, r]) => [x + (rand() - 0.5) * 0.12, y + (rand() - 0.5) * 0.1, r * (0.9 + rand() * 0.2)]);
        list.push({ i: (i + (j & 1) * 0.5 + (rand() - 0.5) * 0.3) / 5.5, j: (j + (rand() - 0.5) * 0.3) / 4.4, puffs, size: 0.85 + rand() * 0.3, drift: (rand() - 0.5) * 0.5, bob: rand() * TAU });
      }
    }
    // Back to front, so the lower clouds overlap the ones above them.
    return list.sort((a, b) => a.j - b.j);
  };
  const drawClouds = () => {
    if (!travel) return;
    const { phase, t } = travel;
    if (!travel.bank) travel.bank = cloudBank(travel.seed);
    const R = Math.max(W, H) * 0.15;
    const rim = Math.max(2, R * 0.035);
    // How closed the bank is overall: 0 open, 1 shut.
    const shut = phase === 'close' ? t / TRAVEL.close : phase === 'hold' ? 1 : 1 - t / TRAVEL.open;
    const cover = clamp((shut - 0.72) / 0.28, 0, 1);
    if (cover > 0) {
      ctx.save();
      ctx.globalAlpha = cover;
      rect(0, 0, W, H, CLOUD.shade);
      ctx.restore();
    }
    travel.bank.forEach(cloud => {
      const cx = cloud.i * W;
      const cy = cloud.j * H;
      const off = Math.min(1, Math.abs(cx - W / 2) / (W / 2));
      const side = cx < W / 2 ? -1 : 1;
      let p;
      let swell = 1;
      if (phase === 'open') {
        const q = clamp((t / TRAVEL.open - off * 0.3) / 0.7, 0, 1);
        p = 1 - EASES.in(q);
        swell = 1 + EASES.in(q) * 0.6;
      } else p = phase === 'hold' ? 1 : EASES.out(clamp((t / TRAVEL.close - (1 - off) * 0.3) / 0.7, 0, 1));
      if (p <= 0) return;
      const x = cx + side * (1 - p) * (W * 0.6 + R * 1.5);
      const y = cy + (1 - p) * cloud.drift * R + Math.sin(clock * 1.3 + cloud.bob) * R * 0.025;
      const r = R * cloud.size * swell;
      cloud.puffs.forEach(([px, py, pr]) => circle(x + px * r, y + py * r, pr * r + rim, INK));
      cloud.puffs.forEach(([px, py, pr]) => circle(x + px * r, y + py * r, pr * r, CLOUD.deep));
      cloud.puffs.forEach(([px, py, pr]) => circle(x + px * r - pr * r * 0.06, y + py * r - pr * r * 0.12, pr * r * 0.9, CLOUD.shade));
      cloud.puffs.forEach(([px, py, pr]) => circle(x + px * r - pr * r * 0.12, y + py * r - pr * r * 0.2, pr * r * 0.74, CLOUD.fill));
    });
  };

  let tags = [];
  let plots = [];
  const drawScene = () => {
    const u = cw();
    moveBudget = MOVE_NEW;
    ctx.save();
    if (shake > 0) ctx.translate((Math.random() - 0.5) * shake * 10, (Math.random() - 0.5) * shake * 10);
    if (anime.zoom > 0) {
      // The zoom punch: the view jumps in on the hit and eases back out.
      const z = proj(anime.x, anime.y);
      const k = 1 + Math.sin((anime.zoom * Math.PI) / 2) * 0.1;
      ctx.translate(z.x, z.y);
      ctx.scale(k, k);
      ctx.translate(-z.x, -z.y);
    }
    const lots = heist ? [] : forSale();
    drawGround(lots);
    if (heist) drawRivalYard();
    plots = plotRects();
    if (!heist) drawTurfArt();
    drawDecals();
    const sel = selectedItem();
    if (sel) {
      const f = footprint(sel);
      drawFootprint(f.x, f.y, f.w, f.d, 'rgba(186,236,183,0.35)', MINT_D);
      if (kindOf(sel) === 'crew') drawRange(sel);
    }
    if (ghost) drawGhostGround();
    // Everything that stands, sorted by its front edge.
    const list = [];
    const v = visibleChunks();
    const lotKeys = new Set(lots.map(lot => lot.key));
    const seen = lots.filter(lot => inView(lot.cx, lot.cy, v));
    for (let cy = v.cy0; cy <= v.cy1; cy += 1) {
      for (let cx = v.cx0; cx <= v.cx1; cx += 1) {
        if (ownedChunk(cx, cy) || lotKeys.has(chunkKey(cx, cy))) continue;
        propsOf(cx, cy).forEach(p => list.push({ d: p.kind === 'car' ? p.y + 0.8 : p.y, draw: () => drawProp(p) }));
      }
    }
    lotMarks(seen).forEach(mark => list.push({ d: mark.y, draw: () => drawLotMark(mark) }));
    if (heist) {
      heist.props.forEach(prop => list.push({ d: prop.y + prop.d, draw: () => drawStash(prop) }));
      heist.cover.forEach(c => list.push({ d: c.y + c.d, draw: () => drawCover(c) }));
      [[heist.x0, heist.x1, 0], ...frontFence().map(([xa, xb]) => [xa, xb, heist.y1])].forEach(([xa, xb, y]) => list.push({ d: y, draw: () => fenceRun(xa, xb, y) }));
    }
    // Only what can reach into the view is drawn; a tall building just below the bottom edge still pokes up into it.
    const lo = unproj(-u * 2, -u * 3);
    const hi = unproj(W + u * 2, H + u * 6);
    const inSight = (x, y) => x >= lo.x && x <= hi.x && y >= lo.y && y <= hi.y;
    const shown = world().filter(item => {
      const f = hitBox(item);
      return f.x + f.w >= lo.x && f.x <= hi.x && f.y + f.d >= lo.y && f.y <= hi.y;
    });
    shown.forEach(item => {
      if (ghost && ghost.moving === item.id) return;
      list.push({ d: depthOf(item), draw: () => drawItem(item) });
    });
    if (ghost) {
      const fake = { id: -1, type: ghost.type, level: ghost.level, x: ghost.vx, y: ghost.vy };
      list.push({ d: ghost.y + TYPES[ghost.type].d + 0.01, draw: () => {
        ctx.save();
        ctx.globalAlpha = ghost.ok ? 0.9 : 0.6;
        ctx.translate(0, -ghost.lift * cw() * 0.35);
        drawItem(fake);
        ctx.restore();
      } });
    }
    if (!heist && (founded() || K.dead)) list.push({ d: K.y, draw: drawHustler });
    if (!heist) {
      // A group steps aside while a building is being placed over its spot.
      const inWay = group => ghost && group.x > ghost.x - 0.5 && group.x < ghost.x + TYPES[ghost.type].w + 0.5 && group.y > ghost.y - 0.5 && group.y < ghost.y + TYPES[ghost.type].d + 0.5;
      if (!raid || raid.phase === 'end') hangers.filter(group => !inWay(group) && inSight(group.x, group.y)).forEach(group => group.people.forEach(one => list.push({ d: group.y + one.dy, draw: () => drawHanger(group, one) })));
      pigeons.forEach(p => {
        if (inSight(p.x, p.y)) list.push({ d: p.y, draw: () => drawPigeon(p) });
      });
    }
    enemies.forEach(enemy => {
      if (inSight(enemy.x, enemy.y)) list.push({ d: enemy.y, draw: () => drawEnemy(enemy) });
    });
    walkers.filter(w => inSight(w.x, w.y)).forEach(w => list.push({ d: w.y, draw: () => {
      if (w.gone === undefined) return drawWalker(w);
      ctx.save();
      ctx.globalAlpha = Math.max(0, 1 - w.gone / 0.6);
      drawWalker(w);
      return ctx.restore();
    } }));
    moments.forEach(f => {
      if (f.kind === 'fallen') list.push({ d: f.y, draw: () => drawFallen(f) });
      if (f.kind === 'ko') list.push({ d: f.y + f.vy * f.t, draw: () => drawKo(f) });
      if (f.kind === 'crate') list.push({ d: f.y + 0.3, draw: () => drawCrate(f) });
      if (f.kind === 'wreck' && f.t >= 0) list.push({ d: wreckAt(f).y, draw: () => drawWreck(f) });
      if (f.kind === 'twister' && f.t >= 0) list.push({ d: f.y + 0.2, draw: () => drawTwister(f) });
      if (f.kind === 'stampede' && f.t >= 0) f.runners.forEach(r => {
        if (f.t < r.lag) return;
        const pos = runnerAt(f, r);
        list.push({ d: pos.y, draw: () => drawRunner(f, r, pos) });
      });
    });
    plots.forEach(plot => {
      if (inView(plot.cx, plot.cy, v)) list.push({ d: plot.cy * CHUNK + CHUNK / 2, draw: () => drawSaleSign(plot) });
    });
    list.sort((a, b) => a.d - b.d);
    list.forEach(entry => entry.draw());
    drawFog();
    shots.forEach(drawShot);
    drones.forEach(drawDrone);
    drawParts();
    drawMoments();
    shown.forEach(drawHpBar);
    tags = [];
    if (founded()) {
      shown.forEach(item => {
        const tag = tagOf(item);
        if (tag) {
          tags.push({ item, ...tag });
          drawTag(tag);
        }
      });
    }
    if (sel) levelPill(sel);
    drawFloats();
    ctx.restore();
    drawHold();
    drawAnime();
    drawFlare();
    drawFlyers();
    drawClouds();
  };

  // ---------------------------------------------------------------------------------------------
  // Camera and layout

  let W = root.clientWidth;
  let H = root.clientHeight;
  let dpr = 1;
  let compact = false;
  let selected = null;
  let ghost = null;
  let panel = null;
  const toolCd = Object.fromEntries(Object.keys(TOOLS).map(id => [id, 0]));
  // Aces used in this fight; each has only so many goes per round.
  const toolUsed = Object.fromEntries(Object.keys(TOOLS).map(id => [id, 0]));
  const resetTools = () => {
    RAID_TRICKS.forEach(id => {
      toolCd[id] = 0;
      toolUsed[id] = 0;
    });
  };
  const MIN_ZOOM = 0.45;
  // A phone starts on smaller tiles, so it may come in closer to get to the same size as a big screen.
  const maxZoom = () => (compact ? 3 : 1.7);

  // In a fight the camera may go a little further past the edge, to keep a fight there in the middle.
  const clampCam = () => {
    const slack = raid && raid.phase === 'fight' ? 5 : 2;
    cam.z = clamp(cam.z, MIN_ZOOM, maxZoom());
    cam.x = clamp(cam.x, bounds.x0 - slack, bounds.x1 + slack);
    cam.y = clamp(cam.y, bounds.y0 - slack, bounds.y1 + slack);
  };
  // Below full sharpness when the device cannot keep up; see watchPace.
  let quality = 1;
  const layout = () => {
    W = Math.max(1, root.clientWidth);
    H = Math.max(1, root.clientHeight);
    // As sharp as the screen up to two pixels per point: a third would paint more than twice the pixels, and burn
    // the battery for it, for a difference hardly seen. Less only while the device cannot keep up.
    dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1) * quality);
    compact = W < 720;
    root.classList.toggle('is-compact', compact);
    const pw = Math.round(W * dpr);
    const ph = Math.round(H * dpr);
    if (canvas.width !== pw) canvas.width = pw;
    if (canvas.height !== ph) canvas.height = ph;
    const fit = compact ? (W * 0.92) / 12 : Math.min((W * 0.6) / 12, (H * 0.66) / (12 * TILT + 1.5));
    cell = clamp(fit, 26, 84);
    cam.ax = W / 2;
    cam.ay = H / 2 + (compact ? 0 : 12);
    clampCam();
    // The card measures its room again on the next frame.
    if (!els.card.hidden) cardSig = '';
  };
  const focusOn = (x, y) => {
    cam.x = x;
    cam.y = y;
    clampCam();
  };
  // In a fight the camera keeps the fighting in view: the crew and the hustler with every Bootleg at their throat,
  // framed between the top and the bottom of the HUD. It zooms out a little where it must and back in to the zoom
  // the player chose, and after the player drags or pinches it leaves the view to them for a moment.
  let looked = -Infinity;
  const FIGHT_LOOK = 3;
  const FIGHT_CLOSE = 3;
  const FIGHT_WIDE = 0.7;
  const FIGHT_HUD = { top: 170, bottom: 110, side: 16 };
  const fightCam = dt => {
    if (!raid || raid.phase !== 'fight' || camLock || travel) return;
    if (raid.camZ === undefined || clock - looked < FIGHT_LOOK) {
      raid.camZ = cam.z;
      return;
    }
    const ours = world().filter(item => kindOf(item) === 'crew').map(crewAt);
    if (!heist && !K.dead) ours.push({ x: K.x, y: K.y });
    const near = enemies.filter(enemy => !fled(enemy) && ours.some(one => dist(one.x, one.y, enemy.x, enemy.y) < FIGHT_CLOSE));
    if (!near.length) return;
    const spots = [...near, ...ours.filter(one => near.some(enemy => dist(one.x, one.y, enemy.x, enemy.y) < FIGHT_CLOSE))];
    const xs = spots.map(spot => spot.x);
    const ys = spots.map(spot => spot.y);
    const x0 = Math.min(...xs) - 1;
    const x1 = Math.max(...xs) + 1;
    const y0 = Math.min(...ys) - 1.6;
    const y1 = Math.max(...ys) + 0.6;
    const top = compact ? FIGHT_HUD.top : FIGHT_HUD.top * 0.7;
    const roomW = W - 2 * FIGHT_HUD.side;
    const roomH = Math.max(1, H - top - FIGHT_HUD.bottom);
    const fit = Math.min(roomW / ((x1 - x0) * cell), roomH / ((y1 - y0) * TILT * cell));
    const z = clamp(clamp(fit, raid.camZ * FIGHT_WIDE, raid.camZ), MIN_ZOOM, maxZoom());
    cam.z = ease(cam.z, z, 1.5, dt);
    const mid = (top + H - FIGHT_HUD.bottom) / 2;
    cam.x = ease(cam.x, (x0 + x1) / 2, 2, dt);
    cam.y = ease(cam.y, (y0 + y1) / 2 - (mid - cam.ay) / (cw() * TILT), 2, dt);
    clampCam();
  };
  // An ace in play holds the camera (see lockCam); when it is over the camera glides back.
  const followCam = dt => {
    if (!camLock) return;
    if (camLock.follow) {
      const at = camLock.follow();
      if (at) {
        camLock.x = at.x;
        camLock.y = at.y;
      } else camLock.until = Math.min(camLock.until, clock);
    }
    const back = clock >= camLock.until || !raid;
    const goal = back ? camLock.back : camLock;
    cam.x = ease(cam.x, goal.x, back ? 3.5 : 4.5, dt);
    cam.y = ease(cam.y, goal.y, back ? 3.5 : 4.5, dt);
    clampCam();
    if (back && dist(cam.x, cam.y, goal.x, goal.y) < 0.03) camLock = null;
  };
  const rootPoint = event => {
    const box = root.getBoundingClientRect();
    return { x: event.clientX - box.left, y: event.clientY - box.top };
  };
  const centerOf = element => {
    const a = element.getBoundingClientRect();
    const b = root.getBoundingClientRect();
    return { x: a.left - b.left + a.width / 2, y: a.top - b.top + a.height / 2 };
  };

  // ---------------------------------------------------------------------------------------------
  // Pictures for the interface, painted once with the same code as the map.

  const thumbs = new Map();
  // Interface pictures at the screen's own density, so they stay crisp on a phone too.
  const THUMB_DPR = Math.min(3, Math.max(2, Math.ceil(window.devicePixelRatio || 1)));
  const paintThumb = (key, size, setup, paint) => {
    if (thumbs.has(key)) return thumbs.get(key);
    const c = document.createElement('canvas');
    c.width = size * THUMB_DPR;
    c.height = size * THUMB_DPR;
    const saved = { ctx, cell, x: cam.x, y: cam.y, z: cam.z, ax: cam.ax, ay: cam.ay };
    ctx = c.getContext('2d');
    ctx.scale(THUMB_DPR, THUMB_DPR);
    cam.z = 1;
    try {
      setup();
      paint();
    } finally {
      ctx = saved.ctx;
      cell = saved.cell;
      Object.assign(cam, { x: saved.x, y: saved.y, z: saved.z, ax: saved.ax, ay: saved.ay });
    }
    const url = c.toDataURL();
    thumbs.set(key, url);
    return url;
  };
  const itemThumb = (type, level = 1) => {
    const t = TYPES[type];
    const size = 120;
    // Buildings grow with their level, people don't: their frame stays put however far they are upgraded.
    const tall = t.kind === 'crew' ? HEIGHT[type] || 1 : heightOf(type, level);
    // A hero fills his card, standing near its foot.
    const span = t.hero ? tall * 0.98 : t.kind === 'crew' ? 1.2 : Math.max(t.w + 0.2, t.d * TILT + tall + 0.3);
    return paintThumb(`${type}:${level}:${hustlerLook.brand}`, size, () => {
      cell = size / span;
      cam.x = t.w / 2;
      cam.y = t.d;
      cam.ax = size / 2;
      cam.ay = t.hero ? size * 0.86 + t.d * TILT * cell * 0.5 : size - (size - (t.d * TILT + tall) * cell) / 2;
    }, () => DRAWERS[type]({ id: 1, type, level, x: 0, y: 0 }));
  };
  const gangThumb = (type, gold = false) => paintThumb(`gang:${type}:${gold}`, 96, () => {
    cell = 96 / (GANG[type].size * 2.15);
    cam.x = 0;
    cam.y = 0;
    cam.ax = 48;
    cam.ay = 92;
  }, () => drawEnemy({ id: 3, type, gold, x: 0, y: 0, h: 0, face: 1, walk: 0, swing: 0, stun: 0, slow: 0, hit: 0, hp: 1, max: 1 }));
  // A Bootleg from the waist up, framed for a round picture.
  const gangPortrait = (type, gold = false) => paintThumb(`gang-face:${type}:${gold}`, 84, () => {
    cell = 84 / (GANG[type].size * 1.5);
    cam.x = 0;
    cam.y = 0;
    cam.ax = 45;
    cam.ay = 84 * 1.4;
  }, () => drawEnemy({ id: 3, type, gold, x: 0, y: 0, h: 0, face: 1, walk: 0, swing: 0, stun: 0, slow: 0, hit: 0, hp: 1, max: 1 }));
  // Every trick has its colour, in the shop, on the raid buttons and in its effects.
  const TRICK_COLOR = {
    faust: GOLD, shirt: '#ff6a1a', bombe: '#8c9a3c', drohne: DRONE_RED, fans: MOSH,
    rakete: '#1d9bf0', birne: '#ec4899', tornado: '#14b8a6', laser: '#39d353', beben: '#a16207'
  };
  const RAID_TRICKS = Object.keys(TOOLS).filter(id => !TOOLS[id].tap);
  const paintPortrait = (canvasEl, size) => {
    const c = canvasEl.getContext('2d');
    canvasEl.width = size * THUMB_DPR;
    canvasEl.height = size * THUMB_DPR;
    const saved = ctx;
    ctx = c;
    ctx.setTransform(THUMB_DPR, 0, 0, THUMB_DPR, 0, 0);
    ctx.clearRect(0, 0, size, size);
    try {
      drawPerson(size * 0.5, size * 2.23, size * 1.9, { face: 1, noShadow: true, owner: canvasEl });
    } finally {
      ctx = saved;
    }
  };

  // ---------------------------------------------------------------------------------------------
  // Interface helpers

  // Every hustler goes by a street name that comes with the look.
  const NAME_A = ['Lil', 'Big', 'Young', 'MC', 'DJ', 'Kid', 'Baby', 'OG', 'Tiny', 'Fresh', 'Easy', 'Real'];
  const NAME_B = ['Stitch', 'Druck', 'Merch', 'Hoodie', 'Cap', 'Tee', 'Siebi', 'Sticker', 'Tape', 'Drop', 'Kiez', 'Spray'];
  const hustlerName = seed => `${NAME_A[seed % NAME_A.length]} ${NAME_B[Math.floor(seed / NAME_A.length) % NAME_B.length]}`;
  // The name the player picked, or the street name that came with the look.
  const playerName = () => S.name || hustlerName(S.seed);
  // Cash and fans are drawn in colour, like on the map.
  const CASH_ICON = '<path class="c1" d="M5 5.2l15.6 2.6-1.6 9.4L3.4 14.6z"/><rect class="c2" x="2.5" y="8" width="17" height="10.5" rx="1.2"/><circle class="c3" cx="11" cy="13.25" r="3"/><path class="c4" d="M11 11.4l1.85 1.85L11 15.1l-1.85-1.85z"/><path class="c0" d="M4.52 8L5 5.2l15.6 2.6-1.1 6.5M2.5 8h17v10.5h-17z"/>';
  const FANS_ICON = '<path class="f1" d="M3.5 20.5c.4-4.4 3.8-7 8.5-7s8.1 2.6 8.5 7z"/><circle class="f1" cx="12" cy="7.8" r="4.3"/><circle class="f2" cx="10.6" cy="6.6" r="1.2"/><path class="f0" d="M3.5 20.5c.4-4.4 3.8-7 8.5-7s8.1 2.6 8.5 7zM16.3 7.8a4.3 4.3 0 1 1-8.6 0 4.3 4.3 0 0 1 8.6 0z"/>';
  const ICONS = {
    cash: CASH_ICON,
    fans: FANS_ICON,
    x: '<path d="M6.5 6.5l11 11M17.5 6.5l-11 11"/>',
    bag: '<path d="M5 8.5h14l-1.2 12H6.2z"/><path d="M9 11V7.5a3 3 0 0 1 6 0V11"/>',
    alert: '<path d="M12 3.5l7 2.5v5.5c0 4.5-3 7.8-7 9-4-1.2-7-4.5-7-9V6z"/><path d="M12 8.5v4.5M12 16.4v.1"/>',
    mic: '<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M6 11.5a6 6 0 0 0 12 0M12 17.5v3.5M8.5 21h7"/>',
    flag: '<path d="M6 21V3.5M6 4h11.5l-2.5 4 2.5 4H6"/>',
    hq: '<path d="M3.5 20.5h17M5.5 20.5V8.5L12 4.5v16M12 9.5h6.5v11M8.5 10h1M8.5 13.5h1M8.5 17h1M15 13h.5M15 16.5h.5"/>',
    shield: '<path d="M12 3.5l7 2.5v5.5c0 4.5-3 7.8-7 9-4-1.2-7-4.5-7-9V6z"/>',
    up: '<path d="M12 19V5M5.5 11.5L12 5l6.5 6.5"/>',
    check: '<path d="M5 12.5l4.5 4.5L19 7.5"/>',
    lock: '<rect x="5" y="11" width="14" height="10" rx="2.5"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/>',
    pen: '<path d="M4 20h4L19 9l-4-4L4 16v4zM13.5 6.5l4 4"/>',
    dice: '<rect x="4" y="4" width="16" height="16" rx="4"/><path d="M9 9h.01M15 15h.01M15 9h.01M9 15h.01M12 12h.01"/>',
    clock: '<circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 2"/>',
    heart: '<path d="M12 20s-7.5-4.6-7.5-10A4.3 4.3 0 0 1 12 7.4 4.3 4.3 0 0 1 19.5 10c0 5.4-7.5 10-7.5 10z"/>',
    target: '<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3"/><path d="M12 1.5v4M12 18.5v4M1.5 12h4M18.5 12h4"/>',
    range: '<circle cx="12" cy="12" r="8.5" stroke-dasharray="3 3"/><circle cx="12" cy="12" r="2"/>',
    speed: '<path d="M4 15a8 8 0 0 1 16 0M12 15l4-5"/>',
    fist: '<path d="M6.5 12V8.5a1.75 1.75 0 0 1 3.5 0V12M10 8V6.5a1.75 1.75 0 0 1 3.5 0V11M13.5 8a1.75 1.75 0 0 1 3.5 0v3.5M17 9.5a1.75 1.75 0 0 1 3.5 0V15a6 6 0 0 1-6 6h-2.5a6 6 0 0 1-5.5-5.5V12h5.5a2 2 0 0 1 0 4H9.5"/>',
    knuckles: '<path d="M2.5 8.6a2.4 2.4 0 0 1 4.75-.5 2.4 2.4 0 0 1 4.75 0 2.4 2.4 0 0 1 4.75 0 2.4 2.4 0 0 1 4.75.5v2.4c0 1.4-1.1 2.5-2.5 2.5h-14c-1.4 0-2.5-1.1-2.5-2.5z"/><path d="M7.5 13.5c0 4.2 2 7 4.5 7s4.5-2.8 4.5-7"/><circle cx="4.9" cy="9.6" r=".9"/><circle cx="9.6" cy="9.6" r=".9"/><circle cx="14.4" cy="9.6" r=".9"/><circle cx="19.1" cy="9.6" r=".9"/>',
    mortar: '<path d="M8.6 15.4L15.4 4.6l3.8 2.4-6.8 10.8z"/><path d="M3.5 20.5h17M7 20.5l3.2-4.6M17.5 20.5l-3.4-4.2"/>',
    phone: '<rect x="6.5" y="2.5" width="11" height="19" rx="2.5"/><path d="M10.5 18.5h3M12 7.5l1.2 2.4 2.6.4-1.9 1.8.5 2.6-2.4-1.3-2.4 1.3.5-2.6-1.9-1.8 2.6-.4z"/>',
    medkit: '<rect x="3.5" y="7" width="17" height="13" rx="2.5"/><path d="M9 7V4.5h6V7M12 10.5v6M9 13.5h6"/>',
    crown: '<path d="M3.5 8l4.2 3.6L12 5l4.3 6.6L20.5 8l-1.8 10.5H5.3z"/><path d="M5.5 21h13"/>',
    mask: '<path d="M12 3c-4.1 0-6.8 3.1-6.8 7.4v6.9A3.7 3.7 0 0 0 8.9 21h6.2a3.7 3.7 0 0 0 3.7-3.7v-6.9C18.8 6.1 16.1 3 12 3z"/><path d="M6.5 10h11v3.6h-11zM9.5 11.8h.01M14.5 11.8h.01M10.2 17.4h3.6"/>',
    land: '<path d="M3.5 7l5.5-2.5 6 2.5 5.5-2.5v12.5L15 19.5l-6-2.5-5.5 2.5zM9 4.5V17M15 7v12.5"/>',
    gift: '<rect x="4" y="10.5" width="16" height="10" rx="1.5"/><path d="M3 7.5h18v3H3zM12 7.5v13M12 7.5c-1.3-3.3-5.6-4.4-5.6-1.7 0 1.4 2.6 1.7 5.6 1.7zm0 0c1.3-3.3 5.6-4.4 5.6-1.7 0 1.4-2.6 1.7-5.6 1.7z"/>',
    star: '<path d="M12 3.3l2.6 5.4 5.9.8-4.3 4.1 1 5.9L12 16.7l-5.2 2.8 1-5.9-4.3-4.1 5.9-.8z"/>'
  };
  // The aces are drawn in colour like cash and fans: their own colour, a lighter shine, metal and dark parts,
  // and one ink outline on top.
  const ACE_ICONS = {
    faust: '<path class="a1" d="M4.6 10.5h14.8V15a5.5 5.5 0 0 1-5.5 5.5h-3.8A5.5 5.5 0 0 1 4.6 15z"/><path class="a1" d="M4.6 6.9a1.85 1.85 0 0 1 3.7 0v5.6H4.6zM8.3 6.9a1.85 1.85 0 0 1 3.7 0v5.6H8.3zM12 6.9a1.85 1.85 0 0 1 3.7 0v5.6H12zM15.7 6.9a1.85 1.85 0 0 1 3.7 0v5.6h-3.7z"/><rect class="a7" x="4.6" y="12.4" width="9" height="3.7" rx="1.85"/><path class="a8" d="M6 6.7v1.6M9.7 6.7v1.6M13.4 6.7v1.6M17.1 6.7v1.6M15.5 17.6h1.6"/><path class="a0" d="M4.6 10.5V15a5.5 5.5 0 0 0 5.5 5.5h3.8a5.5 5.5 0 0 0 5.5-5.5V6.9a1.85 1.85 0 0 0-3.7 0v5.6M15.7 6.9a1.85 1.85 0 0 0-3.7 0v5.6M12 6.9a1.85 1.85 0 0 0-3.7 0v5.6M8.3 6.9a1.85 1.85 0 0 0-3.7 0v5.6M6.45 12.4h5.3a1.85 1.85 0 0 1 0 3.7h-5.3a1.85 1.85 0 0 1 0-3.7z"/>',
    shirt: '<rect class="a4" x="3" y="18.5" width="12.5" height="2.6" rx="1.3"/><path class="a1" d="M5.37 15.51l7-10 4.26 2.98-7 10z"/><path class="a7" d="M8.06 11.67l.92-1.32 4.26 2.98-.92 1.32z"/><ellipse class="a4" cx="14.5" cy="7" rx="2.6" ry="1.1" transform="rotate(35 14.5 7)"/><circle class="a4" cx="19.6" cy="3.6" r="1.8"/><path class="a8" d="M7.2 14.9l2.3-3.3M10.6 10l1.6-2.3"/><path class="a0" d="M5.37 15.51l7-10 4.26 2.98-7 10zM13.6 12l4 8.5M16.4 20.5h2.6M4.3 18.5h11.2a1.3 1.3 0 0 1 0 2.6H4.3a1.3 1.3 0 0 1 0-2.6zM17.2 6.4l.6-.6M19.4 7.6l.2-.8"/><ellipse class="a0" cx="14.5" cy="7" rx="2.6" ry="1.1" transform="rotate(35 14.5 7)"/><circle class="a0" cx="19.6" cy="3.6" r="1.8"/>',
    bombe: '<path class="a1" d="M3.5 10a8.5 7.5 0 0 1 17 0c-1.4-1-2.85-1-4.25 0-1.4-1-2.85-1-4.25 0-1.4-1-2.85-1-4.25 0-1.4-1-2.85-1-4.25 0z"/><path class="a7" d="M10 2.8c-1.2 1.8-1.8 4.2-1.75 7.2 1.25-.9 2.5-1 3.75-.1 1.25-.9 2.5-.8 3.75.1.05-3-.55-5.4-1.75-7.2a8.9 8.9 0 0 0-4 0z"/><path class="a9" d="M8 15.5h8v5a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1z"/><path class="a8" d="M6 7.4a7 6 0 0 1 2.4-2.9"/><path class="a0" d="M3.5 10a8.5 7.5 0 0 1 17 0c-1.4-1-2.85-1-4.25 0-1.4-1-2.85-1-4.25 0-1.4-1-2.85-1-4.25 0-1.4-1-2.85-1-4.25 0zM4 10.2l4.5 5.3M20 10.2l-4.5 5.3M12 9.9v5.6M8 15.5h8v5a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1zM8 18.5h8"/>',
    drohne: '<ellipse class="a3" cx="5.5" cy="6.5" rx="3.8" ry="1.3"/><ellipse class="a3" cx="18.5" cy="6.5" rx="3.8" ry="1.3"/><rect class="a1" x="7.5" y="9.5" width="9" height="6" rx="2.2"/><circle class="a4" cx="12" cy="12.5" r="1.7"/><circle class="a6" cx="11.5" cy="12" r=".5"/><path class="a8" d="M9.2 11v2"/><path class="a0" d="M9.7 9.5h4.6a2.2 2.2 0 0 1 2.2 2.2v1.6a2.2 2.2 0 0 1-2.2 2.2H9.7a2.2 2.2 0 0 1-2.2-2.2v-1.6a2.2 2.2 0 0 1 2.2-2.2zM5.5 6.5v1.5l2.4 2M18.5 6.5v1.5l-2.4 2M9.5 15.5L8 18.5M14.5 15.5l1.5 3M6.8 18.5h2.4M14.8 18.5h2.4"/><ellipse class="a0" cx="5.5" cy="6.5" rx="3.8" ry="1.3"/><ellipse class="a0" cx="18.5" cy="6.5" rx="3.8" ry="1.3"/>',
    fans: '<path class="a7" d="M1.8 20.5c0-3.4 1.9-5.7 4.4-5.7s4.4 2.3 4.4 5.7zM13.4 20.5c0-3.4 1.9-5.7 4.4-5.7s4.4 2.3 4.4 5.7z"/><circle class="a7" cx="6.2" cy="10.8" r="2.4"/><circle class="a7" cx="17.8" cy="10.8" r="2.4"/><path class="a1" d="M6.3 21.5c0-4.3 2.4-7.1 5.7-7.1s5.7 2.8 5.7 7.1z"/><circle class="a1" cx="12" cy="9.8" r="3"/><path class="a8" d="M10.6 8.6a1.6 1.6 0 0 1 1.2-1"/><path class="a0" d="M1.8 20.5c0-3.4 1.9-5.7 4.4-5.7 1 0 1.9.4 2.6 1M22.2 20.5c0-3.4-1.9-5.7-4.4-5.7-1 0-1.9.4-2.6 1M6.3 21.5c0-4.3 2.4-7.1 5.7-7.1s5.7 2.8 5.7 7.1zM8.6 9.9a2.4 2.4 0 1 0-1.8 3.2M15.4 9.9a2.4 2.4 0 1 1 1.8 3.2M3.5 5.5l1.6 1.4M20.5 5.5l-1.6 1.4M12 2.2v1.8"/><circle class="a0" cx="12" cy="9.8" r="3"/>',
    rakete: '<g transform="rotate(40 12 12)"><path class="a5" d="M10.5 17.1h3L12 21.7z"/><path class="a7" d="M9.4 12.5L6.8 16.5V18l2.6-1.8zM14.6 12.5l2.6 4V18l-2.6-1.8z"/><path class="a1" d="M9.4 7.5c0-1.8.8-3.6 2.6-5 1.8 1.4 2.6 3.2 2.6 5v8H9.4z"/><path class="a6" d="M9.4 7.5c0-1.8.8-3.6 2.6-5 1.8 1.4 2.6 3.2 2.6 5z"/><rect class="a4" x="10.2" y="15.5" width="3.6" height="1.6"/><rect class="a6" x="9.4" y="10.6" width="5.2" height="1.3"/><path class="a8" d="M10.8 8.6v1M10.8 12.8v1.6"/><path class="a0" d="M9.4 7.5c0-1.8.8-3.6 2.6-5 1.8 1.4 2.6 3.2 2.6 5v8H9.4zM9.4 7.5h5.2M9.4 12.5L6.8 16.5V18l2.6-1.8M14.6 12.5l2.6 4V18l-2.6-1.8M10.2 15.5v1.6h3.6v-1.6M10.5 17.1l1.5 4.6 1.5-4.6"/></g><circle class="a3" cx="3.6" cy="20.4" r="1.5"/><circle class="a0" cx="3.6" cy="20.4" r="1.5"/>',
    birne: '<path class="a1" d="M2.5 2.5h19v3h-19z"/><path class="a4" d="M9.9 11.8h3.4l.6 1.6H9.3z"/><circle class="a4" cx="11.6" cy="17.6" r="4.6"/><path class="a1" d="M7.4 15.8a4.6 4.6 0 0 1 8.4 0z"/><circle class="a2" cx="9.9" cy="16.9" r=".8"/><path class="a8" d="M8.6 18.6a3 3 0 0 0 1.4 2"/><path class="a0" d="M2.5 2.5h19v3h-19zM5.5 2.5l2 3M9.5 2.5l2 3M13.5 2.5l2 3M17.5 2.5l2 3M16.2 5.5l-1.2 2M14.5 8.4l-1.2 2M12.8 11.2l-.4.6M9.9 11.8h3.4l.6 1.6H9.3zM7.4 15.8h8.4M18 14.6h3.5M18.6 17.6h3M18 20.6h2.4"/><circle class="a0" cx="11.6" cy="17.6" r="4.6"/>',
    tornado: '<rect class="a1" x="3" y="2.8" width="18" height="3.6" rx="1.8"/><rect class="a7" x="5.4" y="7.3" width="13" height="3.1" rx="1.55"/><rect class="a1" x="7.8" y="11.3" width="8.8" height="2.9" rx="1.45"/><rect class="a7" x="9.4" y="15.1" width="5.2" height="2.6" rx="1.3"/><path class="a1" d="M10.9 18.6h2.4l-1.5 3.4z"/><rect class="a9" x="2.6" y="13.6" width="2.4" height="1.8" rx=".4" transform="rotate(-20 3.8 14.5)"/><circle class="a4" cx="19.8" cy="17" r="1.2"/><path class="a8" d="M5.6 4.6h6M8.2 8.85h4M10.2 12.75h2.4"/><path class="a0" d="M4.8 2.8h14.4a1.8 1.8 0 0 1 0 3.6H4.8a1.8 1.8 0 0 1 0-3.6zM6.95 7.3h9.9a1.55 1.55 0 0 1 0 3.1h-9.9a1.55 1.55 0 0 1 0-3.1zM9.25 11.3h5.9a1.45 1.45 0 0 1 0 2.9h-5.9a1.45 1.45 0 0 1 0-2.9zM10.7 15.1h2.6a1.3 1.3 0 0 1 0 2.6h-2.6a1.3 1.3 0 0 1 0-2.6zM10.9 18.6l.9 3.4 1.5-3.4"/><rect class="a0" x="2.6" y="13.6" width="2.4" height="1.8" rx=".4" transform="rotate(-20 3.8 14.5)"/><circle class="a0" cx="19.8" cy="17" r="1.2"/>',
    laser: '<ellipse class="a5" cx="12" cy="19.6" rx="7" ry="2.4"/><path class="a1" d="M10.4 5.4h3.2l2 14.2H8.4z"/><path class="a6" d="M11.5 5.4h1l.9 14.2h-2.8z"/><rect class="a1" x="2.5" y="2" width="5.6" height="2.8" rx=".5"/><rect class="a1" x="15.9" y="2" width="5.6" height="2.8" rx=".5"/><rect class="a3" x="9.2" y="1.6" width="5.6" height="3.8" rx="1"/><path class="a0" d="M5.3 2v2.8M18.7 2v2.8M10.4 5.4L8.4 19.6M13.6 5.4l2 14.2M8.1 3.4h1.1M14.8 3.4h1.1M3.8 15.6l-1.6-1.2M20.2 15.6l1.6-1.2M6 12.8l-1.2-1.4M18 12.8l1.2-1.4"/><rect class="a0" x="2.5" y="2" width="5.6" height="2.8" rx=".5"/><rect class="a0" x="15.9" y="2" width="5.6" height="2.8" rx=".5"/><rect class="a0" x="9.2" y="1.6" width="5.6" height="3.8" rx="1"/><ellipse class="a0" cx="12" cy="19.6" rx="7" ry="2.4"/>',
    beben: '<rect class="a1" x="2.5" y="12.5" width="19" height="9" rx="2"/><path class="a7" d="M2.5 17.5h19v2a2 2 0 0 1-2 2h-15a2 2 0 0 1-2-2z"/><path class="a4" d="M11.2 12.5l-1.7 3.2 2.1 1.7-1.7 4.1h2.1l1.6-4-1.9-1.7 1.6-3.3z"/><rect class="a9" x="4" y="6.6" width="2.8" height="2.2" rx=".4" transform="rotate(-18 5.4 7.7)"/><rect class="a4" x="17" y="5.2" width="2.4" height="2" rx=".4" transform="rotate(22 18.2 6.2)"/><path class="a8" d="M4.6 14.6h4.4M15.4 14.6h3"/><path class="a0" d="M4.5 12.5h15a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-15a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2zM11.2 12.5l-1.7 3.2 2.1 1.7-1.7 4.1M13.3 12.5l-1.6 3.3 1.9 1.7-1.6 4M8 10.4a5.6 5.6 0 0 1 8 0M10 8.2a3 3 0 0 1 4 0"/><rect class="a0" x="4" y="6.6" width="2.8" height="2.2" rx=".4" transform="rotate(-18 5.4 7.7)"/><rect class="a0" x="17" y="5.2" width="2.4" height="2" rx=".4" transform="rotate(22 18.2 6.2)"/>'
  };
  const aceIcon = id => `<svg class="mxd-pet__i mxd-pet__i--ace" viewBox="0 0 24 24" aria-hidden="true" focusable="false">${ACE_ICONS[id]}</svg>`;
  const icon = id => `<svg class="mxd-pet__i mxd-pet__i--${id}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">${ICONS[id]}</svg>`;
  const cashIcon = () => icon('cash');
  // An amount with its note in front, kept on one line.
  const cashText = text => `<span class="mxd-pet__amt">${cashIcon()}${text}</span>`;
  const setText = (element, text) => {
    if (!element || element.textContent === text) return false;
    element.textContent = text;
    return true;
  };
  // Counters: every digit sits in a box of its own width, so a rolling number never shifts sideways.
  const setCount = (element, text) => {
    if (!element || element.dataset.count === text) return;
    element.dataset.count = text;
    const [number, unit] = text.split(' ');
    element.innerHTML = [...number].map(ch => (/\d/.test(ch) ? `<i>${ch}</i>` : escapeHtml(ch))).join('') + (unit ? `<small>${escapeHtml(unit)}</small>` : '');
  };
  const show = (element, on) => {
    if (element && element.hidden === on) element.hidden = !on;
  };
  // The price sits as a tag on the top edge of its button, where the thumb pressing it does not cover it.
  const price = (amount, attrs, caption) => `<button type="button" class="mxd-pet__price${S.cash < amount ? ' is-short' : ''}" ${attrs}><em>${cashIcon()}${fmt(amount)}</em><b>${caption}</b></button>`;
  // Small amounts keep their cents or a decimal, big ones get the short form.
  const decimal = value => (value < 10 ? value.toFixed(1).replace('.', ',') : fmt(value));
  const rateText = value => `${fmt(value)}/min`;

  // Messages hang under the hustler card: the newest drops in right under it and pushes the other down,
  // each fades out when its time is up, and a message with the same key or text is updated in place
  // instead of stacking twice.
  const feedNotes = [];
  const NOTE_MAX = 2;
  const dropNote = note => {
    const at = feedNotes.indexOf(note);
    if (at < 0) return;
    feedNotes.splice(at, 1);
    clearTimeout(note.timer);
    note.el.classList.add('is-out');
    setTimeout(() => note.el.remove(), 220);
  };
  const notify = (html, mood, ms, key = '') => {
    let note = feedNotes.find(one => (key && one.key === key) || one.html === html);
    if (!note) {
      note = { el: document.createElement('div'), key, html: '', mood: '' };
      feedNotes.unshift(note);
      els.feed.prepend(note.el);
      while (feedNotes.length > NOTE_MAX) dropNote(feedNotes[feedNotes.length - 1]);
    }
    if (note.html !== html || note.mood !== mood) {
      note.el.className = `mxd-pet__msg${mood ? ` is-${mood}` : ''}`;
      note.el.innerHTML = `<div><div class="mxd-pet__msg-tab">${html}</div></div>`;
      note.html = html;
      note.mood = mood;
    }
    clearTimeout(note.timer);
    note.timer = setTimeout(() => dropNote(note), ms);
    feedNotes.forEach((one, i) => {
      one.el.style.zIndex = String(NOTE_MAX - i);
    });
  };
  const showToast = (text, mood = '') => notify(escapeHtml(text), mood, 2600);
  const banner = (key, html, mood, ms) => notify(html, mood, ms, key);

  // Cash and fans fly from where they were made into their counter. The amount is split over the notes,
  // a few round ones and the rest on the first, and the counter only counts a note once it lands.
  let pendingCash = 0;
  let pendingFans = 0;
  const splitAmount = (amount, n) => {
    const step = 10 ** Math.max(0, Math.floor(Math.log10(amount / n)) - 1);
    const part = Math.max(0, Math.floor(amount / n / step) * step);
    const notes = Array(n).fill(part);
    notes[0] = amount - part * (n - 1);
    return notes.filter(value => value > 0);
  };
  const fly = (from, amount, fans = false) => {
    const target = fans ? els.fans : els.cash;
    if (!target || amount <= 0) return;
    const to = centerOf(target.querySelector('.mxd-pet__i') || target);
    splitAmount(amount, clamp(Math.ceil(Math.log10(amount + 1) * 1.6), 1, 6)).forEach((value, i) => {
      flyers.push({ x: from.x, y: from.y, tx: to.x, ty: to.y, t: -i * 0.09, dur: 0.65 + i * 0.03, ox: (Math.random() - 0.5) * 70, oy: -36 - Math.random() * 36, spin: (Math.random() - 0.5) * 1.2, fans, value });
    });
    if (fans) pendingFans += amount;
    else pendingCash += amount;
  };
  const drawFlyers = () => {
    for (let i = flyers.length - 1; i >= 0; i -= 1) {
      const f = flyers[i];
      if (f.t < 0) continue;
      const p = Math.min(1, f.t / f.dur);
      const e = p * p * (3 - 2 * p);
      const x = lerp(f.x + f.ox * Math.sin(p * Math.PI), f.tx, e);
      const y = lerp(f.y + f.oy * Math.sin(p * Math.PI), f.ty, e);
      const size = 8 + Math.sin(p * Math.PI) * 3 - p * 2;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(f.spin * (1 - p));
      (f.fans ? drawFan : drawCash)(ctx, 0, 0, size);
      ctx.restore();
      // Each note says what it is worth while it is on its way.
      if (p < 0.75) {
        ctx.save();
        ctx.globalAlpha = Math.min(1, (0.75 - p) * 5);
        ctx.font = `700 11px ${FONT}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.lineJoin = 'round';
        ctx.lineWidth = 3;
        ctx.strokeStyle = PAPER;
        ctx.strokeText(`+${fmt(f.value)}`, x, y - size * 1.7);
        ctx.fillStyle = f.fans ? '#1f6fd6' : '#2f8a3a';
        ctx.fillText(`+${fmt(f.value)}`, x, y - size * 1.7);
        ctx.restore();
      }
    }
  };
  const updateFlyers = dt => {
    for (let i = flyers.length - 1; i >= 0; i -= 1) {
      const f = flyers[i];
      f.t += dt;
      if (f.t >= f.dur) {
        flyers.splice(i, 1);
        if (f.fans) pendingFans = Math.max(0, pendingFans - f.value);
        else pendingCash = Math.max(0, pendingCash - f.value);
        play('clink', f.fans);
        const target = f.fans ? els.fans : els.cash;
        target.classList.remove('is-bump');
        void target.offsetWidth;
        target.classList.add('is-bump');
      }
    }
  };

  // ---------------------------------------------------------------------------------------------
  // HUD: the hustler and the next move top left, cash and fans top right,
  // the raid bottom left, the shop bottom right.

  let crestLook = '';
  let shownCash = S.cash;
  let shownFans = S.fans;
  const renderHud = dt => {
    const playing = founded();
    root.classList.toggle('is-playing', playing);
    if (!playing) return;
    if (crestLook !== `${S.seed}:${S.name}`) {
      crestLook = `${S.seed}:${S.name}`;
      paintPortrait(els.crest, 96);
      setText(els.name, playerName());
    }
    setText(els.hqLevel, String(hqLevel()));
    const fighting = raid && raid.phase !== 'end';
    root.classList.toggle('is-raid', Boolean(fighting));
    els.lifeFill.style.width = `${clamp(K.hp / hustlerMaxHp(), 0, 1) * 100}%`;
    els.life.classList.toggle('is-low', K.hp / hustlerMaxHp() < 0.35);
    // The counters roll toward the real numbers, leaving out what is still flying in.
    const cashTo = Math.max(0, S.cash - pendingCash);
    const fansTo = Math.max(0, S.fans - pendingFans);
    shownCash = Math.abs(cashTo - shownCash) < 1 ? cashTo : ease(shownCash, cashTo, 14, dt);
    shownFans = Math.abs(fansTo - shownFans) < 1 ? fansTo : ease(shownFans, fansTo, 14, dt);
    setCount(els.cashCount, fmt(shownCash));
    setText(els.cashRate, `+${fmt(totalCashRate())}/min`);
    setCount(els.fansCount, fmt(shownFans));
    setText(els.fansRate, `+${rateText(totalFansRate())}`);
    const buyable = fighting ? 0 : BUILD_TABS.reduce((sum, [tab]) => sum + buyableIn(tab), 0);
    if (setText(els.buildCount, String(buyable))) {
      els.buildCount.classList.remove('is-pop');
      void els.buildCount.offsetWidth;
      els.buildCount.classList.add('is-pop');
    }
    show(els.buildCount, buyable > 0);
    const shopping = Boolean(panel && panel.kind === 'build');
    show(els.wallet, shopping);
    if (shopping) setCount(els.wallet.querySelector('b'), fmt(shownCash));
    renderMove();
    renderRaid();
    renderWar();
    renderHeist(fighting);
    renderPay();
    renderMagnet(fighting);
    renderDaily(fighting);
    renderChief();
    show(els.rescue, Boolean(rescue));
    if (rescue) els.rescue.style.setProperty('--left', (rescue.left / RESCUE_TIME).toFixed(3));
    renderTools();
    renderCard();
    revealSelected(dt);
    if (panel) refreshPanel();
  };
  // A freshly picked item that would sit under its card glides up above it, unless a finger is on the map.
  let revealId = null;
  let revealT = 0;
  const revealSelected = dt => {
    const item = selectedItem();
    if (!item) {
      revealId = null;
      return;
    }
    if (item.id !== revealId) {
      revealId = item.id;
      revealT = 0.9;
    }
    if (revealT <= 0 || press || els.card.hidden) return;
    revealT -= dt;
    const top = els.card.getBoundingClientRect().top - root.getBoundingClientRect().top;
    const over = screenBox(item).b - (top - 16);
    if (over <= 0) return;
    cam.y += (over / (cw() * TILT)) * Math.min(1, dt * 9);
    clampCam();
  };
  const ring = fraction => `<svg class="mxd-pet__ring" viewBox="0 0 40 40" aria-hidden="true"><circle cx="20" cy="20" r="17"/><circle cx="20" cy="20" r="17" style="stroke-dashoffset:${(106.8 * (1 - clamp(fraction, 0, 1))).toFixed(1)}"/></svg>`;
  let heistSig = '';
  // Raids stay greyed out behind a lock until the first hero joins, and make way for a Problem about to come.
  // The Pate's life across the screen while he is in the fight.
  const renderChief = () => {
    const chief = raid && raid.phase === 'fight' && enemies.find(enemy => enemy.chief && !enemy.fleeing);
    show(els.chief, Boolean(chief));
    if (chief) els.chief.style.setProperty('--hp', clamp(chief.hp / chief.max, 0, 1).toFixed(3));
  };
  // The magnet waits beside the shop until it is used, and goes once there is nothing left to collect.
  const renderMagnet = fighting => {
    if (S.magnet && !S.items.some(item => (item.stock || 0) >= 1)) S.magnet = false;
    show(els.magnet, S.magnet && !fighting && !heist && !travel);
  };
  // The rewards button: a pip for each of the round's tasks, the weekend event if one is on, and how much waits to be collected.
  let dailySig = '';
  const renderDaily = fighting => {
    const shown = !fighting && !heist && !travel;
    show(els.daily, shown);
    if (!shown) return;
    const { tasks } = taskRound();
    const done = tasks.filter(task => task.done).length;
    const ready = dayCount();
    const live = eventOn();
    const sig = `${done}|${tasks.length}|${ready}|${live ? live.id : ''}`;
    if (sig === dailySig) return;
    dailySig = sig;
    els.daily.classList.toggle('is-ready', ready > 0);
    els.daily.setAttribute('aria-label', `Belohnungen: ${done} von ${tasks.length} Aufgaben${ready ? `, ${ready} zum Abholen` : ''}${live ? `, ${live.name}` : ''}`);
    const pips = tasks.map(task => `<s${task.done ? ' class="is-done"' : ''}></s>`).join('');
    els.daily.innerHTML = `<span class="mxd-pet__gift">${icon('gift')}${ready ? `<i>${ready}</i>` : ''}</span><span class="mxd-pet__reward"><b>${ready ? 'Abholen' : 'Belohnungen'}</b><span class="mxd-pet__pips">${pips}</span></span>`;
  };
  let paySig = '';
  const renderPay = () => {
    const shown = problemSoon();
    show(els.pay, shown);
    if (!shown || paySig) return;
    paySig = 'on';
    els.pay.innerHTML = cashIcon();
  };
  const renderHeist = fighting => {
    const shown = founded() && !fighting && !heist && !problemSoon();
    show(els.heist, shown);
    if (!shown) return;
    // Before the first hero there are no Raids yet, and the slab says so.
    const locked = !heistOpen();
    const ready = locked ? 0 : S.heists.length;
    const value = locked ? 'Raids' : ready ? 'Raid bereit' : formatClock(Math.max(0, Math.min(...S.heistBack) - Date.now()));
    const sig = `${locked}|${ready}|${value}`;
    if (sig === heistSig) return;
    heistSig = sig;
    els.heist.setAttribute('aria-label', locked ? 'Raids ab dem ersten Helden' : `Raids: ${value}`);
    els.heist.innerHTML = `<span class="mxd-pet__tile">${icon(locked ? 'lock' : 'mask')}${ready ? `<i>${ready}</i>` : ''}</span><span class="mxd-pet__body"><b>${value}</b></span>`;
  };
  // A battle in one slab where the next Problem sits in peace: the crew's tile, the gang beaten so far over the
  // balance of power (the crew's share in yellow, the gang's in its colour), and the gang's tile.
  let warSig = '';
  const renderWar = () => {
    const on = Boolean(heist && raid && raid.phase !== 'end');
    show(els.war, on);
    if (!on) {
      warSig = '';
      return;
    }
    if (warSig !== String(raid.heist.id)) {
      warSig = String(raid.heist.id);
      els.war.style.setProperty('--them', heist.color);
      els.war.setAttribute('aria-label', `Schlacht gegen ${heistGang(raid.heist)}`);
      els.war.innerHTML = `<span class="mxd-pet__tile">${icon('shield')}</span><span class="mxd-pet__body"><b data-war-count></b><span class="mxd-pet__war-bar"><i data-war-share></i></span></span><span class="mxd-pet__tile">${icon('mask')}</span>`;
    }
    setText(els.war.querySelector('[data-war-count]'), `${raid.killed} / ${raid.total}`);
    els.war.querySelector('[data-war-share]').style.width = `${(clamp(heist.share, 0.04, 0.96) * 100).toFixed(1)}%`;
  };
  // How the battle went, in the words of a war report.
  const battleGrade = done => {
    if (done.result !== 'won') return done.fled ? 'Rückzug' : 'Niederlage';
    const lost = done.lost.length / Math.max(1, heist ? heist.items0 : 1);
    return !lost ? 'Glorreicher Sieg' : lost < 0.25 ? 'Klarer Sieg' : lost < 0.5 ? 'Knapper Sieg' : 'Pyrrhussieg';
  };
  let raidSig = '';
  // A Problem shows its last minute as a countdown and stays while it is on; a Raid has its own war slab.
  const renderRaid = () => {
    const soon = problemSoon();
    const shown = soon || (Boolean(raid) && !(heist && raid.phase !== 'end'));
    show(els.raid, shown);
    if (!shown) return;
    let mood = '';
    let title;
    let value;
    let fraction = 0;
    let glyph = 'alert';
    if (soon) {
      const left = Math.max(0, threatAt() - Date.now());
      mood = 'alert';
      title = S.revenge && S.revenge.at <= S.raidAt ? 'Gegenangriff naht' : 'Problem naht';
      value = formatClock(left);
      fraction = 1 - left / PROBLEM_NOTICE;
    } else if (raid.phase === 'warn') {
      mood = 'alert';
      title = 'Problem naht';
      value = `${Math.ceil(raid.t)} s`;
      fraction = 1 - raid.t / 5;
    } else if (raid.phase === 'fight') {
      mood = 'alert';
      title = raid.waves > 1 ? `Welle ${raid.wave || 1}/${raid.waves}` : `Problem ${raid.count || raid.n}`;
      value = `${raid.killed} / ${raid.total}`;
      fraction = raid.killed / Math.max(1, raid.total);
    } else {
      mood = raid.result === 'won' ? 'good' : '';
      title = raid.heist ? battleGrade(raid) : raid.result === 'won' ? 'Abgewehrt' : 'Abgezogen';
      value = `+${fmt(raid.earned + raid.bonus)}`;
      fraction = 1;
      glyph = raid.result === 'won' ? 'check' : 'shield';
    }
    const sig = `${mood}|${title}|${value}|${fraction.toFixed(2)}|${glyph}`;
    if (sig === raidSig) return;
    raidSig = sig;
    els.raid.className = `mxd-pet__slab mxd-pet__raid${mood ? ` is-${mood}` : ''}`;
    els.raid.setAttribute('aria-label', `${title}: ${value}`);
    els.raid.innerHTML = `<span class="mxd-pet__tile">${ring(fraction)}${icon(glyph)}</span><span class="mxd-pet__body"><b>${value}</b></span>`;
  };
  // The next move: a goal with a cash reward, tap to collect once it is done.
  let moveSig = '';
  const renderMove = () => {
    const move = currentMove();
    const m = moveInfo(move);
    const done = m.have >= m.need;
    const progress = m.need > 1 ? `${fmt(m.have)} / ${fmt(m.need)}` : '';
    const sig = `${move.n}|${m.text}|${progress}|${done}|${move.reward}`;
    if (sig === moveSig) return;
    moveSig = sig;
    els.move.classList.toggle('is-done', done);
    els.move.setAttribute('aria-label', done ? `Move geschafft, ${fmt(move.reward)} Cash abholen` : `Nächster Move: ${m.text}`);
    els.move.innerHTML = `<em>${icon(done ? 'check' : 'flag')}</em><b>${done ? 'Abholen' : escapeHtml(m.text)}${progress && !done ? `<small>${progress}</small>` : ''}</b><i><s style="width:${(clamp(m.have / m.need, 0, 1) * 100).toFixed(0)}%"></s></i><b class="mxd-pet__move-reward">${cashIcon()}+${fmt(move.reward)}</b>`;
  };
  let toolSig = '';
  const renderTools = () => {
    const fighting = raid && raid.phase === 'fight';
    const list = fighting ? S.deck.filter(hasAce) : [];
    show(els.tools, list.length > 0);
    if (!list.length) {
      toolSig = '';
      return;
    }
    const sig = list.map(id => `${id}:${ACE_USES - toolUsed[id]}`).join();
    if (sig !== toolSig) {
      toolSig = sig;
      els.tools.innerHTML = list.map(id => `<button type="button" class="mxd-pet__tool" data-tool="${id}" style="--tc:${TRICK_COLOR[id]}" aria-label="${TOOLS[id].name}, noch ${ACE_USES - toolUsed[id]}×"><span><i></i>${aceIcon(id)}<b>${TOOLS[id].name}</b><em>${Array.from({ length: ACE_USES }, (_, k) => `<s${k < ACE_USES - toolUsed[id] ? '' : ' class="is-used"'}></s>`).join('')}</em></span></button>`).join('');
    }
    list.forEach(id => {
      const button = els.tools.querySelector(`[data-tool="${id}"]`);
      const left = toolCd[id];
      const empty = toolUsed[id] >= ACE_USES;
      button.style.setProperty('--cd', empty ? 0 : clamp(left / TOOLS[id].cool, 0, 1).toFixed(3));
      button.classList.toggle('is-empty', empty);
      const cooling = left > 0 || empty;
      if (button.classList.contains('is-cooling') && !cooling) {
        button.classList.remove('is-ready');
        void button.offsetWidth;
        button.classList.add('is-ready');
      }
      button.classList.toggle('is-cooling', cooling);
    });
  };

  // The card: whatever is selected, with what it does now and what the next level brings.
  const selectedItem = () => (selected && selected.kind === 'item' ? itemById(selected.id) : null);
  const statsOf = (item, level) => {
    const t = info(item);
    if (t.kind === 'promo') return [['fans', 'Fans', rateText(fansRateOf(item.type, level))], ['heart', 'Stabilität', fmt(hpOf(item.type, level))]];
    if (t.kind === 'sale') return [['bag', 'Bestellungen', `${decimal(ordersOf(item.type, level, false))}/min`], ['cash', 'Cash', `${fmt(cashRateOf(item.type, level))}/min`], ['heart', 'Stabilität', fmt(hpOf(item.type, level))]];
    if (item.type === 'hq') return [['up', 'Max. Stufe', String(levelCap(level))], ['cash', 'Pro Verkauf', fmt(payoutAt(level))], ['heart', 'Hustler', `${fmt(100 * 1.12 ** (level - 1))} LP`]];
    const life = ['heart', 'Leben', fmt(hpOf(item.type, level))];
    const dmg = dmgOf(item.type, level);
    const hit = ['target', 'Schaden', `${dmg < 100 ? dmg.toFixed(1).replace('.', ',') : fmt(dmg)}${t.hits ? ` ×${t.hits}` : ''}`];
    return [hit, t.perk, life];
  };
  const nextTierAt = level => {
    const tier = tierOf(level);
    if (tier < TIER_AT.length - 1) return TIER_AT[tier + 1];
    return 80 * 2 ** (tier - 4);
  };
  const lookBar = item => {
    const at = nextTierAt(item.level);
    const from = item.level >= 80 ? 80 * 2 ** (tierOf(item.level) - 5) : TIER_AT[tierOf(item.level)];
    const p = (item.level - from) / Math.max(1, at - from);
    const pic = ART[item.type] ? '' : `<img class="mxd-pet__next-pic" src="${itemThumb(item.type, at)}" alt="">`;
    return `<div class="mxd-pet__next">${pic}<div class="mxd-pet__next-info"><em>Nächstes Aussehen</em><div><b>${tierName(at)}</b><span>Stufe ${item.level} / ${at}</span></div><i><s style="width:${(clamp(p, 0, 1) * 100).toFixed(0)}%"></s></i></div></div>`;
  };
  let cardSig = '';
  let cardOut = 0;
  const badge = text => `<b class="mxd-pet__badge">${text}</b>`;
  // Painted art for the cards, where there is some: the section passes the asset URLs.
  const ART = Object.fromEntries((root.dataset.art || '').split(/\s+/).map(url => [(/hero-(\w+)\.jpg/.exec(url) || [])[1], url]).filter(([type]) => type));
  // Loaded in the background once the game is up and held on to, so the cards keep their art when the connection drops.
  const artKept = [];
  setTimeout(() => Object.values(ART).forEach(url => artKept.push(Object.assign(new window.Image(), { src: url }))), 1500);
  const thumbImg = (type, level) => (ART[type] ? `<img class="mxd-pet__thumb mxd-pet__thumb--art" src="${ART[type]}" alt="">` : `<img class="mxd-pet__thumb" src="${itemThumb(type, level)}" alt="">`);
  const thumbBox = (img, level = 0) => `<span class="mxd-pet__thumb-box">${img}${level ? badge(level) : ''}</span>`;
  const cardHead = (img, title, sub) => `<div class="mxd-pet__card-head">${img}<div class="mxd-pet__card-title"><b>${title}</b>${sub}</div><button type="button" class="mxd-pet__card-x" data-card="close" aria-label="Schließen">${icon('x')}</button></div>`;
  const renderCard = () => {
    let sig = 'none';
    let html = '';
    const fighting = raid && raid.phase !== 'end';
    // Moving something needs no card: it follows the finger and lands where it is let go.
    if (ghost && !ghost.moving) {
      const t = TYPES[ghost.type];
      const cost = buildCost(ghost.type);
      sig = `ghost|${ghost.type}|${ghost.ok}|${S.cash >= cost}`;
      html = `${cardHead(thumbBox(thumbImg(ghost.type, ghost.level), ghost.level), `${t.name} ${t.kind === 'crew' ? 'anheuern' : 'bauen'}`, ghost.ok ? `<em>${t.kind === 'crew' ? 'Zieh auf den Posten' : 'Zieh es an seinen Platz'}</em>` : '<span class="mxd-pet__tag is-alert">Hier ist kein Platz</span>')}
        <div class="mxd-pet__actions"><button type="button" class="mxd-pet__btn" data-card="cancel">Abbrechen</button>${price(cost, `data-card="place"${ghost.ok ? '' : ' disabled'}`, t.kind === 'crew' ? 'Anheuern' : 'Bauen')}</div>`;
    } else if (selected && selected.kind === 'hustler') {
      sig = `hustler|${fighting}|${K.dead > 0}`;
      html = `${cardHead(thumbBox(`<canvas class="mxd-pet__thumb mxd-pet__thumb--hustler" data-card-portrait></canvas>`), escapeHtml(playerName()), '<em data-v="act"></em>')}
        <div class="mxd-pet__card-body"><div class="mxd-pet__stats"><div class="mxd-pet__stat">${icon('heart')}<em>Leben</em><b data-v="hp"></b></div><div class="mxd-pet__stat">${icon('clock')}<em>Im Game seit</em><b>${formatSpan(Date.now() - S.founded)}</b></div><div class="mxd-pet__stat">${icon('alert')}<em>Probleme gelöst</em><b>${fmt(S.stats.raids)}</b></div></div></div>`;
    } else if (selected && selected.kind === 'pay') {
      if (!problemSoon()) selected = null;
      else {
        const cost = protectionPrice();
        sig = `pay|${cost >= 1}`;
        html = `${cardHead(thumbBox(`<div class="mxd-pet__thumb mxd-pet__thumb--icon mxd-pet__thumb--pay">${cashIcon()}</div>`), 'Schutzgeld', '<em>Die Hälfte von deinem Cash</em>')}
          <div class="mxd-pet__card-body"><div class="mxd-pet__hint">Zahl die Bootlegs aus und dieser Angriff fällt aus. Das nächste Problem kommt wie gewohnt.</div></div>
          <div class="mxd-pet__actions"><button type="button" class="mxd-pet__btn" data-card="close">Abbrechen</button>${price(cost, `data-card="pay"${cost >= 1 ? '' : ' disabled'}`, 'Zahlen')}</div>`;
      }
    } else if (selected && selected.kind === 'plot') {
      const cost = landPrice();
      sig = `plot|${selected.key}|${S.cash >= cost}|${fighting}`;
      html = `${cardHead(thumbBox(`<div class="mxd-pet__thumb mxd-pet__thumb--icon">${icon('land')}</div>`), 'Neues Revier', `<em>${CHUNK} × ${CHUNK} Felder für deinen Block</em>`)}
        <div class="mxd-pet__card-body"><div class="mxd-pet__hint">Jedes Revier kostet mehr als das letzte. Mehr Platz heißt mehr Promo, mehr Verkauf und Posten für mehr Helden.</div></div>
        <div class="mxd-pet__actions">${price(cost, `data-card="buy-land"${fighting ? ' disabled' : ''}`, 'Übernehmen')}</div>`;
    } else if (selectedItem()) {
      const item = selectedItem();
      const t = info(item);
      const cost = item.ruin ? repairCost(item) : upgradeCost(item);
      const maxed = item.level >= maxLevel(item.type);
      const stock = Math.floor(item.stock || 0);
      const stocked = (t.kind === 'promo' || t.kind === 'sale') && !t.online && !item.ruin;
      sig = `item|${item.id}|${item.level}|${S.cash >= cost}|${maxed}|${item.ruin}|${Boolean(item.rebuild)}|${fighting}|${hqLevel()}`;
      const now = statsOf(item, item.level);
      const next = maxed ? null : statsOf(item, item.level + 1);
      const stats = now.map(([ic, name, value], i) => `<div class="mxd-pet__stat">${icon(ic)}<em>${name}</em><b>${value}${next && next[i][2] !== value ? `<s>${next[i][2]}</s>` : ''}</b></div>`).join('');
      const stockRow = stocked ? `<div class="mxd-pet__stock"><div><em>${t.kind === 'promo' ? 'Neue Fans' : 'Kasse'}</em><b data-v="stock"></b></div><i><s data-v="stock-bar"></s></i></div>`
        : t.online ? '<div class="mxd-pet__stock"><div><em>Läuft 24/7, das Cash kommt direkt rein</em><b data-v="paid"></b></div></div>' : '';
      const up = item.ruin
        ? price(cost, `data-card="repair"${fighting ? ' disabled' : ''}`, 'Reparieren')
        : maxed
        ? `<button type="button" class="mxd-pet__btn" disabled>${icon('lock')}Base ${baseFor(item.level + 1)} nötig</button>`
        : price(cost, `data-card="upgrade" data-hold="upgrade"${item.rebuild ? ' disabled' : ''}`, `Auf Stufe ${item.level + 1}`);
      const collect = stocked ? `<button type="button" class="mxd-pet__btn mxd-pet__btn--mint" data-card="collect" data-v="collect"${stock >= 1 ? '' : ' disabled'}>${icon(t.kind === 'promo' ? 'fans' : 'cash')}Einsammeln</button>` : '';
      const showBtn = item.type === 'buehne' && !fighting ? `<button type="button" class="mxd-pet__btn mxd-pet__btn--signal" data-card="show" data-v="show">${icon('mic')}<span></span></button>` : '';
      const crewHint = item.ruin ? '<div class="mxd-pet__hint">Steht still, bis du es reparierst. Danach baut es sich wieder auf und läuft weiter.</div>'
        : t.hero ? '<div class="mxd-pet__hint">Kämpft bei jedem Problem und jedem Raid mit und zündet alle paar Sekunden sein Special. Wer fällt, ist für immer weg.</div>' : '';
      const state = item.ruin ? '<span class="mxd-pet__tag is-alert">Zerstört</span>' : item.rebuild ? '<span class="mxd-pet__tag">Wird aufgebaut</span>' : `<span class="mxd-pet__tag">${tierName(item.level)}</span>`;
      html = `${cardHead(thumbBox(thumbImg(item.type, item.level), item.level), t.name, state)}
        <div class="mxd-pet__card-body"><div class="mxd-pet__stats">${stats}</div>${stockRow}${maxed ? '' : lookBar(item)}${crewHint}</div>
        <div class="mxd-pet__actions">${showBtn}${collect}${up}</div>`;
    }
    if (sig !== cardSig) {
      cardSig = sig;
      clearTimeout(cardOut);
      if (html) {
        els.card.innerHTML = html;
        els.card.hidden = false;
        els.card.classList.remove('is-leaving');
        const portrait = els.card.querySelector('[data-card-portrait]');
        if (portrait) paintPortrait(portrait, 64);
      } else if (!els.card.hidden) {
        // Slide out with the last content still on it, then let go.
        els.card.classList.add('is-leaving');
        cardOut = setTimeout(() => {
          els.card.hidden = true;
          els.card.classList.remove('is-leaving');
          els.card.innerHTML = '';
        }, 220);
      }
    }
    // Live values without rebuilding the buttons under the finger.
    const item = selectedItem();
    if (item) {
      const stockEl = els.card.querySelector('[data-v="stock"]');
      if (stockEl) setText(stockEl, `${fmt(item.stock || 0)} / ${fmt(capOf(item))}`);
      const collectEl = els.card.querySelector('[data-v="collect"]');
      if (collectEl) collectEl.disabled = (item.stock || 0) < 1;
      const bar = els.card.querySelector('[data-v="stock-bar"]');
      if (bar) bar.style.width = `${(clamp((item.stock || 0) / Math.max(1, capOf(item)), 0, 1) * 100).toFixed(1)}%`;
      const paid = els.card.querySelector('[data-v="paid"]');
      if (paid) setText(paid, fmt(item.paid || 0));
      const showEl = els.card.querySelector('[data-v="show"]');
      if (showEl) {
        const now = Date.now();
        const ready = !showLive(item, now) && (item.showAt || 0) <= now;
        setText(showEl.querySelector('span'), ready ? 'Show starten' : showLive(item, now) ? `Show läuft ${formatClock(item.showUntil - now)}` : `Nächste Show ${formatClock(item.showAt - now)}`);
        showEl.disabled = !ready || !standing(item);
      }
    }
    if (selected && selected.kind === 'pay') {
      const tag = els.card.querySelector('[data-card="pay"] em');
      if (tag) tag.innerHTML = `${cashIcon()}${fmt(protectionPrice())}`;
    }
    if (selected && selected.kind === 'hustler') {
      const act = els.card.querySelector('[data-v="act"]');
      if (act) setText(act, actLine());
      const hp = els.card.querySelector('[data-v="hp"]');
      if (hp) setText(hp, `${fmt(Math.ceil(K.hp))} / ${fmt(Math.ceil(hustlerMaxHp()))}`);
    }
  };

  // ---------------------------------------------------------------------------------------------
  // Panels: a drawer on desktop, a sheet on phones.

  let lastFocus = null;
  let panelSig = '';
  const openPanel = (kind, data = null) => {
    if (!panel) lastFocus = document.activeElement;
    panel = { kind, data };
    panelSig = '';
    els.modal.hidden = false;
    root.classList.add('has-panel');
    refreshPanel(true);
    els.panel.scrollTop = 0;
    play('swish');
    requestAnimationFrame(() => els.panel.classList.add('is-open'));
  };
  const closePanel = () => {
    if (!panel) return;
    panel = null;
    els.panel.classList.remove('is-open');
    root.classList.remove('has-panel');
    setTimeout(() => {
      if (!panel) els.modal.hidden = true;
    }, 220);
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  };
  const chip = text => `<span class="mxd-pet__chip">${text}</span>`;
  // Every shop tab has its icon and colour; a card shows its art on a sunburst in that colour.
  const SHOP_LOOK = { promo: ['mic', '#ff4fa3'], sale: ['bag', '#5fd35a'], aces: ['mortar', '#ff8a1f'], hero: ['crown', GOLD] };
  const shopCard = (open, img, name, text, chips, side, extra = '', cc = '') => `<div class="mxd-pet__shop${open ? '' : ' is-locked'}${extra}"${cc ? ` style="--cc:${cc}"` : ''}><div class="mxd-pet__shop-img">${img}${open ? '' : `<span class="mxd-pet__shop-lock">${icon('lock')}</span>`}</div><div class="mxd-pet__shop-main"><b>${name}</b><em>${text}</em>${chips ? `<div class="mxd-pet__chips">${chips}</div>` : ''}</div>${side}</div>`;
  const NEW_RIBBON = '<span class="mxd-pet__new">NEU</span>';
  const buildRow = type => {
    const t = TYPES[type];
    const have = itemsOf(type).length;
    const limit = limitOf(type);
    const open = unlocked(type);
    const full = have >= limit;
    const cost = buildCost(type);
    let side;
    if (!open) side = `<div class="mxd-pet__locked">${icon('lock')}Base ${t.unlock}</div>`;
    else if (full) side = t.hero ? `<div class="mxd-pet__locked">${icon('check')}Dabei</div>` : `<div class="mxd-pet__locked">${icon('hq')}Base ${hqLevel() + 1}</div>`;
    else side = price(cost, `data-act="build" data-id="${type}"${busy() ? ' disabled' : ''}`, t.hero ? 'Anheuern' : 'Bauen');
    let meta = t.range ? chip(`${icon('range')}${t.range.toString().replace('.', ',')}`) + chip(`${icon('heart')}${fmt(hpOf(type, 1))}`) : '';
    if (t.kind === 'promo') meta = chip(`${icon('fans')}+${rateText(fansRateOf(type, 1))}`);
    if (t.kind === 'sale') meta = chip(`${icon('cash')}+${fmt(cashRateOf(type, 1))}/min`) + (t.online ? chip('24/7') : '');
    return shopCard(open, `${ART[type] ? `<img class="is-art" src="${ART[type]}" alt="">` : `<img src="${itemThumb(type, 1)}" alt="">`}${open && !t.hero ? `<span class="mxd-pet__tag">${have}/${limit}</span>` : ''}${isNew(type) ? NEW_RIBBON : ''}`, t.name, t.text, meta, side, t.hero ? ' is-holo' : '');
  };
  // What you could get in a shop tab: every unlocked building with room left, and every hero and ace you can pay for.
  const buyableIn = tab => {
    if (tab === 'aces') return Object.keys(TOOLS).filter(id => aceOpen(id) && !hasAce(id) && S.cash >= aceCost(id)).length;
    return BUILD_TABS.find(([id]) => id === tab)[2].filter(type => unlocked(type) && itemsOf(type).length < limitOf(type) && (!TYPES[type].hero || S.cash >= buildCost(type))).length;
  };
  // Whatever just unlocked and is still untouched leads its tab, the latest unlock first. Heroes are open from the start.
  const isNew = id => !TYPES[id].hero && unlocked(id) && !itemsOf(id).length;
  const newFirst = ids => {
    const fresh = ids.filter(isNew).sort((a, b) => TYPES[b].unlock - TYPES[a].unlock);
    return [...fresh, ...ids.filter(id => !fresh.includes(id))];
  };
  const buildHtml = tab => {
    const tabs = `<div class="mxd-pet__tabs" role="tablist">${BUILD_TABS.map(([id, name]) => {
      const n = buyableIn(id);
      const [glyph, color] = SHOP_LOOK[id];
      return `<button type="button" role="tab" class="mxd-pet__tab${id === tab ? ' is-on' : ''}" style="--tc:${color}" data-act="tab" data-id="${id}" aria-selected="${id === tab}">${icon(glyph)}<b>${name}</b>${n ? `<i class="mxd-pet__count">${n}</i>` : ''}</button>`;
    }).join('')}</div>`;
    const entry = BUILD_TABS.find(([id]) => id === tab);
    return `<div class="mxd-pet__store" style="--cc:${SHOP_LOOK[tab][1]}">${tabs}${tab === 'aces' ? acesHtml() : `<div class="mxd-pet__shops">${newFirst(entry[2]).map(buildRow).join('')}</div>`}</div>`;
  };
  const fact = (name, value) => `<div class="mxd-pet__fact"><em>${name}</em><b>${value}</b></div>`;
  // Raids: one line with the score, the five tips as compact cards, the rule in a footnote.
  const HEIST_LOOK = [MINT_D, '#ffb020', SIGNAL, '#e0115f'];
  const HEIST_FACES = ['kid', 'brute', 'boss', 'boss'];
  const heistCard = h => {
    tuneHeist(h);
    const s = HEIST_SIZES[h.size];
    const loot = heistLoot(h);
    const why = heistBlock();
    const go = why ? `<div class="mxd-pet__locked mxd-pet__hx-go">${icon('lock')}${why}</div>` : `<button type="button" class="mxd-pet__btn mxd-pet__btn--signal mxd-pet__hx-go" data-act="heist" data-id="${h.id}">Raiden</button>`;
    return `<div class="mxd-pet__hx-card" style="--hc:${HEIST_LOOK[h.size]}"><div class="mxd-pet__hx-head"><span class="mxd-pet__hx-img"><img src="${gangThumb(HEIST_FACES[h.size])}" alt=""></span><div class="mxd-pet__hx-main"><b>${heistGang(h)}</b><em>${heistPlace(h)}</em><div class="mxd-pet__hx-tags"><b class="is-lvl">${s.name}</b><b>${heistGuards(h)} Bootlegs${s.waves > 1 ? ` · ${s.waves} Wellen` : ''}</b></div></div></div>
      <div class="mxd-pet__hx-loot"><b>${cashIcon()}+${fmt(loot.cash)}</b><b>${icon('fans')}+${fmt(loot.fans)}</b></div>${go}</div>`;
  };
  // An empty slot: the scout is still asking around; the next tip lands when the bar is full.
  const heistWait = left => `<div class="mxd-pet__hx-card is-wait"><div class="mxd-pet__hx-head"><span class="mxd-pet__hx-img">${icon('mask')}</span><div class="mxd-pet__hx-main"><b>Nächster Tipp in ${formatClock(left)}</b><em>Dein Späher hört sich bei den anderen Gangs um.</em></div></div>
      <div class="mxd-pet__hx-bar"><s style="width:${(clamp(1 - left / HEIST_GAP, 0, 1) * 100).toFixed(1)}%"></s></div></div>`;
  const raidsHtml = () => {
    const now = Date.now();
    const waits = [...S.heistBack].sort((a, b) => a - b).map(at => heistWait(Math.max(0, at - now)));
    const shut = heistOpen() ? '' : `<div class="mxd-pet__hx-card is-wait"><div class="mxd-pet__hx-head"><span class="mxd-pet__hx-img">${icon('lock')}</span><div class="mxd-pet__hx-main"><b>Raids ab dem ersten Helden</b><em>Hol dir im Shop einen Helden, dann hört sich dein Späher bei den anderen Gangs um.</em></div></div></div>`;
    return `<div class="mxd-pet__hx">${shut}
      ${S.revenge ? `<div class="mxd-pet__hx-revenge">${icon('alert')}<b>Gegenangriff: ${S.revenge.gang}</b><i>${formatClock(Math.max(0, S.revenge.at - now))}</i></div>` : ''}
      ${S.heists.map(heistCard).join('')}${waits.join('')}</div>`;
  };
  // The ace shop: the deck of three on top, a tap on a card takes it out; below it every ace as a shop card in its
  // own colour, bought once and then put in the deck or taken out.
  const deckHtml = () => {
    const fighting = aceBusy();
    const full = S.deck.length >= ACE_SLOTS;
    const slots = Array.from({ length: ACE_SLOTS }, (_, i) => {
      const id = S.deck[i];
      if (!id) return `<li class="mxd-pet__deck-slot is-free"><span>${icon('star')}</span><b>Frei</b></li>`;
      return `<li class="mxd-pet__deck-slot"><button type="button" style="--tc:${TRICK_COLOR[id]}" data-act="ace-equip" data-id="${id}"${fighting ? ' disabled' : ''} aria-label="${TOOLS[id].name} ablegen"><span>${aceIcon(id)}</span><b>${TOOLS[id].name}</b>${fighting ? '' : `<i>${icon('x')}</i>`}</button></li>`;
    }).join('');
    const hint = fighting ? 'Kampf läuft: das Deck bleibt, wie es ist' : full ? 'Tipp auf eine Karte, um sie abzulegen' : `Jede Attacke ${ACE_USES}× pro Kampf`;
    return `<div class="mxd-pet__deck"><div class="mxd-pet__deck-head"><b>Dein Deck</b><em>${S.deck.length} / ${ACE_SLOTS}</em></div><ol>${slots}</ol><p>${hint}</p></div>`;
  };
  const aceRow = id => {
    const t = TOOLS[id];
    const have = hasAce(id);
    const open = aceOpen(id);
    const on = S.deck.includes(id);
    const chips = t.tap ? chip('Unbegrenzt') : chip(`${icon('target')}${ACE_USES}×`) + chip(`${icon('clock')}${t.cool} s`);
    const fighting = aceBusy() ? ' disabled' : '';
    let side;
    if (!open) side = `<div class="mxd-pet__locked">${icon('lock')}Base ${t.unlock}</div>`;
    else if (!have) side = price(aceCost(id), `data-act="ace-buy" data-id="${id}"`, 'Kaufen');
    else if (t.tap) side = `<div class="mxd-pet__locked">${icon('check')}Immer dabei</div>`;
    else if (on) side = `<button type="button" class="mxd-pet__btn" data-act="ace-equip" data-id="${id}"${fighting}>Ablegen</button>`;
    else if (S.deck.length >= ACE_SLOTS) side = `<button type="button" class="mxd-pet__btn is-full" data-act="ace-equip" data-id="${id}"${fighting}>Deck voll</button>`;
    else side = `<button type="button" class="mxd-pet__btn mxd-pet__btn--mint" data-act="ace-equip" data-id="${id}"${fighting}>Ausrüsten</button>`;
    const tag = on ? `<span class="mxd-pet__tag is-on">${icon('check')}Im Deck</span>` : have && !t.tap ? '<span class="mxd-pet__tag">Gekauft</span>' : '';
    return shopCard(open, aceIcon(id) + tag, t.name, t.text, chips, side, `${on ? ' is-on' : ''} is-ace`, TRICK_COLOR[id]);
  };
  const acesHtml = () => `${deckHtml()}<div class="mxd-pet__shops">${Object.keys(TOOLS).map(aceRow).join('')}</div>`;
  // Heute: the weekend event, the chest of the day and the round's three tasks.
  const TASK_ICONS = { heists: 'mask', problems: 'shield', kills: 'target', punches: 'fist', combo: 'knuckles', aces: 'mortar', talks: 'mic', upgrades: 'up' };
  const dayCount = () => (chestReady() ? 1 : 0) + taskRound().tasks.filter(taskReady).length;
  const dayBar = fraction => `<span class="mxd-pet__day-bar"><s style="width:${(clamp(fraction, 0, 1) * 100).toFixed(1)}%"></s></span>`;
  // The weekend event as a banner: its Bootleg in a burst of light with what it multiplies stuck on, a live or
  // coming pill with the time, and its name and rule. Gold while it runs, white before the weekend.
  const EVENT_FACES = { beute: ['boss', false], gold: ['kid', true] };
  const eventHtml = () => {
    const day = dayOf();
    const live = eventOn(day);
    const next = day + 6 - weekday(day);
    const shown = live || eventOn(next);
    const [face, gold] = EVENT_FACES[shown.id];
    const tag = live ? `<span class="mxd-pet__event-tag is-live">Live · noch ${formatSpan(eventLeft())}</span>` : `<span class="mxd-pet__event-tag">${icon('clock')}Ab Samstag</span>`;
    return `<div class="mxd-pet__event${live ? ' is-live' : ''}"><div class="mxd-pet__event-art"><img class="mxd-pet__event-face" src="${gangPortrait(face, gold)}" alt=""><span class="mxd-pet__event-mark">${shown.mark}</span></div><div class="mxd-pet__event-main">${tag}<b>${shown.name}</b><i>${shown.text}</i></div></div>`;
  };
  const chestHtml = () => {
    const at = chestIndex();
    const ready = chestReady();
    const run = S.chest.day >= dayOf() - 1 ? S.chest.streak : 0;
    const cells = CHEST.map((_, i) => {
      const state = i < at || (i === at && !ready) ? ' is-done' : i === at ? ' is-now' : '';
      const big = i === CHEST.length - 1;
      const pic = icon(state === ' is-done' ? 'check' : 'gift');
      return `<li class="mxd-pet__chest-day${state}${big ? ' is-big' : ''}"><em>Tag ${i + 1}</em>${pic}<b>${fmt(chestPay(i))}</b></li>`;
    }).join('');
    const go = ready
      ? `<button type="button" class="mxd-pet__btn mxd-pet__btn--signal mxd-pet__day-go" data-act="chest">Kiste öffnen</button>`
      : `<div class="mxd-pet__locked mxd-pet__day-go">${icon('clock')}Nächste Kiste in ${formatSpan(dayStart(dayOf() + 1) - Date.now())}</div>`;
    return `<div class="mxd-pet__day-card"><div class="mxd-pet__day-head"><b>Tageskiste</b><em>${run ? `${run} ${run === 1 ? 'Tag' : 'Tage'} in Folge` : 'Jeden Tag eine'}</em></div><ol class="mxd-pet__chest">${cells}</ol>${go}</div>`;
  };
  const taskRow = (task, i) => {
    const side = task.done
      ? `<span class="mxd-pet__task-done">${icon('check')}</span>`
      : taskReady(task)
        ? `<button type="button" class="mxd-pet__btn mxd-pet__btn--mint mxd-pet__task-go" data-act="task" data-id="${i}">Abholen</button>`
        : `<span class="mxd-pet__task-pay">${cashIcon()}${fmt(taskPay())}</span>`;
    return `<li class="mxd-pet__task${task.done ? ' is-done' : ''}"><span class="mxd-pet__task-icon">${icon(TASK_ICONS[task.kind])}</span><div class="mxd-pet__task-main"><b>${taskText(task)}</b>${dayBar(taskHave(task) / task.need)}<em>${fmt(taskHave(task))} / ${fmt(task.need)}</em></div>${side}</li>`;
  };
  const tasksHtml = () => `<div class="mxd-pet__day-card"><div class="mxd-pet__day-head"><b>Aufgaben</b><em>Neue in ${formatSpan(roundLeft())}</em></div><ol class="mxd-pet__tasks">${taskRound().tasks.map(taskRow).join('')}</ol></div>`;
  const dailyHtml = () => `<div class="mxd-pet__day">${eventHtml()}${chestHtml()}${tasksHtml()}</div>`;
  const PANEL_TITLES = { build: 'Shop', heist: 'Raids', daily: 'Heute' };
  const refreshPanel = (force = false) => {
    if (!panel) return;
    const { kind } = panel;
    let sig = kind;
    if (kind === 'build') {
      const tab = panel.data || 'promo';
      sig += `|${tab}|${hqLevel()}|${Boolean(raid)}|${raid && raid.phase}|${S.items.length}|`;
      const entry = BUILD_TABS.find(([id]) => id === tab);
      sig += entry[2].map(buildCost).map(cost => (S.cash >= cost ? 1 : 0)).join('');
      sig += `|${BUILD_TABS.map(([id]) => buyableIn(id)).join()}`;
      if (tab === 'sale') sig += `|${Math.round(payout() * 100)}`;
      if (tab === 'aces') sig += `|${S.deck.join()}|${aceBusy()}|${Object.keys(TOOLS).map(id => (hasAce(id) ? 'h' : aceOpen(id) ? (S.cash >= aceCost(id) ? 1 : 0) : 'x')).join('')}|${nice(salesPace())}`;
    }
    if (kind === 'heist') sig += `|${S.heists.map(h => `${h.id}:${heistLoot(h).cash}`).join()}|${heistBlock()}|${S.heistBack.map(at => Math.floor((at - Date.now()) / 1000)).join()}|${S.revenge ? Math.floor((S.revenge.at - Date.now()) / 1000) : ''}`;
    if (kind === 'daily') sig += `|${dayCount()}|${taskRound().tasks.map(task => `${taskHave(task)}${task.done}`).join()}|${S.chest.day}|${nice(salesPace())}|${Math.floor(Date.now() / MINUTE)}|${eventOn()?.id}`;
    if (!force && sig === panelSig) return;
    panelSig = sig;
    els.panel.dataset.kind = kind;
    setText(els.panelTitle, PANEL_TITLES[kind]);
    const html = {
      build: () => buildHtml(panel.data || 'promo'),
      heist: raidsHtml,
      daily: dailyHtml
    }[kind]();
    const scroll = els.panelBody.scrollTop;
    els.panelBody.innerHTML = html;
    els.panelBody.scrollTop = scroll;
  };

  // ---------------------------------------------------------------------------------------------
  // Actions

  const spend = amount => {
    if (S.cash < amount) {
      showToast(`Dir fehlen ${fmt(amount - S.cash)} Cash`, 'alert');
      play('nope');
      return false;
    }
    S.cash -= amount;
    shownCash = Math.min(shownCash, S.cash + amount);
    return true;
  };
  // Protection money: the coming Problem, or the counterattack that comes first, is called off. The count of
  // Problems stays where it is, so the next one is the same fight again.
  const payProtection = () => {
    const cost = protectionPrice();
    if (!problemSoon() || cost < 1 || !spend(cost)) return;
    if (S.revenge && S.revenge.at <= S.raidAt) S.revenge = null;
    else S.raidAt = Date.now() + raidGap();
    selected = null;
    banner('raid', `<b>Schutzgeld bezahlt</b><em>${cashText(fmt(cost))} · Bis zum nächsten Problem ist Ruhe</em>`, 'good', 4200);
    play('clink');
    persist();
  };
  const armBuild = type => {
    if (itemsOf(type).length >= limitOf(type) || busy()) return;
    const spot = freeSpot(type, { x: cam.x, y: cam.y });
    if (!spot) {
      showToast('Kein Platz mehr. Übernimm ein neues Revier.', 'alert');
      return;
    }
    ghost = { type, level: 1, x: spot.x, y: spot.y, vx: spot.x, vy: spot.y, lift: 0, ok: true, moving: null };
    selected = null;
    closePanel();
  };
  const armMove = item => {
    ghost = { type: item.type, level: item.level, x: item.x, y: item.y, vx: item.x, vy: item.y, lift: 0, ok: true, moving: item.id };
    selected = null;
    play('lift');
    if (window.navigator.vibrate) window.navigator.vibrate(12);
  };
  // Snaps the ghost to the tile under the point; `grab` is where on the ghost it is held.
  const ghostTo = (point, grab) => {
    const t = TYPES[ghost.type];
    const at = unproj(point.x, point.y);
    const x = Math.round(at.x - (grab ? grab.x : t.w / 2));
    const y = Math.round(at.y - (grab ? grab.y : t.d / 2));
    if (x === ghost.x && y === ghost.y) return;
    ghost.x = x;
    ghost.y = y;
    ghost.ok = canPlace(ghost.type, ghost.x, ghost.y, ghost.moving ? itemById(ghost.moving) : null);
    play('tick');
  };
  // Let go over a taken spot: the ghost slides to the closest free one nearby, if there is one.
  const snapGhost = () => {
    if (!ghost || ghost.ok) return Boolean(ghost);
    const skip = ghost.moving ? itemById(ghost.moving) : null;
    for (let r = 1; r <= 2; r += 1) {
      let best = null;
      for (let dx = -r; dx <= r; dx += 1) {
        for (let dy = -r; dy <= r; dy += 1) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r || !canPlace(ghost.type, ghost.x + dx, ghost.y + dy, skip)) continue;
          if (!best || Math.hypot(dx, dy) < Math.hypot(best.dx, best.dy)) best = { dx, dy };
        }
      }
      if (best) {
        ghost.x += best.dx;
        ghost.y += best.dy;
        ghost.ok = true;
        return true;
      }
    }
    return false;
  };
  // A move that has nowhere to go puts the building back where it was.
  const dropMove = () => {
    if (!ghost || !ghost.moving) return;
    const home = itemById(ghost.moving);
    if (home && home.x === ghost.x && home.y === ghost.y) {
      ghost = null;
      selected = { kind: 'item', id: home.id };
      return;
    }
    if (snapGhost()) {
      placeGhost();
      return;
    }
    const id = ghost.moving;
    ghost = null;
    selected = { kind: 'item', id };
    showToast('Kein Platz dort, es bleibt, wo es war', 'alert');
    play('nope');
  };
  const grabOf = point => {
    const at = unproj(point.x, point.y);
    return { x: at.x - ghost.x, y: at.y - ghost.y };
  };
  const placeGhost = () => {
    if (!ghost || !ghost.ok) return;
    if (ghost.moving) {
      const item = itemById(ghost.moving);
      if (item) {
        item.x = ghost.x;
        item.y = ghost.y;
        item.pop = 0.3;
        selected = { kind: 'item', id: item.id };
        const c = center(item);
        burst(c.x, c.y, 0.1, 10, [PAPER, '#c9c8c3'], { speed: 1.6, up: 1, size: 0.05 });
      }
      ghost = null;
      refreshOcc();
      play('build');
      persist();
      return;
    }
    const cost = buildCost(ghost.type);
    if (!spend(cost)) return;
    const item = { id: S.nextId, type: ghost.type, x: ghost.x, y: ghost.y, level: 1, stock: 0 };
    S.nextId += 1;
    S.items.push(item);
    item.pop = 0.4;
    refreshOcc();
    const c = center(item);
    burst(c.x, c.y, 0.3, 14, [PAPER, MINT, '#c9c8c3'], { speed: 2, up: 2, size: 0.06 });
    play('build');
    selected = { kind: 'item', id: item.id };
    ghost = null;
    persist();
  };
  const repair = item => {
    if (!item.ruin || busy() || !spend(repairCost(item))) return;
    item.ruin = false;
    item.rebuild = REBUILD;
    item.hp = maxHp(item);
    const c = center(item);
    burst(c.x, c.y, 0.4, 14, [PAPER, '#c9c8c3', MINT], { speed: 1.6, up: 2.5, size: 0.06, kind: 'debris' });
    showToast(`${info(item).name} wird repariert`, 'good');
    play('repair');
    persist();
  };
  // Held down, the button buys every step the cash covers in one go.
  const upgrade = (item, all) => {
    if (item.level >= maxLevel(item.type) || item.ruin || item.rebuild) return;
    if (!spend(upgradeCost(item))) return;
    const from = item.level;
    const before = tierOf(from);
    item.level += 1;
    while (all && item.level < maxLevel(item.type) && S.cash >= upgradeCost(item)) {
      S.cash -= upgradeCost(item);
      item.level += 1;
    }
    S.stats.upgrades += item.level - from;
    item.hp = maxHp(item);
    item.pop = 0.4;
    const c = center(item);
    burst(c.x, c.y, 1, 16, [MINT, YELLOW, PAPER], { speed: 1.8, up: 3.5, size: 0.05, kind: 'spark' });
    play('level');
    if (item.level - from > 1) showToast(`${info(item).name}: ${item.level - from} Stufen auf einmal, jetzt Stufe ${item.level}`, 'good');
    if (tierOf(item.level) > before) {
      showToast(`Neues Aussehen: ${tierName(item.level)}`, 'good');
      if (item.type === 'hq' && VIBE_TEXT[tierOf(item.level)]) banner('vibe', `<b>Das Viertel lebt auf</b><em>${VIBE_TEXT[tierOf(item.level)]}</em>`, 'good', 4200);
    }
    if (item.type === 'hq') {
      K.hp = Math.min(hustlerMaxHp(), K.hp + hustlerMaxHp() * 0.12 * (item.level - from));
      const opened = unlock => unlock > from && unlock <= item.level;
      const fresh = Object.keys(TYPES).filter(type => opened(TYPES[type].unlock));
      const toolsNew = Object.keys(TOOLS).filter(id => opened(TOOLS[id].unlock));
      const names = [...fresh.map(type => TYPES[type].name), ...toolsNew.map(id => TOOLS[id].name)];
      showToast(names.length ? `Base ${item.level}: ${names.join(', ')} frei` : `Base ${item.level}: mehr Plätze, höhere Stufen`, 'good');
    }
    persist();
  };
  // Cash and fans land in the counters with a little show.
  const gain = (item, cash, fans) => {
    S.cash += cash;
    S.earned += cash;
    S.fans += fans;
    const box = screenBox(item);
    const from = { x: (box.l + box.r) / 2, y: box.t };
    if (cash > 0) fly(from, cash);
    if (fans > 0) fly(from, fans, true);
    const c = center(item);
    floatText(c.x, c.y, heightOf(item.type, item.level) + 0.3, cash > 0 ? `+${fmt(cash)}` : `+${fmt(fans)} Fans`, cash > 0 ? MINT_D : '#2f8cff', 1, cash > 0 ? 'cash' : '');
    play(cash > 0 ? 'coin' : 'fans');
  };
  const collect = item => {
    const amount = Math.floor(item.stock || 0);
    if (amount < 1) return;
    item.stock -= amount;
    if (kindOf(item) === 'promo') gain(item, 0, amount);
    else gain(item, amount, 0);
  };
  // The magnet: everything that piled up while you were away, collected in one go.
  const pullAll = () => {
    if (!S.magnet || raid || heist) return;
    S.magnet = false;
    S.items.forEach(collect);
    play('level');
    persist();
  };
  // A tap on promo or a sale spot hustles by hand: a quick sale or a few fans, then a short breath.
  // The hustler does one deal at a time, wherever you tap.
  let hustledAt = -9;
  const hustle = item => {
    if (clock - hustledAt < 0.9 || !standing(item)) return;
    hustledAt = clock;
    item.pop = 0.25;
    if (kindOf(item) === 'promo') gain(item, 0, hustleFans(item));
    else {
      S.stats.sold += 1;
      gain(item, Math.round(hustleCash(item)), 0);
    }
  };
  // Someone on the street wants merch: talk to them and they buy from you on the spot.
  const talkTo = walker => {
    walker.bought = true;
    walker.pop = 0.7;
    const cash = walker.price;
    S.cash += cash;
    S.earned += cash;
    S.fans += 1;
    S.stats.talks += 1;
    S.stats.sold += 1;
    const tag = walkerTag(walker);
    const at = { x: tag.x + tag.w / 2, y: tag.y + tag.h / 2 };
    fly(at, cash);
    fly(at, 1, true);
    burst(walker.x, walker.y, 1.5, 28, [MINT, YELLOW, PAPER, MINT_D, GOLD], { speed: 2.8, up: 3.6, size: 0.06, kind: 'spark' });
    floatText(walker.x, walker.y, 1.9, `+${fmt(cash)}`, MINT_D, 1.3, 'cash');
    play('coin');
  };
  const claimMove = () => {
    const move = currentMove();
    const m = moveInfo(move);
    if (m.have < m.need) {
      showToast(`Nächster Move: ${m.text}`);
      return;
    }
    S.cash += move.reward;
    S.earned += move.reward;
    S.moveN += 1;
    S.move = null;
    fly(centerOf(els.move), move.reward);
    showToast(`Move geschafft: +${fmt(move.reward)} Cash`, 'good');
    play('quest');
    persist();
  };
  // The day: tasks, the chest, and records.
  const payOut = (cash, from) => {
    S.cash += cash;
    S.earned += cash;
    if (cash) fly(from, cash);
  };
  const claimTask = (index, from) => {
    const task = taskRound().tasks[index];
    if (!task || !taskReady(task)) return;
    task.done = true;
    payOut(taskPay(), from);
    if (taskRound().tasks.every(done => done.done)) {
      cutIn('Alle drei geschafft', GOLD, `Neue Aufgaben in ${formatSpan(roundLeft())}`, 1.8);
      play('fanfare');
    } else {
      showToast(`Aufgabe geschafft: ${taskText(task)}`, 'good');
      play('quest');
    }
    persist();
  };
  const openChest = from => {
    if (!chestReady()) return;
    const day = dayOf();
    const index = chestIndex();
    S.chest = { day, streak: S.chest.day === day - 1 ? S.chest.streak + 1 : 1 };
    const cash = chestPay(index);
    const big = index === CHEST.length - 1;
    payOut(cash, from);
    showToast(big ? `Große Kiste: +${fmt(cash)} Cash` : `Kiste Tag ${index + 1}: +${fmt(cash)} Cash`, 'good');
    play(big ? 'fanfare' : 'quest');
    persist();
  };
  const RECORD_TEXT = {
    combo: n => `${n}er Combo`,
    streak: n => `${n} Raids in Folge gewonnen`,
    fastest: ms => `Problem in ${formatClock(ms)} gelöst`,
    loot: n => `${fmt(n)} Cash aus einem Raid`
  };
  const noteRecord = (key, value) => {
    if (!beats(key, value)) return;
    banner(`record:${key}`, `<b>Neuer Rekord!</b><em>${RECORD_TEXT[key](value)}</em>`, 'good', 3800);
    play('fanfare');
  };
  const onComboEnd = n => {
    if (n >= 5) noteRecord('combo', n);
  };
  const startShow = item => {
    const now = Date.now();
    if (showLive(item, now) || (item.showAt || 0) > now || !standing(item) || busy()) return;
    item.showUntil = now + SHOW_LEN;
    item.showAt = now + SHOW_LEN + SHOW_GAP;
    S.stats.shows += 1;
    const c = center(item);
    burst(c.x, c.y, 2, 22, ['#ff4fa3', YELLOW, PAPER, '#2f6fff'], { speed: 2.4, up: 4, size: 0.06, kind: 'spark' });
    showToast(`Show läuft: ${formatClock(SHOW_LEN)} lang achtmal so viele Fans`, 'good');
    play('crowd');
    persist();
  };
  const buyLand = key => {
    const cost = landPrice();
    if (!key || heist || landKeys.has(key) || !spend(cost)) return;
    S.land.push(key);
    refreshLand();
    const [cx, cy] = key.split(',').map(Number);
    burst(cx * CHUNK + CHUNK / 2, cy * CHUNK + CHUNK / 2, 0.2, 24, [MINT, PAPER, '#c9c8c3'], { speed: 3, up: 2, size: 0.07 });
    play('build');
    selected = null;
    persist();
  };
  // The ace shop: a card is bought once with cash, and three of them make the deck a fight starts with.
  // The deck stays as it is while a fight is on.
  const aceBusy = () => Boolean(heist || travel || (raid && raid.phase !== 'end'));
  const buyAce = id => {
    if (hasAce(id) || !aceOpen(id) || !spend(aceCost(id))) return;
    S.tools[id] = 1;
    if (S.deck.length < ACE_SLOTS && !aceBusy()) S.deck.push(id);
    banner('ace', `<b>${TOOLS[id].name} gekauft</b><em>${S.deck.includes(id) ? 'Liegt in deinem Deck' : 'Rüste sie im Deck aus'}</em>`, 'good', 3200);
    play('fanfare');
    persist();
  };
  const equipAce = id => {
    if (aceBusy() || TOOLS[id].tap || !hasAce(id)) return;
    if (S.deck.includes(id)) S.deck = S.deck.filter(one => one !== id);
    else if (S.deck.length >= ACE_SLOTS) {
      showToast(`Nur ${ACE_SLOTS} Attacken passen ins Deck. Leg erst eine ab.`, 'alert');
      play('nope');
      return;
    }
    else S.deck.push(id);
    play('tick');
    persist();
  };
  const ACE_MOVES = { shirt: useShirts, bombe: useDrop, drohne: useDrone, fans: useFans, rakete: useRockets, birne: useWreck, tornado: useTwister, laser: useLaser, beben: useQuake };
  const useTool = id => {
    if (!raid || raid.phase !== 'fight' || toolCd[id] > 0 || !S.deck.includes(id)) return;
    if (toolUsed[id] >= ACE_USES) {
      showToast(`${TOOLS[id].name} ist für diese Runde aufgebraucht`);
      return;
    }
    // Between waves there may be nobody to hit; then the ace stays ready.
    if (ACE_MOVES[id]() === false) {
      showToast('Gerade ist keiner da');
      return;
    }
    toolCd[id] = TOOLS[id].cool;
    toolUsed[id] += 1;
    S.stats.aces += 1;
    cutIn(TOOLS[id].name, TRICK_COLOR[id]);
  };
  // Raid hooks from the simulation.
  const onRaidWarn = () => {
    resetTools();
    closePanel();
    ghost = null;
    selected = null;
    const size = `${raid.total} Bootlegs${raid.waves > 1 ? ` in ${raid.waves} Wellen` : ''}`;
    if (raid.revenge) {
      banner('raid', `<b>Gegenangriff: ${raid.revenge}</b><em>${size} · doppelte Belohnung</em>`, 'alert', 4600);
      cutIn('Gegenangriff', '#e0115f', `Rache von ${raid.revenge} · ${size}`, 2.4);
    } else if (raid.boss) {
      banner('raid', `<b>Boss-Problem: Der Pate kommt selbst</b><em>${size} · dreifache Belohnung</em>`, 'alert', 4600);
      cutIn(`Boss-Problem ${raid.count}`, BOSS_RED, `Der Pate kommt selbst · ${size}`, 2.4);
    } else {
      banner('raid', `<b>Die Bootlegs kommen</b><em>${size}</em>`, 'alert', 4200);
      cutIn(`Problem ${raid.count}`, SIGNAL, size, 2);
    }
    focusOn(K.x, K.y);
  };
  const onFoeMet = key => {
    const foe = key === 'elite' ? ELITE : GANG[key];
    banner(`foe:${key}`, `<b>Neu: ${foe.name}</b><em>${foe.text}</em>`, 'alert', 6000);
  };
  const onCrewDown = item => {
    showToast(`${info(item).name} ist gefallen. Für immer.`, 'alert');
  };
  const onHustlerExposed = item => {
    showToast(`${info(item).name} zerstört. Der Hustler muss raus!`, 'alert');
  };
  const onRaidEnd = done => {
    resetTools();
    noteRecord('fastest', Math.round(done.clock * 1000));
    persist();
    const lost = done.lost.length ? `${done.lost.length} Gebäude kaputt, tippen zum Reparieren` : 'Nichts kaputt';
    banner('raid', `<b>Problem ${done.n} gelöst</b><em>${cashText(`+${fmt(done.earned + done.bonus)}`)}${done.stolen ? ` · ${cashText(fmt(done.stolen))} geklaut` : ''} · ${lost}</em>`, 'good', 6000);
    setTimeout(() => {
      if (raid === done) raid = null;
    }, 4000);
  };
  const goHeist = id => {
    const h = S.heists.find(one => one.id === id);
    if (!h || heistBlock()) return;
    closePanel();
    travelTo(() => startHeist(h));
  };
  // Going somewhere: the clouds close, the place changes behind them once they are shut, they hold a breath
  // and part over the new place. The HUD steps aside meanwhile, and the map takes no input.
  const TRAVEL = { close: 0.8, hold: 0.55, open: 1.0 };
  const travelTo = arrive => {
    if (travel) return;
    const quick = reducedMotion.matches ? 0.35 : 1;
    travel = { phase: 'close', t: 0, arrive, quick, seed: Math.floor(Math.random() * 1e6) };
    ghost = null;
    selected = null;
    press = null;
    root.classList.add('is-travel');
    play('clouds');
  };
  const updateTravel = dt => {
    if (!travel) return;
    travel.t += dt / travel.quick;
    if (travel.phase === 'close' && travel.t >= TRAVEL.close) {
      travel.arrive();
      Object.assign(travel, { phase: 'hold', t: 0 });
    } else if (travel.phase === 'hold' && travel.t >= TRAVEL.hold) {
      Object.assign(travel, { phase: 'open', t: 0 });
      play('clouds');
    } else if (travel.phase === 'open') {
      // The HUD comes back once the clouds have parted far enough to make room for it.
      if (travel.t >= TRAVEL.open * 0.45) root.classList.remove('is-travel');
      if (travel.t >= TRAVEL.open) travel = null;
    }
  };
  const onRevengeSworn = revenge => banner('revenge', `<b>Rache geschworen: ${revenge.gang}</b><em>Gegenangriff in etwa ${formatSpan(revenge.at - Date.now())}</em>`, 'alert', 4200);
  const onHeistTip = tips => banner('heist', tips.length === 1 ? `<b>Neuer Tipp: ${heistGang(tips[0])}</b><em>${heistPlace(tips[0])} · ${HEIST_SIZES[tips[0].size].name}</em>` : `<b>${tips.length} neue Tipps</b><em>${tips.map(tip => HEIST_SIZES[tip.size].name).join(' · ')}</em>`, '', 3600);
  const onHeistStart = h => {
    resetTools();
    ghost = null;
    selected = null;
    cam.z = compact ? 1 : 1.05;
    focusOn(heist.center.x, heist.center.y - 1.5);
    const squads = heist.squads.filter(squad => !squad.wave).length;
    const more = heist.squads.length - squads;
    banner('raid', `<b>Schlacht: ${heistGang(h)}</b><em>${heistPlace(h)} · ${squads} Trupps in Stellung${more ? ` · ${more} in Reserve` : ''}</em>`, 'alert', 4000);
    cutIn(`Raid: ${heistGang(h)}`, HEIST_LOOK[h.size], `${HEIST_SIZES[h.size].name} · ${heistPlace(h)}`, 2, TRAVEL.open * 0.6);
  };
  const onReinforce = count => banner('raid', `<b>Verstärkung!</b><em>${count} Bootlegs kommen aus dem Lager</em>`, 'alert', 2600);
  const onBossDown = () => banner('raid', '<b>Der Pate fällt!</b><em>Die ganze Gang wankt</em>', 'good', 2600);
  const onHeistEnd = done => {
    resetTools();
    const won = done.result === 'won';
    if (won) heist.props.forEach((prop, i) => {
      const at = proj(prop.x + prop.w / 2, prop.y + prop.d / 2, 1);
      fly(at, Math.round(done.bonus / heist.props.length));
      if (i === 0) fly(at, done.fans, true);
    });
    S.streak = won ? S.streak + 1 : 0;
    if (won) {
      noteRecord('streak', S.streak);
      noteRecord('loot', done.bonus);
    }
    persist();
    const fell = done.fallen || 0;
    const cost = fell ? ` · ${fell} gefallen` : '';
    const why = done.fled ? 'Deine Helden brechen ab, bevor noch mehr fallen' : `Die ${heistGang(done.heist)} waren zu stark`;
    banner('raid', won ? `<b>${battleGrade(done)}!</b><em>${cashText(`+${fmt(done.bonus + done.earned)}`)} · +${fmt(done.fans)} Fans${cost}</em>` : `<b>${battleGrade(done)}</b><em>${why}${done.earned ? ` · ${cashText(`+${fmt(done.earned)}`)} aus dem Kampf` : ''}${cost}</em>`, won ? 'good' : '', 6000);
  };
  const onHeistHome = () => {
    clampCam();
    showToast('Deine Helden sind zurück im Revier', 'good');
  };
  // Tapped free in time: back on his feet with a third of his life, and a shove sends the Bootlegs round him flying.
  const rescueHustler = () => {
    if (!rescue) return;
    rescue = null;
    K.hp = hustlerMaxHp() / 3;
    enemies.forEach(enemy => {
      if (dist(enemy.x, enemy.y, K.x, K.y) <= 3) knock(enemy, K.x, K.y, 2, 1);
    });
    moments.push({ kind: 'ring', x: K.x, y: K.y, r: 3, t: 0, dur: 0.45, color: YELLOW });
    flare = Math.max(flare, 0.3);
    flareColor = PAPER;
    banner('rescue', '<b>Gerettet!</b><em>Ein zweites Mal gibt es in diesem Kampf nicht</em>', 'good', 2600);
    play('fanfare');
  };
  const onHustlerDown = () => {
    const record = { reign: Date.now() - S.founded, raids: S.stats.raids, kills: S.stats.kills, earned: S.earned, hq: hqLevel(), at: Date.now() };
    S.over = record;
    persist();
    closePanel();
    ghost = null;
    selected = null;
    setTimeout(showOver, 2400);
  };

  // ---------------------------------------------------------------------------------------------
  // Getting started, and starting over once the hustler has fallen.

  // The start screen: the game and why it all works for real, the loop in four steps, the one rule that hurts, your hustler.
  const introStep = (n, glyph, color, title, text) => `<div class="mxd-pet__step" style="--sc:${color}">${icon(glyph)}<i>${n}</i><b>${title}</b><span>${text}</span></div>`;
  const showIntro = () => {
    show(els.over, false);
    els.intro.innerHTML = `<div class="mxd-pet__sheet">
      <div class="mxd-pet__sheet-head">
        <div class="mxd-pet__sheet-title">Merch Tycoon</div>
        <p class="mxd-pet__tagline">Vom Klapptisch zum Flagship Store. Was du hier spielst, geht mit MIXEDOWN On-Demand auch in echt: dein Merch, deine Fans, produziert erst nach der Bestellung.</p>
      </div>
      <div class="mxd-pet__sheet-body">
      <div class="mxd-pet__how">
        ${introStep(1, 'mic', SHOP_LOOK.promo[1], 'Promo machen', 'Plakate, Sticker und Shows bringen dir Fans.')}
        ${introStep(2, 'bag', SHOP_LOOK.sale[1], 'Verkaufen', 'Fans bestellen an deinen Ständen und Shops. Du sammelst das Cash ein.')}
        ${introStep(3, 'hq', YELLOW, 'Wachsen', 'Mehr Spots, höhere Stufen, eine größere Base. Mit jeder Base-Stufe bringt jeder Verkauf mehr.')}
        ${introStep(4, 'shield', SHOP_LOOK.hero[1], 'Verteidigen', 'Bootlegs greifen dein Revier an. Deine Helden halten dagegen, du setzt deine Attacken ein.')}
      </div>
      <div class="mxd-pet__hustler-pick"><canvas data-intro-portrait></canvas><label><em>Dein Hustler</em><span class="mxd-pet__field"><input type="text" data-intro-name value="${escapeHtml(playerName())}" maxlength="${NAME_MAX}" autocomplete="off" autocapitalize="words" spellcheck="false" enterkeyhint="done" aria-label="Name deines Hustlers">${icon('pen')}</span></label><button type="button" class="mxd-pet__btn mxd-pet__btn--square" data-intro="reroll" aria-label="Neuer Look und Name">${icon('dice')}</button></div>
      <div class="mxd-pet__go"><button type="button" class="mxd-pet__btn mxd-pet__btn--mint mxd-pet__btn--wide" data-intro="start">Los geht's</button></div></div></div>`;
    show(els.intro, true);
    paintPortrait(els.intro.querySelector('[data-intro-portrait]'), 84);
  };
  // The dice spins through a run of looks and names, slowing down, and stops on a new one.
  let spinning = false;
  const spinHustler = () => {
    if (spinning) return;
    spinning = true;
    const portrait = els.intro.querySelector('[data-intro-portrait]');
    const field = els.intro.querySelector('[data-intro-name]');
    const step = n => {
      // Started in the middle of a spin: the hustler on screen is the one.
      if (founded()) {
        spinning = false;
        return;
      }
      S.seed = newSeed();
      S.name = '';
      hustlerLook = makeHustler(S.seed);
      paintPortrait(portrait, 84);
      field.value = hustlerName(S.seed);
      play('tick');
      if (n < 7) setTimeout(() => step(n + 1), 45 + n * n * 6);
      else {
        spinning = false;
        play('tap');
      }
    };
    step(0);
  };
  const start = () => {
    const now = Date.now();
    S.founded = now;
    S.raidAt = now + FIRST_RAID;
    S.last = now;
    refreshLand();
    refreshOcc();
    placeHustler();
    focusOn(K.x, K.y - 1);
    show(els.intro, false);
    showToast('Dein erster Move steht oben links', 'good');
    persist();
  };
  const showOver = () => {
    const p = S.over;
    if (!p) return;
    els.over.innerHTML = `<div class="mxd-pet__sheet">
      <div class="mxd-pet__sheet-head"><div class="mxd-pet__sheet-title">Revier verloren</div></div>
      <div class="mxd-pet__sheet-body">
      <div class="mxd-pet__note">Der Hustler hat ${formatSpan(p.reign)} durchgehalten. Jetzt gehört das Revier den Bootlegs.</div>
      <div class="mxd-pet__facts">${fact('Probleme gelöst', fmt(p.raids))}${fact('Bootlegs erledigt', fmt(p.kills))}${fact('Cash gemacht', `${cashIcon()}${fmt(p.earned)}`)}${fact('Base', `Stufe ${p.hq}`)}</div>
      <button type="button" class="mxd-pet__btn mxd-pet__btn--mint mxd-pet__btn--wide" data-over="again">Nochmal von vorn</button></div></div>`;
    show(els.over, true);
  };
  const startOver = () => {
    // The records, the streak of chests and the Bootlegs met belong to the player, not to the block.
    const kept = { best: S.best, chest: S.chest, met: S.met };
    S = { ...freshSave(Date.now()), ...kept };
    hustlerLook = makeHustler(S.seed);
    props.clear();
    thumbs.clear();
    crestLook = '';
    raid = null;
    enemies.length = 0;
    walkers.length = 0;
    pigeons.length = 0;
    moments.length = 0;
    zones.length = 0;
    shots.length = 0;
    parts.length = 0;
    decals.length = 0;
    refreshLand();
    refreshOcc();
    placeHustler();
    persist();
    showIntro();
  };

  // ---------------------------------------------------------------------------------------------
  // Input on the map: pan with one finger, pinch or wheel to zoom, tap to pick.

  const pointers = new Map();
  let press = null;
  let pinch = null;
  // The map dragged along under a finger or the mouse.
  const mapDragged = () => Boolean(press && press.moved && !press.ghost && !pinch);
  // A tap picks what is painted under the finger, whatever the perspective puts in front. Everything that could
  // be there is drawn again into a few pixels around the spot, and the front-most one that covers the spot itself
  // wins. A person standing in front of it gets a fingertip of slack, and so does anything when the spot is empty.
  const PICK_ALPHA = 100;
  const pickCanvas = document.createElement('canvas');
  const pickCtx = pickCanvas.getContext('2d', { willReadFrequently: true });
  // How close to the spot `draw` paints, in pixels up to `r`; Infinity if not within `r` at all.
  const coverage = (point, r, draw) => {
    const size = r * 2 + 1;
    // Setting the size also wipes the canvas and its state.
    pickCanvas.width = size;
    pickCanvas.height = size;
    pickCtx.translate(r - point.x, r - point.y);
    const main = ctx;
    // Some buildings throw off sparks as they are drawn; the copy for the pick must not.
    const sparks = parts.slice();
    ctx = pickCtx;
    try {
      draw();
    } finally {
      ctx = main;
      parts.splice(0, parts.length, ...sparks);
    }
    const data = pickCtx.getImageData(0, 0, size, size).data;
    let near = Infinity;
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        if (data[(y * size + x) * 4 + 3] >= PICK_ALPHA) near = Math.min(near, Math.hypot(x - r, y - r));
      }
    }
    return near <= r ? near : Infinity;
  };
  // Everything a tap could mean around the spot, front to back.
  const candidates = (point, r) => {
    const u = cw();
    const list = [];
    S.items.forEach(item => {
      // The box is a first cut only: heroes and their gear reach a little past it.
      const box = screenBox(item);
      if (point.x < box.l - u || point.x > box.r + u || point.y < box.t - u || point.y > box.b + u) return;
      list.push({ kind: 'item', item, d: depthOf(item), draw: () => drawItem(item) });
    });
    if (!raid) {
      const s = walkerSize();
      walkers.forEach(w => {
        if (!w.wants || w.bought || w.state === 'out') return;
        const at = proj(w.x, w.y);
        const tag = walkerTag(w);
        const reach = Math.max(s, tag.w / 2) + r;
        if (Math.abs(point.x - at.x) > reach || point.y < tag.y - r || point.y > at.y + r) return;
        list.push({ kind: 'walker', walker: w, d: w.y, person: true, draw: () => drawWalker(w) });
      });
    }
    if (!K.inside && !K.dead) {
      const at = proj(K.x, K.y);
      const s = u * 0.95;
      if (Math.abs(point.x - at.x) < s + r && point.y > at.y - s * 1.6 - r && point.y < at.y + r) list.push({ kind: 'hustler', d: K.y, person: true, draw: drawHustler });
    }
    return list.sort((a, b) => b.d - a.d);
  };
  const pickAt = point => {
    const r = compact ? 12 : 6;
    const found = candidates(point, r).map(c => ({ ...c, near: coverage(point, r, c.draw) })).filter(c => c.near <= r);
    const closest = list => list.reduce((best, c) => (!best || c.near < best.near ? c : best), null);
    const hit = found.find(c => c.near === 0);
    return closest(found.filter(c => c.person && (!hit || c.d >= hit.d))) || hit || closest(found);
  };
  const tap = point => {
    if (ghost) {
      ghostTo(point);
      // A building being moved goes straight to where you tap.
      if (ghost.moving) dropMove();
      else snapGhost();
      return;
    }
    const fighting = raid && raid.phase === 'fight';
    // A fight runs on its own and another gang's ground has nothing of yours to pick: a tap puts the card away,
    // and in a fight a tap on a Bootleg is the Goldfaust.
    if (fighting || heist) {
      selected = null;
      if (fighting) punchAt(point);
      return;
    }
    const tag = tags.find(t => point.x >= t.x - 6 && point.x <= t.x + t.w + 6 && point.y >= t.y - 6 && point.y <= t.y + t.h + 8);
    if (tag) {
      if (tag.repair) repair(tag.item);
      else collect(tag.item);
      return;
    }
    const pick = pickAt(point);
    if (pick && pick.kind === 'walker') {
      talkTo(pick.walker);
      return;
    }
    if (pick && pick.kind === 'hustler') {
      selected = { kind: 'hustler' };
      play('tap');
      if (!raid) {
        K.goal = null;
        K.act = 'wave';
        K.emote = 'wave';
        K.wait = 2;
        K.face = point.x < proj(K.x, K.y).x ? -1 : 1;
      }
      return;
    }
    const item = pick && pick.item;
    if (item && item.ruin && !(raid && raid.phase !== 'end')) {
      repair(item);
      return;
    }
    if (item) {
      const kind = kindOf(item);
      if ((kind === 'promo' || kind === 'sale') && !raid) {
        if ((item.stock || 0) >= 1) collect(item);
        else hustle(item);
      }
      selected = { kind: 'item', id: item.id };
      play('tap');
      return;
    }
    const plot = plots.find(p => point.x >= p.a.x && point.x <= p.b.x && point.y >= p.a.y && point.y <= p.b.y);
    if (plot) {
      selected = { kind: 'plot', key: plot.key };
      play('tap');
      return;
    }
    selected = null;
  };
  const zoomAt = (point, z) => {
    const before = unproj(point.x, point.y);
    cam.z = clamp(z, MIN_ZOOM, maxZoom());
    looked = clock;
    const after = unproj(point.x, point.y);
    cam.x += before.x - after.x;
    cam.y += before.y - after.y;
    clampCam();
  };
  const onGhost = point => {
    if (!ghost) return false;
    const t = TYPES[ghost.type];
    const pad = compact ? 0.8 : 0.3;
    const a = proj(ghost.x - pad, ghost.y - pad, heightOf(ghost.type, ghost.level) + 0.2);
    const b = proj(ghost.x + t.w + pad, ghost.y + t.d + pad);
    return point.x >= a.x && point.x <= b.x && point.y >= a.y && point.y <= b.y;
  };
  // iOS opens its magnifier on a long press or a quick second tap: the map takes no touch defaults at all, and
  // nothing outside the name field starts a selection or a callout.
  canvas.addEventListener('touchstart', event => event.preventDefault(), { passive: false });
  ['selectstart', 'contextmenu'].forEach(type => root.addEventListener(type, event => {
    if (!event.target.closest || !event.target.closest('input, textarea')) event.preventDefault();
  }));
  canvas.addEventListener('pointerdown', event => {
    if (!founded() || travel) return;
    canvas.setPointerCapture(event.pointerId);
    const point = rootPoint(event);
    pointers.set(event.pointerId, point);
    if (pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      pinch = { d: dist(a.x, a.y, b.x, b.y), z: cam.z };
      press = null;
      return;
    }
    const held = onGhost(point);
    // Holding a building a moment picks it up; one that is already selected can be dragged right away.
    const pick = !ghost && !raid && pickAt(point);
    const item = pick && pick.item;
    const chosen = item && selected && selected.kind === 'item' && selected.id === item.id;
    press = { id: event.pointerId, x: point.x, y: point.y, lx: point.x, ly: point.y, moved: false, ghost: held, grab: held ? grabOf(point) : null, hold: item ? { id: item.id, at: performance.now() } : null, drag: chosen ? item.id : null };
  });
  canvas.addEventListener('pointermove', event => {
    const point = rootPoint(event);
    if (!pointers.has(event.pointerId)) return;
    pointers.set(event.pointerId, point);
    if (pinch && pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      zoomAt({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, pinch.z * (dist(a.x, a.y, b.x, b.y) / Math.max(1, pinch.d)));
      return;
    }
    if (!press || press.id !== event.pointerId) return;
    if (!press.moved && dist(point.x, point.y, press.x, press.y) > 7) {
      press.moved = true;
      press.hold = null;
      const pick = press.drag && !ghost && itemById(press.drag);
      if (pick) {
        armMove(pick);
        press.ghost = true;
        press.grab = grabOf({ x: press.x, y: press.y });
      }
      canvas.classList.add('is-grabbing');
      if (press.ghost && ghost) ghost.lift = Math.max(ghost.lift, 0.01);
    }
    if (!press.moved) return;
    if (press.ghost && ghost) ghostTo(point, press.grab);
    else {
      // Dragging the map takes the camera back from an ace.
      camLock = null;
      looked = clock;
      cam.x -= (point.x - press.lx) / cw();
      cam.y -= (point.y - press.ly) / (cw() * TILT);
      clampCam();
    }
    press.lx = point.x;
    press.ly = point.y;
  });
  const endPointer = event => {
    if (!pointers.has(event.pointerId)) return;
    pointers.delete(event.pointerId);
    canvas.classList.remove('is-grabbing');
    if (pinch) {
      if (pointers.size < 2) pinch = null;
      return;
    }
    if (press && press.id === event.pointerId && event.type === 'pointerup') {
      // A moved building drops where it is let go, or on the closest free spot; a new one still waits for the price.
      if (press.ghost && (press.moved || press.picked) && ghost && ghost.moving) dropMove();
      else if (press.ghost && press.moved && ghost) snapGhost();
      else if (!press.moved && !press.picked) tap(rootPoint(event));
    }
    press = null;
  };
  // Long press to pick a building up, and pan the map while a ghost is dragged to the edge.
  const HOLD_MS = 450;
  const updatePress = dt => {
    if (!press) return;
    if (press.hold && !ghost && performance.now() - press.hold.at > HOLD_MS) {
      const item = itemById(press.hold.id);
      press.hold = null;
      if (item && !raid) {
        armMove(item);
        press.ghost = true;
        press.picked = true;
        press.grab = grabOf({ x: press.lx, y: press.ly });
        ghost.lift = 0.01;
      }
    }
    if (!press.ghost || !ghost || !(press.moved || press.picked)) return;
    const edge = 56;
    const ex = press.lx < edge ? press.lx - edge : press.lx > W - edge ? press.lx - (W - edge) : 0;
    const ey = press.ly < edge ? press.ly - edge : press.ly > H - edge ? press.ly - (H - edge) : 0;
    if (!ex && !ey) return;
    cam.x += (ex / edge) * dt * 9 / cam.z;
    cam.y += (ey / edge) * dt * 9 / cam.z / TILT;
    clampCam();
    ghostTo({ x: press.lx, y: press.ly }, press.grab);
  };
  canvas.addEventListener('pointerup', endPointer);
  canvas.addEventListener('pointercancel', endPointer);
  canvas.addEventListener('wheel', event => {
    if (!founded()) return;
    event.preventDefault();
    zoomAt(rootPoint(event), cam.z * Math.exp(-event.deltaY * 0.0015));
  }, { passive: false });

  // Holding an upgrade button fills it, and once full it buys every step the cash covers. The click that
  // follows the release is then swallowed, so it does not buy one more.
  const BUY_HOLD_MS = 550;
  let buyHold = null;
  let swallow = false;
  const holdButton = () => buyHold && root.querySelector(`[data-hold="${buyHold.key}"]`);
  const dropHold = () => {
    const button = holdButton();
    if (button) button.style.removeProperty('--hold');
    buyHold = null;
  };
  root.addEventListener('pointerdown', event => {
    swallow = false;
    const target = event.target.closest('[data-hold]');
    if (!target || target.disabled || pointers.size) return;
    buyHold = { key: target.dataset.hold, id: event.pointerId, x: event.clientX, y: event.clientY, at: performance.now() };
  });
  root.addEventListener('pointermove', event => {
    if (buyHold && buyHold.id === event.pointerId && dist(event.clientX, event.clientY, buyHold.x, buyHold.y) > 12) dropHold();
  });
  ['pointerup', 'pointercancel'].forEach(type => window.addEventListener(type, event => {
    if (buyHold && buyHold.id === event.pointerId) dropHold();
  }));
  const updateHold = () => {
    if (!buyHold) return;
    const button = holdButton();
    if (!button || button.disabled) {
      buyHold = null;
      return;
    }
    const done = (performance.now() - buyHold.at) / BUY_HOLD_MS;
    button.style.setProperty('--hold', Math.min(1, done).toFixed(3));
    if (done < 1) return;
    dropHold();
    swallow = true;
    const item = selectedItem();
    if (item) upgrade(item, true);
  };

  // Buttons everywhere go through one listener.
  root.addEventListener('click', event => {
    const target = event.target.closest('button, [data-open]');
    if (!target || target.disabled) return;
    if (swallow) {
      swallow = false;
      if (target.dataset.hold) return;
    }
    if (target.dataset.open) {
      if (target.dataset.open === 'build') openPanel('build', 'promo');
      else openPanel(target.dataset.open);
      return;
    }
    if (target === els.rescue) {
      rescueHustler();
      return;
    }
    if (target === els.magnet) {
      pullAll();
      return;
    }
    if (target === els.move) {
      claimMove();
      return;
    }
    if (target === els.pay) {
      selected = { kind: 'pay' };
      play('tap');
      return;
    }
    if (target === els.who) {
      if (heist) return;
      selected = { kind: 'hustler' };
      focusOn(K.x, K.y - 1);
      play('tap');
      return;
    }
    if (target === els.close) {
      closePanel();
      return;
    }
    if (target.dataset.tool) {
      useTool(target.dataset.tool);
      return;
    }
    const card = target.dataset.card;
    if (card) {
      const item = selectedItem();
      if (card === 'cancel') ghost = null;
      else if (card === 'close') {
        ghost = null;
        selected = null;
      }
      else if (card === 'place') placeGhost();
      else if (card === 'upgrade' && item) upgrade(item);
      else if (card === 'repair' && item) repair(item);
      else if (card === 'collect' && item) collect(item);
      else if (card === 'show' && item) startShow(item);
      else if (card === 'buy-land' && selected) buyLand(selected.key);
      else if (card === 'pay') payProtection();
      return;
    }
    const act = target.dataset.act;
    if (act === 'tab') {
      panel.data = target.dataset.id;
      play('tick');
      refreshPanel(true);
      els.panelBody.scrollTop = 0;
    } else if (act === 'build') armBuild(target.dataset.id);
    else if (act === 'heist') goHeist(Number(target.dataset.id));
    else if (act === 'ace-buy' || act === 'ace-equip') {
      if (act === 'ace-buy') buyAce(target.dataset.id);
      else equipAce(target.dataset.id);
      refreshPanel(true);
    }
    else if (act === 'task' || act === 'chest') {
      const from = centerOf(target);
      if (act === 'task') claimTask(Number(target.dataset.id), from);
      else openChest(from);
      refreshPanel(true);
    }
    if (target.dataset.intro === 'reroll') spinHustler();
    if (target.dataset.intro === 'start') {
      S.name = cleanName(els.intro.querySelector('[data-intro-name]').value);
      start();
    }
    if (target.dataset.over === 'again') startOver();
  });
  // Enter puts the keyboard away on a phone.
  els.intro.addEventListener('keydown', event => {
    if (event.key === 'Enter' && event.target.matches('[data-intro-name]')) event.target.blur();
  });
  els.modal.addEventListener('click', event => {
    if (event.target === els.modal) closePanel();
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    if (panel) closePanel();
    else if (ghost) ghost = null;
    else selected = null;
  });

  // ---------------------------------------------------------------------------------------------
  // Time: the frame loop, saving, and coming back after a while.

  let lastFrame = performance.now();
  let lastProduce = Date.now();
  let saveTimer = 0;
  // The countdown to the next Problem only runs while someone plays: the tab in view and touched within the
  // last minute and a half. Time away, in another tab or idle in front of the screen pushes the Problem back.
  const IDLE = 90 * SECOND;
  let lastInput = Date.now();
  let lastTick = Date.now();
  const tickCountdown = now => {
    if (document.visibilityState !== 'visible' || now - lastInput > IDLE) {
      S.raidAt += now - lastTick;
      if (S.revenge) S.revenge.at += now - lastTick;
    }
    lastTick = now;
  };
  // Coming back to a save: the time since it was last written did not count, and no Problem is further off than
  // the longest gap between two.
  const resumeCountdown = now => {
    S.raidAt = clamp(S.raidAt + Math.max(0, now - S.last), now + 3000, now + RAID_GAP.max);
    if (S.revenge) S.revenge.at = clamp(S.revenge.at + Math.max(0, now - S.last), now + 3000, now + REVENGE.max);
    lastTick = now;
  };
  // Every frame costs battery and warms the phone, so the game draws only as often as it needs. While the map is
  // dragged, pinched or wheeled, and on a computer whenever it is in use, every frame the screen shows is drawn: a
  // 120 Hz screen gets 120. On a phone a fight or someone playing gets sixty, and the block thirty once nobody has
  // touched it for a while.
  const FRAME_FULL = 1000 / 60;
  const DESK = Boolean(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
  const frameGap = now => {
    if (press || pinch || now - lastInput < 300) return 0;
    if (raid || heist || travel || camLock || ghost || rescue || now - lastInput < IDLE) return DESK ? 0 : FRAME_FULL;
    return 1000 / 30;
  };
  // Drawing costs by the pixel: a device that stays under 25 frames a second draws fewer of them, step by step,
  // and gets them back once it runs smoothly for a while. Should the sharper step prove too slow again, it stays put.
  const pace = { grace: 3000, time: 0, frames: 0, smooth: 0, raised: false, settled: false };
  const watchPace = gap => {
    if (gap > 250) return;
    if (pace.grace > 0) {
      pace.grace -= gap;
      return;
    }
    pace.time += gap;
    pace.frames += 1;
    if (pace.time < 2000) return;
    const frame = pace.time / pace.frames;
    pace.time = 0;
    pace.frames = 0;
    if (frame > 40 && dpr > 1) {
      if (pace.raised) pace.settled = true;
      pace.raised = false;
      pace.smooth = 0;
      quality *= 0.8;
    } else if (frame < 20 && quality < 1 && !pace.settled) {
      pace.smooth += 1;
      if (pace.smooth < 3) return;
      pace.smooth = 0;
      pace.raised = true;
      quality = Math.min(1, quality / 0.8);
    } else {
      pace.smooth = 0;
      return;
    }
    layout();
    pace.grace = 1000;
  };
  // One frame loop at a time: it stops while another tab has the save and starts again on taking it back.
  let looping = false;
  const loop = perfNow => {
    looping = !elsewhere;
    if (elsewhere) return;
    // A frame early for the pace wanted is skipped; a little slack keeps a 60 Hz screen from dropping every other one.
    const gap = frameGap(Date.now());
    if (perfNow - lastFrame < gap - 2) {
      requestAnimationFrame(loop);
      return;
    }
    // A slow device shows as frames later than wanted, whatever the pace.
    watchPace(perfNow - lastFrame - Math.max(0, gap - FRAME_FULL));
    const real = Math.min(0.05, Math.max(0, (perfNow - lastFrame) / 1000));
    updateRescue(real);
    const dt = rescue ? real * 0.1 : hitstop > 0 ? real * 0.12 : real;
    hitstop = Math.max(0, hitstop - real);
    lastFrame = perfNow;
    clock += dt;
    const now = Date.now();
    if (founded()) {
      // While the crew is out on a Raid the block waits; what it made meanwhile comes in on return.
      if (!heist) {
        produce(Math.max(0, (now - lastProduce) / 1000));
        lastProduce = now;
        updateHustler(dt);
        updateWalkers(dt);
      }
      updateItems(dt);
      updateRaid(dt);
      updateEnemies(dt);
      refillHeists(now);
      updateCrew(dt);
      updateTricks(dt);
      updateShots(dt);
      updatePress(dt);
      updateHold();
      RAID_TRICKS.forEach(id => {
        toolCd[id] = Math.max(0, toolCd[id] - dt);
      });
      tickCountdown(now);
      if (!raid && S.revenge && now >= S.revenge.at && document.visibilityState === 'visible') {
        const { gang } = S.revenge;
        S.revenge = null;
        startRaid(gang);
      }
      if (!raid && now >= S.raidAt && document.visibilityState === 'visible') startRaid();
      saveTimer += dt;
      if (saveTimer > 3) {
        saveTimer = 0;
        persist();
      }
    } else {
      lastProduce = now;
      lastTick = now;
      if (K.dead) {
        updateHustler(dt);
        updateEnemies(dt);
      }
    }
    shake = Math.max(0, shake - dt);
    updateEffects(dt);
    updateFlyers(dt);
    followCam(real);
    fightCam(real);
    updateTravel(real);
    if (ghost) {
      ghost.ok = canPlace(ghost.type, ghost.x, ghost.y, ghost.moving ? itemById(ghost.moving) : null);
      const k = Math.min(1, dt * 22);
      ghost.vx += (ghost.x - ghost.vx) * k;
      ghost.vy += (ghost.y - ghost.vy) * k;
      const lifted = press && press.ghost && (press.moved || press.picked);
      ghost.lift += ((lifted ? 1 : 0) - ghost.lift) * Math.min(1, dt * 14);
    }
    view.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx = view;
    drawScene();
    renderHud(dt);
    requestAnimationFrame(loop);
  };

  // Ace levels and aces taken out of the game come back as cash at what they cost in the shop today.
  const payAceBack = () => {
    if (!S.aceBack) return;
    const back = nice(salesPace() * S.aceBack);
    delete S.aceBack;
    S.cash += back;
    banner('aceback', `<b>Attacken ohne Stufen</b><em>${cashText(`+${fmt(back)}`)} zurück für alte Stufen</em>`, 'good', 7000);
    persist();
  };
  const boot = () => {
    claimSave();
    refreshLand();
    refreshOcc();
    const now = Date.now();
    S.items.forEach(item => {
      item.hp = maxHp(item);
    });
    layout();
    placeHustler();
    cam.z = compact ? 1.25 : 1.1;
    focusOn(K.x, K.y - 1.5);
    if (S.over) showOver();
    else if (!founded()) showIntro();
    else {
      const held = kind => S.items.reduce((sum, item) => sum + (kindOf(item) === kind ? item.stock || 0 : 0), 0);
      const before = { cash: S.cash + held('sale'), fans: held('promo') };
      const away = (now - S.last) / 1000;
      produce(Math.max(0, away));
      if (away * SECOND >= MAGNET_AWAY) S.magnet = true;
      const cash = S.cash + held('sale') - before.cash;
      const fans = held('promo') - before.fans;
      if (away > 120 && cash + fans >= 1) showToast(`Während du weg warst: +${fmt(cash)} Cash und +${fmt(fans)} Fans`, 'good');
      const live = eventOn();
      if (live) banner('event', `<b>Wochenend-Event: ${live.name}</b>${live.text ? `<em>${live.text}</em>` : ''}`, 'event', 5200);
      if (refunded) banner('refund', `<b>Gutschrift</b><em>${cashText(`+${fmt(refunded)}`)} zurück für alles, was es nicht mehr gibt</em>`, 'good', 7000);
      payAceBack();
      resumeCountdown(now);
    }
    lastProduce = now;
    root.classList.remove('is-booting');
    looping = true;
    requestAnimationFrame(loop);
  };
  window.addEventListener('resize', layout);
  if (window.ResizeObserver) new window.ResizeObserver(() => layout()).observe(root);
  let leftAt = Date.now();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      leftAt = Date.now();
      if (founded()) tickCountdown(Date.now());
      persist();
    } else {
      if (founded() && Date.now() - leftAt >= MAGNET_AWAY) S.magnet = true;
      // Nothing ran while the tab was out of view, so none of that time counts.
      if (founded()) S.raidAt += Date.now() - lastTick;
      lastTick = Date.now();
      takeOver();
    }
    lastFrame = performance.now();
  });
  ['pointerdown', 'pointermove', 'wheel', 'keydown'].forEach(type => window.addEventListener(type, () => {
    lastInput = Date.now();
  }, { capture: true, passive: true }));
  ['pointerdown', 'keydown'].forEach(type => window.addEventListener(type, askToKeep, { capture: true, passive: true }));
  window.addEventListener('pagehide', persist);
  // Another tab took over: this one pauses without a word and keeps its picture.
  window.addEventListener('storage', event => {
    if (event.key === OWNER_KEY && event.newValue && event.newValue !== TAB) elsewhere = true;
  });
  // Used again: take the save back, load whatever the other tab made of it, and carry on from there.
  const takeOver = () => {
    if (!elsewhere) return;
    claimSave();
    elsewhere = false;
    const latest = readSave();
    if (latest) {
      if (heist) Object.assign(cam, heist.cam);
      S = latest;
      hustlerLook = makeHustler(S.seed);
      heist = null;
      raid = null;
      rescue = null;
      ghost = null;
      selected = null;
      press = null;
      crestLook = '';
      [enemies, walkers, pigeons, moments, zones, shots, drones, parts, decals].forEach(list => {
        list.length = 0;
      });
      flare = 0;
      calmAnime();
      closePanel();
      refreshLand();
      refreshOcc();
      S.items.forEach(item => {
        item.hp = maxHp(item);
      });
      placeHustler();
      const now = Date.now();
      if (founded()) {
        produce(Math.max(0, (now - S.last) / 1000));
        payAceBack();
        resumeCountdown(now);
      }
      lastProduce = now;
      shownCash = S.cash;
      shownFans = S.fans;
      if (S.over) showOver();
      else if (!founded()) showIntro();
      else {
        show(els.over, false);
        show(els.intro, false);
      }
    }
    lastFrame = performance.now();
    if (!looping) {
      looping = true;
      requestAnimationFrame(loop);
    }
  };
  window.addEventListener('focus', takeOver);
  root.addEventListener('pointerdown', takeOver, true);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => thumbs.clear());
  boot();
})();
